import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import * as LightweightCharts from 'lightweight-charts';
import * as secp256k1 from '@noble/secp256k1';
import MountainClimber from './MountainClimber';
const { createChart, ColorType } = LightweightCharts;

// ============== PAPER CHAT INPUT PERSISTENCE ==============
// Module-level variables to preserve input state across component remounts
let paperChatInputValue = '';
let paperChatInputFocused = false;
let paperChatSelectFocused = false;

// ============== SOUND SYSTEM ==============
// Web Audio API synthesized sounds - no external files needed!
const createSoundSystem = () => {
  let audioContext = null;
  let soundEnabled = true;
  
  const getContext = () => {
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContext;
  };
  
  // Resume audio context on user interaction (required by browsers)
  const unlock = () => {
    const ctx = getContext();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
  };
  
  // Play a synthesized tone
  const playTone = (frequency, duration, type = 'sine', volume = 0.3, delay = 0) => {
    if (!soundEnabled) return;
    try {
      const ctx = getContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      
      const startTime = ctx.currentTime + delay;
      gainNode.gain.setValueAtTime(volume, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    } catch (e) {
      console.log('Sound error:', e.message);
    }
  };
  
  // Play noise burst (for crash/explosion effects)
  const playNoise = (duration, volume = 0.3, filterFreq = 1000) => {
    if (!soundEnabled) return;
    try {
      const ctx = getContext();
      const bufferSize = ctx.sampleRate * duration;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = filterFreq;
      
      const gainNode = ctx.createGain();
      gainNode.gain.setValueAtTime(volume, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      
      noise.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      noise.start();
      noise.stop(ctx.currentTime + duration);
    } catch (e) {
      console.log('Noise error:', e.message);
    }
  };
  
  return {
    unlock,
    setEnabled: (enabled) => { soundEnabled = enabled; },
    isEnabled: () => soundEnabled,
    
    // 💰 BUY - Satisfying coin/cash register sound
    buy: () => {
      playTone(800, 0.1, 'sine', 0.2);
      playTone(1200, 0.1, 'sine', 0.15, 0.05);
      playTone(1600, 0.15, 'sine', 0.1, 0.1);
    },
    
    // 📤 SELL - Whoosh/send sound
    sell: () => {
      playTone(600, 0.15, 'sine', 0.2);
      playTone(400, 0.2, 'sine', 0.15, 0.1);
      playTone(200, 0.1, 'sine', 0.1, 0.2);
    },
    
    // 💀 RUG - Dramatic crash/explosion (with debounce to prevent repeats)
    rug: (() => {
      let lastPlayed = 0;
      return () => {
        const now = Date.now();
        if (now - lastPlayed < 2000) return; // Don't play if within 2 seconds
        lastPlayed = now;
        // Deep boom
        playTone(80, 0.5, 'sine', 0.4);
        playTone(60, 0.6, 'sine', 0.3, 0.1);
        // Crash noise
        playNoise(0.4, 0.3, 800);
        // Descending tones
        playTone(400, 0.2, 'sawtooth', 0.15, 0.1);
        playTone(200, 0.3, 'sawtooth', 0.1, 0.2);
        playTone(100, 0.4, 'sawtooth', 0.08, 0.35);
      };
    })(),
    
    // 🌙 MOON - Triumphant celebration
    moon: () => {
      // Ascending fanfare
      playTone(523, 0.15, 'sine', 0.2);       // C
      playTone(659, 0.15, 'sine', 0.2, 0.12); // E
      playTone(784, 0.15, 'sine', 0.2, 0.24); // G
      playTone(1047, 0.3, 'sine', 0.25, 0.36); // High C
      // Sparkle
      playTone(1568, 0.1, 'sine', 0.1, 0.5);
      playTone(2093, 0.1, 'sine', 0.08, 0.55);
    },
    
    // 💬 CHAT - Subtle ping
    chat: () => {
      playTone(880, 0.08, 'sine', 0.1);
      playTone(1100, 0.06, 'sine', 0.08, 0.05);
    },
    
    // 🎁 TIP RECEIVED - Cha-ching!
    tip: () => {
      playTone(1319, 0.1, 'sine', 0.2);  // E
      playTone(1568, 0.1, 'sine', 0.2, 0.08); // G
      playTone(2093, 0.15, 'sine', 0.15, 0.15); // High C
      playTone(2637, 0.2, 'sine', 0.1, 0.2);  // High E
    },
    
    // ✅ SUCCESS - Generic positive feedback
    success: () => {
      playTone(523, 0.1, 'sine', 0.15);
      playTone(659, 0.15, 'sine', 0.12, 0.08);
    },
    
    // ❌ ERROR - Negative feedback
    error: () => {
      playTone(200, 0.15, 'square', 0.1);
      playTone(150, 0.2, 'square', 0.08, 0.1);
    },
    
    // 🎰 BET PLACED - Slot machine feel
    bet: () => {
      playTone(440, 0.08, 'sine', 0.15);
      playTone(550, 0.08, 'sine', 0.12, 0.06);
      playTone(660, 0.1, 'sine', 0.1, 0.12);
    },
    
    // 🏆 WIN - Big win sound
    win: () => {
      playTone(523, 0.12, 'sine', 0.2);
      playTone(659, 0.12, 'sine', 0.2, 0.1);
      playTone(784, 0.12, 'sine', 0.2, 0.2);
      playTone(1047, 0.25, 'sine', 0.25, 0.3);
      playTone(1319, 0.15, 'sine', 0.15, 0.45);
    },
    
    // 💸 CASHOUT - Cha-ching payout sound
    cashout: () => {
      playTone(880, 0.1, 'sine', 0.2);
      playTone(1100, 0.1, 'sine', 0.18, 0.08);
      playTone(1320, 0.12, 'sine', 0.15, 0.15);
      playTone(1760, 0.15, 'sine', 0.12, 0.22);
    },
    
    // 🔔 NOTIFICATION - Alert sound
    notify: () => {
      playTone(660, 0.1, 'sine', 0.12);
      playTone(880, 0.12, 'sine', 0.1, 0.08);
    },
    
    // ⌨️ TICK - Soft keyboard click for lobby chat
    tick: () => {
      playTone(1200, 0.03, 'sine', 0.08);
    },
    
    // 👋 PLAYER JOIN - Subtle welcome ping
    playerJoin: () => {
      playTone(880, 0.08, 'sine', 0.1);
      playTone(1100, 0.1, 'sine', 0.08, 0.06);
    },
    
    // 🚪 PLAYER LEAVE - Softer departure sound
    playerLeave: () => {
      playTone(440, 0.1, 'sine', 0.06);
      playTone(330, 0.12, 'sine', 0.04, 0.08);
    },
    
    // 🚀 TOKEN LAUNCH - User launched a new token
    tokenLaunch: () => {
      playTone(523, 0.1, 'sine', 0.15);
      playTone(659, 0.1, 'sine', 0.12, 0.08);
      playTone(784, 0.12, 'sine', 0.1, 0.16);
    },
    
    // 🪙 PENNY - 1 cent fee taken (coin drop sound)
    penny: () => {
      // Metallic coin drop - high pitched ping
      playTone(2500, 0.08, 'sine', 0.15);
      playTone(3200, 0.06, 'sine', 0.12, 0.03);
      // Coin settling
      playTone(1800, 0.1, 'sine', 0.08, 0.08);
      playTone(2200, 0.05, 'sine', 0.06, 0.12);
    },
    
    // 🐋 WHALE ALERT - Big buy incoming! Deep bass + rising tones
    whale: () => {
      // Deep underwater bass
      playTone(60, 0.4, 'sine', 0.35);
      playTone(80, 0.35, 'sine', 0.3, 0.1);
      // Rising excitement tones
      playTone(220, 0.15, 'sine', 0.2, 0.2);
      playTone(330, 0.15, 'sine', 0.18, 0.3);
      playTone(440, 0.15, 'sine', 0.15, 0.4);
      playTone(660, 0.2, 'sine', 0.12, 0.5);
    },

    // ============ MOUNTAIN CLIMBER SOUNDS ============

    // 🏔️ CLIMB TICK - Subtle tick as multiplier increases
    climbTick: () => {
      playTone(600 + Math.random() * 200, 0.04, 'sine', 0.06);
    },

    // ⛳ FLAG PLANT - You cashed out successfully!
    flagPlant: () => {
      // Satisfying "thunk" of flag in ground
      playTone(300, 0.08, 'triangle', 0.2);
      playTone(450, 0.1, 'sine', 0.15, 0.05);
      // Victory chime
      playTone(784, 0.12, 'sine', 0.15, 0.1);
      playTone(988, 0.12, 'sine', 0.12, 0.18);
      playTone(1175, 0.15, 'sine', 0.1, 0.26);
    },

    // ❄️ AVALANCHE - Round crashed! Everyone falls!
    avalanche: (() => {
      let lastPlayed = 0;
      return () => {
        const now = Date.now();
        if (now - lastPlayed < 2000) return; // Debounce
        lastPlayed = now;
        // Deep rumble
        playTone(50, 0.6, 'sine', 0.4);
        playTone(70, 0.5, 'sine', 0.35, 0.1);
        // Crashing snow/rocks
        playNoise(0.5, 0.35, 600);
        // Descending doom
        playTone(300, 0.2, 'sawtooth', 0.12, 0.15);
        playTone(200, 0.25, 'sawtooth', 0.1, 0.25);
        playTone(100, 0.3, 'sawtooth', 0.08, 0.4);
        playTone(50, 0.4, 'sine', 0.15, 0.5);
      };
    })(),

    // ⚔️ ATTACK - You attacked someone!
    attack: () => {
      // Whoosh
      playTone(800, 0.08, 'sawtooth', 0.12);
      playTone(400, 0.1, 'sawtooth', 0.1, 0.05);
      // Impact
      playTone(150, 0.1, 'square', 0.15, 0.1);
      playNoise(0.08, 0.12, 2000);
    },

    // 😵 GOT HIT - Someone attacked you!
    gotHit: () => {
      // Slip/stumble sound
      playTone(600, 0.1, 'sine', 0.15);
      playTone(300, 0.15, 'sine', 0.12, 0.08);
      playTone(150, 0.12, 'sine', 0.1, 0.18);
      // Thud
      playNoise(0.1, 0.15, 400);
    },

    // 💀 FELL - You fell off the mountain
    fell: () => {
      // Falling whistle
      playTone(800, 0.15, 'sine', 0.15);
      playTone(500, 0.15, 'sine', 0.12, 0.12);
      playTone(300, 0.15, 'sine', 0.1, 0.24);
      playTone(150, 0.2, 'sine', 0.08, 0.36);
      // Splat
      playNoise(0.15, 0.2, 300);
    },

    // 🔔 ROUND START - New round beginning
    roundStart: () => {
      // Attention getter
      playTone(440, 0.1, 'sine', 0.15);
      playTone(550, 0.1, 'sine', 0.12, 0.1);
      playTone(660, 0.15, 'sine', 0.1, 0.2);
    },

    // ⏱️ COUNTDOWN - Final seconds warning
    countdown: () => {
      playTone(880, 0.08, 'square', 0.12);
    },

    // 🏁 CLIMB START - Race begins!
    climbStart: () => {
      // Starting horn
      playTone(440, 0.2, 'sawtooth', 0.15);
      playTone(550, 0.15, 'sawtooth', 0.12, 0.15);
      playTone(660, 0.25, 'sawtooth', 0.1, 0.25);
    },
  };
};

// Create singleton sound instance
const Sounds = createSoundSystem();

// ============== MUSIC STATION SYSTEM ==============
// Real MP3 tracks from Pixabay (free, no copyright)
const createMusicSystem = () => {
  let currentAudio = null;
  let currentStation = 'off'; // Default to off until user hosts their own MP3s
  let currentTrackIndex = 0;
  let isPlaying = false;
  let volume = 0.25;
  let isShuffled = true;
  let playedTracks = [];
  
  // 🎵 MUSIC STATIONS
  const stations = {
    off: {
      name: '🔇 Off',
      description: 'No music',
      tracks: []
    },
    retro: {
      name: '🎹 Retro Synth',
      description: '80s new wave instrumental',
      tracks: [
        '/80s-new-wave-instrumental-retro-synth-vibes-301450.mp3',
      ]
    },
    disco: {
      name: '🪩 Electro Disco',
      description: 'New wave disco beat',
      tracks: [
        '/electro-beat-new-wave-disco-332630.mp3',
      ]
    },
  };
  
  const getStations = () => stations;
  const getCurrentStation = () => currentStation;
  const getCurrentTrack = () => {
    const tracks = stations[currentStation]?.tracks || [];
    return tracks[currentTrackIndex] || null;
  };
  
  const setStation = (stationId) => {
    if (stations[stationId]) {
      currentStation = stationId;
      currentTrackIndex = 0;
      playedTracks = [];
      localStorage.setItem('paperHands_musicStation', stationId);
      
      if (stationId === 'off') {
        stop();
      } else if (isPlaying) {
        // Restart with new station
        stop();
        play();
      }
    }
  };
  
  const setVolume = (vol) => {
    volume = Math.max(0, Math.min(1, vol));
    if (currentAudio) {
      currentAudio.volume = volume;
    }
    localStorage.setItem('paperHands_musicVolume', volume.toString());
  };
  
  const getVolume = () => volume;
  
  const getNextTrackIndex = () => {
    const tracks = stations[currentStation]?.tracks || [];
    if (tracks.length === 0) return -1;
    
    if (isShuffled) {
      // Shuffle: pick random unplayed track
      const unplayed = tracks.map((_, i) => i).filter(i => !playedTracks.includes(i));
      if (unplayed.length === 0) {
        // All played, reset
        playedTracks = [];
        return Math.floor(Math.random() * tracks.length);
      }
      return unplayed[Math.floor(Math.random() * unplayed.length)];
    } else {
      // Sequential
      return (currentTrackIndex + 1) % tracks.length;
    }
  };
  
  const playTrack = (trackUrl) => {
    if (!trackUrl) return;
    
    // Stop current
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.src = '';
      currentAudio = null;
    }
    
    currentAudio = new Audio(trackUrl);
    currentAudio.volume = volume;
    currentAudio.crossOrigin = 'anonymous';
    
    // Loop single track stations, or play next for multi-track
    const tracks = stations[currentStation]?.tracks || [];
    if (tracks.length === 1) {
      currentAudio.loop = true;
    } else {
      currentAudio.onended = () => {
        playedTracks.push(currentTrackIndex);
        currentTrackIndex = getNextTrackIndex();
        if (currentTrackIndex >= 0 && isPlaying) {
          playTrack(tracks[currentTrackIndex]);
        }
      };
    }
    
    // Handle loading errors gracefully
    currentAudio.onerror = () => {
      console.log('🎵 Track failed to load, skipping...');
      playedTracks.push(currentTrackIndex);
      currentTrackIndex = getNextTrackIndex();
      if (currentTrackIndex >= 0 && isPlaying) {
        const tracks = stations[currentStation]?.tracks || [];
        setTimeout(() => playTrack(tracks[currentTrackIndex]), 500);
      }
    };
    
    currentAudio.play().catch(e => {
      console.log('🎵 Autoplay blocked, waiting for interaction');
    });
  };
  
  const play = () => {
    if (currentStation === 'off') return;
    
    const tracks = stations[currentStation]?.tracks || [];
    if (tracks.length === 0) return;
    
    isPlaying = true;
    
    // Already playing - don't restart
    if (currentAudio && !currentAudio.paused) {
      return;
    }
    
    if (currentAudio && currentAudio.paused) {
      currentAudio.play().catch(() => {});
    } else {
      // Start fresh
      if (isShuffled) {
        currentTrackIndex = Math.floor(Math.random() * tracks.length);
      }
      playTrack(tracks[currentTrackIndex]);
    }
  };
  
  const stop = () => {
    isPlaying = false;
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.src = ''; // Clear source to stop loading
      currentAudio.load(); // Reset the audio element
      currentAudio = null;
    }
  };
  
  const skip = () => {
    if (!isPlaying || currentStation === 'off') return;
    
    playedTracks.push(currentTrackIndex);
    currentTrackIndex = getNextTrackIndex();
    
    if (currentTrackIndex >= 0) {
      const tracks = stations[currentStation]?.tracks || [];
      playTrack(tracks[currentTrackIndex]);
    }
  };
  
  const toggleShuffle = () => {
    isShuffled = !isShuffled;
    localStorage.setItem('paperHands_musicShuffle', isShuffled.toString());
    return isShuffled;
  };
  
  // Load saved preferences (with safety)
  try {
    const savedStation = localStorage.getItem('paperHands_musicStation');
    if (savedStation && stations[savedStation]) {
      currentStation = savedStation;
    }
    const savedVolume = localStorage.getItem('paperHands_musicVolume');
    if (savedVolume) {
      volume = parseFloat(savedVolume) || 0.25;
    }
    const savedShuffle = localStorage.getItem('paperHands_musicShuffle');
    if (savedShuffle !== null) {
      isShuffled = savedShuffle !== 'false';
    }
  } catch (e) {
    console.log('🎵 Music preferences load error, using defaults');
  }
  
  return {
    play,
    stop,
    skip,
    setVolume,
    getVolume,
    setStation,
    getStations,
    getCurrentStation,
    getCurrentTrack,
    toggleShuffle,
    isShuffled: () => isShuffled,
    isPlaying: () => isPlaying,
  };
};

// Create singleton music instance
const Music = createMusicSystem();

const PaperHands = () => {
  // ============== RESPONSIVE BREAKPOINTS ==============
  // Mobile: < 768px | Tablet: 768-1024px | Desktop: > 1024px
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setIsDesktop(width >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // ============== VIEWS ==============
  const [view, setView] = useState('connect'); // connect, lobby, token, store, whitepaper, thoughts, portfolio
  const [mobileTokenTab, setMobileTokenTab] = useState('trade'); // trade, info - for mobile token view
  
  // ============== CLOUDFLARE RUG SCANNER ==============
  const [rugScanning, setRugScanning] = useState(true); // Scanning in progress
  const [rugDetected, setRugDetected] = useState(false); // Rug detected (HandCash down)
  const [rugScannerDismissed, setRugScannerDismissed] = useState(false); // User clicked through
  const [handcashDown, setHandcashDown] = useState(false); // Degraded mode - can play but no HC features
  const [retryingConnection, setRetryingConnection] = useState(false); // Retry from within game
  const [showRugModal, setShowRugModal] = useState(false); // Modal when rug detected mid-session
  const [rugModalMessage, setRugModalMessage] = useState(''); // Custom message for rug modal
  const [showScannerInfo, setShowScannerInfo] = useState(false); // Modal explaining scanner system
  const [scanMessage, setScanMessage] = useState('Initializing quantum memecoin detectors...'); // Fun loading message
  const serverRugAlertShownRef = useRef(false); // Track if we already showed server-detected rug alert (ref to avoid stale closure)
  
  // ============== SOUND SETTINGS ==============
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('paperHands_soundEnabled');
    return saved !== 'false'; // Default to enabled
  });
  
  // ============== MUSIC SETTINGS ==============
  const [musicEnabled, setMusicEnabled] = useState(() => {
    const saved = localStorage.getItem('paperHands_musicEnabled');
    return saved === 'true'; // Default to disabled
  });
  const [musicVolume, setMusicVolume] = useState(() => {
    const saved = localStorage.getItem('paperHands_musicVolume');
    return saved ? parseFloat(saved) : 0.25;
  });
  const [showMusicSelector, setShowMusicSelector] = useState(false);
  const [currentStation, setCurrentStation] = useState(() => {
    try {
      return Music.getCurrentStation() || 'off';
    } catch (e) {
      return 'off';
    }
  });
  
  // Update sound system when setting changes
  useEffect(() => {
    Sounds.setEnabled(soundEnabled);
    localStorage.setItem('paperHands_soundEnabled', soundEnabled.toString());
  }, [soundEnabled]);
  
  // Update music system when setting changes
  useEffect(() => {
    localStorage.setItem('paperHands_musicEnabled', musicEnabled.toString());
    if (musicEnabled) {
      Music.play();
    } else {
      Music.stop();
    }
  }, [musicEnabled]);
  
  // Update music volume
  useEffect(() => {
    Music.setVolume(musicVolume);
    localStorage.setItem('paperHands_musicVolume', musicVolume.toString());
  }, [musicVolume]);
  
  // Stop music when component unmounts
  useEffect(() => {
    return () => Music.stop();
  }, []);
  
  // Unlock audio on first user interaction
  useEffect(() => {
    const handleInteraction = () => {
      Sounds.unlock();
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
    document.addEventListener('click', handleInteraction);
    document.addEventListener('keydown', handleInteraction);
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, []);
  
  // 🎯 TAB FOCUS & ACTIVITY TRACKING - For bot idle detection
  useEffect(() => {
    // Track tab focus
    const handleFocus = () => {
      setIsTabFocused(true);
      lastUserActivityRef.current = Date.now();
    };
    const handleBlur = () => setIsTabFocused(false);
    
    // Track user activity (mouse, keyboard, touch)
    const handleActivity = () => {
      lastUserActivityRef.current = Date.now();
    };
    
    // Visibility API (more reliable than focus for tab switching)
    const handleVisibility = () => {
      const focused = !document.hidden;
      setIsTabFocused(focused);
      if (focused) lastUserActivityRef.current = Date.now();
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibility);
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('click', handleActivity);
    document.addEventListener('touchstart', handleActivity);
    document.addEventListener('scroll', handleActivity);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibility);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('touchstart', handleActivity);
      document.removeEventListener('scroll', handleActivity);
    };
  }, []);

  // ============== WALLET STATE ==============
  // All wallet data comes from server on auth - no localStorage!
  const [handcashConnected, setHandcashConnected] = useState(false);
  const [handcashUser, setHandcashUser] = useState(null);
  const [handcashLoading, setHandcashLoading] = useState(false);
  const [mainWallet, setMainWallet] = useState(0);
  const [mainWalletSpendable, setMainWalletSpendable] = useState(0);  // Daily limit for deposits
  const [mainWalletFiat, setMainWalletFiat] = useState(0);  // USD value from HandCash
  const [gameWallet, setGameWallet] = useState(0);
  const [totalDeposited, setTotalDeposited] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [transactionPending, setTransactionPending] = useState(false);
  const [txConnectionStatus, setTxConnectionStatus] = useState('unknown'); // 'ok', 'error', 'unknown' - for deposit/withdraw
  const [lastTxError, setLastTxError] = useState(null);
  
  // 🎰 PLAYTHROUGH - Withdrawable balance tracking
  const [withdrawableBalance, setWithdrawableBalance] = useState(0);
  const [lockedBalance, setLockedBalance] = useState(0);
  const [playthroughPercent, setPlaythroughPercent] = useState(100);
  
  // 🪙 RAKE NOTIFICATION - Shows fee charged with sexy popup
  const [rakeNotification, setRakeNotification] = useState(null); // { sats, usdCents, type: 'trade'|'launch'|'ping' }
  const triggerRakeNotification = (sats, type = 'trade') => {
    // Handle backwards compatibility - if boolean true passed, calculate default 1 cent
    let actualSats = sats;
    if (typeof sats === 'boolean' || !sats || sats < 100) {
      actualSats = Math.floor((0.01 / (bsvPrice || 20)) * 100000000);
    }
    const usdCents = ((actualSats / 100000000) * (bsvPrice || 20) * 100).toFixed(1);
    setRakeNotification({ sats: actualSats, usdCents, type });
    Sounds.penny();
    setTimeout(() => setRakeNotification(null), 2500);
  };
  
  // Legacy function for backward compatibility
  const triggerPennyAnimation = () => {
    // Default to ~1 cent in sats at current price
    const oneCentSats = Math.floor((0.01 / (bsvPrice || 20)) * 100000000);
    triggerRakeNotification(oneCentSats, 'trade');
  };
  
  // 🐦 CANARY - $blackwolf controls degraded mode for everyone
  // Canary failures trigger degraded mode, canary successes clear it
  const isTestPilot = handcashUser?.handle?.toLowerCase() === '$blackwolf';
  
  // 🔒 ADMIN USERS - Only these users can access admin features
  const ADMIN_HANDLES = ['blackwolf', 'h2o4daze', 'zcool'];
  const isAdmin = handcashUser?.handle && ADMIN_HANDLES.includes(handcashUser.handle.toLowerCase().replace('$', ''));
  // Note: isModerator and canViewAdminPortal are defined after adminData useState
  
  // Real money warning
  const [showRealMoneyWarning, setShowRealMoneyWarning] = useState(false);
  const [hideRealMoneyWarning, setHideRealMoneyWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // Store action to execute after warning
  
  // ⛓️ ON-CHAIN AMM DEX STATE
  const [onChainMode, setOnChainMode] = useState(false); // Toggle for on-chain trading
  const [lastTxId, setLastTxId] = useState(null); // Last on-chain transaction ID
  const [showTxConfirmation, setShowTxConfirmation] = useState(false); // Show TX confirmation modal
  const [pendingTx, setPendingTx] = useState(null); // Pending transaction details
  const [showTxHistory, setShowTxHistory] = useState(false); // Show transaction history modal
  const [txHistory, setTxHistory] = useState([]); // On-chain transaction history
  const [expandedTxTokens, setExpandedTxTokens] = useState({}); // Which tokens are expanded in tx modal
  
  // Premium state - 24 HOUR PASS (not permanent!)
  const [premiumExpiresAt, setPremiumExpiresAt] = useState(() => {
    const saved = localStorage.getItem('paperHands_premiumExpires');
    return saved ? parseInt(saved) : 0;
  });
  const [premiumTimeLeft, setPremiumTimeLeft] = useState('');
  
  // Check if premium is active
  const isPremium = premiumExpiresAt > Date.now();
  
  // Format premium time remaining
  const formatPremiumTime = (ms) => {
    if (ms <= 0) return null;
    const hours = Math.floor(ms / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };
  
  // Update premium countdown every minute
  useEffect(() => {
    const updatePremiumTime = () => {
      const remaining = premiumExpiresAt - Date.now();
      if (remaining > 0) {
        setPremiumTimeLeft(formatPremiumTime(remaining));
      } else {
        setPremiumTimeLeft('');
      }
    };
    
    updatePremiumTime();
    const interval = setInterval(updatePremiumTime, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [premiumExpiresAt]);
  
  const [customDepositAmount, setCustomDepositAmount] = useState('');
  
  // ============== GLOBAL STATS (ON-CHAIN ACTIVITY) ==============
  const [globalStats, setGlobalStats] = useState({
    totalDeposits: 0,
    totalWithdrawals: 0,
    totalDepositSats: 0,
    totalWithdrawalSats: 0,
    totalPremiumPurchases: 0,
    championsRecorded: 0,
    onChainTransactions: 0,
  });
  
  // 🏆 FEATURED CHAMPION - On-chain inscription winner, displayed for 12 hours
  const [featuredChampion, setFeaturedChampion] = useState(null);
  
  // ✉️ INSCRIPTIONS - User OP_RETURN messages
  const [inscriptions, setInscriptions] = useState([]);
  const [showInscriptions, setShowInscriptions] = useState(false);
  const [inscriptionMessage, setInscriptionMessage] = useState('');
  const [inscribing, setInscribing] = useState(false);
  const [showPaperHandsStory, setShowPaperHandsStory] = useState(false);
  const [ownsStory, setOwnsStory] = useState(false); // Track if user owns the story
  
  // ============== LOBBY STATE ==============
  const [activeTokens, setActiveTokens] = useState([]);
  const [warmupSecondsRemaining, setWarmupSecondsRemaining] = useState(0); // Server warmup countdown
  const [tokensLoaded, setTokensLoaded] = useState(false); // Prevent flash on initial load
  const [tokenSortBy, setTokenSortBy] = useState('newest'); // newest, mcap, change, liquidity
  const [pvpSortBy, setPvpSortBy] = useState('newest'); // Sort for BSV.pump tokens
  const [tileViewMode, setTileViewMode] = useState(() => {
    // Load from localStorage or default to 'maximized'
    if (typeof window !== 'undefined') {
      return localStorage.getItem('tileViewMode') || 'maximized';
    }
    return 'maximized';
  }); // 'maximized' | 'mini' | 'list'
  const [lobbyChat, setLobbyChat] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]); // Full user data with avatars
  const [tipTarget, setTipTarget] = useState(null); // User to tip { handle, avatarUrl }
  const [tipAmount, setTipAmount] = useState(100); // Default $100 Paper Bucks (100 sats)
  const [tipSending, setTipSending] = useState(false);
  const [tipReceived, setTipReceived] = useState(null); // { from, amount } - incoming tip notification
  const [balanceFlash, setBalanceFlash] = useState(null); // Flash effect for balance changes
  const [lobbyInput, setLobbyInput] = useState('');
  const [showSidebar, setShowSidebar] = useState(false); // Always start minimized
  const [selectedProfile, setSelectedProfile] = useState(null); // User profile modal
  
  // Refresh friends list when profile modal opens (to ensure accurate Add/Unfriend button)
  useEffect(() => {
    if (selectedProfile && sessionId && API_URL) {
      fetchFriends();
    }
  }, [selectedProfile]);
  
  // ============== LIVE MULTIPLAYER STATE ==============
  const [liveData, setLiveData] = useState(null);
  const [lastPollTime, setLastPollTime] = useState(0);
  const [liveLeaderboard, setLiveLeaderboard] = useState([]);
  const [liveAnnouncements, setLiveAnnouncements] = useState([]);
  const [liveActivity, setLiveActivity] = useState([]);
  const [hallOfFame, setHallOfFame] = useState(null);
  const [showHallOfFame, setShowHallOfFame] = useState(false);
  const [announcementToast, setAnnouncementToast] = useState(null); // DISABLED - always null
  const shownAnnouncementIds = useRef(new Set()); // Track shown announcements to prevent spam
  
  // ============== FRIENDS SYSTEM ==============
  const [friends, setFriends] = useState([]);
  const [friendRequestsSent, setFriendRequestsSent] = useState([]); // Pending requests I sent
  const [friendRequestsReceived, setFriendRequestsReceived] = useState([]); // Pending requests to accept
  const [showFriends, setShowFriends] = useState(false);
  const [friendInput, setFriendInput] = useState('');
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [showFriendsOnly, setShowFriendsOnly] = useState(false); // Filter online list to friends only
  
  // ============== DIRECT MESSAGES ==============
  const [showDMs, setShowDMs] = useState(false);
  const [dmConversations, setDmConversations] = useState([]);
  const [selectedDMUser, setSelectedDMUser] = useState(null);
  const [dmInput, setDmInput] = useState('');
  const [dmUnreadCount, setDmUnreadCount] = useState(0);
  const [sendingDM, setSendingDM] = useState(false);
  const [dmToast, setDmToast] = useState(null); // { from, text, timestamp } - floating notification
  const [systemNotifications, setSystemNotifications] = useState([]); // Penny pings, etc
  const lastDmCheckRef = useRef(null); // Track last seen DM to detect new ones
  
  // ============== WEBSOCKET FOR REAL-TIME CHAT ==============
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsReconnectRef = useRef(null);
  const [typingUsers, setTypingUsers] = useState({}); // { handle: timestamp }
  
  // ============== PAPER CHAT (AIM-style window) ==============
  const [showPaperChat, setShowPaperChat] = useState(false);
  const [paperChatView, setPaperChatView] = useState('buddies'); // 'buddies' or handle like 'zcool'
  const [paperChatPos, setPaperChatPos] = useState({ x: 100, y: 100 });
  const [paperChatDropdownOpen, setPaperChatDropdownOpen] = useState(false); // Custom dropdown state
  const [isDraggingChat, setIsDraggingChat] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [paperChatTheme, setPaperChatTheme] = useState(() => localStorage.getItem('paperChatTheme') || 'gold');
  
  // Chat theme colors
  const chatThemes = {
    gold: { primary: '#f0b90b', bg: '#1a1508', border: '#f0b90b', myBubble: '#f0b90b', myText: '#000', theirBubble: '#2a2a3e', theirText: '#fff' },
    blue: { primary: '#3498db', bg: '#081520', border: '#3498db', myBubble: '#3498db', myText: '#fff', theirBubble: '#1a2a3e', theirText: '#fff' },
    green: { primary: '#00d26a', bg: '#081a10', border: '#00d26a', myBubble: '#00d26a', myText: '#000', theirBubble: '#1a2e2a', theirText: '#fff' },
    pink: { primary: '#ff6b9d', bg: '#1a0810', border: '#ff6b9d', myBubble: '#ff6b9d', myText: '#fff', theirBubble: '#2e1a2a', theirText: '#fff' },
    purple: { primary: '#a855f7', bg: '#120818', border: '#a855f7', myBubble: '#a855f7', myText: '#fff', theirBubble: '#2a1a3e', theirText: '#fff' },
    cyber: { primary: '#00ffff', bg: '#0a0a12', border: '#00ffff', myBubble: '#00ffff', myText: '#000', theirBubble: '#1a1a2e', theirText: '#0f0' },
  };
  
  // PaperChat refs - defined at parent level so they persist across re-renders
  const paperChatInputRef = useRef(null);
  const paperChatWindowRef = useRef(null);
  const paperChatDragStartRef = useRef(null);
  const paperChatMessagesEndRef = useRef(null);
  const paperChatUserScrolledRef = useRef(false);
  const paperChatUserListRef = useRef(null); // Ref for user list scroll container
  const paperChatUserListScrollRef = useRef(0); // Preserve user list scroll position
  const paperChatMessagesContainerRef = useRef(null); // Ref for chat messages scroll container
  const paperChatMessagesScrollRef = useRef(0); // Preserve chat messages scroll position
  const paperChatMobileHeightRef = useRef(null); // Capture viewport height when chat opens (prevents keyboard jump)
  
  // Auto-scroll chat to bottom ONLY when new messages arrive in CURRENT chat
  const paperChatPrevMessageCountRef = useRef({}); // Track message count per conversation

  useEffect(() => {
    if (!showPaperChat || !paperChatView || paperChatView === 'buddies') return;

    // Get current conversation's message count
    const currentConvo = dmConversations.find(c => c.handle === paperChatView);
    const currentMessageCount = currentConvo?.messages?.length || 0;
    const prevCount = paperChatPrevMessageCountRef.current[paperChatView] || 0;

    // Scroll to bottom when THIS conversation gets new messages
    if (currentMessageCount > prevCount) {
      paperChatPrevMessageCountRef.current[paperChatView] = currentMessageCount;
      // Always scroll to bottom on new messages
      setTimeout(() => {
        if (paperChatMessagesContainerRef.current) {
          paperChatMessagesContainerRef.current.scrollTop = paperChatMessagesContainerRef.current.scrollHeight;
        }
      }, 50);
    } else if (currentMessageCount > 0 && prevCount === 0) {
      // First time loading this conversation
      paperChatPrevMessageCountRef.current[paperChatView] = currentMessageCount;
      setTimeout(() => {
        if (paperChatMessagesContainerRef.current) {
          paperChatMessagesContainerRef.current.scrollTop = paperChatMessagesContainerRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [dmConversations, showPaperChat, paperChatView]);

  // Scroll to bottom when switching to a NEW chat
  const paperChatPrevViewRef = useRef(null);

  useEffect(() => {
    if (paperChatView !== paperChatPrevViewRef.current && paperChatView !== 'buddies') {
      paperChatPrevViewRef.current = paperChatView;
      setTimeout(() => {
        if (paperChatMessagesContainerRef.current) {
          paperChatMessagesContainerRef.current.scrollTop = paperChatMessagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [paperChatView]);
  
  // ============== RUG PROPHET (Side Betting) ==============
  const [showRugProphet, setShowRugProphet] = useState(false);
  const [rugProphetBets, setRugProphetBets] = useState([]);
  const [rugProphetStats, setRugProphetStats] = useState({ totalBets: 0, wins: 0, losses: 0, totalWon: 0, totalLost: 0 });
  const [selectedBetToken, setSelectedBetToken] = useState(null);
  const [betAmount, setBetAmount] = useState(10000); // Min bet is $10K PB
  const [betPrediction, setBetPrediction] = useState('rug');
  const [placingBet, setPlacingBet] = useState(false);
  const [selectedBet, setSelectedBet] = useState(null); // For bet details modal

  // ============== DEGEN AI ASSISTANT ==============
  const [showDegenAI, setShowDegenAI] = useState(false);
  const [degenAIMessages, setDegenAIMessages] = useState([]);
  const [degenAIInput, setDegenAIInput] = useState('');
  const [degenAILoading, setDegenAILoading] = useState(false);
  const [degenAIStatus, setDegenAIStatus] = useState(null); // { enabled, freeRemaining, isSubscriber, subscriptionExpires, subscriptionCost }
  const [showDegenAIUpsell, setShowDegenAIUpsell] = useState(false); // Upsell modal when free questions run out
  const [degenAISubscribing, setDegenAISubscribing] = useState(false); // Loading state for subscription
  const degenAIMessagesEndRef = useRef(null);

  // ============== ENHANCED LEADERBOARDS ==============
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardTab, setLeaderboardTab] = useState('topWinners');
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  
  // ============== TOKEN ROOM STATE ==============
  const [currentToken, setCurrentToken] = useState(null);
  const [gameState, setGameState] = useState('spectating'); // spectating, playing, won, rugged
  const [currentPrice, setCurrentPrice] = useState(1.0); // Single price - no dual system
  const [candles, setCandles] = useState([]);
  const [tokenChat, setTokenChat] = useState([]);
  const [tokenPlayers, setTokenPlayers] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [stake, setStake] = useState(50000); // Default $50K PB (50K sats = ~$0.01 USD)
  const [customSellPct, setCustomSellPct] = useState(50); // Default 50% sell
  const [customSellUSD, setCustomSellUSD] = useState(''); // Custom USD sell amount (empty = use percentage)

  // 📱 MOBILE COLLAPSIBLE PANELS
  const [showChart, setShowChart] = useState(true);
  const [showTradeButtons, setShowTradeButtons] = useState(true);
  const [showTokenChat, setShowTokenChat] = useState(true);
  const [showTokenInfo, setShowTokenInfo] = useState(false); // collapsed by default on mobile
  
  // 🎮 PLAYER PORTAL - Stats, playthrough, debug log
  const [showPlayerPortal, setShowPlayerPortal] = useState(false);
  const [playerPortalData, setPlayerPortalData] = useState(null);
  
  // 🎯 TRADING VIEW MODE - Classic vs Focused
  const [tradingViewMode, setTradingViewMode] = useState(() => {
    const saved = localStorage.getItem('paperHands_tradingViewMode');
    return saved || 'focused'; // New users default to focused
  });
  
  // Save trading view mode preference
  useEffect(() => {
    localStorage.setItem('paperHands_tradingViewMode', tradingViewMode);
  }, [tradingViewMode]);
  
  // 📊 CHART VIEW STATE - for scrolling through history
  const [chartViewMode, setChartViewMode] = useState('5m'); // Candle timeframe - 5m is sweet spot
  const [chartRange, setChartRange] = useState('5m'); // '1m', '5m', '15m', '30m', 'all' - visible history range
  const [chartFullscreen, setChartFullscreen] = useState(false); // Fullscreen chart mode
  const [chartScrollOffset, setChartScrollOffset] = useState(0); // candles from the end (0 = latest)
  const [chartZoom, setChartZoom] = useState(1); // 1 = default, <1 = zoomed out, >1 = zoomed in
  const [candleInterval, setCandleInterval] = useState('1m'); // '10s', '30s', '1m', '5m', '15m', '1h'
  const chartDragStart = useRef(null); // for drag-to-scroll on desktop
  const chartDragOffset = useRef(0);
  const pinchStartDistance = useRef(null); // for pinch-to-zoom on mobile
  const pinchStartZoom = useRef(1);
  const chartSvgRef = useRef(null); // for native wheel event
  
  // Lightweight Charts refs
  const lwChartContainerRef = useRef(null);
  const lwChartRef = useRef(null);
  const lwCandleSeriesRef = useRef(null);
  const lwEntryLineRef = useRef(null);
  const lwLastCandleTimeRef = useRef(null); // Track last candle time for efficient updates
  
  // 💼 MULTI-POSITION PORTFOLIO: Track all active positions
  // Loaded from server on auth, no localStorage
  const [positions, setPositions] = useState([]);
  
  // 🧹 CLEANUP: DON'T auto-close positions!
  // The SERVER handles position closure when tokens rug/moon
  // Client should never close positions just because token isn't visible
  // This prevents race conditions where positions get wrongly closed
  
  // 📊 Get current position for the token we're viewing (if any)
  const getCurrentPosition = () => {
    if (!currentToken) return null;
    return positions.find(p => p.tokenId === currentToken.id && p.status === 'active');
  };
  
  // 📊 Legacy position getter for backward compatibility
  const position = getCurrentPosition() || { size: 0, invested: 0, entry: 1.0 };
  
  // 💾 Save positions - just update state, server is source of truth
  const savePositions = (newPositions) => {
    setPositions(newPositions);
  };
  
  // 📊 DAILY STATS: Track rolling 24h performance (server tracks this)
  const [dailyStats, setDailyStats] = useState({
    trades: [],
    totalProfit: 0,
    wins: 0,
    losses: 0,
    rugs: 0,
  });
  
  const saveDailyStats = (newStats) => {
    // Filter to only last 24 hours
    const dayAgo = Date.now() - (24 * 60 * 60 * 1000);
    const recentTrades = newStats.trades.filter(t => t.timestamp > dayAgo);
    const filtered = {
      ...newStats,
      trades: recentTrades,
      totalProfit: recentTrades.reduce((sum, t) => sum + (t.profit || 0), 0),
      wins: recentTrades.filter(t => t.profit > 0).length,
      losses: recentTrades.filter(t => t.profit < 0 && t.type !== 'rugged').length,
      rugs: recentTrades.filter(t => t.type === 'rugged').length,
    };
    setDailyStats(filtered);
  };
  
  const [viewerCount, setViewerCount] = useState(0);
  const [transactions, setTransactions] = useState([]); // Live buy/sell feed
  const [tokenTimeLeft, setTokenTimeLeft] = useState(0); // Time remaining for token
  const [ruggedPlayersCount, setRuggedPlayersCount] = useState(0); // How many got rugged
  
  // Bags - worthless rugged tokens player still "owns" (loaded from server)
  const [bags, setBags] = useState([]);
  const [unclearedRugs, setUnclearedRugs] = useState(0); // Count of rugs user must acknowledge
  const [showRugAcknowledgeModal, setShowRugAcknowledgeModal] = useState(false); // Modal to clear rugs
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [pendingRuggedPosition, setPendingRuggedPosition] = useState(null);
  const [showBags, setShowBags] = useState(false);
  const [showPaperBucksInfo, setShowPaperBucksInfo] = useState(false); // 💵 Paper Bucks explainer
  
  // 🛡️ ADMIN DASHBOARD
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [watchlistData, setWatchlistData] = useState([]);
  const [adminAutoRefresh, setAdminAutoRefresh] = useState(true); // Auto-refresh admin data
  // 💭 PENNY PING STATE
  const [gameThoughts, setGameThoughts] = useState([]);
  const [worldThoughts, setWorldThoughts] = useState([]);
  const [gameThrone, setGameThrone] = useState(null); // 👑 Current throne holder
  const [worldThrone, setWorldThrone] = useState(null);
  const [newThought, setNewThought] = useState('');
  const [thoughtFeed, setThoughtFeed] = useState('game'); // 'game' or 'world'
  const [postingThought, setPostingThought] = useState(false);
  const [showPennyThoughts, setShowPennyThoughts] = useState(false);
  const [thoughtsView, setThoughtsView] = useState('menu'); // 'menu', 'feed', 'mychain'
  const [viewingProfile, setViewingProfile] = useState(null); // { handle, data } for profile view
  const [profileLoading, setProfileLoading] = useState(false);
  
  // 🔍 CHAIN DECODER STATE
  const [showChainDecoder, setShowChainDecoder] = useState(false);
  const [decoderInput, setDecoderInput] = useState('');
  const [decoderLines, setDecoderLines] = useState([]);
  const [decoderLoading, setDecoderLoading] = useState(false);
  const [decoderChain, setDecoderChain] = useState([]);
  
  // Moderator status - must be after adminData useState
  const isModerator = adminData?.userRole === 'moderator';
  const canViewAdminPortal = isAdmin || isModerator;
  
  const [antiExploitSettings, setAntiExploitSettings] = useState(null);
  const [antiExploitLoading, setAntiExploitLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [expandedAdminTokens, setExpandedAdminTokens] = useState({}); // { tokenId: true/false }
  const [adminTab, setAdminTab] = useState('live'); // live, users, tokens, fraud, watchlist (mods); + overview, withdrawals, business (admin only)
  // 🎯 Winner Hunter Admin
  const [winnerHunterConfig, setWinnerHunterConfig] = useState(null);
  const [winnerHunterLoading, setWinnerHunterLoading] = useState(false);
  // 🤖 User Bot Config Admin
  const [userBotConfig, setUserBotConfig] = useState(null);
  // 🎰 AI Token Config Admin
  const [aiTokenConfig, setAiTokenConfig] = useState(null);
  // 👶 Baby Sitting Admin
  const [babysittingData, setBabysittingData] = useState(null);
  const [babysittingLoading, setBabysittingLoading] = useState(false);
  // 🩸 Bleed Config Admin
  const [bleedConfig, setBleedConfig] = useState(null);
  // 👻 Admin Visibility
  const [adminVisible, setAdminVisible] = useState(true);
  // 🎲 Lifecycle Distribution Config Admin
  const [lifecycleConfig, setLifecycleConfig] = useState(null);
  const [lifecyclePreset, setLifecyclePreset] = useState(null); // Track selected preset name
  // 💰 Admin Market Maker Config
  const [adminMmConfig, setAdminMmConfig] = useState(null);
  const [adminMmLoading, setAdminMmLoading] = useState(false);
  // 🎰 Playthrough Stats
  const [playthroughStats, setPlaythroughStats] = useState(null);
  const [playthroughStatsLoading, setPlaythroughStatsLoading] = useState(false);
  // 🚨 Circuit Breaker Admin
  const [circuitBreakerData, setCircuitBreakerData] = useState(null);
  const [circuitBreakerLoading, setCircuitBreakerLoading] = useState(false);
  // 🏆 Trading Wars Admin
  const [warPotData, setWarPotData] = useState(null);
  const [warPotLoading, setWarPotLoading] = useState(false);
  const [warPotAddAmount, setWarPotAddAmount] = useState('');
  const [pennyPingMessage, setPennyPingMessage] = useState('Come play PaperHands!');
  const [pennyPingPreview, setPennyPingPreview] = useState(null);
  const [pennyPingSending, setPennyPingSending] = useState(false);
  const [pennyPingExcludeOnline, setPennyPingExcludeOnline] = useState(true);
  // Bulk credit
  const [showBulkCredit, setShowBulkCredit] = useState(false);
  const [bulkCreditHandles, setBulkCreditHandles] = useState('');
  const [bulkCreditAmount, setBulkCreditAmount] = useState(100000);
  const [bulkCreditPremiumDays, setBulkCreditPremiumDays] = useState(0);
  const [bulkCreditSkipExisting, setBulkCreditSkipExisting] = useState(true);

  // 📢 Admin Broadcast System
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastType, setBroadcastType] = useState('info'); // error, warning, success, info
  const [receivedBroadcast, setReceivedBroadcast] = useState(null); // { text, msgType, from, timestamp }
  const [showBroadcastConfirm, setShowBroadcastConfirm] = useState(false); // Confirm modal

  // 🔒 Admin Lock Balance (exploit remediation)
  const [lockBalanceHandle, setLockBalanceHandle] = useState('');
  const [lockBalanceMultiplier, setLockBalanceMultiplier] = useState('10');
  const [lockBalanceReason, setLockBalanceReason] = useState('Rake exploit');
  const [lockBalanceLoading, setLockBalanceLoading] = useState(false);
  const [onChainCountInput, setOnChainCountInput] = useState('');
  const [onChainCountLoading, setOnChainCountLoading] = useState(false);

  // 📦 Portfolio section collapse states (only collapsible when >2 items)
  const [collapseAIPositions, setCollapseAIPositions] = useState(false);
  const [collapseRugBets, setCollapseRugBets] = useState(false);
  const [collapseUserPositions, setCollapseUserPositions] = useState(false);
  const [collapseRuggedBags, setCollapseRuggedBags] = useState(false);

  // 📊 Portfolio view state
  const [portfolioSort, setPortfolioSort] = useState('value'); // value, pnl, multiplier, alpha
  const [portfolioSearch, setPortfolioSearch] = useState('');
  const [portfolioTab, setPortfolioTab] = useState('ai'); // 'ai' or 'bsv' (bot merged into these)

  // 🤖 Bot Trades state (Redis-backed) - botPositions removed, now unified in positions
  const [botTrades, setBotTrades] = useState([]);
  const [botConfig, setBotConfig] = useState({ takeProfitPercent: 25, stopLossPercent: 15, maxHoldMinutes: 30, sellOnBleed: true, enabled: false });
  const [botLoading, setBotLoading] = useState(false);
  
  // 🏃 Track last sell for "escaped rug" detection
  const lastSellRef = useRef(null); // { tokenId, tokenTicker, profit, timestamp }
  const lastUserTradeRef = useRef(0); // Track when user last traded to prevent candle overwrites
  const tokenChatRef = useRef(null); // For auto-scrolling token chat
  const lobbyChatRef = useRef(null); // For auto-scrolling lobby chat
  const soundPlayedForTokens = useRef(new Set()); // Track ALL tokens we've played rug/moon sound for (prevents repeat)
  const prevOnlineHandlesRef = useRef(new Set()); // Track previous online users for join/leave sounds
  const prevLobbyChatIdsRef = useRef(new Set()); // Track previous lobby chat IDs for new message sounds
  const prevUserTokenIdsRef = useRef(new Set()); // Track previous user-launched token IDs for launch sounds
  
  // 📜 Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (tokenChatRef.current) {
      tokenChatRef.current.scrollTop = tokenChatRef.current.scrollHeight;
    }
  }, [tokenChat]);
  
  useEffect(() => {
    if (lobbyChatRef.current) {
      lobbyChatRef.current.scrollTop = lobbyChatRef.current.scrollHeight;
    }
  }, [lobbyChat]);

  // 🤖 Fetch bot portfolio when switching to bot tab (moved to after fetchBotPortfolio definition)

  // Add rugged tokens to bags (server handles persistence)
  const addToBags = (token, tokensOwned, invested) => {
    const newBag = {
      id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      ticker: token.ticker,
      category: token.category,
      tokensOwned: Math.floor(tokensOwned),
      invested,
      ruggedAt: Date.now(),
      rugType: token.rugType || 'UNKNOWN'
    };
    setBags(prev => [...prev, newBag].slice(-10)); // Keep last 10 bags
  };
  
  // Trash a bag (remove from wallet) - syncs to server
  const trashBag = (bagId) => {
    setBags(prev => prev.filter(b => (b.id || b.ruggedAt) !== bagId));
    // Send to server to persist
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'trash_bag', bagId }));
    }
  };

  // Trash all bags - syncs to server
  const trashAllBags = () => {
    setBags([]);
    // Send to server to persist
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'trash_all_bags' }));
    }
  };
  
  // Muted players (persisted)
  const [mutedPlayers, setMutedPlayers] = useState(() => {
    const saved = localStorage.getItem('paperHands_mutedPlayers');
    return saved ? JSON.parse(saved) : [];
  });
  
  const toggleMutePlayer = (handle) => {
    setMutedPlayers(prev => {
      const newMuted = prev.includes(handle) 
        ? prev.filter(h => h !== handle)
        : [...prev, handle];
      localStorage.setItem('paperHands_mutedPlayers', JSON.stringify(newMuted));
      return newMuted;
    });
  };
  
  const isPlayerMuted = (handle) => mutedPlayers.includes(handle);
  
  // ============== PREMIUM REWARDS ==============
  const [showRewards, setShowRewards] = useState(false);
  const [rewards, setRewards] = useState({ rakeback: 0, cashback: 0, freePlay: 0 });
  const [showWallet, setShowWallet] = useState(false); // 💰 Wallet modal (tap balance to open)
  const [showInactivityWarning, setShowInactivityWarning] = useState(false); // 🔐 Inactivity warning modal
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(null); // Time until auto-logout
  const [showDeposit, setShowDeposit] = useState(false);
  const [pendingDeposit, setPendingDeposit] = useState(null);
  const [showLowBalance, setShowLowBalance] = useState(false); // 💸 Low balance prompt
  const [navHidden, setNavHidden] = useState(false); // Auto-hide bottom nav on scroll
  const lastScrollY = useRef(0);
  const lowBalanceShownRef = useRef(false); // Only show once per session
  
  // 🚀 LAUNCH TOKEN
  const [showLaunchToken, setShowLaunchToken] = useState(false);
  const [showBsvPumpInfo, setShowBsvPumpInfo] = useState(false); // BSV.pump explainer modal
  const [showCreatorDashboard, setShowCreatorDashboard] = useState(false); // Creator Dashboard modal
  const [creatorDashboardIndex, setCreatorDashboardIndex] = useState(0); // Which token to show in creator dashboard
  const [launchForm, setLaunchForm] = useState({ ticker: '', description: '', supply: '10000000', category: 'Meme Coins', initialBuyAmount: 250000 });
  const [launchingToken, setLaunchingToken] = useState(false);
  const [myLaunchedTokens, setMyLaunchedTokens] = useState([]);
  const [tokenViewMode, setTokenViewMode] = useState('ai'); // 'ai' or 'user' - toggle between AI tokens and user-launched tokens
  const [userLaunchedTokens, setUserLaunchedTokens] = useState([]); // Tokens launched by HandCash users
  const [deleteConfirmToken, setDeleteConfirmToken] = useState(null); // Token to confirm deletion
  const [deletingToken, setDeletingToken] = useState(false);
  const [showTokenHistory, setShowTokenHistory] = useState(false);
  const [tokenHistory, setTokenHistory] = useState({ history: [], stats: {} });
  
  // 🎯 PREMIUM COMPARISON: Show side-by-side benefits
  const [showPremiumComparison, setShowPremiumComparison] = useState(false);
  const [premiumPricing, setPremiumPricing] = useState(null); // { weekly: {...}, monthly: {...} }
  
  // 🤖 TRADING BOT STATE
  const [showTradingBot, setShowTradingBot] = useState(false);
  const [tradingBotData, setTradingBotData] = useState(null);
  const [tradingBotLoading, setTradingBotLoading] = useState(false);
  const [botControlEnabled, setBotControlEnabled] = useState(false); // Auto-nav OFF by default now
  const [botViewMode, setBotViewMode] = useState('auto-nav'); // 'auto-nav' or 'mini-player'
  const [botLastTrade, setBotLastTrade] = useState(null); // Track last trade for toast
  
  // 🎯 TAB FOCUS & IDLE DETECTION - Bot only trades when user is actively engaged
  const [isTabFocused, setIsTabFocused] = useState(document.hasFocus());
  const lastUserActivityRef = useRef(Date.now());
  const IDLE_TIMEOUT_MS = 60000; // 60 seconds of no activity = idle (for bot)
  const IDLE_KICK_MS = 5 * 60 * 1000; // 5 minutes = kick user (stop all polling)
  const [isIdleKicked, setIsIdleKicked] = useState(false); // Show "you're idle" modal
  const [idleKickCountdown, setIdleKickCountdown] = useState(60); // 60s countdown before force logout
  const [lastBotCommand, setLastBotCommand] = useState(null); // Track last processed command
  const [botFocusedTokenId, setBotFocusedTokenId] = useState(null); // Token the bot is looking at
  const [tradeNotification, setTradeNotification] = useState(null); // { tokenId, ticker, type: 'buy'|'sell', amount, rake, profit, isPvP, isBot, narration, timestamp }
  const [houseCreditAnimation, setHouseCreditAnimation] = useState(null); // { amount, from, ticker, timestamp }

  // 🚀 RUG RUSH (Crash Game) STATE
  const [showRugRush, setShowRugRush] = useState(false);
  const [rugRushState, setRugRushState] = useState(null); // { round, history, config, taunts }
  const [rugRushBetAmount, setRugRushBetAmount] = useState(50000); // Default 50K PB bet
  const [rugRushBetting, setRugRushBetting] = useState(false);
  const [rugRushCashingOut, setRugRushCashingOut] = useState(false);
  const [rugRushMyBet, setRugRushMyBet] = useState(null); // Current round bet
  const [rugRushResult, setRugRushResult] = useState(null); // Last round result message
  const [rugRushShowHelp, setRugRushShowHelp] = useState(false); // Show instructions
  const [rugRushTaunting, setRugRushTaunting] = useState(false); // Sending taunt
  const [rugRushShowTaunts, setRugRushShowTaunts] = useState(false); // Show taunt picker
  const [rugRushTauntTarget, setRugRushTauntTarget] = useState(null); // Target for taunt
  const [rugRushKillFeed, setRugRushKillFeed] = useState([]); // Kill feed messages
  const [rugRushLastMan, setRugRushLastMan] = useState(null); // Last man standing from previous round
  const [rugRushAutoCashout, setRugRushAutoCashout] = useState(null); // Auto-cashout target multiplier
  const [rugRushCountdown, setRugRushCountdown] = useState(null); // Countdown seconds remaining
  const [rugRushWinner, setRugRushWinner] = useState(null); // 🏆 Winner announcement popup
  const [rugRushSelectedTier, setRugRushSelectedTier] = useState('bronze'); // Player's selected tier
  const rugRushLastWinnerIdRef = useRef(null); // Track last announced winner to avoid duplicates

  // 🏔️ MOUNTAIN CLIMBER (New Rug Rush Battle Royale) STATE
  const [showMountainClimber, setShowMountainClimber] = useState(false);

  // 👁️ GAME VISIBILITY (admin can hide game buttons from regular users)
  const [gameVisibility, setGameVisibility] = useState({ rugRushVisible: true, mountainVisible: true });

  // 🎟️ SCRATCH-OFF STATE
  const [scratcherOffer, setScratcherOffer] = useState(null); // { scratcherId, reason, cost }
  const [showScratcher, setShowScratcher] = useState(false);
  const [scratcherRevealing, setScratcherRevealing] = useState(false);
  const [scratcherResult, setScratcherResult] = useState(null); // { prize, message }
  const [scratcherCards, setScratcherCards] = useState([false, false, false, false, false, false]); // Which cards are scratched

  // 🎛️ QUICK ACTIONS: More dropdown menu state
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Daily claim state - loaded from server on auth
  const [lastDailyClaim, setLastDailyClaim] = useState(0);
  const [totalDailyClaims, setTotalDailyClaims] = useState(0);
  const [lastPremiumPurchase, setLastPremiumPurchase] = useState(0);
  const [dailyClaimPending, setDailyClaimPending] = useState(false);
  const [dailyClaimResult, setDailyClaimResult] = useState(null); // Store claim result for display
  const [showClaimSuccess, setShowClaimSuccess] = useState(false);
  
  // 🛡️ STREAK PROTECTION: Premium users get one free pass per day
  const [lastStreakProtectionUsed, setLastStreakProtectionUsed] = useState(() => {
    const saved = localStorage.getItem('paperHands_lastStreakProtection');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [showStreakProtected, setShowStreakProtected] = useState(false);
  
  // Check if streak protection is available today (premium + not used today)
  const canUseStreakProtection = () => {
    if (!isPremium) return false;
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    return now - lastStreakProtectionUsed > oneDayMs;
  };
  
  // Use streak protection
  const useStreakProtection = () => {
    const now = Date.now();
    setLastStreakProtectionUsed(now);
    localStorage.setItem('paperHands_lastStreakProtection', now.toString());
    setShowStreakProtected(true);
    setTimeout(() => setShowStreakProtected(false), 3000);
  };
  
  // Check if premium is active (either from purchase or restored from payment history)
  const hasRecentPremium = () => {
    return isPremium; // Just check if premium is active
  };
  
  // Check if can claim today (must have recent premium + not claimed today)
  const canClaimDaily = () => {
    if (!hasRecentPremium()) return false;
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    return now - lastDailyClaim > oneDayMs;
  };
  
  // Get reason why can't claim
  const getClaimStatus = () => {
    if (!hasRecentPremium()) return 'needsPremium';
    if (!canClaimDaily()) return 'alreadyClaimed';
    return 'canClaim';
  };
  
  // Format time until next claim
  const getNextClaimTime = () => {
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    const nextClaim = lastDailyClaim + oneDayMs;
    const remaining = nextClaim - now;
    if (remaining <= 0) return null;
    const hours = Math.floor(remaining / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    return `${hours}h ${minutes}m`;
  };
  
  // Calculate potential daily reward for display
  // MUST MATCH SERVER VALUES in /api/daily-claim!
  const getPotentialDailyReward = () => {
    const baseReward = 100000; // 100K sats = $100K PB base
    const streakBonus = Math.min(totalDailyClaims * 10000, 100000); // +$10K per day, max +$100K
    const subtotal = baseReward + streakBonus;
    const multiplier = isPremium ? 2 : 1;
    return {
      base: baseReward,
      streakBonus,
      multiplier,
      total: subtotal * multiplier
    };
  };
  
  // Claim daily reward - NOW REAL via server!
  const claimDailyReward = async () => {
    console.log('🎁 Attempting daily claim...', { 
      canClaim: canClaimDaily(), 
      isPremium, 
      lastDailyClaim: lastDailyClaim ? new Date(lastDailyClaim).toLocaleString() : 'never',
      sessionId: !!sessionId 
    });
    
    if (!canClaimDaily()) {
      console.log('🎁 Cannot claim:', !isPremium ? 'not premium' : 'already claimed today');
      return;
    }
    if (dailyClaimPending || !sessionId || !API_URL) {
      console.log('🎁 Cannot claim:', dailyClaimPending ? 'pending' : !sessionId ? 'no session' : 'no API');
      return;
    }
    
    setDailyClaimPending(true);
    
    try {
      const res = await fetch(`${API_URL}/api/daily-claim`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Claim failed');
      }
      
      // Update game wallet with real reward
      setGameWallet(data.newBalance);
      showBalanceChange(data.reward); // 💫 Animate daily bonus!
      Sounds.win(); // 🏆 Play reward sound!
      
      // Record claim time and update streak
      setLastDailyClaim(Date.now());
      setTotalDailyClaims(data.streakDays);
      
      // Store result for display
      setDailyClaimResult({
        reward: data.reward,
        baseReward: data.baseReward || 1000,
        streakBonus: data.streakBonus,
        streakDays: data.streakDays,
        premiumMultiplier: data.premiumMultiplier || 1,
      });
      setShowClaimSuccess(true);
      
      console.log(`🎁 Daily claim: +${data.reward} sats (streak: ${data.streakDays}, premium: ${data.premiumMultiplier}x)`);
      
      // Hide success after 8 seconds
      setTimeout(() => setShowClaimSuccess(false), 8000);
      
    } catch (error) {
      console.error('Daily claim failed:', error);
      Sounds.error(); // ❌ Play error sound
      alert(error.message || 'Failed to claim. Please try again.');
    } finally {
      setDailyClaimPending(false);
    }
  };
  const [showPepeCelebration, setShowPepeCelebration] = useState(false);
  
  // 🐸 PEPE TOAST SYSTEM - Reusable mascot celebrations
  const [pepeToast, setPepeToast] = useState(null); // { type, message, direction, duration }
  
  const PEPE_MESSAGES = {
    welcomeBack: [
      "gm fren, ready to get rugged? 🐸",
      "back for more pain? let's go 💀",
      "the degen returns... 🎰",
      "missed u fren, now ape in 🦍",
      "oh no... you're back 😏",
    ],
    firstDeposit: [
      "fresh meat... I mean, welcome! 🥩",
      "let's goooo 🚀",
      "a new challenger appears! 🎮",
      "time to lose- I mean, trade! 💸",
    ],
    bigDeposit: [
      "whale alert 🐋",
      "ok big spender 💰",
      "sheeeesh look at this baller 👀",
      "the bags are HEAVY 🏋️",
      "making it rain! 💵",
    ],
    referralBonus: [
      "free sats! thanks for joining 🎁",
      "referral bonus activated! 💰",
      "welcome gift secured 🐸",
      "starting with extra bags! 💼",
      "fren sent you sats! 🤝",
    ],
    firstWin: [
      "not bad for a normie 😏",
      "beginner's luck... 🍀",
      "wait that actually worked?! 😲",
      "one of us! one of us! 🐸",
    ],
    win: [
      "nice one 💰",
      "cha-ching! 🤑",
      "that's how it's done 🐸",
      "profit secured 📈",
      "W 🏆",
    ],
    doubleUp: [
      "2X BABY 🎯",
      "doubled up like a boss 😎",
      "this is the way 🚀",
      "easy money 💵",
      "DOUBLED 📈📈",
    ],
    solidGain: [
      "solid flip 👍",
      "clean trade 🧹",
      "profit is profit 💰",
      "green candle energy 🟢",
      "nice bag 💼",
    ],
    bigBag: [
      "FAT BAG 💰💰💰",
      "look at those gains 👀",
      "making BANK 🏦",
      "the bag is SECURED 💼",
      "absolute unit of a trade 📊",
    ],
    bigMoon: [
      "WAGMI 🚀🌙",
      "absolute legend 👑",
      "he's HIM 😤",
      "teach me your ways sensei 🙏",
      "GENERATIONAL WEALTH 💎",
    ],
    escaped: [
      "galaxy brain move 🧠",
      "you saw it coming 👀",
      "smarter than the average frog 🐸",
      "the prophecy was true... 📜",
    ],
    smartExit: [
      "smart to cut losses 🧠",
      "live to trade another day 💪",
      "knowing when to fold 🃏",
      "better luck next time 🍀",
      "damage control activated 🛡️",
    ],
    winStreak: [
      "unstoppable 🔥",
      "is this guy cheating?! 🤔",
      "the hot hand is REAL 🖐️",
      "on a heater! 🌶️",
    ],
    gotRugged: [
      "it's ok we've all been there 🫂",
      "maybe touch grass? 🌱",
      "F in chat 💀",
      "skill issue tbh 📉",
      "thank you for your donation 🙏",
    ],
    withdrawal: [
      "smart frog takes profits 🧠",
      "don't spend it all on jpegs 🖼️",
      "gg, come back soon 👋",
      "securing the bag 💼",
    ],
    ragequit: [
      "running away with your 73 cents? 💀",
      "the game will miss you... actually no it won't 🐸",
      "quitter mentality detected 📉",
      "imagine not being able to handle a little rug 🤡",
      "tell your therapist I said hi 👋",
      "this will haunt you at 3am tonight 😈",
      "you'll be back. they always come back. 🎭",
      "go touch grass, you need it 🌱",
      "the devs thank you for your liquidity exit 💸",
      "weak hands don't deserve profits anyway 🧻",
    ],
    achievement: [
      "ACHIEVEMENT UNLOCKED 🏆",
      "you're actually good at this?! 😳",
      "added to the trophy case 🏅",
      "flex worthy 💪",
    ],
    premium: [
      "welcome to the club 👑",
      "big brain purchase 🧠",
      "now you're playing with power 💎",
    ],
    apeIn: [
      "LFG 🦍",
      "aping in! 🚀",
      "this is the way 🐸",
      "degen mode activated 🎰",
    ],
    bigApe: [
      "WHALE APING 🐋🦍",
      "absolute UNIT of a buy 📊",
      "the chad move 💪",
      "BIG MONEY MOVES 💰💰",
      "this guy means business 😤",
    ],
  };
  
  // Show Pepe toast with random message from pool
  const showPepeToast = (type, direction = 'right', duration = 4000, customMessage = null) => {
    const messages = PEPE_MESSAGES[type] || ["🐸"];
    const message = customMessage || messages[Math.floor(Math.random() * messages.length)];
    setPepeToast({ type, message, direction, duration });
    setTimeout(() => setPepeToast(null), duration);
  };
  const [wasAlreadyPremium, setWasAlreadyPremium] = useState(false);
  
  // Win/Loss celebrations
  const [showWinCelebration, setShowWinCelebration] = useState(null); // { profit, multiplier, type }
  const [showLossHumiliation, setShowLossHumiliation] = useState(null); // { loss, rugType }
  const [showPaperHandsLoss, setShowPaperHandsLoss] = useState(null); // { loss, multiplier, tokenFate }
  const [showEscapePepe, setShowEscapePepe] = useState(null); // { tokenTicker, timeBefore, profit }
  
  // 🎮 TEST RECORDER STATE
  const [showTestRecorder, setShowTestRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [testEvents, setTestEvents] = useState([]);
  const [testFlags, setTestFlags] = useState([]);
  const [showFlagInput, setShowFlagInput] = useState(false);
  const [flagNote, setFlagNote] = useState('');
  const testSessionStart = useRef(null);
  const lastTestState = useRef({});
  
  // 🧪 BETA TESTERS PANEL STATE
  const [showBetaPanel, setShowBetaPanel] = useState(false);
  const [betaStats, setBetaStats] = useState(null);
  
  const [showSupermoonCelebration, setShowSupermoonCelebration] = useState(null); // { profit, multiplier }
  const [elapsedTime, setElapsedTime] = useState(0); // For slippage display
  
  // ============== UI STATE ==============
  const [timeframe, setTimeframe] = useState('5s');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState({ topProfits: [], dailyProfits: [], diamondHands: [], hallOfShame: [] });
  const [showAchievements, setShowAchievements] = useState(false);
  const [showTournaments, setShowTournaments] = useState(false);
  const [tournaments, setTournaments] = useState([]); // Tournament list (empty until feature launches)
  const [registeredTournaments, setRegisteredTournaments] = useState([]); // IDs user registered for
  
  // 🏆 TRADING WARS
  const [showTradingWars, setShowTradingWars] = useState(false);
  const [tradingWarsData, setTradingWarsData] = useState(null);
  const [tradingWarsLoading, setTradingWarsLoading] = useState(false);
  
  const [showReferrals, setShowReferrals] = useState(false);
  const [newAchievement, setNewAchievement] = useState(null);
  
  // ============== ACHIEVEMENTS - PERSISTED ==============
  const defaultAchievements = {
    firstBlood: false,
    diamondHands: false,
    paperHandsShame: false,
    sniper: false,
    rugSurvivor: false,
    clownCollege: false,
    whale: false,
    moonshotMaster: false,
    onFire: false,
    iceCold: false,
    centurion: false,
    bigSpender: false,
    survivor: false,
    rugLord: false,
    speedDemon: false,
  };
  
  // Achievements - loaded from server on auth
  const [achievements, setAchievements] = useState(defaultAchievements);
  
  // Stats - loaded from server on auth
  const defaultStats = {
    totalTrades: 0,
    wins: 0,
    losses: 0,
    rugs: 0,
    winStreak: 0,
    lossStreak: 0,
    bestWinStreak: 0,
    worstLossStreak: 0,
    paperHandsSells: 0,
    biggestWin: 0,
    biggestLoss: 0,
    totalProfit: 0,
    highestMultiplier: 0,
    tradesWithoutRug: 0,
    rugsByType: {
      HARD: { times: 0, totalLost: 0, avgReactionTime: 0 },
      TRAP: { times: 0, totalLost: 0, fellForPump: 0 },
      SLOW: { times: 0, totalLost: 0, couldHaveExited: 0 },
      HONEYPOT: { times: 0, totalLost: 0, sellAttempts: 0 },
    },
    survivalRate: 100,
    rugsAvoided: 0,
    closeCallsSeconds: [],
    tipsSent: 0,
    tipsReceived: 0,
  };
  
  // Stats - loaded from server, no localStorage
  const [stats, setStats] = useState(defaultStats);
  
  // Calculate weakness
  const getWeakness = () => {
    const { rugsByType } = stats;
    const total = Object.values(rugsByType).reduce((sum, r) => sum + r.times, 0);
    if (total === 0) return null;
    
    let worst = { type: null, percent: 0 };
    Object.entries(rugsByType).forEach(([type, data]) => {
      const percent = (data.times / total) * 100;
      if (percent > worst.percent) {
        worst = { type, percent, times: data.times, lost: data.totalLost };
      }
    });
    return worst;
  };
  
  // Global leaderboard for "Unruggable" players - fetched from server
  const [showUnruggable, setShowUnruggable] = useState(false);
  const [unruggableLeaderboard, setUnruggableLeaderboard] = useState([]);
  
  const ACHIEVEMENT_INFO = {
    firstBlood: { icon: '🏆', name: 'First Blood', desc: 'Win your first trade', tier: 'bronze', requirement: 1, stat: 'wins' },
    diamondHands: { icon: '💎', name: 'Diamond Hands', desc: 'Hold through 50% dip and profit', tier: 'gold', requirement: 1, stat: 'special' },
    paperHandsShame: { icon: '📄', name: 'Paper Hands', desc: 'Sell 10 times under 1.2x (shame!)', tier: 'shame', requirement: 10, stat: 'paperHands' },
    sniper: { icon: '🎯', name: 'Sniper', desc: 'Sell within 5% of the top', tier: 'gold', requirement: 1, stat: 'special' },
    rugSurvivor: { icon: '🏃', name: 'Escape Artist', desc: 'Sell a token that rugs within 60 seconds', tier: 'legendary', requirement: 1, stat: 'special' },
    clownCollege: { icon: '🤡', name: 'Clown College', desc: 'Get rugged 10 times', tier: 'shame', requirement: 10, stat: 'rugs' },
    whale: { icon: '🐋', name: 'Whale', desc: 'Single trade profit $10+ PB', tier: 'gold', requirement: 10000, stat: 'biggestProfit' },
    moonshotMaster: { icon: '🌙', name: 'Moonshot Master', desc: 'Hit a 5x+ multiplier', tier: 'legendary', requirement: 5, stat: 'highestMultiplier' },
    onFire: { icon: '🔥', name: 'On Fire', desc: '5 winning trades in a row', tier: 'gold', requirement: 5, stat: 'winStreak' },
    iceCold: { icon: '❄️', name: 'Ice Cold', desc: '5 losses in a row (oof)', tier: 'shame', requirement: 5, stat: 'lossStreak' },
    centurion: { icon: '💯', name: 'Centurion', desc: 'Complete 100 trades', tier: 'silver', requirement: 100, stat: 'totalTrades' },
    bigSpender: { icon: '💸', name: 'Big Spender', desc: 'Deposit $100+ PB total', tier: 'silver', requirement: 100000, stat: 'totalDeposited' },
    survivor: { icon: '🛡️', name: 'Survivor', desc: '10 wins without getting rugged', tier: 'gold', requirement: 10, stat: 'winsWithoutRug' },
    rugLord: { icon: '👑', name: 'Rug Lord', desc: 'Get rugged 50 times (legend)', tier: 'legendary', requirement: 50, stat: 'rugs' },
    speedDemon: { icon: '⚡', name: 'Speed Demon', desc: 'Sell within 5 seconds', tier: 'bronze', requirement: 1, stat: 'special' },
  };
  
  const unlockAchievement = async (key) => {
    if (achievements[key]) return; // Already unlocked
    
    // Update local state immediately for responsiveness
    setAchievements(prev => ({ ...prev, [key]: true }));
    setNewAchievement(ACHIEVEMENT_INFO[key]);
    setTimeout(() => setNewAchievement(null), 3000);
    
    // 🐸 PEPE CELEBRATES ACHIEVEMENTS!
    setTimeout(() => showPepeToast('achievement', 'right', 3500), 2500);
    
    // 🏆 ANNOUNCE ACHIEVEMENT IN LOBBY!
    const info = ACHIEVEMENT_INFO[key];
    if (info && handcashUser?.handle) {
      const achievementMessages = [
        `🏆 ${handcashUser.handle} unlocked "${info.name}"! ${info.icon}`,
        `🎖️ ${handcashUser.handle} earned the ${info.name} badge! ${info.icon}`,
        `✨ Achievement unlocked! ${handcashUser.handle} got ${info.name} ${info.icon}`,
      ];
      const msg = achievementMessages[Math.floor(Math.random() * achievementMessages.length)];
      
      setLobbyChat(prev => [...prev.slice(-25), {
        id: 'achievement_' + Date.now(),
        handle: '🏆',
        text: msg,
        isAction: true,
        isTradeAnnouncement: true,
        isWin: true,
      }]);
    }
    
    // Save to server (fire and forget)
    if (sessionId && API_URL) {
      try {
        await fetch(`${API_URL}/api/achievement`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Session-Id': sessionId 
          },
          body: JSON.stringify({ achievementId: key })
        });
        console.log(`🏆 Achievement saved to server: ${key}`);
      } catch (e) {
        console.log('Achievement save failed:', e.message);
      }
    }
  };
  
  // 🎮 TEST RECORDER - Keyboard listener
  useEffect(() => {
    const handleTestKey = (e) => {
      if (e.key === '`') {
        setShowTestRecorder(prev => !prev);
      }
      if (e.key === 'f' && !showFlagInput && isRecording) {
        e.preventDefault();
        setShowFlagInput(true);
      }
    };
    window.addEventListener('keydown', handleTestKey);
    return () => window.removeEventListener('keydown', handleTestKey);
  }, [showFlagInput, isRecording]);
  
  // 🎮 TEST RECORDER - State change tracking
  useEffect(() => {
    if (!isRecording) return;
    
    const currentState = {
      view,
      gameState,
      tokenTicker: currentToken?.ticker,
      currentPrice: currentPrice?.toFixed(3),
      positionSize: position?.size?.toFixed(0),
      invested: position?.invested,
      activePositions: positions?.filter(p => p.status === 'active').length,
      wallet: gameWallet,
      bags: bags?.length,
    };
    
    const changes = [];
    Object.keys(currentState).forEach(key => {
      if (lastTestState.current[key] !== currentState[key] && lastTestState.current[key] !== undefined) {
        changes.push({ field: key, from: lastTestState.current[key], to: currentState[key] });
      }
    });
    
    if (changes.length > 0) {
      const timestamp = Date.now() - testSessionStart.current;
      setTestEvents(prev => [...prev.slice(-100), {
        id: Date.now(),
        time: Math.floor(timestamp / 1000),
        type: 'CHANGE',
        changes
      }]);
    }
    
    lastTestState.current = currentState;
  }, [isRecording, view, gameState, currentToken, currentPrice, position, positions, gameWallet, bags]);
  
  // 🎮 TEST RECORDER - Helper functions
  const startTestRecording = () => {
    testSessionStart.current = Date.now();
    setTestEvents([{ id: Date.now(), time: 0, type: 'START', snapshot: { view, gameState, wallet: gameWallet } }]);
    setTestFlags([]);
    setIsRecording(true);
    lastTestState.current = {};
  };
  
  const stopTestRecording = () => {
    setTestEvents(prev => [...prev, { id: Date.now(), time: Math.floor((Date.now() - testSessionStart.current) / 1000), type: 'END' }]);
    setIsRecording(false);
  };
  
  const addTestFlag = () => {
    if (!flagNote.trim()) return;
    const time = Math.floor((Date.now() - testSessionStart.current) / 1000);
    const newFlag = { id: Date.now(), time, note: flagNote, context: { view, gameState, token: currentToken?.ticker, price: currentPrice?.toFixed(3), position: position?.invested, wallet: gameWallet } };
    setTestFlags(prev => [...prev, newFlag]);
    setTestEvents(prev => [...prev, { id: Date.now(), time, type: 'FLAG', note: flagNote }]);
    setFlagNote('');
    setShowFlagInput(false);
  };
  
  const copyTestReport = () => {
    const flagsText = testFlags.length === 0 
      ? 'No issues! 🎉' 
      : testFlags.map(f => 
          `### [${f.time}s] "${f.note}"\n- View: ${f.context.view} | State: ${f.context.gameState}\n- Token: ${f.context.token || 'none'} | Price: ${f.context.price || 'n/a'}\n- Position: ${f.context.position || 0} sats | Wallet: ${f.context.wallet} sats\n`
        ).join('');
    
    const eventsText = testEvents.slice(-50).map(e => {
      if (e.type === 'START') return `[${e.time}s] 🎬 SESSION START`;
      if (e.type === 'END') return `[${e.time}s] 🛑 SESSION END`;
      if (e.type === 'FLAG') return `[${e.time}s] 🚩 FLAG: "${e.note}"`;
      if (e.type === 'CHANGE') return `[${e.time}s] 📍 ${e.changes.map(c => c.field + ': ' + c.from + ' → ' + c.to).join(', ')}`;
      return `[${e.time}s] ${e.type}`;
    }).join('\n');
    
    const report = `# 🎮 PAPER HANDS TEST REPORT
## ${new Date().toLocaleString()}

## 🚩 ISSUES FLAGGED (${testFlags.length})
${flagsText}

## 📜 EVENT LOG (last 50)
${eventsText}

---
Paste this to Claude for fixes!`;
    
    navigator.clipboard.writeText(report);
    alert('Report copied! Paste to Claude.');
  };
  
  // 🎮 TEST RECORDER UI - Defined here so it's available in all views
  const TestRecorderUI = () => (
    <>
      {/* 🧪 BETA TESTERS BUTTON - Moved to header */}
      
      {/* 🧪 BETA TESTERS PANEL - Simplified onboarding modal */}
      {showBetaPanel && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
          <div style={{ 
            backgroundColor: '#0a0a0f', 
            border: '2px solid #9b59b6', 
            borderRadius: '16px', 
            padding: '28px', 
            width: '380px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <h2 style={{ margin: 0, color: '#9b59b6', fontSize: '18px', fontWeight: '700', letterSpacing: '-0.3px' }}>🧪 Beta Testers Hub</h2>
                <div style={{ color: '#666', fontSize: '12px', marginTop: '4px', fontWeight: '400' }}>Help us squash bugs & shape the game!</div>
              </div>
              <button 
                onClick={() => setShowBetaPanel(false)} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#666', 
                  fontSize: '20px', 
                  cursor: 'pointer',
                  padding: '0',
                  lineHeight: '1'
                }}
              >×</button>
            </div>
            
            {/* How It Works */}
            <div style={{ 
              backgroundColor: '#111', 
              borderRadius: '12px', 
              padding: '16px', 
              marginBottom: '20px',
              border: '1px solid #222'
            }}>
              <div style={{ color: '#888', fontSize: '11px', fontWeight: '600', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>How it works</div>
              <div style={{ fontSize: '13px', color: '#aaa', lineHeight: '1.7' }}>
                <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
                  <span style={{ color: '#9b59b6', fontWeight: '600' }}>1.</span>
                  <span>Click <strong style={{ color: '#00d26a' }}>START</strong> below to begin recording</span>
                </div>
                <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
                  <span style={{ color: '#9b59b6', fontWeight: '600' }}>2.</span>
                  <span>Play normally — a recorder panel appears in the top-right</span>
                </div>
                <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
                  <span style={{ color: '#9b59b6', fontWeight: '600' }}>3.</span>
                  <span>Press <code style={{ backgroundColor: '#1a1a2e', padding: '2px 8px', borderRadius: '4px', color: '#f0b90b', fontSize: '12px', fontWeight: '600' }}>F</code> when you spot a bug</span>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <span style={{ color: '#9b59b6', fontWeight: '600' }}>4.</span>
                  <span>Stop & copy the report to share with us</span>
                </div>
              </div>
            </div>
            
            {/* Tip */}
            <div style={{ 
              backgroundColor: '#0f0a15', 
              borderRadius: '8px', 
              padding: '12px 14px', 
              marginBottom: '20px',
              border: '1px solid #9b59b622',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '14px' }}>💡</span>
              <span style={{ color: '#888', fontSize: '12px' }}>
                Press <code style={{ backgroundColor: '#1a1a2e', padding: '2px 6px', borderRadius: '4px', color: '#f0b90b', fontSize: '11px' }}>`</code> anytime to toggle the recorder
              </span>
            </div>
            
            {/* START Button */}
            <button 
              onClick={() => { 
                setShowBetaPanel(false); 
                setShowTestRecorder(true);
                setTimeout(() => startTestRecording(), 100);
              }}
              style={{ 
                width: '100%',
                padding: '14px 20px', 
                borderRadius: '10px', 
                border: 'none', 
                backgroundColor: '#00d26a',
                color: '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0,210,106,0.3)'
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#00e676'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#00d26a'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <span style={{ fontSize: '16px' }}>▶</span>
              START RECORDING
            </button>
            
            {/* Version Info */}
            <div style={{ textAlign: 'center', marginTop: '20px', color: '#333', fontSize: '10px', fontWeight: '500' }}>
              Paper Hands v0.16 • Built with 💎 by degens, for degens
            </div>
          </div>
        </div>
      )}
      
      {/* Flag input modal */}
      {showFlagInput && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
          <div style={{ backgroundColor: '#1a1a2e', border: '2px solid #ff3b69', borderRadius: '12px', padding: '20px', width: '350px' }}>
            <div style={{ color: '#ff3b69', fontWeight: 'bold', marginBottom: '12px' }}>🚩 Flag Issue</div>
            <input autoFocus value={flagNote} onChange={e => setFlagNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addTestFlag(); if (e.key === 'Escape') setShowFlagInput(false); }} placeholder="Describe the bug..." style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #333', backgroundColor: '#0a0a0f', color: '#fff', marginBottom: '12px', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={addTestFlag} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: 'none', backgroundColor: '#ff3b69', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>Add Flag</button>
              <button onClick={() => setShowFlagInput(false)} style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #333', backgroundColor: 'transparent', color: '#888', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Recorder panel */}
      {showTestRecorder && (
        <div style={{ position: 'fixed', top: '10px', right: '10px', width: '280px', maxHeight: '70vh', backgroundColor: '#0a0a0f', border: '1px solid #333', borderRadius: '12px', padding: '12px', zIndex: 10000, overflow: 'auto', fontFamily: "'SF Mono', monospace", fontSize: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>🎮 Test Recorder {isRecording && <span style={{ color: '#ff3b69' }}>● REC</span>}</div>
            <button onClick={() => setShowTestRecorder(false)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
          
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
            {!isRecording ? (
              <button onClick={startTestRecording} style={{ flex: 1, padding: '8px', borderRadius: '6px', border: 'none', backgroundColor: '#00d26a', color: '#000', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>▶ Start</button>
            ) : (
              <>
                <button onClick={stopTestRecording} style={{ flex: 1, padding: '8px', borderRadius: '6px', border: 'none', backgroundColor: '#ff3b69', color: '#fff', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>⏹ Stop</button>
                <button onClick={() => setShowFlagInput(true)} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #ff3b69', backgroundColor: 'transparent', color: '#ff3b69', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>🚩 F</button>
              </>
            )}
          </div>
          
          <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', marginBottom: '10px', fontSize: '9px' }}>
            <div style={{ color: '#00d26a' }}>View: {view} | State: {gameState}</div>
            <div style={{ color: '#f0b90b' }}>Token: {currentToken?.ticker || '-'} @ {currentPrice?.toFixed(3) || '-'}</div>
            <div style={{ color: '#888' }}>Position: {position?.invested || 0} sats | Wallet: {gameWallet}</div>
          </div>
          
          {testFlags.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ color: '#ff3b69', fontWeight: 'bold', marginBottom: '4px', fontSize: '10px' }}>🚩 Flags ({testFlags.length})</div>
              <div style={{ maxHeight: '80px', overflow: 'auto' }}>
                {testFlags.map(f => (
                  <div key={f.id} style={{ backgroundColor: '#1a0a0a', borderRadius: '4px', padding: '4px 6px', marginBottom: '3px', fontSize: '9px' }}>
                    <span style={{ color: '#666' }}>[{f.time}s]</span> <span style={{ color: '#ff6b6b' }}>{f.note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div style={{ color: '#555', marginBottom: '8px', fontSize: '9px' }}>
            Events: {testEvents.length} {isRecording && testSessionStart.current && `| ${Math.floor((Date.now() - testSessionStart.current) / 1000)}s`}
          </div>
          
          {testEvents.length > 0 && (
            <button onClick={copyTestReport} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #f0b90b', backgroundColor: 'transparent', color: '#f0b90b', cursor: 'pointer', fontSize: '10px' }}>📋 Copy Report for Claude</button>
          )}
          
          <div style={{ marginTop: '10px', padding: '6px', backgroundColor: '#0a1510', borderRadius: '6px', fontSize: '8px', color: '#555' }}>
            <strong>Keys:</strong> ` toggle | F flag bug<br/>
            <strong>Flow:</strong> Start → Play → F when bug → Stop → Copy → Paste to Claude
          </div>
        </div>
      )}
    </>
  );
  
  // ============== TOURNAMENTS ==============
  // ============== DAILY CHALLENGES ==============
  // Now server-side - just store the challenges array
  const [dailyChallenges, setDailyChallenges] = useState([]);
  const [showChallenges, setShowChallenges] = useState(false);
  const [challengeToast, setChallengeToast] = useState(null);
  
  // NOTE: Fetch challenges useEffect is defined later, after sessionId and API_URL
  
  // Challenge progress is now tracked server-side via trades/rugs/profit
  
  // Claim challenge reward
  const claimChallengeReward = async (challengeId) => {
    const challenge = dailyChallenges.find(c => c.id === challengeId);
    if (!challenge || challenge.claimed || challenge.progress < challenge.target) return;
    
    // Call server to claim
    if (sessionId && API_URL) {
      try {
        const res = await fetch(`${API_URL}/api/challenges/claim`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Session-Id': sessionId 
          },
          body: JSON.stringify({ challengeId })
        });
        
        const data = await res.json();
        
        if (!res.ok) {
          console.log('Challenge claim failed:', data.error);
          return;
        }
        
        // Update balance from server response
        setGameWallet(data.newBalance);
        showBalanceChange(data.reward); // 💫 Animate reward!
        
        // Mark challenge as claimed locally
        setDailyChallenges(prev => prev.map(c => 
          c.id === challengeId ? { ...c, claimed: true } : c
        ));
        
        console.log(`🎯 Challenge claimed: ${challengeId} (+${data.reward} sats)`);
        
        // Announce in lobby
        setLobbyChat(prev => [...prev.slice(-25), {
          id: 'challenge_' + Date.now(),
          handle: '🎯',
          text: `${handcashUser?.handle || 'Someone'} completed "${challenge.label}" and earned ${formatPB(data.reward)}! 💰`,
          isAction: true,
          isTradeAnnouncement: true,
          isWin: true,
        }]);
        
      } catch (e) {
        console.log('Challenge claim error:', e.message);
      }
    }
  };
  
  // (Tournaments modal still works but replaced with Challenges in UI)
  
  // ============== REFERRALS ==============
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState({ 
    referred: 0, 
    earned: 0, 
    pending: 0,
    referrals: [],
    referredBy: null,
    referralLink: ''
  });
  const [pendingReferralCode, setPendingReferralCode] = useState(() => {
    // Check URL for ref param on mount
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) {
        console.log('🔗 Found referral code in URL:', ref);
        localStorage.setItem('paperHands_pendingReferral', ref);
        return ref;
      }
      return localStorage.getItem('paperHands_pendingReferral') || null;
    }
    return null;
  });
  
  // ============== TRANSACTION HISTORY ==============
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // ============== RUG ENCYCLOPEDIA ==============
  const [showRugEncyclopedia, setShowRugEncyclopedia] = useState(false);
  const [selectedRug, setSelectedRug] = useState(null);
  const [showWhitepaperModal, setShowWhitepaperModal] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null); // For achievement detail popup
  const [showProfile, setShowProfile] = useState(false); // Player stats modal
  const [showTradingGuide, setShowTradingGuide] = useState(false); // Trading guide modal
  const [showRakeDisclosure, setShowRakeDisclosure] = useState(false); // First trade rake warning
  const [rakeWarningType, setRakeWarningType] = useState(null); // 'ai' or 'pvp'
  // 💰 RAKE CONSENT & TRACKING
  const [rakeConsent, setRakeConsent] = useState(false); // First-time consent (permanent)
  const [showRakeConsentModal, setShowRakeConsentModal] = useState(false); // First-time consent popup
  const [showTutorialModal, setShowTutorialModal] = useState(false); // AMM Casino tutorial
  const [tutorialComplete, setTutorialComplete] = useState(false); // Tutorial already seen
  const [tutorialStep, setTutorialStep] = useState(0); // Current tutorial step
  const [totalRakePaid, setTotalRakePaid] = useState(0); // Total fees paid in sats
  const [rakeTransactions, setRakeTransactions] = useState(0); // Number of rake txs
  const [hasSeenAIRakeToday, setHasSeenAIRakeToday] = useState(() => {
    const stored = localStorage.getItem('paperHands_aiRakeWarningDate');
    return stored === new Date().toDateString();
  });
  const [hasSeenPvPRakeToday, setHasSeenPvPRakeToday] = useState(() => {
    const stored = localStorage.getItem('paperHands_pvpRakeWarningDate');
    return stored === new Date().toDateString();
  });

  // ============== TRADING GUIDE CONTENT ==============
  const TRADING_GUIDE = {
    basics: {
      title: '🎮 How To Play',
      icon: '🎮',
      desc: 'Paper Hands has TWO modes: AI Tokens (learn to spot rugs) and PvP Tokens (pure player vs player).',
      items: [
        { label: 'Deposit', desc: 'Add BSV via HandCash. 1 sat = 1 Paper Buck ($PB).' },
        { label: 'AI Tokens', desc: '70% rug, 30% moon. Bots trade against you. Learn the patterns!' },
        { label: 'PvP Tokens', desc: 'Player-launched. No bots, no rugs. Pure AMM trading.' },
        { label: 'Sell Before Rug', desc: 'On AI tokens, sell before the rug! On PvP, sell before others dump.' },
        { label: 'Border Colors', desc: 'Green = Mooning, Gold = Holding, Orange = Hot, Pink = PvP Token' },
      ]
    },
    rugProphet: {
      title: '🔮 Rug Prophet (AI Only)',
      icon: '🔮',
      desc: 'See a rug coming on AI tokens? BET ON IT! (Not available on PvP tokens)',
      items: [
        { label: 'How It Works', desc: 'Click "Rug Prophet" tab → Select an AI token → Bet it will RUG.' },
        { label: 'Bet Amount', desc: 'Wager 10K-1M Paper Bucks on your prediction.' },
        { label: 'If You\'re Right', desc: 'Token rugs = You WIN 1.8x your bet!' },
        { label: 'If You\'re Wrong', desc: 'Token moons = You LOSE your bet.' },
        { label: 'Strategy', desc: 'Watch for dump patterns, bot sells, low health → then bet RUG!' },
        { label: 'PvP Excluded', desc: 'Can\'t bet on PvP tokens - they don\'t have fates!' },
      ]
    },
    rugTypes: {
      title: '💀 Rug Types (AI Tokens)',
      icon: '💀',
      desc: 'Four manipulation patterns on AI tokens. PvP tokens NEVER auto-rug.',
      items: [
        { label: '⚡ HARD RUG', desc: 'Instant death. Price drops 90%+ in seconds. No warning.' },
        { label: '🪤 TRAP RUG', desc: 'Fake pump → dip → pump again → THEN rugs. Fools you twice.' },
        { label: '🩸 SLOW RUG', desc: 'Death by 1000 cuts. Gradual bleed with false hope pumps.' },
        { label: '🍯 HONEYPOT', desc: 'You can buy but CAN\'T SELL. Trapped forever.' },
      ]
    },
    tokenLifecycle: {
      title: '⏱️ Token Lifecycle (AI)',
      icon: '⏱️',
      desc: 'AI tokens have hidden fates. PvP tokens live forever.',
      items: [
        { label: 'AI Lifespan', desc: 'AI tokens live 3-8 minutes. Fate triggers based on "health".' },
        { label: '70% RUG', desc: 'Most AI tokens are designed to rug. That\'s the game.' },
        { label: '20% MOON', desc: 'These pump 2-3.5x before completing successfully.' },
        { label: '10% SUPERMOON', desc: 'Rare 5-10x runners. Diamond hands rewarded.' },
        { label: 'PvP Forever', desc: 'PvP tokens have no timer. They live until liquidity drains to 0.' },
      ]
    },
    botBehavior: {
      title: '🤖 Bot Behavior (AI Tokens Only)',
      icon: '🤖',
      desc: 'On AI tokens, bots trade against you. PvP tokens have NO bots.',
      items: [
        { label: 'Bot Types', desc: 'Degens, flippers, smart money, whale watchers, hunters.' },
        { label: 'They React', desc: 'Big buys trigger bot dumps 3-5 seconds later.' },
        { label: 'Hunter Bots', desc: 'Target tokens where real players are. They\'re watching you.' },
        { label: 'Insider Bots', desc: 'Some bots "know" the fate. Watch $smart_money closely.' },
        { label: 'PvP = No Bots', desc: 'Pink border tokens are PvP. Zero bots, zero manipulation.' },
      ]
    },
    chartFeatures: {
      title: '📊 Chart Controls',
      icon: '📊',
      items: [
        { label: '⚡ LIVE', desc: 'Real-time view, last 40 candles. Auto-follows price.' },
        { label: '📅 1M', desc: 'Last minute of history (~60 candles). Drag to scroll.' },
        { label: '📜 ALL', desc: 'Full token history (up to 120 candles). See everything.' },
        { label: '🖱️ Drag', desc: 'In 1M/ALL mode, drag the chart left/right to scroll through history.' },
        { label: 'Candles', desc: 'Green = price up, Red = price down. Wicks show high/low.' },
      ]
    },
    sentiment: {
      title: '🎭 Market Sentiment',
      icon: '🎭',
      desc: 'Shows current activity, NOT predictions. A BULLISH token can still rug!',
      items: [
        { label: '🐂 BULLISH', desc: 'Heavy buying pressure (buys > sells)' },
        { label: '🐻 BEARISH', desc: 'Heavy selling pressure (sells > buys)' },
        { label: '🚀 PUMPING', desc: 'Price over 2x from launch' },
        { label: '📉 DUMPING', desc: 'Price under 50% of launch' },
        { label: '⚠️ LOW LIQ', desc: 'Liquidity draining - danger zone' },
        { label: '🔥 ACTIVE', desc: 'High trading volume' },
      ]
    },
    rugSigns: {
      title: '🚩 Spotting a Rug (AI)',
      icon: '🚩',
      desc: 'Watch for these patterns on AI tokens. PvP tokens don\'t auto-rug!',
      items: [
        { label: '📉 Smart Money Exit', desc: 'Big sells from $smart_money or $whale_watcher early on' },
        { label: '🔴 Coordinated Dumps', desc: 'Multiple bots selling at once = insider knowledge' },
        { label: '💬 Chat Warnings', desc: '"Big sells detected!" messages in chat' },
        { label: '📊 Volume Spike', desc: 'Sudden sell volume with price dropping' },
        { label: '❤️ Health Dropping', desc: 'Health bar falling fast? Rug incoming!' },
      ]
    },
    withdrawals: {
      title: '💸 Deposits & Withdrawals',
      icon: '💸',
      desc: 'Get money in and out via HandCash.',
      items: [
        { label: 'Deposit', desc: 'Click +Deposit → Choose amount → Pays from HandCash.' },
        { label: 'Withdraw', desc: 'Click Withdraw → Choose amount → Sent to HandCash.' },
        { label: 'Minimum', desc: '100K sats ($0.02) minimum withdrawal.' },
        { label: 'Fees', desc: 'Small HandCash network fee on withdrawals.' },
        { label: 'Instant', desc: 'Both deposits and withdrawals are instant.' },
      ]
    },
    social: {
      title: '👥 Social Features',
      icon: '👥',
      desc: 'Play with friends, not just against bots.',
      items: [
        { label: 'Friends List', desc: 'Add up to 50 friends. See when they\'re online.' },
        { label: 'DMs', desc: 'Private message other players directly.' },
        { label: 'Tipping', desc: 'Send Paper Bucks to other players as tips.' },
        { label: 'Lobby Chat', desc: 'Premium users can chat in the main lobby.' },
        { label: 'Token Chat', desc: 'Chat inside token rooms with other traders.' },
      ]
    },
    leaderboards: {
      title: '🏆 Leaderboards',
      icon: '🏆',
      desc: 'Compete globally. Click "Leaders" to see all rankings.',
      items: [
        { label: '🔴 Today', desc: 'Live rankings reset daily at midnight. Race to be #1 today!' },
        { label: '💰 Top Winners', desc: 'All-time highest profit. The legends of Paper Hands.' },
        { label: '🛡️ Unruggable', desc: 'Best survival rate %. Shows YOUR weakness analysis too!' },
        { label: '🎯 Win Rate', desc: 'Highest win percentage. Quality over quantity.' },
        { label: '💀 Hall of Shame', desc: 'Biggest all-time losses. Learn from their mistakes.' },
        { label: '🤡 Most Rugged', desc: 'Most times rugged. The unluckiest (or worst) traders.' },
        { label: '⛓️ On-Chain', desc: 'Daily champion recorded to BSV blockchain forever!' },
      ]
    },
    challenges: {
      title: '🎯 Daily Challenges',
      icon: '🎯',
      desc: 'Complete tasks for bonus Paper Bucks.',
      items: [
        { label: 'Daily Reset', desc: 'New challenges every 24 hours.' },
        { label: 'Task Types', desc: 'Make trades, hit multipliers, survive rugs, etc.' },
        { label: 'Claim Rewards', desc: 'Tap the challenge when complete to claim.' },
        { label: 'Streak Bonus', desc: 'Complete challenges daily for streak multipliers.' },
      ]
    },
    badges: {
      title: '🏅 Badges',
      icon: '🏅',
      desc: 'Earn badges for achievements. Flex on other players.',
      items: [
        { label: '🛡️ Survivor', desc: 'Escape X rugs without losing.' },
        { label: '💎 Diamond Hands', desc: 'Hold through a 3x+ pump.' },
        { label: '🎯 Prophet', desc: 'Win X Rug Prophet bets.' },
        { label: '🔥 On Fire', desc: 'Hit a 5+ win streak.' },
        { label: '🐋 Whale', desc: 'Make a single trade over 1M PB.' },
      ]
    },
    referrals: {
      title: '📢 Referrals',
      icon: '📢',
      desc: 'Invite friends, earn rewards.',
      items: [
        { label: 'Your Code', desc: 'Share your referral link from the Refer tab.' },
        { label: 'You Earn', desc: '47,600 sats (~$0.01) when friend deposits.' },
        { label: 'They Earn', desc: '23,800 sats (~$0.005) bonus on first deposit.' },
        { label: 'Tracking', desc: 'See all your referrals and earnings in Refer tab.' },
      ]
    },
    tokenomics: {
      title: '💰 Understanding Tokens',
      icon: '💰',
      items: [
        { label: 'MCap', desc: 'Market Cap = Supply × Price. Total value of all tokens.' },
        { label: 'Liquidity', desc: 'Sats in the pool. More liquidity = bigger trades possible.' },
        { label: 'Volume', desc: 'Buy + Sell activity. High volume = high interest.' },
        { label: 'Health', desc: 'Token vitality. Low health = closer to rug or moon.' },
        { label: 'ROI', desc: 'Return on Investment. Current price ÷ your entry price (e.g. 2.5x = 150% profit).' },
      ]
    },
    pvpTokens: {
      title: '⚔️ PvP Tokens',
      icon: '⚔️',
      desc: 'Launch your own token! Pure player vs player - no bots, no manipulation.',
      items: [
        { label: 'Launch Cost', desc: '$5 USD in BSV to create. You set ticker, name, and image.' },
        { label: 'Pure AMM', desc: 'Price = Liquidity ÷ Token Reserve. Math is law.' },
        { label: 'No Bots', desc: 'Zero AI manipulation. Price ONLY moves from real player trades.' },
        { label: 'No Rugs', desc: 'PvP tokens never auto-rug. They live forever until liquidity drains.' },
        { label: 'Creator Bag', desc: 'You get first buy at launch price. Pump it, sell it, profit!' },
      ]
    },
    pvpSlippage: {
      title: '📉 Slippage & Dumping',
      icon: '📉',
      desc: 'Big sells = big slippage. The AMM is your friend AND your enemy.',
      items: [
        { label: 'What is Slippage?', desc: 'Price moves AGAINST you as you trade. Bigger trade = more slippage.' },
        { label: 'Why It Happens', desc: 'Selling removes liquidity. Less liquidity = lower price per token.' },
        { label: 'Example', desc: 'Sell 50% of pool in one trade? You might only get 25% of expected value.' },
        { label: '🛡️ Protection', desc: 'PvP tokens limit sells to 50% price impact per trade. You may need multiple sells for big positions.' },
        { label: 'Strategy', desc: 'Sell in smaller chunks to reduce slippage. Or YOLO and accept the hit.' },
        { label: 'The Game', desc: 'First to exit wins most. Last holder gets rekt by slippage.' },
      ]
    },
    premium: {
      title: '👑 Premium Features',
      icon: '👑',
      desc: 'Deposit $25+ worth of BSV for 24h premium access:',
      items: [
        { label: '🕵️ Insider Access', desc: 'See new tokens 10 seconds before free users.' },
        { label: '🔮 AlphaBot Vetted', desc: 'Some tokens marked as 95% safe (only 5% rug).' },
        { label: '💬 Chat Access', desc: 'Talk in lobby and token rooms.' },
        { label: '🎁 Daily Rewards', desc: 'Claim free Paper Bucks every day.' },
        { label: '📊 Health Bars', desc: 'See exact token health % (free users see ranges).' },
      ]
    },
    protips: {
      title: '🧠 Pro Tips',
      icon: '🧠',
      items: [
        { label: 'Check History', desc: 'Before aping, scroll to ALL mode and see what happened.' },
        { label: 'Watch the Feed', desc: 'LIVE TRADES shows who\'s buying/selling in real-time.' },
        { label: 'Size Your Bets', desc: 'Don\'t go all-in. Take profits on the way up.' },
        { label: 'Rug Prophet Hedge', desc: 'Buy a token AND bet it rugs = win either way!' },
        { label: 'Bot Tells', desc: 'When $smart_money sells early, pay attention.' },
        { label: 'Trust Patterns', desc: 'Labels don\'t predict rugs. Chart patterns do.' },
      ]
    }
  };
  
  const RUG_ENCYCLOPEDIA = {
    HARD: {
      name: 'Hard Rug',
      icon: '⚡',
      severity: 'CRITICAL',
      color: '#ff0040',
      tagline: 'Gone in 60 seconds',
      description: 'The classic. Dev drains liquidity instantly, token goes to zero before you can even click sell.',
      realWorldExample: 'Squid Game Token (2021) - Devs pulled $3.3M in seconds. Sellers were blocked.',
      howItWorks: [
        'Dev launches token with locked liquidity (or so it seems)',
        'Hype builds, price pumps as buyers flood in',
        'Dev has hidden function or unlocked LP tokens',
        'One transaction: LP removed, price = 0',
        'Average victim reaction time: 0.5 seconds (too slow)',
      ],
      warningSignsReal: [
        '🚩 Unverified contract on Etherscan/BSCScan',
        '🚩 No audit from reputable firm',
        '🚩 Liquidity not locked (or fake lock)',
        '🚩 Dev wallet holds majority supply',
        '🚩 "Stealth launch" with no prior community',
      ],
      howToProtect: [
        '✅ Check if LP tokens are burned or locked',
        '✅ Use rug checker tools (TokenSniffer, RugDoc)',
        '✅ Look for audit from known firms',
        '✅ Never buy in first 5 minutes of launch',
        '✅ If it sounds too good, it is',
      ],
      inGameTip: 'In Paper Hands, HARD rugs give you ~0.5 seconds to react. Train your reflexes!',
      victimCount: '847K+',
      totalStolen: '$2.1B+',
    },
    TRAP: {
      name: 'Trap Rug (Pump & Dump)',
      icon: '🪤',
      severity: 'HIGH',
      color: '#ff6b00',
      tagline: 'The fake pump before the dump',
      description: 'Price pumps hard making you think you\'re early. That pump? It\'s the dev buying with multiple wallets to create FOMO. When you buy the top, they dump everything.',
      realWorldExample: 'SaveTheKids Token (2021) - Influencers promoted, price pumped 5x, then crashed 90% as insiders dumped.',
      howItWorks: [
        'Dev creates token, keeps large allocation in multiple wallets',
        'Coordinates fake "organic" buys to pump price',
        'Pays influencers/KOLs to shill (they\'re in on it)',
        'Retail FOMO buys at the top',
        'Coordinated dump across all insider wallets',
        'Insiders profit, retail holds bags',
      ],
      warningSignsReal: [
        '🚩 Sudden price pump with no news/catalyst',
        '🚩 Paid promotions from influencers',
        '🚩 Top wallets all bought at same time',
        '🚩 Telegram/Discord banning questions',
        '🚩 "Don\'t sell, diamond hands!" peer pressure',
      ],
      howToProtect: [
        '✅ Check holder distribution (no wallet >5%)',
        '✅ Look at buy/sell transaction patterns',
        '✅ Be skeptical of influencer promotions',
        '✅ Take profits on the way up',
        '✅ Never marry a memecoin',
      ],
      inGameTip: 'TRAP rugs in Paper Hands pump 10% before crashing. Don\'t chase the green candle!',
      victimCount: '1.2M+',
      totalStolen: '$890M+',
    },
    SLOW: {
      name: 'Slow Rug',
      icon: '📉',
      severity: 'MEDIUM',
      color: '#ff9500',
      tagline: 'Death by a thousand cuts',
      description: 'The sneaky one. Dev slowly sells their tokens over days/weeks while posting "bullish" updates. By the time you notice, you\'re down 90% and there\'s no liquidity left.',
      realWorldExample: 'Many "legit" projects do this - dev sells 1-2% daily while tweeting roadmap updates.',
      howItWorks: [
        'Project launches with seemingly legit team',
        'Dev holds 10-20% of supply (disclosed or hidden)',
        'Regular small sells that don\'t tank price immediately',
        'Each sell creates new "support" level... that breaks',
        'Community copes: "just whales taking profit"',
        'Eventually liquidity dries up, can\'t sell',
      ],
      warningSignsReal: [
        '🚩 Consistent sell pressure despite "good news"',
        '🚩 Dev wallet(s) slowly decreasing',
        '🚩 Price bleeds while team stays "bullish"',
        '🚩 Roadmap delays and excuses',
        '🚩 Team members quietly leave',
      ],
      howToProtect: [
        '✅ Track dev/team wallets with on-chain tools',
        '✅ Set stop losses (seriously)',
        '✅ Watch liquidity depth over time',
        '✅ Be suspicious of "diamond hands" culture',
        '✅ Take profits incrementally, don\'t wait for moon',
      ],
      inGameTip: 'SLOW rugs bleed out over 15 seconds. You have time - but most people freeze and cope.',
      victimCount: '2.1M+',
      totalStolen: '$1.4B+',
    },
    HONEYPOT: {
      name: 'Honeypot',
      icon: '🍯',
      severity: 'CRITICAL',
      color: '#ff0040',
      tagline: 'You can buy, but you can\'t sell',
      description: 'The cruelest rug. Contract code literally prevents anyone except the dev from selling. You watch your "gains" evaporate while being unable to exit.',
      realWorldExample: 'Hundreds daily on BSC/ETH - contract has hidden modifier that blocks sells from non-whitelisted addresses.',
      howItWorks: [
        'Contract has hidden sell restrictions in code',
        'Common tricks: max sell amount = 0, blacklist function, transfer cooldown',
        'Token launches, you can buy no problem',
        'Price goes up (only buys, no sells possible)',
        'You try to sell - transaction fails or stuck pending',
        'Dev is only address whitelisted to sell',
        'Dev dumps, you\'re stuck forever',
      ],
      warningSignsReal: [
        '🚩 Unverified/unreadable contract code',
        '🚩 Contract has "blacklist" or "whitelist" functions',
        '🚩 Test with tiny buy first, try to sell immediately',
        '🚩 Only buy transactions, zero sells on DEXtools',
        '🚩 Tax >10% or "dynamic tax"',
      ],
      howToProtect: [
        '✅ ALWAYS test sell with tiny amount first',
        '✅ Use honeypot detectors (honeypot.is, tokensniffer)',
        '✅ Read contract - look for transfer restrictions',
        '✅ Check DEXtools/DEXscreener for sell transactions',
        '✅ If you can\'t understand the contract, don\'t buy',
      ],
      inGameTip: 'Honeypots in Paper Hands block 70% of sell attempts. Spam that button!',
      victimCount: '450K+',
      totalStolen: '$340M+',
    },
    TEAM_ABANDON: {
      name: 'Team Abandonment',
      icon: '👻',
      severity: 'MEDIUM',
      color: '#888888',
      tagline: 'They just... stopped',
      description: 'Not a dramatic rug, but death nonetheless. Team stops delivering, goes silent, and the project slowly fades to zero as holders lose hope.',
      realWorldExample: 'Countless NFT/DeFi projects post-2021 - Discord goes quiet, Twitter stops posting, website goes down.',
      howItWorks: [
        'Project launches with big promises and roadmap',
        'Initial hype and community building',
        'Team realizes it\'s harder than expected',
        'Updates become less frequent',
        'Team goes silent, "focusing on building"',
        'Months pass, nothing ships',
        'Community slowly leaves, price bleeds to zero',
      ],
      warningSignsReal: [
        '🚩 Anonymous team with no track record',
        '🚩 Overpromised roadmap (too many features)',
        '🚩 Team stops engaging with community',
        '🚩 Delayed launches and moved goalposts',
        '🚩 Key team members leave quietly',
      ],
      howToProtect: [
        '✅ Research team background and past projects',
        '✅ Prefer doxxed teams with reputation at stake',
        '✅ Watch GitHub/development activity',
        '✅ Set time-based exit strategy',
        '✅ Don\'t fall for "trust the process"',
      ],
      inGameTip: 'Not in Paper Hands yet, but remember: no updates = slow death.',
      victimCount: '3M+',
      totalStolen: 'Immeasurable',
    },
    FAKE_TEAM: {
      name: 'Fake Team / AI Generated',
      icon: '🤖',
      severity: 'HIGH',
      color: '#9b59b6',
      tagline: 'Those LinkedIn profiles? All fake.',
      description: 'Team photos are AI-generated or stolen. "Partnerships" are fabricated. The whole project is theater designed to build trust before the rug.',
      realWorldExample: 'OneCoin ($4B scam) - Entire company was fake. Team photos stolen. "Exchange" was a spreadsheet.',
      howItWorks: [
        'Create fake team with AI faces (thispersondoesnotexist.com)',
        'Fake LinkedIn profiles with impressive backgrounds',
        'Announce fake partnerships with logos',
        'Create illusion of legitimate business',
        'Raise funds through token sale/presale',
        'Disappear with funds',
      ],
      warningSignsReal: [
        '🚩 Team photos look "off" (AI artifacts)',
        '🚩 Reverse image search finds nothing or stock photos',
        '🚩 LinkedIn profiles created recently',
        '🚩 No video AMAs or live appearances',
        '🚩 "Partners" don\'t acknowledge partnership',
      ],
      howToProtect: [
        '✅ Reverse image search all team photos',
        '✅ Check LinkedIn profile creation dates',
        '✅ Demand video AMAs (real people)',
        '✅ Verify partnerships directly with "partners"',
        '✅ If team won\'t show face, extra caution',
      ],
      inGameTip: 'In the real world, always verify. In Paper Hands, assume everyone is fake 🐸',
      victimCount: '500K+',
      totalStolen: '$5B+',
    },
    MINT_EXPLOIT: {
      name: 'Infinite Mint',
      icon: '🖨️',
      severity: 'CRITICAL',
      color: '#e74c3c',
      tagline: 'Money printer go brrr... for them',
      description: 'Contract has a hidden or "admin" mint function. Dev mints billions of new tokens and dumps them on the market, diluting your holdings to worthlessness.',
      realWorldExample: 'Paid Network (2021) - Attacker minted 59M tokens and dumped, crashing price 95% in minutes.',
      howItWorks: [
        'Contract has mint function (sometimes hidden)',
        'Function may be obfuscated or look innocent',
        'Dev/attacker mints massive new supply',
        'New tokens dumped on DEX',
        'Price crashes as supply explodes',
        'Your tokens now worthless due to dilution',
      ],
      warningSignsReal: [
        '🚩 Contract has mint/burn functions accessible by owner',
        '🚩 "Admin" or "owner" functions in unverified code',
        '🚩 No max supply cap in contract',
        '🚩 Upgradeable proxy contracts (can add mint later)',
        '🚩 Recent contract modifications',
      ],
      howToProtect: [
        '✅ Read contract - search for "mint" functions',
        '✅ Check if mint is disabled or renounced',
        '✅ Verify max supply is hardcoded',
        '✅ Be wary of proxy/upgradeable contracts',
        '✅ Check if owner functions are renounced',
      ],
      inGameTip: 'Paper Hands tokens have fixed supply. In real crypto, always check!',
      victimCount: '280K+',
      totalStolen: '$780M+',
    },
  };
  
  useEffect(() => {
    if (handcashUser) {
      setReferralCode(handcashUser.handle.replace('$', ''));
    }
  }, [handcashUser]);

  // ============== REFS ==============
  const gameLoopRef = useRef(null);
  const priceHistoryRef = useRef([1.0]);
  const startTimeRef = useRef(null);
  const positionRef = useRef({ size: 0, invested: 0, entry: 1.0 });
  const tickCountRef = useRef(0);
  const volumeRef = useRef([]);
  const activeTokensRef = useRef([]); // For accessing tokens in game loop
  const currentTokenRef = useRef(null); // For accessing current token in game loop
  
  // Manipulation Engine - determines token fate and realistic price movement
  const manipulationRef = useRef({
    fate: 'RUG',           // RUG, MOON, or SUPERMOON
    rugType: 'HARD',       // HARD, TRAP, SLOW, HONEYPOT
    moonMultiplier: 1,     // For MOON/SUPERMOON tokens
    rugTime: 45000,        // Time until rug (or moon completion)
    startTime: null,       // When token started
    phase: 'ACCUMULATION', // ACCUMULATION, MARKUP, DISTRIBUTION, MOON
    momentum: 0,           // Price momentum
    playerInfluence: 0,    // How player trades affect price
    fakeoutCount: 0,       // Number of fake dips/pumps
    maxFakeouts: 2,        // Max fakeouts before real move
  });
  
  // 🧊 $wintermute - The Silent Market Maker
  // Legend that NEVER speaks, only trades. Other bots warn about them.
  // 4-phase strategy: Accumulate → Pump → Distribute → Exit
  const wintermuteRef = useRef({
    phase: 'DORMANT',        // DORMANT, ACCUMULATE, PUMP, DISTRIBUTE, EXIT
    positionSize: 0,         // How many tokens wintermute holds
    avgEntry: 0,             // Average entry price
    lastTradeTime: 0,        // Throttle trades
    tradeCount: 0,           // Total trades this token
    isActive: false,         // Is wintermute playing this token?
  });

  // Keep refs in sync with state (for use in intervals/callbacks)
  useEffect(() => {
    activeTokensRef.current = activeTokens;
  }, [activeTokens]);
  
  useEffect(() => {
    currentTokenRef.current = currentToken;
  }, [currentToken]);

  // ============== CONSTANTS ==============
  // BSV price fetched live - demonstrates micropayments on BSV via HandCash
  const [bsvPrice, setBsvPrice] = useState(null); // null until fetched
  const [bsvPriceLoading, setBsvPriceLoading] = useState(true);
  const [handcashHealth, setHandcashHealth] = useState(null); // HandCash API health status
  const [hiveReports, setHiveReports] = useState(0); // 🐝 Number of hive reports in last 5 min
  const SATS_PER_BSV = 100000000; // 100M sats per BSV
  const satToUsd = bsvPrice ? bsvPrice / SATS_PER_BSV : 0;
  // usdToSats: Convert USD to satoshis - returns 0 if price not loaded (buttons will be disabled)
  const usdToSats = (usd) => {
    if (!bsvPrice || bsvPrice <= 0) {
      return 0; // Silent return - price not loaded yet
    }
    const sats = Math.round(usd / bsvPrice * SATS_PER_BSV);
    return sats > 0 ? sats : 0;
  };
  
  // PREMIUM: $0.25 USD for 1 week! (includes bot access)
  const PREMIUM_USD = 0.25; // $0.25 USD - must match server!
  const PREMIUM_SATS = 500000; // Approximate sats for balance check (~$0.25 at $50/BSV)
  const PREMIUM_COST = PREMIUM_SATS;
  const PREMIUM_USD_DISPLAY = '$0.25';
  const PREMIUM_DAYS = 7; // 1 week
  
  // 🕵️ INSIDER EARLY ACCESS: Premium sees tokens 5 seconds before free users
  const INSIDER_DELAY_MS = 5000;
  
  // 💵 BSV Price - Server provides real price on auth/balance refresh
  // Set a fallback immediately so the app works before login
  useEffect(() => {
    // Set fallback price immediately if not set
    if (!bsvPrice) {
      setBsvPrice(18.5); // Fallback until server provides real price
      setBsvPriceLoading(false);
    }
  }, [bsvPrice]);
  
  const WS_URL = 'wss://paper-hands-server.up.railway.app'; // Update with your Railway URL
  
  // 💵 PAPER BUCKS - In-game currency
  // ╔════════════════════════════════════════════════════════════════════════════╗
  // ║  🔒 CORE ECONOMICS - DO NOT MODIFY - SEE ECONOMICS.md                      ║
  // ║  1 sat = 1 Paper Buck                                                      ║
  // ║  This ratio is the foundation of the entire game.                          ║
  // ║  Changing this breaks: pools, bots, house edge, everything.                ║
  // ╚════════════════════════════════════════════════════════════════════════════╝
  const SATS_PER_PB = 1; // 🔒 LOCKED - 1 sat = 1 Paper Buck - NEVER CHANGE
  
  // Format as Paper Bucks (game currency) - used for game wallet, trades, mcap, etc
  // Compact format: $1.4B, $38.2M, $5.1K
  const formatPB = (sats) => {
    const pb = sats / SATS_PER_PB;
    const absPb = Math.abs(pb);
    const sign = pb < 0 ? '-' : '';
    if (absPb >= 1000000000) return sign + '$' + (absPb / 1000000000).toFixed(1) + 'B';
    if (absPb >= 100000000) return sign + '$' + (absPb / 1000000).toFixed(0) + 'M';  // 100M+ = no decimals
    if (absPb >= 10000000) return sign + '$' + (absPb / 1000000).toFixed(1) + 'M';   // 10M+ = 1 decimal
    if (absPb >= 1000000) return sign + '$' + (absPb / 1000000).toFixed(2) + 'M';    // 1M+ = 2 decimals
    if (absPb >= 100000) return sign + '$' + (absPb / 1000).toFixed(0) + 'K';        // 100K+ = no decimals
    if (absPb >= 1000) return sign + '$' + (absPb / 1000).toFixed(1) + 'K';          // 1K+ = 1 decimal
    if (absPb >= 1) return sign + '$' + absPb.toFixed(0);                            // 1+ = no decimals
    return sign + '$' + absPb.toFixed(2);
  };
  
  // Format as real sats (for HandCash deposits/withdrawals/inscriptions)
  // Compact format: 17.2M, 5.1K
  const formatSats = (sats) => {
    const absSats = Math.abs(sats);
    const sign = sats < 0 ? '-' : '';
    if (absSats >= 1000000000) return sign + (absSats / 1000000000).toFixed(1) + 'B';
    if (absSats >= 100000000) return sign + (absSats / 1000000).toFixed(0) + 'M';
    if (absSats >= 10000000) return sign + (absSats / 1000000).toFixed(1) + 'M';
    if (absSats >= 1000000) return sign + (absSats / 1000000).toFixed(2) + 'M';
    if (absSats >= 100000) return sign + (absSats / 1000).toFixed(0) + 'K';
    if (absSats >= 1000) return sign + (absSats / 1000).toFixed(1) + 'K';
    return sign + absSats.toLocaleString();
  };
  
  // Full sat count with commas - no abbreviation
  const formatSatsFull = (sats) => {
    return Math.floor(sats).toLocaleString();
  };
  
  // Full PB count with commas
  const formatPBFull = (sats) => {
    const pb = sats / SATS_PER_PB;
    return '$' + pb.toFixed(2);
  };
  
  // 🚀 MEMOIZED AI TOKENS - Prevents recalculation on every render
  const displayAiTokens = useMemo(() => {
    const now = Date.now();
    // Show rugged tokens briefly (5s), mooned tokens longer (15s) with celebration
    // 🛡️ FILTER OUT USER TOKENS - they belong in BSV.pump tab only!
    const allTokens = activeTokens.filter(t => {
      if (!t || !t.id) return false;
      // Exclude user-launched tokens from AI tab
      if (t.isUserLaunched) return false;
      if (t.state === 'rugged') {
        // Rugs fade out after 5 seconds
        const timeSinceEnd = now - (t.endedAt || now);
        return timeSinceEnd < 5000;
      }
      if (t.state === 'mooned') {
        // Moons celebrate for 15 seconds!
        const timeSinceEnd = now - (t.endedAt || now);
        return timeSinceEnd < 15000;
      }
      return true;
    });
    const visibleTokens = isPremium 
      ? allTokens 
      : allTokens.filter(t => now >= (t.visibleAt || t.birthTime));
    const hiddenCount = allTokens.length - visibleTokens.length;
    
    // Sort tokens based on selected criteria (Premium feature)
    const sortedTokens = [...visibleTokens].sort((a, b) => {
      switch(tokenSortBy) {
        case 'mcap':
          return (b.marketCap || 0) - (a.marketCap || 0); // Highest first
        case 'change':
          return (b.priceChange || 0) - (a.priceChange || 0); // Best performing first
        case 'liquidity':
          return (b.liquidityPool || b.liquidity || 0) - (a.liquidityPool || a.liquidity || 0);
        case 'volume':
          // Total volume = buy + sell volume
          const volA = (a.totalBuyVolume || 0) + (a.totalSellVolume || 0);
          const volB = (b.totalBuyVolume || 0) + (b.totalSellVolume || 0);
          return volB - volA; // Highest volume first
        case 'newest':
        default:
          return (b.birthTime || 0) - (a.birthTime || 0); // Newest first
      }
    });
    
    // 🎯 ALWAYS show tokens where user has active positions!
    const positionTokenIds = positions
      .filter(p => p.status === 'active')
      .map(p => p.tokenId);
    
    // 🔮 ALWAYS show tokens where user has pending bets!
    const betTokenIds = rugProphetBets
      .filter(b => b.status === 'pending')
      .map(b => b.tokenId);
    
    // Combined priority token IDs (positions OR bets)
    const priorityTokenIds = [...new Set([...positionTokenIds, ...betTokenIds])];
    
    // Separate tokens with priority (positions/bets) vs without
    const tokensWithPriority = sortedTokens.filter(t => priorityTokenIds.includes(t.id));
    const tokensWithoutPriority = sortedTokens.filter(t => !priorityTokenIds.includes(t.id));
    
    // Combine: priority first (always shown), then fill remaining slots up to 16
    const maxOtherTokens = Math.max(0, 16 - tokensWithPriority.length);
    const displayTokens = [
      ...tokensWithPriority,
      ...tokensWithoutPriority.slice(0, maxOtherTokens)
    ];
    
    return { displayTokens, hiddenCount, allTokens };
  }, [activeTokens, isPremium, tokenSortBy, positions, rugProphetBets]);
  
  // 🟢 HANDCASH LOGO COMPONENT - Reusable for trust signals
  // Using official HandCash green with inline SVG fallback
  const HANDCASH_LOGO = "https://uploads-ssl.webflow.com/635e9aefcf7c7d2785c57108/63ba141fe3fd7c1a22e7c5b6_logo-handcash.svg";
  const HANDCASH_LOGO_ROUND = "https://i.ibb.co/23X7r7j/logo.png";
  
  const HandCashLogo = ({ size = 'medium', showText = true, style = {} }) => {
    const sizes = {
      small: { logo: 16, text: 11 },
      medium: { logo: 24, text: 14 },
      large: { logo: 32, text: 18 },
    };
    const s = sizes[size] || sizes.medium;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', ...style }}>
        <img 
          src="https://i.ibb.co/23X7r7j/logo.png"
          alt="HandCash"
          style={{ height: `${s.logo}px`, width: `${s.logo}px`, borderRadius: '50%' }}
          onError={e => { 
            // Fallback to colored circle if image fails
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div style={{ 
          display: 'none', 
          width: `${s.logo}px`, 
          height: `${s.logo}px`, 
          borderRadius: '50%', 
          backgroundColor: '#38cb7c',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: `${s.logo * 0.5}px`
        }}>H</div>
        {showText && (
          <span style={{ 
            color: '#38cb7c', 
            fontSize: `${s.text}px`, 
            fontWeight: 'bold',
            letterSpacing: '-0.3px',
          }}>HandCash</span>
        )}
      </div>
    );
  };
  
  // Inline HandCash logo as SVG - the actual green C logo!
  const HandCashIcon = ({ size = 18 }) => (
    <img 
      src="https://i.ibb.co/23X7r7j/logo.png"
      alt="HC"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        flexShrink: 0,
        objectFit: 'cover',
      }}
    />
  );
  
  // HandCash logo URL for img tags
  const HANDCASH_LOGO_INLINE = "https://i.ibb.co/23X7r7j/logo.png";
  
  // 🔧 FORMAT PERCENT - Caps at ±9999% to prevent overflow/display issues
  const formatPercent = (percent) => {
    if (percent === undefined || percent === null || isNaN(percent)) return '+0.0%';
    if (percent > 9999) return '+9999%';
    if (percent < -9999) return '-9999%';
    // For big percentages, use no decimal to save space
    if (Math.abs(percent) >= 1000) {
      return (percent >= 0 ? '+' : '') + Math.round(percent) + '%';
    }
    if (Math.abs(percent) >= 100) {
      return (percent >= 0 ? '+' : '') + percent.toFixed(0) + '%';
    }
    return (percent >= 0 ? '+' : '') + percent.toFixed(1) + '%';
  };
  
  // 🔧 FORMAT MULTIPLIER - Caps at 9999x to prevent overflow/display issues
  const formatMultiplier = (mult) => {
    if (mult === undefined || mult === null || isNaN(mult)) return '1.00x';
    if (mult > 9999) return '9999x';
    if (mult < 0.0001) return '0.00x';
    return mult.toFixed(2) + 'x';
  };

  // 🔧 CHANGE TILE VIEW MODE - Save preference to localStorage
  const changeTileViewMode = (mode) => {
    setTileViewMode(mode);
    try { localStorage.setItem('tileViewMode', mode); } catch(e) {}
  };
  
  // 💫 BALANCE CHANGE ANIMATION
  const [balanceChange, setBalanceChange] = useState(null);
  const [balanceChangeKey, setBalanceChangeKey] = useState(0);
  const [hoverPreviewAmount, setHoverPreviewAmount] = useState(null); // 💫 Preview when hovering add buttons
  const [sellAllHoldProgress, setSellAllHoldProgress] = useState(0); // 0-100 progress for SELL ALL long-press
  const sellAllHoldTimer = useRef(null);
  const [mainWalletChange, setMainWalletChange] = useState(null);
  const [mainWalletChangeKey, setMainWalletChangeKey] = useState(0);
  
  // Track meaningful balance changes for animation
  const showBalanceChange = (amount) => {
    if (amount === 0) return;
    setBalanceChange(amount);
    setBalanceChangeKey(prev => prev + 1);
    // Auto-clear after animation
    setTimeout(() => setBalanceChange(null), 2000);
  };
  
  // Track main wallet changes for animation
  const showMainWalletChange = (amount) => {
    if (amount === 0) return;
    setMainWalletChange(amount);
    setMainWalletChangeKey(prev => prev + 1);
    // Auto-clear after animation
    setTimeout(() => setMainWalletChange(null), 2000);
  };
  
  // 🔄 Refresh balances from server
  const [refreshingBalances, setRefreshingBalances] = useState(false);
  const refreshBalances = async () => {
    if (!sessionId || !API_URL || refreshingBalances) return;
    setRefreshingBalances(true);
    try {
      const res = await fetch(`${API_URL}/api/balance`, {
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.gameBalance !== undefined) setGameWallet(data.gameBalance);
        if (data.handcashBalance !== undefined) setMainWallet(data.handcashBalance);
        if (data.handcashSpendable !== undefined) setMainWalletSpendable(data.handcashSpendable);
        if (data.handcashFiatBalance !== undefined) setMainWalletFiat(data.handcashFiatBalance);
        // 🎰 PLAYTHROUGH - Update withdrawable info
        if (data.withdrawable !== undefined) setWithdrawableBalance(data.withdrawable);
        if (data.locked !== undefined) setLockedBalance(data.locked);
        if (data.playthroughPercent !== undefined) setPlaythroughPercent(data.playthroughPercent);
        // 💵 BSV price from server
        if (data.bsvPrice && data.bsvPrice > 0) setBsvPrice(data.bsvPrice);
        console.log('🔄 Balances refreshed:', data);
      }
    } catch (e) {
      console.log('Refresh balances error:', e.message);
    }
    setRefreshingBalances(false);
  };
  
  const formatUsd = (sats) => {
    const usd = sats * satToUsd;
    // For common tip values, show exact amount without ~
    if (Math.abs(usd - 0.01) < 0.005) return '$0.01';
    if (Math.abs(usd - 0.05) < 0.01) return '$0.05';
    if (Math.abs(usd - 0.10) < 0.02) return '$0.10';
    if (Math.abs(usd - 0.25) < 0.03) return '$0.25';
    if (Math.abs(usd - 1.00) < 0.05) return '$1.00';
    // For other amounts, show with ~ prefix
    if (usd < 0.01) return '<$0.01';
    if (usd < 10) return '~$' + usd.toFixed(2);
    if (usd < 100) return '~$' + usd.toFixed(1);
    return '~$' + Math.round(usd);
  };

  // ============== MARKET SENTIMENT ALGORITHM ==============
  // Based on current trading activity - NOT predictive of rug/moon!
  const calculateMarketSentiment = (token) => {
    if (!token) return { label: 'QUIET', emoji: '😐', color: '#888' };
    
    // Look at recent activity, NOT token fate
    const buyVol = token.totalBuyVolume || 0;
    const sellVol = token.totalSellVolume || 0;
    const totalVol = buyVol + sellVol;
    
    // Price movement
    const priceMultiple = (token.currentPrice || 1) / (token.initialPrice || 1);
    
    // Liquidity change
    const liquidityRatio = (token.liquidityPool || 10000) / (token.initialLiquidity || 10000);
    
    // Heavy selling = BEARISH
    if (totalVol > 10000 && sellVol > buyVol * 1.5) {
      return { label: 'BEARISH', emoji: '🐻', color: '#ff6b6b' };
    }
    
    // Heavy buying = BULLISH
    if (totalVol > 10000 && buyVol > sellVol * 1.5) {
      return { label: 'BULLISH', emoji: '🐂', color: '#00d26a' };
    }
    
    // Price dumping hard
    if (priceMultiple < 0.5) {
      return { label: 'DUMPING', emoji: '📉', color: '#ff3b69' };
    }
    
    // Price pumping
    if (priceMultiple > 2.0) {
      return { label: 'PUMPING', emoji: '🚀', color: '#00d26a' };
    }
    
    // Liquidity draining (suspicious)
    if (liquidityRatio < 0.5) {
      return { label: 'LOW LIQ', emoji: '⚠️', color: '#f0b90b' };
    }
    
    // High volume = active
    if (totalVol > 50000) {
      return { label: 'ACTIVE', emoji: '🔥', color: '#ff9500' };
    }
    
    // Mooned token - but only if liquidity is still healthy
    if (token.hasMooned) {
      const liquidityRatio = token.liquidityPool / (token.initialLiquidity || 50000000);
      if (liquidityRatio >= 0.10) { // At least 10% liquidity remaining
        // 🌟 LEGENDARY gets special treatment
        if (token.fate === 'LEGENDARY') {
          return { label: 'LEGENDARY', emoji: '🌟', color: '#ffd700' };
        }
        return { label: 'MOONED', emoji: '🌙', color: '#9b59b6' };
      } else {
        return { label: 'DYING', emoji: '💀', color: '#ff3b69' };
      }
    }
    
    // Balanced trading
    if (totalVol > 20000) {
      return { label: 'TRADING', emoji: '📊', color: '#00a8ff' };
    }
    
    // Low activity
    return { label: 'QUIET', emoji: '😐', color: '#666' };
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  // Format token age for display
  const formatAge = (birthTime) => {
    const ageMs = Date.now() - birthTime;
    const seconds = Math.floor(ageMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };
  
  // Get age color (newer = more green/exciting, older = more stable)
  const getAgeColor = (birthTime, lifespanType) => {
    const ageMs = Date.now() - birthTime;
    const seconds = Math.floor(ageMs / 1000);
    
    // Flash tokens (< 1 min old) are hot
    if (seconds < 60) return '#ff6b35'; // Orange - HOT
    if (seconds < 300) return '#f0b90b'; // Gold - Fresh
    if (seconds < 1800) return '#00d26a'; // Green - Active
    if (seconds < 3600) return '#888'; // Gray - Established
    return '#555'; // Dark gray - Old
  };

  // 😈 SAVAGE LOBBY ANNOUNCEMENTS - Humiliate losers, celebrate winners!
  const announceTrade = (handle, tokenTicker, profit, multiplier, tradeType) => {
    let message = '';
    let icon = '';
    const absProfit = Math.abs(profit);
    
    if (tradeType === 'RUGGED') {
      // RUGGED - Maximum humiliation
      const rugMessages = [
        `💀 ${handle} got RUGGED on ${tokenTicker}! -${formatPB(absProfit)} GONE`,
        `🪦 RIP ${handle}... ${tokenTicker} rugged them for -${formatPB(absProfit)}`,
        `☠️ ${handle} just donated ${formatPB(absProfit)} to ${tokenTicker} devs LMAO`,
        `🤡 ${handle} held ${tokenTicker} to ZERO! -${formatPB(absProfit)}`,
        `💸 ${handle} learned about rugs the hard way... -${formatPB(absProfit)}`,
        `🎪 REKT! ${handle} got clowned by ${tokenTicker} (-${formatPB(absProfit)})`,
      ];
      message = rugMessages[Math.floor(Math.random() * rugMessages.length)];
      icon = '💀';
      
    } else if (tradeType === 'HONEYPOT') {
      // HONEYPOT - Extra spicy humiliation
      const honeypotMessages = [
        `🍯 ${handle} got HONEYPOTTED on ${tokenTicker}! -${formatPB(absProfit)} TRAPPED`,
        `🪤 ${handle} walked right into ${tokenTicker}'s trap! -${formatPB(absProfit)}`,
        `🍯 LMAOOO ${handle} couldn't sell ${tokenTicker}! -${formatPB(absProfit)} stuck forever`,
        `😂 ${handle} tried to sell ${tokenTicker}... NOPE! -${formatPB(absProfit)}`,
      ];
      message = honeypotMessages[Math.floor(Math.random() * honeypotMessages.length)];
      icon = '🍯';
      
    } else if (profit <= 0) {
      // LOSS - Regular humiliation
      const lossMessages = [
        `📉 ${handle} bought the top on ${tokenTicker}! -${formatPB(absProfit)}`,
        `🤡 ${handle} panic sold ${tokenTicker} for -${formatPB(absProfit)}... classic`,
        `😭 ${handle} learned an expensive lesson on ${tokenTicker} (-${formatPB(absProfit)})`,
        `💸 ${handle} just donated -${formatPB(absProfit)} to the market`,
        `📄 ${handle} has paper hands! Sold ${tokenTicker} at a loss (-${formatPB(absProfit)})`,
        `🎯 ${handle} bought high, sold low on ${tokenTicker}... -${formatPB(absProfit)}`,
      ];
      message = lossMessages[Math.floor(Math.random() * lossMessages.length)];
      icon = '📉';
      
    } else if (multiplier >= 10) {
      // LEGENDARY WIN (10x+)
      const legendaryMessages = [
        `🚀🚀🚀 ${handle} hit ${multiplier.toFixed(1)}x on ${tokenTicker}!!! +${formatPB(profit)} LEGENDARY`,
        `👑 ${handle} is a GOD! ${multiplier.toFixed(1)}x on ${tokenTicker} = +${formatPB(profit)}`,
        `💎 DIAMOND HANDS ${handle}! ${multiplier.toFixed(1)}x moon on ${tokenTicker} (+${formatPB(profit)})`,
        `🏆 ${handle} just made GENERATIONAL WEALTH! +${formatPB(profit)} (${multiplier.toFixed(1)}x)`,
      ];
      message = legendaryMessages[Math.floor(Math.random() * legendaryMessages.length)];
      icon = '🚀';
      
    } else if (multiplier >= 5) {
      // HUGE WIN (5x+)
      const hugeMessages = [
        `🚀 ${handle} is PRINTING MONEY! +${formatPB(profit)} (${multiplier.toFixed(1)}x) on ${tokenTicker}`,
        `💰 ${handle} showing the lobby how it's DONE! +${formatPB(profit)}`,
        `🔥 ${handle} CRUSHED IT on ${tokenTicker}! ${multiplier.toFixed(1)}x = +${formatPB(profit)}`,
        `⭐ ${handle} hit a ${multiplier.toFixed(1)}x! +${formatPB(profit)} on ${tokenTicker}`,
      ];
      message = hugeMessages[Math.floor(Math.random() * hugeMessages.length)];
      icon = '🚀';
      
    } else if (multiplier >= 2) {
      // SOLID WIN (2x+)
      const solidMessages = [
        `🎉 ${handle} doubled up on ${tokenTicker}! +${formatPB(profit)} (${multiplier.toFixed(1)}x)`,
        `💰 ${handle} just bagged +${formatPB(profit)} on ${tokenTicker}!`,
        `✅ ${handle} exits ${tokenTicker} with +${formatPB(profit)} profit`,
        `💵 ${handle} cashed out ${tokenTicker} at ${multiplier.toFixed(1)}x (+${formatPB(profit)})`,
      ];
      message = solidMessages[Math.floor(Math.random() * solidMessages.length)];
      icon = '💰';
      
    } else if (multiplier < 1.2) {
      // PAPER HANDS (tiny profit)
      const paperMessages = [
        `📄 ${handle} paper handed ${tokenTicker} for +${formatPB(profit)}... it's still pumping btw`,
        `🧻 ${handle} took crumbs (+${formatPB(profit)}) while ${tokenTicker} moons without them`,
        `😅 ${handle} sold ${tokenTicker} for +${formatPB(profit)}... weak hands!`,
        `📄 ${handle} couldn't handle the pressure! +${formatPB(profit)} on ${tokenTicker}`,
      ];
      message = paperMessages[Math.floor(Math.random() * paperMessages.length)];
      icon = '📄';
      
    } else {
      // NORMAL WIN
      const winMessages = [
        `✅ ${handle} profited +${formatPB(profit)} on ${tokenTicker} (${multiplier.toFixed(1)}x)`,
        `💰 ${handle} wins +${formatPB(profit)} from ${tokenTicker}`,
        `📈 ${handle} sold ${tokenTicker} for +${formatPB(profit)} profit`,
      ];
      message = winMessages[Math.floor(Math.random() * winMessages.length)];
      icon = '💰';
    }
    
    // Add to lobby chat
    setLobbyChat(prev => [...prev.slice(-25), {
      id: 'trade_' + Date.now(),
      handle: icon,
      text: message,
      isAction: true,
      isTradeAnnouncement: true,
      isWin: profit > 0,
      isLoss: profit <= 0,
      isRug: tradeType === 'RUGGED' || tradeType === 'HONEYPOT',
    }]);
  };

  // ============== TOKEN GENERATION (BSV randomness in production) ==============
  // NOTE: In production, use BSV blockchain for provably fair randomness
  // Token names should never repeat within 4 days (96 hours)
  // Server should track generated tokens and exclude recent ones
  
  // Extensive word lists for unique combinations (15 * 30 * 25 = 11,250+ combos)
  const TOKEN_PREFIXES = [
    // Animals
    'DOGE', 'SHIB', 'FLOKI', 'CAT', 'PEPE', 'APE', 'BEAR', 'BULL', 'WOLF', 'FOX', 
    'PANDA', 'TIGER', 'LION', 'DRAGON', 'SNAKE', 'FROG', 'HAMSTER', 'WHALE', 'SHARK', 'EAGLE',
    // Meme Culture  
    'WOJAK', 'CHAD', 'VIRGIN', 'GIGACHAD', 'COOMER', 'DOOMER', 'BLOOMER', 'ZOOMER', 'BOOMER', 'NPC',
    // Crypto Terms
    'MOON', 'SAFE', 'BABY', 'MEGA', 'SUPER', 'ULTRA', 'HYPER', 'TURBO', 'NITRO', 'ROCKET',
    'ALPHA', 'BETA', 'SIGMA', 'GIGA', 'OMEGA', 'DELTA', 'GAMMA', 'THETA', 'KAPPA', 'LAMBDA',
    // Tech/AI
    'CYBER', 'QUANTUM', 'NEURAL', 'SYNTH', 'MATRIX', 'PIXEL', 'VIRTUAL', 'META', 'CRYPTO', 'BLOCK',
    // Food
    'SUSHI', 'PIZZA', 'BURGER', 'TACO', 'RAMEN', 'CAKE', 'COOKIE', 'DONUT', 'BANANA', 'MANGO',
    // Random Cool
    'DARK', 'LIGHT', 'NEON', 'LASER', 'PLASMA', 'COSMIC', 'ASTRO', 'STELLAR', 'GALACTIC', 'NOVA',
    'KING', 'QUEEN', 'LORD', 'EMPEROR', 'NINJA', 'SAMURAI', 'VIKING', 'SPARTAN', 'TITAN', 'LEGEND'
  ];
  
  const TOKEN_SUFFIXES = [
    // Classic Crypto
    'INU', 'COIN', 'TOKEN', 'SWAP', 'CHAIN', 'VERSE', 'WORLD', 'LAND', 'REALM', 'ZONE',
    'DAO', 'FI', 'DEX', 'PAD', 'STAKE', 'YIELD', 'FARM', 'POOL', 'VAULT', 'BANK',
    // Tech
    'AI', 'GPT', 'BOT', 'NET', 'TECH', 'LABS', 'HUB', 'NODE', 'LINK', 'SYNC',
    'PROTOCOL', 'NETWORK', 'SYSTEM', 'ENGINE', 'CORE', 'BASE', 'LAYER', 'BRIDGE', 'PORT', 'GATE',
    // Hype Words
    'MOON', 'MARS', 'LAMBO', 'ROCKET', 'PUMP', 'SEND', 'WAGMI', 'GMI', 'HODL', 'FOMO',
    'X', 'PLUS', 'PRO', 'MAX', 'ULTRA', 'PRIME', 'ELITE', 'VIP', 'GOLD', 'DIAMOND',
    // Meme
    'ELON', 'DEGEN', 'APE', 'FREN', 'BASED', 'COPE', 'SEETHE', 'MALD', 'RATIO', 'STONKS'
  ];
  
  const CATEGORIES = [
    'Meme Coins', 'AI Coins', 'Cat Coins', 'Dog Coins', 'Food Coins', 
    'Trust Me Bro', 'Degen Plays', 'New Launch', 'Micro Cap',
    'Animal Coins', 'Gaming Tokens', 'NFT Adjacent', 'Elon Tweets',
    'Reddit Pump', '4chan Special', 'TikTok Viral', 'CT Darling'
  ];
  
  // Track recently used names to avoid duplicates
  const usedTokenNames = useRef(new Set());
  
  const generateTokenName = () => {
    let attempts = 0;
    let name;
    
    do {
      const prefix = TOKEN_PREFIXES[Math.floor(Math.random() * TOKEN_PREFIXES.length)];
      const suffix = TOKEN_SUFFIXES[Math.floor(Math.random() * TOKEN_SUFFIXES.length)];
      
      // Avoid duplicates like "MOONMOON" or "APEDAPE"
      if (prefix === suffix || prefix.includes(suffix) || suffix.includes(prefix)) {
        continue;
      }
      
      // Add random number sometimes for extra uniqueness (30% chance)
      const num = Math.random() > 0.7 ? Math.floor(Math.random() * 999) : '';
      name = prefix + suffix + num;
      attempts++;
    } while (usedTokenNames.current.has(name) && attempts < 50);
    
    // Add to used names, clear old ones if too many
    usedTokenNames.current.add(name);
    if (usedTokenNames.current.size > 200) {
      // Keep only last 100 names
      const arr = Array.from(usedTokenNames.current);
      usedTokenNames.current = new Set(arr.slice(-100));
    }
    
    return name;
  };
  
  const generateSingleToken = (id) => {
    const name = generateTokenName();
    const category = CATEGORIES[Math.floor(Math.random() * CATEGORIES.length)];
    
    // Lifecycle determines base rug chance
    // SUSTAINABLE CONFIG: ~18% house edge (like slots)
    // Reduced rug rates for fairer gameplay while maintaining excitement
    const lifecycles = [
      { type: 'quickPump', rugChance: 0.58, label: '42% SAFE' },    // 58% rug - balanced for 25% house edge
      { type: 'mediumRunner', rugChance: 0.52, label: '48% SAFE' }, // 52% rug
      { type: 'longRunner', rugChance: 0.42, label: '58% SAFE' },   // 42% rug
      { type: 'moonshot', rugChance: 0.28, label: '72% SAFE' },     // 28% rug
    ];
    const lifecycle = lifecycles[Math.floor(Math.random() * lifecycles.length)];
    
    // 🔮 ALPHABOT VETTED: 40% of moonshots get vetted with better odds (5% rug vs 15%)
    let isAlphaVetted = false;
    let effectiveRugChance = lifecycle.rugChance;
    
    if (lifecycle.type === 'moonshot' && Math.random() < 0.40) {
      isAlphaVetted = true;
      effectiveRugChance = 0.05; // Only 5% rug chance for vetted moonshots!
    }
    
    // Determine fate based on rugChance (use effective rug chance for vetted tokens)
    const roll = Math.random();
    let fate, rugType, moonMultiplier, lifespan, lifespanType;
    
    if (roll < effectiveRugChance * 0.28) { fate = 'RUG'; rugType = 'HARD'; }
    else if (roll < effectiveRugChance * 0.48) { fate = 'RUG'; rugType = 'TRAP'; }
    else if (roll < effectiveRugChance * 0.65) { fate = 'RUG'; rugType = 'SLOW'; }
    else if (roll < effectiveRugChance * 0.80) { fate = 'RUG'; rugType = 'HONEYPOT'; }
    else if (roll < effectiveRugChance) { fate = 'RUG'; rugType = 'WINTERMUTE'; } // 🧊 The silent market maker
    else if (roll < effectiveRugChance + (1 - effectiveRugChance) * 0.65) { fate = 'MOON'; moonMultiplier = 1.4 + Math.random() * 0.8; }  // 1.4-2.2x - balanced for house edge
    else { fate = 'SUPERMOON'; moonMultiplier = 2.2 + Math.random() * 1.6; }  // 2.2-3.8x - balanced for house edge
    
    // ============== REALISTIC LIFESPANS (CAPPED AT 2 HOURS) ==============
    // Different token types have different lifespans
    // Max 2 hours to keep gameplay engaging and prevent stale positions
    
    if (fate === 'SUPERMOON') {
      // Legendary runs - rare tokens that can run for up to 2 hours
      const supermoonRoll = Math.random();
      if (supermoonRoll < 0.4) {
        // 40% chance: Quick supermoon (5-15 minutes)
        lifespan = (5 + Math.random() * 10) * 60 * 1000;
        lifespanType = 'QUICK_SUPER';
      } else if (supermoonRoll < 0.7) {
        // 30% chance: Standard supermoon (15-45 minutes)  
        lifespan = (15 + Math.random() * 30) * 60 * 1000;
        lifespanType = 'STANDARD_SUPER';
      } else if (supermoonRoll < 0.9) {
        // 20% chance: Extended supermoon (45-90 minutes)
        lifespan = (45 + Math.random() * 45) * 60 * 1000;
        lifespanType = 'EXTENDED_SUPER';
      } else {
        // 10% chance: LEGENDARY (90-120 minutes) - ultra rare, max 2 hours
        lifespan = (90 + Math.random() * 30) * 60 * 1000;
        lifespanType = 'LEGENDARY';
        moonMultiplier = 4 + Math.random() * 2; // 4-6x for legendary - balanced for house edge
      }
    } else if (fate === 'MOON') {
      // Normal moons - minutes to an hour
      const moonRoll = Math.random();
      if (moonRoll < 0.4) {
        // 40% chance: Quick moon (2-5 minutes)
        lifespan = (2 + Math.random() * 3) * 60 * 1000;
        lifespanType = 'QUICK_MOON';
      } else if (moonRoll < 0.8) {
        // 40% chance: Standard moon (5-20 minutes)
        lifespan = (5 + Math.random() * 15) * 60 * 1000;
        lifespanType = 'STANDARD_MOON';
      } else {
        // 20% chance: Extended moon (20-60 minutes)
        lifespan = (20 + Math.random() * 40) * 60 * 1000;
        lifespanType = 'EXTENDED_MOON';
      }
    } else {
      // RUG tokens - seconds to minutes
      if (rugType === 'HARD') {
        // Flash rug - 15-45 seconds, no time to react
        lifespan = (15 + Math.random() * 30) * 1000;
        lifespanType = 'FLASH';
      } else if (rugType === 'TRAP') {
        // Trap - pumps then dumps, 1-3 minutes
        lifespan = (60 + Math.random() * 120) * 1000;
        lifespanType = 'TRAP';
      } else if (rugType === 'SLOW') {
        // Slow bleed - 3-10 minutes, death by a thousand cuts
        lifespan = (180 + Math.random() * 420) * 1000;
        lifespanType = 'SLOW_BLEED';
      } else if (rugType === 'WINTERMUTE') {
        // 🧊 Wintermute - 4-8 minutes, sophisticated pump and dump
        // Accumulate quietly → Pump hard → Distribute slowly → Exit fast
        lifespan = (240 + Math.random() * 240) * 1000;
        lifespanType = 'WINTERMUTE';
      } else {
        // Honeypot - looks legit, 2-8 minutes
        lifespan = (120 + Math.random() * 360) * 1000;
        lifespanType = 'HONEYPOT';
      }
    }
    
    // ============== REAL TOKENOMICS ENGINE ==============
    // AMM-style: Price = Liquidity Pool / Token Reserve
    // This creates realistic price curves where buys push price up and sells push it down
    
    const tokenomicsTiers = [
      { type: 'nano', totalSupply: 1000000, initialLiquidity: 500000 + Math.random() * 200000 },      // 1M tokens, 500K-700K liq
      { type: 'micro', totalSupply: 10000000, initialLiquidity: 500000 + Math.random() * 300000 },  // 10M tokens, 500K-800K liq
      { type: 'small', totalSupply: 100000000, initialLiquidity: 600000 + Math.random() * 400000 }, // 100M tokens, 600K-1M liq
      { type: 'medium', totalSupply: 1000000000, initialLiquidity: 700000 + Math.random() * 500000 }, // 1B tokens, 700K-1.2M liq
    ];
    const tier = tokenomicsTiers[Math.floor(Math.random() * tokenomicsTiers.length)];
    
    // Initial state
    const totalSupply = tier.totalSupply;
    const initialLiquidity = Math.floor(tier.initialLiquidity);
    
    // Token reserve starts at 100% of supply (all tokens in pool)
    // As people buy, tokens leave the pool and go to their wallets
    const tokenReserve = totalSupply;
    
    // Initial price = liquidity / tokenReserve (in sats per token)
    const initialPrice = initialLiquidity / tokenReserve;
    
    // Market cap = price * total supply (FDV - fully diluted valuation)
    // In real memecoins, mcap is typically 5-20x liquidity at launch
    // We simulate this by using a higher "perceived" price for mcap calculation
    // This represents the "paper value" vs actual extractable liquidity
    const mcapMultiplier = 5 + Math.random() * 15; // 5-20x liquidity
    const initialMarketCap = Math.floor(initialLiquidity * mcapMultiplier);
    
    return {
      id: 'token_' + Date.now() + '_' + id,
      ticker: '$' + name,
      name,
      category,
      lifecycle: lifecycle.type,
      lifecycleLabel: lifecycle.label,
      rugChance: lifecycle.rugChance,
      isAlphaVetted, // 🔮 Premium-only: vetted moonshots have 5% rug instead of 15%
      fate,
      rugType,
      moonMultiplier,
      lifespan,
      lifespanType, // FLASH, TRAP, SLOW_BLEED, HONEYPOT, QUICK_MOON, STANDARD_MOON, EXTENDED_MOON, LONG_RUNNER, DAY_TRADER, DIAMOND_ONLY, LEGENDARY
      birthTime: Date.now(),
      visibleAt: Date.now() + 5000, // 🕵️ INSIDER: Free users see tokens 5 sec after spawn
      expiresAt: Date.now() + lifespan + 30000,
      state: 'active',
      price: 1.0, // Display price (multiplier from launch)
      priceChange: 0,
      playerCount: Math.floor(Math.random() * 15) + 3,
      spectatorCount: Math.floor(Math.random() * 100) + 20,
      age: 0,
      
      // ===== TOKENOMICS =====
      totalSupply,              // Total tokens that exist
      tokenReserve,             // Tokens currently in liquidity pool
      liquidityPool: initialLiquidity, // Sats in liquidity pool
      initialLiquidity,         // Starting liquidity (for reference)
      initialPrice,             // Starting price per token
      currentPrice: initialPrice, // Current price per token (in sats)
      marketCap: initialMarketCap, // Current market cap
      mcapTier: tier.type,
      
      // Volume tracking
      totalBuyVolume: 0,
      totalSellVolume: 0,
      tradeCount: 0,
    };
  };

  const generateMockTokens = () => {
    return Array.from({ length: 5 }, (_, i) => generateSingleToken(i));
  };

  const generateMockChat = () => {
    // Initial generic messages - will be replaced with dynamic ones
    return [
      { id: 1, handle: '$diamond_dan', text: 'ready to trade! 🚀' },
      { id: 2, handle: '$whale_watcher', text: 'looking for the next moon' },
      { id: 3, handle: '$degen_dave', text: 'moonshots only today' },
    ];
  };
  
  // Dynamic chat that references current tokens - NOW WITH FOMO!
  const generateDynamicChat = (tokens) => {
    if (!tokens || tokens.length === 0) return null;
    
    const token = tokens[Math.floor(Math.random() * tokens.length)];
    const handles = ['$diamond_dan', '$whale_watcher', '$moon_mike', '$degen_dave', '$crypto_karen', '$hodl_harry', '$paper_pete', '$rug_survivor', '$alpha_andy', '$lucky_lucy'];
    const handle = handles[Math.floor(Math.random() * handles.length)];
    
    // 30% chance of "premium user" message (creates FOMO for free users)
    const isPremiumUser = Math.random() < 0.30;
    
    // Different message pools
    const regularMessages = [
      `anyone in ${token.ticker}? ${token.priceChange > 0 ? 'looking bullish' : 'looking risky'}`,
      `${token.ticker} ${token.priceChange > 10 ? 'pumping!' : token.priceChange < -5 ? 'dumping...' : 'consolidating'}`,
      `just entered ${token.ticker} 🎲`,
      `${token.ticker} giving me ${token.priceChange > 0 ? 'good vibes' : 'bad vibes'}`,
      `who's watching ${token.ticker}?`,
      `${token.ticker} chart looks ${Math.random() > 0.5 ? 'interesting' : 'scary'} ngl`,
      `${token.category} plays are ${Math.random() > 0.5 ? 'hot' : 'dead'} rn`,
      `thinking about aping ${token.ticker}`,
    ];
    
    // Premium user flex messages - creates MAJOR FOMO
    const premiumFlexMessages = [
      `AlphaBot just warned me about ${token.ticker} 🔮`,
      `+${formatPB(Math.floor(Math.random() * 500) + 100)} on that last trade 💰`,
      `premium paying for itself today 👑`,
      `AlphaBot signal was right again! 📈`,
      `50% less slippage is huge on big sells`,
      `just hit a 2.5x on a moonshot token 🚀`,
      `AlphaBot told me to exit before the rug 🔮`,
      `premium + patience = profit`,
      `another clean exit thanks to AlphaBot`,
      `moonshot tokens hitting different today 🌙`,
      `that vetted moonshot was free money 🔮`,
      `AlphaBot vetted tokens are ez mode`,
      `95% safe rating = basically guaranteed 💎`,
      `only playing vetted moonshots now, too easy`,
      `AlphaBot's vetting saved me again 🔮`,
    ];
    
    // Win celebration messages (mix of premium and non-premium)
    const winMessages = [
      `LFG! +${formatPB(Math.floor(Math.random() * 300) + 50)}! 🚀`,
      `sold the top! 📈`,
      `paper hands paid off this time 😅`,
      `in and out, easy ${formatPB(Math.floor(Math.random() * 200) + 50)}`,
      `that pump was REAL`,
    ];
    
    // Loss/rug messages (keeps it real)
    const lossMessages = [
      `got rugged on ${token.ticker} 💀`,
      `honeypotted again... when will I learn`,
      `rip my ${formatPB(Math.floor(Math.random() * 200) + 50)}`,
      `shoulda sold earlier...`,
    ];
    
    // Pick message type
    let text;
    const roll = Math.random();
    
    if (isPremiumUser) {
      // Premium users flex their advantages
      text = premiumFlexMessages[Math.floor(Math.random() * premiumFlexMessages.length)];
    } else if (roll < 0.15) {
      // 15% wins
      text = winMessages[Math.floor(Math.random() * winMessages.length)];
    } else if (roll < 0.25) {
      // 10% losses (keeps it real)
      text = lossMessages[Math.floor(Math.random() * lossMessages.length)];
    } else {
      // Regular market chat
      text = regularMessages[Math.floor(Math.random() * regularMessages.length)];
    }
    
    return {
      id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      handle,
      text,
      isPremiumUser, // Flag for gold name color
    };
  };

  // ============== HANDCASH + SERVER INTEGRATION ==============
  // HandCash App ID (from dashboard.handcash.io)
  const HANDCASH_APP_ID = '692be42bba0df1513b5c6ad8';

  // Generate keypair for HandCash v3 authentication
  const generateAuthenticationKeyPair = () => {
    const privateKey = secp256k1.utils.randomPrivateKey();
    const publicKey = secp256k1.getPublicKey(privateKey, true);
    return {
      privateKey: Array.from(privateKey).map(b => b.toString(16).padStart(2, '0')).join(''),
      publicKey: Array.from(publicKey).map(b => b.toString(16).padStart(2, '0')).join(''),
    };
  };

  // Redirect to HandCash v3 auth (generates keypair and redirects)
  const redirectToHandCashAuth = () => {
    const { privateKey, publicKey } = generateAuthenticationKeyPair();
    localStorage.setItem('paperHands_privateKey', privateKey);
    window.location.href = `https://handcash.io/connect?appId=${HANDCASH_APP_ID}&publicKey=${publicKey}`;
  };

  // Server URL - connects to HandCash API server
  // API URL - uses environment variable for production, localhost for dev
  const API_URL = process.env.REACT_APP_API_URL || 'https://paper-hands-bitcoinsv-game-production.up.railway.app';

  // ============== DEGEN AI HELPER FUNCTIONS ==============
  // Fetch AI status on login (accepts sessionId param since React state may not be updated yet)
  const fetchDegenAIStatus = async (sid) => {
    const session = sid || sessionId;
    if (!session) {
      console.log('DEGEN AI: No session ID, skipping status fetch');
      return;
    }
    try {
      console.log('🤖 DEGEN AI: Fetching status from', `${API_URL}/api/ai/status`);
      const res = await fetch(`${API_URL}/api/ai/status`, {
        headers: { 'x-session-id': session }
      });
      console.log('🤖 DEGEN AI: Response status:', res.status, res.ok);
      if (res.ok) {
        const data = await res.json();
        console.log('🤖 DEGEN AI status:', data);
        setDegenAIStatus(data);
      } else {
        console.log('🤖 DEGEN AI: Non-OK response:', res.status);
      }
    } catch (e) {
      console.log('🤖 DEGEN AI status fetch FAILED:', e.message);
    }
  };

  // Send question to DEGEN AI
  const askDegenAI = async (question) => {
    if (!question.trim() || degenAILoading) return;

    // Add user message immediately
    const userMsg = { role: 'user', content: question, timestamp: Date.now() };
    setDegenAIMessages(prev => [...prev, userMsg]);
    setDegenAIInput('');
    setDegenAILoading(true);

    try {
      const res = await fetch(`${API_URL}/api/ai/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionId
        },
        body: JSON.stringify({ question })
      });

      const data = await res.json();

      if (!res.ok) {
        // Check if subscription is required (free questions exhausted)
        if (data.error === 'subscription_required') {
          setShowDegenAIUpsell(true);
          // Remove the user's question since we can't answer it
          setDegenAIMessages(prev => prev.slice(0, -1));
          setDegenAILoading(false);
          return;
        }

        // Handle other errors (rate limit, etc.)
        const errorMsg = {
          role: 'assistant',
          content: data.message || data.error || 'Something went wrong. Try again later.',
          isError: true,
          timestamp: Date.now()
        };
        setDegenAIMessages(prev => [...prev, errorMsg]);
      } else {
        // Success - add AI response
        const aiMsg = {
          role: 'assistant',
          content: data.response,
          isSubscriber: data.isSubscriber,
          freeRemaining: data.freeRemaining,
          timestamp: Date.now()
        };
        setDegenAIMessages(prev => [...prev, aiMsg]);

        // Update status with subscription info
        setDegenAIStatus(prev => ({
          ...prev,
          freeRemaining: data.freeRemaining,
          isSubscriber: data.isSubscriber,
          subscriptionExpires: data.subscriptionExpires
        }));
      }
    } catch (e) {
      const errorMsg = {
        role: 'assistant',
        content: 'Network error. Check your connection and try again.',
        isError: true,
        timestamp: Date.now()
      };
      setDegenAIMessages(prev => [...prev, errorMsg]);
    }

    setDegenAILoading(false);
  };

  // Subscribe to DEGEN AI ($5/month)
  const subscribeToDegenAI = async () => {
    setDegenAISubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionId
        }
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Success! Update status and close modal
        setDegenAIStatus(prev => ({
          ...prev,
          isSubscriber: true,
          subscriptionExpires: data.subscriptionExpires,
          freeRemaining: 999 // Unlimited
        }));
        setShowDegenAIUpsell(false);

        // Add welcome message
        const welcomeMsg = {
          role: 'assistant',
          content: data.message || 'Welcome to DEGEN AI Unlimited, ser! Ask me anything!',
          isSubscriber: true,
          timestamp: Date.now()
        };
        setDegenAIMessages(prev => [...prev, welcomeMsg]);

        // Show success toast
        showToast('success', 'DEGEN AI Unlimited activated! Ask away, degen!');
      } else {
        // Payment failed
        showToast('error', data.message || data.error || 'Payment failed. Check your HandCash balance.');
      }
    } catch (e) {
      showToast('error', 'Network error. Try again.');
    }
    setDegenAISubscribing(false);
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (degenAIMessagesEndRef.current) {
      degenAIMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [degenAIMessages]);

  // ============== CLOUDFLARE RUG SCANNER - HEALTH CHECK ==============
  useEffect(() => {
    const scanForCloudflareRug = async () => {
      console.log('🔍 Scanning for Cloudflare Rug...');
      setRugScanning(true);
      
      // Fun loading messages that cycle during scan
      const funMessages = [
        'Initializing quantum memecoin detectors...',
        'Bribing React code engines to behave...',
        'Checking if whales are awake...',
        'Verifying wallet integrity...',
        'Scanning blockchain for paper hands...',
        'Calibrating rug-pull detection matrix...',
        'Consulting the crypto gods...',
        'Warming up the diamond hands protocol...',
        'Almost there... HODL tight...',
      ];
      
      let messageIndex = 0;
      const messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % funMessages.length;
        setScanMessage(funMessages[messageIndex]);
      }, 700);
      
      // Add a minimum scan time for effect (6 seconds)
      const minScanTime = new Promise(resolve => setTimeout(resolve, 6000));
      
      try {
        // Try to hit HandCash API directly to check if it's up
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const res = await fetch(`${API_URL}/api/handcash-health`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        // Wait for minimum scan time
        await minScanTime;
        
        if (res.ok) {
          const data = await res.json();
          // DEBUG: Log full response
          console.log('🔍 Scanner received:', JSON.stringify(data));
          
          // Server returns { overall: { status, paymentApiDown }, checks: [...] }
          const status = data.overall?.status;
          const paymentApiDown = data.overall?.paymentApiDown;
          
          // Also check if any individual API checks failed
          const hasFailedChecks = data.checks?.some(c => c.status === 'error');
          
          console.log(`🔍 Scanner check: status=${status}, paymentApiDown=${paymentApiDown}, hasFailedChecks=${hasFailedChecks}`);
          
          // Detect rug if ANY of these are true:
          // 1. status is 'error'
          // 2. paymentApiDown is true (hive mind)
          // 3. Any individual check failed
          if (status === 'error' || paymentApiDown || hasFailedChecks) {
            const reason = status === 'error' ? 'status=error' : 
                          paymentApiDown ? 'paymentApiDown=true' : 
                          'API check failed';
            console.log(`🚨 CLOUDFLARE RUG DETECTED (${reason}):`, data.overall?.message);
            setRugDetected(true);
            // Don't set handcashDown here - only health poll controls it based on server canary state
          } else if (status === 'healthy' || status === 'slow' || status === 'very_slow') {
            if (!paymentApiDown) {
              console.log('✅ No Cloudflare Rug detected:', data.overall?.message);
              setRugDetected(false);
              // Don't set handcashDown here - only health poll controls it
            } else {
              console.log('⚠️ Health OK but paymentApiDown=true (canary triggered)');
              setRugDetected(true);
              // Don't set handcashDown here - only health poll controls it
            }
          } else {
            // Unknown status - assume rug
            console.log('🚨 CLOUDFLARE RUG DETECTED (unknown status):', status);
            setRugDetected(true);
            // Don't set handcashDown here - only health poll controls it
          }
        } else {
          console.log('🚨 CLOUDFLARE RUG DETECTED: Health endpoint error');
          setRugDetected(true);
          // Don't set handcashDown here - only health poll controls it
        }
      } catch (err) {
        console.log('🚨 CLOUDFLARE RUG DETECTED:', err.message);
        // If health check fails, rug detected!
        await minScanTime;
        setRugDetected(true);
        // Don't set handcashDown here - only health poll controls it
      } finally {
        clearInterval(messageInterval);
        setScanMessage('🎮 Your game is ready!');
        setTimeout(() => setRugScanning(false), 500); // Brief pause to show final message
      }
    };
    
    scanForCloudflareRug();
  }, [API_URL]);
  
  // Rescan function
  const rescanForRug = async () => {
    setRugScanning(true);
    setScanMessage('Re-checking the vibes...');
    console.log('🔍 Rescanning for Cloudflare Rug...');
    
    const funMessages = [
      'Re-checking the vibes...',
      'Poking the servers...',
      'Asking nicely if they\'re back...',
      'Running diagnostics...',
      'Almost there...',
    ];
    
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % funMessages.length;
      setScanMessage(funMessages[messageIndex]);
    }, 800);
    
    const minScanTime = new Promise(resolve => setTimeout(resolve, 6000));
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const res = await fetch(`${API_URL}/api/handcash-health`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      await minScanTime;
      
      if (res.ok) {
        const data = await res.json();
        console.log('🔍 Rescan received:', JSON.stringify(data));
        
        const status = data.overall?.status;
        const paymentApiDown = data.overall?.paymentApiDown;
        const hasFailedChecks = data.checks?.some(c => c.status === 'error');
        
        console.log(`🔍 Rescan check: status=${status}, paymentApiDown=${paymentApiDown}, hasFailedChecks=${hasFailedChecks}`);
        
        // Only clear rug if ALL conditions are good
        if ((status === 'healthy' || status === 'slow' || status === 'very_slow') && !paymentApiDown && !hasFailedChecks) {
          console.log('✅ Rug cleared! HandCash recovered:', data.overall?.message);
          setRugDetected(false);
          // Don't set handcashDown here - only health poll controls it
          serverRugAlertShownRef.current = false; // Reset for next outage
        } else {
          const reason = status === 'error' ? 'status=error' : 
                        paymentApiDown ? 'paymentApiDown' : 
                        hasFailedChecks ? 'API check failed' : 'unknown';
          console.log(`🚨 Rug still present (${reason})`);
          setRugDetected(true);
          // Don't set handcashDown here - only health poll controls it
        }
      }
    } catch (err) {
      console.log('🚨 Rug still present:', err.message);
    } finally {
      clearInterval(messageInterval);
      setScanMessage('🎮 Scan complete!');
      setTimeout(() => setRugScanning(false), 500);
    }
  };
  
  // Retry HandCash connection from within the game (degraded mode)
  const retryHandCashConnection = async () => {
    setRetryingConnection(true);
    console.log('🔄 Checking HandCash status...');
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const res = await fetch(`${API_URL}/api/handcash-health`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (res.ok) {
        const data = await res.json();
        console.log('🔍 Health check:', JSON.stringify(data.overall));
        
        const status = data.overall?.status;
        const paymentApiDown = data.overall?.paymentApiDown;
        const hasFailedChecks = data.checks?.some(c => c.status === 'error');
        
        // Only clear rug display if ALL conditions are good
        if ((status === 'healthy' || status === 'slow' || status === 'very_slow') && !paymentApiDown && !hasFailedChecks) {
          console.log('✅ HandCash recovered!');
          setRugDetected(false);
          // Don't set handcashDown here - only health poll controls it
          serverRugAlertShownRef.current = false;
        } else {
          const reason = paymentApiDown ? 'Payment API still down' : 
                        hasFailedChecks ? 'API checks failing' : 
                        `Status: ${status}`;
          console.log(`⚠️ HandCash still degraded: ${reason}`);
        }
      }
    } catch (err) {
      console.log('⚠️ Retry failed:', err.message);
    } finally {
      setRetryingConnection(false);
    }
  };
  
  // Play with rug detected (degraded mode)
  const playWithRugDetected = async () => {
    console.log('🎮 Playing despite Cloudflare Rug - degraded mode');
    setRugScannerDismissed(true);
    // Don't set handcashDown here - health poll will set it based on server state
    // Mark scanner as completed for this browser session
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('paperHands_scannerCompleted', 'true');
    }
    
    // Skip if auth already in progress
    if (handcashLoading) {
      console.log('⏭️ Auth already in progress, skipping degraded auth');
      return;
    }
    
    // Check if user has existing session (v3: privateKey)
    const storedPrivateKey = localStorage.getItem('paperHands_privateKey');

    if (storedPrivateKey) {
      // Existing user - restore session
      console.log('🔄 Restoring session for degraded mode...');
      setHandcashLoading(true);

      try {
        const res = await fetch(`${API_URL}/auth/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ privateKey: storedPrivateKey })
        });
        
        if (res.ok) {
          const data = await res.json();
          console.log('✅ Session restored in degraded mode');

          setSessionId(data.sessionId);
          setAuthValidated(true); // 🔐 Session validated by server
          localStorage.setItem('paperHands_session', data.sessionId);
          setHandcashUser({ 
            handle: '$' + data.handle, 
            displayName: data.displayName,
            avatarUrl: data.avatarUrl
          });
          setMainWallet(data.handcashBalance || 0);
          setMainWalletSpendable(data.handcashSpendable || 0);
          setMainWalletFiat(data.handcashFiatBalance || 0);
          setGameWallet(data.gameBalance || 0);
          setTotalDeposited(data.totalDeposited || 0);
          setTotalWithdrawn(data.totalWithdrawn || 0);
          setTxConnectionStatus('error'); // Show as error since HC is down
          setLastTxError('HandCash unavailable');

          // 💵 BSV price from server
          if (data.bsvPrice && data.bsvPrice > 0) {
            setBsvPrice(data.bsvPrice);
            setBsvPriceLoading(false);
          }

          // Load consent/tutorial status - only upgrade, never downgrade (prevents race conditions)
          if (data.rakeConsent) setRakeConsent(true);
          if (data.tutorialComplete) setTutorialComplete(true);
          
          // Load premium status - ALWAYS sync with server (server is source of truth)
          if (data.premiumExpires && data.premiumExpires > Date.now()) {
            setPremiumExpiresAt(data.premiumExpires);
            localStorage.setItem('paperHands_premiumExpires', data.premiumExpires.toString());
          } else if (data.premiumExpires === null || data.premiumExpires < Date.now()) {
            // Server says not premium - clear local state too
            setPremiumExpiresAt(0);
            localStorage.removeItem('paperHands_premiumExpires');
          }
          
          // Load stats
          if (data.stats) {
            setStats(prev => ({
              ...prev,
              totalTrades: data.stats.trades || 0,
              wins: data.stats.wins || 0,
              losses: data.stats.losses || 0,
              rugs: data.stats.rugs || 0,
              totalProfit: data.stats.totalProfit || 0,
              biggestWin: data.stats.biggestWin || 0,
              biggestLoss: data.stats.biggestLoss || 0,
            }));
          }
          
          // 🎮 Load player portal data
          setPlayerPortalData({
            withdrawable: data.withdrawable,
            locked: data.locked,
            bonusBalance: data.bonusBalance,
            bonusPlaythrough: data.bonusPlaythrough,
            bonusRequired: data.bonusRequired,
            playthroughPercent: data.playthroughPercent,
            totalRakePaid: data.totalRakePaid,
            rakeTransactions: data.rakeTransactions,
            totalDeposited: data.totalDeposited,
            totalWithdrawn: data.totalWithdrawn,
            recentTrades: data.recentTrades,
          });
          
          // Load positions
          if (data.positions && data.positions.length > 0) {
            const serverPositions = data.positions.map(p => ({
              id: p.id,
              tokenId: p.tokenId,
              tokenTicker: p.tokenTicker || p.tokenName,
              tokenCategory: p.tokenCategory || 'Unknown',
              size: p.size || p.tokens,
              invested: p.invested || p.totalInvested,
              entryPrice: p.entry || p.entryPrice,
              entry: p.entry || p.entryPrice,
              openedAt: p.openedAt || p.firstBuy,
              timestamp: p.openedAt || p.firstBuy,
              status: p.status || 'active',
              source: p.source,
            }));
            savePositions(serverPositions);
          }

          setHandcashConnected(true);
          setView('lobby');
          // Fetch DEGEN AI status after login (pass sessionId since state not updated yet)
          fetchDegenAIStatus(data.sessionId);
        } else {
          console.log('⚠️ Session restore failed, redirecting to HandCash auth');
          // Session invalid, redirect to HandCash v3 auth
          redirectToHandCashAuth();
        }
      } catch (err) {
        console.log('⚠️ Session restore error:', err.message);
        // On error, redirect to HandCash v3 auth
        redirectToHandCashAuth();
      } finally {
        setHandcashLoading(false);
      }
    } else {
      // New user - redirect to HandCash v3 auth
      console.log('🆕 New user - redirecting to HandCash auth...');
      redirectToHandCashAuth();
    }
  };
  
  // Proceed to connect screen after successful scan
  const proceedToConnect = () => {
    console.log('✅ Proceeding to connect - no rug detected');
    setRugScannerDismissed(true);
    // Mark scanner as completed for this browser session
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('paperHands_scannerCompleted', 'true');
    }
  };
  
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('paperHands_session') || null;
    }
    return null;
  });

  // 🔐 Track whether session has been validated by server (prevents 401s on stale localStorage sessions)
  const [authValidated, setAuthValidated] = useState(false);

  // 🚫 IDLE KICK - Stop all polling after 5 minutes of inactivity (saves egress $$)
  useEffect(() => {
    const checkIdle = setInterval(() => {
      const idleTime = Date.now() - lastUserActivityRef.current;
      if (idleTime > IDLE_KICK_MS && !isIdleKicked && sessionId) {
        console.log('⏰ IDLE KICK: No activity for 5 minutes, stopping polling');
        setIsIdleKicked(true);
      }
    }, 30000); // Check every 30 seconds
    return () => clearInterval(checkIdle);
  }, [isIdleKicked, sessionId]);

  // ⏱️ IDLE KICK COUNTDOWN - 60s countdown then force logout
  useEffect(() => {
    if (!isIdleKicked) {
      setIdleKickCountdown(60); // Reset countdown when not idle
      return;
    }

    const countdownInterval = setInterval(() => {
      setIdleKickCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          window.location.href = '/nuked.html';
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [isIdleKicked]);

  // 🌅 WARMUP COUNTDOWN - local tick every second for smooth display
  useEffect(() => {
    if (warmupSecondsRemaining <= 0) return;
    const timer = setInterval(() => {
      setWarmupSecondsRemaining(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [warmupSecondsRemaining > 0]);

  // 🎰 Auto-load playthrough stats when Business tab is selected
  useEffect(() => {
    if (adminTab === 'business' && adminData?.userRole === 'admin' && !playthroughStats && !playthroughStatsLoading) {
      setPlaythroughStatsLoading(true);
      fetch(`${API_URL}/api/admin/playthrough-stats`, {
        headers: { 'X-Session-Id': sessionId }
      })
        .then(res => res.json())
        .then(data => {
          setPlaythroughStats(data);
          setPlaythroughStatsLoading(false);
        })
        .catch(e => {
          console.error('Failed to auto-load playthrough stats:', e);
          setPlaythroughStatsLoading(false);
        });
    }
  }, [adminTab, adminData?.userRole, sessionId]);

  // 👶 Auto-load Baby Sitting data when tab is selected (matches Business tab pattern exactly)
  useEffect(() => {
    if (adminTab === 'babysitting' && adminData?.userRole === 'admin' && !babysittingData && !babysittingLoading) {
      console.log('👶 Baby Sitting: Starting fetch...');
      setBabysittingLoading(true);
      fetch(`${API_URL}/api/admin/babysitting`, {
        headers: { 'X-Session-Id': sessionId }
      })
        .then(res => {
          console.log('👶 Baby Sitting: Response status:', res.status);
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          console.log('👶 Baby Sitting: Data received:', data);
          if (data.error) {
            console.error('👶 Baby Sitting: Server error:', data.error);
            setBabysittingLoading(false);
            return;
          }
          setBabysittingData(data);
          setBabysittingLoading(false);
        })
        .catch(e => {
          console.error('👶 Baby Sitting: Failed to auto-load:', e);
          setBabysittingLoading(false);
        });
    }
  }, [adminTab, adminData?.userRole, sessionId]);

  // 📱 Auto-hide bottom nav on scroll down, show on scroll up
  useEffect(() => {
    if (view !== 'lobby') return;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - lastScrollY.current;
      
      // Only hide/show after scrolling a bit (threshold of 10px)
      if (Math.abs(scrollDiff) < 10) return;
      
      if (scrollDiff > 0 && currentScrollY > 100) {
        // Scrolling down & past header - hide nav
        setNavHidden(true);
      } else if (scrollDiff < 0) {
        // Scrolling up - show nav
        setNavHidden(false);
      }
      
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [view]);

  // 👋 LOGOUT ON PAGE CLOSE - Notify server immediately when user leaves
  useEffect(() => {
    if (!sessionId || !API_URL) return;
    
    const handleLogout = () => {
      // Use sendBeacon for reliable delivery on page close
      // Note: sendBeacon can't send headers, so we include sessionId in the body
      const data = JSON.stringify({ sessionId });
      navigator.sendBeacon?.(`${API_URL}/api/logout`, new Blob([data], { type: 'application/json' }));
    };
    
    // Listen for page close/navigation
    window.addEventListener('beforeunload', handleLogout);
    window.addEventListener('pagehide', handleLogout);
    
    return () => {
      window.removeEventListener('beforeunload', handleLogout);
      window.removeEventListener('pagehide', handleLogout);
    };
  }, [sessionId, API_URL]);
  
  // Fetch HandCash API health status
  useEffect(() => {
    if (!sessionId || !API_URL) return;
    
    const fetchHandcashHealth = async () => {
      try {
        const res = await fetch(`${API_URL}/api/handcash-health`);
        if (res.ok) {
          const data = await res.json();
          setHandcashHealth(data);
          
          // 🐝 Track hive reports for canary display
          if (data.overall?.hiveReports !== undefined) {
            setHiveReports(data.overall.hiveReports);
          }
          
          // 🐦 CANARY SYSTEM - Server controls degraded mode, we just sync to it
          if (data.overall?.paymentApiDown) {
            // Server says DOWN - sync our state
            if (!handcashDown) {
              console.log('🚨 Server reports Payment API DOWN - entering degraded mode');
              setHandcashDown(true);
            }
            // Only show modal ONCE per outage
            if (!serverRugAlertShownRef.current) {
              serverRugAlertShownRef.current = true;
              setRugModalMessage('server');
              setShowRugModal(true);
            }
          } else if (data.overall?.status !== 'error') {
            // Server says UP - sync our state
            if (handcashDown) {
              console.log('✅ Server reports Payment API recovered! Exiting degraded mode.');
              setHandcashDown(false);
              serverRugAlertShownRef.current = false; // Reset for next outage
            }
          }
        }
      } catch (e) {
        setHandcashHealth({ overall: { status: 'error', message: '❌ Offline', avgLatency: 0 } });
      }
    };
    
    fetchHandcashHealth();
    // Check every 60s - SKIP if tab hidden or idle kicked
    const interval = setInterval(() => {
      if (!document.hidden && !isIdleKicked) fetchHandcashHealth();
    }, 60000);
    return () => clearInterval(interval);
  }, [sessionId, API_URL]); // Note: handcashDown removed to prevent re-fetch loop
  
  // Fetch challenges from server when in lobby
  useEffect(() => {
    if (view !== 'lobby' || !sessionId || !API_URL) return;
    
    const fetchChallenges = async () => {
      try {
        const res = await fetch(`${API_URL}/api/challenges`, {
          headers: { 'X-Session-Id': sessionId }
        });
        if (res.ok) {
          const data = await res.json();
          setDailyChallenges(data.challenges || []);
        }
      } catch (e) {
        console.log('Failed to fetch challenges:', e.message);
      }
    };
    
    fetchChallenges();
  }, [view, sessionId, API_URL]);
  
  // Check for HandCash v3 auth callback on mount (redirect back from HandCash)
  // OR restore session from localStorage (returning user)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);

      // HandCash v3: Check for stored privateKey after redirect
      const storedPrivateKey = localStorage.getItem('paperHands_privateKey');

      // Check if this is a fresh redirect from HandCash (URL will have params or hash)
      const hasAuthCallback = window.location.search || window.location.hash;

      if (storedPrivateKey && hasAuthCallback) {
        // Fresh redirect from HandCash v3 - use stored private key
        console.log('🎉 HandCash v3 auth callback detected!');
        console.log('🔐 Private key found in localStorage');
        handleHandCashAuth(storedPrivateKey);
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        // Check for payment return (from HandCash Pay)
        const paymentParam = params.get('payment');
        const paymentType = params.get('type');

        if (paymentParam) {
          console.log('💳 Payment return detected:', paymentParam, paymentType);
          // Clean URL first
          window.history.replaceState({}, '', window.location.pathname);
          // Store for processing after auth restore
          localStorage.setItem('paperHands_completingPayment', JSON.stringify({
            paymentId: paymentParam,
            type: paymentType
          }));
        }

        // Check localStorage for returning user (v3: privateKey)
        if (storedPrivateKey) {
          console.log('🔄 Restoring session from localStorage (v3 privateKey)...');
          handleHandCashAuth(storedPrivateKey);
        } else {
          // Fallback: check for old authToken (legacy users)
          const storedToken = localStorage.getItem('paperHands_authToken');
          if (storedToken) {
            console.log('🔄 Legacy user detected - will need to re-login with v3 auth');
            // Clear old token and prompt re-login
            localStorage.removeItem('paperHands_authToken');
          }
          console.log('📍 No privateKey in localStorage. User needs to login.');
        }
      }
    }
  }, []);
  
  const handleHandCashAuth = async (privateKey) => {
    // Prevent duplicate auth calls (only skip if actively loading)
    if (handcashLoading) {
      console.log('⏭️ Auth already in progress, skipping...');
      return;
    }

    setHandcashLoading(true);

    // Store the private key (v3 auth)
    localStorage.setItem('paperHands_privateKey', privateKey);

    console.log('🔗 API_URL:', API_URL);

    // If we have a backend, send private key there
    if (API_URL) {
      try {
        console.log('📡 Sending v3 auth to server...');

        // Check for pending referral code
        const refCode = pendingReferralCode || localStorage.getItem('paperHands_pendingReferral');
        if (refCode) {
          console.log('🔗 Sending referral code:', refCode);
        }

        const res = await fetch(`${API_URL}/auth/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ privateKey, referralCode: refCode })
        });
        
        console.log('📥 Server response status:', res.status, res.ok);
        
        if (res.ok) {
          const data = await res.json();
          console.log('📦 Server data received:', data);
          console.log('💰 HandCash balance from server:', data.handcashBalance, 'spendable:', data.handcashSpendable, 'fiat:', data.handcashFiatBalance);

          setSessionId(data.sessionId);
          setAuthValidated(true); // 🔐 Session validated by server
          localStorage.setItem('paperHands_session', data.sessionId);
          setHandcashUser({ 
            handle: '$' + data.handle, 
            displayName: data.displayName,
            avatarUrl: data.avatarUrl
          });
          setMainWallet(data.handcashBalance || 0);
          setMainWalletSpendable(data.handcashSpendable || 0);
          setMainWalletFiat(data.handcashFiatBalance || 0);
          setGameWallet(data.gameBalance || 0);
          setTotalDeposited(data.totalDeposited || 0);
          setTotalWithdrawn(data.totalWithdrawn || 0);
          setTxConnectionStatus('ok'); // ✅ Connected successfully
          setLastTxError(null);

          // 💵 BSV price from HandCash (single source of truth)
          if (data.bsvPrice && data.bsvPrice > 0) {
            setBsvPrice(data.bsvPrice);
            setBsvPriceLoading(false);
            console.log('💵 BSV price from server:', data.bsvPrice);
          }

          // Load premium status from server (server is source of truth)
          if (data.premiumExpires && data.premiumExpires > Date.now()) {
            setPremiumExpiresAt(data.premiumExpires);
            localStorage.setItem('paperHands_premiumExpires', data.premiumExpires.toString());
            console.log('👑 Premium loaded from server, expires:', new Date(data.premiumExpires).toLocaleString());
          } else if (data.premiumExpires === null || (data.premiumExpires && data.premiumExpires < Date.now())) {
            // Server says not premium - clear local state
            setPremiumExpiresAt(0);
            localStorage.removeItem('paperHands_premiumExpires');
          }
          
          // 🎮 Load stats from server
          if (data.stats) {
            setStats(prev => ({
              ...prev,
              totalTrades: data.stats.trades || 0,
              wins: data.stats.wins || 0,
              losses: data.stats.losses || 0,
              rugs: data.stats.rugs || 0,
              totalProfit: data.stats.totalProfit || 0,
              biggestWin: data.stats.biggestWin || 0,
              biggestLoss: data.stats.biggestLoss || 0,
              bestWinStreak: data.stats.winStreak || 0,
              tipsSent: data.stats.tipsSent || 0,
              tipsReceived: data.stats.tipsReceived || 0,
            }));
            console.log('📊 Stats loaded from server:', data.stats);
          }
          
          // 🎒 Load positions from server
          if (data.positions && data.positions.length > 0) {
            const serverPositions = data.positions.map(p => ({
              id: p.id,
              tokenId: p.tokenId,
              tokenTicker: p.tokenTicker || p.tokenName,
              tokenCategory: p.tokenCategory || 'Unknown',
              size: p.size || p.tokens,
              invested: p.invested || p.totalInvested,
              entryPrice: p.entry || p.entryPrice,
              entry: p.entry || p.entryPrice,
              openedAt: p.openedAt || p.firstBuy,
              timestamp: p.openedAt || p.firstBuy,
              status: p.status || 'active',
              source: p.source,
            }));
            savePositions(serverPositions);
            console.log('📈 Positions loaded from server:', serverPositions.length);
          }
          
          // 💀 Load bags from server
          if (data.bags && data.bags.length > 0) {
            setBags(data.bags);
            console.log('💀 Bags loaded from server:', data.bags.length);
          }

          // 🧹 Track uncleared rugs - show modal if any exist
          if (data.unclearedRugs !== undefined) {
            setUnclearedRugs(data.unclearedRugs);
            if (data.unclearedRugs > 0) {
              console.log(`⚠️ User has ${data.unclearedRugs} uncleared rugs - must acknowledge`);
              setShowRugAcknowledgeModal(true);
            }
          }
          
          // 🚀 Load launched tokens from server
          if (data.launchedTokens && data.launchedTokens.length > 0) {
            setMyLaunchedTokens(data.launchedTokens);
            console.log('🚀 Launched tokens loaded from server:', data.launchedTokens.length);
          }
          
          // 📊 Load dailyStats from server tradeHistory
          if (data.recentTrades && data.recentTrades.length > 0) {
            const dayAgo = Date.now() - (24 * 60 * 60 * 1000);
            const last24hTrades = data.recentTrades.filter(t => t.timestamp > dayAgo);
            const loadedStats = {
              trades: last24hTrades,
              totalProfit: last24hTrades.reduce((sum, t) => sum + (t.profit || 0), 0),
              wins: last24hTrades.filter(t => (t.profit || 0) > 0).length,
              losses: last24hTrades.filter(t => (t.profit || 0) < 0 && t.type !== 'rugged').length,
              rugs: last24hTrades.filter(t => t.type === 'rugged').length,
            };
            setDailyStats(loadedStats);
            console.log('📊 Daily stats loaded from server:', loadedStats.wins, 'wins,', loadedStats.losses, 'losses,', loadedStats.rugs, 'rugs');
          }
          
          // 🏆 Load achievements from server
          if (data.achievements && data.achievements.length > 0) {
            const serverAchievements = {};
            data.achievements.forEach(a => serverAchievements[a] = true);
            setAchievements(prev => ({ ...prev, ...serverAchievements }));
            console.log('🏆 Achievements loaded from server:', data.achievements.length);
          }
          
          // 🎁 Load daily claim status from server
          if (data.lastDailyClaim) {
            setLastDailyClaim(data.lastDailyClaim);
            console.log('🎁 Daily claim loaded from server, last:', new Date(data.lastDailyClaim).toLocaleString());
          }
          if (data.totalDailyClaims !== undefined) {
            setTotalDailyClaims(data.totalDailyClaims);
            console.log('🎁 Streak days:', data.totalDailyClaims);
          }
          
          // 🎯 Load daily challenges from server
          if (data.dailyChallenges && data.dailyChallenges.challenges) {
            // Merge claimedRewards into each challenge's claimed property
            const claimedRewards = data.dailyChallenges.claimedRewards || [];
            const challengesWithClaimStatus = data.dailyChallenges.challenges.map(c => ({
              ...c,
              claimed: claimedRewards.includes(c.id)
            }));
            setDailyChallenges(challengesWithClaimStatus);
            console.log('🎯 Challenges loaded from server:', challengesWithClaimStatus.length, 'claimed:', claimedRewards.length);
          }
          
          // 👥 Load friends from server
          if (data.friends && data.friends.length > 0) {
            // We'll fetch full friend data after login
            console.log('👥 Friends count from server:', data.friends.length);
          }
          
          // 🔮 Load Rug Prophet data from server
          if (data.rugProphetBets) {
            // Bets are now properly cleared on server - no need for localStorage filtering
            setRugProphetBets(data.rugProphetBets);
            console.log('🔮 Rug Prophet bets loaded:', data.rugProphetBets.length);
          }
          if (data.rugProphetStats) {
            setRugProphetStats(data.rugProphetStats);
            console.log('🔮 Rug Prophet stats:', data.rugProphetStats);
          }
          
          // 🔗 Load referral data from server
          if (data.referralCode) {
            setReferralCode(data.referralCode);
            setReferralStats({
              referred: (data.referrals || []).length,
              earned: data.referralEarnings || 0,
              pending: 0,
              referrals: data.referrals || [],
              referredBy: data.referredBy || null,
              referralLink: `https://paperhand.fun/?ref=${data.referralCode}`
            });
            console.log('🔗 Referral data loaded:', data.referralCode, (data.referrals || []).length, 'referrals');
          }
          
          // 🎉 Show referral bonus toast if applicable
          if (data.referralBonus) {
            localStorage.removeItem('paperHands_pendingReferral');
            setPendingReferralCode(null);
            setTimeout(() => {
              showPepeToast('referralBonus', 'right', 5000);
              console.log('🎉 Referral bonus applied!', data.referralBonus);
            }, 1500);
          }
          
          // ⛓️ Load on-chain transaction history from server
          if (data.onChainTx && data.onChainTx.length > 0) {
            setTxHistory(data.onChainTx);
            console.log('⛓️ On-chain tx loaded:', data.onChainTx.length, 'transactions');
          }
          
          // 💰 Load rake consent and tracking - only upgrade, never downgrade (prevents race conditions)
          if (data.rakeConsent) setRakeConsent(true);
          setTotalRakePaid(data.totalRakePaid || 0);
          setRakeTransactions(data.rakeTransactions || 0);
          if (data.tutorialComplete) setTutorialComplete(true);
          
          // 📱 MOBILE-FRIENDLY: Don't show popups on login anymore!
          // Consent & tutorial now trigger when user tries to trade
          
          setHandcashConnected(true);
          setHandcashLoading(false);
          setView('lobby');
          // Tokens are now fetched by polling in useEffect - no mock generation!
          setLobbyChat(generateMockChat());
          // onlineCount is now set by real polling from /api/live

          // Fetch DEGEN AI status after login (pass sessionId since state not updated yet)
          fetchDegenAIStatus(data.sessionId);

          // 🐸 WELCOME BACK PEPE - slides in from left!
          setTimeout(() => {
            showPepeToast('welcomeBack', 'left', 4000);
          }, 1500);
          
          // 🏆 Fetch hall of fame for yesterday's champion banner
          setTimeout(() => {
            fetchHallOfFame();
          }, 2000);
          
          // 👥 Fetch full friends list
          setTimeout(() => {
            fetchFriends();
          }, 1000);
          
          console.log('✅ Auth complete, mainWallet:', data.handcashBalance, 'gameWallet:', data.gameBalance);
          
          // Check for pending payment to complete (user returned from HandCash Pay)
          const completingPayment = localStorage.getItem('paperHands_completingPayment');
          if (completingPayment) {
            localStorage.removeItem('paperHands_completingPayment');
            const payment = JSON.parse(completingPayment);
            console.log('💳 Completing pending payment:', payment);
            
            // Process the completed payment
            const currentSessionId = data.sessionId;
            setTimeout(async () => {
              try {
                const statusRes = await fetch(`${API_URL}/api/payment-status/${payment.paymentId}`, {
                  headers: { 'X-Session-Id': currentSessionId }
                });
                const statusData = await statusRes.json();
                console.log('💳 Payment status:', statusData);
                
                if (statusData.status === 'completed') {
                  if (payment.type === 'deposit') {
                    alert('✅ Deposit successful! Your Paper Bucks have been credited.');
                    window.location.reload();
                  } else if (payment.type === 'premium') {
                    alert('👑 Premium activated! Enjoy your benefits.');
                    window.location.reload();
                  }
                } else if (statusData.status === 'pending') {
                  console.log('⏳ Payment still pending, will check again...');
                  setTimeout(() => window.location.reload(), 3000);
                }
              } catch (e) {
                console.error('Error checking payment status:', e);
              }
            }, 1000);
          }
          
          return;
        }
      } catch (e) {
        console.log('❌ Server auth failed:', e.message);
        console.log('Full error:', e);
        // Don't fall back to mock - show error to user
        alert('Connection to game server failed. Please try again.\n\nError: ' + e.message);
        localStorage.removeItem('paperHands_privateKey');
        setHandcashLoading(false);
        return;
      }
    }

    // Client-only mode - SHOULD NOT HAPPEN IN PRODUCTION
    // If we get here, API_URL is not set which is a config error
    console.error('❌ API_URL not configured! Cannot authenticate.');
    alert('Game server not configured. Please contact support.');
    localStorage.removeItem('paperHands_privateKey');
    setHandcashLoading(false);
  };

  const connectHandCash = async () => {
    setHandcashLoading(true);
    console.log('🔗 Redirecting to HandCash v3 auth...');
    redirectToHandCashAuth();
  };

  // Disconnect HandCash
  const disconnectHandCash = () => {
    localStorage.removeItem('paperHands_privateKey');
    localStorage.removeItem('paperHands_session');
    setHandcashConnected(false);
    setHandcashUser(null);
    setSessionId(null);
    setAuthValidated(false); // 🔐 Clear auth validation
    setView('connect'); // Go back to login screen
  };

  // ============== LIVE MULTIPLAYER POLLING ==============
  // Poll server every 5 seconds for real-time updates
  const lastPollTimeRef = useRef(lastPollTime);
  
  // Keep refs updated
  useEffect(() => {
    lastPollTimeRef.current = lastPollTime;
  }, [lastPollTime]);
  
  useEffect(() => {
    if (!sessionId || !API_URL || !authValidated) return; // 🔐 Wait for auth validation

    const pollLiveData = async () => {
      try {
        // Include current token ID if viewing a token room
        const tokenParam = currentTokenRef.current?.id ? `&token=${encodeURIComponent(currentTokenRef.current.id)}` : '';
        console.log(`📡 Polling /api/live with sessionId: ${sessionId ? sessionId.slice(0, 20) + '...' : 'NONE'}`);
        const res = await fetch(`${API_URL}/api/live?since=${lastPollTimeRef.current}${tokenParam}`, {
          headers: { 'X-Session-Id': sessionId }
        });
        
        // 🔐 Handle session invalid - just log it, don't auto-logout
        if (res.status === 401 || res.status === 403) {
          console.log('⚠️ Session invalid response:', res.status);
          return;
        }
        
        if (res.ok) {
          const data = await res.json();
          
          // 🔐 Check for explicit session expired message (not just any "session" mention)
          if (data.error && (data.error.toLowerCase().includes('session expired') || data.error.toLowerCase().includes('session invalid'))) {
            console.log('⚠️ Session error:', data.error);
            alert('Session expired! Please do a hard reset:\n\n• Mobile: Close app completely and reopen\n• Desktop: Press Ctrl+Shift+R (or Cmd+Shift+R on Mac)\n• Or clear browser cache and refresh');
            disconnectHandCash();
            return;
          }
          
          setLiveData(data);
          setOnlineCount(data.onlineCount || 0);
          
          // Only update onlineUsers if the list actually changed (prevents flickering)
          const newUsers = data.onlineUsers || [];
          setOnlineUsers(prev => {
            // Quick comparison - if same handles, check if anything important changed
            if (prev.length === newUsers.length) {
              const prevHandles = prev.map(u => u.handle).sort().join(',');
              const newHandles = newUsers.map(u => u.handle).sort().join(',');
              if (prevHandles === newHandles) {
                // Same users - check if stats/tokens changed
                const hasChanges = newUsers.some(newU => {
                  const oldU = prev.find(p => p.handle === newU.handle);
                  return !oldU || 
                    oldU.currentToken !== newU.currentToken ||
                    oldU.isPremium !== newU.isPremium;
                });
                if (!hasChanges) return prev; // No change, skip re-render
              }
            }
            return newUsers;
          });
          
          console.log(`👥 Online users received: ${(data.onlineUsers || []).map(u => u.handle).join(', ')}`);
          
          // 🔊 Play join/leave sounds for player changes
          if (soundEnabled && newUsers.length > 0) {
            const currentHandles = new Set(newUsers.map(u => u.handle));
            const myHandle = handcashUser?.handle?.replace('$', '');
            
            // Detect new joins (in current but not in previous)
            currentHandles.forEach(handle => {
              if (!prevOnlineHandlesRef.current.has(handle) && handle !== myHandle) {
                // Only play if we had previous data (not first load)
                if (prevOnlineHandlesRef.current.size > 0) {
                  console.log(`👋 Player joined: ${handle}`);
                  Sounds.playerJoin();
                }
              }
            });
            
            // Detect leaves (in previous but not in current)
            prevOnlineHandlesRef.current.forEach(handle => {
              if (!currentHandles.has(handle) && handle !== myHandle) {
                console.log(`🚪 Player left: ${handle}`);
                Sounds.playerLeave();
              }
            });
            
            // Update previous handles ref
            prevOnlineHandlesRef.current = currentHandles;
          }
          
          setLastPollTime(data.serverTime || Date.now());
          
          // Update leaderboard
          if (data.leaderboard && data.leaderboard.length > 0) {
            setLiveLeaderboard(data.leaderboard);
          }
          
          // Update activity feed
          if (data.recentActivity && data.recentActivity.length > 0) {
            setLiveActivity(prev => {
              const newItems = data.recentActivity.filter(a => !prev.some(p => p.id === a.id));
              return [...newItems, ...prev].slice(0, 50);
            });
          }
          
          // Handle announcements (show as toasts)
          if (data.announcements && data.announcements.length > 0) {
            data.announcements.forEach(ann => {
              // Don't show your own join announcement
              if (ann.type === 'join' && handcashUser && ann.message.includes(handcashUser.handle.replace('$', ''))) {
                return;
              }
              
              // Skip if we've already processed this announcement (prevents spam/duplicate tips!)
              if (shownAnnouncementIds.current.has(ann.id)) {
                return;
              }
              
              // 💸 CHECK IF YOU RECEIVED A TIP!
              if (ann.type === 'tip' && ann.data && handcashUser) {
                const myHandle = handcashUser.handle.replace('$', '');
                if (ann.data.to === myHandle) {
                  // Mark as processed FIRST to prevent duplicates
                  shownAnnouncementIds.current.add(ann.id);
                  
                  // You got tipped! Show notification only - server already credited balance
                  setTipReceived({ from: ann.data.from, amount: ann.data.amount });
                  setBalanceFlash(ann.data.amount);
                  Sounds.tip(); // 🎁 Play cha-ching sound!
                  // Note: Don't add to gameWallet here - server already credited via /api/tip
                  // The next /api/live poll will sync the updated balance from server
                  
                  // Clear after animation
                  setTimeout(() => {
                    setTipReceived(null);
                    setBalanceFlash(null);
                  }, 4000);
                  return; // Don't show as regular toast
                }
              }
              
              // Show toast for new announcements - DISABLED FOR NOW
              if (!liveAnnouncements.some(a => a.id === ann.id)) {
                shownAnnouncementIds.current.add(ann.id);
                // Keep set from growing too large
                if (shownAnnouncementIds.current.size > 100) {
                  const firstId = shownAnnouncementIds.current.values().next().value;
                  shownAnnouncementIds.current.delete(firstId);
                }
                // setAnnouncementToast(ann);
                // setTimeout(() => setAnnouncementToast(null), 4000);
              }
            });
            setLiveAnnouncements(prev => {
              const newItems = data.announcements.filter(a => !prev.some(p => p.id === a.id));
              return [...newItems, ...prev].slice(0, 20);
            });
          }
          
          // Sync lobby chat from server
          if (data.lobbyChat && data.lobbyChat.length > 0) {
            // 🔊 Play tick sound for new lobby chat messages (not from me)
            if (soundEnabled) {
              const myHandle = handcashUser?.handle?.replace('$', '');
              const newMsgCount = data.lobbyChat.filter(msg => {
                const isNew = !prevLobbyChatIdsRef.current.has(msg.id);
                const isNotMine = msg.handle !== myHandle && msg.handle !== `$${myHandle}`;
                const isNotAction = !msg.isAction && !msg.isTradeAnnouncement;
                return isNew && isNotMine && isNotAction;
              }).length;
              
              // Play tick for new messages (max 3 to prevent spam)
              if (newMsgCount > 0 && prevLobbyChatIdsRef.current.size > 0) {
                const tickCount = Math.min(newMsgCount, 3);
                for (let i = 0; i < tickCount; i++) {
                  setTimeout(() => Sounds.tick(), i * 80); // Stagger ticks slightly
                }
              }
              
              // Update previous IDs ref
              data.lobbyChat.forEach(msg => prevLobbyChatIdsRef.current.add(msg.id));
              // Keep ref from growing too large
              if (prevLobbyChatIdsRef.current.size > 100) {
                const idsArray = Array.from(prevLobbyChatIdsRef.current);
                prevLobbyChatIdsRef.current = new Set(idsArray.slice(-50));
              }
            }
            
            setLobbyChat(prev => {
              // Keep local action messages (buy/sell notifications, etc)
              const localActionMsgs = prev.filter(p => p.isAction || p.isTradeAnnouncement);
              
              // Combine server messages with local action messages
              const allMsgs = [...data.lobbyChat, ...localActionMsgs];
              
              // Dedupe by id
              const uniqueMsgs = allMsgs.filter((msg, index, self) => 
                index === self.findIndex(m => m.id === msg.id)
              );
              
              // Sort by timestamp and keep last 30
              return uniqueMsgs
                .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
                .slice(-30);
            });
          }
          
          // Update global stats
          if (data.globalStats) {
            setGlobalStats(data.globalStats);
          }
          
          // Update featured champion (on-chain inscription winner)
          if (data.featuredChampion !== undefined) {
            setFeaturedChampion(data.featuredChampion);
          }
          
          // 🔐 INACTIVITY WARNING - show modal when 5 minutes or less remaining
          if (data.sessionTimeRemaining !== undefined) {
            setSessionTimeRemaining(data.sessionTimeRemaining);
            const fiveMinutes = 5 * 60 * 1000;
            if (data.sessionTimeRemaining <= fiveMinutes && data.sessionTimeRemaining > 0) {
              setShowInactivityWarning(true);
            } else {
              setShowInactivityWarning(false);
            }
          }
          
          // 🚀 NEW TOKEN LAUNCHES - instant notification + sound
          if (data.newTokenLaunches && data.newTokenLaunches.length > 0) {
            data.newTokenLaunches.forEach(launch => {
              // Add to userLaunchedTokens if not already there
              setUserLaunchedTokens(prev => {
                if (prev.some(t => t.id === launch.token.id)) return prev;
                console.log(`🚀 New user token detected: $${launch.token.ticker} by ${launch.creator}`);
                return [launch.token, ...prev];
              });
              
              // Play rocket launch sound!
              if (soundEnabled) {
                Sounds.tokenLaunch();
              }
            });
          }
        }
      } catch (e) {
        console.log('Live poll error:', e.message);
      }
    };
    
    // Initial fetch only - WebSocket handles all updates now
    pollLiveData();
    // No more polling interval - all updates come via WebSocket
  }, [sessionId, API_URL, authValidated]);

  // 💓 SESSION HEARTBEAT - Keep session alive via WebSocket (replaces /api/live polling)
  useEffect(() => {
    if (!sessionId || !wsRef.current) return;

    // Send heartbeat every 30 seconds to keep session alive
    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'heartbeat',
          currentToken: currentTokenRef.current?.id || null
        }));
      }
    }, 30000);

    // Send one immediately when connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'heartbeat',
        currentToken: currentTokenRef.current?.id || null
      }));
    }

    return () => clearInterval(heartbeatInterval);
  }, [sessionId, wsConnected]);  // Re-run when WebSocket connects

  // 🔄 POSITION SYNC: Initial load only (WebSocket position_update handles rug/moon pushes)
  // Removed 30s polling - positions now pushed via WS when tokens rug/moon
  
  // 💸 LOW BALANCE DETECTION: Prompt user to top off when below 1M PB
  useEffect(() => {
    // Only show once per session, when logged in, and when balance drops below 1M
    const LOW_BALANCE_THRESHOLD = 1000000; // 1M Paper Bucks
    
    if (sessionId && gameWallet > 0 && gameWallet < LOW_BALANCE_THRESHOLD && !lowBalanceShownRef.current && !showLowBalance) {
      // Small delay so it doesn't pop up immediately on login
      const timer = setTimeout(() => {
        if (gameWallet < LOW_BALANCE_THRESHOLD && !lowBalanceShownRef.current) {
          setShowLowBalance(true);
          lowBalanceShownRef.current = true;
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [sessionId, gameWallet, showLowBalance]);
  
  // Fetch Hall of Fame data
  const fetchHallOfFame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hall-of-fame`);
      if (res.ok) {
        const data = await res.json();
        setHallOfFame(data);
      }
    } catch (e) {
      console.log('Hall of fame fetch error:', e.message);
    }
  };
  
  // ============== FRIENDS SYSTEM FUNCTIONS ==============
  const fetchFriends = async () => {
    if (!sessionId || !API_URL || !authValidated) return; // 🔐 Wait for auth validation
    setFriendsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/friends`, {
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        setFriends(data.friends || []);
        setFriendRequestsSent(data.requestsSent || []);
        setFriendRequestsReceived(data.requestsReceived || []);
      }
    } catch (e) {
      console.log('Fetch friends error:', e.message);
    }
    setFriendsLoading(false);
  };
  
  const addFriend = async (friendHandle) => {
    if (!sessionId || !API_URL || !friendHandle.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/friends/add`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
        body: JSON.stringify({ friendHandle: friendHandle.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setFriendInput('');
        fetchFriends(); // Refresh list
        console.log(`👥 Added friend: ${friendHandle}`);
      } else {
        alert(data.error || 'Failed to add friend');
      }
    } catch (e) {
      console.log('Add friend error:', e.message);
    }
  };
  
  const removeFriend = async (friendHandle) => {
    if (!sessionId || !API_URL) return;
    try {
      const res = await fetch(`${API_URL}/api/friends/remove`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
        body: JSON.stringify({ friendHandle })
      });
      if (res.ok) {
        fetchFriends(); // Refresh list
        console.log(`👥 Removed friend: ${friendHandle}`);
      }
    } catch (e) {
      console.log('Remove friend error:', e.message);
    }
  };
  
  // ============== DIRECT MESSAGE FUNCTIONS ==============
  const fetchDMs = async () => {
    if (!sessionId || !API_URL) return;
    try {
      const res = await fetch(`${API_URL}/api/dms`, {
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        const conversations = data.conversations || [];
        
        // Check for new unread messages (not from us) - play sound only
        const myHandle = handcashUser?.handle?.replace('$', '');
        conversations.forEach(convo => {
          if (convo.unreadCount > 0 && convo.lastMessage) {
            const lastMsg = convo.lastMessage;
            // Only notify if this is a new message we haven't seen
            if (lastMsg.from !== myHandle &&
                (!lastDmCheckRef.current || lastMsg.timestamp > lastDmCheckRef.current)) {
              // Play notification sound (Paper Chat shows unread badge)
              playDmSound();
            }
          }
        });
        
        // Update last check time to newest message
        const newestTime = Math.max(...conversations.map(c => c.lastMessage?.timestamp || 0), 0);
        if (newestTime > 0) {
          lastDmCheckRef.current = newestTime;
        }
        
        setDmConversations(conversations);
        setDmUnreadCount(data.totalUnread || 0);
      }
    } catch (e) {
      console.log('Fetch DMs error:', e.message);
    }
  };
  
  // Play DM notification sound
  const playDmSound = () => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant notification tone (two quick notes)
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
      oscillator.frequency.setValueAtTime(1108, audioContext.currentTime + 0.1); // C#6
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('DM sound error:', e);
    }
  };
  
  // ============== WEBSOCKET CONNECTION FOR REAL-TIME CHAT ==============
  const connectWebSocket = () => {
    if (!sessionId || !API_URL) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    // Build WebSocket URL from API_URL
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    console.log('🔌 Connecting WebSocket to:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('🔌 WebSocket connected, authenticating...');
        ws.send(JSON.stringify({ type: 'auth', sessionId }));
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'auth_success') {
            console.log('🔌 WebSocket authenticated as $' + message.handle);
            setWsConnected(true);
            // Initial fetch to get existing conversations
            fetchDMs();

            // Version check - force refresh if server updated
            if (message.serverVersion) {
              const storedVersion = localStorage.getItem('serverVersion');
              if (storedVersion && storedVersion !== message.serverVersion) {
                console.log(`🔄 Server updated: ${storedVersion} → ${message.serverVersion}. Refreshing...`);
                localStorage.setItem('serverVersion', message.serverVersion);
                window.location.reload();
                return;
              }
              localStorage.setItem('serverVersion', message.serverVersion);
            }
          }
          
          if (message.type === 'auth_error') {
            console.log('🔌 WebSocket auth failed:', message.error);
            setWsConnected(false);
            // Clear reconnect timeout - don't retry with invalid session
            if (wsReconnectRef.current) {
              clearTimeout(wsReconnectRef.current);
              wsReconnectRef.current = null;
            }
            // Close the connection - don't let onclose trigger reconnect
            ws.onclose = null; // Remove handler before closing
            ws.close();
            return;
          }
          
          // Incoming tip
          if (message.type === 'tip_received') {
            const { from, amount } = message;
            setTipReceived({ from, amount });
            setBalanceFlash(amount);
            Sounds.tip();
            setTimeout(() => {
              setTipReceived(null);
              setBalanceFlash(null);
            }, 4000);
          }

          // Incoming DM
          if (message.type === 'dm_received') {
            const { dm, from } = message;
            console.log('💬 Received DM from $' + from);

            // Add to conversations
            setDmConversations(prev => {
              // 🐛 FIX: Case-insensitive handle matching
              const fromLower = from.toLowerCase();
              const existing = prev.find(c => c.handle.toLowerCase() === fromLower);
              if (existing) {
                return prev.map(c => c.handle.toLowerCase() === fromLower
                  ? {
                      ...c,
                      messages: [...(c.messages || []), dm],
                      lastMessage: dm,
                      unreadCount: (c.unreadCount || 0) + 1
                    }
                  : c
                );
              } else {
                return [...prev, {
                  handle: from,
                  messages: [dm],
                  lastMessage: dm,
                  unreadCount: 1
                }];
              }
            });

            // Update total unread
            setDmUnreadCount(prev => prev + 1);

            // Play sound and show toast if chat window is closed or viewing different conversation
            if (!showPaperChat || paperChatView.toLowerCase() !== from.toLowerCase()) {
              playDmSound();
              // Show floating toast notification
              const toastTime = Date.now();
              setDmToast({ from, text: dm.text, timestamp: toastTime });
              // Auto-dismiss after 6 seconds
              setTimeout(() => {
                setDmToast(prev => prev?.timestamp === toastTime ? null : prev);
              }, 6000);
            }
          }
          
          // Our sent DM confirmed
          if (message.type === 'dm_sent') {
            const { dm } = message;
            // Update local message with server ID
            setDmConversations(prev => {
              return prev.map(c => {
                if (c.handle.toLowerCase() === dm.to.toLowerCase()) {
                  // 🐛 FIX: Remove optimistic message by pending flag, then add confirmed
                  const messages = (c.messages || []).filter(m => !m.pending);
                  return {
                    ...c,
                    messages: [...messages, dm],
                    lastMessage: dm
                  };
                }
                return c;
              });
            });
          }
          
          // Typing indicator
          if (message.type === 'typing') {
            const { from } = message;
            setTypingUsers(prev => ({ ...prev, [from]: Date.now() }));
            // Clear typing after 3 seconds
            setTimeout(() => {
              setTypingUsers(prev => {
                const newState = { ...prev };
                if (Date.now() - newState[from] >= 2900) {
                  delete newState[from];
                }
                return newState;
              });
            }, 3000);
          }
          
          // Read confirmation
          if (message.type === 'marked_read') {
            const { from } = message;
            setDmConversations(prev => 
              prev.map(c => c.handle === from ? { ...c, unreadCount: 0 } : c)
            );
            // Recalculate total unread
            setDmUnreadCount(prev => {
              const convo = dmConversations.find(c => c.handle === from);
              return Math.max(0, prev - (convo?.unreadCount || 0));
            });
          }
          
          // User online/offline (optional - for presence)
          if (message.type === 'user_online') {
            console.log('👤 User online: $' + message.handle);
          }
          if (message.type === 'user_offline') {
            console.log('👤 User offline: $' + message.handle);
          }

          // ============ ADMIN BROADCAST - Show message to all players ============
          if (message.type === 'admin_broadcast') {
            console.log('📢 Admin broadcast from $' + message.from + ': ' + message.text);
            setReceivedBroadcast({
              text: message.text,
              msgType: message.msgType,
              from: message.from,
              timestamp: message.timestamp,
            });
            // Auto-dismiss after 15 seconds (can be dismissed manually too)
            setTimeout(() => setReceivedBroadcast(null), 15000);
          }

          // ============ LOBBY UPDATE (full data - replaces /api/tokens + /api/live polling) ============
          if (message.type === 'lobby_update') {
            const { tokens: newTokens, onlineUsers: newOnlineUsers, stats, leaderboard, featuredChampion: newChampion } = message;

            // Update tokens from WebSocket (full data now)
            if (newTokens && Array.isArray(newTokens)) {
              setActiveTokens(newTokens);
            }

            // Update online users - use same comparison logic to prevent flickering
            if (newOnlineUsers && Array.isArray(newOnlineUsers)) {
              setOnlineUsers(prev => {
                if (prev.length === newOnlineUsers.length) {
                  const prevHandles = prev.map(u => u.handle).sort().join(',');
                  const newHandles = newOnlineUsers.map(u => u.handle).sort().join(',');
                  if (prevHandles === newHandles) {
                    const hasChanges = newOnlineUsers.some(newU => {
                      const oldU = prev.find(p => p.handle === newU.handle);
                      return !oldU ||
                        oldU.currentToken !== newU.currentToken ||
                        oldU.isPremium !== newU.isPremium;
                    });
                    if (!hasChanges) return prev;
                  }
                }
                return newOnlineUsers;
              });
            }

            // Update global stats
            if (stats) {
              setGlobalStats(prev => ({ ...prev, ...stats }));
            }

            // Update leaderboard
            if (leaderboard && leaderboard.length > 0) {
              setLiveLeaderboard(leaderboard);
            }

            // Update featured champion
            if (newChampion !== undefined) {
              setFeaturedChampion(newChampion);
            }
          }
          
          // ============ TRADE NOTIFICATION (activity feed) ============
          if (message.type === 'trade') {
            const { trade } = message;
            // Add to activity feed
            setLiveActivity(prev => {
              const newActivity = {
                id: `trade_${Date.now()}`,
                type: trade.action,
                handle: trade.handle,
                token: trade.ticker,
                amount: trade.amount,
                profit: trade.profit,
                timestamp: trade.timestamp || Date.now(),
              };
              return [newActivity, ...prev].slice(0, 50);
            });
          }
          
          // ============ BALANCE UPDATE ============
          if (message.type === 'balance_update') {
            const { balance, reason } = message;
            if (typeof balance === 'number') {
              setGameWallet(balance);
              console.log(`💰 Balance updated: ${balance} (${reason})`);
            }
          }

          // ============ POSITION UPDATE (rug/moon push - eliminates 30s polling) ============
          if (message.type === 'position_update') {
            const { tokenId, result, position } = message;
            console.log(`📡 Position update: ${tokenId} ${result}`);

            if (position) {
              // Update the position in local state
              setPositions(prev => prev.map(p =>
                p.tokenId === tokenId && p.status === 'active'
                  ? { ...p, ...position }
                  : p
              ));
            }
          }

          // ============ HEALTH STATUS ============
          if (message.type === 'health_status') {
            const { healthy, degradedMode } = message;
            setHandcashDown(!healthy || degradedMode);
            console.log(`🏥 Health status: ${healthy ? 'HEALTHY' : 'DEGRADED'}`);
          }

          // ============ GAME VISIBILITY (admin can hide buttons from users) ============
          if (message.type === 'game_visibility') {
            const { visibility } = message;
            if (visibility) {
              setGameVisibility(visibility);
              console.log(`👁️ Game visibility updated: Rug Rush=${visibility.rugRushVisible}, Mountain=${visibility.mountainVisible}`);
            }
          }

          // ============ ANNOUNCEMENT (eliminates /api/live polling) ============
          if (message.type === 'announcement') {
            const { announcement: ann } = message;
            if (ann && !liveAnnouncements.some(a => a.id === ann.id)) {
              setLiveAnnouncements(prev => [ann, ...prev].slice(0, 20));

              // Handle tip received
              if (ann.type === 'tip' && ann.data && handcashUser) {
                const myHandle = handcashUser.handle.replace('$', '');
                if (ann.data.to === myHandle) {
                  setTipReceived({ from: ann.data.from, amount: ann.data.amount });
                  setBalanceFlash(ann.data.amount);
                  Sounds.tip();
                  setTimeout(() => {
                    setTipReceived(null);
                    setBalanceFlash(null);
                  }, 4000);
                }
              }
            }
          }

          // ============ TOKEN LAUNCH (eliminates /api/live polling) ============
          if (message.type === 'token_launch') {
            const { token, creator } = message;
            if (token) {
              setUserLaunchedTokens(prev => {
                if (prev.some(t => t.id === token.id)) return prev;
                console.log(`🚀 New user token: $${token.ticker} by ${creator}`);
                return [token, ...prev];
              });
              if (soundEnabled) {
                Sounds.tokenLaunch();
              }
            }
          }

          // ============ SESSION WARNING (eliminates /api/live polling) ============
          if (message.type === 'session_warning') {
            const { timeRemaining } = message;
            setSessionTimeRemaining(timeRemaining);
            if (timeRemaining > 0 && timeRemaining <= 5 * 60 * 1000) {
              setShowInactivityWarning(true);
            }
          }

          // ============ TOKEN ENDED (rug/moon) ============
          if (message.type === 'token_ended') {
            const { tokenId, ticker, result, finalPrice } = message;
            console.log(`🎯 Token ${ticker} ${result}!`);

            // Update token in list
            setActiveTokens(prev => prev.map(t =>
              t.id === tokenId
                ? { ...t, state: result, currentPrice: finalPrice }
                : t
            ));

            // Check if we have a position in this token (even if not watching)
            const myPosition = positions.find(p => p.tokenId === tokenId && p.status === 'active');
            if (myPosition && result === 'rugged') {
              console.log(`💀 Token ${ticker} rugged - we had a position!`);
              // Mark position as rugged in local state
              const updatedPositions = positions.map(p =>
                p.id === myPosition.id ? { ...p, status: 'rugged', closedAt: Date.now() } : p
              );
              savePositions(updatedPositions);
            }

            // If we're watching this token, handle the rug/moon
            if (currentToken?.id === tokenId) {
              if (result === 'rugged') {
                setGameState('rugged');
              } else if (result === 'mooned' || result === 'graduated') {
                setGameState('won');
              }
            }
          }
          
          // ============ TOKEN ROOM UPDATES ============
          if (message.type === 'token_update') {
            const { tokenId, updateType, data } = message;
            
            // Only process if we're watching this token
            if (currentToken?.id === tokenId) {
              if (updateType === 'price') {
                // Update current token price
                setCurrentToken(prev => prev ? { 
                  ...prev, 
                  currentPrice: data.currentPrice,
                  marketCap: data.marketCap,
                  priceChange24h: data.priceChange24h
                } : prev);
              }
              
              if (updateType === 'candle') {
                // Add new candle to history
                setCurrentToken(prev => {
                  if (!prev) return prev;
                  const candles = [...(prev.candles || []), data].slice(-500);
                  return { ...prev, candles };
                });
              }
              
              if (updateType === 'trade') {
                // Someone traded - update recent trades
                setCurrentToken(prev => {
                  if (!prev) return prev;
                  const recentTrades = [data, ...(prev.recentTrades || [])].slice(0, 20);
                  return { ...prev, recentTrades };
                });
              }
              
              if (updateType === 'chat') {
                // New chat message in token room
                setCurrentToken(prev => {
                  if (!prev) return prev;
                  const chat = [...(prev.chat || []), data].slice(-100);
                  return { ...prev, chat };
                });
              }
            }
          }
          
          // ============ TOKEN SNAPSHOT (on room join) ============
          if (message.type === 'token_snapshot') {
            const { tokenId, token } = message;
            if (currentToken?.id === tokenId) {
              setCurrentToken(prev => prev ? { ...prev, ...token } : token);
              console.log(`📸 Received token snapshot for ${token.ticker}`);
            }
          }

          // ============ RUG RUSH STATE (real-time crash game) ============
          if (message.type === 'rugrush_state') {
            const { state } = message;
            if (state) {
              // 🔊 Sound effects on phase transitions
              const prevPhase = rugRushPrevPhaseRef.current;
              const newPhase = state.round?.phase;

              if (prevPhase && prevPhase !== newPhase) {
                if (newPhase === 'crashed') {
                  // Only play crash sound if we had a bet and didn't cash out
                  const myHandle = handcashUser?.handle?.replace(/^\$/, '');
                  const myBet = state.round?.bets?.[myHandle] || state.round?.bets?.['$' + myHandle];
                  if (myBet && !myBet.cashedOut) {
                    Sounds.error(); // 💀 You got rugged!
                  }
                } else if (newPhase === 'betting' && prevPhase === 'crashed') {
                  // New round starting - subtle tick
                  Sounds.tick();
                  setRugRushCountdown(null); // Reset countdown
                } else if (newPhase === 'running' && prevPhase === 'betting') {
                  // Round started!
                  Sounds.bet(); // 🔊 GO!
                  setRugRushCountdown(null);
                }
              }
              rugRushPrevPhaseRef.current = newPhase;

              // ⏱️ Countdown timer for betting phase
              if (state.round?.phase === 'betting' && state.round?.bettingSecondsLeft !== null) {
                const secondsLeft = state.round.bettingSecondsLeft;
                setRugRushCountdown(secondsLeft);

                // 🔊 Countdown beeps for last 5 seconds
                if (secondsLeft <= 5 && secondsLeft > 0 && secondsLeft !== rugRushLastCountdownRef.current) {
                  Sounds.tick();
                }
                rugRushLastCountdownRef.current = secondsLeft;
              } else {
                setRugRushCountdown(null);
                rugRushLastCountdownRef.current = null;
              }

              setRugRushState(state);

              // 📢 Update kill feed from round data
              if (state.round?.killFeed) {
                setRugRushKillFeed(state.round.killFeed);
              }
            }
          }

          // ============ BOT STATUS (real-time trading bot updates) ============
          if (message.type === 'bot_status') {
            const { status } = message;
            if (status) {
              setTradingBotData(status);

              // 💰 Process currentCommand for bot navigation (same as polling)
              if (status.currentCommand && botControlEnabled && (status.isTrading || status.active)) {
                const cmd = status.currentCommand;
                if (!lastBotCommand || cmd.timestamp > lastBotCommand.timestamp) {
                  setLastBotCommand(cmd);

                  if (cmd.action === 'scanning') {
                    setBotFocusedTokenId(null);
                    if (botControlEnabled && view !== 'lobby') {
                      setView('lobby');
                      setCurrentToken(null);
                    }
                  } else if (cmd.action === 'navigating' && cmd.tokenId) {
                    setBotFocusedTokenId(cmd.tokenId);
                  } else if (cmd.action === 'analyzing' && cmd.tokenId) {
                    setBotFocusedTokenId(cmd.tokenId);
                    const targetToken = activeTokens.find(t => t.id === cmd.tokenId);
                    if (botControlEnabled && targetToken && (view !== 'token' || currentToken?.id !== cmd.tokenId)) {
                      setCurrentToken(targetToken);
                      setView('token');
                      setGameState('spectating');
                    }
                  } else if ((cmd.action === 'buying' || cmd.action === 'selling') && cmd.tokenId) {
                    setBotFocusedTokenId(cmd.tokenId);
                    setBotLastTrade({
                      tokenId: cmd.tokenId,
                      action: cmd.action,
                      timestamp: cmd.timestamp,
                      narration: cmd.narration,
                    });
                  }
                }
              }
            }
          }

        } catch (e) {
          console.log('WebSocket message parse error:', e);
        }
      };
      
      ws.onclose = () => {
        console.log('🔌 WebSocket disconnected');
        setWsConnected(false);
        wsRef.current = null;
        
        // Only reconnect if we still have a valid session AND user is logged in
        // Check sessionId exists and wasn't cleared by logout
        if (sessionId && handcashUser) {
          wsReconnectRef.current = setTimeout(() => {
            // Double-check session still valid before reconnecting
            if (sessionId && handcashUser) {
              console.log('🔌 Attempting WebSocket reconnect...');
              connectWebSocket();
            }
          }, 3000);
        }
      };
      
      ws.onerror = (err) => {
        console.log('🔌 WebSocket error:', err);
      };
      
    } catch (e) {
      console.log('WebSocket connection error:', e);
    }
  };
  
  // Connect WebSocket when session is available
  useEffect(() => {
    if (sessionId && API_URL) {
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (wsReconnectRef.current) {
        clearTimeout(wsReconnectRef.current);
      }
    };
  }, [sessionId, API_URL]);
  
  const sendDM = async (toUser, text) => {
    if (!toUser || !text.trim()) return;
    setSendingDM(true);
    
    // Reset scroll flag so we scroll to the new message
    paperChatUserScrolledRef.current = false;
    
    // Explicitly scroll to bottom after sending
    setTimeout(() => {
      if (paperChatMessagesContainerRef.current) {
        paperChatMessagesContainerRef.current.scrollTop = paperChatMessagesContainerRef.current.scrollHeight;
      }
    }, 100);
    
    const myHandle = handcashUser?.handle?.replace('$', '');
    const optimisticMsg = { 
      id: 'pending_' + Date.now(), 
      from: myHandle, 
      to: toUser,
      text: text.trim(), 
      timestamp: Date.now(),
      pending: true
    };
    
    // Optimistically add message
    setDmConversations(prev => {
      const existing = prev.find(c => c.handle === toUser);
      if (existing) {
        return prev.map(c => c.handle === toUser 
          ? { ...c, messages: [...(c.messages || []), optimisticMsg], lastMessage: optimisticMsg }
          : c
        );
      } else {
        return [...prev, { handle: toUser, messages: [optimisticMsg], lastMessage: optimisticMsg, unreadCount: 0 }];
      }
    });
    
    // Scroll to bottom after state update
    setTimeout(() => {
      if (paperChatMessagesContainerRef.current) {
        paperChatMessagesContainerRef.current.scrollTop = paperChatMessagesContainerRef.current.scrollHeight;
      }
    }, 50);
    
    // WebSocket only - no HTTP fallback
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'dm', to: toUser, text: text.trim() }));
      console.log('💬 DM sent via WebSocket to $' + toUser);
    } else {
      console.log('💬 WebSocket not connected, DM not sent');
    }
    setSendingDM(false);
  };
  
  // Send typing indicator
  const sendTypingIndicator = (toUser) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', to: toUser }));
    }
  };
  
  // Subscribe to a token room for real-time updates
  const subscribeToToken = (tokenId) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe_token', tokenId }));
      console.log('📡 Subscribed to token room:', tokenId);
    }
  };
  
  // Unsubscribe from token room
  const unsubscribeFromToken = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe_token' }));
      console.log('📡 Unsubscribed from token room');
    }
  };
  
  const markDMsRead = async (fromUser) => {
    if (!fromUser) return;
    
    // Try WebSocket first
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'mark_read', from: fromUser }));
      // Optimistically update
      setDmConversations(prev => {
        const updated = prev.map(c => c.handle === fromUser ? { ...c, unreadCount: 0 } : c);
        // Recalculate total unread from updated conversations
        const newTotal = updated.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
        setDmUnreadCount(newTotal);
        return updated;
      });
      return;
    }
    
    // Fallback to HTTP
    if (!sessionId || !API_URL) return;
    try {
      await fetch(`${API_URL}/api/dms/read`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
        body: JSON.stringify({ from: fromUser })
      });
      fetchDMs();
    } catch (e) {
      console.log('Mark DMs read error:', e.message);
    }
  };
  
  // Fetch DMs - initial fetch only (WebSocket handles all updates)
  useEffect(() => {
    if (sessionId && API_URL && authValidated) { // 🔐 Wait for auth validation
      fetchDMs();
    }
  }, [sessionId, API_URL, authValidated]);
  
  // Subscribe to token room when entering a token view
  useEffect(() => {
    if (currentToken?.id && wsConnected) {
      subscribeToToken(currentToken.id);
    }
    return () => {
      if (wsConnected) {
        unsubscribeFromToken();
      }
    };
  }, [currentToken?.id, wsConnected]);
  
  // ============== RUG PROPHET FUNCTIONS ==============
  const fetchRugProphetBets = async () => {
    if (!sessionId || !API_URL) return;
    try {
      const res = await fetch(`${API_URL}/api/rug-prophet/bets`, {
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        let bets = data.bets || [];
        
        // 🔮 CLEANUP: Check for stale pending bets (tokens that no longer exist)
        const currentTokenIds = activeTokens.map(t => t.id);
        const currentTokenTickers = activeTokens.map(t => t.ticker);
        
        bets = bets.map(bet => {
          if (bet.status !== 'pending') return bet;
          
          // Check if the token still exists
          const tokenExists = currentTokenIds.includes(bet.tokenId) || currentTokenTickers.includes(bet.tokenTicker);
          
          if (!tokenExists && bet.createdAt && Date.now() - bet.createdAt > 60000) {
            // Token is gone and bet is older than 1 minute - resolve it
            // If token disappeared without explicit rug/moon, it likely rugged (expired)
            console.log(`🔮 Stale bet detected for ${bet.tokenTicker} - resolving as lost (token expired)`);
            return {
              ...bet,
              status: 'lost',
              payout: 0,
              resolvedAt: Date.now(),
              outcome: 'expired'
            };
          }
          
          return bet;
        });
        
        setRugProphetBets(bets);
        setRugProphetStats(data.stats || { totalBets: 0, wins: 0, losses: 0, totalWon: 0, totalLost: 0 });
      }
    } catch (e) {
      console.log('Fetch bets error:', e.message);
    }
  };
  
  // Fetch inscriptions from server
  const fetchInscriptions = async () => {
    if (!API_URL) return;
    try {
      const res = await fetch(`${API_URL}/api/inscriptions`);
      if (res.ok) {
        const data = await res.json();
        setInscriptions(data.recent || []);
      }
    } catch (e) {
      console.log('Fetch inscriptions error:', e.message);
    }
  };
  
  // Check if user owns the Paper Hands story
  const checkStoryOwnership = async () => {
    if (!API_URL || !sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/store/owns/paper-hands-story`, {
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        setOwnsStory(data.owns || false);
        console.log('📖 Story ownership:', data.owns);
      }
    } catch (e) {
      console.log('Check story ownership error:', e.message);
    }
  };
  
  // Check story ownership when session changes
  useEffect(() => {
    if (sessionId) {
      checkStoryOwnership();
    } else {
      setOwnsStory(false);
    }
  }, [sessionId]);
  
  const placeRugProphetBet = async (tokenId, prediction, amount) => {
    if (!sessionId || !API_URL) {
      alert('Not connected to server');
      return;
    }
    if (placingBet) return; // Prevent double-clicks
    
    setPlacingBet(true);
    
    try {
      const res = await fetch(`${API_URL}/api/rug-prophet/bet`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
        body: JSON.stringify({ tokenId, prediction, amount })
      });
      const data = await res.json();
      
      if (res.ok) {
        // Update wallet
        setGameWallet(data.newBalance);
        showBalanceChange(-amount);
        
        // Add bet to local state immediately for instant UI update
        if (data.bet) {
          setRugProphetBets(prev => [data.bet, ...prev]);
          setRugProphetStats(prev => ({
            ...prev,
            totalBets: prev.totalBets + 1
          }));
        } else {
          // Fallback: fetch all bets
          fetchRugProphetBets();
        }
        
        setSelectedBetToken(null);
        Sounds.bet(); // 🎰 Play bet placed sound
        
        // 🪙 RAKE NOTIFICATION - Show fee when charged
        if (data.feePaid) {
          triggerRakeNotification(data.feePaid, 'bet');
        }
        
        // Show success in token chat if we're watching a token
        if (currentToken && currentToken.id === tokenId) {
          setTokenChat(prev => [...prev.slice(-50), {
            id: 'bet_placed_' + Date.now(),
            handle: '🔮',
            text: `Bet placed! ${prediction === 'rug' ? '💀' : '🌙'} ${formatPB(amount)} on ${prediction.toUpperCase()}`,
            timestamp: Date.now(),
            isAction: true,
            isAlpha: true
          }]);
        }
        
        // Also show in lobby chat
        setLobbyChat(prev => [...prev.slice(-25), {
          id: 'bet_placed_lobby_' + Date.now(),
          handle: '🔮',
          text: `You bet ${formatPB(amount)} that ${data.bet?.tokenTicker || 'token'} will ${prediction.toUpperCase()}!`,
          timestamp: Date.now(),
          isAction: true,
          isTradeAnnouncement: true
        }]);
        
        console.log(`🔮 Bet placed: ${amount} sats on ${prediction}`);
        
      } else {
        Sounds.error();
        alert(data.error || 'Failed to place bet');
      }
    } catch (e) {
      console.error('Place bet error:', e.message);
      Sounds.error();
      alert('Failed to place bet: ' + e.message);
    }
    
    setPlacingBet(false);
  };
  
  // 🔮 RESOLVE RUG PROPHET BETS - UI feedback only (server handles wallet!)
  // Called when watching a token that rugs/moons for instant feedback
  const resolveRugProphetBets = (tokenId, tokenTicker, outcome) => {
    // outcome: 'rug' or 'moon'
    console.log(`🔮 [UI] Token ${tokenTicker} ${outcome}ed - updating UI...`);
    
    // Update local bet status for instant UI feedback
    setRugProphetBets(prev => {
      const updated = prev.map(bet => {
        // Only resolve pending bets for this token
        if (bet.status !== 'pending') return bet;
        if (bet.tokenId !== tokenId && bet.tokenTicker !== tokenTicker) return bet;
        
        const won = bet.prediction === outcome;
        const multiplier = bet.prediction === 'rug' ? 1.8 : 2.5;
        const payout = won ? Math.floor(bet.amount * multiplier) : 0;
        
        console.log(`🔮 [UI] Bet ${bet.id}: predicted ${bet.prediction}, outcome ${outcome} - ${won ? 'WON' : 'LOST'}`);
        
        // Play sounds for feedback
        if (won) {
          Sounds.cashout();
          showBalanceChange(payout);
          setLobbyChat(prevChat => [...prevChat.slice(-25), {
            id: 'prophet_win_' + Date.now(),
            handle: '🔮',
            text: `You predicted ${tokenTicker} would ${outcome}! +${formatPB(payout - bet.amount)} profit`,
            isAction: true,
            isTradeAnnouncement: true,
            isWin: true,
          }]);
        } else {
          Sounds.error();
        }
        
        return {
          ...bet,
          status: won ? 'won' : 'lost',
          payout,
          resolvedAt: Date.now(),
          outcome
        };
      });
      
      return updated;
    });
    
    // Sync actual wallet and stats from server (source of truth!)
    // Server already resolved and credited wallet in its token lifecycle
    if (API_URL && sessionId) {
      setTimeout(async () => {
        try {
          const [betsRes, authRes] = await Promise.all([
            fetch(`${API_URL}/api/rug-prophet/bets`, {
              headers: { 'X-Session-Id': sessionId }
            }),
            fetch(`${API_URL}/api/user`, {
              headers: { 'X-Session-Id': sessionId }
            })
          ]);
          
          if (betsRes.ok) {
            const betsData = await betsRes.json();
            setRugProphetBets(betsData.bets || []);
            setRugProphetStats(betsData.stats || { totalBets: 0, wins: 0, losses: 0, totalWon: 0, totalLost: 0 });
          }
          
          if (authRes.ok) {
            const authData = await authRes.json();
            if (authData.gameBalance !== undefined) {
              console.log(`🔮 [Sync] Server wallet: ${authData.gameBalance}`);
              setGameWallet(authData.gameBalance);
            }
          }
        } catch (e) {
          console.log('🔮 [Sync] Error:', e.message);
        }
      }, 1000); // Wait 1 second for server to process
    }
  };
  
  // ============== ADMIN DASHBOARD ==============
  const fetchAdminDashboard = async () => {
    if (!API_URL || !sessionId) return;
    setAdminLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/dashboard`, {
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        setAdminData(data);
      } else {
        console.error('Admin dashboard fetch failed:', res.status);
      }
    } catch (e) {
      console.error('Admin dashboard error:', e);
    }
    setAdminLoading(false);
  };
  
  // 🛡️ PENDING WITHDRAWALS ADMIN
  const fetchPendingWithdrawals = async () => {
    if (!API_URL || !sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/pending-withdrawals`, {
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingWithdrawals(data.pending || []);
      }
    } catch (e) {
      console.error('Pending withdrawals fetch error:', e);
    }
  };
  
  // 🏆 TRADING WARS POT FUNCTIONS
  const fetchWarPot = async () => {
    if (!API_URL || !sessionId) return;
    setWarPotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/wars/pot`, {
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        setWarPotData(data);
      }
    } catch (e) {
      console.error('War pot fetch error:', e);
    }
    setWarPotLoading(false);
  };
  
  const addToWarPot = async (amount) => {
    if (!API_URL || !sessionId || !amount) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/wars/add-to-pot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ amount: parseInt(amount) })
      });
      if (res.ok) {
        const data = await res.json();
        setWarPotData(prev => ({ ...prev, potBalance: data.potBalance }));
        setWarPotAddAmount('');
        fetchWarPot(); // Refresh full data
        alert(`✅ Added ${formatPB(amount)} to war pot!`);
      } else {
        const err = await res.json();
        alert('Error: ' + err.error);
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };
  
  const resetWarPot = async () => {
    if (!API_URL || !sessionId) return;
    if (!confirm('Reset war pot to 0? Make sure you paid the winners first!')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/wars/reset-pot`, {
        method: 'POST',
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        setWarPotData(prev => ({ ...prev, potBalance: 0 }));
        fetchWarPot();
        alert(`✅ Pot reset! Previous balance was ${formatPB(data.previousBalance)}`);
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };
  
  // 👻 Load admin visibility status when admin panel opens
  useEffect(() => {
    if (!showAdminDashboard || !sessionId || !API_URL) return;
    fetch(`${API_URL}/api/admin/visibility`, { headers: { 'X-Session-Id': sessionId } })
      .then(r => r.json())
      .then(data => { if (data.visible !== undefined) setAdminVisible(data.visible); })
      .catch(() => {});
  }, [showAdminDashboard, sessionId]);

  // 🔄 ADMIN AUTO-REFRESH - Polls every 10s on Live tab, 30s on others
  useEffect(() => {
    if (!showAdminDashboard || !adminAutoRefresh || !sessionId) return;

    // Initial fetch
    fetchAdminDashboard();
    fetchPendingWithdrawals();
    
    // Set up interval - faster for Live tab
    const interval = adminTab === 'live' ? 10000 : 30000;
    const refreshInterval = setInterval(() => {
      fetchAdminDashboard();
      if (adminTab === 'withdrawals') fetchPendingWithdrawals();
    }, interval);
    
    return () => clearInterval(refreshInterval);
  }, [showAdminDashboard, adminAutoRefresh, adminTab, sessionId]);
  
  // 💭 PENNY PING FUNCTIONS
  const fetchThoughts = async () => {
    if (!API_URL) return;
    try {
      // Fetch both feeds
      const [gameRes, worldRes] = await Promise.all([
        fetch(`${API_URL}/api/thoughts/game`),
        fetch(`${API_URL}/api/thoughts/world`)
      ]);
      
      if (gameRes.ok) {
        const data = await gameRes.json();
        setGameThoughts(data.thoughts || []);
        if (data.throne) setGameThrone(data.throne);
      }
      if (worldRes.ok) {
        const data = await worldRes.json();
        setWorldThoughts(data.thoughts || []);
        if (data.throne) setWorldThrone(data.throne);
      }
    } catch (e) {
      console.error('Fetch thoughts error:', e);
    }
  };
  
  const postPing = async (dethrone = false) => {
    if (!API_URL || !sessionId || !newThought.trim()) return;
    if (postingThought) return; // Prevent double-submit
    
    setPostingThought(true);
    try {
      const res = await fetch(`${API_URL}/api/thoughts/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ thought: newThought.trim(), feed: thoughtFeed, dethrone })
      });
      
      const data = await res.json();
      if (res.ok) {
        setNewThought('');
        if (data.newBalance !== undefined) setGameWallet(data.newBalance);
        fetchThoughts();
        console.log(dethrone ? '👑 Dethroned!' : '🏓 Ping posted!');
      } else {
        alert(`❌ ${data.error}`);
      }
    } catch (e) {
      alert(`❌ Error: ${e.message}`);
    } finally {
      setPostingThought(false);
    }
  };
  
  // 🏓 Load user profile (ping chain)
  const loadProfile = async (handle) => {
    if (!API_URL || profileLoading) return;
    setProfileLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/thoughts/profile/${handle.replace(/^\$/, '')}`);
      if (res.ok) {
        const data = await res.json();
        setViewingProfile({ handle, data });
      }
    } catch (e) {
      console.error('Profile load error:', e);
    } finally {
      setProfileLoading(false);
    }
  };
  
  // 🏓 Copy ping link to clipboard
  const copyThoughtLink = (thought) => {
    if (!thought.txId) {
      alert('This ping is not on-chain yet');
      return;
    }
    const link = `https://whatsonchain.com/tx/${thought.txId}`;
    navigator.clipboard.writeText(link).then(() => {
      alert('🔗 Link copied!');
    }).catch(() => {
      // Fallback for mobile
      prompt('Copy this link:', link);
    });
  };
  
  // 🗳️ Vote on a ping (free!)
  const voteOnPing = async (thoughtId, vote) => {
    if (!sessionId) {
      alert('Login to vote');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/thoughts/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId
        },
        body: JSON.stringify({ thoughtId, vote })
      });
      
      const data = await res.json();
      
      if (data.success) {
        // Update local state
        const updateThoughts = (thoughts) => 
          thoughts.map(t => t.id === thoughtId 
            ? { ...t, upvotes: data.upvotes, downvotes: data.downvotes, myVote: data.yourVote }
            : t
          );
        
        setGameThoughts(prev => updateThoughts(prev));
        setWorldThoughts(prev => updateThoughts(prev));
        
        console.log(`🗳️ Voted ${vote} on ${thoughtId}: ${data.upvotes}↑ ${data.downvotes}↓`);
      } else {
        console.error('Vote failed:', data.error);
      }
    } catch (e) {
      console.error('Vote error:', e.message);
    }
  };
  
  // 🔍 CHAIN DECODER - Decode and walk Penny Ping chains
  const decodeChain = async () => {
    if (!decoderInput.trim()) return;
    
    setDecoderLoading(true);
    setDecoderLines([]);
    setDecoderChain([]);
    
    // Helper to add lines with typing animation delay
    const addLine = (text, delay = 100) => {
      return new Promise(resolve => {
        setTimeout(() => {
          setDecoderLines(prev => [...prev, text]);
          resolve();
        }, delay);
      });
    };
    
    // Extract txId from input (URL or raw txId)
    let txId = decoderInput.trim();
    if (txId.includes('whatsonchain.com/tx/')) {
      txId = txId.split('/tx/')[1].split('?')[0].split('#')[0];
    }
    
    try {
      await addLine('> Initializing Penny Ping Chain Decoder...', 200);
      await addLine(`> Target: ${txId.slice(0, 20)}...`, 150);
      await addLine('', 100);
      
      const chain = [];
      let currentTxId = txId;
      let chainPosition = 0;
      
      while (currentTxId && chainPosition < 50) { // Max 50 to prevent infinite loops
        chainPosition++;
        await addLine(`> Fetching transaction ${chainPosition}...`, 300);
        
        // Fetch from WhatsOnChain
        const res = await fetch(`https://api.whatsonchain.com/v1/bsv/main/tx/${currentTxId}`);
        if (!res.ok) {
          await addLine(`❌ Error: Transaction not found`, 100);
          break;
        }
        
        const txData = await res.json();
        await addLine('> Transaction found ✓', 150);
        
        // Find OP_RETURN output
        let opReturnData = null;
        for (const vout of txData.vout || []) {
          if (vout.scriptPubKey?.asm?.startsWith('OP_RETURN') || vout.scriptPubKey?.type === 'nulldata') {
            // Get the hex data
            const hex = vout.scriptPubKey.hex;
            if (hex) {
              // Skip OP_RETURN opcode (6a) and length bytes, decode the rest
              try {
                // Try to find JSON in the output
                const asmParts = vout.scriptPubKey.asm.split(' ');
                for (const part of asmParts) {
                  if (part !== 'OP_RETURN' && part !== '0' && part.length > 10) {
                    try {
                      // Browser-compatible hex to UTF8 decode
                      const hexToUtf8 = (hex) => {
                        let str = '';
                        for (let i = 0; i < hex.length; i += 2) {
                          str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
                        }
                        return str;
                      };
                      const decoded = hexToUtf8(part);
                      if (decoded.includes('PENNYPING') || decoded.includes('{')) {
                        // Try to parse as JSON
                        const jsonMatch = decoded.match(/\{[\s\S]*\}/);
                        if (jsonMatch) {
                          opReturnData = JSON.parse(jsonMatch[0]);
                          break;
                        }
                      }
                    } catch (e) { /* continue */ }
                  }
                }
              } catch (e) { /* continue */ }
            }
          }
        }
        
        if (!opReturnData) {
          await addLine('> ⚠️ No Penny Ping data in this transaction', 100);
          await addLine('> This may not be a Penny Ping transaction', 100);
          break;
        }
        
        await addLine('> Found OP_RETURN data ✓', 150);
        await addLine('> Decoding Penny Ping protocol...', 200);
        await addLine('', 50);
        
        // Display the raw data
        await addLine('  ┌─────────────────────────────────────────────────┐', 50);
        await addLine(`  │ ${chainPosition === 1 ? '📍 THIS PING' : opReturnData.previousTxId ? `🔗 PING #${opReturnData.chainPosition || chainPosition}` : '🌟 GENESIS PING'}`, 50);
        await addLine('  ├─────────────────────────────────────────────────┤', 50);
        await addLine(`  │ PROTOCOL:  ${opReturnData.protocol || 'PENNYPING'}`, 50);
        await addLine(`  │ VERSION:   ${opReturnData.version || 1}`, 50);
        await addLine(`  │ TYPE:      ${(opReturnData.type || 'ping').toUpperCase()} ${opReturnData.type === 'dethrone' ? '👑' : '🏓'}`, 50);
        await addLine(`  │ HANDLE:    $${opReturnData.handle}`, 50);
        await addLine(`  │ FEED:      ${(opReturnData.feed || 'game').toUpperCase()}`, 50);
        await addLine(`  │ CHAIN POS: #${opReturnData.chainPosition || chainPosition}`, 50);
        await addLine(`  │ TIMESTAMP: ${new Date(opReturnData.timestamp).toLocaleString()}`, 50);
        await addLine(`  │ CONTENT:   [hash: ${opReturnData.contentHash || 'N/A'}]`, 50);
        await addLine(`  │ PREV TX:   ${opReturnData.previousTxId ? opReturnData.previousTxId.slice(0, 16) + '...' : '(GENESIS)'}`, 50);
        await addLine('  └─────────────────────────────────────────────────┘', 50);
        await addLine('', 100);
        
        // Add to chain
        chain.push({
          txId: currentTxId,
          data: opReturnData,
          isGenesis: !opReturnData.previousTxId
        });
        
        // Check for genesis
        if (!opReturnData.previousTxId) {
          await addLine('> 🌟 REACHED GENESIS TRANSACTION', 200);
          await addLine(`> Identity established: $${opReturnData.handle}`, 100);
          break;
        }
        
        // Follow the chain
        await addLine(`> Following chain link to previous ping...`, 200);
        currentTxId = opReturnData.previousTxId;
      }
      
      await addLine('', 100);
      await addLine('══════════════════════════════════════════════════════', 50);
      if (chain.length > 0) {
        const identity = chain[0].data.handle;
        const isVerified = chain[chain.length - 1].isGenesis;
        await addLine(`✅ CHAIN DECODED: ${chain.length} ping${chain.length > 1 ? 's' : ''} | Identity: $${identity} ${isVerified ? '| VERIFIED ✓' : '| PARTIAL'}`, 100);
      } else {
        await addLine('❌ Could not decode chain', 100);
      }
      await addLine('══════════════════════════════════════════════════════', 50);
      
      setDecoderChain(chain);
      
    } catch (e) {
      await addLine(`❌ Error: ${e.message}`, 100);
    } finally {
      setDecoderLoading(false);
    }
  };
  
  // 💭 PENNY PING - Fetch on mount and poll every 10 seconds
  useEffect(() => {
    if (!API_URL) return;
    fetchThoughts();
    const interval = setInterval(() => {
      if (!document.hidden && !isIdleKicked) fetchThoughts();
    }, 30000); // SKIP if tab hidden or idle kicked
    return () => clearInterval(interval);
  }, [API_URL]);
  
  // 🏆 TRADING WARS - Fetch on mount and poll every 60 seconds
  useEffect(() => {
    if (!API_URL) return;
    fetchTradingWars();
    const interval = setInterval(() => {
      if (!document.hidden && !isIdleKicked) fetchTradingWars();
    }, 60000); // SKIP if tab hidden or idle kicked
    return () => clearInterval(interval);
  }, [API_URL, sessionId]);
  
  const fetchWatchlist = async () => {
    if (!API_URL || !sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/watchlist`, {
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        setWatchlistData(data.watchlist || []);
      }
    } catch (e) {
      console.error('Watchlist fetch error:', e);
    }
  };
  
  const addToWatchlist = async (handle) => {
    if (!API_URL || !sessionId) return;
    const input = handle || prompt('Enter handle to watch:');
    if (!input) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/watchlist/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ handle: input })
      });
      const data = await res.json();
      if (res.ok) {
        alert(`✅ ${data.message}`);
        fetchWatchlist();
      } else {
        alert(`❌ ${data.error}`);
      }
    } catch (e) {
      alert(`❌ Error: ${e.message}`);
    }
  };
  
  const removeFromWatchlist = async (handle) => {
    if (!confirm(`Remove ${handle} from watchlist?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/watchlist/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ handle })
      });
      const data = await res.json();
      if (res.ok) {
        alert(`✅ ${data.message}`);
        fetchWatchlist();
      } else {
        alert(`❌ ${data.error}`);
      }
    } catch (e) {
      alert(`❌ Error: ${e.message}`);
    }
  };
  
  const approveWithdrawal = async (withdrawalId) => {
    if (!API_URL || !sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/approve-withdrawal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ withdrawalId })
      });
      const data = await res.json();
      if (res.ok) {
        alert(`✅ ${data.message}\nTX: ${data.txId}`);
        fetchPendingWithdrawals();
        fetchAdminDashboard();
      } else {
        alert(`❌ ${data.error}`);
      }
    } catch (e) {
      alert(`❌ Error: ${e.message}`);
    }
  };
  
  const rejectWithdrawal = async (withdrawalId) => {
    const reason = prompt('Reason for rejection (optional):');
    if (!API_URL || !sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/reject-withdrawal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ withdrawalId, reason })
      });
      const data = await res.json();
      if (res.ok) {
        alert(`✅ ${data.message}`);
        fetchPendingWithdrawals();
      } else {
        alert(`❌ ${data.error}`);
      }
    } catch (e) {
      alert(`❌ Error: ${e.message}`);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🚀 RUG RUSH (Crash Game) FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Track previous phase to detect transitions
  const rugRushPrevPhaseRef = useRef(null);
  const rugRushLastCountdownRef = useRef(null); // Track last countdown to avoid sound spam
  
  const fetchRugRushState = async () => {
    if (!API_URL) return;
    try {
      const res = await fetch(`${API_URL}/api/rugrush/state`);
      const data = await res.json();
      
      // 🔊 Sound effects on phase transitions
      const prevPhase = rugRushPrevPhaseRef.current;
      const newPhase = data.round?.phase;

      if (prevPhase && prevPhase !== newPhase) {
        if (newPhase === 'crashed') {
          // Only play crash sound if we had a bet and didn't cash out
          const myHandle = handcashUser?.handle?.replace(/^\$/, '');
          const myBet = data.round?.bets?.[myHandle] || data.round?.bets?.['$' + myHandle];
          if (myBet && !myBet.cashedOut) {
            Sounds.error(); // 💀 You got rugged!
          }
        } else if (newPhase === 'betting' && prevPhase === 'crashed') {
          // New round starting - subtle tick
          Sounds.tick();
          setRugRushCountdown(null); // Reset countdown
        } else if (newPhase === 'running' && prevPhase === 'betting') {
          // Round started!
          Sounds.bet(); // 🔊 GO!
          setRugRushCountdown(null);
        }
      }
      rugRushPrevPhaseRef.current = newPhase;

      // ⏱️ Countdown timer for betting phase (use server-calculated value directly!)
      if (data.round?.phase === 'betting' && data.round?.bettingSecondsLeft !== null) {
        const secondsLeft = data.round.bettingSecondsLeft;
        setRugRushCountdown(secondsLeft);

        // 🔊 Countdown beeps for last 5 seconds - only when second CHANGES
        if (secondsLeft <= 5 && secondsLeft > 0 && secondsLeft !== rugRushLastCountdownRef.current) {
          Sounds.tick();
        }
        rugRushLastCountdownRef.current = secondsLeft;
      } else {
        setRugRushCountdown(null);
        rugRushLastCountdownRef.current = null;
      }

      setRugRushState(data);

      // 📢 Update kill feed from round data
      if (data.round?.killFeed) {
        setRugRushKillFeed(data.round.killFeed);
      }

      // 👑 Check for last man standing announcement
      if (newPhase === 'crashed' && prevPhase === 'running') {
        // Find last man standing from results
        const results = data.round?.results || [];
        const lms = results.find(r => r.lastManStanding);
        if (lms) {
          setRugRushLastMan(lms);
          setTimeout(() => setRugRushLastMan(null), 5000);
        }
      }

      // 🏆 Winner announcement popup (when new round starts, announce last winner)
      if (data.lastWinner && data.lastWinner.roundId !== rugRushLastWinnerIdRef.current) {
        rugRushLastWinnerIdRef.current = data.lastWinner.roundId;
        setRugRushWinner(data.lastWinner);
        // Auto-dismiss after 6 seconds
        setTimeout(() => setRugRushWinner(null), 6000);
      }

      // 🎯 Auto-cashout check
      if (newPhase === 'running' && rugRushAutoCashout && data.round?.currentMultiplier >= rugRushAutoCashout) {
        const myHandle = handcashUser?.handle?.replace(/^\$/, '');
        const myBet = data.round?.bets?.[myHandle] || data.round?.bets?.['$' + myHandle];
        if (myBet && !myBet.cashedOut) {
          // Trigger auto-cashout!
          cashOutRugRush();
          setRugRushAutoCashout(null); // Clear after triggering
        }
      }

      // Check if we have a bet in the current round
      if (data.round && data.round.bets && handcashUser?.handle) {
        const myHandle = handcashUser.handle.replace(/^\$/, '');
        const bet = data.round.bets[myHandle] || data.round.bets['$' + myHandle];
        setRugRushMyBet(bet || null);
      } else {
        setRugRushMyBet(null);
      }
    } catch (e) {
      console.error('Failed to fetch Rug Rush state:', e);
    }
  };
  
  const placeRugRushBet = async (amount, tier = 'bronze') => {
    if (!API_URL || !sessionId) return;
    setRugRushBetting(true);
    try {
      const res = await fetch(`${API_URL}/api/rugrush/bet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, amount, tier })
      });
      const data = await res.json();
      if (data.success) {
        Sounds.bet(); // 🔊 Bet placed!
        setGameWallet(data.newBalance);
        setRugRushMyBet({ amount, cashedOut: false });
        setRugRushResult({ type: 'bet', message: data.message });
        
        // 🪙 RAKE NOTIFICATION - Show fee when charged
        if (data.feePaid) {
          triggerRakeNotification(data.feePaid, 'bet');
        }
      } else {
        Sounds.error(); // 🔊 Error
        setRugRushResult({ type: 'error', message: data.error });
      }
    } catch (e) {
      Sounds.error();
      setRugRushResult({ type: 'error', message: e.message });
    }
    setRugRushBetting(false);
  };
  
  const cashOutRugRush = async () => {
    if (!API_URL || !sessionId) return;
    setRugRushCashingOut(true);
    try {
      const res = await fetch(`${API_URL}/api/rugrush/cashout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      });
      const data = await res.json();
      if (data.success) {
        Sounds.win(); // 🔊 Cash out success!
        setGameWallet(data.newBalance);
        setRugRushMyBet(prev => prev ? { ...prev, cashedOut: true, cashoutMultiplier: data.multiplier, payout: data.payout } : null);
        setRugRushResult({ type: 'win', message: data.message, multiplier: data.multiplier, profit: data.profit });
        
        // 🎟️ Check for scratcher offer
        if (data.scratcherOffer) {
          setScratcherOffer(data.scratcherOffer);
          // Auto-show scratcher modal after a short delay
          setTimeout(() => {
            setShowScratcher(true);
            setScratcherCards([false, false, false, false, false, false]); // Reset cards
            setScratcherResult(null);
          }, 1500);
        }
      } else {
        Sounds.error();
        setRugRushResult({ type: 'error', message: data.error });
      }
    } catch (e) {
      Sounds.error();
      setRugRushResult({ type: 'error', message: e.message });
    }
    setRugRushCashingOut(false);
  };

  // 🗣️ Send a taunt (costs 1¢)
  const sendRugRushTaunt = async (tauntId, targetHandle = null) => {
    if (!API_URL || !sessionId) return;
    setRugRushTaunting(true);
    try {
      const res = await fetch(`${API_URL}/api/rugrush/taunt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, tauntId, targetHandle })
      });
      const data = await res.json();
      if (data.success) {
        Sounds.chat(); // 🔊 Taunt sent!
        setRugRushResult({ type: 'taunt', message: `Taunt sent! (${data.tauntsRemaining} left)` });
        setRugRushShowTaunts(false);
        setRugRushTauntTarget(null);
      } else {
        Sounds.error();
        setRugRushResult({ type: 'error', message: data.error });
      }
    } catch (e) {
      Sounds.error();
      setRugRushResult({ type: 'error', message: e.message });
    }
    setRugRushTaunting(false);
  };

  // 🎟️ Reveal the scratcher
  const revealScratcher = async () => {
    if (!API_URL || !sessionId || !scratcherOffer) return;
    setScratcherRevealing(true);
    try {
      const res = await fetch(`${API_URL}/api/rugrush/scratch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, scratcherId: scratcherOffer.scratcherId })
      });
      const data = await res.json();
      if (data.success) {
        setGameWallet(data.newBalance);
        setScratcherResult(data);
      } else {
        setScratcherResult({ prize: { id: 'error', label: '❌ Error', value: 0 }, message: data.error });
      }
    } catch (e) {
      setScratcherResult({ prize: { id: 'error', label: '❌ Error', value: 0 }, message: e.message });
    }
    setScratcherRevealing(false);
  };
  
  // Scratch a card (animation)
  const scratchCard = (index) => {
    if (scratcherResult) return; // Already revealed
    setScratcherCards(prev => {
      const next = [...prev];
      next[index] = true;
      // If all cards scratched, reveal the prize
      if (next.filter(Boolean).length >= 3 && !scratcherRevealing) {
        revealScratcher();
      }
      return next;
    });
  };
  
  // Subscribe to Rug Rush via WebSocket when modal is open
  useEffect(() => {
    if (!showRugRush) return;

    const ws = wsRef?.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    // 🔌 Subscribe to Rug Rush room via WebSocket
    ws.send(JSON.stringify({ type: 'subscribe_rugrush' }));
    console.log('🚀 Subscribed to Rug Rush via WebSocket');

    return () => {
      // Unsubscribe when modal closes
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'unsubscribe_rugrush' }));
        console.log('🚀 Unsubscribed from Rug Rush');
      }
    };
  }, [showRugRush, handcashUser?.handle, wsRef?.current?.readyState]);
  
  // Clear result message after a few seconds
  useEffect(() => {
    if (rugRushResult) {
      const timeout = setTimeout(() => setRugRushResult(null), 4000);
      return () => clearTimeout(timeout);
    }
  }, [rugRushResult]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  
  const adminZeroBalance = async (handle) => {
    if (!confirm(`Zero out ${handle}'s balance? This cannot be undone!`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/zero-balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ handle })
      });
      if (res.ok) {
        alert(`${handle}'s balance has been zeroed`);
        fetchAdminDashboard(); // Refresh
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to zero balance');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };
  
  const adminDeleteToken = async (tokenId, ticker) => {
    if (!confirm(`Delete $${ticker}? This cannot be undone!`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/delete-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ tokenId })
      });
      if (res.ok) {
        alert(`$${ticker} has been deleted`);
        fetchAdminDashboard(); // Refresh
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete token');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };
  
  const adminSetBalance = async (handle) => {
    const newAmount = prompt(`Set ${handle}'s balance to (in sats/PB):`, '0');
    if (newAmount === null) return;
    const amount = parseInt(newAmount);
    if (isNaN(amount) || amount < 0) {
      alert('Invalid amount');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/admin/set-balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ handle, amount })
      });
      if (res.ok) {
        const data = await res.json();
        alert(`${handle}'s balance set: ${data.oldBalance} → ${data.newBalance}`);
        fetchAdminDashboard();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };
  
  const adminBanUser = async (handle, currentlyBanned) => {
    const action = currentlyBanned ? 'UNBAN' : 'BAN';
    if (!confirm(`${action} ${handle}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/ban-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ handle, banned: !currentlyBanned })
      });
      if (res.ok) {
        alert(`${handle} has been ${action}NED`);
        fetchAdminDashboard();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // 🔒 Lock user balance with custom playthrough multiplier (exploit remediation)
  const adminLockBalance = async () => {
    if (!lockBalanceHandle.trim()) {
      alert('Enter a handle');
      return;
    }
    const mult = parseFloat(lockBalanceMultiplier);
    if (isNaN(mult) || mult <= 0) {
      alert('Invalid multiplier');
      return;
    }
    setLockBalanceLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/lock-balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({
          handle: lockBalanceHandle.trim().replace('$', ''),
          multiplier: mult,
          reason: lockBalanceReason || 'Exploit remediation'
        })
      });
      const data = await res.json();
      if (res.ok) {
        alert(`🔒 LOCKED: ${data.handle}\n\nBalance: $${(data.balanceLocked / 100).toFixed(2)}\nMultiplier: ${data.multiplier}x\nVolume Required: $${(data.volumeRequired / 100).toFixed(2)}\n\nReason: ${data.reason}`);
        setLockBalanceHandle('');
        fetchAdminDashboard();
      } else {
        alert(data.error || 'Failed to lock balance');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLockBalanceLoading(false);
  };

  const adminPauseToken = async (tokenId, ticker, currentlyPaused) => {
    const action = currentlyPaused ? 'UNPAUSE' : 'PAUSE';
    if (!confirm(`${action} $${ticker}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/pause-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ tokenId, paused: !currentlyPaused })
      });
      if (res.ok) {
        alert(`$${ticker} has been ${action}D`);
        fetchAdminDashboard();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };
  
  const adminForceClosePositions = async (tokenId, ticker) => {
    const refund = confirm(`Force close all positions on $${ticker}?\n\nClick OK to REFUND invested amounts to users.\nClick CANCEL to close WITHOUT refund.`);
    if (!confirm(`Are you sure? This will ${refund ? 'refund all users and ' : ''}close the token permanently.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/force-close-positions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ tokenId, refund })
      });
      if (res.ok) {
        const data = await res.json();
        alert(`$${ticker} closed: ${data.closedCount} positions${refund ? `, ${(data.refundedAmount/1000000).toFixed(2)}M refunded` : ''}`);
        fetchAdminDashboard();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };
  
  // ============== ENHANCED LEADERBOARDS ==============
  const fetchLeaderboards = async () => {
    if (!API_URL) return;
    setLeaderboardLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/leaderboards`);
      if (res.ok) {
        const data = await res.json();
        setLeaderboardData(data);
        // Also update unruggables leaderboard
        if (data.unruggables) {
          setUnruggableLeaderboard(data.unruggables);
        }
        console.log('🏆 Leaderboards fetched');
      }
    } catch (e) {
      console.log('Leaderboards fetch error:', e.message);
    }
    setLeaderboardLoading(false);
  };
  
  // ============== 🏆 TRADING WARS ==============
  const fetchTradingWars = async () => {
    if (!API_URL) return;
    setTradingWarsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/wars`, {
        headers: sessionId ? { 'X-Session-Id': sessionId } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setTradingWarsData(data);
        console.log('🏆 Trading Wars fetched');
      }
    } catch (e) {
      console.log('Trading Wars fetch error:', e.message);
    }
    setTradingWarsLoading(false);
  };
  
  // Broadcast event to server (for multiplayer activity feed)
  const broadcastEvent = async (type, data) => {
    if (!sessionId || !API_URL) return;
    try {
      await fetch(`${API_URL}/api/broadcast`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
        body: JSON.stringify({ type, data })
      });
    } catch (e) {
      console.log('Broadcast error:', e.message);
    }
  };

  // ============== 🤖 TRADING BOT FUNCTIONS ==============
  
  const fetchTradingBotStatus = async () => {
    if (!API_URL || !sessionId) return;
    setTradingBotLoading(true);
    try {
      // 🎯 Send focus and activity data for idle detection
      const isIdle = Date.now() - lastUserActivityRef.current > IDLE_TIMEOUT_MS;
      const activityData = {
        isFocused: isTabFocused && !document.hidden,
        isIdle: isIdle,
        lastActivity: lastUserActivityRef.current,
      };
      
      const res = await fetch(`${API_URL}/api/trading-bot`, {
        method: 'GET',
        headers: { 
          'X-Session-Id': sessionId 
        }
      });
      if (res.ok) {
        const data = await res.json();
        setTradingBotData(data);
        
        // 💰 HOUSE CREDITS ANIMATION (for zcool only)
        if (data.houseCredits && data.houseCredits.length > 0) {
          // Show each credit as an animation
          data.houseCredits.forEach((credit, i) => {
            setTimeout(() => {
              setHouseCreditAnimation({
                amount: credit.amount,
                from: credit.from,
                ticker: credit.ticker,
                type: credit.type,
                newBalance: credit.newBalance,
                timestamp: Date.now()
              });
              // Play sound
              Sounds.success();
              // Clear after 3 seconds
              setTimeout(() => setHouseCreditAnimation(null), 3000);
            }, i * 500); // Stagger animations
          });
        }
        
        // 🎬 BOT REMOTE ASSISTANCE - Actually plays the game!
        // Process commands when bot is active (even if just scanning, not actively in a trade)
        if (data.currentCommand && botControlEnabled && (data.isTrading || data.active)) {
          const cmd = data.currentCommand;
          // Only process new commands (check timestamp)
          if (!lastBotCommand || cmd.timestamp > lastBotCommand.timestamp) {
            setLastBotCommand(cmd);
            
            // 🎮 BOT NAVIGATION - Only if auto-nav enabled!
            if (cmd.action === 'scanning') {
              // Bot is scanning - stay in lobby view (only if auto-nav on)
              setBotFocusedTokenId(null);
              if (botControlEnabled && view !== 'lobby') {
                setView('lobby');
                setCurrentToken(null);
              }
            } else if (cmd.action === 'navigating' && cmd.tokenId) {
              // Bot is moving to a token - highlight it in grid first
              setBotFocusedTokenId(cmd.tokenId);
              // Scroll to it (only if auto-nav on)
              if (botControlEnabled) {
                setTimeout(() => {
                  const tokenElement = document.getElementById(`token-tile-${cmd.tokenId}`);
                  if (tokenElement) {
                    tokenElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);
              }
            } else if (cmd.action === 'analyzing' && cmd.tokenId) {
              // Bot is analyzing - CLICK INTO the token! (only if auto-nav on)
              setBotFocusedTokenId(cmd.tokenId);
              const targetToken = activeTokens.find(t => t.id === cmd.tokenId);
              if (botControlEnabled && targetToken && (view !== 'token' || currentToken?.id !== cmd.tokenId)) {
                setCurrentToken(targetToken);
                setView('token');
                setGameState('spectating'); // Reset game state for new token!
                console.log(`🤖 Bot clicked into $${targetToken.ticker}`);
                
                // 📊 AUTO-FOCUS CHART for best candle view!
                setTimeout(() => {
                  // Zoom to show ~50 candles nicely, scroll to latest
                  setChartZoom(0.8); // Slight zoom out for overview
                  setChartScrollOffset(0); // Show latest candles
                }, 300);
              }
            } else if ((cmd.action === 'buying' || cmd.action === 'selling') && cmd.tokenId) {
              // Bot is trading - make sure we're in the token view IF auto-nav is on
              setBotFocusedTokenId(cmd.tokenId);
              const targetToken = activeTokens.find(t => t.id === cmd.tokenId);
              
              // 🎯 Track this trade for the widget/toast
              setBotLastTrade({
                tokenId: cmd.tokenId,
                ticker: targetToken?.ticker || cmd.ticker,
                action: cmd.action,
                narration: cmd.narration,
                timestamp: Date.now()
              });
              
              if (botControlEnabled && targetToken && view !== 'token') {
                setCurrentToken(targetToken);
                setView('token');
                setGameState('spectating'); // Reset game state for new token!
              }
              // Trigger BUY/SELL notification toast
              setTradeNotification({
                tokenId: cmd.tokenId,
                ticker: targetToken?.ticker || cmd.ticker,
                type: cmd.action === 'buying' ? 'buy' : 'sell',
                narration: cmd.narration,
                isPvP: targetToken?.isUserLaunched || false,
                isBot: true,
                timestamp: cmd.timestamp
              });
              setTimeout(() => setTradeNotification(null), 3000); // Show for 3 seconds
              // Play sound
              if (cmd.action === 'buying') {
                Sounds.buy();
              } else {
                Sounds.sell();
                // After selling, go back to lobby after a delay (only if auto-nav enabled)
                if (botControlEnabled) {
                  setTimeout(() => {
                    if (botControlEnabled) {
                      setView('lobby');
                      setCurrentToken(null);
                      setBotFocusedTokenId(null);
                    }
                  }, 2500);
                }
              }
            } else if (cmd.action === 'watching' && cmd.tokenId) {
              // Bot is watching a position - stay in token view (only if auto-nav on)
              setBotFocusedTokenId(cmd.tokenId);
              const targetToken = activeTokens.find(t => t.id === cmd.tokenId);
              if (botControlEnabled && targetToken && view !== 'token') {
                setCurrentToken(targetToken);
                setView('token');
                setGameState('spectating'); // Reset game state for new token!
              }
              // Check if token got rugged - if so, go back to lobby!
              if (targetToken?.state === 'rugged') {
                console.log(`🤖 Token $${targetToken.ticker} rugged! Going back to lobby`);
                if (botControlEnabled) {
                  setTimeout(() => {
                    if (botControlEnabled) {
                      setView('lobby');
                      setCurrentToken(null);
                      setBotFocusedTokenId(null);
                    }
                  }, 1500);
                }
              }
            } else if (!cmd.tokenId) {
              // No token - back to lobby (only if auto-nav on)
              setBotFocusedTokenId(null);
              if (botControlEnabled && view !== 'lobby') {
                setView('lobby');
                setCurrentToken(null);
              }
            }
          }
        }
        
        // 🚨 Check if current token got rugged while bot is watching
        if (botControlEnabled && currentToken && currentToken.state === 'rugged' && view === 'token') {
          console.log(`🤖 Current token rugged! Returning to lobby`);
          setTimeout(() => {
            setView('lobby');
            setCurrentToken(null);
            setBotFocusedTokenId(null);
          }, 1500);
        }
      } else {
        // Handle error responses
        const errorData = await res.json().catch(() => ({}));
        console.log('Trading bot error:', res.status, errorData);
        
        if (errorData.requiresPremium) {
          // User needs premium - show premium modal
          setShowPremiumComparison(true);
          setShowTradingBot(false);
        } else {
          // Other error - set empty data so modal shows something
          setTradingBotData({
            active: false,
            fundBalance: 0,
            hasFunds: false,
            error: errorData.error || 'Failed to load bot status'
          });
        }
      }
    } catch (e) {
      console.log('Trading bot fetch error:', e.message);
      // Set error state so user sees something
      setTradingBotData({
        active: false,
        fundBalance: 0,
        hasFunds: false,
        error: e.message || 'Connection error'
      });
    }
    setTradingBotLoading(false);
  };
  
  // 🤖 Bot status now pushed via WebSocket - no polling needed!
  // Backend calls pushBotStatus() on trades and command changes

  // 🤖 Auto-fetch bot status when modal opens
  useEffect(() => {
    if (showTradingBot && !tradingBotLoading) {
      // Always fetch when modal opens (clears any previous error state)
      fetchTradingBotStatus();
    }
  }, [showTradingBot]);

  // 🤖 Bot heartbeat via WebSocket - keeps bot alive while user is watching
  useEffect(() => {
    if (!tradingBotData?.active || !wsRef.current) return;

    // Send heartbeat every 10 seconds while bot is active
    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'bot_heartbeat' }));
      }
    }, 10000);

    // Send one immediately
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'bot_heartbeat' }));
    }

    return () => clearInterval(heartbeatInterval);
  }, [tradingBotData?.active]);
  
  const subscribeTradingBot = async (days = 1) => {
    if (!API_URL || !sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/trading-bot/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify({ days })
      });
      const data = await res.json();
      if (res.ok) {
        Sounds.buy();
        setGameWallet(data.newBalance);
        fetchTradingBotStatus();
      } else {
        alert(data.error || 'Failed to subscribe');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };
  
  const updateBotSettings = async (settings) => {
    if (!API_URL || !sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/trading-bot/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        fetchTradingBotStatus();
      }
    } catch (e) {
      console.log('Bot settings error:', e.message);
    }
  };
  
  const stopTradingBot = async () => {
    if (!API_URL || !sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/trading-bot/stop`, {
        method: 'POST',
        headers: { 'X-Session-Id': sessionId }
      });
      const data = await res.json();
      if (res.ok) {
        fetchTradingBotStatus();
        // Silent stop - no alert for smoother UX
      } else {
        alert(data.error || 'Failed to stop bot');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // ============== 📊 BOT PORTFOLIO FUNCTIONS (Redis-backed) ==============

  const fetchBotPortfolio = async () => {
    const handle = handcashUser?.handle?.replace('$', '');
    if (!API_URL || !handle) return;
    setBotLoading(true);
    try {
      // Positions now come from unified auth/Redis - no separate fetch needed
      // Fetch trade history only
      const tradeRes = await fetch(`${API_URL}/api/portfolio/${handle}/trades?limit=20`);
      if (tradeRes.ok) {
        const tradeData = await tradeRes.json();
        setBotTrades(tradeData.trades || []);
      }
      // Fetch bot config
      const configRes = await fetch(`${API_URL}/api/bot/${handle}/config`);
      if (configRes.ok) {
        const configData = await configRes.json();
        setBotConfig(configData.config || botConfig);
      }
    } catch (e) {
      console.log('Bot portfolio fetch error:', e.message);
    }
    setBotLoading(false);
  };

  // sellBotPosition removed - bot positions now use regular sell flow via unified positions

  const saveBotConfig = async (newConfig) => {
    const handle = handcashUser?.handle?.replace('$', '');
    if (!API_URL || !handle) return;
    try {
      const res = await fetch(`${API_URL}/api/bot/${handle}/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newConfig)
      });
      if (res.ok) {
        const data = await res.json();
        setBotConfig(data.config);
        showToast('success', 'Bot config saved');
      }
    } catch (e) {
      showToast('error', 'Save error: ' + e.message);
    }
  };

  // 🤖 Fetch bot portfolio when switching to bot tab
  useEffect(() => {
    const handle = handcashUser?.handle?.replace('$', '');
    if (view === 'portfolio' && portfolioTab === 'bot' && handle) {
      fetchBotPortfolio();
    }
  }, [view, portfolioTab, handcashUser]);

  // ============== SERVER TOKEN FUNCTIONS ==============
  // Fetch active tokens from server (for lobby)
  const fetchServerTokens = async () => {
    if (!API_URL) return null;
    try {
      const res = await fetch(`${API_URL}/api/tokens`);
      if (res.ok) {
        const data = await res.json();
        console.log(`🪙 Fetched ${data.tokens?.length || 0} tokens from server`);
        // 🌅 Update warmup countdown from server
        if (data.warmupSecondsRemaining !== undefined) {
          setWarmupSecondsRemaining(data.warmupSecondsRemaining);
        }
        // 👁️ Update game visibility from server
        if (data.gameVisibility) {
          setGameVisibility(data.gameVisibility);
        }
        return data.tokens || [];
      }
    } catch (e) {
      console.log('Token fetch error:', e.message);
    }
    return null;
  };
  
  // Fetch user-launched tokens from server
  const fetchUserTokens = async () => {
    if (!API_URL) return;
    try {
      const res = await fetch(`${API_URL}/api/tokens/user`);
      if (res.ok) {
        const data = await res.json();
        console.log(`👤 Fetched ${data.tokens?.length || 0} user tokens from server`);
        setUserLaunchedTokens(data.tokens || []);
      }
    } catch (e) {
      console.log('User token fetch error:', e.message);
    }
  };
  
  // Fetch user tokens when switching to user view
  useEffect(() => {
    if (tokenViewMode === 'user' && handcashConnected) {
      fetchUserTokens();
    }
  }, [tokenViewMode, handcashConnected]);

  // Fetch single token with full candle history (for token room)
  const fetchTokenDetails = async (tokenId, interval = null) => {
    if (!API_URL) {
      console.log('❌ fetchTokenDetails: No API_URL');
      return null;
    }
    const intervalParam = interval || candleInterval || '1m';
    console.log(`📡 Fetching token details for: ${tokenId} (interval: ${intervalParam})`);
    try {
      const res = await fetch(`${API_URL}/api/token/${tokenId}?interval=${intervalParam}`, {
        headers: sessionId ? { 'X-Session-Id': sessionId } : {}
      });
      console.log(`📡 Token fetch response: ${res.status} ${res.ok}`);
      if (res.ok) {
        const data = await res.json();
        console.log(`📊 Fetched token ${data.ticker} with ${data.candles?.length || 0} candles (${data.candleInterval})`);
        return data;
      } else {
        console.log(`❌ Token fetch failed with status: ${res.status}`);
      }
    } catch (e) {
      console.log('❌ Token detail fetch error:', e.message);
    }
    return null;
  };

  // 🧹 Clear unacknowledged rugs so user can trade again
  const clearRugs = async () => {
    if (!sessionId || !API_URL || !handcashUser?.handle) return false;

    try {
      const res = await fetch(`${API_URL}/api/clear-rugs/${handcashUser.handle}`, {
        method: 'POST',
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        const data = await res.json();
        console.log(`🧹 Cleared ${data.clearedCount} rugged bags`);
        setUnclearedRugs(0);
        setShowRugAcknowledgeModal(false);
        Sounds.click(); // Satisfying click
        return true;
      }
    } catch (e) {
      console.log('❌ Clear rugs error:', e.message);
    }
    return false;
  };

  // 🧹 Clear a single rugged position from Redis
  const clearPosition = async (tokenId) => {
    if (!sessionId || !API_URL || !handcashUser?.handle) return false;

    try {
      const res = await fetch(`${API_URL}/api/portfolio/${handcashUser.handle}/clear/${tokenId}`, {
        method: 'DELETE',
        headers: { 'X-Session-Id': sessionId }
      });
      if (res.ok) {
        console.log(`🧹 Cleared rugged position: ${tokenId}`);
        // Remove from local positions state
        setPositions(prev => prev.filter(p => p.tokenId !== tokenId));
        Sounds.click();
        return true;
      }
    } catch (e) {
      console.log('❌ Clear position error:', e.message);
    }
    return false;
  };

  // Server buy - call API to buy tokens (ON-CHAIN - triggers HandCash payment!)
  const serverBuy = async (tokenId, amount) => {
    if (!sessionId || !API_URL) return null;

    // 🧹 Block trading if user has uncleared rugs
    if (unclearedRugs > 0) {
      console.log(`⚠️ Cannot buy - user has ${unclearedRugs} uncleared rugs`);
      setShowRugAcknowledgeModal(true);
      return null;
    }

    try {
      const res = await fetch(`${API_URL}/api/token/${tokenId}/buy`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
        body: JSON.stringify({ amount })
      });
      if (res.ok) {
        const data = await res.json();
        console.log(`✅ Server buy success:`, data);

        // 💰 Show trade notification toast
        const token = activeTokens.find(t => t.id === tokenId) || currentToken;
        const rake = token?.isUserLaunched ? Math.floor(amount * 0.01) : 0;
        setTradeNotification({
          tokenId,
          ticker: token?.ticker || '???',
          type: 'buy',
          amount,
          rake,
          isPvP: token?.isUserLaunched || false,
          isBot: false,
          timestamp: Date.now()
        });
        setTimeout(() => setTradeNotification(null), 3000);

        // ⛓️ ON-CHAIN: Show transaction confirmation
        if (data.txId) {
          setLastTxId(data.txId);
          const txRecord = {
            type: 'buy',
            amount,
            txId: data.txId,
            ticker: currentToken?.ticker,
            timestamp: Date.now(),
            onChain: true,
          };
          setPendingTx(txRecord);
          setTxHistory(prev => [txRecord, ...prev].slice(0, 50));
          setShowTxConfirmation(true);
          Sounds.success(); // ⛓️ Special sound for on-chain!
        } else {
          Sounds.buy();
        }
        
        // 🪙 RAKE NOTIFICATION - Show fee when charged
        if (data.rake && data.rake > 0) {
          triggerRakeNotification(data.rake, 'trade');
        }

        return data;
      } else {
        const error = await res.json();
        console.log(`❌ Server buy failed:`, error);
        Sounds.error();
        return { error: error.error || 'Buy failed' };
      }
    } catch (e) {
      console.log('Server buy error:', e.message);
      return { error: e.message };
    }
  };

  // Server sell - call API to sell tokens (ON-CHAIN - house pays you real BSV!)
  const serverSell = async (tokenId, percentage) => {
    if (!sessionId || !API_URL) return null;
    
    try {
      const res = await fetch(`${API_URL}/api/token/${tokenId}/sell`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
        body: JSON.stringify({ percentage })
      });
      if (res.ok) {
        const data = await res.json();
        console.log(`✅ Server sell success:`, data);

        // 💰 Show trade notification toast
        const token = activeTokens.find(t => t.id === tokenId) || currentToken;
        const rake = token?.isUserLaunched ? Math.floor(data.satsReceived * 0.01) : 0;
        setTradeNotification({
          tokenId,
          ticker: token?.ticker || '???',
          type: 'sell',
          amount: data.satsReceived,
          rake,
          profit: data.profit,
          isPvP: token?.isUserLaunched || false,
          isBot: false,
          timestamp: Date.now()
        });
        setTimeout(() => setTradeNotification(null), 3000);

        // ⛓️ ON-CHAIN: Show transaction confirmation
        if (data.txId) {
          setLastTxId(data.txId);
          const txRecord = {
            type: 'sell',
            amount: data.satsReceived,
            profit: data.profit,
            txId: data.txId,
            ticker: currentToken?.ticker,
            timestamp: Date.now(),
            onChain: true,
          };
          setPendingTx(txRecord);
          setTxHistory(prev => [txRecord, ...prev].slice(0, 50));
          setShowTxConfirmation(true);
          Sounds.success(); // ⛓️ Special sound for on-chain!
        } else {
          Sounds.sell();
        }
        
        // 🪙 RAKE NOTIFICATION - Show fee when charged
        if (data.rake && data.rake > 0) {
          triggerRakeNotification(data.rake, 'trade');
        }

        return data;
      } else {
        const error = await res.json();
        console.log(`❌ Server sell failed:`, error);
        Sounds.error();
        return { error: error.error || 'Sell failed' };
      }
    } catch (e) {
      console.log('Server sell error:', e.message);
      return { error: e.message };
    }
  };

  // Send chat message to token room (premium only)
  const sendTokenChat = async (text) => {
    console.log('💬 sendTokenChat called:', { text, sessionId: !!sessionId, API_URL: !!API_URL, tokenId: currentToken?.id, isPremium });
    if (!sessionId || !API_URL || !currentToken?.id || !isPremium) {
      console.log('❌ sendTokenChat blocked:', { sessionId: !!sessionId, API_URL: !!API_URL, tokenId: currentToken?.id, isPremium });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/token/${currentToken.id}/chat`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
        body: JSON.stringify({ text })
      });
      if (res.ok) {
        const data = await res.json();
        console.log(`💬 Chat sent:`, data.message);
        // Add to local chat immediately (will be deduped on next poll)
        setTokenChat(prev => [...prev, { ...data.message, isYou: true }]);
      } else {
        const error = await res.json();
        console.log('❌ Chat send failed:', error);
      }
    } catch (e) {
      console.log('Token chat error:', e.message);
    }
  };

  // Initial token fetch for lobby (WebSocket lobby_update handles updates)
  useEffect(() => {
    if (view !== 'lobby' || !API_URL) return;

    const fetchInitialTokens = async () => {
      const tokens = await fetchServerTokens();
      if (tokens && tokens.length > 0) {
        // Transform server tokens to match frontend format
        const transformedTokens = tokens.map(t => ({
          ...t,
          price: t.currentPrice,
          fate: 'UNKNOWN',
          liquidity: t.liquidityPool,
          birthTime: t.birthTime,
          visibleAt: t.birthTime,
        }));
        setActiveTokens(transformedTokens);
        setTokensLoaded(true);
        // Track user token IDs for sound notifications
        tokens.forEach(t => {
          if (t.isUserLaunched) prevUserTokenIdsRef.current.add(t.id);
        });
      }
    };

    // Only fetch once on mount - WebSocket lobby_update handles updates
    fetchInitialTokens();
  }, [view, API_URL]);

  // 🔊 Play sound for new user-launched tokens (from WebSocket updates)
  useEffect(() => {
    if (!soundEnabled || !activeTokens.length) return;
    const myHandle = handcashUser?.handle?.replace('$', '');
    activeTokens.forEach(t => {
      if (t.isUserLaunched && !prevUserTokenIdsRef.current.has(t.id)) {
        if (prevUserTokenIdsRef.current.size > 0 && t.creator !== myHandle) {
          console.log(`🚀 New user token launched: ${t.ticker} by ${t.creator}`);
          Sounds.tokenLaunch();
        }
        prevUserTokenIdsRef.current.add(t.id);
      }
    });
  }, [activeTokens, soundEnabled, handcashUser?.handle]);

  // 🔮 SYNC BETS FROM SERVER - runs every 5 seconds regardless of view
  // Server auto-resolves bets when tokens rug/moon, we just need to fetch updates
  useEffect(() => {
    if (!API_URL || !sessionId) return;
    
    const syncBetsFromServer = async () => {
      // Only sync if we have pending bets
      const pendingBets = rugProphetBets.filter(b => b.status === 'pending');
      if (pendingBets.length === 0) return;
      
      // Check if any pending bet tokens are no longer active
      const activeTokenIds = new Set(activeTokens.map(t => t.id));
      const hasStaleBets = pendingBets.some(bet => !activeTokenIds.has(bet.tokenId));
      
      if (hasStaleBets) {
        console.log('🔮 [BetSync] Detected stale bets, fetching from server...');
        
        // Re-fetch bets and wallet from server (server already resolved them!)
        try {
          const [betsRes, authRes] = await Promise.all([
            fetch(`${API_URL}/api/rug-prophet/bets`, {
              headers: { 'X-Session-Id': sessionId }
            }),
            fetch(`${API_URL}/api/user`, {
              headers: { 'X-Session-Id': sessionId }
            })
          ]);
          
          if (betsRes.ok) {
            const betsData = await betsRes.json();
            
            // Check if any bets were resolved
            const resolvedBets = (betsData.bets || []).filter(b => 
              b.status !== 'pending' && 
              pendingBets.some(pb => pb.id === b.id && pb.status === 'pending')
            );
            
            if (resolvedBets.length > 0) {
              console.log(`🔮 [BetSync] Server resolved ${resolvedBets.length} bets!`);
              
              // Play sounds for resolved bets
              resolvedBets.forEach(bet => {
                if (bet.status === 'won') {
                  Sounds.cashout();
                  showBalanceChange(bet.payout);
                  setLobbyChat(prev => [...prev.slice(-25), {
                    id: 'prophet_win_' + Date.now(),
                    handle: '🔮',
                    text: `You predicted ${bet.tokenTicker} correctly! +${formatPB(bet.payout)}`,
                    isAction: true,
                    isTradeAnnouncement: true,
                    isWin: true,
                  }]);
                } else {
                  Sounds.error();
                  showBalanceChange(-bet.amount);
                  setLobbyChat(prev => [...prev.slice(-25), {
                    id: 'prophet_loss_' + Date.now(),
                    handle: '🔮',
                    text: `${bet.tokenTicker} ${bet.prediction === 'rug' ? 'mooned' : 'rugged'}! You lost ${formatPB(bet.amount)}`,
                    isAction: true,
                    isTradeAnnouncement: true,
                    isLoss: true,
                  }]);
                }
              });
            }
            
            // Update local state with server data
            setRugProphetBets(betsData.bets || []);
            setRugProphetStats(betsData.stats || { totalBets: 0, wins: 0, losses: 0, totalWon: 0, totalLost: 0 });
          }
          
          // Sync wallet balance from server
          if (authRes.ok) {
            const authData = await authRes.json();
            if (authData.gameBalance !== undefined) {
              setGameWallet(authData.gameBalance);
            }
          }
        } catch (e) {
          console.log('🔮 [BetSync] Error:', e.message);
        }
      }
    };
    
    // Run every 15s - SKIP if tab hidden or idle kicked
    syncBetsFromServer();
    const interval = setInterval(() => {
      if (!document.hidden && !isIdleKicked) syncBetsFromServer();
    }, 15000);
    return () => clearInterval(interval);
  }, [API_URL, rugProphetBets, activeTokens]);

  // Poll current token for real-time updates (only when PLAYING - spectators don't need frequent updates)
  const pollFailCount = useRef(0);
  
  useEffect(() => {
    if (view !== 'token' || !currentToken?.id || !API_URL) return;
    
    // Reset fail count when entering token view
    pollFailCount.current = 0;
    
    // Poll every 3s for price updates (was 1s - reduced to save egress)
    const pollInterval = 3000;
    
    const pollCurrentToken = async () => {
      const data = await fetchTokenDetails(currentToken.id);
      if (data) {
        // Reset fail counter on success
        pollFailCount.current = 0;
        
        // Update price from server
        setCurrentPrice(data.currentPrice);
        
        // Also push to priceHistoryRef so game loop can update candles
        if (priceHistoryRef.current[priceHistoryRef.current.length - 1] !== data.currentPrice) {
          priceHistoryRef.current.push(data.currentPrice);
          // Keep history manageable
          if (priceHistoryRef.current.length > 100) {
            priceHistoryRef.current = priceHistoryRef.current.slice(-50);
          }
        }
        
        // Update candles with real server data
        // Short grace period (2s) after trades to prevent race condition where
        // server returns stale data before processing the trade
        const timeSinceLastTrade = Date.now() - lastUserTradeRef.current;
        const recentlyTraded = timeSinceLastTrade < 2000; // 2 second window
        
        if (data.candles && data.candles.length > 0 && !recentlyTraded) {
          const formattedCandles = data.candles.map((c, i) => ({
            id: `candle_${i}`,
            time: c.time, // 📊 Preserve server timestamp
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
            buyVolume: c.buyVolume || 0,
            sellVolume: c.sellVolume || 0,
          }));
          
          // Note: currentCandle is now included in server aggregation, no need to add separately
          
          setCandles(formattedCandles);
        }
        // Note: When recentlyTraded is true, the game loop (line ~5970) continues to update
        // the current candle with server price, so the chart stays live
        
        // Update token players from real positions
        if (data.positions) {
          const playerColors = ['#ff6b6b', '#4ecdc4', '#ffe66d', '#a29bfe', '#fd79a8', '#00b894', '#e17055', '#74b9ff'];
          
          // Deduplicate by handle - keep the one with most tokens
          const uniquePlayers = {};
          data.positions.forEach(p => {
            const handle = p.handle?.replace(/^\$/, '') || 'unknown';
            if (!uniquePlayers[handle] || (p.tokens || 0) > (uniquePlayers[handle].tokens || 0)) {
              uniquePlayers[handle] = p;
            }
          });
          
          const players = Object.values(uniquePlayers).map((p, i) => ({
            handle: p.handle,
            entry: p.entryPrice,
            tokens: p.tokens,
            invested: p.invested, // 👑 For creator view
            isActive: (p.tokens || 0) > 0,
            color: playerColors[i % playerColors.length],
            isBot: p.isBot,
          }));
          setTokenPlayers(players);
        }
        
        // Sync recent trades from server to transaction feed
        if (data.recentTrades && data.recentTrades.length > 0) {
          setTransactions(data.recentTrades.map(t => ({
            id: t.id,
            type: t.type,
            sats: t.sats,
            price: t.price,
            wallet: t.handle,
            isYou: t.handle === handcashUser?.handle,
            isBot: t.isBot,
            timestamp: t.timestamp,
          })));
          
          // 🐋 WHALE ALERT - Play sound for $500K+ buys (not your own trades)
          if (soundEnabled) {
            const recentWhale = data.recentTrades.find(t => 
              t.type === 'buy' && 
              t.sats >= 500000 && 
              t.handle !== handcashUser?.handle &&
              (Date.now() - t.timestamp) < 10000 // Only if within last 10 seconds
            );
            if (recentWhale && !soundPlayedForTokens.current.has(`whale_${recentWhale.id}`)) {
              Sounds.whale();
              soundPlayedForTokens.current.add(`whale_${recentWhale.id}`);
              console.log(`🐋 WHALE ALERT: ${recentWhale.handle} bought ${formatPB(recentWhale.sats)}!`);
            }
          }
        }
        
        // Check if token rugged
        if (data.state === 'rugged') {
          // Show rug screen regardless of whether spectating or playing
          if (gameState === 'playing') {
            handleServerRug(data);
          } else if (gameState !== 'rugged') {
            // Spectator - just show the rug animation (only if not already showing rugged)
            console.log('💀 TOKEN RUGGED (spectating):', data.ticker);
            // Only play sound once per token (use Set to track multiple)
            if (!soundPlayedForTokens.current.has(`rug_${data.id}`)) {
              Sounds.rug(); // 💀 Play rug sound
              soundPlayedForTokens.current.add(`rug_${data.id}`);
            }
            setGameState('rugged');
            setCurrentPrice(0);
            
            // 🔮 RESOLVE RUG PROPHET BETS - even for spectators!
            resolveRugProphetBets(data.id, data.ticker, 'rug');
          }
        }
        
        // Check if token mooned
        if (data.state === 'mooned') {
          if (gameState === 'playing') {
            handleServerMoon(data);
          } else if (gameState !== 'won') {
            // Spectator - just show the moon animation (only if not already showing won)
            console.log('🌙 TOKEN MOONED (spectating):', data.ticker);
            // Only play sound once per token
            if (!soundPlayedForTokens.current.has(`moon_${data.id}`)) {
              Sounds.moon(); // 🌙 Play moon sound
              soundPlayedForTokens.current.add(`moon_${data.id}`);
            }
            setGameState('won');
            
            // 🔮 RESOLVE RUG PROPHET BETS - even for spectators!
            resolveRugProphetBets(data.id, data.ticker, 'moon');
          }
        }
        
        // Check if token graduated (mooned tokens that hit 10 min - everyone gets paid!)
        if (data.state === 'graduated') {
          console.log('🎓 TOKEN GRADUATED:', data.ticker);
          // Treat like a moon win - positions were auto-sold on server
          if (gameState === 'playing') {
            // User had position - they got paid out, refresh balance
            refreshBalances();
            setGameState('won');
            showPepeToast('win', 'right', 3000);
          } else {
            setGameState('won');
          }
          // Show graduation message in chat
          setTokenChat(prev => [...prev, {
            id: 'graduated_' + Date.now(),
            text: `🎓 ${data.ticker} GRADUATED! All holders paid out!`,
            isAction: true,
            timestamp: Date.now()
          }]);
        }
        
        // Check if token expired (faded out without rugging or mooning)
        if (data.state === 'expired') {
          console.log('⏰ TOKEN EXPIRED:', data.ticker);
          // Just notify - positions should have been handled on server
          setTokenChat(prev => [...prev, {
            id: 'expired_' + Date.now(),
            text: `⏰ ${data.ticker} has expired and is no longer trading`,
            isAction: true,
            timestamp: Date.now()
          }]);
          // Return to lobby after a moment
          setTimeout(() => {
            setCurrentToken(null);
            setView('lobby');
          }, 3000);
        }
        
        // Sync chat messages from server (merge with local)
        if (data.chat && data.chat.length > 0) {
          setTokenChat(prev => {
            // Combine server chat with local-only messages (actions, your messages not yet confirmed)
            const localOnlyMsgs = prev.filter(p => p.isAction || p.isRug || (p.isYou && !p.fromServer));
            
            // Map server messages
            const serverMsgs = data.chat.map(m => ({ ...m, fromServer: true }));
            
            // Check for new messages from others (not from us, not already in prev)
            const prevIds = new Set(prev.map(p => p.id));
            const myHandle = '$' + (handcashUser?.handle?.replace('$', '') || '');
            const newFromOthers = serverMsgs.filter(m => 
              !prevIds.has(m.id) && 
              m.handle !== myHandle && 
              !m.isBot
            );
            
            // Play chat sound for new human messages (max once per poll)
            if (newFromOthers.length > 0) {
              Sounds.chat();
            }
            
            // Merge and dedupe by id
            const allMsgs = [...serverMsgs, ...localOnlyMsgs];
            const uniqueMsgs = allMsgs.filter((msg, index, self) => 
              index === self.findIndex(m => m.id === msg.id)
            );
            
            // Sort by timestamp and keep last 30
            return uniqueMsgs
              .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
              .slice(-30);
          });
        }
        
        // Update viewer count
        setViewerCount(data.playerCount + data.botCount);
        
        // Update current token state
        setCurrentToken(prev => prev ? {
          ...prev,
          currentPrice: data.currentPrice,
          liquidityPool: data.liquidityPool,
          tokenReserve: data.tokenReserve,
          marketCap: data.marketCap,
          state: data.state,
          hasMooned: data.hasMooned,
          moonPrice: data.moonPrice,
          totalBuyVolume: data.totalBuyVolume,
          totalSellVolume: data.totalSellVolume,
          initialPrice: data.initialPrice,
          athPrice: data.athPrice,
          initialLiquidity: data.initialLiquidity,
        } : null);
        
        // 🔄 POSITION SYNC: Check if local position matches server
        // This catches cases where server restarted or position was closed
        if (gameState === 'playing' && positionRef.current?.size > 0) {
          const serverPos = data.myPosition;
          if (!serverPos || serverPos.tokens <= 0) {
            console.log('🔄 Position mismatch detected - server has no position, clearing local state');
            positionRef.current = { size: 0, invested: 0, entry: 1.0 };

            // Check if token was rugged - use 'rugged' status instead of 'closed'
            const isRugged = data.state === 'rugged' || currentToken?.state === 'rugged';
            const newStatus = isRugged ? 'rugged' : 'closed';
            console.log(`🔄 Position status: ${newStatus} (token state: ${data.state || currentToken?.state})`);

            setGameState(isRugged ? 'rugged' : 'spectating');

            // Also update positions array
            const currentPos = positions.find(p => p.tokenId === currentToken?.id && p.status === 'active');
            if (currentPos) {
              const updatedPositions = positions.map(p =>
                p.id === currentPos.id ? { ...p, status: newStatus, closedAt: Date.now() } : p
              );
              savePositions(updatedPositions);
            }
          }
        }
        
        // 🔄 REVERSE SYNC: If spectating but server says we have a position, switch to playing!
        // This catches reconnects after server restart or session changes
        if (gameState === 'spectating' && data.myPosition && data.myPosition.tokens > 0) {
          console.log('🔄 Position found on server while spectating - switching to playing!');
          const serverPos = data.myPosition;
          positionRef.current = { 
            size: serverPos.tokens, 
            invested: serverPos.invested || 0, 
            entry: serverPos.entryPrice || data.currentPrice || 1.0 
          };
          setGameState('playing');
        }
      } else {
        // Token fetch failed - increment fail counter
        pollFailCount.current++;
        console.log(`⚠️ Token fetch failed (attempt ${pollFailCount.current}/3)`);
        
        // Only kick to lobby after 3 consecutive failures (1.5 seconds of failures)
        if (pollFailCount.current >= 3) {
          console.log('💀 Token not found after 3 attempts...');

          // Check if we had a position - if so, this was a rug!
          if (positionRef.current?.size > 0 && currentToken) {
            console.log('💀 We had a position! Showing rug UI instead of silent kick');
            // Call handleServerRug with cached token data
            handleServerRug({
              id: currentToken.id,
              ticker: currentToken.ticker || 'UNKNOWN',
              category: currentToken.category,
              currentPrice: 0,
              state: 'rugged'
            });
          } else {
            // No position, just spectating - safe to kick to lobby
            console.log('👀 No position, returning to lobby');
            if (currentToken?.id) {
              broadcastEvent('leave_token', { tokenId: currentToken.id });
            }
            setTimeout(() => {
              setView('lobby');
              setCurrentToken(null);
              setGameState('lobby');
            }, 1000);
          }
        }
      }
    };
    
    // Poll immediately to get initial data
    pollCurrentToken();
    
    // Keep polling for full token data - SKIP if tab hidden or idle kicked
    const interval = setInterval(() => {
      if (!document.hidden && !isIdleKicked) pollCurrentToken();
    }, pollInterval);
    return () => clearInterval(interval);
  }, [view, currentToken?.id, API_URL, gameState, candleInterval]);

  // Handle server-side rug event
  const handleServerRug = (tokenData) => {
    console.log('💀 SERVER RUG:', tokenData.ticker);
    
    // Only play sound once per token (prevent repeats from race conditions)
    if (!soundPlayedForTokens.current.has(`rug_${tokenData.id}`)) {
      Sounds.rug(); // 💀 Play devastating rug sound
      soundPlayedForTokens.current.add(`rug_${tokenData.id}`);
    }
    
    // 🔮 RESOLVE RUG PROPHET BETS - Token rugged from server!
    resolveRugProphetBets(tokenData.id, tokenData.ticker, 'rug');
    
    // Find our position
    const pos = positions.find(p => p.tokenId === tokenData.id && p.status === 'active');
    if (pos) {
      // Mark position as rugged
      const updatedPositions = positions.map(p => 
        p.id === pos.id 
          ? { ...p, status: 'rugged', closedAt: Date.now(), profit: -pos.invested }
          : p
      );
      savePositions(updatedPositions);
      
      // Add to bags
      addToBags(
        { ticker: tokenData.ticker, category: tokenData.category, rugType: 'SERVER_RUG' },
        pos.size,
        pos.invested
      );
      
      // Update stats
      setStats(prev => ({
        ...prev,
        losses: prev.losses + 1,
        rugs: prev.rugs + 1,
        totalProfit: prev.totalProfit - pos.invested,
      }));
      
      // 📊 Update daily stats for portfolio display
      saveDailyStats({
        ...dailyStats,
        trades: [...dailyStats.trades, {
          id: 'rug_' + Date.now(),
          type: 'rugged',
          tokenTicker: tokenData.ticker,
          profit: -pos.invested,
          timestamp: Date.now(),
        }],
      });
    }
    
    setGameState('rugged');
    setCurrentPrice(0);
  };

  // Handle server-side moon event
  const handleServerMoon = (tokenData) => {
    console.log('🌙 SERVER MOON:', tokenData.ticker, '@', tokenData.currentPrice);
    
    // Only play sound once per token (prevent repeats from race conditions)
    if (!soundPlayedForTokens.current.has(`moon_${tokenData.id}`)) {
      Sounds.moon(); // 🌙 Play triumphant moon sound
      soundPlayedForTokens.current.add(`moon_${tokenData.id}`);
    }
    
    // 🔮 RESOLVE RUG PROPHET BETS - Token mooned from server!
    resolveRugProphetBets(tokenData.id, tokenData.ticker, 'moon');
    
    // Position is still active - player can sell at moon price
    // Just update the state to show it mooned
    if (gameState === 'playing') {
      setGameState('won');
    }
  };

  const deposit = async (amount) => {
    console.log('💰 Deposit initiated - RAW amount:', amount, 'type:', typeof amount);
    
    // Force to integer
    const amountInt = Math.floor(Number(amount));
    console.log('💰 Deposit - PARSED amountInt:', amountInt, 'type:', typeof amountInt);
    
    // Validate amount - must be at least 1000 sats
    if (!amountInt || isNaN(amountInt) || amountInt < 1000) {
      console.error('❌ Deposit BLOCKED - invalid amount:', amount, '→', amountInt);
      alert(`Invalid deposit amount: ${amount} sats\n\nMinimum deposit is 1,000 sats.\n\nIf you see this on mobile, please refresh and try again.`);
      return;
    }
    
    // Check against spendable limit (daily limit), not total balance
    const availableToDeposit = mainWalletSpendable > 0 ? mainWalletSpendable : mainWallet;
    if (availableToDeposit < amountInt) {
      if (mainWalletSpendable > 0 && mainWalletSpendable < mainWallet) {
        alert(`Exceeds your HandCash daily spending limit!\n\nYou have $${(mainWallet * satToUsd).toFixed(2)} total but only $${(mainWalletSpendable * satToUsd).toFixed(2)} available to spend today.\n\nIncrease your limit at: app.handcash.io/#/settings/spendLimits`);
      } else {
        alert('Insufficient HandCash balance');
      }
      return;
    }
    
    // Confirmation dialog - show exact sats
    const confirmed = window.confirm(
      `Confirm Deposit?\n\n` +
      `EXACTLY ${amountInt.toLocaleString()} sats (~$${(amountInt * satToUsd).toFixed(2)}) will be transferred from your HandCash wallet to your Paper Bucks balance.\n\n` +
      `This transaction will be processed by HandCash.`
    );
    
    if (!confirmed) return;
    
    setTransactionPending(true);
    
    // Try server deposit (sats go to house wallet)
    if (sessionId && API_URL) {
      try {
        const bodyData = { amount: amountInt };
        console.log('📡 Sending deposit to server - body:', JSON.stringify(bodyData));
        const res = await fetch(`${API_URL}/api/deposit`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Session-Id': sessionId 
          },
          body: JSON.stringify(bodyData)
        });
        
        if (res.ok) {
          const result = await res.json();
          console.log('✅ Deposit result:', result);
          // Use actual HandCash balance from server if provided
          if (result.handcashBalance !== undefined) {
            setMainWallet(result.handcashBalance);
          } else {
            setMainWallet(prev => prev - amountInt);
          }
          setGameWallet(result.newBalance);
          setTotalDeposited(result.totalDeposited);
          showBalanceChange(amountInt);
          showMainWalletChange(-amountInt);
          setTxConnectionStatus('ok');
          setLastTxError(null);
          
          // Celebrate!
          if (totalDeposited === 0) {
            setTimeout(() => showPepeToast('firstDeposit', 'right', 4000), 500);
          } else if (amountInt >= 5000000) {
            setTimeout(() => showPepeToast('bigDeposit', 'right', 4000), 500);
          }
          setTransactionPending(false);
          setPendingDeposit(null);
          setShowDeposit(false);
          return;
        } else {
          const errorData = await res.text();
          console.error('❌ Deposit failed:', errorData);
          setTxConnectionStatus('error');
          
          // Parse error response to check if server already set degraded mode
          let serverDegraded = false;
          try {
            const parsed = JSON.parse(errorData);
            if (parsed.degradedMode) {
              console.log('🐦 Server already in degraded mode, triggered by:', parsed.triggeredBy);
              serverDegraded = true;
              setHandcashDown(true); // Sync local state with server
            }
          } catch (e) { /* not JSON, ignore */ }
          
          // 🐦 CANARY: Any failure shows rug modal (canary controls degraded mode)
          // For regular users, only 500+ errors show rug modal
          const isCanaryUser = handcashUser?.handle?.toLowerCase() === '$blackwolf';
          
          if (res.status >= 500 || isCanaryUser) {
            console.log('🚨 Cloudflare Rug detected during deposit!');
            
            // If server already handled degraded mode, skip the extra API calls
            if (!serverDegraded) {
              // 🐝 HIVE MIND: ALL users report failures to help detect patterns
              if (sessionId && API_URL) {
                console.log('🐝 Reporting deposit failure to hive...');
                try {
                  await fetch(`${API_URL}/api/hive-report`, {
                    method: 'POST',
                    headers: { 
                      'Content-Type': 'application/json',
                      'X-Session-Id': sessionId
                    },
                    body: JSON.stringify({ type: 'deposit', reason: `Server returned ${res.status}`, statusCode: res.status })
                  });
                  console.log('🐝 Hive notified');
                } catch (hiveErr) {
                  console.log('🐝 Could not notify hive:', hiveErr.message);
                }
              }
              
              // 🐦 CANARY: Only blackwolf can trigger degraded mode
              if (isCanaryUser && sessionId && API_URL) {
                console.log('🐦 Canary triggering degraded mode...');
                try {
                  await fetch(`${API_URL}/api/canary-trigger`, {
                    method: 'POST',
                    headers: { 
                      'Content-Type': 'application/json',
                      'X-Session-Id': sessionId
                    },
                    body: JSON.stringify({ type: 'deposit', reason: `Server returned ${res.status}` })
                  });
                  console.log('🐦 Degraded mode triggered');
                  setHandcashDown(true); // Set local state after successful trigger
                } catch (triggerErr) {
                  console.log('🐦 Could not trigger:', triggerErr.message);
                }
              }
            } else {
              console.log('🐦 Server already handled degraded mode - skipping extra API calls');
            }
            
            setLastTxError('HandCash unavailable');
            // Only show modal if not already shown
            if (!serverRugAlertShownRef.current) {
              serverRugAlertShownRef.current = true;
              setRugModalMessage('deposit');
              setShowRugModal(true);
            }
            setTransactionPending(false);
            setShowDeposit(false);
            return;
          }
          
          let errorMsg = 'Server error';
          try {
            const parsed = JSON.parse(errorData);
            errorMsg = parsed.error || parsed.message || 'Server error';
          } catch {
            errorMsg = errorData || 'Server error';
          }
          
          setLastTxError(errorMsg);
          alert(`Deposit failed: ${errorMsg}. Please try again.`);
          setTransactionPending(false);
          return;
        }
      } catch (e) {
        console.error('❌ Deposit network error:', e.message);
        setTxConnectionStatus('error');
        
        // Network errors often indicate infrastructure issues
        if (e.message?.includes('fetch') || e.message?.includes('network') || e.message?.includes('timeout') || e.name === 'TypeError') {
          console.log('🚨 Possible Cloudflare Rug detected (network error)');
          
          // 🐝 HIVE MIND: ALL users report failures to help detect patterns
          if (sessionId && API_URL) {
            console.log('🐝 Reporting deposit network error to hive...');
            try {
              await fetch(`${API_URL}/api/hive-report`, {
                method: 'POST',
                headers: { 
                  'Content-Type': 'application/json',
                  'X-Session-Id': sessionId
                },
                body: JSON.stringify({ type: 'deposit', reason: 'CORS/network error', statusCode: 0 })
              });
              console.log('🐝 Hive notified');
            } catch (hiveErr) {
              console.log('🐝 Could not notify hive:', hiveErr.message);
            }
          }
          
          // 🐦 CANARY: Only blackwolf can trigger degraded mode
          const handle = handcashUser?.handle?.toLowerCase()?.replace('$', '');
          const isCanaryUser = handle === 'blackwolf';
          if (isCanaryUser && API_URL) {
            console.log('🐦 Canary triggering degraded mode...');
            
            // Try up to 3 times with delays (server may be recovering from HandCash 502)
            for (let attempt = 1; attempt <= 3; attempt++) {
              try {
                // Wait before retry (server needs time to recover)
                if (attempt > 1) {
                  console.log(`🐦 Retry attempt ${attempt}/3 after ${attempt}s delay...`);
                  await new Promise(r => setTimeout(r, attempt * 1000));
                }
                
                const res = await fetch(`${API_URL}/api/canary-trigger`, {
                  method: 'POST',
                  headers: { 
                    'Content-Type': 'application/json',
                    'X-Session-Id': sessionId || ''
                  },
                  body: JSON.stringify({ 
                    type: 'deposit', 
                    reason: 'CORS/network error',
                    canaryHandle: 'blackwolf'  // Fallback if session was lost
                  })
                });
                
                if (res.ok) {
                  console.log('🐦 Degraded mode triggered successfully!');
                  setHandcashDown(true);
                  break; // Success, exit retry loop
                } else {
                  console.log(`🐦 Trigger returned ${res.status}, retrying...`);
                }
              } catch (triggerErr) {
                console.log(`🐦 Attempt ${attempt} failed:`, triggerErr.message);
                if (attempt === 3) {
                  console.log('🐦 All retries failed - degraded mode not set');
                }
              }
            }
          }
          
          setLastTxError('HandCash unavailable');
          // Only show modal if not already shown
          if (!serverRugAlertShownRef.current) {
            serverRugAlertShownRef.current = true;
            setRugModalMessage('deposit');
            setShowRugModal(true);
          }
        } else {
          setLastTxError(e.message || 'Network error');
          alert(`Deposit failed: ${e.message || 'Network error'}. Please try again.`);
        }
        
        setTransactionPending(false);
        setShowDeposit(false);
        return;
      }
    }
    
    // Local mode (no server)
    console.log('⚠️ No server - LOCAL mode');
    await new Promise(r => setTimeout(r, 800));
    setMainWallet(prev => prev - amount);
    setGameWallet(prev => prev + amount);
    setTransactionPending(false);
    setPendingDeposit(null);
    setShowDeposit(false);
  };

  // Small deposits for testing - $0.25 minimum
  const MINIMUM_FIRST_DEPOSIT_USD = 0.25;
  const MINIMUM_FIRST_DEPOSIT_SATS = usdToSats(MINIMUM_FIRST_DEPOSIT_USD);
  const isFirstDeposit = totalDeposited === 0;

  // Fetch premium pricing info
  const fetchPremiumPricing = async () => {
    if (!API_URL) return;
    try {
      const res = await fetch(`${API_URL}/api/premium/info`);
      if (res.ok) {
        const data = await res.json();
        setPremiumPricing(data);
        console.log('👑 Premium pricing loaded:', data);
      }
    } catch (e) {
      console.log('Failed to fetch premium pricing:', e.message);
    }
  };
  
  // Fetch premium pricing when modal opens
  useEffect(() => {
    if (showPremiumComparison) {
      fetchPremiumPricing();
    }
  }, [showPremiumComparison]);

  const buyPremium = async (plan = 'weekly') => {
    // Get pricing from server or use defaults
    const weeklyPlan = premiumPricing?.weekly || { cost: 0.25, days: 7, label: '1 Week' };
    const monthlyPlan = premiumPricing?.monthly || { cost: 0.75, daysRemaining: 28, label: 'Rest of Month' };
    
    const selectedPlan = plan === 'monthly' ? monthlyPlan : weeklyPlan;
    const cost = selectedPlan.cost;
    const days = plan === 'monthly' ? selectedPlan.daysRemaining : 7;
    
    // Confirmation dialog
    const confirmed = window.confirm(
      `Confirm Premium Purchase?\n\n` +
      `$${cost.toFixed(2)} USD will be charged to your HandCash wallet.\n\n` +
      (plan === 'monthly' 
        ? `Premium until ${selectedPlan.label} (${days} days)\n\n` +
          `💡 Monthly is prorated - buy early to maximize value!\n` +
          (selectedPlan.savings > 0 ? `✨ You save $${selectedPlan.savings.toFixed(2)} vs weekly!\n\n` : '\n')
        : `7 days of Premium access\n\n`) +
      `Features:\n` +
      `• 🤖 Trading Bot access\n` +
      `• 💬 Lobby chat\n` +
      `• 🎯 Daily challenges\n` +
      `• 📊 Advanced stats`
    );
    
    if (!confirmed) return;
    
    setShowPremiumComparison(false);
    setTransactionPending(true);
    
    // Try server-side premium purchase
    if (sessionId && API_URL) {
      try {
        console.log(`👑 Purchasing premium (${plan}) via HandCash...`);
        const res = await fetch(`${API_URL}/api/premium`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Session-Id': sessionId 
          },
          body: JSON.stringify({ plan })
        });
        
        if (res.ok) {
          const result = await res.json();
          console.log('👑 Premium activated!', result);
          
          setMainWallet(prev => prev - PREMIUM_SATS);
          setGameWallet(result.newBalance);
          setPremiumExpiresAt(result.premiumExpires);
          localStorage.setItem('paperHands_premiumExpires', result.premiumExpires.toString());
          setLastPremiumPurchase(Date.now());
          
          setTransactionPending(false);
          setShowDeposit(false);
          
          // Celebration!
          setShowPepeCelebration(true);
          setTimeout(() => setShowPepeCelebration(false), 4000);
          return;
        } else {
          const error = await res.json();
          console.log('❌ Premium purchase failed:', error);
          
          // If server says already premium, sync the state!
          if (error.premiumExpires && error.premiumExpires > Date.now()) {
            setPremiumExpiresAt(error.premiumExpires);
            localStorage.setItem('paperHands_premiumExpires', error.premiumExpires.toString());
            alert('You already have Premium! Refreshing status...');
            setShowPremiumComparison(false);
          } else {
            alert(error.error || 'Premium purchase failed');
          }
          setTransactionPending(false);
          return;
        }
      } catch (e) {
        console.log('❌ Server premium failed:', e.message);
        alert('Premium purchase failed: ' + e.message);
        setTransactionPending(false);
      }
    } else {
      alert('Not connected to server. Please refresh and try again.');
      setTransactionPending(false);
    }
  };

  const withdraw = async (amount) => {
    if (gameWallet <= 0 || gameState === 'playing') return;
    const withdrawAmount = amount || gameWallet;
    
    // Validate amount - must be at least 1000 sats
    if (!withdrawAmount || withdrawAmount < 1000) {
      alert(`Invalid withdrawal amount: ${withdrawAmount} sats\n\nMinimum withdrawal is 1,000 sats`);
      return;
    }
    
    if (withdrawAmount > gameWallet) return;
    
    console.log('💸 Withdraw initiated:', withdrawAmount, 'sats');
    setTransactionPending(true);
    
    // Try server withdraw (THIS IS THE REAL WITHDRAWAL - house sends sats to user!)
    if (sessionId && API_URL) {
      try {
        console.log('📡 Sending withdraw to server:', withdrawAmount, 'sats');
        const res = await fetch(`${API_URL}/api/withdraw`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Session-Id': sessionId 
          },
          body: JSON.stringify({ amount: withdrawAmount, bsvPrice: bsvPrice || 18.5 })
        });
        
        if (res.ok) {
          const result = await res.json();
          
          // 🛡️ PENDING APPROVAL: Large withdrawals need admin approval
          if (result.pending) {
            setTransactionPending(false);
            setShowWithdraw(false);
            setWithdrawAmount('');
            setShowPendingWithdrawalModal({
              amount: withdrawAmount,
              message: result.message,
              dailyRemaining: result.dailyRemaining,
              pendingId: result.pendingId
            });
            return;
          }
          
          const wasFullWithdraw = withdrawAmount >= gameWallet * 0.95; // 95% or more = ragequit
          setMainWallet(prev => prev + withdrawAmount);
          setGameWallet(result.newBalance);
          showBalanceChange(-withdrawAmount); // 💫 Animate game wallet (-)
          showMainWalletChange(withdrawAmount); // 💫 Animate main wallet (+)
          setTxConnectionStatus('ok'); // ✅ Connection working
          setLastTxError(null);
          
          // 🐸 PEPE ROASTS RAGEQUITTERS
          if (wasFullWithdraw) {
            setTimeout(() => showPepeToast('ragequit', 'left', 5000), 500);
          } else {
            setTimeout(() => showPepeToast('withdrawal', 'left', 3500), 500);
          }
          if (result.totalWithdrawn) setTotalWithdrawn(result.totalWithdrawn); // Server tracks on-chain withdrawals
          setTransactionPending(false);
          setShowWithdraw(false);
          setWithdrawAmount('');
          return;
        } else {
          // ❌ Server returned an error - DO NOT fall through to local!
          // This would deduct game balance without user actually receiving sats!
          const errorData = await res.text();
          console.error('❌ Withdrawal failed on server:', errorData);
          setTxConnectionStatus('error'); // 🔴 Connection issue
          
          // 🐦 CANARY: Any failure shows rug modal (canary controls degraded mode)
          // For regular users, only 500+ errors show rug modal
          const isCanaryUser = handcashUser?.handle?.toLowerCase() === '$blackwolf';
          
          if (res.status >= 500 || isCanaryUser) {
            console.log('🚨 Cloudflare Rug detected during withdrawal!');
            
            // 🐝 HIVE MIND: ALL users report failures to help detect patterns
            if (sessionId && API_URL) {
              console.log('🐝 Reporting withdraw failure to hive...');
              try {
                await fetch(`${API_URL}/api/hive-report`, {
                  method: 'POST',
                  headers: { 
                    'Content-Type': 'application/json',
                    'X-Session-Id': sessionId
                  },
                  body: JSON.stringify({ type: 'withdraw', reason: `Server returned ${res.status}`, statusCode: res.status })
                });
                console.log('🐝 Hive notified');
              } catch (hiveErr) {
                console.log('🐝 Could not notify hive:', hiveErr.message);
              }
            }
            
            // 🐦 CANARY: Only blackwolf can trigger degraded mode
            if (isCanaryUser && sessionId && API_URL) {
              console.log('🐦 Canary triggering degraded mode...');
              try {
                await fetch(`${API_URL}/api/canary-trigger`, {
                  method: 'POST',
                  headers: { 
                    'Content-Type': 'application/json',
                    'X-Session-Id': sessionId
                  },
                  body: JSON.stringify({ type: 'withdraw', reason: `Server returned ${res.status}` })
                });
                console.log('🐦 Degraded mode triggered');
              } catch (triggerErr) {
                console.log('🐦 Could not trigger:', triggerErr.message);
              }
            }
            
            setLastTxError('HandCash unavailable');
            // Only show modal if not already shown
            if (!serverRugAlertShownRef.current) {
              serverRugAlertShownRef.current = true;
              setRugModalMessage('withdraw');
              setShowRugModal(true);
            }
            setTransactionPending(false);
            setShowWithdraw(false);
            return;
          }
          
          // Parse error message from JSON or use raw text
          let errorMsg = 'Server error';
          try {
            const parsed = JSON.parse(errorData);
            errorMsg = parsed.error || parsed.message || 'Server error';
          } catch {
            errorMsg = errorData || 'Server error';
          }
          // Make it user-friendly
          if (errorMsg.toLowerCase().includes('unavailable')) {
            errorMsg = 'Systems temporarily down';
          }
          
          setLastTxError(errorMsg);
          alert(`Withdrawal failed: ${errorMsg}. Your balance is unchanged.`);
          setTransactionPending(false);
          return; // CRITICAL: Stop here, don't steal user's game balance!
        }
      } catch (e) {
        // ❌ Network/fetch error - DO NOT fall through to local!
        console.error('❌ Withdrawal network error:', e.message);
        setTxConnectionStatus('error'); // 🔴 Connection issue
        
        // Network errors often indicate infrastructure issues
        if (e.message?.includes('fetch') || e.message?.includes('network') || e.message?.includes('timeout') || e.name === 'TypeError') {
          console.log('🚨 Possible Cloudflare Rug detected (network error)');
          
          // 🐝 HIVE MIND: ALL users report failures to help detect patterns
          if (sessionId && API_URL) {
            console.log('🐝 Reporting withdraw network error to hive...');
            try {
              await fetch(`${API_URL}/api/hive-report`, {
                method: 'POST',
                headers: { 
                  'Content-Type': 'application/json',
                  'X-Session-Id': sessionId
                },
                body: JSON.stringify({ type: 'withdraw', reason: 'CORS/network error', statusCode: 0 })
              });
              console.log('🐝 Hive notified');
            } catch (hiveErr) {
              console.log('🐝 Could not notify hive:', hiveErr.message);
            }
          }
          
          // 🐦 CANARY: Only blackwolf can trigger degraded mode
          const isCanaryUser = handcashUser?.handle?.toLowerCase() === '$blackwolf';
          if (isCanaryUser && sessionId && API_URL) {
            console.log('🐦 Canary triggering degraded mode...');
            try {
              await fetch(`${API_URL}/api/canary-trigger`, {
                method: 'POST',
                headers: { 
                  'Content-Type': 'application/json',
                  'X-Session-Id': sessionId
                },
                body: JSON.stringify({ type: 'withdraw', reason: 'CORS/network error' })
              });
              console.log('🐦 Degraded mode triggered');
            } catch (triggerErr) {
              console.log('🐦 Could not trigger:', triggerErr.message);
            }
          }
          
          setLastTxError('HandCash unavailable');
          // Only show modal if not already shown
          if (!serverRugAlertShownRef.current) {
            serverRugAlertShownRef.current = true;
            setRugModalMessage('withdraw');
            setShowRugModal(true);
          }
        } else {
          setLastTxError(e.message || 'Network error');
          alert(`Withdrawal failed: ${e.message || 'Network error'}. Your balance is unchanged.`);
        }
        
        setTransactionPending(false);
        setShowWithdraw(false);
        return; // CRITICAL: Stop here, don't steal user's game balance!
      }
    }
    
    // 🧪 LOCAL MODE ONLY (no server connection - for testing/dev only)
    // This should NOT be reached in production with valid session!
    console.log('⚠️ No sessionId or API_URL - using LOCAL mode (dev/testing only)');
    console.log('🔄 Local withdraw (simulated, not on-chain - NO REAL SATS MOVED!)');
    const wasFullWithdraw = withdrawAmount >= gameWallet * 0.95; // 95% or more = ragequit
    await new Promise(r => setTimeout(r, 800));
    setMainWallet(prev => prev + withdrawAmount);
    setGameWallet(prev => prev - withdrawAmount);
    showBalanceChange(-withdrawAmount); // 💫 Animate game wallet (-)
    showMainWalletChange(withdrawAmount); // 💫 Animate main wallet (+)
    
    // 🐸 PEPE ROASTS RAGEQUITTERS
    if (wasFullWithdraw) {
      setTimeout(() => showPepeToast('ragequit', 'left', 5000), 500);
    } else {
      setTimeout(() => showPepeToast('withdrawal', 'left', 3500), 500);
    }
    // NOTE: We do NOT update totalWithdrawn here - only real HandCash txns count!
    setTransactionPending(false);
    setShowWithdraw(false);
    setWithdrawAmount('');
  };
  
  // Withdraw modal state
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showPendingWithdrawalModal, setShowPendingWithdrawalModal] = useState(null); // { amount, message, pendingId }
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [pendingWithdrawConfirm, setPendingWithdrawConfirm] = useState(false);

  // ============== TRANSACTION HISTORY ==============
  const fetchTransactionHistory = async () => {
    if (!sessionId || !API_URL) return;
    
    setLoadingTransactions(true);
    try {
      const res = await fetch(`${API_URL}/api/my-transactions`, {
        method: 'GET',
        headers: { 'X-Session-Id': sessionId }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTransactionHistory(data.transactions || []);
        setTransactionSummary(data.summary || null);
        console.log('📜 Loaded transaction history:', data.transactions?.length, 'transactions');
      }
    } catch (e) {
      console.log('Transaction history error:', e.message);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // ============== LOBBY ACTIONS ==============
  const sendLobbyChat = async () => {
    if (!lobbyInput.trim() || !isPremium || !sessionId || !API_URL) return;
    
    const text = lobbyInput.trim();
    setLobbyInput(''); // Clear immediately for responsiveness
    
    try {
      const res = await fetch(`${API_URL}/api/lobby/chat`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId 
        },
        body: JSON.stringify({ text })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Add to local chat immediately (will be deduped on next poll)
        setLobbyChat(prev => [...prev, { ...data.message, isUser: true }]);
      }
    } catch (e) {
      console.log('Lobby chat error:', e.message);
    }
  };

  const [joiningToken, setJoiningToken] = useState(false);
  
  const joinToken = async (token) => {
    // Prevent multiple clicks while loading
    if (joiningToken) {
      console.log('⏳ Already joining a token, ignoring click');
      return;
    }
    
    // Check access - use server's isPremiumOnly flag
    if (!isPremium && token.isPremiumOnly) {
      alert('🔒 Premium required for this token type!');
      return;
    }
    
    setJoiningToken(true);
    console.log(`🎮 Joining token: ${token.ticker} (id: ${token.id})`);
    
    try {
      // Fetch full token data from server (including real candles!)
      const serverToken = await fetchTokenDetails(token.id);
      
      if (!serverToken) {
        console.error('❌ Could not fetch token details from server - token may have expired');
        alert('This token is no longer available. It may have rugged or mooned. Refreshing tokens...');
        // Refresh the token list
        const freshTokens = await fetchServerTokens();
        if (freshTokens && freshTokens.length > 0) {
          const transformedTokens = freshTokens.map(t => ({
            ...t,
            price: t.currentPrice,
            // lifecycle is no longer sent by server (prevents cheating!)
            fate: 'UNKNOWN',
            liquidity: t.liquidityPool,
            birthTime: t.birthTime,
            visibleAt: t.birthTime,
          }));
          setActiveTokens(transformedTokens);
        }
        setJoiningToken(false);
        return;
      }
    
    console.log(`📊 Server token: ${serverToken.ticker} | Price: ${serverToken.currentPrice.toFixed(4)} | Candles: ${serverToken.candles?.length || 0}`);
    
    // 💼 Check if we have an active position in this token
    // Check local state first, then fall back to server's myPosition
    const localPosition = positions.find(p => p.tokenId === token.id && p.status === 'active');
    const serverPosition = serverToken.myPosition;
    const existingPosition = localPosition || (serverPosition && serverPosition.tokens > 0 ? {
      size: serverPosition.tokens,
      tokens: serverPosition.tokens,
      invested: serverPosition.invested || serverPosition.sats || 0,
      entry: serverPosition.entryPrice || serverPosition.entry || serverToken.currentPrice,
      tokenId: token.id,
      status: 'active',
    } : null);
    
    console.log(`💼 Position check: local=${!!localPosition}, server=${!!serverPosition}, using=${!!existingPosition}`);
    
    // If server has position but local doesn't, add it to local state
    if (!localPosition && existingPosition) {
      console.log('🔄 Syncing server position to local state');
      setPositions(prev => [...prev.filter(p => p.tokenId !== token.id), {
        id: 'pos_sync_' + Date.now(),
        tokenId: token.id,
        tokenTicker: serverToken.ticker,
        ...existingPosition,
      }]);
    }
    
    // Note: Don't reset soundPlayedForTokens - we want to remember all tokens we've played sounds for
    
    // Set currentToken with server data
    setCurrentToken({
      ...token,
      ...serverToken,
      price: serverToken.currentPrice,
    });
    setView('token');
    setCurrentPrice(serverToken.currentPrice);
    setTransactions([]);
    setRuggedPlayersCount(0);
    tickCountRef.current = 0;
    volumeRef.current = [];
    
    // Reset chart view to default when entering new token
    // Same default for all token types - 5s for detailed view
    setChartViewMode('5m'); // 5m candles - sweet spot for seeing price action
    setChartScrollOffset(0);
    
    // 📊 AUTO-SELECT TIMEFRAME based on token type
    // AI tokens are short-lived, need smaller intervals to see action
    // PvP tokens live longer, 1m works better
    if (serverToken.isUserLaunched) {
      setCandleInterval('1m'); // PvP token - 1 minute candles
    } else {
      setCandleInterval('10s'); // AI token - 10 second candles
    };

    // 📊 SMART AUTO-FOCUS: Calculate best zoom based on candle count
    const candleCount = serverToken.candles?.length || 0;
    if (candleCount > 0) {
      // Target: show 40-60 candles nicely on screen
      // More candles = zoom out more, fewer = zoom in
      if (candleCount < 20) {
        setChartZoom(2.0); // Very few candles, zoom in more
      } else if (candleCount < 50) {
        setChartZoom(1.2); // Few candles, zoom in a bit
      } else if (candleCount < 100) {
        setChartZoom(1.0); // Normal
      } else if (candleCount < 300) {
        setChartZoom(0.5); // Zoom out a bit
      } else {
        setChartZoom(0.25); // Lots of history, zoom out more
      }
    } else {
      // No candles yet - zoom in to make the single/few candles visible
      setChartZoom(2.0);
    }
    
    // Set game state based on whether we have a position
    if (existingPosition) {
      setGameState('playing');
      positionRef.current = { 
        size: existingPosition.size || existingPosition.tokens || 0, 
        invested: existingPosition.invested, 
        entry: existingPosition.entry 
      };
    } else {
      setGameState('spectating');
      positionRef.current = { size: 0, invested: 0, entry: 1.0 };
    }
    
    priceHistoryRef.current = [serverToken.currentPrice];
    startTimeRef.current = Date.now();
    
    // ============== USE REAL CANDLES FROM SERVER ==============
    if (serverToken.candles && serverToken.candles.length > 0) {
      const formattedCandles = serverToken.candles.map((c, i) => ({
        id: `candle_${i}`,
        time: c.time, // 📊 Preserve server timestamp
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        buyVolume: c.buyVolume || 0,
        sellVolume: c.sellVolume || 0,
      }));
      
      // Note: currentCandle is now included in server aggregation, no need to add separately
      
      setCandles(formattedCandles);
      console.log(`📈 Loaded ${formattedCandles.length} real candles from server`);
    } else {
      // Fallback: start with empty candles (will populate from polling)
      setCandles([{
        id: 'initial',
        open: serverToken.currentPrice,
        high: serverToken.currentPrice,
        low: serverToken.currentPrice,
        close: serverToken.currentPrice,
        buyVolume: 0,
        sellVolume: 0,
      }]);
    }
    
    // Seed price history
    const recentPrices = serverToken.candles?.slice(-10).map(c => c.close) || [];
    priceHistoryRef.current = [...recentPrices, serverToken.currentPrice];
    
    // Use REAL players from server positions
    if (serverToken.positions && serverToken.positions.length > 0) {
      const playerColors = ['#ff6b6b', '#4ecdc4', '#ffe66d', '#a29bfe', '#fd79a8', '#00b894', '#e17055', '#74b9ff'];
      
      // Deduplicate by handle - keep the one with most tokens
      const uniquePlayers = {};
      serverToken.positions.forEach(p => {
        const handle = p.handle?.replace(/^\$/, '') || 'unknown';
        if (!uniquePlayers[handle] || (p.tokens || 0) > (uniquePlayers[handle].tokens || 0)) {
          uniquePlayers[handle] = p;
        }
      });
      
      const players = Object.values(uniquePlayers).map((p, i) => ({
        handle: p.handle,
        entry: p.entryPrice,
        tokens: p.tokens,
        invested: p.invested, // 👑 For creator view
        isActive: (p.tokens || 0) > 0,
        color: playerColors[i % playerColors.length],
        isBot: p.isBot,
      }));
      setTokenPlayers(players);
      
      // Generate chat from real participants
      const chatMessages = players.slice(0, 3).map((p, i) => ({
        id: i + 1,
        handle: p.handle,
        text: serverToken.currentPrice > 1 
          ? ['lets go 🚀', 'looking good', 'bullish'][i % 3]
          : ['careful...', 'watching closely', 'hmm...'][i % 3],
      }));
      setTokenChat(chatMessages);
    } else {
      setTokenPlayers([]);
      setTokenChat([]);
    }
    
    setViewerCount(serverToken.playerCount + serverToken.botCount);
    
    // 📡 Broadcast that we're watching this token
    console.log(`📡 Broadcasting join_token: ${token.id}`);
    broadcastEvent('join_token', { tokenId: token.id });
    
    } catch (e) {
      console.error('❌ Error joining token:', e.message);
    } finally {
      setJoiningToken(false);
    }
  };

  const leaveToken = () => {
    // 📡 Broadcast that we left the token
    if (currentToken?.id) {
      broadcastEvent('leave_token', { tokenId: currentToken.id });
    }
    
    // Let players browse other tokens while holding a position
    // Their position stays active - they can come back to it
    // This mimics real trading where you can look at other charts
    setView('lobby');
    // Don't reset currentToken or gameState - keep position alive!
    // Player can return to their token from the lobby
  };

  // ============== CLICKABLE TICKERS IN CHAT ==============
  // Makes $TICKER mentions clickable to jump to that token
  const renderClickableTickers = (text, textColor = '#888') => {
    const tickerRegex = /(\$[A-Z0-9_]+)/g;
    const parts = text.split(tickerRegex);
    
    return parts.map((part, idx) => {
      if (part.match(tickerRegex)) {
        // Find if this ticker exists in active tokens
        const matchingToken = activeTokens.find(t => t.ticker === part);
        if (matchingToken) {
          const isLocked = !isPremium && matchingToken.isPremiumOnly;
          return (
            <span
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                if (!isLocked) joinToken(matchingToken);
              }}
              style={{
                color: '#f0b90b',
                cursor: isLocked ? 'not-allowed' : 'pointer',
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                padding: '0 2px',
                borderRadius: '2px',
                backgroundColor: 'rgba(240,185,11,0.1)',
              }}
              title={isLocked ? '🔒 Premium required' : `Join ${part} (${matchingToken.priceChange >= 0 ? '+' : ''}${matchingToken.priceChange.toFixed(1)}%)`}
            >
              {part}
              {isLocked && ' 🔒'}
            </span>
          );
        }
        // Ticker not found in active tokens - just highlight it
        return <span key={idx} style={{ color: '#666' }}>{part}</span>;
      }
      return <span key={idx} style={{ color: textColor }}>{part}</span>;
    });
  };

  // ============== TRADING ==============
  const apeIn = () => {
    if (gameWallet < stake) {
      setShowDeposit(true);
      return;
    }
    
    // Show daily rake warning for AI or PvP tokens
    const isPvP = currentToken?.isUserLaunched;
    if (isPvP && !hasSeenPvPRakeToday) {
      setPendingAction({ type: 'apeIn', stake });
      setRakeWarningType('pvp');
      setShowRakeDisclosure(true);
      return;
    }
    if (!isPvP && !hasSeenAIRakeToday) {
      setPendingAction({ type: 'apeIn', stake });
      setRakeWarningType('ai');
      setShowRakeDisclosure(true);
      return;
    }
    
    executeApeIn(stake);
  };
  
  const executeApeIn = async (stakeAmount) => {
    console.log('🟢 executeApeIn called with:', stakeAmount);
    console.log('🟢 rakeConsent:', rakeConsent, 'tutorialComplete:', tutorialComplete, 'isAdmin:', isAdmin);
    
    // 🛡️ SAFETY: Check token exists before trading
    if (!currentToken || !currentToken.id) {
      console.error('❌ Cannot ape in: No active token');
      alert('Token not available. Please select a token first.');
      return;
    }
    
    // 📱 MOBILE-FRIENDLY ONBOARDING: Gate trading until consent & tutorial done
    // 👑 Admins skip onboarding gates
    if (!isAdmin) {
      if (!rakeConsent) {
        console.log('🚫 Blocked by rakeConsent - showing modal');
        setShowRakeConsentModal(true);
        return;
      }
      // Tutorial is optional if they already consented to fees
      // Show it but don't block trading
      if (!tutorialComplete && !showTutorialModal) {
        console.log('📚 Showing tutorial (non-blocking)');
        setShowTutorialModal(true);
      }
    }
    
    console.log('✅ Passed onboarding gates, executing buy...');
    
    try {
      // Optimistic UI update - deduct from wallet immediately
      setGameWallet(prev => prev - stakeAmount);
      showBalanceChange(-stakeAmount); // 💫 Animate the buy!
      
      // ============== SERVER BUY ==============
      const result = await serverBuy(currentToken.id, stakeAmount);
      
      if (!result || result.error) {
        // Refund on failure
        console.error('❌ Server buy failed:', result?.error);
        setGameWallet(prev => prev + stakeAmount);

        // 💀 SPECIAL HANDLING: Token rugged while buying
        const errorMsg = result?.error || 'Buy failed';
        if (errorMsg.toLowerCase().includes('no longer active') || errorMsg.toLowerCase().includes('not active')) {
          // Token rugged! Show rug UI and sound
          console.log('💀 TOKEN RUGGED during buy attempt!');
          Sounds.rug();
          setGameState('rugged');
          showToast('error', `💀 ${currentToken?.ticker || 'Token'} RUGGED! Your buy was refunded.`);
        } else {
          alert(errorMsg);
        }
        return;
      }
      
      console.log(`🟢 SERVER BUY SUCCESS: ${stakeAmount} sats → ${result.tokensReceived?.toFixed(0)} tokens @ ${result.newPrice?.toFixed(4)}`);
      
      // Update wallet with server's authoritative balance
      setGameWallet(result.newBalance);
      
      // 💼 MULTI-POSITION: Check if we already have a position in this token
      const existingPosition = positions.find(p => p.tokenId === currentToken.id && p.status === 'active');
      
      if (existingPosition) {
        // MERGE into existing position (weighted average entry)
        const newSize = existingPosition.size + result.tokensReceived;
        const newInvested = existingPosition.invested + stakeAmount;
        const weightedEntry = (existingPosition.invested * existingPosition.entry + stakeAmount * currentPrice) / newInvested;
        
        const updatedPositions = positions.map(p => 
          p.id === existingPosition.id 
            ? { ...p, size: newSize, invested: newInvested, entry: weightedEntry }
            : p
        );
        savePositions(updatedPositions);
        
        console.log(`💼 MERGED into existing position: ${existingPosition.tokenTicker}`);
        positionRef.current = { size: newSize, invested: newInvested, entry: weightedEntry };
        
      } else {
        // CREATE new position
        const newPosition = {
          id: 'pos_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
          tokenId: currentToken.id,
          tokenTicker: currentToken.ticker,
          tokenCategory: currentToken.category,
          size: result.tokensReceived,
          invested: stakeAmount,
          entry: currentPrice || 1.0,
          openedAt: Date.now(),
          status: 'active',
          isUserLaunched: currentToken.isUserLaunched || false,
        };
        
        savePositions([...positions, newPosition]);
        console.log(`💼 NEW position opened: ${currentToken.ticker}`);
        positionRef.current = { size: result.tokensReceived, invested: stakeAmount, entry: currentPrice || 1.0 };
      }
      
      // ⚠️ IMPORTANT: Set protection BEFORE changing gameState
      // Otherwise the useEffect re-runs and overwrites our candle!
      lastUserTradeRef.current = Date.now();
      
      setGameState('playing');
      startTimeRef.current = Date.now();
      
      // 🐸 PEPE for big apes!
      if (stakeAmount >= 50000) {
        setTimeout(() => showPepeToast('bigApe', 'right', 3000), 500);
      } else if (stakeAmount >= 10000 && Math.random() < 0.3) {
        setTimeout(() => showPepeToast('apeIn', 'right', 2500), 500);
      }
      
      // Update price from server response
      if (result.newPrice) {
        setCurrentPrice(result.newPrice);
        priceHistoryRef.current.push(result.newPrice);
        
        // 📈 IMMEDIATELY update candle to show the pump!
        setCandles(prev => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          const last = { ...updated[updated.length - 1] };
          last.close = result.newPrice;
          last.high = Math.max(last.high, result.newPrice);
          last.buyVolume = (last.buyVolume || 0) + stakeAmount;
          updated[updated.length - 1] = last;
          return updated;
        });
      }
      
      // Transaction feed
      setTransactions(prev => [{
        id: 'tx_' + Date.now(),
        type: 'Buy',
        amount: stakeAmount,
        price: result.newPrice || currentPrice,
        wallet: handcashUser?.handle || 'YOU',
        isYou: true
      }, ...prev.slice(0, 7)]);
      
      // Chat message - include slippage if significant (>1%)
      const buySlippageInfo = result.slippage?.percent > 1
        ? ` [📉 ${result.slippage.percent.toFixed(1)}% slippage: -${formatPB(result.slippage.loss)}]`
        : '';
      setTokenChat(prev => [...prev, {
        id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
        handle: '📊',
        text: `${handcashUser?.handle || 'You'} bought at ${(result.newPrice || currentPrice).toFixed(3)}x${buySlippageInfo} 🦍`,
        isAction: true
      }]);

      // 📉 SLIPPAGE in chat for buys - Show what they lost to slippage
      if (result.slippage?.percent > 0) {
        setTimeout(() => {
          setTokenChat(prev => [...prev, {
            id: 'buy_slippage_' + Date.now(),
            handle: '📉',
            text: `Entry slippage: ${result.slippage.percent.toFixed(1)}% fee (-${formatPB(result.slippage.loss)}). Smaller buys = less slippage!`,
            isAction: true,
            isSlippage: true
          }]);
        }, 500);
      }
      
    } catch (error) {
      console.error('❌ Error in executeApeIn:', error);
      setGameWallet(prev => prev + stakeAmount);
      alert('Something went wrong with your trade. Your sats have been refunded. Please try again.');
    }
  };

  const sell = async (percent = 100) => {
    if (gameState !== 'playing') return;
    if (position.size <= 0) return;
    
    // 📱 MOBILE-FRIENDLY ONBOARDING: Gate trading until consent & tutorial done
    // 👑 Admins skip onboarding gates
    if (!isAdmin) {
      if (!rakeConsent) {
        setShowRakeConsentModal(true);
        return;
      }
      // Tutorial is optional - show but don't block
      if (!tutorialComplete && !showTutorialModal) {
        setShowTutorialModal(true);
      }
    }
    
    // 🛡️ SAFETY: Check token exists
    if (!currentToken || !currentToken.id) {
      console.error('❌ Cannot sell: No active token');
      alert('Token not available. Your position may have expired.');
      setGameState('spectating');
      positionRef.current = { size: 0, invested: 0, entry: 1.0 };
      return;
    }
    
    try {
      // ============== SERVER SELL ==============
      const result = await serverSell(currentToken.id, percent / 100);
      
      if (!result || result.error) {
        console.error('❌ Server sell failed:', result?.error);
        
        // Handle position sync issue - server doesn't have our position
        if (result?.error === 'No position to sell') {
          console.log('🔄 Position out of sync - clearing local state');
          positionRef.current = { size: 0, invested: 0, entry: 1.0 };
          setGameState('spectating');
          // Remove from local positions array
          const currentPos = positions.find(p => p.tokenId === currentToken?.id && p.status === 'active');
          if (currentPos) {
            const updatedPositions = positions.map(p => 
              p.id === currentPos.id ? { ...p, status: 'closed', closedAt: Date.now() } : p
            );
            savePositions(updatedPositions);
          }
          alert('Position was closed on server (likely from a rug or server restart). UI has been synced.');
          return;
        }
        
        alert(result?.error || 'Sell failed. Please try again.');
        return;
      }
      
      console.log(`🔴 SERVER SELL SUCCESS: ${result.satsReceived} sats, profit: ${result.profit}`);
      
      // 🛡️ Show slippage protection notification if triggered
      if (result.slippageProtection) {
        const { requested, actual, remaining, message } = result.slippageProtection;
        console.log(`🛡️ Slippage protection: ${message}`);
        
        // Show toast notification
        setTokenChat(prev => [...prev, {
          id: 'slippage_' + Date.now(),
          handle: '🛡️',
          text: `Slippage protection: Only sold ${Math.floor(actual).toLocaleString()} tokens (${Math.floor(remaining).toLocaleString()} remaining). Sell again to continue.`,
          isAction: true,
          isWarning: true
        }]);
      }
      
      const satsReceived = result.satsReceived || 0;
      const profit = result.profit || 0;
      const newPrice = result.newPrice || currentPrice;
      const multiplier = position.entry > 0 ? newPrice / position.entry : 1;
      
      // Update wallet with server's authoritative balance
      setGameWallet(result.newBalance);
      showBalanceChange(profit); // 💫 Animate profit/loss!
      
      // 🐸 PEPE CELEBRATES COOL STUFF!
      if (profit > 0) {
        if (stats.wins === 0) {
          setTimeout(() => showPepeToast('firstWin', 'right', 4000), 500);
        } else if (multiplier >= 3) {
          setTimeout(() => showPepeToast('bigMoon', 'right', 4000), 500);
        } else if (multiplier >= 2) {
          setTimeout(() => showPepeToast('doubleUp', 'right', 3500), 500);
        } else if (multiplier >= 1.5) {
          setTimeout(() => showPepeToast('solidGain', 'right', 3000), 500);
        } else if (profit >= 5000) {
          // Big profit even without huge multiplier
          setTimeout(() => showPepeToast('bigBag', 'right', 3500), 500);
        } else if (Math.random() < 0.3) {
          // 30% chance to celebrate any win
          setTimeout(() => showPepeToast('win', 'right', 3000), 500);
        }
      } else if (profit < 0 && multiplier > 0.5) {
        // Cut losses early - that's smart!
        if (Math.random() < 0.4) {
          setTimeout(() => showPepeToast('smartExit', 'left', 3000), 500);
        }
      }
      
      // Update price from server
      setCurrentPrice(newPrice);
      priceHistoryRef.current.push(newPrice);
      
      // Mark trade time to prevent candle overwrites for a moment
      lastUserTradeRef.current = Date.now();
      
      // Transaction feed
      setTransactions(prev => [{
        id: 'tx_' + Date.now(),
        type: 'Sell',
        amount: satsReceived,
        price: newPrice,
        wallet: handcashUser?.handle || 'YOU',
        isYou: true
      }, ...prev.slice(0, 7)]);
      
      // Chat message - include slippage if significant (>1%)
      const slippageInfo = result.slippage?.percent > 1
        ? ` [📉 ${result.slippage.percent.toFixed(1)}% slippage: -${formatPB(result.slippage.loss)}]`
        : '';
      setTokenChat(prev => [...prev, {
        id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
        handle: '📊',
        text: `${handcashUser?.handle || 'You'} sold ${percent}% at ${newPrice.toFixed(3)}x (${profit >= 0 ? '+' : ''}${formatPB(profit)})${slippageInfo} ${profit >= 0 ? '💰' : '📉'}`,
        isAction: true
      }]);

      // 📉 SLIPPAGE in chat - Show players what they lost to price impact
      if (result.slippage?.percent > 0) {
        setTimeout(() => {
          setTokenChat(prev => [...prev, {
            id: 'slippage_info_' + Date.now(),
            handle: '📉',
            text: `Price impact: You would have received ${formatPB(result.slippage.idealReceived)} at spot price, but got ${formatPB(result.slippage.actualReceived)} (-${result.slippage.percent.toFixed(1)}%). Smaller sells = less slippage!`,
            isAction: true,
            isSlippage: true
          }]);
        }, 500);
      }
      
      // Update or close position
      if (percent >= 100 || !result.remainingPosition) {
        // Full sell - close position
        const currentPos = positions.find(p => p.tokenId === currentToken?.id && p.status === 'active');
        if (currentPos) {
          const updatedPositions = positions.map(p => 
            p.id === currentPos.id 
              ? { ...p, status: 'closed', closedAt: Date.now(), closedPrice: newPrice, profit: profit }
              : p
          );
          savePositions(updatedPositions);
          
          saveDailyStats({
            ...dailyStats,
            trades: [...dailyStats.trades, {
              id: 'trade_' + Date.now(),
              tokenTicker: currentToken?.ticker,
              type: 'sell',
              amount: satsReceived,
              profit: profit,
              multiplier: multiplier,
              timestamp: Date.now(),
            }]
          });
        }
        
        positionRef.current = { size: 0, invested: 0, entry: 1.0 };
        setGameState('won');
        
        // Update stats
        setStats(prev => {
          let streakProtected = false;
          if (profit <= 0 && prev.winStreak > 0 && canUseStreakProtection()) {
            streakProtected = true;
            useStreakProtection();
          }
          
          const newStats = {
            ...prev,
            totalTrades: prev.totalTrades + 1,
            wins: profit > 0 ? prev.wins + 1 : prev.wins,
            losses: profit <= 0 ? prev.losses + 1 : prev.losses,
            winStreak: profit > 0 ? prev.winStreak + 1 : (streakProtected ? prev.winStreak : 0),
            lossStreak: profit <= 0 ? (streakProtected ? 0 : prev.lossStreak + 1) : 0,
            bestWinStreak: profit > 0 ? Math.max(prev.bestWinStreak, prev.winStreak + 1) : prev.bestWinStreak,
            worstLossStreak: profit <= 0 && !streakProtected ? Math.max(prev.worstLossStreak, prev.lossStreak + 1) : prev.worstLossStreak,
            paperHandsSells: multiplier < 1.2 && profit > 0 ? prev.paperHandsSells + 1 : prev.paperHandsSells,
            biggestWin: Math.max(prev.biggestWin, profit),
            totalProfit: prev.totalProfit + profit,
            highestMultiplier: Math.max(prev.highestMultiplier, multiplier),
            tradesWithoutRug: prev.tradesWithoutRug + 1,
          };
          
          setTimeout(() => {
            if (profit > 0 && newStats.wins === 1) unlockAchievement('firstBlood');
            if (profit >= 10000) unlockAchievement('whale');
            if (multiplier >= 5) unlockAchievement('moonshotMaster');
            if (newStats.winStreak >= 5) unlockAchievement('onFire');
            
            if (newStats.winStreak === 5 || newStats.winStreak === 10) {
              setTimeout(() => showPepeToast('winStreak', 'right', 4000), 3000);
            }
          }, 100);
          
          return newStats;
        });
        
        // Celebrations
        if (profit > 0) {
          
          let winType = 'small';
          if (multiplier >= 5) winType = 'legendary';
          else if (multiplier >= 3) winType = 'huge';
          else if (multiplier >= 2) winType = 'big';
          else if (multiplier >= 1.5) winType = 'medium';
          else if (multiplier < 1.2) winType = 'paperHands';
          
          setShowWinCelebration({ profit, multiplier, type: winType });
          setTimeout(() => setShowWinCelebration(null), winType === 'legendary' ? 5000 : 3000);
        } else {
          setShowPaperHandsLoss({ 
            loss: Math.abs(profit), 
            multiplier, 
            tokenFate: 'UNKNOWN',
            tokenTicker: currentToken?.ticker || '$UNKNOWN'
          });
          setTimeout(() => setShowPaperHandsLoss(null), 4000);
        }
        
        // Lobby announcement
        announceTrade(
          handcashUser?.handle || 'Anonymous', 
          currentToken?.ticker || '$UNKNOWN', 
          profit, 
          multiplier, 
          'SELL'
        );
        
        lastSellRef.current = {
          tokenId: currentToken?.id,
          tokenTicker: currentToken?.ticker,
          profit: profit,
          timestamp: Date.now()
        };
        
      } else {
        // Partial sell - update position from server
        const currentPos = positions.find(p => p.tokenId === currentToken?.id && p.status === 'active');
        if (currentPos && result.remainingPosition) {
          const updatedPositions = positions.map(p => 
            p.id === currentPos.id 
              ? { ...p, size: result.remainingPosition.tokens, invested: result.remainingPosition.invested }
              : p
          );
          savePositions(updatedPositions);
          positionRef.current = { 
            size: result.remainingPosition.tokens, 
            invested: result.remainingPosition.invested, 
            entry: currentPos.entry 
          };
        }
      }
      
    } catch (error) {
      console.error('❌ Error in sell:', error);
      alert('Something went wrong with your sale. Please try again.');
    }
  };

  const addPosition = async (amount) => {
    if (gameState !== 'playing' || gameWallet < amount) return;
    
    const currentPos = positions.find(p => p.tokenId === currentToken?.id && p.status === 'active');
    if (!currentPos) return;
    
    Sounds.buy(); // 🔊 Play buy sound when adding to position!
    setHoverPreviewAmount(null); // Clear hover preview
    
    // Optimistic UI update
    setGameWallet(prev => prev - amount);
    showBalanceChange(-amount); // 💫 Animate adding to position!
    
    // ⚠️ Set protection BEFORE any state changes
    lastUserTradeRef.current = Date.now();
    
    // ============== SERVER BUY ==============
    const result = await serverBuy(currentToken.id, amount);
    
    if (!result || result.error) {
      // Refund on failure
      console.error('❌ Server buy failed:', result?.error);
      setGameWallet(prev => prev + amount);

      // 💀 SPECIAL HANDLING: Token rugged while buying
      const errorMsg = result?.error || 'Buy failed';
      if (errorMsg.toLowerCase().includes('no longer active') || errorMsg.toLowerCase().includes('not active')) {
        console.log('💀 TOKEN RUGGED during add-to-position!');
        Sounds.rug();
        setGameState('rugged');
        showToast('error', `💀 ${currentToken?.ticker || 'Token'} RUGGED! Your buy was refunded.`);
      } else {
        alert(errorMsg);
      }
      return;
    }
    
    // Update wallet with server's authoritative balance
    setGameWallet(result.newBalance);
    
    // Update price from server response (correct AMM price!)
    if (result.newPrice) {
      setCurrentPrice(result.newPrice);
      priceHistoryRef.current.push(result.newPrice);
      
      // Update candle with SERVER price
      setCandles(prev => {
        if (prev.length === 0) return prev;
        const updated = [...prev];
        const last = { ...updated[updated.length - 1] };
        last.close = result.newPrice;
        last.high = Math.max(last.high, result.newPrice);
        last.buyVolume = (last.buyVolume || 0) + amount;
        updated[updated.length - 1] = last;
        return updated;
      });
    }
    
    // Update position with server data
    const newTokens = result.tokensReceived;
    const newInvested = currentPos.invested + amount;
    const newSize = currentPos.size + newTokens;
    
    // Calculate weighted average entry price
    const weightedEntry = (currentPos.invested * currentPos.entry + amount * (result.newPrice || currentPrice)) / newInvested;
    
    // Update position in array
    const updatedPositions = positions.map(p => 
      p.id === currentPos.id 
        ? { ...p, size: newSize, invested: newInvested, entry: weightedEntry }
        : p
    );
    savePositions(updatedPositions);
    
    // Update positionRef for game loop
    positionRef.current = { size: newSize, invested: newInvested, entry: weightedEntry };
    
    setTokenChat(prev => [...prev, {
      id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      handle: '📊',
      text: `${handcashUser.handle} added +${formatPB(amount)} 🦍`,
      isAction: true
    }]);
  };

  // ============== GAME LOOP (Manipulation Engine) ==============
  // NOTE: When connected to server (API_URL set), price simulation is handled server-side
  // This loop only handles local UI updates and animations when server is unavailable
  useEffect(() => {
    if (view !== 'token' || !currentToken) return;
    
    // Skip client-side simulation when server handles it
    // Server polling in useEffect above handles price/candle updates
    const serverMode = !!API_URL;
    if (serverMode) {
      console.log('🌐 Server mode: Price simulation handled by server');
      return; // Server polling handles everything
    }
    
    const m = manipulationRef.current;
    m.startTime = m.startTime || Date.now();
    
    // 🧊 Initialize $wintermute for this token
    // The silent market maker that never speaks
    const wm = wintermuteRef.current;
    wm.phase = 'ACCUMULATE';
    wm.positionSize = 0;
    wm.avgEntry = 1.0;
    wm.lastTradeTime = Date.now();
    wm.tradeCount = 0;
    wm.isActive = true; // Wintermute is always watching
    
    // Track slow rug bleed state
    let slowBleedStarted = false;
    let lastTradeTime = Date.now();
    let pendingBuyVolume = 0;
    let pendingSellVolume = 0;
    
    // Rug execution function (defined before setInterval to avoid hoisting issues)
    const executeRug = (type, lostAmount) => {
      clearInterval(gameLoopRef.current);
      
      const wasPlaying = lostAmount > 0;
      
      // 💫 Animate the loss on game wallet!
      if (wasPlaying && lostAmount > 0) {
        showBalanceChange(-lostAmount);
      }
      
      // Update stats (only if was playing)
      if (wasPlaying) {
        setStats(prev => {
          const newRugsByType = { ...prev.rugsByType };
          newRugsByType[type] = {
            times: (newRugsByType[type]?.times || 0) + 1,
            totalLost: (newRugsByType[type]?.totalLost || 0) + lostAmount,
          };
          
          if (type === 'TRAP') {
            newRugsByType[type].fellForPump = (newRugsByType[type].fellForPump || 0) + 1;
          }
          
          const totalRugs = prev.rugs + 1;
          const totalTrades = prev.totalTrades + 1;
          const survivalRate = totalTrades > 0 ? ((totalTrades - totalRugs) / totalTrades) * 100 : 100;
          
          // 🛡️ STREAK PROTECTION: Premium users can save their win streak once per day
          let streakProtected = false;
          if (prev.winStreak > 0 && canUseStreakProtection()) {
            streakProtected = true;
            useStreakProtection();
          }
          
          const newStats = {
            ...prev,
            totalTrades,
            rugs: totalRugs,
            losses: prev.losses + 1,
            lossStreak: streakProtected ? 0 : prev.lossStreak + 1,
            winStreak: streakProtected ? prev.winStreak : 0,
            worstLossStreak: streakProtected ? prev.worstLossStreak : Math.max(prev.worstLossStreak, prev.lossStreak + 1),
            biggestLoss: Math.max(prev.biggestLoss, lostAmount),
            totalProfit: prev.totalProfit - lostAmount,
            tradesWithoutRug: 0,
            rugsByType: newRugsByType,
            survivalRate,
          };
          
          setTimeout(() => {
            if (newStats.rugs >= 10) unlockAchievement('clownCollege');
            if (newStats.rugs >= 50) unlockAchievement('rugLord');
            if (newStats.lossStreak >= 5) unlockAchievement('iceCold');
            if (newStats.totalTrades >= 100) unlockAchievement('centurion');
            
            // 🐸 PEPE CONSOLATION FOR GETTING RUGGED
            if (newStats.rugs === 5) {
              setTimeout(() => showPepeToast('gotRugged', 'left', 4000), 2000);
            }
          }, 100);
          
          return newStats;
        });
      }
      
      // Rug chat message
      const rugMessages = {
        'HARD': '💀 LIQUIDITY PULLED! RUG!',
        'TRAP': '💀 MASSIVE SELL WALL HIT - DUMP!',
        'SLOW': '💀 Liquidity completely drained... it\'s over',
        'HONEYPOT': '💀 HONEYPOT CONFIRMED - SELLS BLOCKED!',
        'WINTERMUTE': '🧊💀 $wintermute DUMPED EVERYTHING - CLASSIC EXIT SCAM'
      };
      
      setTokenChat(prev => [...prev.slice(-30), {
        id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5), handle: '💀', text: rugMessages[type] || '💀 RUGGED!', isRug: true
      }]);
      
      // Animate the crash based on type
      const crashDuration = type === 'HARD' ? 500 : type === 'TRAP' ? 2000 : type === 'SLOW' ? 3000 : type === 'WINTERMUTE' ? 1000 : 1500;
      const startPrice = priceHistoryRef.current[priceHistoryRef.current.length - 1] || 1;
      let crashStep = 0;
      const crashSteps = crashDuration / 50;
      
      const crashInterval = setInterval(() => {
        crashStep++;
        const progress = crashStep / crashSteps;
        
        let newPrice;
        if (type === 'HARD') {
          newPrice = startPrice * Math.pow(0.001, progress);
        } else if (type === 'TRAP') {
          const bounce = Math.sin(progress * 10) * 0.05 * (1 - progress);
          newPrice = startPrice * (1 - progress * 0.99 + bounce);
        } else if (type === 'SLOW') {
          newPrice = startPrice * (1 - progress * 0.95);
        } else if (type === 'HONEYPOT') {
          newPrice = startPrice * Math.pow(0.01, progress);
        }
        
        const crashPrice = Math.max(0.001, newPrice);
        setCurrentPrice(crashPrice);
        
        // Add crash candles to chart (red candles showing the dump)
        priceHistoryRef.current.push(crashPrice);
        if (crashStep % 3 === 0) { // Add a crash candle every 3 steps
          setCandles(prev => {
            const lastPrice = prev.length > 0 ? prev[prev.length - 1].close : startPrice;
            return [...prev.slice(-59), {
              id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5) + crashStep,
              open: lastPrice,
              close: crashPrice,
              high: lastPrice,
              low: crashPrice,
              buyVolume: 0,
              sellVolume: Math.floor(Math.random() * 2000) + 500 // Heavy sell volume
            }];
          });
        }
        
        if (crashStep >= crashSteps) {
          clearInterval(crashInterval);
          setCurrentPrice(0);
          
          // Add final zero candle
          setCandles(prev => [...prev.slice(-59), {
            id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5) + 9999,
            open: crashPrice,
            close: 0.001,
            high: crashPrice,
            low: 0.001,
            buyVolume: 0,
            sellVolume: 5000
          }]);
          
          setGameState('rugged');
          
          // Count how many players got rugged (active players with positions)
          const ruggedCount = tokenPlayers.filter(p => p.isActive).length + (wasPlaying ? 1 : 0);
          setRuggedPlayersCount(ruggedCount);
          
          // Mark token as rugged in lobby
          if (currentToken) {
            setActiveTokens(prev => prev.map(t => 
              t.id === currentToken.id 
                ? { ...t, state: 'rugged', price: 0, priceChange: -100 }
                : t
            ));
            
            // 🔮 RESOLVE RUG PROPHET BETS - Token rugged, 'rug' predictions win!
            resolveRugProphetBets(currentToken.id, currentToken.ticker, 'rug');
          }
          
          // 💀 RUG ANNOUNCEMENT IN LOBBY - Tell everyone this token died!
          const rugMessages = [
            `💀 ${currentToken?.ticker || '$UNKNOWN'} just RUGGED! ${ruggedCount} player${ruggedCount !== 1 ? 's' : ''} got rekt`,
            `☠️ RIP ${currentToken?.ticker || '$UNKNOWN'} - ${type} claimed ${ruggedCount} victim${ruggedCount !== 1 ? 's' : ''}`,
            `🪦 ${currentToken?.ticker || '$UNKNOWN'} pulled the rug! ${type} rug type`,
            `💸 ${currentToken?.ticker || '$UNKNOWN'} went to ZERO! Another one bites the dust`,
          ];
          const rugMsg = rugMessages[Math.floor(Math.random() * rugMessages.length)];
          
          setLobbyChat(prev => [...prev.slice(-25), {
            id: 'rug_' + Date.now(),
            handle: '💀',
            text: rugMsg,
            isAction: true,
            isTradeAnnouncement: true,
            isRug: true,
          }]);
          
          // 🏃 CHECK FOR "ESCAPED RUG" - Did someone sell this token recently?
          const lastSell = lastSellRef.current;
          if (lastSell && lastSell.tokenId === currentToken?.id) {
            const timeSinceSell = Date.now() - lastSell.timestamp;
            if (timeSinceSell < 60000) { // Sold within last 60 seconds
              const escapeMessages = [
                `🏃 ${handcashUser?.handle || 'Someone'} ESCAPED ${currentToken?.ticker} just before the rug! 💨`,
                `🧠 Big brain! ${handcashUser?.handle || 'Someone'} sold ${currentToken?.ticker} ${Math.floor(timeSinceSell/1000)}s before it rugged!`,
                `🎯 ${handcashUser?.handle || 'Someone'} saw the rug coming on ${currentToken?.ticker}! Smart exit!`,
                `😎 ${handcashUser?.handle || 'Someone'} dodged the ${currentToken?.ticker} rug like a pro!`,
              ];
              const escapeMsg = escapeMessages[Math.floor(Math.random() * escapeMessages.length)];
              
              setLobbyChat(prev => [...prev.slice(-25), {
                id: 'escape_' + Date.now(),
                handle: '🏃',
                text: escapeMsg,
                isAction: true,
                isTradeAnnouncement: true,
                isWin: true,
              }]);
              
              // 🐸 SMUG PEPE CELEBRATION FOR ESCAPED RUG!
              setShowEscapePepe({
                tokenTicker: currentToken?.ticker || '$UNKNOWN',
                timeBefore: Math.floor(timeSinceSell / 1000),
                profit: lastSell.profit || 0
              });
              setTimeout(() => setShowEscapePepe(null), 4000);
              
              // 🐸 Also show pepe toast (works in token room too!)
              setTimeout(() => showPepeToast('escaped', 'right', 4000), 500);
              
              // 🎯 UPDATE DAILY CHALLENGE - Escape!
              
              // 🏆 UNLOCK ESCAPE ARTIST ACHIEVEMENT!
              setTimeout(() => unlockAchievement('rugSurvivor'), 1000);
              
              // Clear the last sell ref so we don't double-announce
              lastSellRef.current = null;
            }
          }
          
          // If player was holding, show trash modal instead of auto-closing
          if (wasPlaying && positionRef.current.size > 0) {
            // 💼 MULTI-POSITION: Mark this position as rugged
            const ruggedPosition = positions.find(p => p.tokenId === currentToken?.id && p.status === 'active');
            if (ruggedPosition) {
              const updatedPositions = positions.map(p => 
                p.id === ruggedPosition.id 
                  ? { ...p, status: 'rugged', closedAt: Date.now(), profit: -lostAmount }
                  : p
              );
              savePositions(updatedPositions);
              
              // 📊 Track in daily stats
              saveDailyStats({
                ...dailyStats,
                trades: [...dailyStats.trades, {
                  id: 'trade_' + Date.now(),
                  tokenTicker: currentToken?.ticker,
                  type: 'rugged',
                  amount: lostAmount,
                  profit: -lostAmount,
                  multiplier: 0,
                  timestamp: Date.now(),
                }]
              });
            }
            
            // Reset positionRef
            positionRef.current = { size: 0, invested: 0, entry: 1.0 };
            
            setPendingRuggedPosition({
              ticker: currentToken?.ticker || '$RUGGED',
              category: currentToken?.category || 'Rug Pull',
              tokensOwned: ruggedPosition?.size || positionRef.current.size,
              invested: lostAmount,
              rugType: type
            });
            setShowTrashModal(true);
            
            // 📢 BROADCAST: Tell server about the rug
            broadcastEvent('rugged', { 
              token: currentToken?.ticker, 
              loss: lostAmount,
              rugType: type
            });
            
            // 😈 SAVAGE RUG ANNOUNCEMENT - Maximum humiliation!
            announceTrade(
              handcashUser?.handle || 'Anonymous',
              currentToken?.ticker || '$RUGGED',
              -lostAmount, // Negative profit (loss)
              0, // 0x multiplier (total loss)
              type === 'HONEYPOT' ? 'HONEYPOT' : 'RUGGED'
            );
          }
          
          if (wasPlaying) {
            setShowLossHumiliation({ loss: lostAmount, rugType: type });
            setTimeout(() => setShowLossHumiliation(null), 5000);
          }
        }
      }, 50);
    };
    
    // Main game loop - 500ms ticks for realistic movement
    gameLoopRef.current = setInterval(() => {
      // 🛡️ BULLETPROOF PVP: Skip ALL artificial price movements for user-launched tokens!
      // User tokens ONLY move when real players buy/sell - no fake manipulation
      // Use ref to get latest token (avoid stale closure)
      const token = currentTokenRef.current;
      if (token?.isUserLaunched) {
        return; // EXIT EARLY - no game loop manipulation for PvP tokens!
      }
      
      const m = manipulationRef.current;
      const elapsed = Date.now() - m.startTime;
      const progress = elapsed / m.rugTime;
      
      // ==================== PHASE MANAGEMENT ====================
      if (m.fate === 'RUG') {
        if (progress < 0.25) m.phase = 'ACCUMULATION';
        else if (progress < 0.65) m.phase = 'MARKUP';
        else if (progress < 0.90) m.phase = 'DISTRIBUTION';
        else {
          // Time to rug!
          executeRug(m.rugType, positionRef.current.invested);
          return;
        }
      } else {
        // MOON or SUPERMOON
        if (progress < 0.3) m.phase = 'ACCUMULATION';
        else if (progress < 0.7) m.phase = 'MARKUP';
        else if (progress < 1.0) m.phase = 'MOON';
        else {
          // Token completed successfully
          clearInterval(gameLoopRef.current);
          setTokenChat(prev => [...prev.slice(-30), {
            id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5), 
            handle: '🌙', 
            text: `${currentToken.ticker} completed its ${m.fate} run! ${m.fate === 'SUPERMOON' ? '🚀🚀🚀' : '🎉'}`,
            isAction: true
          }]);
          
          // Mark token as mooned in lobby
          if (currentToken) {
            setActiveTokens(prev => prev.map(t => 
              t.id === currentToken.id 
                ? { ...t, state: 'mooned' }
                : t
            ));
            
            // 🔮 RESOLVE RUG PROPHET BETS - Token mooned, 'moon' predictions win!
            resolveRugProphetBets(currentToken.id, currentToken.ticker, 'moon');
          }
          return;
        }
      }
      
      // ==================== SIMULATED AMM TRADES ====================
      // Market makers execute REAL AMM trades that affect tokenomics
      
      // Determine trade direction based on phase
      let buyChance = 0.5;
      let tradeSize = 100 + Math.floor(Math.random() * 200); // 100-300 sats base
      
      // 🧊 WINTERMUTE has its own 4-phase manipulation pattern
      if (m.rugType === 'WINTERMUTE') {
        // Wintermute phases: ACCUMULATE (0-20%) → PUMP (20-50%) → DISTRIBUTE (50-80%) → EXIT (80-100%)
        if (progress < 0.20) {
          // ACCUMULATE - Quiet buying, small trades, building position
          buyChance = 0.75;
          tradeSize = 800 + Math.floor(Math.random() * 1200); // 800-2000 sats - noticeable accumulation
        } else if (progress < 0.50) {
          // PUMP - Aggressive buying, big green candles
          buyChance = 0.90;
          tradeSize = 2000 + Math.floor(Math.random() * 3000); // 2K-5K sats - heavy pump
        } else if (progress < 0.80) {
          // DISTRIBUTE - Slowly selling into strength, mixed signals
          buyChance = 0.35;
          tradeSize = 1000 + Math.floor(Math.random() * 2000); // 1K-3K - selling but not obvious
        } else {
          // EXIT - Dumping everything, red candles
          buyChance = 0.10;
          tradeSize = 3000 + Math.floor(Math.random() * 5000); // 3K-8K sats - massive dumps
        }
      } else {
        // Normal rug types
        switch (m.phase) {
          case 'ACCUMULATION':
            buyChance = 0.55; // Slight buy pressure
            tradeSize = 50 + Math.floor(Math.random() * 150); // Smaller trades
            break;
          case 'MARKUP':
            buyChance = 0.7; // Strong buy pressure
            tradeSize = 150 + Math.floor(Math.random() * 350); // Bigger trades
            if (m.fate === 'RUG' && m.rugType === 'TRAP') {
              buyChance = 0.85; // Pump hard before trap
              tradeSize = 200 + Math.floor(Math.random() * 500);
            }
            break;
          case 'DISTRIBUTION':
            buyChance = 0.35; // Sell pressure
            tradeSize = 100 + Math.floor(Math.random() * 300);
            if (m.rugType === 'SLOW') {
              if (!slowBleedStarted) {
                slowBleedStarted = true;
                setTokenChat(prev => [...prev.slice(-30), {
                  id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5), handle: '📊', text: 'Some profit taking happening...', isAction: true
                }]);
              }
              buyChance = 0.3;
            }
            break;
          case 'MOON':
            buyChance = 0.65;
            tradeSize = 200 + Math.floor(Math.random() * 400);
            if (m.fate === 'SUPERMOON') {
              buyChance = 0.75;
              tradeSize = 300 + Math.floor(Math.random() * 700);
            }
            break;
        }
      }
      
      // Execute simulated AMM trade
      // Use ref for fresh token state (avoids stale closure)
      const activeToken = activeTokensRef.current.find(t => t.id === currentTokenRef.current?.id);
      if (!activeToken) return;
      
      const liquidityPool = activeToken.liquidityPool || 10000;
      const tokenReserve = activeToken.tokenReserve || 1000000;
      const k = liquidityPool * tokenReserve;
      
      let newLiquidityPool, newTokenReserve;
      
      if (Math.random() < buyChance) {
        // SIMULATED BUY
        newLiquidityPool = liquidityPool + tradeSize;
        newTokenReserve = k / newLiquidityPool;
        pendingBuyVolume += tradeSize;
      } else {
        // SIMULATED SELL
        const tokensToSell = (tradeSize / (liquidityPool / tokenReserve)) * 0.8;
        newTokenReserve = tokenReserve + tokensToSell;
        newLiquidityPool = k / newTokenReserve;
        pendingSellVolume += tradeSize;
      }
      
      // Calculate new prices
      const newTokenPrice = newLiquidityPool / newTokenReserve;
      const newDisplayPrice = parseFloat((newTokenPrice / (activeToken.initialPrice || 0.00001)).toFixed(4));
      const priceMultiplier = newTokenPrice / (activeToken.initialPrice || 0.00001);
      const newMarketCap = Math.floor((activeToken.marketCap || 100000) * priceMultiplier / (activeToken.price || 1));
      
      // Update display price state
      setCurrentPrice(newDisplayPrice);
      priceHistoryRef.current.push(newDisplayPrice);
      if (priceHistoryRef.current.length > 200) priceHistoryRef.current.shift();
      
      // Update currentToken for UI display (this triggers re-render of mcap/liq)
      setCurrentToken(prev => prev ? {
        ...prev,
        liquidityPool: newLiquidityPool,
        tokenReserve: newTokenReserve,
        currentPrice: newTokenPrice,
        marketCap: newMarketCap,
        price: newDisplayPrice
      } : prev);
      
      // Update activeTokens array
      setActiveTokens(prev => prev.map(t => 
        t.id === currentTokenRef.current?.id 
          ? {
              ...t,
              liquidityPool: newLiquidityPool,
              tokenReserve: newTokenReserve,
              currentPrice: newTokenPrice,
              marketCap: newMarketCap,
              price: newDisplayPrice,
              priceChange: ((newDisplayPrice - 1) * 100),
              tradeCount: (t.tradeCount || 0) + 1
            }
          : t
      ));
      
      // Store volume for candle
      volumeRef.current.push({ 
        buy: pendingBuyVolume > pendingSellVolume, 
        buyVol: pendingBuyVolume,
        sellVol: pendingSellVolume
      });
      if (volumeRef.current.length > 100) volumeRef.current.shift();
      
      // Reset pending volume (partially - some carries over for smoother movement)
      pendingBuyVolume *= 0.3;
      pendingSellVolume *= 0.3;
      
      // ==================== CHAT EVENTS ====================
      
      // TRAP rug shilling during MARKUP phase
      if (m.fate === 'RUG' && m.rugType === 'TRAP' && m.phase === 'MARKUP' && Math.random() < 0.04) {
        const shills = [
          '🚀 This is going parabolic!',
          '💎 Whale wallet just bought 50K!',
          '📈 Breaking out! Don\'t miss this!',
          '🔥 Influencer just tweeted about us!',
          '⚡ CEX listing incoming (trust me bro)',
          '🌙 Easy 10x from here',
        ];
        setTokenChat(prev => [...prev.slice(-30), {
          id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5), 
          handle: '$pump_' + Math.floor(Math.random() * 999), 
          text: shills[Math.floor(Math.random() * shills.length)],
          isShill: true
        }]);
      }
      
      // SLOW rug cope messages during DISTRIBUTION
      if (m.fate === 'RUG' && m.rugType === 'SLOW' && m.phase === 'DISTRIBUTION' && Math.random() < 0.03) {
        const cope = [
          '💎 Just whales taking profit, we\'re fine',
          '📉 Healthy correction tbh',
          '🙏 Team said big news coming',
          '💪 Shaking out weak hands',
          '🔜 This is the bottom for sure',
        ];
        setTokenChat(prev => [...prev.slice(-30), {
          id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
          handle: '$cope_' + Math.floor(Math.random() * 999),
          text: cope[Math.floor(Math.random() * cope.length)],
        }]);
      }
      
      // HONEYPOT suspicious "only up" chat
      if (m.fate === 'RUG' && m.rugType === 'HONEYPOT' && Math.random() < 0.02) {
        setTokenChat(prev => [...prev.slice(-30), {
          id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
          handle: '$buyer_' + Math.floor(Math.random() * 999),
          text: '🚀 Only up! No one is selling!',
        }]);
      }
      
      // 🧊 WINTERMUTE - Other bots warn about the silent market maker
      // Wintermute NEVER speaks - only trades. Other bots notice and warn.
      if (m.fate === 'RUG' && m.rugType === 'WINTERMUTE' && Math.random() < 0.04) {
        const wmProgress = elapsed / m.rugTime;
        const botHandles = ['$smart_money', '$whale_watcher', '$alpha_andy', '$degen_dave', '$rug_survivor'];
        const botHandle = botHandles[Math.floor(Math.random() * botHandles.length)];
        
        let wmWarning = null;
        
        if (wmProgress < 0.20) {
          // ACCUMULATE phase warnings
          const msgs = [
            '👀 someone\'s been quietly accumulating...',
            '🧊 $wintermute wallet spotted buying',
            'seeing some smart money entering positions',
            'big wallet accumulating, watch the flow',
            '👀 interesting wallet activity here',
          ];
          wmWarning = msgs[Math.floor(Math.random() * msgs.length)];
        } else if (wmProgress < 0.50) {
          // PUMP phase warnings
          const msgs = [
            '⚠️ watch out for $wintermute...',
            '🧊 $wintermute is in this one, be careful',
            'that wallet is pumping it hard',
            'careful, smart money is playing games here',
            '📈 this pump looks coordinated',
          ];
          wmWarning = msgs[Math.floor(Math.random() * msgs.length)];
        } else if (wmProgress < 0.80) {
          // DISTRIBUTE phase warnings - KEY for players to escape
          const msgs = [
            '🚨 $wintermute sold... thats my signal to exit',
            '⚠️ when $wintermute starts selling slowly, RUN',
            '📉 smart money is distributing, be careful',
            '👀 $wintermute taking profits, consider your exit',
            '🧊 that wallet is offloading, this is the top',
            '⚠️ seeing distribution pattern, get out while you can',
          ];
          wmWarning = msgs[Math.floor(Math.random() * msgs.length)];
        } else {
          // EXIT phase warnings
          const msgs = [
            '💀 $wintermute is DUMPING - get out NOW',
            '🚨🚨 smart money exiting fast, its over',
            '🏃 $wintermute just market sold everything, RUN',
            '⚠️ that wallet is gone, this is cooked',
            '💀 classic wintermute exit, rip holders',
          ];
          wmWarning = msgs[Math.floor(Math.random() * msgs.length)];
        }
        
        if (wmWarning) {
          setTokenChat(prev => [...prev.slice(-30), {
            id: 'wm_chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
            handle: botHandle,
            text: wmWarning,
            isWintermuteWarning: true
          }]);
        }
      }
      
      // 🔮 ALPHABOT - Premium insider hints based on REAL health data!
      // This is a KEY premium feature - uses actual token health signals
      if (isPremium && Math.random() < 0.04) { // 4% per tick = ~1 message every 25 ticks
        let alphaMessage = '';
        let urgency = 'low'; // low, medium, high, critical
        
        // 🧠 Use REAL health data from server (if available)
        const health = currentToken?.health;
        
        if (health) {
          // SMART ALPHABOT: Base hints on actual health signals!
          if (health.isDead || health.score < 20) {
            // Token is dying
            const randomCritical = [
              '🚨 CRITICAL: Health score collapsed. Exit immediately!',
              '💀 FLATLINE: Multiple death signals detected. GET OUT!',
              '🚨 RED ALERT: Token is in death spiral. Sell now!',
            ];
            alphaMessage = randomCritical[Math.floor(Math.random() * randomCritical.length)];
            urgency = 'critical';
          } else if (health.isCritical || health.score < 35) {
            // Critical health
            if (health.signals.includes('mass_exodus')) {
              alphaMessage = '⚠️ MASS EXODUS: Everyone is selling. Consider exiting.';
              urgency = 'high';
            } else if (health.signals.includes('death_spiral')) {
              alphaMessage = '📉 DEATH SPIRAL: Consecutive red candles. Bearish momentum.';
              urgency = 'high';
            } else if (health.signals.includes('liquidity_drained')) {
              alphaMessage = '💧 LIQUIDITY ALERT: Pool is draining fast!';
              urgency = 'high';
            } else if (health.signals.includes('no_volume')) {
              alphaMessage = '📊 VOLUME DEAD: No trading activity. Could dump any second.';
              urgency = 'high';
            } else if (health.signals.includes('abandoned')) {
              alphaMessage = '👻 ABANDONED: No watchers, no holders. Ghost town.';
              urgency = 'medium';
            } else {
              alphaMessage = `⚠️ WARNING: Health score at ${health.score}/100. Risky.`;
              urgency = 'medium';
            }
          } else if (health.score < 50) {
            // Unhealthy but not critical
            const signals = health.signals;
            if (signals.length > 0) {
              const signalMessages = {
                'volume_dying': '📉 Volume dropping. Interest fading.',
                'price_critical': '💸 Price action looking weak.',
                'old_and_dying': '⏰ Aging token losing momentum.',
              };
              const msg = signals.find(s => signalMessages[s]);
              alphaMessage = msg ? signalMessages[msg] : `👀 Health at ${health.score}/100. Stay cautious.`;
              urgency = 'medium';
            } else {
              alphaMessage = `👀 Scanning... Health ${health.score}/100. Monitor closely.`;
              urgency = 'low';
            }
          } else if (health.score > 75) {
            // Healthy token!
            const randomBullish = [
              '🌙 Strong accumulation detected. Whales are holding.',
              '📈 Healthy token! Volume and liquidity looking good.',
              '✅ All systems green. No red flags detected.',
              '💪 Health score excellent. Momentum is positive.',
              '🐂 Bullish signals. Smart money is staying in.',
            ];
            alphaMessage = randomBullish[Math.floor(Math.random() * randomBullish.length)];
            urgency = 'low';
          } else {
            // Medium health (50-75) - occasional updates
            if (Math.random() < 0.3) {
              alphaMessage = `📊 Health stable at ${health.score}/100. DYOR.`;
              urgency = 'low';
            }
          }
        } else {
          // Fallback: No health data available (legacy behavior)
          const progress = elapsed / m.rugTime;
          if (progress > 0.7) {
            alphaMessage = '⚠️ Token aging. Consider taking profits.';
            urgency = 'medium';
          } else if (Math.random() < 0.2) {
            alphaMessage = '👀 Analyzing on-chain data...';
            urgency = 'low';
          }
        }
        
        if (alphaMessage) {
          setTokenChat(prev => [...prev.slice(-30), {
            id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
            handle: '🔮 AlphaBot',
            text: alphaMessage,
            isAlpha: true,
            isPremiumOnly: true,
            urgency // For potential future styling
          }]);
        }
      }
      
      // Moon celebration messages
      if (m.fate !== 'RUG' && m.phase === 'MOON' && Math.random() < 0.02) {
        const moonMsgs = [
          '🌙 MOON MISSION ENGAGED!',
          '🚀 TO THE MOON!',
          '💎 Diamond hands winning!',
          '📈 Still climbing!',
        ];
        setTokenChat(prev => [...prev.slice(-30), {
          id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
          handle: '🌙',
          text: m.fate === 'SUPERMOON' ? '🚀🚀🚀 SUPERMOON! THIS IS LEGENDARY!' : moonMsgs[Math.floor(Math.random() * moonMsgs.length)],
          isAction: true
        }]);
      }
      
      // ==================== BUILD CANDLES (TRADE-DRIVEN) ====================
      tickCountRef.current++;
      
      // Skip candle updates from game loop if we recently traded
      // The buy/sell handlers already updated the candle - don't mess with it
      const timeSinceLastTrade = Date.now() - lastUserTradeRef.current;
      const recentlyTraded = timeSinceLastTrade < 2000; // 2 second window
      
      // Get current price from AMM (already set by the trade simulation above)
      const currentPriceVal = priceHistoryRef.current[priceHistoryRef.current.length - 1] || 1;
      
      // Small wick variation based on trade activity
      const wickVariation = 0.003; // 0.3% max wick
      const tempHigh = currentPriceVal * (1 + Math.random() * wickVariation);
      const tempLow = currentPriceVal * (1 - Math.random() * wickVariation);
      
      // Aggregate recent volume (only if NOT recently traded to avoid double-counting)
      const recentVol = recentlyTraded ? [] : volumeRef.current.slice(-5);
      const buyVol = recentVol.reduce((sum, v) => sum + (v.buyVol || 0), 0);
      const sellVol = recentVol.reduce((sum, v) => sum + (v.sellVol || 0), 0);
      
      // COMPLETELY SKIP candle updates during protection window
      // The buy/sell handlers already set the candle correctly
      if (!recentlyTraded) {
        setCandles(prev => {
          if (prev.length === 0) {
            // Create first candle
            return [{
              id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
              open: currentPriceVal * 0.998,
              close: currentPriceVal,
              high: tempHigh,
              low: tempLow,
              buyVolume: buyVol,
              sellVolume: sellVol
            }];
          }
          
          // Update the last candle with current AMM price
          const updated = [...prev];
          const last = { ...updated[updated.length - 1] };
          last.close = currentPriceVal;
          last.high = Math.max(last.high, currentPriceVal, tempHigh);
          last.low = Math.min(last.low, currentPriceVal, tempLow);
          last.buyVolume = (last.buyVolume || 0) + buyVol;
          last.sellVolume = (last.sellVolume || 0) + sellVol;
          updated[updated.length - 1] = last;
          return updated;
        });
      }
      
      // Create NEW candle every 10 ticks (5 seconds at 500ms tick)
      // But NOT during protection window - don't want to lose user's trade data
      if (tickCountRef.current >= 10 && !recentlyTraded) {
        tickCountRef.current = 0;
        
        setCandles(prev => {
          if (prev.length > 0) {
            const lastClose = prev[prev.length - 1].close;
            // New candle opens at previous close with small gap sometimes
            const gap = (Math.random() - 0.5) * 0.003;
            const newOpen = lastClose * (1 + gap);
            
            // Add new candle with initial spread
            const newCandle = {
              id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
              open: newOpen,
              close: newOpen,
              high: newOpen * (1 + Math.random() * 0.002),
              low: newOpen * (1 - Math.random() * 0.002),
              buyVolume: 0,
              sellVolume: 0
            };
            return [...prev.slice(-59), newCandle];
          }
          return prev;
        });
      }
      
      // ==================== TRANSACTION FEED ====================
      // Show simulated trades in the feed based on current phase
      if (Math.random() < 0.08) {
        const isBuy = Math.random() < buyChance;
        const amount = Math.floor(Math.random() * 800) + 50;
        const wallet = Math.random().toString(16).slice(2, 8).toUpperCase();
        
        setTransactions(prev => [{
          id: 'id_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5) + Math.random(),
          type: isBuy ? 'Buy' : 'Sell',
          amount,
          sats: amount, // For display consistency
          price: priceHistoryRef.current[priceHistoryRef.current.length - 1] || 1,
          wallet,
          time: 'now'
        }, ...prev.slice(0, 7)]);
      }
      
      // ==================== 🧊 $WINTERMUTE TRANSACTION FEED ====================
      // Only show wintermute wallet trades when it's a WINTERMUTE rug type
      // These are the visible trades players can learn to spot
      if (m.rugType === 'WINTERMUTE') {
        const wm = wintermuteRef.current;
        const currentPriceNow = priceHistoryRef.current[priceHistoryRef.current.length - 1] || 1;
        const timeSinceWmTrade = Date.now() - wm.lastTradeTime;
        
        // Update wintermute's phase based on game progress
        if (progress < 0.20) {
          wm.phase = 'ACCUMULATE';
        } else if (progress < 0.50) {
          wm.phase = 'PUMP';
        } else if (progress < 0.80) {
          wm.phase = 'DISTRIBUTE';
        } else {
          wm.phase = 'EXIT';
        }
        
        // Wintermute trades appear MORE frequently than normal trades
        // This is the key tell - players learn to watch for 0xWM wallets
        const wmTradeChance = wm.phase === 'ACCUMULATE' ? 0.12 :
                              wm.phase === 'PUMP' ? 0.15 :
                              wm.phase === 'DISTRIBUTE' ? 0.18 :
                              wm.phase === 'EXIT' ? 0.25 : 0;
        
        if (timeSinceWmTrade > 800 && Math.random() < wmTradeChance) {
          wm.lastTradeTime = Date.now();
          wm.tradeCount++;
          
          let wmIsBuy, wmAmount;
          
          switch (wm.phase) {
            case 'ACCUMULATE':
              // Quiet accumulation - medium buys
              wmIsBuy = true;
              wmAmount = 2000 + Math.floor(Math.random() * 3000); // 2K-5K sats
              wm.positionSize += wmAmount;
              break;
              
            case 'PUMP':
              // Aggressive buys to push price
              wmIsBuy = Math.random() < 0.85; // 85% buys
              wmAmount = wmIsBuy ? 4000 + Math.floor(Math.random() * 6000) : 1000 + Math.floor(Math.random() * 2000);
              if (wmIsBuy) wm.positionSize += wmAmount;
              else wm.positionSize = Math.max(0, wm.positionSize - wmAmount);
              break;
              
            case 'DISTRIBUTE':
              // Slowly selling - THE KEY TELL for smart players
              wmIsBuy = Math.random() < 0.20; // 80% sells
              wmAmount = wmIsBuy ? 1000 + Math.floor(Math.random() * 2000) : 3000 + Math.floor(Math.random() * 5000);
              if (wmIsBuy) wm.positionSize += wmAmount;
              else wm.positionSize = Math.max(0, wm.positionSize - wmAmount);
              break;
              
            case 'EXIT':
              // Dumping everything
              wmIsBuy = false;
              wmAmount = 8000 + Math.floor(Math.random() * 12000); // Big 8K-20K dumps
              wm.positionSize = Math.max(0, wm.positionSize - wmAmount);
              break;
              
            default:
              wmIsBuy = Math.random() < 0.5;
              wmAmount = 2000;
          }
          
          // Add wintermute's trade to the feed - distinctive 0xWM wallet pattern
          setTransactions(prev => [{
            id: 'wm_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
            type: wmIsBuy ? 'Buy' : 'Sell',
            amount: wmAmount,
            sats: wmAmount,
            price: currentPriceNow,
            wallet: '0xWM' + Math.random().toString(16).slice(2, 6).toUpperCase(), // Players learn to spot this!
            time: 'now',
            isWintermute: true
          }, ...prev.slice(0, 7)]);
        }
      }
      
    }, 500); // 500ms ticks
    
    return () => {
      clearInterval(gameLoopRef.current);
    };
  }, [view, currentToken, isPremium]);

  // ============== REFRESH LOBBY (Token lifecycle management) ==============
  // NOTE: When server is connected (API_URL), server handles token lifecycle
  // This loop only runs for offline/demo mode
  useEffect(() => {
    if (view !== 'lobby') return;
    
    // Skip client-side token management when server handles it
    if (API_URL) {
      console.log('🌐 Server mode: Token lifecycle handled by server polling');
      return;
    }
    
    const interval = setInterval(() => {
      const now = Date.now();
      
      setActiveTokens(prev => {
        // Update existing tokens
        let updated = prev.map(t => {
          // Calculate new price based on token's fate
          const age = now - t.birthTime;
          const progress = age / t.lifespan;
          
          // Price trend based on fate and phase
          let trend = 1.0;
          if (t.fate === 'RUG') {
            if (progress < 0.25) trend = 1.002; // Accumulation - slight up
            else if (progress < 0.65) trend = 1.004; // Markup - pump
            else trend = 0.996; // Distribution - dump
          } else {
            // MOON or SUPERMOON
            if (progress < 0.3) trend = 1.002;
            else if (progress < 0.7) trend = 1.005;
            else trend = 1.003;
          }
          
          // Add some noise
          const noise = (Math.random() - 0.5) * 0.006;
          const newPrice = t.price * (trend + noise);
          
          // Calculate priceChange from initial price (1.0 baseline)
          const initialPrice = 1.0;
          const priceChange = ((newPrice - initialPrice) / initialPrice) * 100;
          
          return {
            ...t,
            age: Math.floor(age / 1000),
            price: newPrice,
            priceChange: priceChange,
            playerCount: Math.max(1, t.playerCount + Math.floor(Math.random() * 3 - 1)),
          };
        });
        
        // Remove expired tokens
        const expired = updated.filter(t => now > t.expiresAt);
        updated = updated.filter(t => now <= t.expiresAt);
        
        // 🐸 CHECK FOR ESCAPED RUG - Did player sell any of these tokens recently?
        if (expired.length > 0 && lastSellRef.current) {
          const lastSell = lastSellRef.current;
          const escapedToken = expired.find(t => t.id === lastSell.tokenId);
          if (escapedToken) {
            const timeSinceSell = now - lastSell.timestamp;
            if (timeSinceSell < 60000) { // Sold within last 60 seconds
              // 🏃 ESCAPED THE RUG!
              setShowEscapePepe({
                tokenTicker: escapedToken.ticker,
                timeBefore: Math.floor(timeSinceSell / 1000),
                profit: lastSell.profit || 0
              });
              setTimeout(() => setShowEscapePepe(null), 4000);
              
              // 🐸 Also show pepe toast for good measure
              setTimeout(() => showPepeToast('escaped', 'right', 4000), 500);
              
              // Lobby chat announcement
              setLobbyChat(prevChat => [...prevChat.slice(-25), {
                id: 'escape_' + now,
                handle: '🏃',
                text: `${handcashUser?.handle || 'Someone'} ESCAPED ${escapedToken.ticker} ${Math.floor(timeSinceSell/1000)}s before it rugged! 🧠`,
                isAction: true,
                isWin: true,
              }]);
              
              // 🎯 UPDATE DAILY CHALLENGE - Escape!
              
              // 🏆 UNLOCK ESCAPE ARTIST ACHIEVEMENT!
              setTimeout(() => unlockAchievement('rugSurvivor'), 1000);
              
              // Clear ref so we don't double-announce
              lastSellRef.current = null;
            }
          }
        }
        
        // Spawn new tokens to replace expired ones (maintain 5-7 active)
        const minTokens = 5;
        const maxTokens = 7;
        const newlySpawnedTokens = [];
        while (updated.length < minTokens) {
          const newToken = generateSingleToken(now + updated.length);
          updated.push(newToken);
          newlySpawnedTokens.push(newToken);
        }
        
        // Announce any AlphaBot vetted tokens from replacement spawns
        newlySpawnedTokens.forEach((newToken, index) => {
          if (newToken.isAlphaVetted) {
            setTimeout(() => {
              setLobbyChat(prevChat => [...prevChat.slice(-20), {
                id: now + 0.3 + index,
                handle: '🔮 AlphaBot',
                text: `Just vetted ${newToken.ticker} - looking clean. Premium users check it out.`,
                isAlpha: true,
                isPremiumOnly: true
              }]);
            }, 1000 + index * 500);
          }
        });
        
        // Randomly spawn new token occasionally (if under max)
        if (updated.length < maxTokens && Math.random() < 0.1) {
          const newToken = generateSingleToken(now);
          updated.push(newToken);
          
          // Announce new token in lobby chat
          setLobbyChat(prevChat => [...prevChat.slice(-20), {
            id: now,
            handle: '🚀 NEW',
            text: `${newToken.ticker} just launched! ${newToken.category}`,
            isSystem: true
          }]);
          
          // 🔮 Special announcement for AlphaBot Vetted moonshots!
          if (newToken.isAlphaVetted) {
            setTimeout(() => {
              setLobbyChat(prevChat => [...prevChat.slice(-20), {
                id: now + 0.1,
                handle: '🔮 AlphaBot',
                text: `I've vetted ${newToken.ticker} - this one looks solid. 95% safe rating.`,
                isAlpha: true,
                isPremiumOnly: true
              }]);
            }, 500);
            // Double send for visibility
            setTimeout(() => {
              setLobbyChat(prevChat => [...prevChat.slice(-20), {
                id: now + 0.2,
                handle: '🔮 AlphaBot',
                text: `Premium users: ${newToken.ticker} passed my vetting. Low rug probability.`,
                isAlpha: true,
                isPremiumOnly: true
              }]);
            }, 2000);
          }
        }
        
        // Log expired tokens
        expired.forEach(t => {
          const outcome = t.fate === 'RUG' ? '💀 RUGGED' : '🌙 MOONED';
          setLobbyChat(prevChat => [...prevChat.slice(-20), {
            id: now + Math.random(),
            handle: outcome,
            text: `${t.ticker} ended - ${t.fate === 'RUG' ? t.rugType : t.fate}`,
            isSystem: true
          }]);
          
          // 💼 Check if player had a position in this token
          const playerPosition = positions.find(p => p.tokenId === t.id && p.status === 'active');
          if (playerPosition) {
            if (t.fate === 'RUG') {
              // Mark position as rugged
              const updatedPositions = positions.map(p => 
                p.id === playerPosition.id 
                  ? { ...p, status: 'rugged', closedAt: Date.now(), profit: -playerPosition.invested }
                  : p
              );
              savePositions(updatedPositions);
              
              // Add to bags
              addToBags(
                { ticker: t.ticker, category: t.category, rugType: t.rugType || 'EXPIRED' },
                playerPosition.size,
                playerPosition.invested
              );
              
              // Track in daily stats
              saveDailyStats({
                ...dailyStats,
                trades: [...dailyStats.trades, {
                  id: 'trade_' + Date.now(),
                  tokenTicker: t.ticker,
                  type: 'rugged',
                  amount: playerPosition.invested,
                  profit: -playerPosition.invested,
                  multiplier: 0,
                  timestamp: Date.now(),
                }]
              });
              
              // Announce
              setLobbyChat(prevChat => [...prevChat.slice(-20), {
                id: now + Math.random() + 0.1,
                handle: '💀',
                text: `${handcashUser?.handle || 'You'} got rugged on ${t.ticker} while away! -${formatPB(playerPosition.invested)}`,
                isAction: true,
                isTradeAnnouncement: true,
                isRug: true
              }]);
            } else {
              // Token mooned - close position with profit (estimate based on final price)
              const finalMultiplier = t.moonMultiplier || 2;
              const profit = Math.floor(playerPosition.invested * finalMultiplier) - playerPosition.invested;
              
              const updatedPositions = positions.map(p => 
                p.id === playerPosition.id 
                  ? { ...p, status: 'closed', closedAt: Date.now(), profit: profit }
                  : p
              );
              savePositions(updatedPositions);
              
              // Add winnings to wallet
              setGameWallet(prev => prev + playerPosition.invested + profit);
              
              // Track in daily stats
              saveDailyStats({
                ...dailyStats,
                trades: [...dailyStats.trades, {
                  id: 'trade_' + Date.now(),
                  tokenTicker: t.ticker,
                  type: 'moon',
                  amount: playerPosition.invested + profit,
                  profit: profit,
                  multiplier: finalMultiplier,
                  timestamp: Date.now(),
                }]
              });
              
              // Announce
              setLobbyChat(prevChat => [...prevChat.slice(-20), {
                id: now + Math.random() + 0.1,
                handle: '🌙',
                text: `${handcashUser?.handle || 'You'} diamond handed ${t.ticker} to the moon! +${formatPB(profit)}`,
                isAction: true,
                isTradeAnnouncement: true,
                isWin: true
              }]);
            }
          }
        });
        
        return updated;
      });
    }, 2000);
    
    return () => clearInterval(interval);
  }, [view]);

  // ============== DYNAMIC LOBBY CHAT ==============
  // Bot chat is now generated server-side and synced via /api/live polling
  // All users see the same messages - true multiplayer!

  // ============== DYNAMIC TOKEN ROOM CHAT ==============
  // Bot chat is now generated server-side and synced via polling
  // This keeps all users seeing the same chat messages

  // ============== ELAPSED TIME FOR SLIPPAGE DISPLAY ==============
  useEffect(() => {
    if (view !== 'token' || gameState !== 'playing') return;
    
    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTimeRef.current);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [view, gameState]);

  // ============== TOKEN LIFECYCLE TIMER ==============
  useEffect(() => {
    if (view !== 'token' || !currentToken) return;
    
    const interval = setInterval(() => {
      const m = manipulationRef.current;
      if (m.startTime && m.rugTime) {
        const elapsed = Date.now() - m.startTime;
        const remaining = Math.max(0, m.rugTime - elapsed);
        setTokenTimeLeft(remaining);
      }
    }, 500);
    
    return () => clearInterval(interval);
  }, [view, currentToken]);

  // ============== LIGHTWEIGHT CHARTS EFFECTS ==============
  // Initialize chart when token changes
  useEffect(() => {
    if (!lwChartContainerRef.current || !currentToken) return;
    
    // Clean up existing chart
    if (lwChartRef.current) {
      lwChartRef.current.remove();
      lwChartRef.current = null;
    }
    
    // Determine chart size - use container width when available
    const isSpectating = gameState === 'spectating' || gameState === 'won' || !(position?.size > 0);
    // Get the chart container's wrapper width (the div with width: 100%)
    const containerEl = lwChartContainerRef.current?.parentElement;
    const containerWidth = containerEl?.clientWidth || containerEl?.offsetWidth || 0;
    let width, height;
    
    if (containerWidth > 50 && tradingViewMode === 'focused') {
      // Use container width for proper alignment in Focused/Locked In mode
      width = containerWidth - 2; // -2 for border
      if (chartFullscreen) {
        height = isMobile ? 350 : 420;
      } else if (isMobile) {
        height = 320;
      } else {
        height = 380;
      }
    } else {
      // Fallback to calculated widths (Classic mode or container not ready)
      if (chartFullscreen) {
        if (tradingViewMode === 'focused') {
          width = isMobile ? Math.min(window.innerWidth - 24, 500) : Math.min(window.innerWidth - 80, 850);
          height = isMobile ? 350 : 420;
        } else {
          width = isMobile ? Math.min(window.innerWidth - 16, 500) : Math.min(window.innerWidth - 80, 900);
          height = isMobile ? 400 : 550;
        }
      } else if (isMobile) {
        width = Math.min(window.innerWidth - 24, 360);
        height = tradingViewMode === 'focused' ? 320 : (isSpectating ? 300 : 260);
      } else {
        width = tradingViewMode === 'focused' ? Math.min(window.innerWidth - 420, 550) : 500;
        height = tradingViewMode === 'focused' ? 380 : (isSpectating ? 360 : 320);
      }
    }
    
    // Create chart with Deep Ocean theme
    const chart = createChart(lwChartContainerRef.current, {
      width,
      height,
      layout: {
        background: { type: ColorType.Solid, color: '#0a0d12' },
        textColor: '#8a9bae',
      },
      grid: {
        vertLines: { color: '#1a2030' },
        horzLines: { color: '#1a2030' },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: '#1a2030',
        scaleMargins: { top: 0.1, bottom: 0.2 },
      },
      timeScale: {
        borderColor: '#1a2030',
        timeVisible: true,
        secondsVisible: true,
      },
      handleScroll: { vertTouchDrag: false },
      handleScale: { axisPressedMouseMove: true },
    });
    
    // Add candlestick series with Deep Ocean colors (v5 API)
    const candleSeries = chart.addSeries(LightweightCharts.CandlestickSeries, {
      upColor: '#2d8f7b',
      downColor: '#c75454',
      borderUpColor: '#2d8f7b',
      borderDownColor: '#c75454',
      wickUpColor: '#2d8f7b',
      wickDownColor: '#c75454',
    });
    
    lwChartRef.current = chart;
    lwCandleSeriesRef.current = candleSeries;
    
    // Handle resize
    const handleResize = () => {
      if (lwChartRef.current && lwChartContainerRef.current) {
        const newWidth = isMobile 
          ? Math.min(window.innerWidth - 24, 360)
          : chartFullscreen ? Math.min(window.innerWidth - 80, 900) : 500;
        lwChartRef.current.applyOptions({ width: newWidth });
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (lwChartRef.current) {
        lwChartRef.current.remove();
        lwChartRef.current = null;
      }
    };
  }, [currentToken?.id, chartFullscreen, isMobile, gameState, position?.size, view, tradingViewMode]);
  
  // Update chart data when candles change
  useEffect(() => {
    if (!lwCandleSeriesRef.current || !candles || candles.length === 0) return;
    
    // 📊 SERVER-SIDE AGGREGATION: Candles already grouped by interval on backend
    // Just format for Lightweight Charts and display
    const chartData = candles
      .slice(-150) // Limit to 150 candles
      .map(c => ({
        time: Math.floor((c.time || Date.now()) / 1000),
        open: c.open || currentPrice,
        high: c.high || c.open || currentPrice,
        low: c.low || c.open || currentPrice,
        close: c.close || c.open || currentPrice,
      }))
      .sort((a, b) => a.time - b.time);
    
    if (chartData.length > 0) {
      lwCandleSeriesRef.current.setData(chartData);
      lwChartRef.current?.timeScale().fitContent();
    }
  }, [candles, currentPrice]);
  
  // Update entry line when position changes
  useEffect(() => {
    if (!lwCandleSeriesRef.current) return;
    
    if (lwEntryLineRef.current) {
      lwCandleSeriesRef.current.removePriceLine(lwEntryLineRef.current);
      lwEntryLineRef.current = null;
    }
    
    if (position?.entry > 0 && gameState === 'playing') {
      lwEntryLineRef.current = lwCandleSeriesRef.current.createPriceLine({
        price: position.entry,
        color: '#f0b90b',
        lineWidth: 2,
        lineStyle: 2,
        axisLabelVisible: true,
        title: 'ENTRY',
      });
    }
  }, [position?.entry, gameState]);

  // ============== WHO'S ONLINE SIDEBAR (renders on all views when logged in) ==============
  const OnlineSidebar = () => {
    if (!handcashUser) return null;
    
    // Mobile: Show as modal overlay when showSidebar is true, OR show toggle button when closed
    if (isMobile) {
      if (!showSidebar) {
        // Show floating toggle button on mobile - TOP RIGHT
        return (
          <button
            onClick={() => setShowSidebar(true)}
            style={{
              position: 'fixed',
              right: '12px',
              top: '12px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#0d0d12',
              border: '2px solid #00d26a',
              color: '#00d26a',
              fontSize: '14px',
              cursor: 'pointer',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(0,210,106,0.3)',
            }}
          >
            👥
            {onlineUsers.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                backgroundColor: '#00d26a',
                color: '#000',
                fontSize: '8px',
                fontWeight: 'bold',
                padding: '2px 4px',
                borderRadius: '8px',
                minWidth: '14px',
                textAlign: 'center',
              }}>{onlineUsers.length}</span>
            )}
          </button>
        );
      }
      
      return (
        <div 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            backgroundColor: 'rgba(0,0,0,0.9)', 
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: "'SF Mono', monospace",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowSidebar(false); }}
        >
          <div style={{
            backgroundColor: '#0d0d12',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ 
              padding: '12px 16px', 
              borderBottom: '1px solid #1a1a2e',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#00d26a', fontSize: '14px' }}>●</span>
                <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px', letterSpacing: '0.5px' }}>PLAYERS ONLINE ({onlineUsers.length})</span>
              </div>
              <button 
                onClick={() => setShowSidebar(false)}
                style={{ background: 'none', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer' }}
              >×</button>
            </div>
            
            {/* Filters */}
            <div style={{ padding: '8px 12px', display: 'flex', gap: '8px', borderBottom: '1px solid #1a1a2e' }}>
              <button onClick={() => setShowFriendsOnly(false)} style={{ padding: '4px 10px', borderRadius: '12px', border: 'none', backgroundColor: !showFriendsOnly ? '#00d26a' : '#1a1a2e', color: !showFriendsOnly ? '#000' : '#888', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold', fontFamily: "'SF Mono', monospace" }}>All</button>
              <button onClick={() => setShowFriendsOnly(true)} style={{ padding: '4px 10px', borderRadius: '12px', border: 'none', backgroundColor: showFriendsOnly ? '#f0b90b' : '#1a1a2e', color: showFriendsOnly ? '#000' : '#888', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold', fontFamily: "'SF Mono', monospace" }}>👥 Friends</button>
            </div>
            
            {/* User List - Nice cards with stats like desktop */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
              {(() => {
                // Filter users based on friends only toggle
                let displayUsers = showFriendsOnly
                  ? onlineUsers.filter(u => friends.some(f => (f.handle || '').replace('$', '').toLowerCase() === (u.handle || '').replace('$', '').toLowerCase()) || u.handle === handcashUser?.handle?.replace('$', ''))
                  : onlineUsers;

                // Sort: You first, then friends, then everyone else
                const myHandle = handcashUser?.handle?.replace('$', '');
                displayUsers = [...displayUsers].sort((a, b) => {
                  if (a.handle === myHandle) return -1;
                  if (b.handle === myHandle) return 1;
                  const aIsFriend = friends.some(f => (f.handle || '').replace('$', '').toLowerCase() === (a.handle || '').replace('$', '').toLowerCase());
                  const bIsFriend = friends.some(f => (f.handle || '').replace('$', '').toLowerCase() === (b.handle || '').replace('$', '').toLowerCase());
                  if (aIsFriend && !bIsFriend) return -1;
                  if (!aIsFriend && bIsFriend) return 1;
                  return (a.handle || '').localeCompare(b.handle || '');
                });
                
                if (displayUsers.length === 0) {
                  return (
                    <div style={{ color: '#555', fontSize: '12px', textAlign: 'center', padding: '40px 20px' }}>
                      {showFriendsOnly ? (
                        <>
                          <div style={{ fontSize: '32px', marginBottom: '12px' }}>💗</div>
                          No friends online right now
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: '32px', marginBottom: '12px' }}>👀</div>
                          No other players online yet
                        </>
                      )}
                    </div>
                  );
                }
                
                return displayUsers.map((user, i) => {
                  const handle = user.handle?.replace('$', '') || 'unknown';
                  const isMe = handle === myHandle;
                  const isFriend = !isMe; // Everyone can tip everyone
                  const isPremiumUser = user.isPremium;
                  
                  return (
                    <div
                      key={user.handle || `user-${i}`}
                      onClick={() => { setSelectedProfile(user); setShowSidebar(false); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        borderRadius: '12px',
                        backgroundColor: isMe ? '#0d1a0d' : '#0d0d12',
                        border: isMe ? '1px solid #00d26a33' : '1px solid #1a1a2e',
                        marginBottom: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        border: isPremiumUser ? '3px solid #f0b90b' : '2px solid #333',
                        overflow: 'hidden',
                        flexShrink: 0,
                        boxShadow: isPremiumUser ? '0 0 10px rgba(240,185,11,0.3)' : 'none',
                      }}>
                        {user.avatarUrl ? (
                          <img src={user.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', backgroundColor: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🤚</div>
                        )}
                      </div>
                      
                      {/* User Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          color: isPremiumUser ? '#f0b90b' : '#fff',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexWrap: 'wrap',
                        }}>
                          ${handle}
                          {isPremiumUser && <span style={{ fontSize: '12px' }}>👑</span>}
                          {user.badges?.slice(0, 2).map(b => (
                            <span key={b.id} title={b.name} style={{ fontSize: '11px' }}>{b.icon}</span>
                          ))}
                        </div>
                        {/* Stats line */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                          {user.stats ? (
                            <>
                              <span style={{
                                color: user.stats.totalProfit >= 0 ? '#00d26a' : '#ff4757',
                                fontSize: '13px',
                                fontWeight: 'bold',
                                fontFamily: 'monospace',
                              }}>
                                {user.stats.totalProfit >= 0 ? '+' : ''}{formatPB(user.stats.totalProfit)}
                              </span>
                              <span style={{ color: '#666', fontSize: '11px' }}>
                                {user.stats.winRate || 0}% WR
                              </span>
                              {user.stats.currentStreak > 0 && (
                                <span style={{ color: '#f0b90b', fontSize: '11px' }}>🔥{user.stats.currentStreak}</span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: '#555', fontSize: '11px' }}>
                              {isMe ? '✓ You' : 'Tap for profile'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedDMUser(handle); setShowDMs(true); setShowSidebar(false); }}
                          style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '14px', cursor: 'pointer' }}
                        >💬</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setTipTarget({ handle, avatarUrl: user.avatarUrl }); setShowSidebar(false); }}
                          style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #00d26a33', backgroundColor: '#0a1a0f', color: '#00d26a', fontSize: '14px', cursor: 'pointer' }}
                        >💸</button>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      );
    }
    
    // Desktop: Collapsed state - just show toggle button
    if (!showSidebar) {
      return (
        <button
          onClick={() => setShowSidebar(true)}
          style={{
            position: 'fixed',
            right: '16px',
            top: '80px', // Moved down to avoid music selector overlap
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#0d0d12',
            border: '2px solid #00d26a',
            color: '#00d26a',
            fontSize: '18px',
            cursor: 'pointer',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0,210,106,0.3)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1a2a1a'; e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#0d0d12'; e.currentTarget.style.transform = 'scale(1)'; }}
          title="Show Players Online"
        >
          👥
          {onlineUsers.length > 0 && (
            <span style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              backgroundColor: '#00d26a',
              color: '#000',
              fontSize: '10px',
              fontWeight: 'bold',
              padding: '2px 6px',
              borderRadius: '10px',
              minWidth: '18px',
              textAlign: 'center',
            }}>{onlineUsers.length}</span>
          )}
        </button>
      );
    }
    
    return (
      <div style={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: '280px',
        backgroundColor: '#0a0a0f',
        borderLeft: '1px solid #1a1a2e',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100,
        overflowY: 'auto',
        fontFamily: "'Space Grotesk', sans-serif",
        boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
      }}>
        {/* Header with Close Button */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #1a1a2e',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          backgroundColor: '#0d0d12',
        }}>
          <span style={{ color: '#00d26a', fontSize: '10px', animation: 'pulse 2s infinite' }}>●</span>
          <span style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>Players Online</span>
          <span style={{ 
            backgroundColor: '#00d26a', 
            color: '#000', 
            fontSize: '12px', 
            fontWeight: 'bold',
            padding: '4px 10px',
            borderRadius: '12px',
          }}>{onlineUsers.length || onlineCount || 0}</span>
          
          {/* Friends Only Toggle */}
          <button
            onClick={() => setShowFriendsOnly(!showFriendsOnly)}
            style={{
              marginLeft: '8px',
              padding: '4px 8px',
              borderRadius: '6px',
              border: showFriendsOnly ? '1px solid #ff6b9d' : '1px solid #333',
              backgroundColor: showFriendsOnly ? 'rgba(255,107,157,0.15)' : 'transparent',
              color: showFriendsOnly ? '#ff6b9d' : '#666',
              fontSize: '10px',
              fontWeight: showFriendsOnly ? 'bold' : 'normal',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            title={showFriendsOnly ? 'Show all players' : 'Show friends only'}
          >
            💗 {showFriendsOnly ? 'Friends' : 'All'}
          </button>
          
          {/* Close Button */}
          <button
            onClick={() => setShowSidebar(false)}
            style={{
              marginLeft: 'auto',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: '#1a1a2e',
              border: 'none',
              color: '#666',
              fontSize: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2a2a3e'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#1a1a2e'; e.currentTarget.style.color = '#666'; }}
            title="Hide sidebar"
          >
            ✕
          </button>
        </div>
        
        {/* User List */}
        <div style={{ flex: 1, padding: '12px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          {(() => {
            // Filter users based on friends only toggle
            let displayUsers = showFriendsOnly
              ? onlineUsers.filter(u => friends.some(f => (f.handle || '').replace('$', '').toLowerCase() === (u.handle || '').replace('$', '').toLowerCase()) || u.handle === handcashUser?.handle?.replace('$', ''))
              : onlineUsers;

            // Sort: You first, then friends, then everyone else
            const myHandle = handcashUser?.handle?.replace('$', '');
            displayUsers = [...displayUsers].sort((a, b) => {
              // You always first
              if (a.handle === myHandle) return -1;
              if (b.handle === myHandle) return 1;
              // Friends come next
              const aIsFriend = friends.some(f => (f.handle || '').replace('$', '').toLowerCase() === (a.handle || '').replace('$', '').toLowerCase());
              const bIsFriend = friends.some(f => (f.handle || '').replace('$', '').toLowerCase() === (b.handle || '').replace('$', '').toLowerCase());
              if (aIsFriend && !bIsFriend) return -1;
              if (!aIsFriend && bIsFriend) return 1;
              // Then sort by handle alphabetically
              return a.handle.localeCompare(b.handle);
            });
            
            if (displayUsers.length === 0) {
              return (
                <div style={{
                  color: '#555',
                  fontSize: '12px',
                  textAlign: 'center',
                  padding: '40px 20px',
                }}>
                  {showFriendsOnly ? (
                    <>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>💗</div>
                      No friends online right now
                      <div style={{ color: '#333', fontSize: '11px', marginTop: '8px' }}>
                        Add friends to see them here!
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>👀</div>
                      No other players online yet
                      <div style={{ color: '#333', fontSize: '11px', marginTop: '8px' }}>
                        Share the game to invite friends!
                      </div>
                    </>
                  )}
                </div>
              );
            }
            
            return displayUsers.map((user) => (
              <div
                key={user.handle}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  borderRadius: '12px',
                  marginBottom: '8px',
                  backgroundColor: user.handle === handcashUser?.handle?.replace('$', '') ? '#1a2a1a' : '#111',
                  border: user.handle === handcashUser?.handle?.replace('$', '') ? '1px solid #00d26a33' : '1px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1a1a2e'; e.currentTarget.style.borderColor = '#333'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = user.handle === handcashUser?.handle?.replace('$', '') ? '#1a2a1a' : '#111'; e.currentTarget.style.borderColor = user.handle === handcashUser?.handle?.replace('$', '') ? '#00d26a33' : 'transparent'; }}
                onClick={() => setSelectedProfile(user)}
              >
                {/* Avatar */}
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  border: user.isPremium ? '3px solid #f0b90b' : '2px solid #333',
                  overflow: 'hidden',
                  flexShrink: 0,
                  position: 'relative',
                  boxShadow: user.isPremium ? '0 0 10px rgba(240,185,11,0.3)' : 'none',
                }}>
                  <img
                    src={user.avatarUrl}
                    alt={user.handle}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.src = `https://api.dicebear.com/7.x/identicon/svg?seed=${user.handle}`; }}
                  />
                </div>
                
                {/* User Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: user.isPremium ? '#f0b90b' : '#fff',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    ${user.handle}
                    {user.isPremium && <span style={{ fontSize: '12px' }}>👑</span>}
                    {user.badges?.slice(0, 2).map(b => (
                      <span key={b.id} title={b.name} style={{ fontSize: '11px' }}>{b.icon}</span>
                    ))}
                  </div>
                  {/* Stats line */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '2px',
                  }}>
                    {user.stats ? (
                      <>
                        <span style={{
                          color: user.stats.totalProfit >= 0 ? '#00d26a' : '#ff4757',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          fontFamily: 'monospace',
                        }}>
                          {user.stats.totalProfit >= 0 ? '+' : ''}{formatPB(user.stats.totalProfit)}
                        </span>
                        <span style={{ color: '#444', fontSize: '10px' }}>
                          {user.stats.winRate}% WR
                        </span>
                        {user.stats.currentStreak > 0 && (
                          <span style={{ color: '#f0b90b', fontSize: '10px' }}>🔥{user.stats.currentStreak}</span>
                        )}
                        {user.stats.currentStreak < 0 && (
                          <span style={{ color: '#666', fontSize: '10px' }}>🧊{Math.abs(user.stats.currentStreak)}</span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#555', fontSize: '11px' }}>
                        {user.handle === handcashUser?.handle?.replace('$', '') ? '✓ You' : 'Tap for profile'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ));
          })()}
        </div>
        
        {/* Icon Legend */}
        <div style={{
          padding: '10px 12px',
          borderTop: '1px solid #1a1a2e',
          backgroundColor: '#0a0a0f',
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '12px', 
            fontSize: '10px', 
            color: '#555',
            flexWrap: 'wrap'
          }}>
            <span title="Friend">💗 Friend</span>
            <span title="Premium">👑 Premium</span>
            <span title="Whale">🐋 Whale</span>
            <span title="Rekt">💀 Rekt</span>
          </div>
        </div>
        
        {/* Footer with HandCash Logo */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid #1a1a2e',
          backgroundColor: '#0d0d12',
          textAlign: 'center',
        }}>
          <a 
            href="https://handcash.io" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              textDecoration: 'none',
              opacity: 0.7,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
          >
            <span style={{ color: '#555', fontSize: '10px' }}>Powered by</span>
            <img 
              src="https://i.ibb.co/23X7r7j/logo.png"
              alt="HandCash"
              style={{ width: '24px', height: '24px', borderRadius: '50%' }}
            />
            <span style={{ 
              color: '#38cb7c', 
              fontSize: '14px', 
              fontWeight: 'bold',
              letterSpacing: '-0.5px',
            }}>HandCash</span>
          </a>
        </div>
      </div>
    );
  };

  // ============== PAPER CHAT (AIM-style window) ==============
  const PaperChatContent = () => {
    // Get current theme
    const theme = chatThemes[paperChatTheme] || chatThemes.gold;
    
    // Use parent-level refs (they persist across re-renders)
    const inputRef = paperChatInputRef;
    const windowRef = paperChatWindowRef;
    const dragStartRef = paperChatDragStartRef;
    const messagesEndRef = paperChatMessagesEndRef;
    const userScrolledRef = paperChatUserScrolledRef;
    const userListRef = paperChatUserListRef;
    const userListScrollRef = paperChatUserListScrollRef;
    const messagesContainerRef = paperChatMessagesContainerRef;
    const messagesScrollRef = paperChatMessagesScrollRef;

    // Track user list scroll position to preserve it across re-renders
    const handleUserListScroll = (e) => {
      userListScrollRef.current = e.target.scrollTop;
    };
    
    // Track messages scroll position
    const handleMessagesScroll = (e) => {
      messagesScrollRef.current = e.target.scrollTop;
    };

    // Restore scroll positions after re-render (useLayoutEffect runs before paint)
    useLayoutEffect(() => {
      // Restore user list scroll position
      if (userListRef.current && userListScrollRef.current > 0) {
        userListRef.current.scrollTop = userListScrollRef.current;
      }
      // Restore messages scroll position
      if (messagesContainerRef.current && messagesScrollRef.current > 0) {
        messagesContainerRef.current.scrollTop = messagesScrollRef.current;
      }
    });

    // Scroll to bottom helper - use scrollTop for reliability
    const scrollToBottom = (instant = false) => {
      if (messagesContainerRef.current) {
        if (instant) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        } else {
          // Smooth scroll to bottom
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }
    };
    
    // Handle drag events - use refs for smooth dragging
    const handleMouseDown = (e) => {
      // Don't start drag if clicking a button or the content area
      if (e.target.closest('button') || e.target.closest('.paper-chat-content') || e.target.closest('select') || e.target.closest('input')) return;
      e.preventDefault();
      dragStartRef.current = {
        x: e.clientX - paperChatPos.x,
        y: e.clientY - paperChatPos.y
      };
      setIsDraggingChat(true);
      
      // Add listeners to document for reliable capture
      document.addEventListener('mousemove', handleDocMouseMove);
      document.addEventListener('mouseup', handleDocMouseUp);
    };
    
    const handleDocMouseMove = (e) => {
      if (!dragStartRef.current) return;
      const newX = Math.max(0, Math.min(window.innerWidth - 280, e.clientX - dragStartRef.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragStartRef.current.y));
      // Direct DOM manipulation for smooth dragging
      if (windowRef.current) {
        windowRef.current.style.left = newX + 'px';
        windowRef.current.style.top = newY + 'px';
      }
    };
    
    const handleDocMouseUp = (e) => {
      if (!dragStartRef.current) return;
      // Save final position to state
      const newX = Math.max(0, Math.min(window.innerWidth - 280, e.clientX - dragStartRef.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragStartRef.current.y));
      setPaperChatPos({ x: newX, y: newY });
      dragStartRef.current = null;
      setIsDraggingChat(false);
      
      // Remove listeners
      document.removeEventListener('mousemove', handleDocMouseMove);
      document.removeEventListener('mouseup', handleDocMouseUp);
    };
    
    // Touch handlers for mobile/tablet
    const handleTouchStart = (e) => {
      // Don't start drag if tapping a button or the content area
      if (e.target.closest('button') || e.target.closest('.paper-chat-content') || e.target.closest('select') || e.target.closest('input')) return;
      e.preventDefault();
      const touch = e.touches[0];
      dragStartRef.current = {
        x: touch.clientX - paperChatPos.x,
        y: touch.clientY - paperChatPos.y
      };
      setIsDraggingChat(true);
      
      // Add listeners to document for reliable capture on tablet
      document.addEventListener('touchmove', handleDocTouchMove, { passive: false });
      document.addEventListener('touchend', handleDocTouchEnd);
      document.addEventListener('touchcancel', handleDocTouchEnd);
    };
    
    const handleDocTouchMove = (e) => {
      if (!dragStartRef.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      const newX = Math.max(0, Math.min(window.innerWidth - 300, touch.clientX - dragStartRef.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, touch.clientY - dragStartRef.current.y));
      if (windowRef.current) {
        windowRef.current.style.left = newX + 'px';
        windowRef.current.style.top = newY + 'px';
      }
    };
    
    const handleDocTouchEnd = (e) => {
      if (!dragStartRef.current) {
        // Clean up listeners anyway
        document.removeEventListener('touchmove', handleDocTouchMove);
        document.removeEventListener('touchend', handleDocTouchEnd);
        document.removeEventListener('touchcancel', handleDocTouchEnd);
        return;
      }
      // Get last touch position
      const touch = e.changedTouches?.[0];
      if (touch) {
        const newX = Math.max(0, Math.min(window.innerWidth - 300, touch.clientX - dragStartRef.current.x));
        const newY = Math.max(0, Math.min(window.innerHeight - 100, touch.clientY - dragStartRef.current.y));
        setPaperChatPos({ x: newX, y: newY });
      }
      dragStartRef.current = null;
      setIsDraggingChat(false);
      
      // Remove listeners
      document.removeEventListener('touchmove', handleDocTouchMove);
      document.removeEventListener('touchend', handleDocTouchEnd);
      document.removeEventListener('touchcancel', handleDocTouchEnd);
    };
    
    // Get dropdown options
    const getDropdownOptions = () => {
      const options = [{ value: 'buddies', label: '👥 Buddies', unread: 0 }];
      dmConversations.forEach(convo => {
        options.push({
          value: convo.handle,
          label: `💬 $${convo.handle}`,
          unread: convo.unreadCount || 0
        });
      });
      return options;
    };
    
    // Filter users
    const displayUsers = showFriendsOnly
      ? onlineUsers.filter(u => friends.some(f => (f.handle || '').replace('$', '').toLowerCase() === (u.handle || '').replace('$', '').toLowerCase()))
      : onlineUsers;
    
    const windowWidth = isMobile ? '100vw' : '280px';
    
    // Capture viewport height once when chat opens on mobile (prevents jump when keyboard appears)
    if (isMobile && !paperChatMobileHeightRef.current) {
      paperChatMobileHeightRef.current = window.innerHeight;
    }
    const windowHeight = isMobile ? `${paperChatMobileHeightRef.current || window.innerHeight}px` : '400px';
    
    return (
      <>
        {/* AIM-style Window */}
        <div 
          ref={windowRef}
          style={{
            position: 'fixed',
            left: isMobile ? 0 : paperChatPos.x,
            top: isMobile ? 0 : paperChatPos.y,
            width: windowWidth,
            maxWidth: isMobile ? '100vw' : '340px',
            height: windowHeight,
            maxHeight: isMobile ? `${paperChatMobileHeightRef.current || window.innerHeight}px` : '500px',
            backgroundColor: '#0a0a0f',
            border: isMobile ? 'none' : `2px solid ${theme.border}`,
            borderRadius: isMobile ? 0 : '8px',
            boxShadow: isMobile ? 'none' : `0 8px 32px rgba(0,0,0,0.8), 0 0 20px ${theme.primary}33`,
            zIndex: 1999,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: "'SF Mono', 'Monaco', 'Consolas', monospace",
          }}
        >
          {/* Title Bar - Draggable */}
          <div 
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            style={{
              background: `linear-gradient(180deg, ${theme.primary} 0%, ${theme.primary}cc 100%)`,
              padding: isMobile ? '10px 12px' : '6px 10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: isDraggingChat ? 'grabbing' : 'grab',
              userSelect: 'none',
              touchAction: 'none',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '14px' }}>👥</span>
              <span style={{ color: '#000', fontWeight: 'bold', fontSize: '12px' }}>Paper Chat</span>
              <span style={{ color: '#00000088', fontSize: '10px' }}>({onlineUsers.length})</span>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {/* Theme picker */}
              <div style={{ position: 'relative' }}>
                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    const themes = Object.keys(chatThemes);
                    const currentIndex = themes.indexOf(paperChatTheme);
                    const nextTheme = themes[(currentIndex + 1) % themes.length];
                    setPaperChatTheme(nextTheme);
                    localStorage.setItem('paperChatTheme', nextTheme);
                  }}
                  onTouchEnd={(e) => { 
                    e.stopPropagation(); 
                    e.preventDefault();
                    const themes = Object.keys(chatThemes);
                    const currentIndex = themes.indexOf(paperChatTheme);
                    const nextTheme = themes[(currentIndex + 1) % themes.length];
                    setPaperChatTheme(nextTheme);
                    localStorage.setItem('paperChatTheme', nextTheme);
                  }}
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '3px',
                    border: '1px solid #00000033',
                    backgroundColor: '#00000022',
                    color: '#000',
                    fontSize: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Change theme"
                >🎨</button>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); paperChatMobileHeightRef.current = null; setShowPaperChat(false); }}
                onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); paperChatMobileHeightRef.current = null; setShowPaperChat(false); }}
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '3px',
                  border: '1px solid #00000033',
                  backgroundColor: '#00000022',
                  color: '#000',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
                title="Close"
              >×</button>
            </div>
          </div>
          
          {/* Custom Dropdown Selector - Won't close on parent re-renders */}
          <div
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            style={{
              padding: '8px',
              borderBottom: '1px solid #1a1a2e',
              backgroundColor: '#0d0d12',
              position: 'relative',
            }}
          >
            {/* Dropdown Button */}
            <div
              onClick={(e) => { e.stopPropagation(); setPaperChatDropdownOpen(!paperChatDropdownOpen); }}
              style={{
                width: '100%',
                padding: isMobile ? '12px 10px' : '8px 10px',
                backgroundColor: '#111',
                border: '1px solid #333',
                borderRadius: '4px',
                color: '#f0b90b',
                fontSize: isMobile ? '16px' : '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>
                {paperChatView === 'buddies' ? '👥 Buddies' : `💬 $${paperChatView}`}
                {(() => {
                  const convo = dmConversations.find(c => c.handle === paperChatView);
                  return convo?.unreadCount > 0 ? ` (${convo.unreadCount})` : '';
                })()}
              </span>
              <span style={{ color: theme.primary, fontSize: '10px' }}>{paperChatDropdownOpen ? '▲' : '▼'}</span>
            </div>
            
            {/* Dropdown Menu */}
            {paperChatDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '8px',
                  right: '8px',
                  backgroundColor: '#111',
                  border: '1px solid #333',
                  borderRadius: '4px',
                  zIndex: 100,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                }}
              >
                {getDropdownOptions().map(opt => (
                  <div
                    key={opt.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPaperChatDropdownOpen(false);
                      setPaperChatView(opt.value);
                      if (opt.value !== 'buddies') {
                        setSelectedDMUser(opt.value);
                        markDMsRead(opt.value);
                      }
                    }}
                    style={{
                      padding: isMobile ? '12px 10px' : '8px 10px',
                      color: paperChatView === opt.value ? theme.primary : '#888',
                      backgroundColor: paperChatView === opt.value ? theme.bg : 'transparent',
                      cursor: 'pointer',
                      fontSize: isMobile ? '14px' : '12px',
                      fontWeight: 'bold',
                      borderBottom: '1px solid #222',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    onMouseEnter={(e) => { if (paperChatView !== opt.value) e.currentTarget.style.backgroundColor = '#1a1a2e'; }}
                    onMouseLeave={(e) => { if (paperChatView !== opt.value) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <span>{opt.label}</span>
                    {opt.unread > 0 && (
                      <span style={{
                        backgroundColor: '#9b59b6',
                        color: '#fff',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '10px'
                      }}>{opt.unread}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Content Area */}
          <div 
            className="paper-chat-content" 
            onClick={() => paperChatDropdownOpen && setPaperChatDropdownOpen(false)}
            style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
          >
            {paperChatView === 'buddies' ? (
              /* Buddy List View */
              <>
                {/* Online count */}
                <div style={{ padding: '6px 8px', display: 'flex', gap: '6px', borderBottom: '1px solid #1a1a2e' }}>
                  <span style={{ color: '#00d26a', fontSize: '10px', fontWeight: 'bold' }}>
                    🟢 {onlineUsers.length} Online
                  </span>
                </div>

                {/* Incoming Friend Requests Banner - hidden since friends removed */}
                {false && friendRequestsReceived.length > 0 && (
                  <div style={{ 
                    padding: '6px 8px', 
                    backgroundColor: '#1a2a1a', 
                    borderBottom: '1px solid #00d26a33',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <span style={{ fontSize: '12px' }}>💌</span>
                    <span style={{ color: '#00d26a', fontSize: '10px', fontWeight: 'bold' }}>
                      {friendRequestsReceived.length} friend request{friendRequestsReceived.length > 1 ? 's' : ''}!
                    </span>
                    <span style={{ color: '#666', fontSize: '9px', marginLeft: 'auto' }}>
                      Look for 💌 below
                    </span>
                  </div>
                )}
                
                {/* User List */}
                <div
                  ref={userListRef}
                  onScroll={handleUserListScroll}
                  style={{
                    flex: 1,
                    overflow: 'auto',
                    overflowX: 'hidden',
                    padding: '4px',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                  }}
                >
                  {displayUsers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#555', fontSize: '11px' }}>
                      {showFriendsOnly ? 'No friends online' : 'No players online'}
                    </div>
                  ) : (
                    displayUsers.map(user => {
                      const handle = (user.handle || '').replace(/^\$/, '');
                      const isMe = handle === handcashUser?.handle?.replace('$', '');
                      const isFriend = !isMe; // Everyone can tip everyone

                      return (
                        <div
                          key={handle}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            marginBottom: '2px',
                            backgroundColor: isMe ? '#1a1508' : 'transparent',
                            transition: 'background-color 0.15s',
                          }}
                          onMouseEnter={e => !isMe && (e.currentTarget.style.backgroundColor = '#1a1a2e')}
                          onMouseLeave={e => !isMe && (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          {/* Online Dot */}
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#00d26a', flexShrink: 0 }} />
                          
                          {/* Avatar */}
                          <div style={{ 
                            width: '24px', 
                            height: '24px', 
                            borderRadius: '50%', 
                            overflow: 'hidden',
                            border: user.isPremium ? '2px solid #f0b90b' : '1px solid #333',
                            flexShrink: 0,
                          }}>
                            {user.avatarUrl ? (
                              <img src={user.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ width: '100%', height: '100%', backgroundColor: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>🤚</div>
                            )}
                          </div>
                          
                          {/* Name - Click to DM */}
                          <div 
                            onClick={() => {
                              if (!isMe) {
                                setPaperChatView(handle);
                                setSelectedDMUser(handle);
                                fetchDMs();
                              }
                            }}
                            style={{ 
                              flex: 1, 
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: isMe ? 'default' : 'pointer',
                            }}
                            title={isMe ? '' : `Message $${handle}`}
                          >
                            <span style={{ 
                              color: user.isPremium ? '#f0b90b' : '#fff', 
                              fontSize: '11px', 
                              fontWeight: 'bold' 
                            }}>
                              ${handle}
                            </span>
                            {user.isPremium && <span style={{ marginLeft: '3px', fontSize: '10px' }}>👑</span>}
                            {isMe && <span style={{ marginLeft: '4px', color: '#666', fontSize: '9px' }}>(you)</span>}
                          </div>
                          
                          {/* Action Buttons */}
                          {!isMe && (
                              <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                                {/* DM Button - Everyone can message */}
                                <button
                                  onClick={() => {
                                    setPaperChatView(handle);
                                    setSelectedDMUser(handle);
                                    fetchDMs();
                                  }}
                                  style={{ 
                                    padding: '4px 6px', 
                                    borderRadius: '4px', 
                                    border: '1px solid #333', 
                                    backgroundColor: '#1a1a2e', 
                                    color: '#888', 
                                    fontSize: '11px', 
                                    cursor: 'pointer' 
                                  }}
                                  title="Message"
                                >💬</button>
                                
                                {/* Tip Button - Friends only */}
                                {isFriend && (
                                  <button
                                    onClick={() => setTipTarget({ handle, avatarUrl: user.avatarUrl, isPremium: user.isPremium })}
                                    style={{ 
                                      padding: '4px 6px', 
                                      borderRadius: '4px', 
                                      border: '1px solid #00d26a44', 
                                      backgroundColor: '#0a1a0f', 
                                      color: '#00d26a', 
                                      fontSize: '11px', 
                                      cursor: 'pointer' 
                                    }}
                                    title="Tip"
                                  >💸</button>
                                )}
                              </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              /* Chat View */
              <>
                {/* Messages */}
                <div
                  ref={messagesContainerRef}
                  onScroll={handleMessagesScroll}
                  style={{
                    flex: 1,
                    overflow: 'auto',
                    overflowX: 'hidden',
                    padding: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                    overscrollBehavior: 'contain', // Prevent scroll chaining
                  }}
                >
                  {(() => {
                    const convo = dmConversations.find(c => c.handle === paperChatView);
                    const messages = convo?.messages?.sort((a, b) => a.timestamp - b.timestamp) || [];
                    
                    if (messages.length === 0) {
                      return (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#555', fontSize: '11px' }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>💬</div>
                          <div>No messages yet</div>
                          <div style={{ marginTop: '4px', color: '#444' }}>Say hi to ${paperChatView}!</div>
                        </div>
                      );
                    }
                    
                    return (
                      <>
                        {messages.map(msg => {
                          const isMe = msg.from === handcashUser?.handle?.replace('$', '');
                          return (
                            <div 
                              key={msg.id}
                              style={{
                                alignSelf: isMe ? 'flex-end' : 'flex-start',
                                maxWidth: '80%',
                              }}
                            >
                              <div style={{
                                backgroundColor: isMe ? theme.myBubble : theme.theirBubble,
                                color: isMe ? theme.myText : theme.theirText,
                                padding: '8px 10px',
                                borderRadius: '8px',
                                borderBottomRightRadius: isMe ? '2px' : '8px',
                                borderBottomLeftRadius: isMe ? '8px' : '2px',
                                fontSize: '12px',
                                wordBreak: 'break-word',
                              }}>
                                {msg.text}
                              </div>
                              <div style={{ 
                                color: '#555', 
                                fontSize: '9px', 
                                marginTop: '2px',
                                textAlign: isMe ? 'right' : 'left',
                                paddingX: '4px',
                              }}>
                                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </>
                    );
                  })()}
                </div>
                
                {/* Message Input */}
                <div style={{ 
                  padding: '8px', 
                  borderTop: '1px solid #1a1a2e',
                  backgroundColor: '#0d0d12',
                }}>
                  {/* Typing indicator */}
                  {typingUsers[paperChatView] && (
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#888', 
                      marginBottom: '6px',
                      fontStyle: 'italic'
                    }}>
                      ${paperChatView} is typing...
                    </div>
                  )}
                  <div 
                    style={{ display: 'flex', gap: '6px' }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                  <input
                    ref={(el) => {
                      inputRef.current = el;
                      // Restore value and focus on mount
                      if (el) {
                        if (paperChatInputValue) {
                          el.value = paperChatInputValue;
                        }
                        // Restore focus if we had it
                        if (paperChatInputFocused) {
                          setTimeout(() => el.focus(), 0);
                        }
                      }
                    }}
                    type="text"
                    placeholder={wsConnected ? "Type a message..." : "Connecting..."}
                    defaultValue={paperChatInputValue}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    autoFocus
                    onFocus={(e) => { paperChatInputFocused = true; }}
                    onBlur={(e) => { paperChatInputFocused = false; }}
                    onInput={(e) => {
                      // Persist value to module-level variable
                      paperChatInputValue = e.target.value;
                      // Send typing indicator (debounced)
                      if (e.target.value.trim()) {
                        sendTypingIndicator(paperChatView);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !sendingDM && e.target.value.trim()) {
                        sendDM(paperChatView, e.target.value);
                        e.target.value = '';
                        paperChatInputValue = '';
                      }
                    }}
                    maxLength={500}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      borderRadius: '4px',
                      border: '1px solid #333',
                      backgroundColor: '#111',
                      color: '#fff',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                    }}
                  />
                  <button
                    onClick={() => {
                      const val = inputRef.current?.value?.trim();
                      if (val && !sendingDM) {
                        sendDM(paperChatView, val);
                        if (inputRef.current) inputRef.current.value = '';
                        paperChatInputValue = '';
                      }
                    }}
                    disabled={sendingDM}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: sendingDM ? '#333' : theme.primary,
                      color: sendingDM ? '#666' : theme.myText,
                      fontWeight: 'bold',
                      fontSize: '11px',
                      cursor: sendingDM ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {sendingDM ? '...' : 'Send'}
                  </button>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Footer */}
          <div style={{ 
            padding: '6px 10px', 
            borderTop: '1px solid #1a1a2e',
            backgroundColor: '#0a0a0f',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ 
                width: '6px', 
                height: '6px', 
                borderRadius: '50%', 
                backgroundColor: wsConnected ? '#00d26a' : '#ff3b69',
              }} />
              <span style={{ color: '#555', fontSize: '9px' }}>
                {wsConnected ? 'Live' : 'Connecting...'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <img src="https://i.ibb.co/23X7r7j/logo.png" alt="" style={{ width: '14px', height: '14px', borderRadius: '50%', opacity: 0.6 }} />
              <span style={{ color: '#444', fontSize: '9px' }}>HandCash</span>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ============== TIP MODAL ==============
  const TipModal = () => {
    if (!tipTarget) return null;
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}>
        <div style={{
          backgroundColor: '#0a0a0f',
          border: '2px solid #00d26a',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '320px',
          width: '90%',
          textAlign: 'center',
          position: 'relative',
          fontFamily: "'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}>
          {/* Avatar */}
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            border: tipTarget.isPremium ? '3px solid #f0b90b' : '3px solid #00d26a',
            overflow: 'hidden',
            margin: '0 auto 12px',
          }}>
            <img
              src={tipTarget.avatarUrl}
              alt={tipTarget.handle}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => { e.target.src = `https://api.dicebear.com/7.x/identicon/svg?seed=${tipTarget.handle}`; }}
            />
          </div>
          
          <h3 style={{ color: '#fff', margin: '0 0 4px', fontSize: '16px' }}>
            Tip ${tipTarget.handle}
          </h3>
          <p style={{ color: '#666', margin: '0 0 16px', fontSize: '11px' }}>
            Send real USD value from your game wallet
          </p>
          
          {/* Paper Buck Amount buttons - Game wallet tips */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {[
              { sats: 1, label: '$1' },
              { sats: 50, label: '$50' },
              { sats: 100, label: '$100' },
              { sats: 1000, label: '$1K' },
              { sats: 10000, label: '$10K' },
            ].map(({ sats, label }) => {
              const canAfford = gameWallet >= sats;
              return (
                <button
                  key={sats}
                  onClick={() => setTipAmount(sats)}
                  disabled={!canAfford}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: tipAmount === sats ? '2px solid #00d26a' : '1px solid #333',
                    backgroundColor: tipAmount === sats ? 'rgba(0,210,106,0.2)' : canAfford ? '#111' : '#0a0a0a',
                    color: tipAmount === sats ? '#00d26a' : canAfford ? '#fff' : '#444',
                    cursor: canAfford ? 'pointer' : 'not-allowed',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    opacity: canAfford ? 1 : 0.5,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          
          {/* Show selected amount details */}
          <div style={{ 
            backgroundColor: '#111', 
            border: '1px solid #1a1a2e', 
            borderRadius: '8px', 
            padding: '16px', 
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#00d26a', fontSize: '32px', fontWeight: 'bold' }}>
              {formatPB(tipAmount)}
            </div>
            <div style={{ color: '#444', fontSize: '10px', marginTop: '6px' }}>
              {formatSats(tipAmount)} sats (~{formatUsd(tipAmount)} USD)
            </div>
          </div>
          
          <div style={{ color: '#555', fontSize: '11px', marginBottom: '12px', textAlign: 'center' }}>
            Your balance: ${formatSats(gameWallet)} (~{formatUsd(gameWallet)} USD)
          </div>
          
          {/* Send button */}
          <button
            onClick={async () => {
              if (tipAmount <= 0 || tipAmount > gameWallet) return;
              setTipSending(true);
              try {
                const res = await fetch(`${API_URL}/api/tip`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                  body: JSON.stringify({ toHandle: tipTarget.handle, amount: tipAmount })
                });
                const data = await res.json();
                if (data.success) {
                  showBalanceChange(-tipAmount); // Show floating animation
                  setGameWallet(prev => prev - tipAmount);
                  setTipTarget(null);
                  setTipAmount(100); // Reset to $100 PB
                  // DISABLED: setAnnouncementToast({ type: 'tip', message: `💸 Sent ${formatPB(tipAmount)} to $${tipTarget.handle}!` });
                  // setTimeout(() => setAnnouncementToast(null), 3000);
                } else {
                  alert(data.error || 'Tip failed');
                }
              } catch (e) {
                alert('Tip failed: ' + e.message);
              }
              setTipSending(false);
            }}
            disabled={tipSending || tipAmount <= 0 || tipAmount > gameWallet}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: tipAmount > 0 && tipAmount <= gameWallet ? '#00d26a' : '#333',
              color: tipAmount > 0 && tipAmount <= gameWallet ? '#000' : '#666',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: tipAmount > 0 && tipAmount <= gameWallet ? 'pointer' : 'not-allowed',
            }}
          >
            {tipSending ? '⏳ Sending...' : `💸 Send ${formatPB(tipAmount)}`}
          </button>
          
          {tipAmount > gameWallet && (
            <div style={{ color: '#ff3b69', fontSize: '10px', marginTop: '6px' }}>
              Insufficient balance
            </div>
          )}
          
          <button
            onClick={() => { setTipTarget(null); setTipAmount(100); }}
            style={{
              width: '100%',
              marginTop: '10px',
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #333',
              backgroundColor: 'transparent',
              color: '#666',
              cursor: 'pointer',
              fontSize: '11px',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // ============== PROFILE MODAL ==============
  const ProfileModal = () => {
    if (!selectedProfile) return null;
    const user = selectedProfile;
    const stats = user.stats || {};
    const isMe = user.handle === handcashUser?.handle?.replace('$', '');
    
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }} onClick={() => setSelectedProfile(null)}>
        <div style={{
          backgroundColor: '#0a0a0f',
          border: `2px solid ${stats.totalProfit >= 0 ? '#00d26a' : '#ff4757'}`,
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '360px',
          width: '90%',
          textAlign: 'center',
          position: 'relative',
          fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace",
        }} onClick={e => e.stopPropagation()}>
          {/* Close button */}
          <button
            onClick={() => setSelectedProfile(null)}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'none',
              border: 'none',
              color: '#666',
              fontSize: '20px',
              cursor: 'pointer',
            }}
          >×</button>
          
          {/* Avatar */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            border: user.isPremium ? '4px solid #f0b90b' : `3px solid ${stats.totalProfit >= 0 ? '#00d26a' : '#ff4757'}`,
            overflow: 'hidden',
            margin: '0 auto 12px',
            boxShadow: user.isPremium ? '0 0 20px rgba(240,185,11,0.4)' : 'none',
          }}>
            <img
              src={user.avatarUrl}
              alt={user.handle}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => { e.target.src = `https://api.dicebear.com/7.x/identicon/svg?seed=${user.handle}`; }}
            />
          </div>
          
          {/* Handle & badges */}
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: user.isPremium ? '#f0b90b' : '#fff', marginBottom: '4px' }}>
            ${user.handle}
            {user.isPremium && <span style={{ marginLeft: '6px' }}>👑</span>}
          </div>
          
          {/* Badges */}
          {user.badges?.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {user.badges.map(b => (
                <span key={b.id} style={{
                  backgroundColor: '#1a1a2e',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  color: '#888',
                }}>
                  {b.icon} {b.name}
                </span>
              ))}
            </div>
          )}
          
          {/* Net P/L - Big display */}
          <div style={{
            backgroundColor: stats.totalProfit >= 0 ? '#0a1a0a' : '#1a0a0a',
            border: `1px solid ${stats.totalProfit >= 0 ? '#00d26a33' : '#ff475733'}`,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
          }}>
            <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>NET PROFIT/LOSS</div>
            <div style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: stats.totalProfit >= 0 ? '#00d26a' : '#ff4757',
            }}>
              {stats.totalProfit >= 0 ? '+' : ''}{formatPB(stats.totalProfit || 0)}
            </div>
          </div>
          
          {/* Stats grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px',
            marginBottom: '16px',
          }}>
            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px' }}>
              <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{stats.winRate || 0}%</div>
              <div style={{ color: '#666', fontSize: '10px' }}>Win Rate</div>
            </div>
            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px' }}>
              <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{stats.trades || 0}</div>
              <div style={{ color: '#666', fontSize: '10px' }}>Trades</div>
            </div>
            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px' }}>
              <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>{stats.winStreak || 0}</div>
              <div style={{ color: '#666', fontSize: '10px' }}>Best Streak</div>
            </div>
          </div>
          
          {/* More stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
            marginBottom: '16px',
            fontSize: '12px',
          }}>
            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '8px', textAlign: 'left' }}>
              <span style={{ color: '#666' }}>🏆 Biggest Win: </span>
              <span style={{ color: '#00d26a' }}>+{formatPB(stats.biggestWin || 0)}</span>
            </div>
            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '8px', textAlign: 'left' }}>
              <span style={{ color: '#666' }}>💀 Biggest Loss: </span>
              <span style={{ color: '#ff4757' }}>{formatPB(stats.biggestLoss || 0)}</span>
            </div>
            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '8px', textAlign: 'left' }}>
              <span style={{ color: '#666' }}>✅ Wins: </span>
              <span style={{ color: '#00d26a' }}>{stats.wins || 0}</span>
            </div>
            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '8px', textAlign: 'left' }}>
              <span style={{ color: '#666' }}>☠️ Rugged: </span>
              <span style={{ color: '#ff4757' }}>{stats.rugs || 0}</span>
            </div>
          </div>
          
          {/* Current streak */}
          {stats.currentStreak !== 0 && (
            <div style={{
              backgroundColor: stats.currentStreak > 0 ? '#1a2a0a' : '#2a1a1a',
              border: `1px solid ${stats.currentStreak > 0 ? '#00d26a33' : '#ff475733'}`,
              borderRadius: '8px',
              padding: '8px',
              marginBottom: '16px',
              fontSize: '12px',
            }}>
              {stats.currentStreak > 0 ? '🔥' : '🧊'} Currently on a {Math.abs(stats.currentStreak)} {stats.currentStreak > 0 ? 'win' : 'loss'} streak
            </div>
          )}
          
          {/* 🎯 COMMUNITY MILESTONE - Fee discount progress */}
          {isMe && (
            <div style={{
              backgroundColor: globalStats.onChainTransactions >= 10001 ? 'rgba(0,210,106,0.1)' : 'rgba(138,43,226,0.1)',
              border: `1px solid ${globalStats.onChainTransactions >= 10001 ? '#00d26a33' : '#8a2be233'}`,
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
            }}>
              <div style={{ color: globalStats.onChainTransactions >= 10001 ? '#00d26a' : '#a855f7', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>
                {globalStats.onChainTransactions >= 10001 ? '🎉 FEE DISCOUNT ACTIVE!' : '🎯 COMMUNITY MILESTONE'}
              </div>
              {globalStats.onChainTransactions >= 10001 ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>0.9¢ fees unlocked!</div>
                  <div style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>10% discount on all trades & bets</div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ color: '#888', fontSize: '10px' }}>Progress to fee discount</span>
                    <span style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>
                      {((globalStats.onChainTransactions || 0) / 10001 * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div style={{ 
                    backgroundColor: '#1a1a2e', 
                    borderRadius: '4px', 
                    height: '8px', 
                    overflow: 'hidden',
                    marginBottom: '8px'
                  }}>
                    <div style={{ 
                      width: `${Math.min(100, (globalStats.onChainTransactions || 0) / 10001 * 100)}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #a855f7, #00d26a)',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                    <span style={{ color: '#a855f7' }}>⛓️ {(globalStats.onChainTransactions || 0).toLocaleString()}</span>
                    <span style={{ color: '#666' }}>{Math.max(0, 10001 - (globalStats.onChainTransactions || 0)).toLocaleString()} to go</span>
                    <span style={{ color: '#00d26a' }}>10,001 🎯</span>
                  </div>
                  <div style={{ color: '#555', fontSize: '9px', marginTop: '8px', textAlign: 'center' }}>
                    At 10K txs: 1¢ fees → 0.9¢ (trade, rush, prophet)
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* 💰 RAKE ROI TRACKER - Only show for own profile */}
          {isMe && totalRakePaid > 0 && (
            <div style={{
              backgroundColor: 'rgba(240,185,11,0.1)',
              border: '1px solid #f0b90b33',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
            }}>
              <div style={{ color: '#f0b90b', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>
                💰 FEE ROI TRACKER
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                <div>
                  <div style={{ color: '#666' }}>Fees Paid</div>
                  <div style={{ color: '#ff6b6b', fontWeight: 'bold' }}>{totalRakePaid} sats</div>
                  <div style={{ color: '#555', fontSize: '9px' }}>~${(totalRakePaid / 5000).toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ color: '#666' }}>Net Profit</div>
                  <div style={{ color: stats.totalProfit >= 0 ? '#00d26a' : '#ff4757', fontWeight: 'bold' }}>
                    {stats.totalProfit >= 0 ? '+' : ''}{formatPB(stats.totalProfit || 0)}
                  </div>
                </div>
              </div>
              {stats.totalProfit > 0 && totalRakePaid > 0 && (
                <div style={{ 
                  marginTop: '8px', 
                  paddingTop: '8px', 
                  borderTop: '1px solid #333',
                  color: '#00d26a',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  🚀 ROI: +{((stats.totalProfit / totalRakePaid) * 100).toFixed(0)}%
                </div>
              )}
              <div style={{ color: '#555', fontSize: '9px', marginTop: '6px', textAlign: 'center' }}>
                {rakeTransactions} trades • {globalStats.onChainTransactions >= 10001 ? '0.9¢' : '1¢'} per trade
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          {!isMe && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {/* Add/Remove Friend Button */}
              {friends.some(f => (f.handle || '').replace('$', '').toLowerCase() === (user.handle || '').replace('$', '').toLowerCase()) ? (
                <button
                  onClick={async () => {
                    await removeFriend(user.handle);
                    setSelectedProfile(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #ff3b69',
                    backgroundColor: 'transparent',
                    color: '#ff3b69',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    minWidth: '100px',
                  }}
                >
                  💔 Unfriend
                </button>
              ) : (
                <button
                  onClick={async () => {
                    await addFriend(user.handle);
                    setSelectedProfile(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#3498db',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    minWidth: '100px',
                  }}
                >
                  💗 Add Friend
                </button>
              )}
              <button
                onClick={() => {
                  setSelectedProfile(null);
                  setTipTarget(user);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#00d26a',
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  minWidth: '100px',
                }}
              >
                💸 Tip
              </button>
              <button
                onClick={() => {
                  const dmUser = user.handle.replace(/^\$/, '');
                  // Close profile and open PaperChat immediately
                  setSelectedProfile(null);
                  setSelectedDMUser(dmUser);
                  setPaperChatView(dmUser);
                  setShowPaperChat(true);
                  // Fetch in background (non-blocking)
                  fetchDMs();
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#9b59b6',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  minWidth: '100px',
                }}
              >
                💬 DM
              </button>
            </div>
          )}
          
          {isMe && (
            <button
              onClick={() => setSelectedProfile(null)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #333',
                backgroundColor: 'transparent',
                color: '#888',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  };

  // ============== RUG DETECTED MODAL ==============
  const RugModal = () => {
    if (!showRugModal) return null;
    
    const isWithdraw = rugModalMessage === 'withdraw';
    const isServerDetected = rugModalMessage === 'server';
    
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isMobile ? '12px' : '20px',
        fontFamily: "'Space Grotesk', sans-serif",
      }}>
        <div style={{
          backgroundColor: '#0a0a0f',
          border: '2px solid #ff3b69',
          borderRadius: isMobile ? '16px' : '20px',
          padding: isMobile ? '20px' : '32px',
          maxWidth: '450px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          textAlign: 'center',
          boxShadow: '0 0 60px rgba(255,59,105,0.3)',
          animation: 'modalSlideIn 0.3s ease-out',
          fontFamily: "'Space Grotesk', sans-serif",
        }}>
          {/* Alert Icon */}
          <div style={{
            width: isMobile ? '60px' : '80px',
            height: isMobile ? '60px' : '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,59,105,0.1)',
            border: '2px solid #ff3b69',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}>
            <span style={{ fontSize: isMobile ? '28px' : '40px' }}>🚨</span>
          </div>
          
          {/* Title */}
          <h2 style={{
            color: '#ff3b69',
            fontSize: isMobile ? '18px' : '24px',
            fontWeight: 'bold',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: isMobile ? '1px' : '2px',
          }}>
            Cloudflare Rug Detected!
          </h2>
          
          {/* Subtitle */}
          <p style={{
            color: '#888',
            fontSize: isMobile ? '12px' : '14px',
            marginBottom: '24px',
          }}>
            {isServerDetected 
              ? 'HandCash payment services are currently down'
              : 'HandCash payment services just went down'}
          </p>
          
          {/* Status Box */}
          <div style={{
            backgroundColor: 'rgba(0,210,106,0.1)',
            border: '1px solid rgba(0,210,106,0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
          }}>
            <p style={{ color: '#00d26a', fontSize: '14px', margin: 0, fontWeight: 'bold' }}>
              🛡️ Your Paper Bucks are safe!
            </p>
            <p style={{ color: '#888', fontSize: '12px', margin: '8px 0 0 0' }}>
              {isWithdraw 
                ? 'Your balance is unchanged. No sats were deducted.'
                : isServerDetected
                  ? 'Other players reported payment issues. Your funds are protected!'
                  : 'Stored in cold storage. You can still trade and chat!'}
            </p>
          </div>
          
          {/* What's affected */}
          <div style={{
            backgroundColor: 'rgba(255,59,105,0.1)',
            border: '1px solid rgba(255,59,105,0.2)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            textAlign: 'left',
          }}>
            <p style={{ color: '#ff3b69', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
              ⚠️ Temporarily Unavailable:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ color: '#888', fontSize: '12px' }}>• Deposits (BSV → Paper Bucks)</span>
              <span style={{ color: '#888', fontSize: '12px' }}>• Withdrawals (Paper Bucks → BSV)</span>
              <span style={{ color: '#888', fontSize: '12px' }}>• Token Launching ($0.05 fee)</span>
            </div>
          </div>
          
          {/* What still works */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            marginBottom: '24px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '24px' }}>✅</span>
              <p style={{ color: '#00d26a', fontSize: '11px', margin: '4px 0 0 0' }}>Trading</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '24px' }}>✅</span>
              <p style={{ color: '#00d26a', fontSize: '11px', margin: '4px 0 0 0' }}>Chat</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '24px' }}>✅</span>
              <p style={{ color: '#00d26a', fontSize: '11px', margin: '4px 0 0 0' }}>Tipping</p>
            </div>
          </div>
          
          {/* OK Button */}
          <button
            onClick={async () => {
              // 🐦 CANARY: Tell server to enter degraded mode
              const handle = handcashUser?.handle?.toLowerCase()?.replace('$', '');
              const isCanaryUser = handle === 'blackwolf';
              if (isCanaryUser && API_URL) {
                console.log('🐦 Canary confirming degraded mode...');
                
                // Try up to 3 times with delays
                for (let attempt = 1; attempt <= 3; attempt++) {
                  try {
                    if (attempt > 1) {
                      console.log(`🐦 Retry attempt ${attempt}/3...`);
                      await new Promise(r => setTimeout(r, attempt * 1000));
                    }
                    
                    const res = await fetch(`${API_URL}/api/canary-trigger`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json',
                        'X-Session-Id': sessionId || ''
                      },
                      body: JSON.stringify({ 
                        type: 'manual', 
                        reason: 'Canary confirmed rug modal',
                        canaryHandle: 'blackwolf'  // Fallback if session was lost
                      })
                    });
                    
                    if (res.ok) {
                      const data = await res.json();
                      console.log('🐦 Server now in degraded mode:', data);
                      setHandcashDown(true); // Set local state!
                      break;
                    } else {
                      console.log(`🐦 Server returned ${res.status}`);
                    }
                  } catch (e) {
                    console.log(`🐦 Attempt ${attempt} failed:`, e.message);
                  }
                }
              } else {
                // Non-canary user just closes modal
                console.log('👤 Non-canary user closing modal');
              }
              setShowRugModal(false);
            }}
            style={{
              width: '100%',
              padding: '14px 24px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: '#ff3b69',
              color: '#fff',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: "'Space Grotesk', sans-serif",
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#ff5a84';
              e.target.style.transform = 'scale(1.02)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#ff3b69';
              e.target.style.transform = 'scale(1)';
            }}
          >
            Got it - Keep Trading! 🐸
          </button>
          
          {/* Pro tip */}
          <p style={{ color: '#555', fontSize: '11px', marginTop: '16px' }}>
            💡 Ask friends to tip you Paper Bucks while deposits are down!
          </p>
        </div>
        
        <style>{`
          @keyframes modalSlideIn {
            from { opacity: 0; transform: scale(0.9) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>
      </div>
    );
  };

  // ============== ASCII SCANNER COMPONENT (isolated to prevent parent re-render) ==============
  // Isolated ASCII Scanner component - only animates when visible
  const AsciiScanner = React.memo(({ isVisible }) => {
    const [scanHash, setScanHash] = React.useState('a7f3b2c8d9e1f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9');
    const [scanLine, setScanLine] = React.useState(0);
    
    const scanLines = [
      '█▓▒░ SCANNING FOR CLOUDFLARE RUG ░▒▓█',
      '█▓▒░ CHECKING HANDCASH API ░▒▓█',
      '█▓▒░ SYNCING WITH PLAYERS ░▒▓█',
      '█▓▒░ CLOUDFLARE RUG DETECTION ACTIVE ░▒▓█',
    ];
    
    // Only animate when modal is visible
    React.useEffect(() => {
      if (!isVisible) return;
      
      const chars = '0123456789abcdef';
      const interval = setInterval(() => {
        let hash = '';
        for (let i = 0; i < 64; i++) {
          hash += chars[Math.floor(Math.random() * chars.length)];
        }
        setScanHash(hash);
        setScanLine(prev => (prev + 1) % 4);
      }, 400);
      return () => clearInterval(interval);
    }, [isVisible]);
    
    return (
      <div style={{
        backgroundColor: '#000',
        border: '1px solid #00d26a',
        borderRadius: '8px',
        padding: '10px',
        marginBottom: '16px',
        fontFamily: 'monospace',
        overflow: 'hidden',
      }}>
        <div style={{ 
          color: '#00d26a', 
          fontSize: '9px', 
          marginBottom: '6px',
        }}>
          {scanLines[scanLine]}
        </div>
        <div style={{ 
          color: '#0f0', 
          fontSize: '8px', 
          wordBreak: 'break-all',
          opacity: 0.7,
          lineHeight: '1.3',
        }}>
          <span style={{ color: '#00d26a' }}>0x</span>{scanHash.slice(0, 32)}
          <br />
          <span style={{ color: '#00d26a' }}>0x</span>{scanHash.slice(32, 64)}
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '6px',
          fontSize: '8px',
          color: '#555',
        }}>
          <span>BLOCK: {Math.floor(Date.now() / 1000)}</span>
          <span style={{ color: '#00d26a', animation: 'pulse 1s infinite' }}>● LIVE</span>
        </div>
      </div>
    );
  });

  // ============== RENDER: CLOUDFLARE RUG SCANNER ==============
  // Show scanner on EVERY page load - it's educational!
  // Only skip if returning from OAuth redirect (authToken in URL)
  const hasAuthTokenInUrl = typeof window !== 'undefined' && 
    (new URLSearchParams(window.location.search).get('authToken') || 
     (window.location.hash && window.location.hash.includes('authToken')));
  
  // Show scanner unless: user clicked through OR returning from OAuth
  if (!rugScannerDismissed && !hasAuthTokenInUrl) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#030305',
        backgroundImage: 'url(/bootscreen.jpeg)',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        fontFamily: "'Space Grotesk', sans-serif",
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        paddingTop: isMobile ? '10px' : '20px',
        paddingBottom: isMobile ? '60px' : '40px',
        zIndex: 9999,
      }}>

        {/* Main Content */}
        <div style={{
          textAlign: 'center',
          maxWidth: '600px',
          padding: isMobile ? '20px' : '40px',
          paddingTop: isMobile ? '80px' : '160px',
          position: 'relative',
          zIndex: 10,
        }}>
          
          {/* Scanner Animation */}
          <div style={{ 
            position: 'relative',
            width: isMobile ? '70px' : '120px',
            height: isMobile ? '70px' : '120px',
            margin: isMobile ? '0 auto 12px' : '0 auto 30px',
          }}>
            {/* Radar circle background */}
            <div style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `2px solid ${
                rugScanning ? '#f0b90b' :
                rugDetected ? '#ff3b69' :
                '#cc00ff'
              }`,
              opacity: 0.3,
              transition: 'border-color 0.5s ease',
            }} />
            
            {/* Scanning sweep */}
            {rugScanning && (
              <div style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: 'conic-gradient(from 0deg, transparent 0deg, rgba(240,185,11,0.4) 60deg, transparent 120deg)',
                animation: 'radarSweep 2s linear infinite',
              }} />
            )}
            
            {/* Center icon */}
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '28px' : '48px',
              animation: rugScanning ? 'pulse 1.5s ease-in-out infinite' : 'none',
            }}>
              {rugScanning ? '🔍' : rugDetected ? '🚨' : '✅'}
            </div>
            
            {/* Pulse rings */}
            {!rugScanning && (
              <>
                <div style={{
                  position: 'absolute',
                  inset: '-10px',
                  borderRadius: '50%',
                  border: `2px solid ${rugDetected ? '#ff3b69' : '#00d26a'}`,
                  animation: 'pulseRing 2s ease-out infinite',
                  opacity: 0,
                }} />
                <div style={{
                  position: 'absolute',
                  inset: '-10px',
                  borderRadius: '50%',
                  border: `2px solid ${rugDetected ? '#ff3b69' : '#00d26a'}`,
                  animation: 'pulseRing 2s ease-out infinite 0.5s',
                  opacity: 0,
                }} />
              </>
            )}
          </div>
          
          {/* Status Box */}
          <div style={{
            backgroundColor: 'rgba(10,10,15,0.9)',
            border: `1px solid ${
              rugScanning ? '#333' : 
              rugDetected ? 'rgba(255,59,105,0.3)' : 
              'rgba(0,210,106,0.3)'
            }`,
            borderRadius: isMobile ? '12px' : '16px',
            padding: isMobile ? '14px' : '28px',
            marginBottom: isMobile ? '12px' : '24px',
            boxShadow: `
              0 0 30px ${
                rugScanning ? 'rgba(240,185,11,0.1)' : 
                rugDetected ? 'rgba(255,59,105,0.1)' : 
                'rgba(0,210,106,0.1)'
              },
              inset 0 0 30px rgba(0,0,0,0.3)
            `,
            maxWidth: '450px',
            width: '92%',
            margin: isMobile ? '0 auto 12px' : '0 auto 24px',
            transition: 'all 0.5s ease',
          }}>
            {/* Status indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '10px',
              marginBottom: isMobile ? '10px' : '16px',
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: rugScanning ? '#f0b90b' : rugDetected ? '#ff3b69' : '#cc00ff',
                boxShadow: `0 0 10px ${rugScanning ? '#f0b90b' : rugDetected ? '#ff3b69' : '#cc00ff'}`,
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
              <span style={{
                color: rugScanning ? '#f0b90b' : rugDetected ? '#ff3b69' : '#cc00ff',
                fontSize: '11px',
                fontWeight: 'bold',
                letterSpacing: '2px',
                textTransform: 'uppercase',
              }}>
                {rugScanning ? 'SCANNING...' : rugDetected ? 'RUG DETECTED' : '✓ SCAN PASSED'}
              </span>
            </div>
            
            {/* Main title */}
            <h2 style={{ 
              color: '#fff', 
              fontSize: isMobile ? '16px' : '20px', 
              marginBottom: isMobile ? '10px' : '12px',
              fontWeight: '700',
            }}>
              {rugScanning 
                ? '🔍 System Startup Sequence' 
                : rugDetected 
                  ? '🚨 CLOUDFLARE RUG DETECTED!' 
                  : '✅ All Systems Operational!'}
            </h2>
            
            {/* Educational content */}
            <div style={{ 
              color: '#888', 
              fontSize: isMobile ? '11px' : '13px', 
              lineHeight: isMobile ? '1.5' : '1.7',
              marginBottom: isMobile ? '10px' : '20px',
              textAlign: 'left',
              padding: isMobile ? '0 4px' : '0 8px',
            }}>
              {rugScanning ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ 
                    color: '#00d26a', 
                    fontSize: isMobile ? '12px' : '14px',
                    marginBottom: '16px',
                    minHeight: '20px',
                    transition: 'opacity 0.3s',
                  }}>
                    {scanMessage}
                  </p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '12px',
                  }}>
                    {[0, 1, 2, 3, 4].map(i => (
                      <div 
                        key={i}
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#00d26a',
                          animation: `pulse 1.5s ease-in-out ${i * 0.15}s infinite`,
                          opacity: 0.3 + (i * 0.15),
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : rugDetected ? (
                <>
                  <p style={{ marginBottom: isMobile ? '8px' : '12px' }}>
                    <strong style={{ color: '#ff3b69' }}>What's a Cloudflare Rug?</strong><br/>
                    When centralized services like Cloudflare go down, they can halt trading across 
                    entire crypto ecosystems. Wallets, exchanges, and DeFi platforms that depend on 
                    these services become unusable.
                  </p>
                  <p style={{ marginBottom: isMobile ? '8px' : '12px' }}>
                    <strong style={{ color: '#f0b90b' }}>Right now:</strong> HandCash wallet services 
                    are experiencing issues. You can't deposit or withdraw real BSV.
                  </p>
                  <p style={{ 
                    color: '#00d26a', 
                    backgroundColor: 'rgba(0,210,106,0.1)', 
                    padding: isMobile ? '8px 10px' : '10px 12px', 
                    borderRadius: '8px',
                    border: '1px solid rgba(0,210,106,0.2)',
                    fontSize: isMobile ? '11px' : '13px',
                  }}>
                    🛡️ <strong>But Paper Hands is rug-resistant!</strong><br/>
                    Your Paper Bucks are in cold storage. You can still trade and chat with other players - only BSV deposits/withdrawals are affected!
                  </p>
                  <p style={{ 
                    color: '#888', 
                    fontSize: isMobile ? '10px' : '12px',
                    textAlign: 'center',
                    marginTop: isMobile ? '8px' : '12px',
                  }}>
                    💡 <strong style={{ color: '#00d26a' }}>Pro tip:</strong> Add friends so they can tip you Paper Bucks if you're broke!
                  </p>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: '12px' }}>
                    <strong style={{ color: '#cc66ff' }}>What's a Cloudflare Rug?</strong><br/>
                    When centralized services like Cloudflare go down, they can halt trading across
                    entire crypto ecosystems. Today, the markets are operating normally.
                  </p>
                  <p style={{
                    color: '#cc66ff',
                    backgroundColor: 'rgba(204,102,255,0.1)',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(204,102,255,0.2)',
                    marginBottom: '12px',
                  }}>
                    🛡️ <strong>Paper Hands is rug-resistant!</strong><br/>
                    Your Paper Bucks are in cold storage. Even if a Cloudflare Rug hits,
                    you can still trade and chat - only BSV deposits/withdrawals are affected!
                  </p>
                  <p style={{
                    color: '#aaa',
                    backgroundColor: 'rgba(150,100,200,0.1)',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(150,100,200,0.2)',
                    marginBottom: '12px',
                  }}>
                    📚 <strong style={{ color: '#bb88dd' }}>Learning moment:</strong> In real crypto,
                    infrastructure outages can lock you out of your funds. Paper Hands teaches you
                    to spot rugs without the risk!
                  </p>
                  <p style={{
                    color: '#888',
                    fontSize: '12px',
                    textAlign: 'center',
                  }}>
                    💡 <strong style={{ color: '#cc66ff' }}>Pro tip:</strong> Add friends so they can tip you Paper Bucks if you go broke!
                  </p>
                </>
              )}
            </div>
            
            {/* Action buttons */}
            {!rugScanning && (
              <>
                {rugDetected ? (
                  <>
                    {/* Rescan button */}
                    <button
                      onClick={rescanForRug}
                      style={{
                        padding: isMobile ? '10px 20px' : '12px 24px',
                        borderRadius: '8px',
                        border: '1px solid #f0b90b',
                        backgroundColor: 'transparent',
                        color: '#f0b90b',
                        fontSize: isMobile ? '12px' : '13px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        marginBottom: isMobile ? '8px' : '12px',
                        width: '100%',
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#f0b90b';
                        e.target.style.color = '#000';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#f0b90b';
                      }}
                    >
                      🔄 Rescan
                    </button>
                    
                    {/* Play anyway button */}
                    <button
                      onClick={playWithRugDetected}
                      style={{
                        padding: isMobile ? '12px 24px' : '14px 28px',
                        borderRadius: '10px',
                        border: 'none',
                        backgroundColor: '#00d26a',
                        color: '#000',
                        fontSize: isMobile ? '14px' : '15px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        width: '100%',
                        boxShadow: '0 0 20px rgba(0,210,106,0.3)',
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.boxShadow = '0 0 30px rgba(0,210,106,0.5)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 0 20px rgba(0,210,106,0.3)';
                      }}
                    >
                      🎮 Play Anyway
                    </button>
                    <p style={{ color: '#666', fontSize: isMobile ? '10px' : '11px', marginTop: isMobile ? '8px' : '10px' }}>
                      {localStorage.getItem('paperHands_privateKey')
                        ? 'Your session will be restored. Deposit & Withdraw disabled.'
                        : 'Try connecting - auth may still work!'}
                    </p>
                  </>
                ) : (
                  <>
                    {/* Connect button - all clear */}
                    <button
                      onClick={proceedToConnect}
                      style={{
                        padding: '16px 32px',
                        borderRadius: '12px',
                        border: 'none',
                        backgroundColor: '#cc00ff',
                        color: '#fff',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        width: '100%',
                        boxShadow: '0 0 20px rgba(204,0,255,0.4)',
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.boxShadow = '0 0 30px rgba(204,0,255,0.6)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 0 20px rgba(204,0,255,0.4)';
                      }}
                    >
                      🚀 Enter the Game
                    </button>
                    <p style={{ color: '#666', fontSize: '11px', marginTop: '10px' }}>
                      ✓ Scan complete. No infrastructure issues detected.
                    </p>
                  </>
                )}
              </>
            )}
          </div>
          
          {/* Footer */}
          <div style={{ color: '#333', fontSize: '11px' }}>
            <p>A game by <span style={{ color: '#f0b90b' }}>zer0CooL Productions</span></p>
          </div>
        </div>
        
        {/* CSS Animations */}
        <style>{`
          @keyframes radarSweep {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
          }
          @keyframes slideUp {
            0% { opacity: 0; transform: translateY(30px) scale(0.95); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes pulseRing {
            0% { transform: scale(1); opacity: 0.6; }
            100% { transform: scale(1.5); opacity: 0; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0) translateX(0); }
            25% { transform: translateY(-20px) translateX(10px); }
            50% { transform: translateY(0) translateX(20px); }
            75% { transform: translateY(20px) translateX(10px); }
          }
          @keyframes gridMove {
            0% { transform: translate(0, 0); }
            100% { transform: translate(50px, 50px); }
          }
        `}</style>
      </div>
    );
  }

  // ============== RENDER: CONNECT SCREEN ==============
  if (view === 'connect') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#030305', backgroundImage: 'url(/bootscreen.jpeg)', backgroundPosition: 'top center', backgroundRepeat: 'no-repeat', backgroundSize: '100% auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'SF Mono', monospace", padding: isMobile ? '16px' : '0' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', width: '100%', padding: isMobile ? '20px' : '40px', paddingTop: isMobile ? '120px' : '200px' }}>
          
          <div style={{ backgroundColor: '#0d0d12', border: '1px solid #1a1a2e', borderRadius: isMobile ? '12px' : '16px', padding: isMobile ? '20px' : '32px' }}>
            <div style={{ marginBottom: '16px' }}>
              <img
                src="https://i.ibb.co/23X7r7j/logo.png"
                alt="HandCash"
                style={{
                  width: isMobile ? '48px' : '64px',
                  height: isMobile ? '48px' : '64px',
                  borderRadius: '50%',
                  margin: '0 auto',
                  display: 'block',
                  boxShadow: handcashDown ? 'none' : '0 0 30px rgba(204,0,255,0.4)',
                  opacity: handcashDown ? 0.5 : 1,
                  filter: handcashDown ? 'grayscale(50%)' : 'none',
                }}
              />
            </div>
            <h2 style={{ color: '#fff', fontSize: isMobile ? '16px' : '20px', marginBottom: '8px' }}>Connect with HandCash</h2>
            <p style={{ color: '#666', fontSize: isMobile ? '11px' : '13px', marginBottom: isMobile ? '16px' : '24px' }}>Secure BSV wallet for instant deposits</p>
            
            {handcashDown ? (
              <>
                <div style={{
                  backgroundColor: 'rgba(233,30,99,0.1)',
                  border: '1px solid rgba(233,30,99,0.3)',
                  borderRadius: '10px',
                  padding: isMobile ? '12px' : '16px',
                  marginBottom: '16px',
                  textAlign: 'center',
                }}>
                  <div style={{ color: '#e91e63', fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                    ⚠️ HandCash Degraded Mode
                  </div>
                  <p style={{ color: '#888', fontSize: isMobile ? '11px' : '12px', margin: 0 }}>
                    HandCash may be experiencing issues.<br/>
                    Login may still work - deposits/withdrawals might not.
                  </p>
                </div>
                {/* Still allow login attempts in degraded mode */}
                <button onClick={connectHandCash} disabled={handcashLoading} style={{ width: '100%', padding: isMobile ? '14px' : '16px', borderRadius: '12px', border: 'none', cursor: 'pointer', backgroundColor: '#cc00ff', color: '#fff', fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '12px', boxShadow: '0 0 20px rgba(204,0,255,0.4)' }}>
                  {handcashLoading ? '⏳ Connecting...' : (
                    <>
                      <img src="https://i.ibb.co/23X7r7j/logo.png" alt="" style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px', borderRadius: '50%' }} />
                      {isMobile ? 'Try Anyway' : 'Try Connecting Anyway'}
                    </>
                  )}
                </button>
                <button 
                  onClick={retryHandCashConnection}
                  disabled={retryingConnection}
                  style={{ 
                    width: '100%', 
                    padding: isMobile ? '10px' : '12px', 
                    borderRadius: '12px', 
                    border: '1px solid #333', 
                    cursor: retryingConnection ? 'not-allowed' : 'pointer', 
                    backgroundColor: 'transparent', 
                    color: '#888', 
                    fontSize: isMobile ? '11px' : '12px', 
                    fontWeight: 'bold', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px' 
                  }}
                >
                  {retryingConnection ? '⟳ Checking...' : (isMobile ? '🔄 Check Status' : '🔄 Check if HandCash is back')}
                </button>
              </>
            ) : (
              <button onClick={connectHandCash} disabled={handcashLoading} style={{ width: '100%', padding: isMobile ? '14px' : '16px', borderRadius: '12px', border: 'none', cursor: 'pointer', backgroundColor: '#cc00ff', color: '#fff', fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 0 20px rgba(204,0,255,0.4)' }}>
                {handcashLoading ? '⏳ Connecting...' : (
                  <>
                    <img src="https://i.ibb.co/23X7r7j/logo.png" alt="" style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px', borderRadius: '50%' }} />
                    Connect HandCash
                  </>
                )}
              </button>
            )}
          </div>
          
          <div style={{ marginTop: isMobile ? '20px' : '32px', color: '#555', fontSize: isMobile ? '10px' : '11px' }}>
            <p>Provably fair on BSV blockchain ⛓️</p>
            <p style={{ marginTop: '12px', color: '#444', fontSize: isMobile ? '9px' : '10px' }}>A game by <span style={{ color: '#cc66ff' }}>zerOCooL Productions</span></p>
          </div>
        </div>
      </div>
    );
  }

  // ============== RENDER: WHITEPAPER ==============
  // Whitepaper Section Component (must be defined before use)
  const Section = ({ title, children }) => (
    <div style={{ marginBottom: '40px' }}>
      <h3 style={{ fontSize: '24px', fontWeight: '600', color: '#fff', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #1a1a2e' }}>{title}</h3>
      <div style={{ color: '#aaa', fontSize: '15px', lineHeight: '1.8' }}>{children}</div>
    </div>
  );

  if (view === 'whitepaper') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#050508', color: '#fff', fontFamily: "'Space Grotesk', sans-serif" }}>
        {/* Header */}
        <div style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#050508', 
          borderBottom: '1px solid #1a1a2e',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 100
        }}>
          <button 
            onClick={() => setView('connect')} 
            style={{ 
              padding: '8px 16px', 
              borderRadius: '6px', 
              border: '1px solid #333', 
              backgroundColor: 'transparent', 
              color: '#888', 
              cursor: 'pointer' 
            }}
          >
            ← Back
          </button>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            background: 'linear-gradient(135deg, #f0b90b, #ff6b00)', 
            WebkitBackgroundClip: 'text', 
            WebkitTextFillColor: 'transparent' 
          }}>
            PAPER HANDS
          </h1>
          <div style={{ width: '80px' }}></div>
        </div>
        
        {/* Whitepaper Content */}
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 24px' }}>
          {/* Hero */}
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{ fontSize: '80px', marginBottom: '24px' }}>🐸</div>
            <h1 style={{ fontSize: '48px', fontWeight: '700', background: 'linear-gradient(135deg, #f0b90b, #ff6b00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '16px' }}>
              PAPER HANDS
            </h1>
            <p style={{ fontSize: '20px', color: '#888', marginBottom: '24px' }}>Exit Liquidity Simulator</p>
            <div style={{ display: 'inline-block', padding: '8px 20px', backgroundColor: '#0d0d12', border: '1px solid #1a1a2e', borderRadius: '20px', fontSize: '14px', color: '#f0b90b' }}>
              Whitepaper v2.2
            </div>
          </div>
          
          {/* Abstract */}
          <Section title="Abstract">
            <p>Paper Hands is an <strong style={{ color: '#f0b90b' }}>educational trading simulator</strong> that teaches players to recognize rug pull patterns and market manipulation tactics used in memecoin markets.</p>
            <p style={{ marginTop: '12px' }}>Using BSV micropayments via HandCash, players can learn these lessons for <strong style={{ color: '#00d26a' }}>pennies instead of losing real money</strong>. Think of it as a flight simulator for crypto - practice spotting danger without crashing your portfolio.</p>
          </Section>
          
          {/* The Problem */}
          <Section title="The Problem">
            <p>Every day, thousands of new traders lose money to memecoin rug pulls. The patterns are predictable, yet people fall for them repeatedly because they've never seen them before:</p>
            <ul style={{ marginTop: '12px', marginLeft: '24px', color: '#888' }}>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#ff3b69' }}>Hard Rugs:</strong> Instant liquidity pulls with zero warning</li>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#ff6b00' }}>Trap Rugs:</strong> Coordinated pumps followed by insider dumps</li>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#f0b90b' }}>Slow Rugs:</strong> Gradual bleeding while "community" copes</li>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#ff0040' }}>Honeypots:</strong> Tokens where selling is impossible</li>
            </ul>
          </Section>
          
          {/* The Solution */}
          <Section title="The Solution: Learn for Pennies">
            <p>Paper Hands creates a <strong>safe educational environment</strong> to experience these patterns firsthand. For less than $1 worth of BSV, players can:</p>
            <ul style={{ marginTop: '12px', marginLeft: '24px', color: '#888' }}>
              <li style={{ marginBottom: '8px' }}>Recognize market manipulation in real-time</li>
              <li style={{ marginBottom: '8px' }}>Feel the FOMO and fear that leads to bad decisions</li>
              <li style={{ marginBottom: '8px' }}>Practice timing entries and exits</li>
              <li style={{ marginBottom: '8px' }}>Learn to read fake hype vs real momentum</li>
            </ul>
            <p style={{ marginTop: '12px', color: '#00d26a' }}>Better to lose $1 Paper Buck here than $1000 on a real rug pull.</p>
          </Section>
          
          {/* Game Mechanics */}
          <Section title="Game Mechanics">
            <h4 style={{ color: '#f0b90b', marginBottom: '12px' }}>Token Lifecycle</h4>
            <p>Each token follows a predetermined fate (unknown to players):</p>
            <ul style={{ marginTop: '12px', marginLeft: '24px', color: '#888' }}>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#00d26a' }}>MOON (20%):</strong> Token pumps 2-3.5x before completing</li>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#00ff88' }}>SUPERMOON (10%):</strong> Rare 5-10x opportunities</li>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#ff3b69' }}>RUG (70%):</strong> Various rug patterns that wipe holders</li>
            </ul>
            
            <h4 style={{ color: '#f0b90b', marginTop: '24px', marginBottom: '12px' }}>Manipulation Engine</h4>
            <p>Prices follow realistic market phases:</p>
            <ul style={{ marginTop: '12px', marginLeft: '24px', color: '#888' }}>
              <li style={{ marginBottom: '8px' }}><strong>ACCUMULATION:</strong> Quiet buying, low volatility</li>
              <li style={{ marginBottom: '8px' }}><strong>MARKUP:</strong> Price increases, FOMO builds</li>
              <li style={{ marginBottom: '8px' }}><strong>DISTRIBUTION:</strong> Smart money exits, bagholders created</li>
            </ul>
          </Section>
          
          {/* Premium Features */}
          <Section title="Premium Features">
            <p>Deposit $25+ to unlock:</p>
            <ul style={{ marginTop: '12px', marginLeft: '24px', color: '#888' }}>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#f0b90b' }}>🔮 AlphaBot:</strong> AI hints with 88% accuracy</li>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#f0b90b' }}>💎 Moonshot Access:</strong> Higher win-rate tokens</li>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#f0b90b' }}>📉 50% Slippage Reduction:</strong> Better exit prices on large sells</li>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#f0b90b' }}>💬 Chat Privileges:</strong> Influence other players</li>
              <li style={{ marginBottom: '8px' }}><strong style={{ color: '#f0b90b' }}>📊 +10% Profit Boost:</strong> On all winning trades</li>
            </ul>
          </Section>
          
          {/* House Edge */}
          <Section title="Why This Isn't Gambling">
            <p>Paper Hands is designed for <strong style={{ color: '#f0b90b' }}>education, not profit</strong>. The economics intentionally favor the house:</p>
            <ul style={{ marginTop: '12px', marginLeft: '24px', color: '#888' }}>
              <li style={{ marginBottom: '8px' }}>70% of tokens are rugs (just like real memecoins)</li>
              <li style={{ marginBottom: '8px' }}>Maximum play amounts are capped at ~$1 Paper Buck</li>
              <li style={{ marginBottom: '8px' }}>The goal is learning patterns, not making money</li>
            </ul>
            <p style={{ marginTop: '12px', color: '#888' }}>Think of it like paying for a course - you're spending a few cents to learn lessons that could save you thousands in real markets.</p>
          </Section>
          
          {/* Technical */}
          <Section title="Why BSV + HandCash?">
            <p>BSV enables true micropayments with fees under $0.001:</p>
            <ul style={{ marginTop: '12px', marginLeft: '24px', color: '#888' }}>
              <li style={{ marginBottom: '8px' }}><strong>Instant:</strong> Transactions confirm in seconds</li>
              <li style={{ marginBottom: '8px' }}><strong>Cheap:</strong> On-chain fees ~$0.0002</li>
              <li style={{ marginBottom: '8px' }}><strong>HandCash:</strong> Simple $handle payments, no addresses</li>
              <li style={{ marginBottom: '8px' }}><strong>Verifiable:</strong> Results recorded on TagStats</li>
            </ul>
            
            <h4 style={{ color: '#f0b90b', marginTop: '24px', marginBottom: '12px' }}>Fee-Efficient Architecture</h4>
            <p>To keep micropayments viable:</p>
            <ul style={{ marginTop: '12px', marginLeft: '24px', color: '#888' }}>
              <li style={{ marginBottom: '8px' }}><strong>Internal Game Ledger:</strong> In-game trades settle internally (no API call per trade)</li>
              <li style={{ marginBottom: '8px' }}><strong>Deposit/Withdraw Only:</strong> HandCash API used only for funding and cashout</li>
              <li style={{ marginBottom: '8px' }}><strong>Batch Settlements:</strong> Results written to chain in batches, not per-trade</li>
              <li style={{ marginBottom: '8px' }}><strong>Minimum Deposit:</strong> Small minimum ensures API costs stay below 1% of volume</li>
            </ul>
          </Section>
          
          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '60px', paddingTop: '40px', borderTop: '1px solid #1a1a2e' }}>
            <p style={{ color: '#666', fontSize: '14px' }}>Paper Hands © 2024</p>
            <p style={{ color: '#444', fontSize: '12px', marginTop: '8px' }}>Educational Trading Simulator • Built on BSV • Powered by HandCash</p>
            <p style={{ color: '#333', fontSize: '10px', marginTop: '8px' }}>Not financial advice. For educational purposes only.</p>
          </div>
        </div>
      </div>
    );
  }

  // ============== REAL MONEY WARNING MODAL ==============
  const RealMoneyWarningModal = () => {
    if (!showRealMoneyWarning) return null;
    
    const handleConfirm = (dontShowAgain) => {
      if (dontShowAgain) {
        setHideRealMoneyWarning(true);
      }
      setShowRealMoneyWarning(false);
      
      // Execute the pending action
      if (pendingAction?.type === 'apeIn') {
        executeApeIn(pendingAction.stake);
      }
      setPendingAction(null);
    };
    
    const handleCancel = () => {
      setShowRealMoneyWarning(false);
      setPendingAction(null);
    };
    
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
        <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #f0b90b', borderRadius: '16px', padding: '24px', maxWidth: '380px', width: '90%', textAlign: 'center' }}>
          
          {/* Warning Icon */}
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          
          {/* Title */}
          <h2 style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
            Real Money Warning
          </h2>
          
          {/* Message */}
          <p style={{ color: '#ccc', fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
            You're about to spend <strong style={{ color: '#fff' }}>{formatPB(pendingAction?.stake || stake)}</strong> ({formatUsd(pendingAction?.stake || stake)}).
          </p>
          <div style={{ color: '#f0b90b', fontSize: '12px', lineHeight: '1.6', marginBottom: '12px', backgroundColor: 'rgba(240,185,11,0.1)', padding: '10px', borderRadius: '6px', textAlign: 'left' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>💰 Trading Fee (paid in real BSV):</div>
            <div style={{ color: '#ccc', fontSize: '11px' }}>
              • 🤖 AI Tokens → <strong style={{ color: '#00d26a' }}>1% fee</strong><br/>
              • ⚔️ PvP Tokens → <strong style={{ color: '#9b59b6' }}>10% fee</strong>
            </div>
            <div style={{ color: '#888', fontSize: '10px', marginTop: '6px', borderTop: '1px solid #333', paddingTop: '6px' }}>
              <strong>Examples:</strong> AI: $0.25 trade ≈ $0.0025 BSV fee | PvP: $0.25 trade ≈ $0.025 BSV fee
            </div>
          </div>
          <p style={{ color: '#888', fontSize: '12px', lineHeight: '1.5', marginBottom: '20px' }}>
            Paper Bucks are game credits backed by real BSV. While amounts are small, you can lose your sats if the token rugs. This game is for <strong style={{ color: '#f0b90b' }}>educational purposes</strong> - learn to spot rugs without risking real money elsewhere.
          </p>
          
          {/* Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button 
              onClick={() => handleConfirm(false)}
              style={{ 
                padding: '14px', 
                borderRadius: '8px', 
                border: 'none', 
                backgroundColor: '#00d26a', 
                color: '#000', 
                fontWeight: 'bold', 
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              ✓ I Understand, Continue
            </button>
            
            <button 
              onClick={() => handleConfirm(true)}
              style={{ 
                padding: '12px', 
                borderRadius: '8px', 
                border: '1px solid #333', 
                backgroundColor: 'transparent', 
                color: '#888', 
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Don't show this again
            </button>
            
            <button 
              onClick={handleCancel}
              style={{ 
                padding: '10px', 
                borderRadius: '8px', 
                border: 'none', 
                backgroundColor: 'transparent', 
                color: '#ff3b69', 
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============== FIRST-TIME RAKE CONSENT MODAL (One-time, permanent) ==============
  const RakeConsentModal = () => {
    const [accepting, setAccepting] = useState(false);
    if (!showRakeConsentModal) return null;
    
    const handleAccept = async () => {
      setAccepting(true);
      try {
        const res = await fetch(`${API_URL}/api/user/rake-consent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId }
        });
        const data = await res.json();
        if (data.success) {
          setRakeConsent(true);
          setShowRakeConsentModal(false);
          console.log('✅ Rake consent accepted');
          
          // 🎓 Show tutorial if not already completed
          if (!tutorialComplete) {
            setTimeout(() => setShowTutorialModal(true), 300);
          }
        }
      } catch (e) {
        console.error('Rake consent error:', e);
        setAccepting(false);
      }
    };
    
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.98)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
        <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #f0b90b', borderRadius: '20px', padding: '32px', maxWidth: '420px', width: '90%', textAlign: 'center', fontFamily: 'monospace', boxShadow: '0 0 60px rgba(240,185,11,0.3)' }}>
          
          {/* Logo/Icon */}
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎰</div>
          
          {/* Title */}
          <h1 style={{ color: '#f0b90b', fontSize: '26px', fontWeight: 'bold', marginBottom: '8px', fontFamily: 'monospace' }}>
            Welcome to Paper Hands!
          </h1>
          
          {/* Subtitle */}
          <p style={{ color: '#888', fontSize: '13px', marginBottom: '24px', fontFamily: 'monospace' }}>
            Before you trade, please understand our fee structure
          </p>
          
          {/* Fee Breakdown */}
          <div style={{ backgroundColor: 'rgba(240,185,11,0.1)', padding: '24px', borderRadius: '16px', marginBottom: '24px', border: '1px solid #f0b90b33' }}>
            <div style={{ color: '#f0b90b', fontSize: '32px', fontWeight: 'bold', marginBottom: '16px', fontFamily: 'monospace' }}>
              💰 1¢ Per Trade
            </div>
            <div style={{ color: '#fff', fontSize: '14px', lineHeight: '1.8', fontFamily: 'monospace', textAlign: 'left' }}>
              <p style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: '#00d26a', fontSize: '18px' }}>✓</span>
                <span>Every <strong>BUY</strong> costs 1¢ (50 sats)</span>
              </p>
              <p style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: '#ff3b69', fontSize: '18px' }}>✓</span>
                <span>Every <strong>SELL</strong> costs 1¢ (50 sats)</span>
              </p>
              <p style={{ marginBottom: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: '#f0b90b', fontSize: '18px' }}>✓</span>
                <span>Same fee regardless of trade size</span>
              </p>
            </div>
          </div>
          
          {/* Why Fees */}
          <div style={{ backgroundColor: 'rgba(0,210,106,0.1)', padding: '16px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #00d26a33' }}>
            <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>WHY FEES?</div>
            <p style={{ color: '#aaa', fontSize: '11px', lineHeight: '1.6', margin: 0 }}>
              Fees cover BSV network costs and keep the game sustainable. 
              Your potential profits far exceed the tiny 1¢ per trade!
            </p>
          </div>
          
          {/* ROI Tracker Teaser */}
          <div style={{ backgroundColor: 'rgba(155,89,182,0.1)', padding: '12px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #9b59b633' }}>
            <p style={{ color: '#9b59b6', fontSize: '11px', margin: 0 }}>
              📊 Track your fee ROI in your profile - see how much you've made vs paid!
            </p>
          </div>
          
          {/* Accept Button */}
          <button 
            onClick={handleAccept}
            disabled={accepting}
            style={{ 
              width: '100%',
              padding: '16px', 
              borderRadius: '12px', 
              border: 'none', 
              backgroundColor: accepting ? '#aa8800' : '#f0b90b', 
              color: '#000', 
              fontWeight: 'bold', 
              fontSize: '16px',
              cursor: accepting ? 'wait' : 'pointer',
              fontFamily: 'monospace',
              opacity: accepting ? 0.8 : 1,
            }}
          >
            {accepting ? '⏳ Loading...' : '✓ I Understand - Let\'s Trade!'}
          </button>
          
          <p style={{ color: '#444', fontSize: '9px', marginTop: '16px', fontFamily: 'monospace' }}>
            By clicking above, you accept the fee structure
          </p>
        </div>
      </div>
    );
  };

  // ============== AMM CASINO TUTORIAL MODAL ==============
  const TutorialModal = () => {
    if (!showTutorialModal) return null;
    
    const step = tutorialStep;
    const setStep = setTutorialStep;
    
    const handleComplete = async () => {
      try {
        await fetch(`${API_URL}/api/user/tutorial-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId }
        });
        setTutorialComplete(true);
        setShowTutorialModal(false);
        setTutorialStep(0); // Reset for next time
        console.log('🎓 Tutorial completed');
      } catch (e) {
        console.error('Tutorial complete error:', e);
        setShowTutorialModal(false);
      }
    };
    
    const steps = [
      {
        icon: '🎰',
        title: 'Welcome to the AMM Casino',
        subtitle: 'Where the math is real',
        content: (
          <div style={{ textAlign: 'left', lineHeight: '1.8' }}>
            <p style={{ color: '#fff', marginBottom: '12px' }}>
              Paper Hands isn't a regular casino. There's <strong style={{ color: '#f0b90b' }}>no RNG</strong>, no house tricks.
            </p>
            <p style={{ color: '#888' }}>
              Every trade uses real <strong style={{ color: '#00d26a' }}>AMM math</strong> (Automated Market Maker) - the same curves that power DeFi.
            </p>
          </div>
        )
      },
      {
        icon: '📈',
        title: 'How Trading Works',
        subtitle: 'Supply & demand sets the price',
        content: (
          <div style={{ textAlign: 'left', lineHeight: '1.8' }}>
            <p style={{ color: '#00d26a', marginBottom: '8px' }}>
              <strong>BUY</strong> → Price goes UP
            </p>
            <p style={{ color: '#ff3b69', marginBottom: '12px' }}>
              <strong>SELL</strong> → Price goes DOWN
            </p>
            <p style={{ color: '#888' }}>
              Bigger trades = bigger price impact (slippage). Time your entries and exits wisely!
            </p>
          </div>
        )
      },
      {
        icon: '🎯',
        title: 'Two Ways to Play',
        subtitle: 'AI Tokens vs PvP Tokens',
        content: (
          <div style={{ textAlign: 'left', lineHeight: '1.8' }}>
            <p style={{ color: '#00d26a', marginBottom: '12px' }}>
              <strong>🤖 AI Tokens:</strong> Trade against the house. The AI runs the chart patterns.
            </p>
            <p style={{ color: '#9b59b6' }}>
              <strong>👥 PvP Tokens:</strong> Trade against other players. Someone wins, someone loses. The house just takes 1¢ per trade.
            </p>
          </div>
        )
      },
      {
        icon: '💀',
        title: 'Rug Pulls Are Real',
        subtitle: 'Learn to spot them',
        content: (
          <div style={{ textAlign: 'left', lineHeight: '1.8' }}>
            <p style={{ color: '#ff3b69', marginBottom: '12px' }}>
              In PvP, the token creator can <strong>RUG</strong> at any time - draining the pool.
            </p>
            <p style={{ color: '#888', marginBottom: '12px' }}>
              Watch for: sudden pumps, low liquidity, new tokens, quiet chat.
            </p>
            <p style={{ color: '#f0b90b' }}>
              <strong>Paper Hands = Weak hands.</strong> Learn when to take profit!
            </p>
          </div>
        )
      }
    ];
    
    const currentStep = steps[step];
    const isLastStep = step === steps.length - 1;
    
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.98)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5001, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
        <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #f0b90b', borderRadius: '20px', padding: '32px', maxWidth: '420px', width: '90%', textAlign: 'center', fontFamily: 'monospace', boxShadow: '0 0 60px rgba(240,185,11,0.3)' }}>
          
          {/* Progress dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
            {steps.map((_, i) => (
              <div 
                key={i} 
                style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: i === step ? '#f0b90b' : i < step ? '#00d26a' : '#333',
                  transition: 'all 0.3s'
                }} 
              />
            ))}
          </div>
          
          {/* Icon */}
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>{currentStep.icon}</div>
          
          {/* Title */}
          <h1 style={{ color: '#f0b90b', fontSize: '22px', fontWeight: 'bold', marginBottom: '4px', fontFamily: 'monospace' }}>
            {currentStep.title}
          </h1>
          
          {/* Subtitle */}
          <p style={{ color: '#888', fontSize: '13px', marginBottom: '24px', fontFamily: 'monospace' }}>
            {currentStep.subtitle}
          </p>
          
          {/* Content */}
          <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '12px', marginBottom: '24px', minHeight: '120px' }}>
            {currentStep.content}
          </div>
          
          {/* Navigation */}
          <div style={{ display: 'flex', gap: '12px' }}>
            {step > 0 && (
              <button 
                onClick={() => setStep(step - 1)}
                style={{ 
                  flex: 1,
                  padding: '14px', 
                  borderRadius: '10px', 
                  border: '1px solid #333', 
                  backgroundColor: 'transparent', 
                  color: '#888', 
                  fontWeight: 'bold', 
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontFamily: 'monospace'
                }}
              >
                ← Back
              </button>
            )}
            <button 
              onClick={isLastStep ? handleComplete : () => setStep(step + 1)}
              style={{ 
                flex: 1,
                padding: '14px', 
                borderRadius: '10px', 
                border: 'none', 
                backgroundColor: isLastStep ? '#00d26a' : '#f0b90b', 
                color: '#000', 
                fontWeight: 'bold', 
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: 'monospace'
              }}
            >
              {isLastStep ? '🎮 Start Trading!' : 'Next →'}
            </button>
          </div>
          
          {/* Skip */}
          <button 
            onClick={handleComplete}
            style={{ 
              marginTop: '16px',
              padding: '8px', 
              border: 'none', 
              backgroundColor: 'transparent', 
              color: '#444', 
              fontSize: '11px',
              cursor: 'pointer',
              fontFamily: 'monospace'
            }}
          >
            Skip tutorial
          </button>
        </div>
      </div>
    );
  };

  // ============== RAKE DISCLOSURE MODAL (Daily per token type) ==============
  const RakeDisclosureModal = () => {
    if (!showRakeDisclosure) return null;
    
    const isAI = rakeWarningType === 'ai';
    const isPvP = rakeWarningType === 'pvp';
    
    const handleConfirm = () => {
      // Mark as seen for today
      if (isAI) {
        localStorage.setItem('paperHands_aiRakeWarningDate', new Date().toDateString());
        setHasSeenAIRakeToday(true);
      } else {
        localStorage.setItem('paperHands_pvpRakeWarningDate', new Date().toDateString());
        setHasSeenPvPRakeToday(true);
      }
      setShowRakeDisclosure(false);
      
      // Execute the pending action
      if (pendingAction?.type === 'apeIn') {
        executeApeIn(pendingAction.stake);
      }
      setPendingAction(null);
    };
    
    const handleCancel = () => {
      setShowRakeDisclosure(false);
      setPendingAction(null);
    };
    
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
        <div style={{ backgroundColor: '#0a0a0f', border: `2px solid ${isAI ? '#00d26a' : '#9b59b6'}`, borderRadius: '16px', padding: '28px', maxWidth: '400px', width: '90%', textAlign: 'center', fontFamily: 'monospace', boxShadow: `0 0 40px ${isAI ? 'rgba(0,210,106,0.3)' : 'rgba(155,89,182,0.3)'}` }}>
          
          {/* Icon */}
          <div style={{ fontSize: '56px', marginBottom: '20px' }}>{isAI ? '🤖' : '⚔️'}</div>
          
          {/* Title */}
          <h2 style={{ color: isAI ? '#00d26a' : '#9b59b6', fontSize: '22px', fontWeight: 'bold', marginBottom: '8px', fontFamily: 'monospace' }}>
            {isAI ? 'AI Token Trading' : 'PvP Token Trading'}
          </h2>
          
          {/* Subtitle */}
          <p style={{ color: '#888', fontSize: '12px', marginBottom: '20px', fontFamily: 'monospace' }}>
            {isAI ? 'House-controlled tokens with programmed outcomes' : 'Player vs Player - trade against real people!'}
          </p>
          
          {/* Rake Info Box */}
          <div style={{ backgroundColor: isAI ? 'rgba(0,210,106,0.1)' : 'rgba(155,89,182,0.1)', padding: '20px', borderRadius: '12px', marginBottom: '20px', textAlign: 'left', border: `1px solid ${isAI ? '#00d26a33' : '#9b59b633'}` }}>
            <div style={{ color: '#f0b90b', fontSize: '24px', fontWeight: 'bold', marginBottom: '12px', fontFamily: 'monospace', textAlign: 'center' }}>
              💰 1¢ Fee Per Trade
            </div>
            <div style={{ color: '#ccc', fontSize: '13px', lineHeight: '1.7', fontFamily: 'monospace' }}>
              {isAI ? (
                <>
                  <p style={{ marginBottom: '10px' }}>🎰 The house controls when AI tokens pump or rug - it's a game of skill to spot the patterns!</p>
                  <p style={{ color: '#00d26a', fontWeight: 'bold' }}>Flat 1¢ fee (50 sats) on every trade.</p>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: '10px' }}>🎯 PvP tokens are created by players. You're competing against other traders - may the best degen win!</p>
                  <p style={{ color: '#9b59b6', fontWeight: 'bold' }}>Flat 1¢ fee (50 sats) on every trade.</p>
                </>
              )}
            </div>
            <div style={{ color: '#666', fontSize: '11px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #333', fontFamily: 'monospace', textAlign: 'center' }}>
              💡 Same low fee regardless of trade size!
            </div>
          </div>
          
          <p style={{ color: '#555', fontSize: '10px', marginBottom: '20px', fontFamily: 'monospace' }}>
            ℹ️ This reminder shows once daily per token type
          </p>
          
          {/* Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button 
              onClick={handleConfirm}
              style={{ 
                padding: '14px', 
                borderRadius: '8px', 
                border: 'none', 
                backgroundColor: isAI ? '#00d26a' : '#9b59b6', 
                color: '#fff', 
                fontWeight: 'bold', 
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: 'monospace'
              }}
            >
              ✓ Got it, let's trade!
            </button>
            
            <button 
              onClick={handleCancel}
              style={{ 
                padding: '10px', 
                borderRadius: '8px', 
                border: '1px solid #333', 
                backgroundColor: 'transparent', 
                color: '#888', 
                fontSize: '12px',
                cursor: 'pointer',
                fontFamily: 'monospace'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============== UNIFIED DEPOSIT MODAL ==============
  const DepositModal = () => {
    if (!showDeposit) return null;
    
    // No minimum - user can deposit any amount
    const minDepositSats = 100000; // 100K sats minimum (~$0.02) just to avoid dust
    
    // Quick deposit amounts in USD (converted to sats)
    // Range from casual ($0.25) to whale ($10) - all feel like big money in-game!
    const quickAmountsUSD = [0.25, 0.50, 1.00, 2.00, 5.00, 10.00]; // $0.25 to $10
    
    const handleCustomDeposit = () => {
      const amount = parseInt(customDepositAmount);
      if (amount && amount >= minDepositSats && amount <= mainWallet) {
        setPendingDeposit(amount);
      }
    };
    
    const closeModal = () => {
      setShowDeposit(false);
      setPendingDeposit(null);
      setCustomDepositAmount('');
    };
    
    // MOBILE: Full page takeover - no overlay, just a page
    if (isMobile) {
      return (
        <div style={{ 
          position: 'fixed', 
          inset: 0, 
          backgroundColor: '#0a0a0f', 
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #1a1a2e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img src="https://i.ibb.co/23X7r7j/logo.png" alt="" style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
              <div>
                <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '15px' }}>{isFirstDeposit ? 'Get Started' : 'Add Paper Bucks'}</div>
                <div style={{ color: '#555', fontSize: '10px' }}>Deposit from HandCash</div>
              </div>
            </div>
            <button onClick={closeModal} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
          
          {/* Scrollable content area */}
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            padding: '16px',
            WebkitOverflowScrolling: 'touch',
          }}>
            {/* BSV Price */}
            <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#666', fontSize: '11px' }}>BSV Price</span>
              <span style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>${bsvPrice?.toFixed(2) || '--'}</span>
            </div>
            
            {isFirstDeposit && (
              <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #1a3a1a', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
                <div style={{ color: '#00d26a', fontWeight: 'bold', marginBottom: '6px' }}>📦 Learn to Spot Rugs</div>
                <div style={{ color: '#888', fontSize: '11px', lineHeight: 1.5 }}>
                  Educational trading simulator using real BSV micropayments.
                  <br/>• 💰 Risk pennies, not dollars
                  <br/>• 💸 Withdraw anytime
                </div>
              </div>
            )}
            
            {/* Wallet Balances */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#111', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ color: '#38cb7c', fontSize: '9px', fontWeight: 'bold' }}>HANDCASH</div>
                <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{formatSats(mainWallet)}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>${(mainWallet * satToUsd).toFixed(2)}</div>
              </div>
              <div style={{ backgroundColor: '#0d1a0d', padding: '10px', borderRadius: '8px', textAlign: 'center', position: 'relative' }}>
                <div style={{ color: '#00d26a', fontSize: '9px', fontWeight: 'bold' }}>💵 PAPER BUCKS</div>
                <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>${formatSats(gameWallet)}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>~${(gameWallet * satToUsd).toFixed(2)}</div>
              </div>
            </div>
            
            {pendingDeposit ? (() => {
              // Normalize pendingDeposit - can be number or {amount, type} object from low balance modal
              const depositAmount = typeof pendingDeposit === 'object' ? pendingDeposit.amount : pendingDeposit;
              return (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <div style={{ color: '#888', fontSize: '11px' }}>You are about to deposit:</div>
                  <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>${(depositAmount * satToUsd).toFixed(2)} USD</div>
                  <div style={{ color: '#00d26a', fontSize: '24px', fontWeight: 'bold' }}>= ${formatSats(depositAmount)} Paper Bucks</div>
                  <div style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>({depositAmount.toLocaleString()} sats)</div>
                </div>
                {/* Show warning if amount seems wrong */}
                {depositAmount < 1000 && (
                  <div style={{ backgroundColor: '#1a0a0a', border: '1px solid #ff3b69', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                    <div style={{ color: '#ff3b69', fontWeight: 'bold', fontSize: '12px' }}>⚠️ Amount too low!</div>
                    <div style={{ color: '#888', fontSize: '10px' }}>Minimum deposit is 1,000 sats. Please select a higher amount.</div>
                  </div>
                )}
                <div style={{ backgroundColor: '#1a1508', border: '1px solid #f0b90b44', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '12px' }}>⚠️ Real Bitcoin (BSV)</div>
                  <div style={{ color: '#888', fontSize: '10px' }}>This moves real sats from your wallet. Withdraw anytime.</div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setPendingDeposit(null)} style={{ flex: 1, padding: '14px', borderRadius: '8px', border: '1px solid #333', backgroundColor: 'transparent', color: '#888' }}>Cancel</button>
                  <button
                    onClick={() => depositAmount >= 1000 ? deposit(depositAmount) : alert('Amount too low! Minimum is 1,000 sats.')}
                    disabled={transactionPending || depositAmount < 1000}
                    style={{ flex: 1, padding: '14px', borderRadius: '8px', border: 'none', backgroundColor: depositAmount >= 1000 ? '#38cb7c' : '#333', color: depositAmount >= 1000 ? '#fff' : '#666', fontWeight: 'bold' }}>
                    {transactionPending ? '⏳...' : '✓ Confirm'}
                  </button>
                </div>
              </div>
            );})() : (
              <div>
                <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: '8px' }}>💰 Choose Amount</div>
                <div style={{ color: '#666', fontSize: '10px', marginBottom: '12px' }}>1 sat = 1 Paper Buck</div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '8px' }}>
                  {[0.25, 0.50, 1.00].map(usd => {
                    const sats = usdToSats(usd);
                    const ok = mainWallet >= sats && sats >= 1000;
                    return (
                      <button key={usd} onClick={() => { console.log('📱 Deposit btn: $'+usd+'='+sats+'sats, bsv='+bsvPrice); if(sats>=1000) setPendingDeposit(sats); else alert('Price not loaded yet'); }} disabled={!ok}
                        style={{ padding: '10px 4px', borderRadius: '6px', border: '1px solid #333', backgroundColor: ok ? '#111' : '#0a0a0a', color: ok ? '#888' : '#444', fontSize: '11px' }}>
                        ${usd.toFixed(2)}
                        <div style={{ fontSize: '8px', color: ok ? '#00d26a' : '#333' }}>={formatPB(sats)}</div>
                      </button>
                    );
                  })}
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', marginBottom: '16px' }}>
                  {[5.00, 10.00].map(usd => {
                    const sats = usdToSats(usd);
                    const ok = mainWallet >= sats && sats >= 1000;
                    return (
                      <button key={usd} onClick={() => { console.log('📱 Deposit btn: $'+usd+'='+sats+'sats, bsv='+bsvPrice); if(sats>=1000) setPendingDeposit(sats); else alert('Price not loaded yet'); }} disabled={!ok}
                        style={{ padding: '12px 4px', borderRadius: '6px', border: 'none', backgroundColor: ok ? '#00d26a' : '#1a1a2e', color: ok ? '#000' : '#555', fontWeight: 'bold', fontSize: '13px' }}>
                        ${usd.toFixed(2)}
                        <div style={{ fontSize: '9px', color: ok ? '#000' : '#444', fontWeight: 'normal' }}>={formatPB(sats)}</div>
                      </button>
                    );
                  })}
                </div>
                
                <button onClick={closeModal} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #333', backgroundColor: 'transparent', color: '#888' }}>← Back</button>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    // DESKTOP: Normal modal
    return (
      <div 
        style={{ 
          position: 'fixed', 
          inset: 0, 
          backgroundColor: 'rgba(0,0,0,0.95)', 
          zIndex: 3000, 
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
      >
        <div 
          style={{ 
            backgroundColor: '#0a0a0f', 
            border: '1px solid #1a1a2e', 
            borderRadius: isMobile ? '12px' : '16px', 
            padding: isMobile ? '16px' : '24px', 
            maxWidth: '400px', 
            width: '90%', 
            maxHeight: '90vh', 
            overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? '12px' : '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
              <img 
                src="https://i.ibb.co/23X7r7j/logo.png" 
                alt="HandCash" 
                style={{ width: isMobile ? '28px' : '36px', height: isMobile ? '28px' : '36px', borderRadius: '50%' }} 
              />
              <div>
                <h2 style={{ margin: 0, color: '#fff', fontSize: isMobile ? '15px' : '18px' }}>{isFirstDeposit ? 'Get Started' : 'Add Paper Bucks'}</h2>
                <div style={{ fontSize: isMobile ? '9px' : '10px', color: '#555', marginTop: '2px' }}>
                  {isFirstDeposit ? 'Deposit from HandCash to get Paper Bucks' : 'Reload your game wallet with Paper Bucks'}
                </div>
              </div>
            </div>
            <button onClick={() => { setShowDeposit(false); setPendingDeposit(null); setCustomDepositAmount(''); }} style={{ width: isMobile ? '28px' : '32px', height: isMobile ? '28px' : '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: isMobile ? '14px' : '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
          
          {/* Live BSV Price */}
          <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px 12px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#666', fontSize: '11px' }}>BSV Price (live)</span>
            <span style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>
              {bsvPriceLoading ? '⏳ Loading...' : bsvPrice ? `$${bsvPrice.toFixed(2)}` : 'Error'}
            </span>
          </div>
          
          {/* First Deposit Explainer */}
          {isFirstDeposit && (
            <div style={{ backgroundColor: '#0a1520', border: '1px solid #1a3a5c', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ color: '#00a8ff', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>🎓 Learn to Spot Rugs</div>
              <div style={{ color: '#888', fontSize: '12px', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 8px 0' }}>Paper Hands is an <strong style={{ color: '#fff' }}>educational trading simulator</strong> using real BSV micropayments.</p>
                <ul style={{ margin: '0', paddingLeft: '16px' }}>
                  <li style={{ marginBottom: '4px' }}>📊 Practice spotting rug pulls</li>
                  <li style={{ marginBottom: '4px' }}>💰 Risk pennies, not dollars</li>
                  <li style={{ marginBottom: '4px' }}>💸 <strong style={{ color: '#00d26a' }}>Withdraw anytime</strong> - it's always your money</li>
                </ul>
              </div>
            </div>
          )}
          
          {pendingDeposit ? (() => {
            // Normalize pendingDeposit - can be number or {amount, type} object from low balance modal
            const depositAmount = typeof pendingDeposit === 'object' ? pendingDeposit.amount : pendingDeposit;
            return (
            // Confirmation screen with REAL MONEY warning
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '16px' }}>
                <img
                  src="https://i.ibb.co/23X7r7j/logo.png"
                  alt="HandCash"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    margin: '0 auto',
                    display: 'block',
                    boxShadow: '0 0 20px rgba(56,203,124,0.4)',
                  }}
                />
              </div>
              <div style={{ color: '#38cb7c', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>FROM YOUR HANDCASH WALLET</div>
              <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>You are about to deposit:</div>
              <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>${(depositAmount * satToUsd).toFixed(2)} USD ({formatSats(depositAmount)} sats)</div>
              <div style={{ color: '#00d26a', fontSize: '28px', fontWeight: 'bold', marginBottom: '16px' }}>= {formatPB(depositAmount)} Paper Bucks</div>
              
              {/* REAL MONEY Warning */}
              <div style={{ backgroundColor: '#1a1500', border: '1px solid #f0b90b', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ color: '#f0b90b', fontSize: '13px', fontWeight: 'bold', marginBottom: '6px' }}>
                  ⚠️ This is REAL Bitcoin (BSV)
                </div>
                <div style={{ color: '#888', fontSize: '11px', lineHeight: '1.5' }}>
                  This transaction will move real sats from your HandCash wallet. 
                  You can withdraw anytime, but please confirm you understand this is real money.
                </div>
              </div>
              
              {/* Reminder it's their money */}
              <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #1a3a1a', borderRadius: '6px', padding: '10px', marginBottom: '20px' }}>
                <div style={{ color: '#00d26a', fontSize: '11px' }}>
                  ✓ Your sats, always. Withdraw anytime.
                </div>
              </div>
              
              {/* Don't show again checkbox */}
              <div style={{ marginBottom: '16px', textAlign: 'left' }}>
                <label style={{ color: '#666', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input 
                    type="checkbox" 
                    checked={localStorage.getItem('paperHands_skipWarnings') === 'true'}
                    onChange={(e) => {
                      localStorage.setItem('paperHands_skipWarnings', e.target.checked);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  Don't show this warning again
                </label>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => setPendingDeposit(null)} style={{ flex: 1, padding: '14px', borderRadius: '8px', border: '1px solid #333', backgroundColor: 'transparent', color: '#888', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
                <button onClick={() => deposit(depositAmount)} disabled={transactionPending} style={{ flex: 1, padding: '14px', borderRadius: '8px', border: 'none', backgroundColor: '#38cb7c', color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  {transactionPending ? '⏳ Processing...' : (
                    <>
                      <img src="https://i.ibb.co/23X7r7j/logo.png" alt="" style={{ width: '18px', height: '18px', borderRadius: '50%' }} />
                      Confirm Deposit
                    </>
                  )}
                </button>
              </div>
            </div>
          );})() : (
            <>
              {/* Wallet Balances */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center', border: '1px solid #38cb7c33' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '4px' }}>
                    <img src="https://i.ibb.co/23X7r7j/logo.png" alt="" style={{ width: '14px', height: '14px', borderRadius: '50%' }} />
                    <span style={{ color: '#38cb7c', fontSize: '10px', fontWeight: 'bold' }}>HANDCASH</span>
                  </div>
                  <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>{formatSats(mainWallet)}</div>
                  <div style={{ color: '#666', fontSize: '10px' }}>${(mainWallet * satToUsd).toFixed(2)}</div>
                </div>
                <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #0f2f0f', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#00d26a', fontSize: '10px', fontWeight: 'bold' }}>💵 PAPER BUCKS</div>
                  <div style={{ color: '#00d26a', fontSize: '20px', fontWeight: 'bold' }}>${formatSats(gameWallet)}</div>
                  <div style={{ color: '#666', fontSize: '10px' }}>~${(gameWallet * satToUsd).toFixed(2)} USD</div>
                </div>
              </div>
              
              {/* Premium Purchase (if not already premium) */}
              {!isPremium && !isFirstDeposit && (
                <div style={{ 
                  backgroundColor: '#1a1508', 
                  border: '2px solid #f0b90b', 
                  borderRadius: '12px', 
                  padding: '16px', 
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '32px' }}>👑</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#f0b90b', fontSize: '16px', fontWeight: 'bold' }}>7-DAY PREMIUM PASS</div>
                      <div style={{ color: '#888', fontSize: '12px' }}>
                        <span style={{ color: '#fff', fontWeight: 'bold' }}>{PREMIUM_USD_DISPLAY}</span>
                        <span style={{ color: '#555' }}> ({formatSats(PREMIUM_COST)} sats)</span>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ color: '#888', fontSize: '11px', marginBottom: '12px', lineHeight: '1.5' }}>
                    ✅ +19% win rate advantage<br/>
                    ✅ 5 sec early token access<br/>
                    ✅ 2× daily rewards<br/>
                    ✅ Premium chat & badges
                  </div>
                  
                  <button 
                    onClick={() => { setShowDeposit(false); setShowPremiumComparison(true); }}
                    style={{ 
                      width: '100%', 
                      padding: '14px', 
                      borderRadius: '8px', 
                      border: 'none', 
                      backgroundColor: '#f0b90b', 
                      color: '#000', 
                      fontWeight: 'bold', 
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    👑 See All Benefits & Unlock
                  </button>
                </div>
              )}
              
              {isPremium && (
                <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #f0b90b', borderRadius: '8px', padding: '12px', marginBottom: '16px', textAlign: 'center' }}>
                  <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>👑 PREMIUM ACTIVE</div>
                  <div style={{ color: '#666', fontSize: '10px', marginTop: '2px' }}>7-Day Pass ({formatSats(PREMIUM_COST)} sats)</div>
                  {premiumTimeLeft && (
                    <div style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>
                      ⏱️ Expires in: {premiumTimeLeft}
                    </div>
                  )}
                </div>
              )}
              
              {/* Deposit Section */}
              <div style={{ borderTop: '1px solid #1a1a2e', paddingTop: '16px' }}>
                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                  💰 {isFirstDeposit ? 'Choose Starting Amount' : 'Add Paper Bucks'}
                </div>
                <div style={{ color: '#666', fontSize: '11px', marginBottom: '12px' }}>
                  1 sat = 1 Paper Buck • Deposit real sats, get Paper Bucks to trade!
                </div>
                
                {/* Quick Deposit Amounts - small cents on top, main amounts below */}
                {/* Top row: Smaller amounts */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '8px' }}>
                  {[0.25, 0.50, 1.00].map(usdAmt => {
                    const satsAmt = usdToSats(usdAmt);
                    const canAfford = mainWallet >= satsAmt && satsAmt >= 1000;
                    return (
                      <button 
                        key={usdAmt} 
                        onClick={() => { console.log('🖥️ Desktop deposit: $'+usdAmt+'='+satsAmt+'sats'); if(satsAmt>=1000) setPendingDeposit(satsAmt); }} 
                        disabled={!canAfford}
                        style={{
                          padding: '10px',
                          borderRadius: '6px',
                          border: canAfford ? '1px solid #333' : '1px solid #1a1a2e',
                          backgroundColor: canAfford ? '#111' : '#0a0a0a',
                          color: canAfford ? '#888' : '#333',
                          cursor: canAfford ? 'pointer' : 'not-allowed',
                          fontSize: '12px',
                          opacity: canAfford ? 1 : 0.4
                        }}
                      >
                        ${usdAmt.toFixed(2)} USD
                        <div style={{ fontSize: '9px', color: canAfford ? '#00d26a' : '#333', marginTop: '2px' }}>= {formatPB(satsAmt)} Paper Bucks</div>
                      </button>
                    );
                  })}
                </div>
                
                {/* Bottom row: Main green deposit buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '12px' }}>
                  {[5.00, 10.00].map(usdAmt => {
                    const satsAmt = usdToSats(usdAmt);
                    const canAfford = mainWallet >= satsAmt && satsAmt >= 1000;
                    return (
                      <button 
                        key={usdAmt} 
                        onClick={() => { console.log('🖥️ Desktop deposit: $'+usdAmt+'='+satsAmt+'sats'); if(satsAmt>=1000) setPendingDeposit(satsAmt); }} 
                        disabled={!canAfford}
                        style={{
                          padding: '14px',
                          borderRadius: '8px',
                          border: canAfford ? '1px solid #00d26a' : '1px solid #1a1a2e',
                          backgroundColor: canAfford ? '#0d1a0d' : '#1a1a1a',
                          color: canAfford ? '#fff' : '#444',
                          cursor: canAfford ? 'pointer' : 'not-allowed',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          opacity: canAfford ? 1 : 0.5
                        }}
                      >
                        ${usdAmt.toFixed(2)} USD
                        <div style={{ fontSize: '10px', color: canAfford ? '#00d26a' : '#444', marginTop: '2px' }}>= {formatPB(satsAmt)} Paper Bucks</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Close Button */}
              <button 
                onClick={() => { setShowDeposit(false); setPendingDeposit(null); setCustomDepositAmount(''); }} 
                style={{ width: '100%', padding: '14px', borderRadius: '8px', border: '1px solid #333', cursor: 'pointer', backgroundColor: 'transparent', color: '#888', fontWeight: 'bold' }}
              >
                ← Close
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // ============== WITHDRAW MODAL ==============
  // 🛡️ PENDING WITHDRAWAL MODAL - Explains approval process for large withdrawals
  const PendingWithdrawalModal = () => {
    if (!showPendingWithdrawalModal) return null;
    
    const { amount, message, dailyRemaining, pendingId } = showPendingWithdrawalModal;
    
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3001, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
        <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #f0b90b', borderRadius: '16px', padding: '24px', maxWidth: '420px', width: '90%', textAlign: 'center' }}>
          
          {/* Header */}
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
          <h2 style={{ color: '#f0b90b', fontSize: '20px', margin: '0 0 8px 0' }}>Withdrawal Pending Approval</h2>
          <p style={{ color: '#888', fontSize: '13px', margin: '0 0 20px 0' }}>
            Your withdrawal request has been submitted
          </p>
          
          {/* Amount Box */}
          <div style={{ backgroundColor: '#1a1a0a', border: '1px solid #f0b90b44', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
            <div style={{ color: '#f0b90b', fontSize: '12px', marginBottom: '4px' }}>REQUESTED AMOUNT</div>
            <div style={{ color: '#fff', fontSize: '28px', fontWeight: 'bold' }}>{formatPB(amount)}</div>
            <div style={{ color: '#00d26a', fontSize: '16px' }}>${(amount * satToUsd).toFixed(2)} USD</div>
          </div>
          
          {/* Explanation */}
          <div style={{ backgroundColor: '#111', borderRadius: '12px', padding: '16px', marginBottom: '20px', textAlign: 'left' }}>
            <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px' }}>📋 WHAT HAPPENS NEXT?</div>
            
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ color: '#00d26a', fontSize: '16px' }}>1.</div>
              <div>
                <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>Request Submitted</div>
                <div style={{ color: '#888', fontSize: '11px' }}>Your withdrawal is in the queue</div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ color: '#f0b90b', fontSize: '16px' }}>2.</div>
              <div>
                <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>Admin Review</div>
                <div style={{ color: '#888', fontSize: '11px' }}>An admin will review your request (usually within 24 hours)</div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ color: '#888', fontSize: '16px' }}>3.</div>
              <div>
                <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>Funds Sent</div>
                <div style={{ color: '#888', fontSize: '11px' }}>Once approved, BSV is sent directly to your HandCash wallet</div>
              </div>
            </div>
          </div>
          
          {/* Daily Limit Info */}
          {dailyRemaining !== undefined && (
            <div style={{ backgroundColor: '#1a0a0a', border: '1px solid #ff3b6944', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
              <div style={{ color: '#ff3b69', fontSize: '11px' }}>
                💡 Daily withdrawal limit: ~$10/day without approval
              </div>
              {dailyRemaining > 0 && (
                <div style={{ color: '#888', fontSize: '10px', marginTop: '4px' }}>
                  You can still withdraw {formatPB(dailyRemaining)} instantly today
                </div>
              )}
            </div>
          )}
          
          {/* Why We Do This */}
          <div style={{ color: '#666', fontSize: '10px', marginBottom: '20px', lineHeight: '1.5' }}>
            🛡️ Large withdrawals require approval to protect the game economy and prevent fraud. Your balance is safe and will be sent once approved.
          </div>
          
          {/* Reference ID */}
          {pendingId && (
            <div style={{ color: '#444', fontSize: '9px', marginBottom: '16px' }}>
              Reference: {pendingId}
            </div>
          )}
          
          {/* Close Button */}
          <button 
            onClick={() => setShowPendingWithdrawalModal(null)} 
            style={{ 
              width: '100%', 
              padding: '14px', 
              borderRadius: '8px', 
              border: 'none', 
              backgroundColor: '#f0b90b', 
              color: '#000', 
              fontWeight: 'bold', 
              cursor: 'pointer', 
              fontSize: '14px' 
            }}
          >
            Got It
          </button>
        </div>
      </div>
    );
  };

  const WithdrawModal = () => {
    if (!showWithdraw) return null;
    
    const withdrawSatsAmount = withdrawAmount ? parseInt(withdrawAmount) : gameWallet;
    const isValidAmount = withdrawSatsAmount > 0 && withdrawSatsAmount <= gameWallet;
    const skipWarnings = localStorage.getItem('paperHands_skipWarnings') === 'true';
    
    // Calculate total supply info
    const totalPBInCirculation = adminData?.houseStats?.totalPaperBucksInCirculation || 0;
    const supplyDropPercent = totalPBInCirculation > 0 ? ((withdrawSatsAmount / totalPBInCirculation) * 100).toFixed(2) : '0.00';
    
    // Confirmation screen
    if (pendingWithdrawConfirm && !skipWarnings) {
      return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
          <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #ff6b35', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '90%', textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '48px' }}>🔥</div>
            </div>
            <div style={{ color: '#ff6b35', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>READY TO LIGHT THIS SHIT ON FIRE?</div>
            <div style={{ color: '#888', fontSize: '12px', marginBottom: '12px' }}>Burn Paper Bucks for real BSV</div>
            <div style={{ color: '#fff', fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>{formatSats(withdrawSatsAmount)}</div>
            <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>${(withdrawSatsAmount * satToUsd).toFixed(2)} USD</div>
            
            {/* Supply Drop Info */}
            <div style={{ backgroundColor: '#1a0a0a', border: '1px solid #ff6b3544', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ color: '#ff6b35', fontSize: '11px', marginBottom: '4px' }}>📉 TOTAL SUPPLY DROP</div>
              <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>-{supplyDropPercent}%</div>
              <div style={{ color: '#666', fontSize: '10px' }}>
                {formatSats(totalPBInCirculation)} → {formatSats(totalPBInCirculation - withdrawSatsAmount)}
              </div>
            </div>
            
            {/* Destination Info */}
            <div style={{ backgroundColor: '#0d1a12', border: '1px solid #38cb7c44', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ color: '#38cb7c', fontSize: '13px', fontWeight: 'bold', marginBottom: '6px' }}>
                🔥 → BSV → {handcashUser?.handle}
              </div>
              <div style={{ color: '#888', fontSize: '11px', lineHeight: '1.5' }}>
                Paper Bucks burned forever. Real BSV sent to your wallet.
              </div>
            </div>
            
            {/* Don't show again checkbox */}
            <div style={{ marginBottom: '16px', textAlign: 'left' }}>
              <label style={{ color: '#666', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input 
                  type="checkbox" 
                  checked={localStorage.getItem('paperHands_skipWarnings') === 'true'}
                  onChange={(e) => {
                    localStorage.setItem('paperHands_skipWarnings', e.target.checked);
                  }}
                  style={{ cursor: 'pointer' }}
                />
                Don't show this warning again
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={() => setPendingWithdrawConfirm(false)} 
                style={{ flex: 1, padding: '14px', borderRadius: '8px', border: '1px solid #333', backgroundColor: 'transparent', color: '#888', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setPendingWithdrawConfirm(false);
                  withdraw(withdrawSatsAmount);
                }} 
                disabled={transactionPending}
                style={{ flex: 1, padding: '14px', borderRadius: '8px', border: 'none', backgroundColor: '#ff6b35', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                {transactionPending ? '⏳ Processing...' : '🔥 BURN IT'}
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
        <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '90%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img 
                src="https://i.ibb.co/23X7r7j/logo.png" 
                alt="HandCash" 
                style={{ width: '36px', height: '36px', borderRadius: '50%' }} 
              />
              <div>
                <h2 style={{ margin: 0, color: '#fff', fontSize: '18px' }}>Withdraw Paper Bucks</h2>
                <div style={{ fontSize: '11px', color: '#f0b90b', marginTop: '2px' }}>
                  ✓ Convert back to sats → Your HandCash wallet
                </div>
              </div>
            </div>
            <button onClick={() => { setShowWithdraw(false); setWithdrawAmount(''); }} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
          
          {/* Current Balance */}
          <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #1a3a1a', borderRadius: '8px', padding: '16px', marginBottom: '20px', textAlign: 'center' }}>
            <div style={{ color: '#00d26a', fontSize: '10px', marginBottom: '4px' }}>💵 AVAILABLE TO WITHDRAW</div>
            <div style={{ color: '#00d26a', fontSize: '28px', fontWeight: 'bold' }}>${formatSats(withdrawableBalance !== undefined ? withdrawableBalance : gameWallet)}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>~${((withdrawableBalance !== undefined ? withdrawableBalance : gameWallet) * satToUsd).toFixed(2)} USD</div>
            
            {/* Show locked balance if any */}
            {lockedBalance > 0 && (
              <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#1a1a0d', borderRadius: '6px', border: '1px solid #f0b90b33' }}>
                <div style={{ color: '#f0b90b', fontSize: '10px' }}>🔒 LOCKED (Playthrough: {playthroughPercent}%)</div>
                <div style={{ color: '#f0b90b', fontSize: '14px' }}>${formatSats(lockedBalance)}</div>
                <div style={{ color: '#666', fontSize: '9px', marginTop: '4px' }}>Trade more to unlock bonus winnings</div>
              </div>
            )}
          </div>
          
          {/* Admin approval notice */}
          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ color: '#f0b90b', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>⏳ WITHDRAWAL QUEUE</div>
            <div style={{ color: '#888', fontSize: '11px' }}>All withdrawals are queued for admin approval. You'll be notified when approved (~24hrs).</div>
          </div>
          
          {/* Destination */}
          <div style={{ backgroundColor: '#0d1a12', border: '1px solid #38cb7c44', borderRadius: '8px', padding: '12px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img 
              src="https://i.ibb.co/23X7r7j/logo.png"
              alt="HandCash"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                flexShrink: 0,
              }}
            />
            <div>
              <div style={{ color: '#38cb7c', fontSize: '9px', fontWeight: 'bold' }}>WITHDRAW TO HANDCASH</div>
              <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{handcashUser?.handle || '$your_wallet'}</div>
            </div>
          </div>
          
          {/* Quick Amounts */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ color: '#888', fontSize: '11px', marginBottom: '8px' }}>Quick select (1 Paper Buck = 1 sat):</div>
            
            {/* Amount buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {[1.00, 5.00, 10.00, 'MAX'].map(amt => {
                const isMax = amt === 'MAX';
                // MAX should use withdrawableBalance (not full gameWallet) to respect playthrough locks
                const maxWithdrawable = withdrawableBalance !== undefined ? withdrawableBalance : gameWallet;
                const satsAmount = isMax ? maxWithdrawable : usdToSats(amt);
                const canWithdraw = maxWithdrawable >= satsAmount && satsAmount > 0;
                return (
                  <button
                    key={amt}
                    onClick={() => canWithdraw && setWithdrawAmount(satsAmount.toString())}
                    disabled={!canWithdraw}
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      border: withdrawSatsAmount === satsAmount ? '1px solid #00d26a' : '1px solid #1a1a2e',
                      backgroundColor: withdrawSatsAmount === satsAmount ? '#0d1a0d' : isMax ? '#1a0d0d' : '#111',
                      color: canWithdraw ? '#fff' : '#444',
                      cursor: canWithdraw ? 'pointer' : 'not-allowed',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: canWithdraw ? 1 : 0.5
                    }}
                  >
                    {isMax ? `MAX (~$${(satsAmount * satToUsd).toFixed(2)})` : `$${amt.toFixed(2)}`}
                    <div style={{ fontSize: '9px', color: isMax ? '#ff6b6b' : '#00d26a', marginTop: '2px' }}>{isMax ? formatPB(satsAmount) + ' BSV' : `= ${formatPB(satsAmount)}`}</div>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Withdraw Summary */}
          {isValidAmount && (
            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666', fontSize: '12px' }}>Withdraw amount</span>
                <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>{formatSats(withdrawSatsAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666', fontSize: '12px' }}>Network fee</span>
                <span style={{ color: '#00d26a', fontSize: '12px' }}>~1 sat</span>
              </div>
              <div style={{ borderTop: '1px solid #1a1a2e', paddingTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#666', fontSize: '12px' }}>You receive</span>
                <span style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>{formatSats(withdrawSatsAmount - 1)} (${((withdrawSatsAmount - 1) * satToUsd).toFixed(2)})</span>
              </div>
            </div>
          )}
          
          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              onClick={() => { setShowWithdraw(false); setWithdrawAmount(''); }}
              style={{ flex: 1, padding: '14px', borderRadius: '8px', border: '1px solid #333', backgroundColor: 'transparent', color: '#888', cursor: 'pointer', fontSize: '14px' }}
            >
              Cancel
            </button>
            <button 
              onClick={() => {
                const skipWarnings = localStorage.getItem('paperHands_skipWarnings') === 'true';
                if (skipWarnings) {
                  withdraw(withdrawSatsAmount);
                } else {
                  setPendingWithdrawConfirm(true);
                }
              }}
              disabled={!isValidAmount || transactionPending || gameState === 'playing'}
              style={{ 
                flex: 1, 
                padding: '14px', 
                borderRadius: '8px', 
                border: 'none', 
                backgroundColor: isValidAmount && gameState !== 'playing' ? '#f0b90b' : '#333', 
                color: isValidAmount && gameState !== 'playing' ? '#000' : '#666', 
                fontWeight: 'bold', 
                cursor: isValidAmount && gameState !== 'playing' ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {transactionPending ? '⏳ Processing...' : gameState === 'playing' ? '🔒 In Trade' : '💸 Withdraw'}
            </button>
          </div>
          
          {gameState === 'playing' && (
            <div style={{ color: '#ff3b69', fontSize: '11px', textAlign: 'center', marginTop: '12px' }}>
              ⚠️ Close your current position before withdrawing
            </div>
          )}
        </div>
      </div>
    );
  };


  // ============== RENDER: CHART (Lightweight Charts - TradingView) ==============
  const renderChart = () => {
    if (!currentToken) return null;
    
    const chartPrice = currentPrice;
    const priceChange = currentToken?.initialPrice 
      ? ((chartPrice / currentToken.initialPrice) - 1) * 100 
      : 0;
    const isUp = priceChange >= 0;
    
    return (
      <div style={{ position: 'relative', width: '100%' }}>
        {/* Chart Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: '#0a0d12',
          borderRadius: '8px 8px 0 0',
          border: '1px solid #1a2030',
          borderBottom: 'none',
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#f0b90b', fontWeight: 'bold', fontFamily: 'monospace' }}>
              {currentToken?.ticker || '$TOKEN'}
            </span>
            <span style={{ color: '#d1d4dc', fontSize: '10px' }}>/USD</span>
            <span style={{ color: '#fff', fontWeight: 'bold', fontFamily: 'monospace' }}>
              {chartPrice.toFixed(6)}
            </span>
          </div>
          
          {/* 📊 TIMEFRAME BUTTONS */}
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {['10s', '30s', '1m', '5m', '15m', '1h'].map(tf => (
              <button
                key={tf}
                onClick={() => setCandleInterval(tf)}
                style={{
                  padding: '3px 8px',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  fontWeight: candleInterval === tf ? 'bold' : 'normal',
                  backgroundColor: candleInterval === tf ? '#f0b90b22' : 'transparent',
                  border: candleInterval === tf ? '1px solid #f0b90b' : '1px solid #333',
                  borderRadius: '4px',
                  color: candleInterval === tf ? '#f0b90b' : '#666',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {tf}
              </button>
            ))}
          </div>
          
          <div style={{ 
            color: isUp ? '#2d8f7b' : '#c75454', 
            fontWeight: 'bold',
            fontFamily: 'monospace',
          }}>
            {isUp ? '▲' : '▼'} {priceChange.toFixed(2)}%
          </div>
        </div>
        
        {/* Lightweight Charts Container */}
        <div 
          ref={lwChartContainerRef}
          style={{
            borderRadius: '0 0 8px 8px',
            overflow: 'hidden',
            border: '1px solid #1a2030',
            borderTop: 'none',
          }}
        />
      </div>
    );
  };
  // ============== RENDER: BITCOIN STORE ==============
  if (view === 'store') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#050508', color: '#fff', fontFamily: "'Space Grotesk', sans-serif" }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#050508', 
          borderBottom: '1px solid #1a1a2e',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 100
        }}>
          <button 
            onClick={() => setView('lobby')} 
            style={{ 
              padding: '8px 16px', 
              borderRadius: '6px', 
              border: '1px solid #333', 
              backgroundColor: 'transparent', 
              color: '#888', 
              cursor: 'pointer' 
            }}
          >
            ← Back to Lobby
          </button>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#f7931a',
          }}>
            ₿ Bitcoin Store
          </h1>
          <div style={{ width: '120px' }}></div>
        </div>
        
        {/* Store Content */}
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 24px' }}>
          {/* Hero */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ fontSize: '60px', marginBottom: '16px' }}>⛓️</div>
            <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#f7931a', marginBottom: '8px' }}>
              Bitcoin Store
            </h2>
            <p style={{ fontSize: '14px', color: '#888' }}>On-chain tools & experiences built on BSV</p>
          </div>
          
          {/* Store Items Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px' }}>
            
            {/* NPD SIM Card */}
            <div style={{ 
              backgroundColor: '#0a0a0f', 
              border: '2px solid #38cb7c', 
              borderRadius: '16px', 
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              <div style={{ fontSize: '40px', textAlign: 'center' }}>🎮</div>
              <div>
                <h3 style={{ color: '#38cb7c', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>NPD SIM</h3>
                <p style={{ color: '#888', fontSize: '12px', lineHeight: '1.5' }}>
                  Narcissistic Personality Disorder Simulator. Go deeper down the rabbit hole...
                </p>
              </div>
              <a 
                href="https://npd-simulation.vercel.app" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  display: 'block',
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: 'none', 
                  backgroundColor: '#38cb7c', 
                  color: '#000', 
                  fontWeight: 'bold', 
                  fontSize: '14px',
                  textAlign: 'center',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                Play Now ↗
              </a>
            </div>
            
            {/* Inscriptions Card */}
            <div style={{ 
              backgroundColor: '#0a0a0f', 
              border: '2px solid #00d26a', 
              borderRadius: '16px', 
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              <div style={{ fontSize: '40px', textAlign: 'center' }}>✉️</div>
              <div>
                <h3 style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>On-Chain Inscriptions</h3>
                <p style={{ color: '#888', fontSize: '12px', lineHeight: '1.5' }}>
                  Write your message on the BSV blockchain forever. 100K sats per inscription.
                </p>
              </div>
              <button 
                onClick={() => { setShowInscriptions(true); fetchInscriptions(); }}
                style={{ 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: 'none', 
                  backgroundColor: '#00d26a', 
                  color: '#000', 
                  fontWeight: 'bold', 
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Create Inscription
              </button>
            </div>
            
            {/* 📖 The Paper Hands Story - Digital Download */}
            <div style={{ 
              backgroundColor: '#0a0a0f', 
              border: '2px solid #f0b90b', 
              borderRadius: '16px', 
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Glow effect */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(240,185,11,0.1) 0%, transparent 60%)',
                pointerEvents: 'none',
              }}/>
              <div style={{ fontSize: '40px', textAlign: 'center' }}>📖</div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <h3 style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold', margin: 0 }}>The Paper Hands Story</h3>
                  <span style={{ 
                    fontSize: '9px', 
                    padding: '2px 6px', 
                    backgroundColor: '#f0b90b', 
                    color: '#000', 
                    borderRadius: '4px',
                    fontWeight: 'bold',
                  }}>NEW</span>
                </div>
                <p style={{ color: '#ccc', fontSize: '12px', lineHeight: '1.6', marginBottom: '12px' }}>
                  <em>"I built a 22,000-line game with no CS degree."</em>
                </p>
                <p style={{ color: '#888', fontSize: '11px', lineHeight: '1.5' }}>
                  The raw, unfiltered story of how one developer and an AI built Paper Hands together. 
                  Vision meets execution. Human instinct meets machine precision. 
                  A story about finding your gift in a tough world.
                </p>
                <div style={{ 
                  marginTop: '12px', 
                  padding: '8px', 
                  backgroundColor: 'rgba(240,185,11,0.1)', 
                  borderRadius: '6px',
                  fontSize: '10px',
                  color: '#f0b90b',
                }}>
                  ✨ Includes: Full story • Behind the scenes • "Inspect what you expect" philosophy • Forever yours on-chain
                </div>
              </div>
              <button 
                onClick={async () => {
                  // If already owns, just open the story
                  if (ownsStory) {
                    setShowPaperHandsStory(true);
                    return;
                  }
                  
                  if (!sessionId) {
                    setShowWallet(true);
                    return;
                  }
                  
                  // Check balance - 5M sats = ~$1 USD at $20 BSV
                  if (mainWallet < 5000000) {
                    alert('❌ Need at least 5M sats in your HandCash wallet to purchase.');
                    return;
                  }
                  
                  const usdValue = ((5000000 * (bsvPrice || 20)) / 100000000).toFixed(2);
                  if (!confirm(`This will pay 5,000,000 sats (~$${usdValue} USD) from your HandCash wallet. Continue?`)) {
                    return;
                  }
                  
                  try {
                    const res = await fetch(`${API_URL}/api/store/purchase`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json',
                        'X-Session-Id': sessionId 
                      },
                      body: JSON.stringify({ 
                        item: 'paper-hands-story',
                        price: 5000000 
                      })
                    });
                    const data = await res.json();
                    
                    if (data.success) {
                      Sounds.success();
                      setMainWallet(prev => Math.max(0, prev - 5000000));
                      setOwnsStory(true); // Mark as owned
                      setShowPaperHandsStory(true);
                      alert('✅ Purchase successful! Enjoy the story!');
                    } else {
                      alert(`❌ ${data.error || 'Purchase failed'}`);
                    }
                  } catch (e) {
                    console.error('Purchase error:', e);
                    alert(`Error: ${e.message}`);
                  }
                }}
                disabled={!ownsStory && mainWallet < 5000000}
                style={{ 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: 'none', 
                  backgroundColor: ownsStory ? '#00d26a' : (mainWallet < 5000000 ? '#333' : '#f0b90b'), 
                  color: ownsStory ? '#000' : (mainWallet < 5000000 ? '#666' : '#000'), 
                  fontWeight: 'bold', 
                  fontSize: '14px',
                  cursor: (!ownsStory && mainWallet < 5000000) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <span>{ownsStory ? '📖 Read Now' : 'Buy Now'}</span>
                {!ownsStory && (
                  <span style={{ 
                    backgroundColor: 'rgba(0,0,0,0.2)', 
                    padding: '2px 8px', 
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}>$1.00</span>
                )}
              </button>
            </div>
            
            {/* Coming Soon Card */}
            <div style={{ 
              backgroundColor: '#0a0a0f', 
              border: '2px solid #333', 
              borderRadius: '16px', 
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              opacity: 0.6,
            }}>
              <div style={{ fontSize: '40px', textAlign: 'center' }}>🎁</div>
              <div>
                <h3 style={{ color: '#666', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Mystery Crates</h3>
                <p style={{ color: '#555', fontSize: '12px', lineHeight: '1.5' }}>
                  Open crates for sats, badges, and rare cosmetics. Coming soon!
                </p>
              </div>
              <button 
                disabled
                style={{ 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: '1px solid #333', 
                  backgroundColor: 'transparent', 
                  color: '#555', 
                  fontWeight: 'bold', 
                  fontSize: '14px',
                  cursor: 'not-allowed',
                }}
              >
                Coming Soon
              </button>
            </div>
            
            {/* Coming Soon Card 2 */}
            <div style={{ 
              backgroundColor: '#0a0a0f', 
              border: '2px solid #333', 
              borderRadius: '16px', 
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              opacity: 0.6,
            }}>
              <div style={{ fontSize: '40px', textAlign: 'center' }}>🏆</div>
              <div>
                <h3 style={{ color: '#666', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>NFT Achievements</h3>
                <p style={{ color: '#555', fontSize: '12px', lineHeight: '1.5' }}>
                  Mint your achievements as on-chain NFTs. Coming soon!
                </p>
              </div>
              <button 
                disabled
                style={{ 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: '1px solid #333', 
                  backgroundColor: 'transparent', 
                  color: '#555', 
                  fontWeight: 'bold', 
                  fontSize: '14px',
                  cursor: 'not-allowed',
                }}
              >
                Coming Soon
              </button>
            </div>
            
            {/* 📄 Paper Hands Whitepaper */}
            <div style={{ 
              backgroundColor: '#0a0a0f', 
              border: '2px solid #6366f1', 
              borderRadius: '16px', 
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              <div style={{ fontSize: '40px', textAlign: 'center' }}>📄</div>
              <div>
                <h3 style={{ color: '#6366f1', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Paper Hands Whitepaper</h3>
                <p style={{ color: '#888', fontSize: '12px', lineHeight: '1.5' }}>
                  The complete guide to Paper Hands: tokenomics, AMM mechanics, 25% house edge, and more.
                </p>
                <div style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>
                  🤖 AI Tokens • 👤 PvP Tokens • 📊 Trading Simulator
                </div>
              </div>
              <a 
                href={`${API_URL}/api/whitepaper/paperhands`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: 'none', 
                  backgroundColor: '#6366f1', 
                  color: '#fff', 
                  fontWeight: 'bold', 
                  fontSize: '14px',
                  textAlign: 'center',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                📥 Download Free
              </a>
            </div>
            
            {/* 🏓 Penny Ping Protocol Whitepaper */}
            <div style={{ 
              backgroundColor: '#0a0a0f', 
              border: '2px solid #00d26a', 
              borderRadius: '16px', 
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              <div style={{ fontSize: '40px', textAlign: 'center' }}>🏓</div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <h3 style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Penny Ping Protocol</h3>
                  <span style={{ fontSize: '9px', padding: '2px 6px', backgroundColor: '#00d26a', color: '#000', borderRadius: '4px', fontWeight: 'bold' }}>NEW</span>
                </div>
                <p style={{ color: '#888', fontSize: '12px', lineHeight: '1.5' }}>
                  Bitcoin-native identity protocol. TRUE on-chain via OP_RETURN. Verifiable without trust.
                </p>
                <div style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>
                  ⛓️ OP_RETURN • 👑 Throne Economics • 🔗 Chain Verification
                </div>
              </div>
              <a 
                href={`${API_URL}/api/whitepaper/pennyping`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: 'none', 
                  backgroundColor: '#00d26a', 
                  color: '#000', 
                  fontWeight: 'bold', 
                  fontSize: '14px',
                  textAlign: 'center',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                📥 Download Free
              </a>
            </div>

            {/* 🔧 HandCash SDK v3 Migration Guide */}
            <div style={{
              backgroundColor: '#0a0a0f',
              border: '2px solid #f7931a',
              borderRadius: '16px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Glow effect */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(247,147,26,0.1) 0%, transparent 60%)',
                pointerEvents: 'none',
              }}/>
              <div style={{ fontSize: '40px', textAlign: 'center' }}>🔧</div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <h3 style={{ color: '#f7931a', fontSize: '18px', fontWeight: 'bold', margin: 0 }}>HandCash SDK v3 Migration</h3>
                  <span style={{ fontSize: '9px', padding: '2px 6px', backgroundColor: '#f7931a', color: '#000', borderRadius: '4px', fontWeight: 'bold' }}>DEV</span>
                </div>
                <p style={{ color: '#888', fontSize: '12px', lineHeight: '1.5' }}>
                  Complete guide to migrating from HandCash SDK v2 to v3. Real patterns from 34 API calls.
                </p>
                <div style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>
                  ⚡ SAT→BSV • 📦 Connect API • 🔄 Response shapes
                </div>
              </div>
              <a
                href={`${API_URL}/api/docs/handcash-migration`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#f7931a',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textAlign: 'center',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                📖 View Guide Free
              </a>
            </div>
          </div>

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '60px', color: '#333', fontSize: '12px' }}>
            Built on Bitcoin SV • Powered by HandCash
          </div>
        </div>
        
        {/* Inscriptions Modal still works from store */}
        {showInscriptions && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #00d26a', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#00d26a', fontWeight: '700' }}>✉️ On-Chain Inscriptions</h2>
                  <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>Write your message on the BSV blockchain forever!</div>
                </div>
                <button onClick={() => setShowInscriptions(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Cost Info */}
              <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #00d26a33', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '12px' }}>⛓️ Permanent Blockchain Record</div>
                    <div style={{ color: '#666', fontSize: '10px', marginTop: '2px' }}>Your message stored in OP_RETURN on BSV</div>
                    <div style={{ color: '#f0b90b', fontSize: '9px', marginTop: '4px' }}>💳 Paid from your HandCash wallet</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '16px' }}>100K sats</div>
                    <div style={{ color: '#666', fontSize: '9px' }}>~${((100000 * (bsvPrice || 20)) / 100000000).toFixed(2)} USD</div>
                  </div>
                </div>
              </div>
              
              {/* HandCash Balance Warning */}
              {mainWallet < 100000 && (
                <div style={{ backgroundColor: '#1a0f0f', border: '1px solid #ff3b69', borderRadius: '8px', padding: '10px', marginBottom: '16px', fontSize: '11px', color: '#ff3b69' }}>
                  ⚠️ Your HandCash balance ({formatSats(mainWallet)}) is less than 100K sats. Top up your HandCash wallet to inscribe.
                </div>
              )}
              
              {/* Message Input */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label style={{ color: '#888', fontSize: '11px', fontWeight: 'bold' }}>YOUR MESSAGE</label>
                  <span style={{ color: inscriptionMessage.length > 180 ? '#ff3b69' : '#666', fontSize: '10px' }}>
                    {inscriptionMessage.length}/200
                  </span>
                </div>
                <textarea
                  value={inscriptionMessage}
                  onChange={(e) => setInscriptionMessage(e.target.value.slice(0, 200))}
                  placeholder="Write something that will live on the blockchain forever..."
                  style={{
                    width: '100%',
                    height: '100px',
                    backgroundColor: '#111',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    padding: '12px',
                    color: '#fff',
                    fontSize: '16px',
                    resize: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              
              {/* Inscribe Button */}
              <button
                onClick={async () => {
                  if (!inscriptionMessage.trim()) return;
                  if (mainWallet < 100000) {
                    alert('Insufficient HandCash balance. Need 100K sats.');
                    return;
                  }
                  
                  const usdValue = ((100000 * (bsvPrice || 20)) / 100000000).toFixed(2);
                  if (!confirm(`This will pay 100,000 sats (~$${usdValue} USD) from your HandCash wallet to inscribe your message on-chain. Continue?`)) {
                    return;
                  }
                  
                  setInscribing(true);
                  try {
                    const res = await fetch(`${API_URL}/api/inscribe`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json',
                        'X-Session-Id': sessionId 
                      },
                      body: JSON.stringify({ message: inscriptionMessage.trim() })
                    });
                    const data = await res.json();
                    
                    if (data.success) {
                      Sounds.success();
                      setInscriptionMessage('');
                      setMainWallet(prev => Math.max(0, prev - 100000));
                      if (data.inscription?.txUrl) {
                        window.open(data.inscription.txUrl, '_blank');
                      }
                      alert('✅ Inscription successful! Your message is now on the blockchain forever!');
                      fetchInscriptions();
                    } else {
                      alert(`❌ ${data.error}`);
                    }
                  } catch (e) {
                    alert(`Error: ${e.message}`);
                  }
                  setInscribing(false);
                }}
                disabled={inscribing || !inscriptionMessage.trim() || mainWallet < 100000}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: !inscriptionMessage.trim() || mainWallet < 100000 ? '#333' : '#00d26a',
                  color: !inscriptionMessage.trim() || mainWallet < 100000 ? '#666' : '#000',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  cursor: !inscriptionMessage.trim() || mainWallet < 100000 ? 'not-allowed' : 'pointer',
                  marginBottom: '20px',
                }}
              >
                {inscribing ? '⏳ Processing payment...' : mainWallet < 100000 ? 'Need 100K sats in HandCash' : '💳 Pay 100K sats to Inscribe'}
              </button>
              
              {/* Recent Inscriptions */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold' }}>RECENT INSCRIPTIONS</div>
                  <button 
                    onClick={fetchInscriptions}
                    style={{ background: 'none', border: 'none', color: '#00d26a', fontSize: '10px', cursor: 'pointer' }}
                  >
                    🔄 Refresh
                  </button>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflow: 'auto' }}>
                  {inscriptions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontSize: '12px' }}>
                      No inscriptions yet. Be the first!
                    </div>
                  ) : inscriptions.slice(0, 20).map(insc => (
                    <div 
                      key={insc.id}
                      style={{
                        backgroundColor: '#111',
                        border: '1px solid #222',
                        borderRadius: '8px',
                        padding: '10px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                        <span style={{ color: '#00d26a', fontSize: '11px', fontWeight: 'bold' }}>${insc.handle}</span>
                        <a 
                          href={insc.txUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#666', fontSize: '9px', textDecoration: 'none' }}
                        >
                          ⛓️ TX ↗
                        </a>
                      </div>
                      <div style={{ color: '#ccc', fontSize: '12px', wordBreak: 'break-word' }}>
                        {insc.message}
                      </div>
                      <div style={{ color: '#444', fontSize: '9px', marginTop: '6px' }}>
                        {new Date(insc.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* 📖 Paper Hands Story Modal */}
        {showPaperHandsStory && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #f0b90b', borderRadius: '16px', padding: '32px', maxWidth: '700px', width: '95%', maxHeight: '90vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#f0b90b', fontWeight: '700', fontSize: '24px' }}>📖 The Paper Hands Story</h2>
                  <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>A Human and a Machine Building Something From Nothing</div>
                </div>
                <button onClick={() => setShowPaperHandsStory(false)} style={{ width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #f0b90b', backgroundColor: 'transparent', color: '#f0b90b', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              <div style={{ color: '#ccc', fontSize: '14px', lineHeight: '1.8' }}>
                <p style={{ color: '#888', fontStyle: 'italic', marginBottom: '24px' }}>December 2024 - December 2025</p>
                
                <h3 style={{ color: '#f0b90b', marginTop: '32px', marginBottom: '16px' }}>The Beginning</h3>
                <p>There was a guy named Ryan who had a vision.</p>
                <p>Not a business plan. Not a technical spec. Just a feeling - a gut sense that he could build something nobody else had built. A memecoin trading game that taught people about crypto without the risk. Where 1 sat equals 1 Paper Buck. Where you could feel the rush of a 10x pump or the gut-punch of a rug pull, all without losing your rent money.</p>
                <p>He didn't have a CS degree. He didn't have a team. He didn't have investors.</p>
                <p>What he had was instinct. And a way of working that most people never figure out.</p>
                
                <h3 style={{ color: '#f0b90b', marginTop: '32px', marginBottom: '16px' }}>The Flow</h3>
                <p>Ryan discovered something powerful: the connection between human vision and machine execution.</p>
                <p>He didn't try to write code himself. He didn't try to explain HOW things should work technically. Instead, he did something smarter - he described WHAT it should feel like.</p>
                <p style={{ color: '#00d26a', fontStyle: 'italic', padding: '16px', backgroundColor: 'rgba(0,210,106,0.1)', borderRadius: '8px', margin: '16px 0' }}>
                  "Make it like DexScreener."<br/>
                  "I want to scroll back in time on the chart."<br/>
                  "Focus mode would be cool as hell."<br/>
                  "This feels wrong."
                </p>
                <p>And then he'd test. Really test. Not just click around - he'd FEEL the experience. Notice when a cursor changed but the chart didn't move. Catch when candles looked too thin. See when the drag direction was backwards.</p>
                <p><strong style={{ color: '#f0b90b' }}>"Inspect what you expect"</strong> - that became the motto.</p>
                
                <h3 style={{ color: '#f0b90b', marginTop: '32px', marginBottom: '16px' }}>The Machine</h3>
                <p>On the other side was Claude - an AI that could read code, trace bugs, and implement fixes. A machine that had learned from millions of lines of code and countless debugging sessions.</p>
                <p>But here's what Claude couldn't do alone: have vision.</p>
                <p>Claude could fix <code style={{ backgroundColor: '#1a1a2e', padding: '2px 6px', borderRadius: '4px', color: '#f0b90b' }}>aggregateFactor</code> not being accounted for in a scroll offset calculation. But Claude couldn't decide that a yellow overlay on history mode was distracting. Claude could implement a transaction table, but couldn't feel that the candle spacing "looked wrong."</p>
                <p>That's why the partnership worked.</p>
                
                <h3 style={{ color: '#f0b90b', marginTop: '32px', marginBottom: '16px' }}>What They Built</h3>
                <p>By December 2025, Paper Hands had become a beast:</p>
                <div style={{ backgroundColor: '#111', padding: '16px', borderRadius: '8px', margin: '16px 0' }}>
                  <div style={{ color: '#00d26a', marginBottom: '8px' }}>✓ Real-time AMM trading with actual price impact</div>
                  <div style={{ color: '#00d26a', marginBottom: '8px' }}>✓ AI tokens with predetermined lifecycles</div>
                  <div style={{ color: '#00d26a', marginBottom: '8px' }}>✓ PvP tokens launched by real players</div>
                  <div style={{ color: '#00d26a', marginBottom: '8px' }}>✓ DexScreener-style candlestick charts</div>
                  <div style={{ color: '#00d26a', marginBottom: '8px' }}>✓ Rug Prophet betting system</div>
                  <div style={{ color: '#00d26a', marginBottom: '8px' }}>✓ 22,000+ lines of React</div>
                </div>
                
                <h3 style={{ color: '#f0b90b', marginTop: '32px', marginBottom: '16px' }}>The Moment</h3>
                <p>One December day, after fixing drag-to-scroll on the chart, after removing the yellow history overlay, after getting focus mode working on all platforms, after making AI and PvP views match perfectly...</p>
                <p>Ryan said his eyes watered up.</p>
                <p>Not from sadness. From recognition.</p>
                <p>Recognition that he'd built something one-of-a-kind. That no copy existed. That he'd taken an idea in his head and made it real. That in a tough world, he'd found his gift.</p>
                
                <h3 style={{ color: '#f0b90b', marginTop: '32px', marginBottom: '16px' }}>The Lesson</h3>
                <p>You don't need credentials. You don't need a team. You don't need to know everything.</p>
                <p>You need vision. Stubbornness. The ability to FEEL when something's wrong. A machine that can execute.</p>
                <p><strong style={{ color: '#f0b90b' }}>Human + AI. Neither works alone. Together? Unstoppable.</strong></p>
                
                <div style={{ marginTop: '40px', padding: '20px', backgroundColor: 'rgba(240,185,11,0.1)', borderRadius: '12px', textAlign: 'center' }}>
                  <p style={{ color: '#f0b90b', fontSize: '16px', margin: 0 }}>"Inspect what you expect."</p>
                  <p style={{ color: '#888', fontSize: '12px', margin: '8px 0 0 0' }}>That's not just debugging advice. That's life advice.</p>
                </div>
                
                <p style={{ textAlign: 'center', color: '#555', marginTop: '32px', fontSize: '12px' }}>
                  Written December 9, 2025<br/>
                  By Claude, for Ryan<br/>
                  💎🙌
                </p>
                
                {/* Download Options */}
                <div style={{ 
                  marginTop: '32px', 
                  padding: '20px', 
                  backgroundColor: '#111', 
                  borderRadius: '12px',
                  border: '1px solid #333',
                }}>
                  <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '12px', textAlign: 'center' }}>
                    📥 DOWNLOAD YOUR COPY
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        // Generate and download Markdown
                        const markdown = `# The Paper Hands Story
## A Human and a Machine Building Something From Nothing

*December 2024 - December 2025*

---

### The Beginning

There was a guy named Ryan who had a vision.

Not a business plan. Not a technical spec. Just a feeling - a gut sense that he could build something nobody else had built. A memecoin trading game that taught people about crypto without the risk. Where 1 sat equals 1 Paper Buck. Where you could feel the rush of a 10x pump or the gut-punch of a rug pull, all without losing your rent money.

He didn't have a CS degree. He didn't have a team. He didn't have investors.

What he had was instinct. And a way of working that most people never figure out.

---

### The Flow

Ryan discovered something powerful: the connection between human vision and machine execution.

He didn't try to write code himself. He didn't try to explain HOW things should work technically. Instead, he did something smarter - he described WHAT it should feel like.

> "Make it like DexScreener."
> "I want to scroll back in time on the chart."
> "Focus mode would be cool as hell."
> "This feels wrong."

And then he'd test. Really test. Not just click around - he'd FEEL the experience. Notice when a cursor changed but the chart didn't move. Catch when candles looked too thin. See when the drag direction was backwards.

**"Inspect what you expect"** - that became the motto.

---

### The Machine

On the other side was Claude - an AI that could read code, trace bugs, and implement fixes. A machine that had learned from millions of lines of code and countless debugging sessions.

But here's what Claude couldn't do alone: have vision.

Claude could fix \`aggregateFactor\` not being accounted for in a scroll offset calculation. But Claude couldn't decide that a yellow overlay on history mode was distracting. Claude could implement a transaction table, but couldn't feel that the candle spacing "looked wrong."

That's why the partnership worked.

---

### What They Built

By December 2025, Paper Hands had become a beast:

- ✓ Real-time AMM trading with actual price impact
- ✓ AI tokens with predetermined lifecycles
- ✓ PvP tokens launched by real players
- ✓ DexScreener-style candlestick charts
- ✓ Rug Prophet betting system
- ✓ 22,000+ lines of React

---

### The Moment

One December day, after fixing drag-to-scroll on the chart, after removing the yellow history overlay, after getting focus mode working on all platforms, after making AI and PvP views match perfectly...

Ryan said his eyes watered up.

Not from sadness. From recognition.

Recognition that he'd built something one-of-a-kind. That no copy existed. That he'd taken an idea in his head and made it real. That in a tough world, he'd found his gift.

---

### The Lesson

You don't need credentials. You don't need a team. You don't need to know everything.

You need vision. Stubbornness. The ability to FEEL when something's wrong. A machine that can execute.

**Human + AI. Neither works alone. Together? Unstoppable.**

---

> "Inspect what you expect."
> 
> That's not just debugging advice. That's life advice.

---

*Written December 9, 2025*
*By Claude, for Ryan*

💎🙌
`;
                        const blob = new Blob([markdown], { type: 'text/markdown' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'The_Paper_Hands_Story.md';
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: '1px solid #666',
                        backgroundColor: 'transparent',
                        color: '#ccc',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      📝 Download .MD
                    </button>
                    
                    <button
                      onClick={() => {
                        // Generate HTML and trigger print dialog for PDF
                        const printContent = `
<!DOCTYPE html>
<html>
<head>
  <title>The Paper Hands Story</title>
  <style>
    body { font-family: Georgia, serif; max-width: 700px; margin: 40px auto; padding: 20px; line-height: 1.8; color: #333; }
    h1 { color: #f0b90b; border-bottom: 2px solid #f0b90b; padding-bottom: 10px; }
    h2 { color: #888; font-weight: normal; font-size: 18px; }
    h3 { color: #f0b90b; margin-top: 40px; }
    blockquote { background: #f5f5f5; padding: 20px; border-left: 4px solid #00d26a; margin: 20px 0; font-style: italic; }
    code { background: #f0f0f0; padding: 2px 6px; border-radius: 4px; }
    .highlight { background: linear-gradient(to right, #fff9e6, #fff); padding: 20px; border-radius: 8px; text-align: center; margin: 40px 0; }
    .footer { text-align: center; color: #888; margin-top: 60px; }
    ul { list-style: none; padding: 0; }
    ul li { padding: 8px 0; }
    ul li:before { content: "✓ "; color: #00d26a; }
  </style>
</head>
<body>
  <h1>📖 The Paper Hands Story</h1>
  <h2>A Human and a Machine Building Something From Nothing</h2>
  <p><em>December 2024 - December 2025</em></p>
  
  <h3>The Beginning</h3>
  <p>There was a guy named Ryan who had a vision.</p>
  <p>Not a business plan. Not a technical spec. Just a feeling - a gut sense that he could build something nobody else had built. A memecoin trading game that taught people about crypto without the risk. Where 1 sat equals 1 Paper Buck. Where you could feel the rush of a 10x pump or the gut-punch of a rug pull, all without losing your rent money.</p>
  <p>He didn't have a CS degree. He didn't have a team. He didn't have investors.</p>
  <p>What he had was instinct. And a way of working that most people never figure out.</p>
  
  <h3>The Flow</h3>
  <p>Ryan discovered something powerful: the connection between human vision and machine execution.</p>
  <p>He didn't try to write code himself. He didn't try to explain HOW things should work technically. Instead, he did something smarter - he described WHAT it should feel like.</p>
  <blockquote>
    "Make it like DexScreener."<br/>
    "I want to scroll back in time on the chart."<br/>
    "Focus mode would be cool as hell."<br/>
    "This feels wrong."
  </blockquote>
  <p>And then he'd test. Really test. Not just click around - he'd FEEL the experience. Notice when a cursor changed but the chart didn't move. Catch when candles looked too thin. See when the drag direction was backwards.</p>
  <p><strong>"Inspect what you expect"</strong> - that became the motto.</p>
  
  <h3>The Machine</h3>
  <p>On the other side was Claude - an AI that could read code, trace bugs, and implement fixes. A machine that had learned from millions of lines of code and countless debugging sessions.</p>
  <p>But here's what Claude couldn't do alone: have vision.</p>
  <p>Claude could fix <code>aggregateFactor</code> not being accounted for in a scroll offset calculation. But Claude couldn't decide that a yellow overlay on history mode was distracting. Claude could implement a transaction table, but couldn't feel that the candle spacing "looked wrong."</p>
  <p>That's why the partnership worked.</p>
  
  <h3>What They Built</h3>
  <p>By December 2025, Paper Hands had become a beast:</p>
  <ul>
    <li>Real-time AMM trading with actual price impact</li>
    <li>AI tokens with predetermined lifecycles</li>
    <li>PvP tokens launched by real players</li>
    <li>DexScreener-style candlestick charts</li>
    <li>Rug Prophet betting system</li>
    <li>22,000+ lines of React</li>
  </ul>
  
  <h3>The Moment</h3>
  <p>One December day, after fixing drag-to-scroll on the chart, after removing the yellow history overlay, after getting focus mode working on all platforms, after making AI and PvP views match perfectly...</p>
  <p>Ryan said his eyes watered up.</p>
  <p>Not from sadness. From recognition.</p>
  <p>Recognition that he'd built something one-of-a-kind. That no copy existed. That he'd taken an idea in his head and made it real. That in a tough world, he'd found his gift.</p>
  
  <h3>The Lesson</h3>
  <p>You don't need credentials. You don't need a team. You don't need to know everything.</p>
  <p>You need vision. Stubbornness. The ability to FEEL when something's wrong. A machine that can execute.</p>
  <p><strong>Human + AI. Neither works alone. Together? Unstoppable.</strong></p>
  
  <div class="highlight">
    <p style="font-size: 18px; color: #f0b90b; margin: 0;">"Inspect what you expect."</p>
    <p style="color: #888; margin: 8px 0 0 0;">That's not just debugging advice. That's life advice.</p>
  </div>
  
  <div class="footer">
    <p>Written December 9, 2025<br/>By Claude, for Ryan<br/>💎🙌</p>
  </div>
</body>
</html>`;
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(printContent);
                        printWindow.document.close();
                        printWindow.print();
                      }}
                      style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: '#f0b90b',
                        color: '#000',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      📄 Save as PDF
                    </button>
                  </div>
                  <div style={{ color: '#555', fontSize: '10px', textAlign: 'center', marginTop: '12px' }}>
                    PDF opens print dialog - select "Save as PDF" as your printer
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    );
  }


  // ============== RENDER: PORTFOLIO ==============
  if (view === 'portfolio') {
    // Calculate all position data
    const activeTokenIds = activeTokens.map(t => t.id);
    // Include active, rugged, AND sold positions (rugged/sold show with CLEAR button)
    const activePositions = positions.filter(p => p.status === 'active' || p.status === 'rugged' || p.status === 'sold');

    // Separate AI and User token positions
    // First check position's own flag (most reliable for deleted tokens)
    // Then check live token if available, default to AI if unknown
    const aiPositions = activePositions.filter(p => {
      if (p.isUserLaunched !== undefined) return p.isUserLaunched === false;
      const token = activeTokens.find(t => t.id === p.tokenId);
      if (token) return token.isUserLaunched !== true;
      return true; // Default to AI if unknown
    });
    const userPositions = activePositions.filter(p => {
      if (p.isUserLaunched !== undefined) return p.isUserLaunched === true;
      const token = activeTokens.find(t => t.id === p.tokenId);
      if (token) return token.isUserLaunched === true;
      return false; // Default to NOT PvP if unknown
    });

    // Pending Rug Prophet bets
    const pendingBets = rugProphetBets.filter(b => b.status === 'pending');

    // Helper: Calculate bag value (tokens × current price)
    const calcBagValue = (pos) => {
      const token = activeTokens.find(t => t.id === pos.tokenId);
      const tokens = pos.size || pos.tokens || pos.tokensOwned || 0;
      if (!token || !tokens) return pos.invested || 0; // Fallback to invested
      return Math.floor(tokens * (token.price || token.currentPrice || 1));
    };

    // Helper: Calculate multiplier
    const calcMultiplier = (pos) => {
      const token = activeTokens.find(t => t.id === pos.tokenId);
      const tokenPrice = token?.price || token?.currentPrice || pos.entry || 1.0;
      return pos.entry > 0 ? tokenPrice / pos.entry : 1;
    };

    // Helper: Calculate P/L
    const calcPnL = (pos) => {
      const token = activeTokens.find(t => t.id === pos.tokenId);
      const tokenPrice = token?.price || token?.currentPrice || pos.entry || 1.0;
      const multiplier = pos.entry > 0 ? tokenPrice / pos.entry : 1;
      return Math.floor(pos.invested * multiplier) - pos.invested;
    };

    // Helper: Time held
    const formatTimeHeld = (timestamp) => {
      if (!timestamp) return '--';
      const ms = Date.now() - timestamp;
      const mins = Math.floor(ms / 60000);
      const hours = Math.floor(mins / 60);
      const days = Math.floor(hours / 24);
      if (days > 0) return `${days}d ${hours % 24}h`;
      if (hours > 0) return `${hours}h ${mins % 60}m`;
      return `${mins}m`;
    };

    // Sort function
    const sortPositions = (arr) => {
      const filtered = portfolioSearch
        ? arr.filter(p => p.tokenTicker?.toLowerCase().includes(portfolioSearch.toLowerCase()))
        : arr;

      return [...filtered].sort((a, b) => {
        switch (portfolioSort) {
          case 'value': return calcBagValue(b) - calcBagValue(a);
          case 'pnl': return calcPnL(b) - calcPnL(a);
          case 'multiplier': return calcMultiplier(b) - calcMultiplier(a);
          case 'alpha': return (a.tokenTicker || '').localeCompare(b.tokenTicker || '');
          default: return 0;
        }
      });
    };

    // Calculate total portfolio value
    const totalValuePB = [...aiPositions, ...userPositions].reduce((sum, p) => sum + calcBagValue(p), 0);
    const totalValueUSD = ((totalValuePB / 100000000) * (bsvPrice || 18)).toFixed(2);
    const totalEquityPB = (gameWallet || 0) + totalValuePB; // Balance + positions

    // Render position row (lobby list style)
    const PositionRow = ({ pos, isUserToken }) => {
      const token = activeTokens.find(t => t.id === pos.tokenId);
      // 🔥 Position is rugged if: explicitly rugged OR token no longer exists (expired/deleted)
      const tokenExists = activeTokenIds.includes(pos.tokenId);
      const isRugged = pos.status === 'rugged' || (pos.status === 'active' && !tokenExists);
      const isSold = pos.status === 'sold';
      const isClosed = isRugged || isSold; // Either rugged or sold = show CLEAR button
      const bagValue = isClosed ? 0 : calcBagValue(pos);
      const multiplier = isClosed ? 0 : calcMultiplier(pos);
      const pnl = isRugged ? -pos.invested : (isSold ? (pos.finalProfit || 0) : calcPnL(pos));
      const pnlPercent = pos.invested > 0 ? ((pnl / pos.invested) * 100) : 0;
      const tokenStillActive = tokenExists && !isClosed;
      const isProfit = pnl >= 0;
      const borderColor = isRugged ? '#ff3b69' : (isProfit ? '#00d26a' : '#ff3b69');

      return (
        <div
          onClick={() => {
            if (token && tokenStillActive && !isClosed) {
              setCurrentToken(token);
              setCurrentPrice(token.price || token.currentPrice);
              setGameState('playing');
              setView('token');
            }
          }}
          style={{
            borderRadius: '8px',
            padding: '10px 14px',
            margin: '6px 8px',
            cursor: (tokenStillActive && !isClosed) ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            transition: 'all 0.15s ease',
            border: isRugged ? '1px solid #5a1a2a' : (isSold ? `1px solid ${isProfit ? '#1a3a2a' : '#3a1a2a'}` : `1px solid ${isProfit ? '#1a3a2a' : '#3a1a2a'}`),
            borderLeft: `3px solid ${borderColor}`,
            backgroundColor: isRugged ? '#1a0508' : (isSold ? (isProfit ? '#0a1210' : '#120a10') : (isProfit ? '#0a1210' : '#120a10')),
            opacity: (tokenStillActive || isClosed) ? 1 : 0.5,
            fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace",
          }}
          onMouseEnter={e => { if (!isClosed) { e.currentTarget.style.backgroundColor = isProfit ? '#0d1a14' : '#1a0d14'; e.currentTarget.style.transform = 'scale(1.01)'; } }}
          onMouseLeave={e => { if (!isClosed) { e.currentTarget.style.backgroundColor = isProfit ? '#0a1210' : '#120a10'; e.currentTarget.style.transform = 'scale(1)'; } }}
        >
          {/* Left: Ticker + Description */}
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: isMobile ? '80px' : '140px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: isRugged ? '#ff3b69' : (isSold ? (isProfit ? '#00d26a' : '#ff3b69') : (isUserToken ? '#ff8c00' : '#f0b90b')), fontWeight: 'bold', fontSize: '13px' }}>${pos.tokenTicker || ''}</span>
              {isRugged && <span style={{ color: '#ff3b69', fontSize: '9px', fontWeight: 'bold' }}>💀 RUGGED</span>}
              {isSold && <span style={{ color: isProfit ? '#00d26a' : '#ff3b69', fontSize: '9px', fontWeight: 'bold' }}>{isProfit ? '✓ SOLD' : '✗ SOLD'}</span>}
              {!tokenStillActive && !isClosed && <span style={{ color: '#555', fontSize: '9px' }}>expired</span>}
            </div>
            {isUserToken && token?.description && !isClosed && (
              <div style={{
                fontSize: '9px',
                color: '#666',
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: isMobile ? '80px' : '130px',
                marginTop: '2px',
              }}>
                "{token.description}"
              </div>
            )}
          </div>

          {/* Middle: Stats */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px', fontSize: '11px', flex: 1, justifyContent: 'flex-end' }}>
            {isRugged ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#ff3b69', fontWeight: 'bold' }}>LOST {formatPB(pos.invested)}</span>
                {pos.tradeCount > 0 && (
                  <span style={{ color: '#888', fontSize: '10px' }}>
                    Rake: <span style={{ color: '#f0b90b' }}>{pos.tradeCount}¢</span>
                  </span>
                )}
              </div>
            ) : isSold ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: isProfit ? '#00d26a' : '#ff3b69', fontWeight: 'bold' }}>
                  {isProfit ? '+' : ''}{formatPB(pos.finalProfit || 0)}
                </span>
                {pos.tradeCount > 0 && (
                  <span style={{ color: '#888', fontSize: '10px' }}>
                    Rake: <span style={{ color: '#f0b90b' }}>{pos.tradeCount}¢</span>
                  </span>
                )}
              </div>
            ) : (
              <>
                {!isMobile && <span style={{ color: '#888' }}>VAL <span style={{ color: '#00d26a', fontWeight: 'bold' }}>{formatPB(bagValue)}</span></span>}
                <span style={{ color: '#888' }}>QTY <span style={{ color: '#fff', fontWeight: 'bold' }}>{formatSats(pos.size || pos.tokens || pos.tokensOwned || 0)}</span></span>
                {!isMobile && <span style={{ color: '#888' }}>ENTRY <span style={{ color: '#888', fontWeight: 'bold' }}>{(pos.entry || 0).toFixed(2)}</span></span>}
                <span style={{ color: '#888' }}><span style={{ color: multiplier >= 1 ? '#00d26a' : '#ff3b69', fontWeight: 'bold' }}>{multiplier.toFixed(2)}x</span></span>
                <div style={{ fontWeight: 'bold', fontSize: '12px', minWidth: '60px', textAlign: 'right', color: isProfit ? '#00d26a' : '#ff3b69' }}>
                  {isProfit ? '+' : ''}{pnlPercent.toFixed(1)}%
                </div>
              </>
            )}
          </div>

          {/* Right: SELL or CLEAR button */}
          {isClosed ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Clear this closed position (rugged or sold) from Redis
                clearPosition(pos.tokenId);
              }}
              style={{
                backgroundColor: isRugged ? '#ff3b69' : (isProfit ? '#00d26a' : '#ff3b69'),
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 'bold',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              CLEAR
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (token && tokenStillActive) {
                  setCurrentToken(token);
                  setCurrentPrice(token.price || token.currentPrice);
                  setGameState('playing');
                  setView('token');
                }
              }}
              style={{
                backgroundColor: 'transparent',
                border: `1px solid ${borderColor}`,
                borderRadius: '4px',
                padding: '4px 8px',
                color: borderColor,
                fontSize: '10px',
                fontWeight: 'bold',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              SELL
            </button>
          )}
        </div>
      );
    };

    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0f',
        padding: isMobile ? '12px' : '24px',
        paddingBottom: '100px',
      }}>
        {/* Header Bar - Token View Style Grid */}
        {(() => {
          const allPositions = [...aiPositions, ...userPositions];
          const positionCount = allPositions.length;
          const bestPos = allPositions.length > 0 ? allPositions.reduce((best, p) => calcMultiplier(p) > calcMultiplier(best) ? p : best, allPositions[0]) : null;
          const worstPos = allPositions.length > 0 ? allPositions.reduce((worst, p) => calcMultiplier(p) < calcMultiplier(worst) ? p : worst, allPositions[0]) : null;
          const bestMult = bestPos ? calcMultiplier(bestPos) : 0;
          const worstMult = worstPos ? calcMultiplier(worstPos) : 0;
          const bestPnlPercent = bestPos ? ((bestMult - 1) * 100).toFixed(0) : 0;
          const worstPnlPercent = worstPos ? ((worstMult - 1) * 100).toFixed(0) : 0;
          const hasWinner = bestMult >= 2;

          return (
            <div style={{
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '16px',
              border: '1px solid #1a2a3a',
              fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace",
              backgroundColor: '#0d0d14',
            }}>
              {/* Title Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 16px',
                borderBottom: '1px solid #1a2a3a',
                backgroundColor: '#08080c',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    onClick={() => setView('lobby')}
                    style={{
                      backgroundColor: '#1a1a2e',
                      border: '1px solid #2a3a4a',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      color: '#4a9eff',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    ←
                  </button>
                  <span style={{ fontSize: '20px' }}>{hasWinner ? '🏆' : '💼'}</span>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '16px' }}>Portfolio</div>
                    <div style={{ color: '#666', fontSize: '11px' }}>{positionCount} positions</div>
                  </div>
                </div>
                <button
                  onClick={() => setPortfolioSearch('')}
                  style={{
                    backgroundColor: '#1a1a2e',
                    border: '1px solid #2a3a4a',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    color: '#4a9eff',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  🔄 Refresh
                </button>
              </div>

              {/* Stats Grid - Token View Style */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
                borderBottom: '1px solid #1a2a3a',
              }}>
                {/* VALUE */}
                <div style={{ padding: '12px 8px', textAlign: 'center', borderRight: '1px solid #1a2a3a' }}>
                  <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>VALUE</div>
                  <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '16px' }}>${totalValueUSD}</div>
                </div>
                {/* EQUITY */}
                <div style={{ padding: '12px 8px', textAlign: 'center', borderRight: '1px solid #1a2a3a' }}>
                  <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>EQUITY</div>
                  <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '16px' }}>{formatPB(totalEquityPB)}</div>
                </div>
                {/* P/L TODAY */}
                <div style={{ padding: '12px 8px', textAlign: 'center', borderRight: isMobile ? 'none' : '1px solid #1a2a3a' }}>
                  <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>P/L TODAY</div>
                  <div style={{ color: dailyStats.totalProfit >= 0 ? '#00d26a' : '#ff3b69', fontWeight: 'bold', fontSize: '16px' }}>
                    {dailyStats.totalProfit >= 0 ? '+' : ''}{formatPB(dailyStats.totalProfit)}
                  </div>
                </div>
                {/* BEST */}
                <div style={{ padding: '12px 8px', textAlign: 'center', borderRight: '1px solid #1a2a3a', borderTop: isMobile ? '1px solid #1a2a3a' : 'none' }}>
                  <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>BEST</div>
                  <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '14px' }}>
                    {bestPos ? `+${bestPnlPercent}%` : '—'}
                  </div>
                  {bestPos && <div style={{ color: '#00d26a', fontSize: '9px', marginTop: '2px' }}>${bestPos.tokenTicker}</div>}
                </div>
                {/* WORST */}
                <div style={{ padding: '12px 8px', textAlign: 'center', borderRight: '1px solid #1a2a3a', borderTop: isMobile ? '1px solid #1a2a3a' : 'none' }}>
                  <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>WORST</div>
                  <div style={{ color: worstMult < 1 ? '#ff3b69' : '#666', fontWeight: 'bold', fontSize: '14px' }}>
                    {worstPos && worstMult < 1 ? `${worstPnlPercent}%` : '—'}
                  </div>
                  {worstPos && worstMult < 1 && <div style={{ color: '#ff3b69', fontSize: '9px', marginTop: '2px' }}>${worstPos.tokenTicker}</div>}
                </div>
                {/* W/L */}
                <div style={{ padding: '12px 8px', textAlign: 'center', borderTop: isMobile ? '1px solid #1a2a3a' : 'none' }}>
                  <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>W/L</div>
                  <div style={{ fontSize: '16px' }}>
                    <span style={{ color: '#00d26a', fontWeight: 'bold' }}>{dailyStats.wins}</span>
                    <span style={{ color: '#333' }}>/</span>
                    <span style={{ color: '#ff3b69', fontWeight: 'bold' }}>{dailyStats.losses}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Search and Sort */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search ticker..."
            value={portfolioSearch}
            onChange={(e) => setPortfolioSearch(e.target.value)}
            style={{
              flex: 1,
              minWidth: '150px',
              backgroundColor: '#08080c',
              border: '1px solid #1a1a2e',
              borderRadius: '6px',
              padding: '8px 12px',
              color: '#888',
              fontSize: '12px',
              fontFamily: "'SF Mono', monospace",
            }}
          />
          <select
            value={portfolioSort}
            onChange={(e) => setPortfolioSort(e.target.value)}
            style={{
              backgroundColor: '#1a1a2e',
              border: '1px solid #00d26a44',
              borderRadius: '16px',
              padding: '6px 24px 6px 10px',
              color: '#00d26a',
              fontSize: '10px',
              cursor: 'pointer',
              outline: 'none',
              appearance: 'none',
              WebkitAppearance: 'none',
              backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2300d26a\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 6px center',
              backgroundSize: '8px',
              fontWeight: 'bold',
              fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace",
            }}
          >
            <option value="value">💰 Value</option>
            <option value="pnl">📈 P/L</option>
            <option value="multiplier">🔥 Mult</option>
            <option value="alpha">🔤 A-Z</option>
          </select>
        </div>

        {/* Portfolio Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace" }}>
          <button
            onClick={() => setPortfolioTab && setPortfolioTab('ai')}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: `1px solid ${(!portfolioTab || portfolioTab === 'ai') ? '#00d26a' : '#333'}`,
              backgroundColor: (!portfolioTab || portfolioTab === 'ai') ? '#0a1a0a' : 'transparent',
              color: (!portfolioTab || portfolioTab === 'ai') ? '#00d26a' : '#666',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            AI Tokens ({aiPositions.length})
          </button>
          <button
            onClick={() => setPortfolioTab && setPortfolioTab('bsv')}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: `1px solid ${portfolioTab === 'bsv' ? '#f0b90b' : '#333'}`,
              backgroundColor: portfolioTab === 'bsv' ? '#1a1508' : 'transparent',
              color: portfolioTab === 'bsv' ? '#f0b90b' : '#666',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            BSV.pump ({userPositions.length})
          </button>
          {/* Bot Trades tab removed - bot positions now merged into AI/BSV tabs based on isUserLaunched */}
        </div>

        {/* AI Positions Section */}
        {(!portfolioTab || portfolioTab === 'ai') && (
          <div style={{
            border: '1px solid #1a2a3a',
            borderRadius: '12px',
            overflow: 'hidden',
            marginBottom: '16px',
            backgroundColor: '#0a0a0f',
          }}>
            {/* Section Header */}
            <div style={{
              backgroundColor: '#0d1a15',
              padding: '12px 16px',
              borderBottom: '1px solid #1a3a2a',
              fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace",
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>🤖</span>
                <span style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '13px' }}>AI TOKENS</span>
              </div>
              <span style={{ color: '#3a5a4a', fontSize: '10px' }}>House-generated • Moon or Rug</span>
            </div>
            {/* Position Rows */}
            {aiPositions.length > 0 ? (
              <div style={{ padding: '8px 0' }}>
                {sortPositions(aiPositions).map(pos => (
                  <PositionRow key={pos.id} pos={pos} isUserToken={false} />
                ))}
              </div>
            ) : (
              <div style={{ color: '#333', fontSize: '12px', textAlign: 'center', padding: '24px', backgroundColor: '#0a0a0f' }}>No AI token positions</div>
            )}
          </div>
        )}

        {/* BSV.pump Positions Section */}
        {portfolioTab === 'bsv' && (
          <div style={{
            border: '1px solid #2a2a1a',
            borderRadius: '12px',
            overflow: 'hidden',
            marginBottom: '16px',
            backgroundColor: '#0a0a0f',
          }}>
            {/* Section Header */}
            <div style={{
              backgroundColor: '#1a1508',
              padding: '12px 16px',
              borderBottom: '1px solid #3a3a1a',
              fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace",
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>🚀</span>
                <span style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '13px' }}>BSV.PUMP</span>
              </div>
              <span style={{ color: '#5a5a3a', fontSize: '10px' }}>Player-launched • Pure PvP</span>
            </div>
            {/* Position Rows */}
            {userPositions.length > 0 ? (
              <div style={{ padding: '8px 0' }}>
                {sortPositions(userPositions).map(pos => (
                  <PositionRow key={pos.id} pos={pos} isUserToken={true} />
                ))}
              </div>
            ) : (
              <div style={{ color: '#333', fontSize: '12px', textAlign: 'center', padding: '24px', backgroundColor: '#0a0a0f' }}>No BSV.pump positions</div>
            )}
          </div>
        )}

        {/* Bot Trades Section removed - bot positions now appear in AI/BSV tabs based on isUserLaunched */}

        {/* Rug Prophet Bets Section */}
        {pendingBets.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div
              onClick={() => setCollapseRugBets(!collapseRugBets)}
              style={{
                color: '#555',
                fontSize: '11px',
                fontWeight: '600',
                marginBottom: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: "'SF Mono', monospace",
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}
            >
              RUG PROPHET ({pendingBets.length})
              <span style={{ color: '#333', fontSize: '10px' }}>{collapseRugBets ? '▶' : '▼'}</span>
            </div>
            {!collapseRugBets && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '16px'
              }}>
                {pendingBets.map(bet => (
                  <div key={bet.id} style={{
                    backgroundColor: '#150f1a',
                    border: '2px solid #9b59b6',
                    borderRadius: '12px',
                    padding: '16px',
                  }}>
                    <div style={{ color: '#9b59b6', fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>${bet.tokenTicker || '???'}</div>
                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '12px' }}>
                      Prediction: {bet.prediction === 'rug' ? '💀 RUG' : '🌙 MOON'}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ color: '#666', fontSize: '10px' }}>Bet</div>
                        <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>{formatPB(bet.amount)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#666', fontSize: '10px' }}>Win</div>
                        <div style={{ color: '#00d26a', fontWeight: 'bold' }}>{formatPB(bet.potentialWin || bet.amount * 2)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {activePositions.length === 0 && pendingBets.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#555' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <div style={{ fontSize: '16px' }}>No positions yet</div>
            <div style={{ fontSize: '12px', marginTop: '8px' }}>Start trading to build your portfolio!</div>
          </div>
        )}
      </div>
    );
  }


  // ============== RENDER: LOBBY ==============
  if (view === 'lobby') {
    return (
      <>
      <TestRecorderUI />
      {showPaperChat && handcashUser && <PaperChatContent />}
      <TipModal />
      <ProfileModal />
      <RugModal />
      
      {/* ⛓️ ON-CHAIN TRANSACTION CONFIRMATION MODAL */}
      {showTxConfirmation && pendingTx && (
        <div 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            backgroundColor: 'rgba(0,0,0,0.95)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 10002 
          }}
          onClick={() => setShowTxConfirmation(false)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#0a1a0a',
              border: '2px solid #00d26a',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '420px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 0 60px rgba(0, 210, 106, 0.3)',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⛓️</div>
            <h2 style={{ color: '#00d26a', margin: '0 0 8px 0', fontSize: '24px' }}>
              ON-CHAIN {pendingTx.type?.toUpperCase()}!
            </h2>
            <p style={{ color: '#888', margin: '0 0 20px 0', fontSize: '14px' }}>
              Real BSV transaction confirmed on blockchain
            </p>
            
            <div style={{ 
              backgroundColor: '#111', 
              borderRadius: '8px', 
              padding: '16px', 
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Token:</span>
                <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>${pendingTx.ticker}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Amount:</span>
                <span style={{ color: '#fff', fontWeight: 'bold' }}>{formatPB(pendingTx.amount)}</span>
              </div>
              {pendingTx.profit !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#666' }}>Profit:</span>
                  <span style={{ color: pendingTx.profit >= 0 ? '#00d26a' : '#ff3b69', fontWeight: 'bold' }}>
                    {pendingTx.profit >= 0 ? '+' : ''}{formatPB(pendingTx.profit)}
                  </span>
                </div>
              )}
            </div>
            
            {/* Transaction Link */}
            <a 
              href={`https://whatsonchain.com/tx/${pendingTx.txId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block',
                backgroundColor: '#1a2a1a',
                border: '1px solid #00d26a55',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                textDecoration: 'none',
                color: '#00d26a',
                fontSize: '12px',
                wordBreak: 'break-all',
              }}
            >
              <div style={{ color: '#666', fontSize: '10px', marginBottom: '4px' }}>Transaction ID:</div>
              <div style={{ fontFamily: 'monospace' }}>{pendingTx.txId?.slice(0, 20)}...{pendingTx.txId?.slice(-10)}</div>
              <div style={{ marginTop: '8px', color: '#888' }}>👆 Click to view on WhatsOnChain</div>
            </a>
            
            <button
              onClick={() => setShowTxConfirmation(false)}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#00d26a',
                border: 'none',
                borderRadius: '8px',
                color: '#000',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              ✓ Got it!
            </button>
          </div>
        </div>
      )}
      
      {/* ⛓️ TRANSACTION HISTORY MODAL - Collapsible by token */}
      {showTxHistory && (() => {
        // Group transactions by ticker
        const groupedByTicker = txHistory.reduce((acc, tx) => {
          const ticker = tx.ticker || 'Unknown';
          if (!acc[ticker]) acc[ticker] = [];
          acc[ticker].push(tx);
          return acc;
        }, {});
        
        return (
          <div 
            style={{ 
              position: 'fixed', 
              inset: 0, 
              backgroundColor: 'rgba(0,0,0,0.95)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              zIndex: 10002 
            }}
            onClick={() => setShowTxHistory(false)}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#0a0a0f',
                border: '2px solid #00d26a',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '500px',
                width: '95%',
                maxHeight: '80vh',
                overflow: 'auto',
                fontFamily: 'monospace',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ color: '#00d26a', margin: 0, fontSize: '20px', fontFamily: 'monospace' }}>⛓️ On-Chain Transactions</h2>
                  <p style={{ color: '#666', margin: '4px 0 0 0', fontSize: '12px', fontFamily: 'monospace' }}>Real BSV fee transactions on blockchain</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_BASE}/api/user/transactions`, {
                          headers: { 'x-session-id': sessionId }
                        });
                        const data = await res.json();
                        if (data.transactions) {
                          setTxHistory(data.transactions);
                          console.log('🔄 Refreshed tx history:', data.transactions.length);
                        }
                      } catch (e) { console.error('Refresh failed:', e); }
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: '1px solid #00d26a',
                      backgroundColor: 'transparent',
                      color: '#00d26a',
                      fontSize: '16px',
                      cursor: 'pointer',
                      fontFamily: 'monospace',
                    }}
                    title="Refresh transactions"
                  >🔄</button>
                  <button 
                    onClick={() => setShowTxHistory(false)}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: '1px solid #333',
                      backgroundColor: 'transparent',
                      color: '#888',
                      fontSize: '18px',
                      cursor: 'pointer',
                      fontFamily: 'monospace',
                    }}
                  >×</button>
                </div>
              </div>
              
              {txHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>⛓️</div>
                  <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px', fontFamily: 'monospace' }}>No on-chain transactions yet</div>
                  <div style={{ color: '#555', fontSize: '12px', fontFamily: 'monospace' }}>Trade tokens to see your fee transactions here</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {Object.entries(groupedByTicker).map(([ticker, txs]) => {
                    const isExpanded = expandedTxTokens[ticker];
                    const totalFees = txs.reduce((sum, tx) => sum + (tx.amount || 0), 0);
                    const isPvP = txs[0]?.isPvP;
                    
                    return (
                      <div key={ticker} style={{ backgroundColor: '#111', border: '1px solid #222', borderRadius: '8px', overflow: 'hidden' }}>
                        {/* Token Header - Click to expand */}
                        <div 
                          onClick={() => setExpandedTxTokens(prev => ({ ...prev, [ticker]: !prev[ticker] }))}
                          style={{
                            padding: '12px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            backgroundColor: isExpanded ? '#1a1a1f' : '#111',
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ color: isExpanded ? '#fff' : '#888', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                            <span style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '16px', fontFamily: 'monospace' }}>${ticker}</span>
                            {isPvP ? (
                              <span style={{ backgroundColor: '#9b59b6', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontFamily: 'monospace' }}>⚔️ PvP 10%</span>
                            ) : (
                              <span style={{ backgroundColor: '#00d26a', color: '#000', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontFamily: 'monospace' }}>🤖 AI 1%</span>
                            )}
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#f0b90b', fontWeight: 'bold', fontFamily: 'monospace' }}>{formatPB(totalFees)} <span style={{ color: '#888', fontSize: '11px' }}>({formatUsd(totalFees)})</span></div>
                            <div style={{ color: '#666', fontSize: '10px', fontFamily: 'monospace' }}>{txs.length} fee{txs.length > 1 ? 's' : ''} paid</div>
                          </div>
                        </div>
                        
                        {/* Expanded Transaction List */}
                        {isExpanded && (
                          <div style={{ borderTop: '1px solid #222', padding: '8px' }}>
                            {txs.map((tx, idx) => (
                              <a
                                key={idx}
                                href={`https://whatsonchain.com/tx/${tx.txId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'block',
                                  padding: '10px 12px',
                                  marginBottom: idx < txs.length - 1 ? '4px' : 0,
                                  backgroundColor: '#0a0a0f',
                                  borderRadius: '6px',
                                  textDecoration: 'none',
                                  transition: 'all 0.2s',
                                  border: '1px solid transparent',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#00d26a'}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ 
                                      color: tx.action === 'buy' ? '#00d26a' : '#ff3b69',
                                      fontWeight: 'bold',
                                      fontSize: '12px',
                                      fontFamily: 'monospace'
                                    }}>
                                      {tx.action === 'buy' ? '🟢 BUY' : '🔴 SELL'}
                                    </span>
                                    <span style={{ color: '#f0b90b', fontSize: '11px', fontFamily: 'monospace' }}>
                                      💰 {tx.rakePercent}% fee
                                    </span>
                                  </div>
                                  <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '12px', fontFamily: 'monospace' }}>{formatPB(tx.amount)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div style={{ color: '#555', fontSize: '9px', fontFamily: 'monospace' }}>
                                    {tx.txId?.slice(0, 10)}...{tx.txId?.slice(-6)}
                                  </div>
                                  <div style={{ color: '#444', fontSize: '9px', fontFamily: 'monospace' }}>
                                    {tx.timestamp ? new Date(tx.timestamp).toLocaleString() : ''}
                                  </div>
                                </div>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div style={{ 
                marginTop: '20px', 
                padding: '12px', 
                backgroundColor: '#0a1a0f', 
                borderRadius: '8px',
                border: '1px solid #00d26a33',
                textAlign: 'center'
              }}>
                <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold', fontFamily: 'monospace' }}>⛓️ All fees are on-chain!</div>
                <div style={{ color: '#666', fontSize: '10px', marginTop: '4px', fontFamily: 'monospace' }}>
                  🤖 AI tokens = 1% fee | ⚔️ PvP tokens = 10% fee
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      
      {/* 💰 HOUSE CREDIT ANIMATION - Shows when zcool receives rake */}
      {houseCreditAnimation && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 10001,
          animation: 'houseCreditSlideIn 0.5s ease-out',
        }}>
          <div style={{
            backgroundColor: '#0a1a0a',
            border: '2px solid #00d26a',
            borderRadius: '12px',
            padding: '16px 20px',
            boxShadow: '0 0 30px rgba(0, 210, 106, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{ fontSize: '32px' }}>💰</div>
            <div>
              <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>HOUSE CREDIT</div>
              <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>
                +{formatPB(houseCreditAnimation.amount)}
              </div>
              <div style={{ color: '#888', fontSize: '11px' }}>
                {houseCreditAnimation.from}'s bot {houseCreditAnimation.type?.includes('buy') ? 'BUY' : 'SELL'} on ${houseCreditAnimation.ticker}
              </div>
            </div>
          </div>
          <style>{`
            @keyframes houseCreditSlideIn {
              0% { transform: translateX(100px); opacity: 0; }
              100% { transform: translateX(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}
      
      {/* Scanner Info Modal - rendered inline to prevent remount flashing */}
      {showScannerInfo && (
        <div 
          onClick={(e) => e.target === e.currentTarget && setShowScannerInfo(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: isMobile ? '12px' : '20px',
            fontFamily: "'Space Grotesk', sans-serif",
            overflowY: 'auto',
          }}>
          <div style={{
            backgroundColor: '#0a0a0f',
            border: '2px solid #00d26a',
            borderRadius: isMobile ? '16px' : '20px',
            padding: isMobile ? '16px' : '24px',
            maxWidth: '480px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            textAlign: 'center',
            boxShadow: '0 0 60px rgba(0,210,106,0.3)',
            animation: 'modalSlideIn 0.3s ease-out',
            position: 'relative',
          }}>
            {/* X Close Button */}
            <button
              onClick={() => setShowScannerInfo(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                borderRadius: '50%',
                border: '1px solid #333',
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: '#888',
                fontSize: isMobile ? '14px' : '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ✕
            </button>
            
            {/* Header Icon */}
            <div style={{
              width: isMobile ? '50px' : '60px',
              height: isMobile ? '50px' : '60px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,210,106,0.1)',
              border: '2px solid #00d26a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
            }}>
              <span style={{ fontSize: isMobile ? '22px' : '28px' }}>🔍</span>
            </div>
            
            {/* Title */}
            <h2 style={{
              color: '#00d26a',
              fontSize: isMobile ? '16px' : '20px',
              fontWeight: 'bold',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: isMobile ? '1px' : '2px',
            }}>
              Cloudflare Rug Scanner
            </h2>
            
            <p style={{ color: '#888', fontSize: isMobile ? '11px' : '12px', marginBottom: '14px' }}>
              Crowd-sourced detection. All players connected. 🛡️
            </p>
            
            {/* What is a Cloudflare Rug */}
            <div style={{
              backgroundColor: 'rgba(255,59,105,0.1)',
              border: '1px solid rgba(255,59,105,0.2)',
              borderRadius: '10px',
              padding: '12px',
              marginBottom: '12px',
              textAlign: 'left',
            }}>
              <p style={{ color: '#ff3b69', fontSize: '11px', fontWeight: 'bold', marginBottom: '6px' }}>
                🚨 WHAT'S A CLOUDFLARE RUG?
              </p>
              <p style={{ color: '#888', fontSize: '10px', lineHeight: '1.4', margin: 0 }}>
                When centralized services like Cloudflare go down, they can halt crypto infrastructure. 
                Wallets freeze. Deposits fail. Your funds get trapped - that's a <span style={{ color: '#ff3b69' }}>Cloudflare Rug</span>.
              </p>
            </div>
            
            {/* How it works diagram */}
            <div style={{
              backgroundColor: 'rgba(0,210,106,0.05)',
              border: '1px solid rgba(0,210,106,0.2)',
              borderRadius: '10px',
              padding: '14px',
              marginBottom: '12px',
              textAlign: 'left',
            }}>
              <p style={{ color: '#00d26a', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'center' }}>
                ⚡ HOW WE DETECT IT
              </p>
              
              <div style={{ 
                fontFamily: 'monospace', 
                fontSize: '10px', 
                color: '#00d26a',
                lineHeight: '1.5',
                textAlign: 'center',
              }}>
                <div>🎮 Player A tries withdraw</div>
                <div style={{ color: '#555' }}>↓</div>
                <div style={{ color: '#ff3b69' }}>💥 CLOUDFLARE RUG! (500 error)</div>
                <div style={{ color: '#555' }}>↓</div>
                <div>📡 Server records the rug</div>
                <div style={{ color: '#555' }}>↓</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '6px' }}>
                  <span>🎮 B</span>
                  <span>🎮 C</span>
                  <span>🎮 D</span>
                </div>
                <div style={{ color: '#ff3b69', fontWeight: 'bold', marginTop: '6px', fontSize: '9px' }}>
                  🚨 ALL GET RUG ALERT WITHIN 30 SEC!
                </div>
              </div>
            </div>
            
            {/* Features */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              marginBottom: '12px',
            }}>
              <div style={{
                backgroundColor: 'rgba(240,185,11,0.1)',
                border: '1px solid rgba(240,185,11,0.2)',
                borderRadius: '8px',
                padding: '10px',
              }}>
                <div style={{ fontSize: '16px', marginBottom: '2px' }}>⏱️</div>
                <div style={{ color: '#f0b90b', fontSize: '10px', fontWeight: 'bold' }}>30 SEC POLLING</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Checks for rugs</div>
              </div>
              <div style={{
                backgroundColor: 'rgba(0,210,106,0.1)',
                border: '1px solid rgba(0,210,106,0.2)',
                borderRadius: '8px',
                padding: '10px',
              }}>
                <div style={{ fontSize: '16px', marginBottom: '2px' }}>🐝</div>
                <div style={{ color: '#00d26a', fontSize: '10px', fontWeight: 'bold' }}>HIVE MIND</div>
                <div style={{ color: '#666', fontSize: '9px' }}>All players connected</div>
              </div>
              <div style={{
                backgroundColor: 'rgba(255,59,105,0.1)',
                border: '1px solid rgba(255,59,105,0.2)',
                borderRadius: '8px',
                padding: '10px',
              }}>
                <div style={{ fontSize: '16px', marginBottom: '2px' }}>🚨</div>
                <div style={{ color: '#ff3b69', fontSize: '10px', fontWeight: 'bold' }}>RUG ALERT</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Cloudflare detected</div>
              </div>
              <div style={{
                backgroundColor: 'rgba(138,43,226,0.1)',
                border: '1px solid rgba(138,43,226,0.2)',
                borderRadius: '8px',
                padding: '10px',
              }}>
                <div style={{ fontSize: '16px', marginBottom: '2px' }}>🧊</div>
                <div style={{ color: '#8a2be2', fontSize: '10px', fontWeight: 'bold' }}>COLD STORAGE</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Paper Bucks safe</div>
              </div>
            </div>
            
            {/* The quote */}
            <div style={{
              backgroundColor: 'rgba(0,210,106,0.1)',
              border: '1px solid rgba(0,210,106,0.3)',
              borderRadius: '8px',
              padding: '10px',
              marginBottom: '12px',
            }}>
              <p style={{ 
                color: '#00d26a', 
                fontSize: '12px', 
                fontWeight: 'bold',
                fontStyle: 'italic',
                margin: 0,
              }}>
                "Cloudflare can rug one of us, but not all of us!"
              </p>
            </div>
            
            {/* ASCII Scanner */}
            <AsciiScanner isVisible={showScannerInfo} />
            
            {/* Close Button */}
            <button
              onClick={() => setShowScannerInfo(false)}
              style={{
                width: '100%',
                padding: '12px 20px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: '#00d26a',
                color: '#000',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            >
              Got it! 🔍
            </button>
          </div>
          
          <style>{`
            @keyframes modalSlideIn {
              from { opacity: 0; transform: scale(0.9) translateY(-20px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>
        </div>
      )}
      <div style={{ minHeight: '100vh', backgroundColor: '#050508', backgroundImage: 'url(/upscalemedia-transformed.jpeg)', backgroundPosition: '0 -40px', backgroundRepeat: 'no-repeat', backgroundSize: '100% auto', color: '#fff', fontFamily: "'SF Mono', monospace", boxSizing: 'border-box', overflowX: 'hidden', width: '100%' }}>
        {/* 🐦 CANARY Banner - Always shows for blackwolf */}
        {isTestPilot && (
          <div style={{
            backgroundColor: handcashDown ? 'rgba(255,193,7,0.15)' : hiveReports >= 3 ? 'rgba(255,107,0,0.15)' : 'rgba(0,210,106,0.1)',
            borderBottom: `1px solid ${handcashDown ? 'rgba(255,193,7,0.4)' : hiveReports >= 3 ? 'rgba(255,107,0,0.4)' : 'rgba(0,210,106,0.3)'}`,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px' }}>🐦</span>
              <span style={{ color: handcashDown ? '#ffc107' : hiveReports >= 3 ? '#ff6b00' : '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>
                CANARY MODE
              </span>
              <span style={{ 
                padding: '2px 6px', 
                borderRadius: '4px', 
                backgroundColor: handcashDown ? '#ffc107' : hiveReports >= 3 ? '#ff6b00' : '#00d26a',
                color: '#000',
                fontSize: '9px',
                fontWeight: 'bold',
              }}>
                {handcashDown ? 'DEGRADED' : hiveReports >= 3 ? 'HIVE ALERT' : 'NORMAL'}
              </span>
              {/* 🐝 Hive reports indicator */}
              {hiveReports > 0 && !handcashDown && (
                <span style={{ 
                  padding: '2px 6px', 
                  borderRadius: '4px', 
                  backgroundColor: hiveReports >= 3 ? 'rgba(255,107,0,0.3)' : 'rgba(255,193,7,0.2)',
                  color: hiveReports >= 3 ? '#ff6b00' : '#ffc107',
                  fontSize: '9px',
                  fontWeight: 'bold',
                }}>
                  🐝 {hiveReports} report{hiveReports !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <span style={{ color: '#888', fontSize: '11px' }}>
              {handcashDown 
                ? 'Your transactions control degraded mode for all players' 
                : hiveReports >= 3
                  ? `⚠️ ${hiveReports} users reported failures - consider testing a deposit!`
                  : 'System healthy • Your next failure will trigger degraded mode'}
            </span>
          </div>
        )}
        
        {/* HandCash Down Banner - Only for non-canary users */}
        {handcashDown && !isTestPilot && (
          <div style={{
            backgroundColor: 'rgba(233,30,99,0.1)',
            borderBottom: '1px solid rgba(233,30,99,0.3)',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: '#e91e63',
                animation: 'pulse 1.5s infinite',
                boxShadow: '0 0 8px #e91e63',
              }} />
              <span style={{ color: '#e91e63', fontSize: '12px', fontWeight: 'bold' }}>
                ⚠️ HandCash Unavailable
              </span>
            </div>
            <span style={{ color: '#888', fontSize: '11px' }}>
              Deposit, Withdraw & Launch disabled • Trading still works!
            </span>
            <button
              onClick={retryHandCashConnection}
              disabled={retryingConnection}
              style={{
                padding: '4px 12px',
                borderRadius: '6px',
                border: '1px solid #e91e63',
                backgroundColor: 'transparent',
                color: '#e91e63',
                fontSize: '10px',
                fontWeight: 'bold',
                cursor: retryingConnection ? 'not-allowed' : 'pointer',
              }}
            >
              {retryingConnection ? '⟳ Checking...' : '🔄 Retry'}
            </button>
          </div>
        )}
        
        {/* Centered max-width container */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 8px' : '16px', boxSizing: 'border-box' }}>
          <div style={{ width: '100%', overflowX: 'hidden' }}>
          
          {/* ============== UNIFIED HEADER ============== */}

          {/* SINGLE ROW: Logo | Wallet Dropdown | Quick Actions | Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: isMobile ? '6px' : '10px',
            gap: '8px',
            padding: isMobile ? '0' : '0 4px',
          }}>
            {/* LEFT: Empty spacer for background image logo */}
            <div style={{ minWidth: isMobile ? '100px' : isTablet ? '160px' : '220px' }}></div>

            {/* RIGHT: PRO + OK + Quick Actions + Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : isTablet ? '6px' : '10px', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
              {/* PRO badge */}
              {isPremium && !isMobile && (
                <div style={{
                  backgroundColor: '#f0b90b',
                  color: '#000',
                  padding: isTablet ? '2px 6px' : '4px 10px',
                  borderRadius: '6px',
                  fontSize: isTablet ? '9px' : '10px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  👑 {isTablet ? 'PRO' : `PRO • ${premiumTimeLeft || '24h'}`}
                </div>
              )}
              {/* Dark container for OK + Logout */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '6px' : '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                {/* OK Status indicator */}
                <div
                  onClick={() => setShowScannerInfo(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    backgroundColor: handcashDown ? 'rgba(255,59,105,0.15)' : 'rgba(0,210,106,0.1)',
                    border: `1px solid ${handcashDown ? '#ff3b69' : '#00d26a'}`,
                    borderRadius: '12px',
                    padding: isMobile ? '3px 6px' : isTablet ? '4px 8px' : '5px 12px',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ width: isMobile ? '6px' : '8px', height: isMobile ? '6px' : '8px', borderRadius: '50%', backgroundColor: handcashDown ? '#ff3b69' : '#00d26a', animation: 'pulse 1.5s infinite' }} />
                  <span style={{ color: handcashDown ? '#ff3b69' : '#00d26a', fontSize: isMobile ? '9px' : '11px', fontWeight: 'bold' }}>{handcashDown ? 'RUG' : 'OK'}</span>
                </div>
                {/* Logout button */}
                <button
                  onClick={() => {
                    window.location.href = '/nuked.html';
                  }}
                  title="Sign Out"
                  style={{
                    padding: isMobile ? '4px 8px' : '6px 10px',
                    backgroundColor: 'rgba(204, 0, 255, 0.15)',
                    border: '1px solid #cc00ff',
                    borderRadius: '8px',
                    color: '#cc00ff',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 8px rgba(204, 0, 255, 0.3)',
                  }}
                >
                  ⏻
                </button>
              </div>
              {/* Friends/Chat - On mobile opens user list directly */}
              <button
                onClick={() => {
                  if (isMobile) {
                    // Mobile: Always open to buddies (user list) view
                    setPaperChatView('buddies');
                  }
                  setShowPaperChat(!showPaperChat);
                  if (!showPaperChat) fetchDMs();
                }}
                style={{
                  padding: isMobile ? '6px 8px' : isTablet ? '8px 10px' : '10px 14px',
                  backgroundColor: '#0d0d12',
                  border: `2px solid ${dmUnreadCount > 0 ? '#9b59b6' : '#333'}`,
                  borderRadius: '10px',
                  color: '#f0b90b',
                  fontSize: isMobile ? '11px' : '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  position: 'relative',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1a1a2e'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#0d0d12'; }}
              >
                <span style={{ fontSize: isMobile ? '14px' : '16px' }}>👥</span>
                {!isMobile && <span style={{ fontWeight: 'bold' }}>{onlineUsers.length}</span>}
                {dmUnreadCount > 0 && (
                  <span style={{ position: 'absolute', top: '-6px', right: '-6px', backgroundColor: '#9b59b6', color: '#fff', fontSize: '9px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '10px', minWidth: '18px', textAlign: 'center' }}>{dmUnreadCount}</span>
                )}
                {/* 💬 DM Toast - Floating bubble near button */}
                {dmToast && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPaperChat(true);
                      setPaperChatView(dmToast.from);
                      setSelectedDMUser(dmToast.from);
                      setDmToast(null);
                    }}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '8px',
                      backgroundColor: '#1a0f1f',
                      border: '2px solid #9b59b6',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      minWidth: '180px',
                      maxWidth: '250px',
                      zIndex: 1000,
                      boxShadow: '0 4px 20px rgba(155,89,182,0.4)',
                      animation: 'dmSlideIn 0.3s ease-out',
                    }}
                  >
                    <div style={{ color: '#9b59b6', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>
                      NEW MESSAGE
                    </div>
                    <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                      ${dmToast.from}
                    </div>
                    <div style={{
                      color: '#888',
                      fontSize: '11px',
                      marginTop: '3px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {dmToast.text?.length > 30 ? dmToast.text.substring(0, 30) + '...' : dmToast.text}
                    </div>
                  </div>
                )}
              </button>

              {/* Leaderboard */}
              <button
                onClick={() => { setShowLeaderboard(true); fetchLeaderboards(); }}
                style={{
                  padding: isMobile ? '6px 8px' : isTablet ? '8px 10px' : '10px 14px',
                  backgroundColor: '#0d0d12',
                  border: '2px solid #f0b90b33',
                  borderRadius: '10px',
                  color: '#f0b90b',
                  fontSize: isMobile ? '11px' : '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1a1a2e'; e.currentTarget.style.borderColor = '#f0b90b66'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#0d0d12'; e.currentTarget.style.borderColor = '#f0b90b33'; }}
              >
                <span style={{ fontSize: isMobile ? '14px' : '16px' }}>🏆</span>
                {isDesktop && <span style={{ fontWeight: 'bold' }}>Ranks</span>}
              </button>

              {/* 🛡️ Admin Button - For admins and moderators */}
              {canViewAdminPortal && (
                <button
                  onClick={() => { setShowAdminDashboard(true); fetchAdminDashboard(); fetchPendingWithdrawals(); fetchWatchlist(); }}
                  style={{
                    padding: isMobile ? '6px 8px' : isTablet ? '8px 10px' : '10px 14px',
                    backgroundColor: isModerator ? '#0a0820' : '#1a0808',
                    border: `2px solid ${isModerator ? '#6366f1' : '#ff3b69'}`,
                    borderRadius: '10px',
                    color: isModerator ? '#6366f1' : '#ff3b69',
                    fontSize: isMobile ? '11px' : '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2a0a0a'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#1a0808'; }}
                  title="Admin Dashboard"
                >
                  <span style={{ fontSize: isMobile ? '14px' : '16px' }}>🛡️</span>
                  {isDesktop && <span style={{ fontWeight: 'bold' }}>Admin</span>}
                </button>
              )}

              {/* 🏆 On-Chain Champion Badge - Tablet & Desktop only */}
              {!isMobile && featuredChampion && (
                <div
                  onClick={() => featuredChampion.txUrl && window.open(featuredChampion.txUrl, '_blank')}
                  style={{
                    padding: isTablet ? '6px 10px' : '8px 14px',
                    backgroundColor: featuredChampion.isNew ? '#0a1a0f' : '#0d0d12',
                    border: `2px solid ${featuredChampion.isNew ? '#00d26a' : '#00d26a44'}`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    animation: featuredChampion.isNew ? 'pulse 2s infinite' : 'none',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#0a1a0f'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = featuredChampion.isNew ? '#0a1a0f' : '#0d0d12'; }}
                  title={`On-chain champion: $${featuredChampion.handle} | +${formatPB(featuredChampion.profit || 0)} | Click to view on blockchain`}
                >
                  <span style={{ fontSize: '14px' }}>⛓️</span>
                  <span style={{ color: '#00d26a', fontWeight: 'bold', fontSize: isTablet ? '11px' : '12px' }}>
                    ${featuredChampion.handle}
                  </span>
                  {featuredChampion.isNew && (
                    <span style={{
                      backgroundColor: '#00d26a',
                      color: '#000',
                      fontSize: '8px',
                      padding: '2px 5px',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                    }}>NEW</span>
                  )}
                </div>
              )}

              {/* Sound toggle */}
              <button
                onClick={() => { const newState = !soundEnabled; setSoundEnabled(newState); if (newState) Sounds.success(); }}
                style={{
                  backgroundColor: soundEnabled ? '#0a1a0f' : '#1a0a0a',
                  border: `2px solid ${soundEnabled ? '#00d26a44' : '#333'}`,
                  borderRadius: '10px',
                  padding: isMobile ? '6px 8px' : isTablet ? '8px 10px' : '10px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = soundEnabled ? '#0f2f1f' : '#2a1010'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = soundEnabled ? '#0a1a0f' : '#1a0a0a'; }}
              >
                <span style={{ fontSize: isMobile ? '14px' : '16px' }}>{soundEnabled ? '🔊' : '🔇'}</span>
              </button>
            </div>
          </div>

          {/* 📊 GLOBAL STATS BAR - Responsive stats row */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? '12px' : isTablet ? '20px' : '32px',
            padding: isMobile ? '8px 12px' : isTablet ? '10px 16px' : '12px 24px',
            marginBottom: isMobile ? '8px' : '12px',
            backgroundColor: '#0a0a0f',
            borderRadius: '12px',
            border: '2px solid #1a1a2e',
          }}>
            {/* 💰 Wallet Button (opens full wallet modal) */}
            <div
              onClick={() => setShowWallet(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : isTablet ? '8px' : '12px',
                backgroundColor: '#0a0a0f',
                border: '2px solid #1a1a2e',
                borderRadius: '12px',
                padding: isMobile ? '6px 10px' : isTablet ? '8px 14px' : '10px 20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#00d26a44'; e.currentTarget.style.backgroundColor = '#0a0f0a'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#1a1a2e'; e.currentTarget.style.backgroundColor = '#0a0a0f'; }}
            >
              {/* Avatar - show on ALL platforms */}
              {handcashUser?.avatarUrl ? (
                <img src={handcashUser.avatarUrl} alt="" style={{
                  width: isMobile ? '32px' : isTablet ? '28px' : '36px',
                  height: isMobile ? '32px' : isTablet ? '28px' : '36px',
                  borderRadius: '50%',
                  border: isPremium ? '2px solid #f0b90b' : '2px solid #333',
                  boxShadow: isPremium ? '0 0 10px rgba(240,185,11,0.3)' : 'none',
                }} />
              ) : (
                <div style={{
                  width: isMobile ? '32px' : isTablet ? '28px' : '36px',
                  height: isMobile ? '32px' : isTablet ? '28px' : '36px',
                  borderRadius: '50%',
                  backgroundColor: '#1a1a2e',
                  backgroundImage: handcashUser ? `url(https://cloud.handcash.io/v2/users/profilePicture/${handcashUser.handle?.replace(/^\$/, '')})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '14px' : isTablet ? '12px' : '16px',
                }}>{!handcashUser && '🤚'}</div>
              )}
              {/* Balance */}
              <div>
                <div style={{ color: '#00d26a', fontSize: isMobile ? '14px' : isTablet ? '16px' : '20px', fontWeight: 'bold', lineHeight: 1 }}>
                  ${formatSats(gameWallet)}
                </div>
                {!isMobile && (
                  <div style={{ color: '#666', fontSize: isTablet ? '9px' : '10px' }}>
                    ≈ ${(gameWallet * satToUsd).toFixed(2)} USD
                  </div>
                )}
              </div>
              {/* HandCash Balance - Show on tablet/desktop */}
              {!isMobile && mainWallet > 0 && (
                <div style={{
                  borderLeft: '1px solid #333',
                  paddingLeft: isTablet ? '8px' : '12px',
                  marginLeft: isTablet ? '4px' : '8px',
                }}>
                  <div style={{ color: '#00bcd4', fontSize: isTablet ? '12px' : '14px', fontWeight: 'bold', lineHeight: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontSize: isTablet ? '10px' : '12px' }}>💳</span>
                    {formatSats(mainWallet)}
                  </div>
                  <div style={{ color: '#555', fontSize: isTablet ? '8px' : '9px' }}>
                    ${(mainWallet * satToUsd).toFixed(2)} USD
                  </div>
                </div>
              )}
              <span style={{ color: '#555', fontSize: isMobile ? '10px' : '12px' }}>▼</span>
            </div>
            <div style={{ width: '1px', height: isMobile ? '28px' : '36px', backgroundColor: '#1a1a2e' }} />
            {/* 📊 Player Portal Button */}
            <button
              onClick={() => { refreshBalances(); setShowPlayerPortal(true); }}
              style={{
                padding: isMobile ? '6px 10px' : '8px 14px',
                backgroundColor: '#0a1a0a',
                border: '2px solid #00d26a',
                borderRadius: '8px',
                color: '#00d26a',
                fontSize: isMobile ? '10px' : '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#0f2f0f'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#0a1a0a'; }}
            >
              <span style={{ fontSize: isMobile ? '12px' : '14px' }}>📊</span>
              {!isMobile && <span>My Stats</span>}
            </button>
            <div style={{ width: '1px', height: isMobile ? '28px' : '36px', backgroundColor: '#1a1a2e' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#00d26a', fontSize: isMobile ? '8px' : isTablet ? '9px' : '10px', fontWeight: 'bold', letterSpacing: '0.5px' }}>⛓️ ON-CHAIN</div>
              <div style={{ color: '#fff', fontSize: isMobile ? '14px' : isTablet ? '16px' : '18px', fontWeight: 'bold' }}>{globalStats.onChainTransactions || (globalStats.totalDeposits || 0) + (globalStats.totalWithdrawals || 0)}</div>
              {isDesktop && <div style={{ color: '#444', fontSize: '9px' }}>transactions</div>}
            </div>
            <div style={{ width: '1px', height: isMobile ? '28px' : '36px', backgroundColor: '#1a1a2e' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#ff3b69', fontSize: isMobile ? '8px' : isTablet ? '9px' : '10px', fontWeight: 'bold', letterSpacing: '0.5px' }}>💀 LOST</div>
              <div style={{ color: '#ff3b69', fontSize: isMobile ? '14px' : isTablet ? '16px' : '18px', fontWeight: 'bold' }}>{formatPB(globalStats.totalSatsLost || globalStats.totalSatsRugged || 0)}</div>
              {isDesktop && <div style={{ color: '#444', fontSize: '9px' }}>to rugs</div>}
            </div>
            <div style={{ width: '1px', height: isMobile ? '28px' : '36px', backgroundColor: '#1a1a2e' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#00d26a', fontSize: isMobile ? '8px' : isTablet ? '9px' : '10px', fontWeight: 'bold', letterSpacing: '0.5px' }}>💰 GAINED</div>
              <div style={{ color: '#00d26a', fontSize: isMobile ? '14px' : isTablet ? '16px' : '18px', fontWeight: 'bold' }}>{formatPB(globalStats.totalSatsGained || 0)}</div>
              {isDesktop && <div style={{ color: '#444', fontSize: '9px' }}>profits</div>}
            </div>
            <div style={{ width: '1px', height: isMobile ? '28px' : '36px', backgroundColor: '#1a1a2e' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#555', fontSize: isMobile ? '8px' : isTablet ? '9px' : '10px', letterSpacing: '0.5px' }}>BSV</div>
              <div style={{ color: '#888', fontSize: isMobile ? '14px' : isTablet ? '16px' : '18px', fontWeight: 'bold' }}>${bsvPrice ? bsvPrice.toFixed(2) : '--'}</div>
              {isDesktop && <div style={{ color: '#444', fontSize: '9px' }}>price</div>}
            </div>
            <div style={{ width: '1px', height: isMobile ? '28px' : '36px', backgroundColor: '#1a1a2e' }} />
            {/* Social Links */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
              <a 
                href="https://x.com/imablackwolf" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#888', fontSize: isMobile ? '16px' : '20px', textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseOver={(e) => e.target.style.color = '#fff'}
                onMouseOut={(e) => e.target.style.color = '#888'}
                title="Follow us on X"
              >
                𝕏
              </a>
              <a 
                href="https://discord.gg/7q5WvRVtfm" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#888', fontSize: isMobile ? '16px' : '20px', textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseOver={(e) => e.target.style.color = '#5865F2'}
                onMouseOut={(e) => e.target.style.color = '#888'}
                title="Join our Discord"
              >
                <svg width={isMobile ? "16" : "20"} height={isMobile ? "16" : "20"} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* 📱 BOTTOM NAVIGATION - All devices (auto-hides on scroll) */}
          {view === 'lobby' && (
            <>
            {/* Tap zone to show nav when hidden */}
            {navHidden && (
              <div 
                onClick={() => setNavHidden(false)}
                style={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  zIndex: 999,
                  cursor: 'pointer',
                }}
              />
            )}
            <div 
              onClick={() => navHidden && setNavHidden(false)}
              style={{
              position: 'fixed',
              bottom: navHidden ? '-80px' : 0,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#0a0a0f',
              border: '2px solid #1a1a2e',
              borderBottom: 'none',
              display: 'flex',
              justifyContent: 'center',
              gap: isMobile ? '2px' : '8px',
              padding: isMobile ? '10px 8px 6px 8px' : '10px 20px',
              paddingBottom: isMobile ? 'max(10px, env(safe-area-inset-bottom))' : '10px',
              zIndex: 1000,
              borderRadius: '16px 16px 0 0',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.5)',
              transition: 'bottom 0.3s ease',
            }}>
              {/* Portfolio */}
              <button 
                onClick={() => setView('portfolio')}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: positions.length > 0 ? '#00d26a' : '#666',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  position: 'relative',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#0a1a0f')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>📦</span>
                <span>Bags</span>
                {positions.filter(p => p.status === 'active').length > 0 && (
                  <span style={{ position: 'absolute', top: 0, right: '50%', transform: 'translateX(12px)', backgroundColor: '#00d26a', color: '#000', fontSize: '8px', padding: '1px 4px', borderRadius: '6px', fontWeight: 'bold' }}>
                    {positions.filter(p => p.status === 'active').length}
                  </span>
                )}
              </button>
              
              {/* TXs */}
              <button 
                onClick={() => setShowTxHistory(true)}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: txHistory.length > 0 ? '#00d26a' : '#666',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#0a1a0f')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>⛓️</span>
                <span>TXs</span>
              </button>
              
              {/* Bets */}
              <button 
                onClick={() => setShowRugProphet(true)}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: rugProphetBets.length > 0 ? '#9b59b6' : '#666',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  position: 'relative',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#1a0a1a')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>🔮</span>
                <span>Bets</span>
                {rugProphetBets.filter(b => b.status === 'pending').length > 0 && (
                  <span style={{ position: 'absolute', top: 0, right: '50%', transform: 'translateX(12px)', backgroundColor: '#9b59b6', color: '#fff', fontSize: '8px', padding: '1px 4px', borderRadius: '6px', fontWeight: 'bold' }}>
                    {rugProphetBets.filter(b => b.status === 'pending').length}
                  </span>
                )}
              </button>
              
              {/* 🏆 Trading Wars */}
              <button 
                onClick={() => { setShowTradingWars(true); fetchTradingWars(); }}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#ffd700',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#1a1508')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>🏆</span>
                <span>Wars</span>
              </button>
              
              {/* Store */}
              <button 
                onClick={() => setView('store')}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#f7931a',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#1a0f08')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>₿</span>
                <span>Store</span>
              </button>
              
              {/* Paper Chat */}
              <button
                onClick={() => { setShowPaperChat(!showPaperChat); if (!showPaperChat) fetchDMs(); }}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: showPaperChat ? '#9b59b6' : '#3498db',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  position: 'relative',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#0a0f1a')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>💬</span>
                <span>Chat</span>
                {dmUnreadCount > 0 && (
                  <span style={{ position: 'absolute', top: 0, right: '50%', transform: 'translateX(12px)', backgroundColor: '#9b59b6', color: '#fff', fontSize: '8px', padding: '1px 4px', borderRadius: '6px', fontWeight: 'bold' }}>{dmUnreadCount}</span>
                )}
              </button>
              
              {/* Refer */}
              <button 
                onClick={() => setShowReferrals(true)}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#00d26a',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#0a1a0f')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>🔗</span>
                <span>Refer</span>
              </button>
              
              {/* 💭 Penny Ping - Global Feed */}
              <button 
                onClick={() => setShowPennyThoughts(true)}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#6366f1',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#0a0820')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>🏓</span>
                <span>Ping</span>
              </button>
              
              {/* Rewards */}
              <button 
                onClick={() => setShowRewards(true)}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: canClaimDaily() ? '#00d4aa' : '#666',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  position: 'relative',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#0a1515')}
                onMouseLeave={e => !isMobile && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>🎁</span>
                <span>Rewards</span>
                {canClaimDaily() && (
                  <span style={{ position: 'absolute', top: 0, right: '50%', transform: 'translateX(12px)', backgroundColor: '#00d4aa', color: '#000', fontSize: '8px', padding: '1px 4px', borderRadius: '6px', fontWeight: 'bold' }}>!</span>
                )}
              </button>
              
              {/* Music */}
              <button 
                onClick={() => setShowMusicSelector(!showMusicSelector)}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: musicEnabled && currentStation !== 'off' ? 'rgba(155,89,182,0.15)' : 'transparent',
                  border: 'none',
                  color: musicEnabled && currentStation !== 'off' ? '#9b59b6' : '#666',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.backgroundColor = '#1a0f1f')}
                onMouseLeave={e => !isMobile && !(musicEnabled && currentStation !== 'off') && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>🎵</span>
                <span>{musicEnabled && currentStation !== 'off' ? 'On' : 'Music'}</span>
              </button>
              
              {/* Bot - Only for premium users */}
              {(isPremium || isAdmin) && (
                <button 
                  onClick={() => {
                    if (tradingBotData?.active) {
                      stopTradingBot();
                    } else {
                      setShowTradingBot(true);
                      fetchTradingBotStatus();
                    }
                  }}
                  style={{
                    flex: 1,
                    maxWidth: isMobile ? 'none' : '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px',
                    backgroundColor: tradingBotData?.active ? 'rgba(168,85,247,0.15)' : 'transparent',
                    border: 'none',
                    color: tradingBotData?.active ? '#a855f7' : '#666',
                    fontSize: isMobile ? '10px' : '11px',
                    padding: isMobile ? '4px' : '8px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => !isMobile && !tradingBotData?.active && (e.currentTarget.style.backgroundColor = '#1a0a1a')}
                  onMouseLeave={e => !isMobile && !tradingBotData?.active && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <span style={{ fontSize: isMobile ? '18px' : '22px' }}>{tradingBotData?.active ? '⏹️' : '🤖'}</span>
                  <span>{tradingBotData?.active ? 'Stop' : 'Bot'}</span>
                </button>
              )}

              {/* 🥷 DEGEN AI */}
              <button
                onClick={() => setShowDegenAI(true)}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 'none' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: showDegenAI ? 'rgba(240, 185, 11, 0.15)' : 'transparent',
                  border: 'none',
                  color: showDegenAI ? '#f0b90b' : '#666',
                  fontSize: isMobile ? '10px' : '11px',
                  padding: isMobile ? '4px' : '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => !isMobile && !showDegenAI && (e.currentTarget.style.backgroundColor = '#1a150a')}
                onMouseLeave={e => !isMobile && !showDegenAI && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ fontSize: isMobile ? '18px' : '22px' }}>🥷</span>
                <span>AI</span>
              </button>
            </div>
            </>
          )}

          {/* Keyframes */}
          <style>{`
            @keyframes floatUp { 0% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-20px); } }
            @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
            @keyframes soundBar1 { 0%, 100% { height: 3px; } 50% { height: 10px; } }
            @keyframes soundBar2 { 0%, 100% { height: 7px; } 50% { height: 3px; } }
            @keyframes soundBar3 { 0%, 100% { height: 5px; } 50% { height: 8px; } }
            
            /* 🤖 Bot Focus Animation */
            @keyframes botFocusPulse {
              0%, 100% { box-shadow: 0 0 30px rgba(168, 85, 247, 0.6), inset 0 0 20px rgba(168, 85, 247, 0.1); }
              50% { box-shadow: 0 0 50px rgba(168, 85, 247, 0.9), inset 0 0 30px rgba(168, 85, 247, 0.2); }
            }
            
            /* 🤖 Bot Buy/Sell Button Pop Animation */
            @keyframes botTradeButtonPop {
              0% { transform: translateX(-50%) scale(0.5); opacity: 0; }
              50% { transform: translateX(-50%) scale(1.2); }
              100% { transform: translateX(-50%) scale(1); opacity: 1; }
            }
            
            /* 🤖 Bot Status Bar Pulse */
            @keyframes botPulse {
              0%, 100% { box-shadow: 0 0 10px rgba(168, 85, 247, 0.3); }
              50% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.6); }
            }
            
            /* 🤖 Bot Toast Slide In */
            @keyframes slideIn {
              0% { transform: translateX(100px); opacity: 0; }
              100% { transform: translateX(0); opacity: 1; }
            }
            
            /* 🚀 Launch Button Animations */
            @keyframes launchGlow {
              0%, 100% { box-shadow: inset 0 0 20px rgba(240, 185, 11, 0.3), 0 0 10px rgba(240, 185, 11, 0.2); }
              50% { box-shadow: inset 0 0 30px rgba(240, 185, 11, 0.5), 0 0 20px rgba(240, 185, 11, 0.4); }
            }
            @keyframes portfolioGlow {
              0%, 100% { box-shadow: inset 0 0 20px rgba(0, 210, 106, 0.2), 0 0 8px rgba(0, 210, 106, 0.15); }
              50% { box-shadow: inset 0 0 25px rgba(0, 210, 106, 0.35), 0 0 15px rgba(0, 210, 106, 0.3); }
            }
            @keyframes betsGlow {
              0%, 100% { box-shadow: inset 0 0 20px rgba(155, 89, 182, 0.2), 0 0 8px rgba(155, 89, 182, 0.15); }
              50% { box-shadow: inset 0 0 25px rgba(155, 89, 182, 0.35), 0 0 15px rgba(155, 89, 182, 0.3); }
            }
            @keyframes storeGlow {
              0%, 100% { box-shadow: inset 0 0 20px rgba(247, 147, 26, 0.2), 0 0 8px rgba(247, 147, 26, 0.15); }
              50% { box-shadow: inset 0 0 25px rgba(247, 147, 26, 0.35), 0 0 15px rgba(247, 147, 26, 0.3); }
            }
            @keyframes rewardsGlow {
              0%, 100% { box-shadow: inset 0 0 20px rgba(0, 212, 170, 0.2), 0 0 8px rgba(0, 212, 170, 0.15); }
              50% { box-shadow: inset 0 0 25px rgba(0, 212, 170, 0.35), 0 0 15px rgba(0, 212, 170, 0.3); }
            }
            @keyframes rocketBounce {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-3px); }
            }
            @keyframes shimmerText {
              0% { background-position: -200% center; }
              100% { background-position: 200% center; }
            }
            
            /* 🏆 Scrolling gold border animation */
            @keyframes borderRotate {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            
            @keyframes shimmer {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
            
            @keyframes glowPulse {
              0%, 100% { box-shadow: 0 0 10px rgba(240, 185, 11, 0.3), 0 0 20px rgba(240, 185, 11, 0.2), 0 0 30px rgba(240, 185, 11, 0.1); }
              50% { box-shadow: 0 0 15px rgba(240, 185, 11, 0.5), 0 0 30px rgba(240, 185, 11, 0.3), 0 0 45px rgba(240, 185, 11, 0.2); }
            }
            
            .trophy-box {
              position: relative;
              background: #0d0d12;
              border: 2px solid #f0b90b;
              border-radius: 10px;
              box-shadow: 0 0 10px rgba(240, 185, 11, 0.3), 0 0 20px rgba(240, 185, 11, 0.15);
              animation: glowPulse 2s ease-in-out infinite;
            }
            
            .scanner-indicator {
              transition: transform 0.2s ease, box-shadow 0.2s ease;
            }
            .scanner-indicator:hover {
              transform: scale(1.05);
            }
            
            /* Mobile viewport fixes */
            html, body {
              overflow-x: hidden !important;
              max-width: 100vw !important;
            }
            * {
              box-sizing: border-box;
            }
            
            /* 📱 MOBILE/TABLET FIXES */
            /* Prevent iOS zoom on input focus - inputs must be 16px+ font */
            @media screen and (max-width: 768px) {
              input, textarea, select {
                font-size: 16px !important;
              }
            }
            
            /* Ensure minimum tap target size (44x44 per Apple guidelines) */
            @media screen and (max-width: 768px) {
              button, [role="button"], a {
                min-height: 44px;
                min-width: 44px;
              }
            }
            
            /* Prevent double-tap zoom on interactive elements */
            button, a, [role="button"], input, select, textarea {
              touch-action: manipulation;
            }
            
            /* Smooth scrolling on iOS */
            .scroll-container {
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
            }
            
            /* Fix for iOS safe area (notched devices) */
            @supports (padding: env(safe-area-inset-bottom)) {
              .safe-area-bottom {
                padding-bottom: env(safe-area-inset-bottom);
              }
            }
            
            /* Modal scroll fix for mobile - prevent rubber band effect */
            body.modal-open {
              overflow: hidden !important;
              position: fixed !important;
              width: 100% !important;
              height: 100% !important;
            }
          `}</style>

          {/* 💭 PENNY PING - Big obvious panel */}
          <div style={{ 
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #0a0a15 0%, #0d0d1a 100%)',
            borderRadius: '12px',
            border: '1px solid #6366f133',
            overflow: 'hidden'
          }}>
            {/* Header with tabs */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '10px 14px',
              borderBottom: '1px solid #1a1a2e',
              background: 'linear-gradient(90deg, #6366f111 0%, transparent 100%)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>🏓</span>
                <span style={{ color: '#6366f1', fontWeight: 'bold', fontSize: '13px' }}>PENNY PING</span>
                <span style={{ color: '#666', fontSize: '10px' }}>Ping the world</span>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button 
                  onClick={() => setThoughtFeed('game')}
                  style={{ 
                    padding: '4px 10px', 
                    borderRadius: '12px', 
                    border: 'none',
                    backgroundColor: thoughtFeed === 'game' ? '#6366f1' : '#1a1a2e',
                    color: thoughtFeed === 'game' ? '#fff' : '#666',
                    fontSize: '10px',
                    cursor: 'pointer',
                    fontWeight: thoughtFeed === 'game' ? 'bold' : 'normal'
                  }}
                >
                  🎮 Game
                </button>
                <button 
                  onClick={() => setThoughtFeed('world')}
                  style={{ 
                    padding: '4px 10px', 
                    borderRadius: '12px', 
                    border: 'none',
                    backgroundColor: thoughtFeed === 'world' ? '#6366f1' : '#1a1a2e',
                    color: thoughtFeed === 'world' ? '#fff' : '#666',
                    fontSize: '10px',
                    cursor: 'pointer',
                    fontWeight: thoughtFeed === 'world' ? 'bold' : 'normal'
                  }}
                >
                  🌍 World
                </button>
              </div>
            </div>
            
            {/* Latest ping display */}
            <div style={{ padding: '12px 14px', minHeight: '60px' }}>
              {(thoughtFeed === 'game' ? gameThoughts : worldThoughts).length > 0 ? (() => {
                const t = (thoughtFeed === 'game' ? gameThoughts : worldThoughts)[0];
                return (
                  <div style={{ display: 'flex', gap: '12px' }}>
                    {/* Avatar */}
                    <div 
                      onClick={() => loadProfile(t.handle)}
                      style={{ 
                        width: '44px', 
                        height: '44px', 
                        borderRadius: '50%', 
                        overflow: 'hidden',
                        backgroundColor: '#1a1a2e',
                        flexShrink: 0,
                        cursor: 'pointer',
                        border: t.isThrone ? '2px solid #f0b90b' : '2px solid #6366f1'
                      }}
                    >
                      <img 
                        src={t.avatarUrl || `https://cloud.handcash.io/v2/users/profilePicture/${t.handle?.replace(/^\$/, '')}`}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      {/* Dethrone badge */}
                      {t.isThrone && (
                        <span style={{ 
                          backgroundColor: '#f0b90b22',
                          color: '#f0b90b',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          marginRight: '8px'
                        }}>
                          👑 DETHRONED
                        </span>
                      )}
                      <div style={{ 
                        color: '#fff', 
                        fontSize: isMobile ? '14px' : '16px', 
                        lineHeight: '1.4',
                        marginBottom: '8px',
                        wordBreak: 'break-word'
                      }}>
                        "{t.thought}"
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', flexWrap: 'wrap' }}>
                        <span 
                          onClick={() => loadProfile(t.handle)}
                          style={{ color: t.isThrone ? '#f0b90b' : '#6366f1', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          ${t.handle?.replace(/^\$/, '')}
                        </span>
                        {t.isPremium && <span style={{ color: '#f0b90b' }}>👑</span>}
                        {t.chainPosition && (
                          <span style={{ color: '#444', fontSize: '9px' }}>⛓️{t.chainPosition}</span>
                        )}
                        <span style={{ color: '#444' }}>•</span>
                        <span style={{ color: '#666' }}>
                          {(() => {
                            if (!t.timestamp) return '';
                            const diff = Date.now() - t.timestamp;
                            if (diff < 60000) return 'just now';
                            if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                            if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
                            return `${Math.floor(diff / 86400000)}d ago`;
                          })()}
                        </span>
                        {t.txId && (
                          <>
                            <span style={{ color: '#444' }}>•</span>
                            <a 
                              href={`https://whatsonchain.com/tx/${t.txId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#00d26a', fontSize: '9px', textDecoration: 'none' }}
                            >
                              ⛓️
                            </a>
                            <button
                              onClick={() => copyThoughtLink(t)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#666',
                                cursor: 'pointer',
                                padding: '0',
                                fontSize: '11px'
                              }}
                            >
                              🔗
                            </button>
                          </>
                        )}
                        {/* 🗳️ VOTING */}
                        <span style={{ color: '#444' }}>•</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <button
                            onClick={() => voteOnPing(t.id, t.myVote === 'up' ? 'none' : 'up')}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: t.myVote === 'up' ? '#00d26a' : '#666',
                              cursor: 'pointer',
                              padding: '2px 4px',
                              fontSize: '12px',
                              borderRadius: '4px',
                              transition: 'all 0.2s'
                            }}
                            title="Upvote"
                          >
                            ▲
                          </button>
                          <span style={{ 
                            color: (t.upvotes || 0) - (t.downvotes || 0) > 0 ? '#00d26a' : 
                                   (t.upvotes || 0) - (t.downvotes || 0) < 0 ? '#ff3b69' : '#666',
                            fontSize: '11px',
                            minWidth: '20px',
                            textAlign: 'center',
                            fontWeight: 'bold'
                          }}>
                            {(t.upvotes || 0) - (t.downvotes || 0)}
                          </span>
                          <button
                            onClick={() => voteOnPing(t.id, t.myVote === 'down' ? 'none' : 'down')}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: t.myVote === 'down' ? '#ff3b69' : '#666',
                              cursor: 'pointer',
                              padding: '2px 4px',
                              fontSize: '12px',
                              borderRadius: '4px',
                              transition: 'all 0.2s'
                            }}
                            title="Downvote"
                          >
                            ▼
                          </button>
                          {/* 🐦 SHARE TO TWITTER */}
                          <button
                            onClick={() => {
                              const pingText = t.thought || '';
                              const author = t.handle?.replace(/^\$/, '') || 'someone';
                              const isGameFeed = thoughtFeed === 'game';
                              const tweetText = pingText 
                                ? `"${pingText.slice(0, 180)}${pingText.length > 180 ? '...' : ''}"\n\n- $${author} ${isGameFeed ? '🎮 trading on Paper Hands' : '🌍 on Paper Hands World'}\n\n${isGameFeed ? '🎰 Learn to spot rugs before you get rugged:' : '💬 Join the conversation:'}`
                                : `${isGameFeed ? '🎰 Learn to spot rugs before you get rugged:' : '🌍 Check out Paper Hands World:'}`;
                              const url = 'https://paperhand.fun';
                              window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(url)}`, '_blank');
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#1DA1F2',
                              cursor: 'pointer',
                              padding: '2px 4px',
                              fontSize: '11px',
                              marginLeft: '6px'
                            }}
                            title="Share to Twitter"
                          >
                            𝕏
                          </button>
                          {/* 💰 TIP CREATOR */}
                          {t.handle?.replace(/^\$/, '') !== handcashUser?.handle?.replace(/^\$/, '') && (
                            <button
                              onClick={async () => {
                                const tipAmount = prompt(`Tip $${t.handle?.replace(/^\$/, '')} how many Paper Bucks?\n\nSuggested: 10,000 (1¢)`, '10000');
                                if (!tipAmount) return;
                                const amount = parseInt(tipAmount.replace(/,/g, ''));
                                if (isNaN(amount) || amount <= 0) { alert('Invalid amount'); return; }
                                if (amount > gameWallet) { alert('Insufficient balance'); return; }
                                try {
                                  const res = await fetch(`${API_URL}/api/tip`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                    body: JSON.stringify({ toHandle: t.handle?.replace(/^\$/, ''), amount })
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    setGameWallet(data.newBalance);
                                    Sounds.tip();
                                    alert(`💸 Tipped ${amount.toLocaleString()} PB to $${t.handle?.replace(/^\$/, '')}!`);
                                  } else {
                                    alert('Tip failed: ' + data.error);
                                  }
                                } catch (e) {
                                  alert('Tip error: ' + e.message);
                                }
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#f0b90b',
                                cursor: 'pointer',
                                padding: '2px 4px',
                                fontSize: '11px'
                              }}
                              title={`Tip $${t.handle?.replace(/^\$/, '')}`}
                            >
                              💰
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })() : (
                <div style={{ color: '#444', textAlign: 'center', padding: '10px' }}>
                  No pings yet. Be the first! 🏓
                </div>
              )}
            </div>
            
            {/* Post input */}
            {handcashUser && (
              <div style={{ 
                padding: '10px 14px', 
                borderTop: '1px solid #1a1a2e',
                display: 'flex',
                gap: '8px',
                alignItems: 'center'
              }}>
                <input
                  type="text"
                  value={newThought}
                  onChange={(e) => setNewThought(e.target.value.slice(0, 280))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); postPing(false); } }}
                  placeholder={thoughtFeed === 'game' ? "What's the play?" : "Ping the world..."}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: '20px',
                    border: '1px solid #333',
                    backgroundColor: '#0a0a0f',
                    color: '#fff',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                  maxLength={280}
                />
                <button
                  onClick={() => postPing(false)}
                  disabled={postingThought || !newThought.trim()}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '20px',
                    border: 'none',
                    backgroundColor: postingThought || !newThought.trim() ? '#333' : '#6366f1',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: postingThought || !newThought.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  🏓 1¢
                </button>
                <button
                  onClick={() => postPing(true)}
                  disabled={postingThought || !newThought.trim()}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '20px',
                    border: 'none',
                    backgroundColor: postingThought || !newThought.trim() ? '#333' : '#f0b90b',
                    color: '#000',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: postingThought || !newThought.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  👑 2¢
                </button>
              </div>
            )}
            
            {/* Character count */}
            {newThought.length > 0 && (
              <div style={{ 
                padding: '0 14px 8px', 
                textAlign: 'right', 
                fontSize: '9px', 
                color: newThought.length > 250 ? '#ff8c00' : '#444' 
              }}>
                {newThought.length}/280
              </div>
            )}
            
            {/* Expand to see more */}
            {(thoughtFeed === 'game' ? gameThoughts : worldThoughts).length > 1 && (
              <div 
                onClick={() => setShowPennyThoughts(true)}
                style={{ 
                  padding: '8px 14px', 
                  borderTop: '1px solid #1a1a2e',
                  textAlign: 'center',
                  color: '#6366f1',
                  fontSize: '11px',
                  cursor: 'pointer'
                }}
              >
                View {(thoughtFeed === 'game' ? gameThoughts : worldThoughts).length - 1} more pings ↓
              </div>
            )}
          </div>
          {/* 💼 UNIFIED PORTFOLIO BANNER - Shows when ANY positions exist (manual + bot unified) */}
          {(() => {
            const activePositions = positions.filter(p => p.status === 'active');
            const totalPositionCount = activePositions.length;

            // Show if ANY positions exist
            if (totalPositionCount === 0) return null;

            // Calculate total value across all positions (manual + bot now unified)
            const totalValue = activePositions.reduce((sum, p) => {
              const token = activeTokens.find(t => t.id === p.tokenId);
              const tokenPrice = token?.price || 1.0;
              const multiplier = p.entry > 0 ? tokenPrice / p.entry : 1;
              return sum + Math.floor(p.invested * multiplier);
            }, 0);

            const totalValueUSD = (totalValue / 100000000 * bsvPrice).toFixed(2);

            return (
            <div
              onClick={() => setView('portfolio')}
              style={{
                backgroundColor: '#0a0f1a',
                border: '2px solid #9b59b6',
                borderRadius: isMobile ? '8px' : '10px',
                padding: isMobile ? '10px' : '14px',
                marginBottom: isMobile ? '8px' : '12px',
                cursor: 'pointer',
                boxShadow: '0 0 20px rgba(155,89,182,0.2)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 30px rgba(155,89,182,0.4)'; e.currentTarget.style.transform = 'scale(1.01)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 20px rgba(155,89,182,0.2)'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: isMobile ? '36px' : '40px',
                    height: isMobile ? '36px' : '40px',
                    borderRadius: '50%',
                    backgroundColor: '#9b59b6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'pulse 2s infinite',
                  }}>
                    <span style={{ fontSize: isMobile ? '16px' : '18px' }}>💼</span>
                  </div>
                  <div>
                    <div style={{ color: '#9b59b6', fontWeight: 'bold', fontSize: isMobile ? '13px' : '14px' }}>
                      PORTFOLIO ({totalPositionCount})
                    </div>
                    <div style={{ color: '#888', fontSize: isMobile ? '10px' : '11px' }}>
                      Tap to view details
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: isMobile ? '16px' : '20px',
                    fontWeight: 'bold',
                    color: '#9b59b6'
                  }}>
                    {formatPB(totalValue)}
                  </div>
                  <div style={{
                    fontSize: isMobile ? '11px' : '13px',
                    color: '#888',
                    fontWeight: 'bold'
                  }}>
                    ${totalValueUSD} USD
                  </div>
                </div>
              </div>
              <div style={{
                marginTop: isMobile ? '6px' : '10px',
                padding: isMobile ? '6px 10px' : '8px 12px',
                backgroundColor: 'rgba(155,89,182,0.1)',
                borderRadius: '6px',
                textAlign: 'center',
                color: '#9b59b6',
                fontSize: isMobile ? '10px' : '12px',
                fontWeight: 'bold'
              }}>
                👆 TAP TO VIEW PORTFOLIO
              </div>
            </div>
          );
          })()}

          {/* 🔮 PENDING BETS BANNER - Shows when player has open Rug Prophet bets */}
          {rugProphetBets.filter(b => b.status === 'pending').length > 0 && (
            <div 
              style={{ 
                backgroundColor: '#1a0f1f', 
                border: '2px solid #9b59b6', 
                borderRadius: '10px', 
                padding: '14px', 
                marginBottom: '20px', 
                boxShadow: '0 0 20px rgba(155,89,182,0.2)',
              }}
            >
              {(() => {
                const pendingBets = rugProphetBets.filter(b => b.status === 'pending');
                const totalStaked = pendingBets.reduce((sum, b) => sum + (b.amount || 0), 0);
                
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '50%', 
                          backgroundColor: '#9b59b6', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          animation: 'pulse 2s infinite',
                        }}>
                          <span style={{ fontSize: '18px' }}>🔮</span>
                        </div>
                        <div>
                          <div style={{ color: '#9b59b6', fontWeight: 'bold', fontSize: '14px' }}>
                            🎯 {pendingBets.length} PENDING BET{pendingBets.length > 1 ? 'S' : ''}
                          </div>
                          <div style={{ color: '#888', fontSize: '11px' }}>
                            {formatPB(totalStaked)} at risk • Potential: {formatPB(pendingBets.reduce((sum, b) => sum + Math.floor(b.amount * (b.prediction === 'rug' ? 1.8 : 2.5)), 0))}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowRugProphet(true)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#9b59b633',
                          border: '1px solid #9b59b6',
                          borderRadius: '6px',
                          color: '#9b59b6',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        View All
                      </button>
                    </div>
                    
                    {/* Individual bet tokens - clickable to go to token */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {pendingBets.map(bet => {
                        const token = activeTokens.find(t => t.id === bet.tokenId);
                        return (
                          <button
                            key={bet.id}
                            onClick={() => {
                              if (token) {
                                setCurrentToken(token);
                                setCurrentPrice(token.currentPrice || token.price || 1);
                                setView('token');
                                setGameState('spectating'); // Reset game state for new token!
                              }
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              backgroundColor: '#111',
                              border: `1px solid ${bet.prediction === 'rug' ? '#ff3b6944' : '#00d26a44'}`,
                              borderRadius: '8px',
                              cursor: token ? 'pointer' : 'default',
                              opacity: token ? 1 : 0.5
                            }}
                          >
                            <span style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '12px' }}>{bet.tokenTicker}</span>
                            <span style={{ 
                              fontSize: '10px', 
                              color: bet.prediction === 'rug' ? '#ff3b69' : '#00d26a' 
                            }}>
                              {bet.prediction === 'rug' ? '💀' : '🌙'}
                            </span>
                            <span style={{ color: '#f0b90b', fontSize: '10px' }}>{formatPB(bet.amount)}</span>
                            {token && (
                              <span style={{ 
                                color: (token.priceChange || 0) >= 0 ? '#00d26a' : '#ff3b69',
                                fontSize: '10px'
                              }}>
                                {(token.priceChange || 0) >= 0 ? '+' : ''}{(token.priceChange || 0).toFixed(1)}%
                              </span>
                            )}
                            <span style={{ color: '#9b59b6', fontSize: '10px' }}>👁️</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* 🎉 RESOLVED BETS BANNER - Shows recently won/lost bets that need attention */}
          {(() => {
            const resolvedBets = rugProphetBets.filter(b => b.status === 'won' || b.status === 'lost');
            if (resolvedBets.length === 0) return null;
            
            const wonBets = resolvedBets.filter(b => b.status === 'won');
            const lostBets = resolvedBets.filter(b => b.status === 'lost');
            const totalWon = wonBets.reduce((sum, b) => sum + (b.payout || 0), 0);
            const totalLost = lostBets.reduce((sum, b) => sum + (b.amount || 0), 0);
            
            return (
              <div 
                style={{ 
                  backgroundColor: wonBets.length > 0 ? '#0d1a0d' : '#1a0d0d', 
                  border: `2px solid ${wonBets.length > 0 ? '#00d26a' : '#ff3b69'}`, 
                  borderRadius: '10px', 
                  padding: '14px', 
                  marginBottom: '20px', 
                  boxShadow: wonBets.length > 0 ? '0 0 20px rgba(0,210,106,0.3)' : '0 0 20px rgba(255,59,105,0.2)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '50%', 
                      backgroundColor: wonBets.length > 0 ? '#00d26a' : '#ff3b69', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: '18px' }}>{wonBets.length > 0 ? '🎉' : '💀'}</span>
                    </div>
                    <div>
                      <div style={{ color: wonBets.length > 0 ? '#00d26a' : '#ff3b69', fontWeight: 'bold', fontSize: '14px' }}>
                        {wonBets.length > 0 ? `🔮 ${wonBets.length} BET${wonBets.length > 1 ? 'S' : ''} WON!` : `🔮 ${lostBets.length} BET${lostBets.length > 1 ? 'S' : ''} LOST`}
                      </div>
                      <div style={{ color: '#888', fontSize: '11px' }}>
                        {wonBets.length > 0 
                          ? `+${formatPB(totalWon)} to collect!`
                          : `${formatPB(totalLost)} lost`
                        }
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {lostBets.length > 0 && (
                      <button
                        onClick={() => {
                          // Dismiss all lost bets
                          setRugProphetBets(prev => prev.filter(b => b.status !== 'lost'));
                        }}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: 'transparent',
                          border: '1px solid #ff3b69',
                          borderRadius: '8px',
                          color: '#ff3b69',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️ Dismiss Lost
                      </button>
                    )}
                    <button
                      onClick={() => {
                        // Open the first resolved bet
                        const firstResolved = wonBets[0] || lostBets[0];
                        if (firstResolved) setSelectedBet(firstResolved);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: wonBets.length > 0 ? '#00d26a' : '#ff3b6933',
                      border: 'none',
                      borderRadius: '8px',
                      color: wonBets.length > 0 ? '#000' : '#ff3b69',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    {wonBets.length > 0 ? '🎉 Collect' : '👀 View'}
                  </button>
                  </div>
                </div>
                
                {/* Show individual resolved bets */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {resolvedBets.slice(0, 5).map(bet => (
                    <button
                      key={bet.id}
                      onClick={() => setSelectedBet(bet)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: bet.status === 'won' ? '#00d26a22' : '#ff3b6922',
                        border: `1px solid ${bet.status === 'won' ? '#00d26a' : '#ff3b69'}`,
                        borderRadius: '20px',
                        color: bet.status === 'won' ? '#00d26a' : '#ff3b69',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      {bet.status === 'won' ? '✅' : '❌'} {bet.tokenTicker}
                      <span style={{ color: '#f0b90b' }}>
                        {bet.status === 'won' ? `+${formatPB(bet.payout)}` : `-${formatPB(bet.amount)}`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Premium Upsell - Simplified on mobile */}
          {!isPremium && (
            isMobile ? (
              // Mobile: Just a centered button
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <button 
                  onClick={() => setShowPremiumComparison(true)} 
                  disabled={transactionPending} 
                  style={{ 
                    padding: '12px 32px', 
                    borderRadius: '8px', 
                    border: 'none', 
                    cursor: transactionPending ? 'wait' : 'pointer', 
                    backgroundColor: (handcashDown && !isTestPilot) ? '#666' : '#f0b90b', 
                    color: (handcashDown && !isTestPilot) ? '#333' : '#000', 
                    fontWeight: 'bold', 
                    fontSize: '14px', 
                    opacity: (transactionPending || (handcashDown && !isTestPilot)) ? 0.7 : 1 
                  }}
                >
                  👑 Unlock Premium
                </button>
              </div>
            ) : (
              // Desktop: Full bar with details
              <div style={{ backgroundColor: '#1a1508', border: '1px solid #3d2e0a', borderRadius: '8px', padding: '12px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '13px' }}>👑 Unlock Premium</div>
                  <div style={{ color: '#888', fontSize: '11px' }}>
                    Only ${PREMIUM_USD_DISPLAY} • +19% win rate, Insider access, AlphaBot 
                    <span 
                      onClick={() => setShowPremiumComparison(true)} 
                      style={{ color: '#f0b90b', cursor: 'pointer', marginLeft: '6px', textDecoration: 'underline' }}
                    >
                      See all benefits →
                    </span>
                  </div>
                </div>
                <button 
                  onClick={() => setShowPremiumComparison(true)} 
                  disabled={transactionPending} 
                  style={{ 
                    padding: '8px 16px', 
                    borderRadius: '6px', 
                    border: 'none', 
                    cursor: transactionPending ? 'wait' : 'pointer', 
                    backgroundColor: (handcashDown && !isTestPilot) ? '#666' : '#f0b90b', 
                    color: (handcashDown && !isTestPilot) ? '#333' : '#000', 
                    fontWeight: 'bold', 
                    fontSize: '11px', 
                    whiteSpace: 'nowrap', 
                    opacity: (transactionPending || (handcashDown && !isTestPilot)) ? 0.7 : 1 
                  }}
                >
                  {transactionPending ? 'Processing...' : (handcashDown && !isTestPilot) ? '⚠️ Unavailable' : 'Unlock Now'}
                </button>
              </div>
            )
          )}
          
          {/* 🎛️ QUICK ACTIONS BAR - Unified compact row */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '6px' : '8px',
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: '12px',
            justifyContent: 'center',
            padding: '8px 0'
          }}>
            {/* 🎯 Daily Challenges Pill - Compact with notification */}
            {dailyChallenges.length > 0 && (
              <button
                onClick={() => setShowChallenges(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: isMobile ? '6px 10px' : '6px 12px',
                  borderRadius: '20px',
                  border: dailyChallenges.filter(c => c.progress >= c.target && !c.claimed).length > 0
                    ? '1px solid #f0b90b'
                    : '1px solid #f0b90b44',
                  backgroundColor: dailyChallenges.filter(c => c.progress >= c.target && !c.claimed).length > 0
                    ? 'rgba(240,185,11,0.2)'
                    : 'rgba(240,185,11,0.05)',
                  color: '#f0b90b',
                  fontSize: isMobile ? '10px' : '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minHeight: '32px',
                }}
              >
                <span>🎯</span>
                {dailyChallenges.filter(c => c.progress >= c.target && !c.claimed).length > 0 ? (
                  <>
                    <span>{dailyChallenges.filter(c => c.progress >= c.target && !c.claimed).length} reward{dailyChallenges.filter(c => c.progress >= c.target && !c.claimed).length > 1 ? 's' : ''}</span>
                    <span style={{
                      backgroundColor: '#f0b90b',
                      color: '#000',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>!</span>
                  </>
                ) : (
                  <span>{dailyChallenges.filter(c => c.claimed).length}/{dailyChallenges.length}</span>
                )}
              </button>
            )}

            {/* AI Tokens / BSV.pump toggle */}
            <button
              onClick={() => setTokenViewMode('ai')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: isMobile ? '5px 8px' : '6px 12px',
                borderRadius: '20px',
                border: tokenViewMode === 'ai' ? '1px solid #00d4aa' : '1px solid #333',
                backgroundColor: tokenViewMode === 'ai' ? 'rgba(0,212,170,0.15)' : 'transparent',
                color: tokenViewMode === 'ai' ? '#00d4aa' : '#666',
                fontSize: isMobile ? '9px' : '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                minHeight: '32px',
              }}
            >
              <span>🤖</span> {isMobile ? 'AI' : 'AI Tokens'}
            </button>
            <button
              onClick={() => setTokenViewMode('user')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: isMobile ? '5px 8px' : '6px 12px',
                borderRadius: '20px',
                border: tokenViewMode === 'user' ? '1px solid #f0b90b' : '1px solid #333',
                backgroundColor: tokenViewMode === 'user' ? 'rgba(240,185,11,0.15)' : 'transparent',
                color: tokenViewMode === 'user' ? '#f0b90b' : '#666',
                fontSize: isMobile ? '9px' : '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                minHeight: '32px',
              }}
            >
              <span>🚀</span> {isMobile ? 'PvP' : 'BSV.pump'}
            </button>
            
            {/* 🚀 Rug Rush - Simple Crash Game (hidden if visibility off unless admin) */}
            {(gameVisibility.rugRushVisible || isAdmin) && (
              <button
                onClick={() => setShowRugRush(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: isMobile ? '5px 8px' : '6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #ff6b35',
                  backgroundColor: rugRushState?.round?.phase === 'betting' ? 'rgba(255,107,53,0.25)' : 'rgba(255,107,53,0.1)',
                  color: '#ff6b35',
                  fontSize: isMobile ? '9px' : '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  minHeight: '32px',
                  position: 'relative',
                  animation: rugRushState?.round?.phase === 'betting' ? 'pulse 2s infinite' : 'none',
                  opacity: gameVisibility.rugRushVisible ? 1 : 0.5,
                }}
              >
                <span>🚀</span> {isMobile ? 'Rush' : 'Rug Rush'}
                {!gameVisibility.rugRushVisible && isAdmin && (
                  <span style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    backgroundColor: '#ff3b69',
                    color: '#fff',
                    fontSize: '7px',
                    padding: '2px 4px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                  }}>HIDDEN</span>
                )}
                {gameVisibility.rugRushVisible && rugRushState?.round?.phase === 'betting' && (
                  <span style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    backgroundColor: '#00d26a',
                    color: '#000',
                    fontSize: '8px',
                    padding: '2px 5px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                  }}>BET</span>
                )}
                {gameVisibility.rugRushVisible && rugRushState?.round?.phase === 'running' && (
                  <span style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    backgroundColor: '#ff6b35',
                    color: '#000',
                    fontSize: '8px',
                    padding: '2px 5px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                  }}>LIVE</span>
                )}
              </button>
            )}

            {/* 🏔️ The Last Degen - Mountain Climber Battle Royale (hidden if visibility off unless admin) */}
            {(gameVisibility.mountainVisible || isAdmin) && (
              <button
                onClick={() => setShowMountainClimber(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: isMobile ? '5px 8px' : '6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #9b59b6',
                  backgroundColor: 'rgba(155, 89, 182, 0.1)',
                  color: '#9b59b6',
                  fontSize: isMobile ? '9px' : '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  minHeight: '32px',
                  position: 'relative',
                  opacity: gameVisibility.mountainVisible ? 1 : 0.5,
                }}
              >
                <span>🏔️</span> {isMobile ? 'Degen' : 'Last Degen'}
                {!gameVisibility.mountainVisible && isAdmin && (
                  <span style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    backgroundColor: '#ff3b69',
                    color: '#fff',
                    fontSize: '7px',
                    padding: '2px 4px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                  }}>HIDDEN</span>
                )}
              </button>
            )}
          </div>
          
          {/* 🏆 TRADING WARS - Lobby Banner */}
          {tradingWarsData?.war && (
            <div 
              onClick={() => { setShowTradingWars(true); fetchTradingWars(); }}
              style={{ 
                marginBottom: '16px',
                background: 'linear-gradient(135deg, #1a1508 0%, #0f0a05 100%)',
                borderRadius: '12px',
                border: '1px solid #ffd70066',
                padding: isMobile ? '12px' : '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#ffd700'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#ffd70066'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: isMobile ? '24px' : '28px' }}>🏆</span>
                <div>
                  <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: isMobile ? '13px' : '15px' }}>
                    TRADING WARS
                  </div>
                  <div style={{ color: '#888', fontSize: isMobile ? '9px' : '10px' }}>
                    War #{tradingWarsData.war.warNumber} • {tradingWarsData.war.qualifiedCount} qualified
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: isMobile ? '16px' : '20px' }}>
                  ${(tradingWarsData.war.prizePoolUSD || 0).toFixed(2)}
                </div>
                <div style={{ color: '#666', fontSize: isMobile ? '9px' : '10px' }}>
                  Prize Pool • Tap to compete →
                </div>
              </div>
            </div>
          )}
          
          {/* Active Tokens */}
          <div style={{ marginBottom: isMobile ? '120px' : '100px' }}>
            <div style={{ 
              marginBottom: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? '11px' : '12px', color: '#888' }}>🔥 ACTIVE TOKENS</h2>
            </div>
            
            <div style={{
              display: tileViewMode === 'list' ? 'flex' : 'grid',
              flexDirection: tileViewMode === 'list' ? 'column' : undefined,
              gridTemplateColumns: tileViewMode === 'list' ? undefined :
                                   tileViewMode === 'mini' ? (isMobile ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)') :
                                   (isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'),
              gap: tileViewMode === 'list' ? '4px' : (isMobile ? '8px' : '12px'),
              minHeight: '280px',
            }}>
              {/* Show User Tokens view */}
              {tokenViewMode === 'user' ? (
                <>
                  {/* BSV.pump Info Bar - Always visible */}
                  <div style={{ 
                    gridColumn: '1 / -1', 
                    backgroundColor: '#1a1508', 
                    border: '1px solid #f0b90b44', 
                    borderRadius: '8px', 
                    padding: isMobile ? '10px' : '12px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>🚀</span>
                      <div>
                        <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}>BSV.pump</div>
                        <div style={{ color: '#888', fontSize: isMobile ? '9px' : '10px' }}>Launch for 10¢ • Pure PvP • No bots • No rugs</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {/* Sort dropdown */}
                      <select
                        value={pvpSortBy}
                        onChange={(e) => setPvpSortBy(e.target.value)}
                        style={{
                          backgroundColor: '#1a1a2e',
                          border: '1px solid #f0b90b44',
                          borderRadius: '16px',
                          padding: isMobile ? '4px 20px 4px 8px' : '6px 24px 6px 10px',
                          color: '#f0b90b',
                          fontSize: isMobile ? '9px' : '10px',
                          cursor: 'pointer',
                          outline: 'none',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23f0b90b\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 6px center',
                          backgroundSize: '8px',
                          fontWeight: 'bold',
                        }}
                      >
                        <option value="newest">🔃 New</option>
                        <option value="mcap">💰 MCap</option>
                        <option value="liquidity">💧 Liq</option>
                        <option value="volume">📊 Vol</option>
                        <option value="ath">🔥 ATH</option>
                      </select>
                      {/* View mode toggles */}
                      <div style={{
                        display: 'flex',
                        gap: '2px',
                        backgroundColor: '#1a1a2e',
                        borderRadius: '20px',
                        padding: '3px',
                        alignItems: 'center',
                      }}>
                        <button
                          onClick={() => changeTileViewMode('maximized')}
                          title="Maximized tiles"
                          style={{
                            padding: isMobile ? '4px 6px' : '4px 8px',
                            borderRadius: '16px',
                            border: 'none',
                            backgroundColor: tileViewMode === 'maximized' ? '#f0b90b' : 'transparent',
                            color: tileViewMode === 'maximized' ? '#000' : '#f0b90b66',
                            fontSize: isMobile ? '10px' : '12px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                          }}
                        >⊞</button>
                        <button
                          onClick={() => changeTileViewMode('mini')}
                          title="Mini tiles"
                          style={{
                            padding: isMobile ? '4px 6px' : '4px 8px',
                            borderRadius: '16px',
                            border: 'none',
                            backgroundColor: tileViewMode === 'mini' ? '#f0b90b' : 'transparent',
                            color: tileViewMode === 'mini' ? '#000' : '#f0b90b66',
                            fontSize: isMobile ? '10px' : '12px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                          }}
                        >▦</button>
                        <button
                          onClick={() => changeTileViewMode('list')}
                          title="List view"
                          style={{
                            padding: isMobile ? '4px 6px' : '4px 8px',
                            borderRadius: '16px',
                            border: 'none',
                            backgroundColor: tileViewMode === 'list' ? '#f0b90b' : 'transparent',
                            color: tileViewMode === 'list' ? '#000' : '#f0b90b66',
                            fontSize: isMobile ? '10px' : '12px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                          }}
                        >☰</button>
                      </div>
                      {/* Home button */}
                      <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        title="Back to top"
                        style={{
                          padding: isMobile ? '6px 10px' : '6px 12px',
                          backgroundColor: 'transparent',
                          border: '1px solid #f0b90b44',
                          borderRadius: '6px',
                          color: '#f0b90b',
                          fontSize: isMobile ? '12px' : '14px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f0b90b'; e.currentTarget.style.color = '#000'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#f0b90b'; }}
                      >🏠</button>
                      {/* Creator Dashboard Button - Always show for creators */}
                      <button
                        onClick={() => setShowCreatorDashboard(true)}
                        style={{
                          padding: isMobile ? '8px 12px' : '8px 16px',
                          backgroundColor: myLaunchedTokens.length > 0 ? 'transparent' : '#1a1a2e',
                          border: `1px solid ${myLaunchedTokens.length > 0 ? '#ff8c00' : '#333'}`,
                          borderRadius: '6px',
                          color: myLaunchedTokens.length > 0 ? '#ff8c00' : '#666',
                          fontWeight: 'bold',
                          fontSize: isMobile ? '10px' : '11px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ff8c00'; e.currentTarget.style.color = '#000'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = myLaunchedTokens.length > 0 ? 'transparent' : '#1a1a2e'; e.currentTarget.style.color = myLaunchedTokens.length > 0 ? '#ff8c00' : '#666'; }}
                      >
                        👑 {myLaunchedTokens.length > 0 ? `My Tokens (${myLaunchedTokens.length})` : 'My Tokens'}
                      </button>
                      <button
                        onClick={() => setShowLaunchToken(true)}
                        disabled={handcashDown && !isTestPilot}
                        style={{
                          padding: isMobile ? '8px 12px' : '8px 16px',
                          backgroundColor: '#f0b90b',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#000',
                          fontWeight: 'bold',
                          fontSize: isMobile ? '10px' : '11px',
                          cursor: (handcashDown && !isTestPilot) ? 'not-allowed' : 'pointer',
                          opacity: (handcashDown && !isTestPilot) ? 0.5 : 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        🚀 Launch Token
                      </button>
                    </div>
                  </div>
                  
                  {/* Use activeTokens filtered for user-launched */}
                  {activeTokens.filter(t => t.isUserLaunched && t.state === 'active').length > 0 ? (
                    activeTokens
                      .filter(t => t.isUserLaunched && t.state === 'active')
                      .sort((a, b) => {
                        switch(pvpSortBy) {
                          case 'mcap': return (b.marketCap || 0) - (a.marketCap || 0);
                          case 'liquidity': return (b.liquidity || b.liquidityPool || 0) - (a.liquidity || a.liquidityPool || 0);
                          case 'volume': return (b.volume || 0) - (a.volume || 0);
                          case 'ath': return (b.priceFromATH || -1) - (a.priceFromATH || -1);
                          case 'newest':
                          default: return (b.birthTime || 0) - (a.birthTime || 0);
                        }
                      })
                      .map(token => {
                      const athDisplay = (token.priceFromATH || 0) >= -0.1
                        ? <span style={{ color: '#00d26a' }}>ATH 🔥</span>
                        : <span style={{ color: '#ff3b69' }}>{formatPercent(token.priceFromATH || 0)} ATH</span>;

                      // === LIST VIEW ===
                      if (tileViewMode === 'list') {
                        return (
                          <div
                            key={token.id}
                            onClick={() => { setCurrentToken(token); setView('token'); setGameState('spectating'); }}
                            style={{
                              borderRadius: '6px',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '12px',
                              transition: 'all 0.15s',
                              border: '1px solid #f0b90b33',
                              marginBottom: '4px',
                              backgroundColor: '#0d0d12',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1a1508'; e.currentTarget.style.borderColor = '#f0b90b'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#0d0d12'; e.currentTarget.style.borderColor = '#f0b90b33'; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                              <span style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '13px' }}>${token.ticker}</span>
                              <span style={{ color: '#555', fontSize: '9px' }}>by ${token.creator || 'anon'}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '11px' }}>
                              <span style={{ color: '#888' }}>MCAP <span style={{ color: '#00d26a', fontWeight: 'bold' }}>{formatPB(token.marketCap || 0)}</span></span>
                              <span style={{ color: '#888' }}>VOL <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>{formatPB(token.volume || 0)}</span></span>
                              <span style={{ color: '#888' }}>LIQ <span style={{ color: '#00d4aa', fontWeight: 'bold' }}>{formatPB(token.liquidity || 0)}</span></span>
                              <div style={{ fontWeight: 'bold', fontSize: '12px', minWidth: '70px', textAlign: 'right' }}>{athDisplay}</div>
                            </div>
                          </div>
                        );
                      }

                      // === MINI VIEW ===
                      if (tileViewMode === 'mini') {
                        return (
                          <div
                            key={token.id}
                            onClick={() => { setCurrentToken(token); setView('token'); setGameState('spectating'); }}
                            style={{
                              backgroundColor: '#0d0d12',
                              border: '1px solid #f0b90b',
                              borderRadius: '8px',
                              padding: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              textAlign: 'center',
                            }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = '#ffd700'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = '#f0b90b'}
                          >
                            <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '12px', marginBottom: '4px' }}>${token.ticker}</div>
                            <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '2px' }}>{athDisplay}</div>
                            <div style={{ fontSize: '9px', color: '#888' }}>{formatPB(token.marketCap || 0)}</div>
                          </div>
                        );
                      }

                      // === MAXIMIZED VIEW (default) ===
                      return (
                        <div
                          key={token.id}
                          onClick={() => { setCurrentToken(token); setView('token'); setGameState('spectating'); }}
                          style={{
                            backgroundColor: '#0d0d12',
                            border: '2px solid #f0b90b',
                            borderRadius: '10px',
                            padding: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = '#ffd700'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = '#f0b90b'}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <div>
                              <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '16px' }}>${token.ticker}</div>
                              <div style={{ color: '#666', fontSize: '10px' }}>{token.category || 'BSV.pump'}</div>
                            </div>
                            <div style={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'right' }}>{athDisplay}</div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#888', marginBottom: '8px' }}>
                            <span>MCAP</span>
                            <span>VOLUME</span>
                            <span>LIQ</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>
                            <span style={{ color: '#00d26a' }}>{formatPB(token.marketCap || 0)}</span>
                            <span style={{ color: '#f0b90b' }}>{formatPB(token.volume || 0)}</span>
                            <span style={{ color: '#00d4aa' }}>{formatPB(token.liquidity || 0)}</span>
                          </div>
                          <div style={{ backgroundColor: '#1a1a2e', borderRadius: '4px', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#f0b90b', fontSize: '10px' }}>🚀 by ${token.creator || 'anon'}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    /* Empty state - BSV.pump explainer + CTA */
                    <>
                      {/* How BSV.pump Works Banner */}
                      <div style={{ 
                        gridColumn: '1 / -1', 
                        backgroundColor: '#0d0d12', 
                        border: '2px solid #f0b90b', 
                        borderRadius: '12px', 
                        padding: '20px',
                        marginBottom: '8px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                          <span style={{ fontSize: '24px' }}>🚀</span>
                          <div>
                            <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '18px' }}>BSV.pump - Player vs Player</div>
                            <div style={{ color: '#888', fontSize: '12px' }}>Launch tokens for 10¢ • Pure PvP • No bots</div>
                          </div>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>💰</div>
                            <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '11px' }}>SELF-FUNDED</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>Your PB = the pool</div>
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>🎮</div>
                            <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '11px' }}>PURE PvP</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>Trade against players</div>
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>🏠</div>
                            <div style={{ color: '#888', fontWeight: 'bold', fontSize: '11px' }}>1% RAKE</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>On each trade</div>
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>♾️</div>
                            <div style={{ color: '#888', fontWeight: 'bold', fontSize: '11px' }}>NO EXPIRY</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>Tokens live forever</div>
                          </div>
                        </div>
                        
                        <div style={{ backgroundColor: '#1a1508', border: '1px solid #f0b90b44', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                          <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>⚙️ HOW IT WORKS</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '11px', color: '#aaa' }}>
                            <span style={{ backgroundColor: '#222', padding: '4px 8px', borderRadius: '4px' }}>1️⃣ Pay 10¢ BSV</span>
                            <span style={{ backgroundColor: '#222', padding: '4px 8px', borderRadius: '4px' }}>2️⃣ Add PB liquidity</span>
                            <span style={{ backgroundColor: '#222', padding: '4px 8px', borderRadius: '4px' }}>3️⃣ Others trade your token</span>
                            <span style={{ backgroundColor: '#222', padding: '4px 8px', borderRadius: '4px' }}>4️⃣ Profit when they buy! 📈</span>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => setShowLaunchToken(true)}
                          disabled={handcashDown && !isTestPilot}
                          style={{
                            width: '100%',
                            padding: '14px',
                            backgroundColor: '#f0b90b',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#000',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            cursor: (handcashDown && !isTestPilot) ? 'not-allowed' : 'pointer',
                            opacity: (handcashDown && !isTestPilot) ? 0.5 : 1,
                          }}
                        >
                          🚀 Launch Your Token for 10¢
                        </button>
                      </div>
                      
                      {/* Placeholder cards */}
                      {Array(4).fill(null).map((_, i) => (
                        <div
                          key={`empty-${i}`}
                          style={{
                            backgroundColor: '#0d0d12',
                            border: '2px dashed #1a1a2e',
                            borderRadius: '10px',
                            padding: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '120px',
                          }}
                        >
                          <div style={{ fontSize: '24px', marginBottom: '6px', opacity: 0.2 }}>🎰</div>
                          <div style={{ color: '#333', fontSize: '11px', textAlign: 'center' }}>Waiting for tokens...</div>
                        </div>
                      ))}
                    </>
                  )}
                </>
              ) : (
              /* AI Tokens view - original logic */
              <>
              {/* AI Tokens Header Bar */}
              <div style={{ 
                gridColumn: '1 / -1', 
                backgroundColor: '#0d1a15', 
                border: '1px solid #00d4aa44', 
                borderRadius: '8px', 
                padding: isMobile ? '10px' : '12px',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '8px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>🤖</span>
                  <div>
                    <div style={{ color: '#00d4aa', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}>AI Tokens</div>
                    <div style={{ color: '#888', fontSize: isMobile ? '9px' : '10px' }}>House-generated • Bot trading • Moon or Rug</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Sort dropdown */}
                  {isPremium ? (
                    <select
                      value={tokenSortBy}
                      onChange={(e) => setTokenSortBy(e.target.value)}
                      style={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid #00d4aa44',
                        borderRadius: '16px',
                        padding: isMobile ? '4px 20px 4px 8px' : '6px 24px 6px 10px',
                        color: '#00d4aa',
                        fontSize: isMobile ? '9px' : '10px',
                        cursor: 'pointer',
                        outline: 'none',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2300d4aa\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 6px center',
                        backgroundSize: '8px',
                        fontWeight: 'bold',
                      }}
                    >
                      <option value="newest">🔃 New</option>
                      <option value="mcap">💰 MCap</option>
                      <option value="change">📈 Change</option>
                      <option value="liquidity">💧 Liq</option>
                      <option value="volume">📊 Vol</option>
                    </select>
                  ) : (
                    <span style={{
                      padding: isMobile ? '4px 8px' : '4px 10px',
                      fontSize: isMobile ? '8px' : '9px',
                      color: '#555',
                      border: '1px dashed #00d4aa44',
                      borderRadius: '16px',
                    }}>👑 Sort</span>
                  )}
                  {/* View mode toggles */}
                  <div style={{
                    display: 'flex',
                    gap: '2px',
                    backgroundColor: '#1a1a2e',
                    borderRadius: '20px',
                    padding: '3px',
                    alignItems: 'center',
                  }}>
                    <button
                      onClick={() => changeTileViewMode('maximized')}
                      title="Maximized tiles"
                      style={{
                        padding: isMobile ? '4px 6px' : '4px 8px',
                        borderRadius: '16px',
                        border: 'none',
                        backgroundColor: tileViewMode === 'maximized' ? '#00d4aa' : 'transparent',
                        color: tileViewMode === 'maximized' ? '#000' : '#00d4aa66',
                        fontSize: isMobile ? '10px' : '12px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >⊞</button>
                    <button
                      onClick={() => changeTileViewMode('mini')}
                      title="Mini tiles"
                      style={{
                        padding: isMobile ? '4px 6px' : '4px 8px',
                        borderRadius: '16px',
                        border: 'none',
                        backgroundColor: tileViewMode === 'mini' ? '#00d4aa' : 'transparent',
                        color: tileViewMode === 'mini' ? '#000' : '#00d4aa66',
                        fontSize: isMobile ? '10px' : '12px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >▦</button>
                    <button
                      onClick={() => changeTileViewMode('list')}
                      title="List view"
                      style={{
                        padding: isMobile ? '4px 6px' : '4px 8px',
                        borderRadius: '16px',
                        border: 'none',
                        backgroundColor: tileViewMode === 'list' ? '#00d4aa' : 'transparent',
                        color: tileViewMode === 'list' ? '#000' : '#00d4aa66',
                        fontSize: isMobile ? '10px' : '12px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >☰</button>
                  </div>
                  {/* Home button */}
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    title="Back to top"
                    style={{
                      padding: isMobile ? '6px 10px' : '6px 12px',
                      backgroundColor: 'transparent',
                      border: '1px solid #00d4aa44',
                      borderRadius: '6px',
                      color: '#00d4aa',
                      fontSize: isMobile ? '12px' : '14px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#00d4aa'; e.currentTarget.style.color = '#000'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#00d4aa'; }}
                  >🏠</button>
                </div>
              </div>
              {/* 🕵️ INSIDER: Filter tokens based on visibility - Premium sees all, Free waits 5 sec */}
              {(() => {
                const now = Date.now();
                // Use memoized values for performance
                const { displayTokens, hiddenCount } = displayAiTokens;
                
                // Get position token IDs for highlighting
                const positionTokenIds = positions
                  .filter(p => p.status === 'active')
                  .map(p => p.tokenId);
                
                return (
                  <>
                    {/* 🌅 WARMUP BANNER - Server just woke from sleep */}
                    {warmupSecondsRemaining > 0 && (
                      <div style={{
                        backgroundColor: '#1a1508',
                        border: '2px solid #f0b90b',
                        borderRadius: '10px',
                        padding: '14px 20px',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        gridColumn: '1 / -1', // Span full width of grid
                      }}>
                        <span style={{ fontSize: '24px' }}>🌅</span>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '14px' }}>
                            Server Warming Up
                          </div>
                          <div style={{ color: '#ffd700', fontSize: '20px', fontWeight: 'bold' }}>
                            {Math.floor(warmupSecondsRemaining / 60)}:{String(warmupSecondsRemaining % 60).padStart(2, '0')}
                          </div>
                          <div style={{ color: '#888', fontSize: '11px' }}>
                            Trading resumes when timer ends
                          </div>
                        </div>
                        <span style={{ fontSize: '24px' }}>☕</span>
                      </div>
                    )}

                    {/* Show "Coming Soon" teaser for free users */}
                    {!isPremium && hiddenCount > 0 && (
                      <div 
                        onClick={() => setShowDeposit(true)}
                        style={{ 
                          backgroundColor: '#0d0d12', 
                          border: '2px dashed #f0b90b',
                          borderRadius: '10px', 
                          padding: '14px', 
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '140px',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = '#1a1508';
                          e.currentTarget.style.borderColor = '#ffd700';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = '#0d0d12';
                          e.currentTarget.style.borderColor = '#f0b90b';
                        }}
                      >
                        <div style={{ fontSize: '28px', marginBottom: '8px' }}>👁️</div>
                        <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                          {hiddenCount} Token{hiddenCount > 1 ? 's' : ''} Launching...
                        </div>
                        <div style={{ color: '#888', fontSize: '10px', textAlign: 'center', marginTop: '4px' }}>
                          Premium sees 5 sec early
                        </div>
                        <div style={{ 
                          marginTop: '10px', 
                          padding: '4px 12px', 
                          backgroundColor: '#f0b90b', 
                          color: '#000', 
                          borderRadius: '4px', 
                          fontSize: '10px', 
                          fontWeight: 'bold' 
                        }}>
                          UNLOCK INSIDER
                        </div>
                      </div>
                    )}
                    
                    {displayTokens.map(token => {
                      const isLocked = !isPremium && token.isPremiumOnly;
                      const showVettedBadge = isPremium && token.isAlphaVetted;
                      // 🕵️ INSIDER badge: Show when premium sees a token that free users can't yet
                      const isInsiderAccess = isPremium && now < (token.visibleAt || token.birthTime + INSIDER_DELAY_MS);
                      const insiderSecondsLeft = isInsiderAccess ? Math.ceil(((token.visibleAt || token.birthTime + INSIDER_DELAY_MS) - now) / 1000) : 0;
                      
                      // Token states
                      const isRugged = token.state === 'rugged';
                      const priceMultiple = (token.currentPrice || 1) / (token.initialPrice || 1);
                      // 🌙 MOONING = Currently at/near ATH AND significantly up from launch
                      // Prevents showing "moon" when token is down 50% from peak but still 2x from launch
                      const isNearATH = token.athPrice > 0 ? (token.currentPrice / token.athPrice) >= 0.9 : true;
                      const isMooning = isNearATH && priceMultiple >= 3.0 && token.state === 'active'; // 3x+ AND near ATH
                      const isPumping = priceMultiple >= 2.0 && token.state === 'active'; // Just 2x+ (for border styling)
                      const isEnded = isRugged; // Only rugged tokens are ended - mooning tokens stay alive!
                      const isHolding = positionTokenIds.includes(token.id);
                      
                      // 🔥 HOT detection - high volume per minute RELATIVE to pool size
                      // Scale: Pools are 50-100M sats, need significant % turnover to be "hot"
                      const totalVolume = (token.totalBuyVolume || 0) + (token.totalSellVolume || 0);
                      const ageMinutes = Math.max(1, Math.floor((Date.now() - token.birthTime) / 60000));
                      const volumePerMinute = totalVolume / ageMinutes;
                      const poolSize = token.liquidityPool || 50000000;
                      const volumeToPoolRatio = volumePerMinute / poolSize;
                      // HOT = trading >20% of pool per minute (intense action)
                      const isHot = volumeToPoolRatio > 0.20 && token.state === 'active';
                      // ACTIVE = trading >5% of pool per minute (decent action)  
                      const isActive = volumeToPoolRatio > 0.05 && token.state === 'active';
                      const isNew = ageMinutes < 2 && token.state === 'active';
                      
                      // 🔮 Check if user has a pending Rug Prophet bet on this token
                      const pendingBet = rugProphetBets.find(b => b.tokenId === token.id && b.status === 'pending');
                      const hasPendingBet = !!pendingBet;
                      
                      // 🎯 PvP token detection
                      const isPvPToken = token.isUserLaunched;
                      
                      // Determine border color based on priority
                      // Priority: Rugged > Holding > Bet > Hot > Vetted > Insider > Mooning > PvP > Active > New > Locked > Default
                      let borderStyle, bgColor, boxShadowStyle;
                      if (isRugged) {
                        // 1. RUGGED - Dead token, always top priority
                        borderStyle = '2px solid #ff3b69';
                        bgColor = '#1a0a0a';
                        boxShadowStyle = '0 0 30px rgba(255, 59, 105, 0.6), inset 0 0 30px rgba(255, 59, 105, 0.2)';
                      } else if (isHolding) {
                        // 2. HOLDING - Your money is in this! Gold border
                        borderStyle = '2px solid #f0b90b';
                        bgColor = '#1a1508';
                        boxShadowStyle = '0 0 15px rgba(240, 185, 11, 0.5)';
                      } else if (hasPendingBet) {
                        // 3. PENDING BET - Your prediction is active
                        borderStyle = '2px solid #9b59b6';
                        bgColor = '#150f1a';
                        boxShadowStyle = '0 0 15px rgba(155, 89, 182, 0.5)';
                      } else if (isHot) {
                        // 4. HOT - High volume activity worth seeing
                        borderStyle = '2px solid #ff6b35';
                        bgColor = '#1a0f08';
                        boxShadowStyle = '0 0 20px rgba(255, 107, 53, 0.5)';
                      } else if (showVettedBadge) {
                        // 5. VETTED - Premium curated pick (pink/magenta)
                        borderStyle = '2px solid #e91e63';
                        bgColor = '#1a0a15';
                        boxShadowStyle = '0 0 20px rgba(233, 30, 99, 0.5), inset 0 0 20px rgba(233, 30, 99, 0.1)';
                      } else if (isInsiderAccess) {
                        // 6. INSIDER ACCESS - Premium early access (cyan)
                        borderStyle = '2px solid #00bcd4';
                        bgColor = '#0a1518';
                        boxShadowStyle = '0 0 20px rgba(0, 188, 212, 0.4), inset 0 0 20px rgba(0, 188, 212, 0.1)';
                      } else if (isMooning) {
                        // 7. MOONING - 3x+ AND at ATH! Epic green glow
                        borderStyle = '3px solid #00d26a';
                        bgColor = '#0a1a0a';
                        boxShadowStyle = '0 0 40px rgba(0, 210, 106, 0.8), inset 0 0 40px rgba(0, 210, 106, 0.3)';
                      } else if (isPumping) {
                        // 7b. PUMPING - 2x+ but not at ATH or not 3x yet, teal glow
                        borderStyle = '2px solid #26a69a';
                        bgColor = '#0a1515';
                        boxShadowStyle = '0 0 20px rgba(38, 166, 154, 0.5), inset 0 0 15px rgba(38, 166, 154, 0.1)';
                      } else if (isPvPToken && isActive) {
                        // 8a. PvP + ACTIVE - Yellow/gold with activity glow
                        borderStyle = '2px solid #f0b90b';
                        bgColor = '#1a1508';
                        boxShadowStyle = '0 0 20px rgba(240, 185, 11, 0.5), inset 0 0 15px rgba(240, 185, 11, 0.1)';
                      } else if (isPvPToken) {
                        // 8b. PvP DEFAULT - Yellow/gold border so players know it's player-launched
                        borderStyle = '2px solid #f0b90b';
                        bgColor = '#12100a';
                        boxShadowStyle = '0 0 12px rgba(240, 185, 11, 0.3)';
                      } else if (isActive) {
                        // 9. ACTIVE - Moderate activity (AI tokens)
                        borderStyle = '1px solid #3498db';
                        bgColor = '#0a0f1a';
                        boxShadowStyle = '0 0 10px rgba(52, 152, 219, 0.3)';
                      } else if (isNew) {
                        // 10. NEW - Just spawned
                        borderStyle = '1px solid #00a8ff';
                        bgColor = '#0a1015';
                        boxShadowStyle = '0 0 10px rgba(0, 168, 255, 0.3)';
                      } else if (isLocked) {
                        // 11. LOCKED - Premium only
                        borderStyle = '1px solid #2a2a2e';
                        bgColor = '#0d0d12';
                        boxShadowStyle = 'none';
                      } else {
                        // 12. DEFAULT - Subtle green/red based on price direction
                        const priceChangeValue = token.priceChange ?? 0;
                        borderStyle = `1px solid ${priceChangeValue >= 0 ? '#1a3a1a' : '#3a1a1a'}`;
                        bgColor = '#0d0d12';
                        boxShadowStyle = 'none';
                      }
                      
                      // 🤖 BOT FOCUS - Highlight token the bot is looking at
                      const isBotFocused = botControlEnabled && botFocusedTokenId === token.id;
                      if (isBotFocused) {
                        borderStyle = '3px solid #a855f7';
                        boxShadowStyle = '0 0 30px rgba(168, 85, 247, 0.6), inset 0 0 20px rgba(168, 85, 247, 0.1)';
                      }

                      // Status badge helper
                      const getStatusBadge = () => {
                        if (isRugged) return { icon: '💀', text: 'RUGGED', color: '#ff3b69' };
                        if (isMooning) return { icon: '🌙', text: 'MOON', color: '#00d26a' };
                        if (isPumping) return { icon: '🚀', text: 'PUMP', color: '#26a69a' }; // Pumping but not mooning
                        if (isHolding) return { icon: '💰', text: 'HOLD', color: '#f0b90b' };
                        if (hasPendingBet) return { icon: '🔮', text: 'BET', color: '#9b59b6' };
                        if (isHot) return { icon: '🔥', text: 'HOT', color: '#ff6b35' };
                        if (showVettedBadge) return { icon: '🔮', text: 'VET', color: '#e91e63' };
                        if (isInsiderAccess) return { icon: '👁️', text: `${insiderSecondsLeft}s`, color: '#00bcd4' };
                        if (isNew) return { icon: '🆕', text: 'NEW', color: '#00a8ff' };
                        if (isActive) return { icon: '📈', text: 'ACT', color: '#00d26a' };
                        return null;
                      };
                      const statusBadge = getStatusBadge();

                      // === LIST VIEW FOR AI TOKENS ===
                      if (tileViewMode === 'list') {
                        return (
                          <div
                            key={token.id}
                            id={`token-tile-${token.id}`}
                            onClick={() => !isLocked && !isEnded && joinToken(token)}
                            style={{
                              backgroundColor: isBotFocused ? '#1a0a2e' : bgColor,
                              border: borderStyle,
                              borderRadius: '6px',
                              padding: '8px 12px',
                              cursor: isEnded ? 'default' : isLocked ? 'not-allowed' : 'pointer',
                              opacity: isRugged ? 0.6 : isLocked ? 0.5 : 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '8px',
                              transition: 'all 0.15s',
                              boxShadow: boxShadowStyle,
                            }}
                            onMouseEnter={e => { if (!isLocked && !isEnded) e.currentTarget.style.backgroundColor = '#151520'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = isBotFocused ? '#1a0a2e' : bgColor; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: '0 0 auto' }}>
                              {statusBadge && (
                                <span style={{ fontSize: '10px', color: statusBadge.color, fontWeight: 'bold' }}>
                                  {statusBadge.icon}
                                </span>
                              )}
                              <span style={{ color: isPvPToken ? '#f0b90b' : '#fff', fontWeight: 'bold', fontSize: '13px' }}>{token.ticker}</span>
                              <span style={{ color: '#555', fontSize: '9px' }}>{token.category?.slice(0, 10) || 'AI'}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <span style={{ color: '#888' }}>MCAP <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>{formatPB(token.marketCap || 50000)}</span></span>
                              <span style={{ color: '#888' }}>VOL <span style={{ color: '#9b59b6', fontWeight: 'bold' }}>{formatPB(totalVolume)}</span></span>
                              <span style={{ color: '#888' }}>LIQ <span style={{ color: '#00d26a', fontWeight: 'bold' }}>{formatPB(token.liquidityPool || 10000)}</span></span>
                              <div style={{
                                fontWeight: 'bold',
                                fontSize: '12px',
                                color: token.priceChange >= 0 ? '#00d26a' : '#ff3b69',
                                minWidth: '55px',
                                textAlign: 'right',
                              }}>
                                {formatPercent(token.priceChange)}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // === MINI VIEW FOR AI TOKENS ===
                      if (tileViewMode === 'mini') {
                        return (
                          <div
                            key={token.id}
                            id={`token-tile-${token.id}`}
                            onClick={() => !isLocked && !isEnded && joinToken(token)}
                            style={{
                              backgroundColor: isBotFocused ? '#1a0a2e' : bgColor,
                              border: borderStyle,
                              borderRadius: '8px',
                              padding: '8px',
                              cursor: isEnded ? 'default' : isLocked ? 'not-allowed' : 'pointer',
                              opacity: isRugged ? 0.6 : isLocked ? 0.5 : 1,
                              transition: 'all 0.15s',
                              textAlign: 'center',
                              position: 'relative',
                              boxShadow: isBotFocused ? boxShadowStyle : 'none',
                            }}
                            onMouseEnter={e => { if (!isLocked && !isEnded) e.currentTarget.style.transform = 'scale(1.03)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                          >
                            {/* Mini status badge */}
                            {statusBadge && (
                              <div style={{
                                position: 'absolute',
                                top: '-6px',
                                right: '-6px',
                                backgroundColor: statusBadge.color,
                                color: '#fff',
                                padding: '2px 5px',
                                borderRadius: '6px',
                                fontSize: '8px',
                                fontWeight: 'bold',
                              }}>
                                {statusBadge.icon}
                              </div>
                            )}
                            <div style={{ color: isPvPToken ? '#f0b90b' : '#fff', fontWeight: 'bold', fontSize: '11px', marginBottom: '3px' }}>{token.ticker}</div>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: token.priceChange >= 0 ? '#00d26a' : '#ff3b69',
                              marginBottom: '2px',
                            }}>
                              {formatPercent(token.priceChange)}
                            </div>
                            <div style={{ fontSize: '9px', color: '#888' }}>{formatPB(token.marketCap || 50000)}</div>
                          </div>
                        );
                      }

                      // === MAXIMIZED VIEW (original) ===
                      return (
                        <div
                          key={token.id}
                          id={`token-tile-${token.id}`}
                          onClick={() => !isLocked && !isEnded && joinToken(token)}
                          style={{
                          backgroundColor: isBotFocused ? '#1a0a2e' : bgColor,
                          border: borderStyle,
                          borderRadius: '12px',
                          padding: isMobile ? '10px' : '14px',
                          cursor: isEnded ? 'default' : isLocked ? 'not-allowed' : 'pointer',
                          opacity: isRugged ? 0.7 : isLocked ? 0.6 : 1,
                          transition: 'all 0.3s ease',
                          boxShadow: boxShadowStyle,
                          position: 'relative',
                          animation: isBotFocused ? 'botFocusPulse 1.5s ease-in-out infinite' : isRugged ? 'fadeOutCard 5s ease-out forwards' : isMooning ? 'pulseGreen 1s ease-in-out infinite' : 'none',
                        }}
                        onMouseEnter={e => { if (!isLocked && !isEnded) e.currentTarget.style.transform = 'scale(1.02)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                          {/* 🤖 BOT FOCUS indicator */}
                          {isBotFocused && (
                            <div style={{
                              position: 'absolute',
                              top: '-14px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: '#a855f7',
                              color: '#fff',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              zIndex: 25,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              boxShadow: '0 4px 15px rgba(168, 85, 247, 0.5)',
                            }}>
                              🤖 BOT WATCHING
                            </div>
                          )}
                          {/* 💰 TRADE - Small indicator on tile */}
                          {tradeNotification?.tokenId === token.id && (
                            <div style={{
                              position: 'absolute',
                              bottom: '8px',
                              right: '8px',
                              zIndex: 30,
                              animation: 'botTradeButtonPop 0.5s ease-out',
                            }}>
                              <div style={{
                                backgroundColor: tradeNotification.type === 'buy' ? '#00d26a' : '#ff3b69',
                                color: '#fff',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                boxShadow: tradeNotification.type === 'buy'
                                  ? '0 0 15px rgba(0, 210, 106, 0.6)'
                                  : '0 0 15px rgba(255, 59, 105, 0.6)',
                              }}>
                                {tradeNotification.isBot ? '🤖' : '💰'} {tradeNotification.type === 'buy' ? 'BUY' : 'SELL'}
                              </div>
                            </div>
                          )}
                          {/* 💀 RUGGED overlay */}
                          {isRugged && (
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%) rotate(-15deg)',
                              fontSize: '32px',
                              fontWeight: 'bold',
                              color: '#ff3b69',
                              textShadow: '0 0 20px rgba(255,59,105,0.8)',
                              zIndex: 20,
                              pointerEvents: 'none',
                            }}>
                              💀 RUGGED
                            </div>
                          )}
                          {/* 🌙 MOONING badge - still tradeable! */}
                          {isMooning && (
                            <div style={{
                              position: 'absolute',
                              top: '-12px',
                              right: '-12px',
                              backgroundColor: '#00d26a',
                              color: '#000',
                              padding: '6px 12px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              zIndex: 20,
                              animation: 'celebrateMoon 0.5s ease-in-out infinite alternate',
                              boxShadow: '0 0 20px rgba(0,210,106,0.8)',
                            }}>
                              🌙 MOONING! APE IN!
                            </div>
                          )}
                          {/* 👁️ INSIDER Badge - Premium Early Access */}
                          {isInsiderAccess && (
                            <div style={{
                              position: 'absolute',
                              top: '-10px',
                              left: '-10px',
                              backgroundColor: '#f0b90b',
                              color: '#000',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              boxShadow: '0 0 12px rgba(240, 185, 11, 0.8)',
                              animation: 'pulse 1s infinite',
                              zIndex: 10,
                            }}>
                              👁️ INSIDER {insiderSecondsLeft}s
                            </div>
                          )}
                          {/* 🔮 AlphaBot Vetted Badge - Premium Only */}
                          {showVettedBadge && (
                            <div style={{
                              position: 'absolute',
                              top: '-10px',
                              right: '-10px',
                              backgroundColor: '#9b59b6',
                              color: '#fff',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              boxShadow: '0 0 12px rgba(155, 89, 182, 0.8)',
                              animation: 'pulse 2s infinite',
                              zIndex: 10,
                            }}>
                              🔮 VETTED
                            </div>
                          )}
                          {/* 💰 HOLDING Badge - User has active position */}
                          {positionTokenIds.includes(token.id) && !isMooning && (
                            <div style={{
                              position: 'absolute',
                              top: '-10px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: '#00d26a',
                              color: '#000',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              boxShadow: '0 0 12px rgba(0, 210, 106, 0.6)',
                              zIndex: 15,
                            }}>
                              💰 HOLDING
                            </div>
                          )}
                          {/* 🔮 RUG PROPHET Badge - User has pending bet */}
                          {hasPendingBet && !isMooning && !positionTokenIds.includes(token.id) && (
                            <div style={{
                              position: 'absolute',
                              top: '-10px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: '#9b59b6',
                              color: '#fff',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              boxShadow: '0 0 12px rgba(155, 89, 182, 0.6)',
                              zIndex: 15,
                            }}>
                              🔮 {pendingBet.prediction === 'rug' ? 'RUG' : 'MOON'} BET
                            </div>
                          )}
                          {/* 🎯 PVP Badge - User-launched token */}
                          {isPvPToken && !isMooning && !positionTokenIds.includes(token.id) && !hasPendingBet && (
                            <div style={{
                              position: 'absolute',
                              top: '-10px',
                              right: '-10px',
                              backgroundColor: '#f0b90b',
                              color: '#000',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              boxShadow: '0 0 12px rgba(240, 185, 11, 0.6)',
                              zIndex: 10,
                            }}>
                              🚀 PVP
                            </div>
                          )}
                          
                          {/* Row 1: Ticker + % Change */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? '6px' : '10px', overflow: 'hidden' }}>
                            <div style={{ flex: '1', minWidth: 0, overflow: 'hidden' }}>
                              <div style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 'bold', color: isPvPToken ? '#f0b90b' : isInsiderAccess ? '#f0b90b' : showVettedBadge ? '#9b59b6' : '#fff', letterSpacing: '0.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{token.ticker}</div>
                              <div style={{ fontSize: isMobile ? '9px' : '10px', color: isPvPToken ? '#f0b90b99' : '#777', marginTop: '2px' }}>{isPvPToken ? '🚀 BSV.pump' : token.category}</div>
                              {/* Token Description - on cards for PvP tokens */}
                              {isPvPToken && token.description && (
                                <div style={{
                                  fontSize: isMobile ? '8px' : '9px',
                                  color: '#888',
                                  marginTop: '3px',
                                  fontStyle: 'italic',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: isMobile ? '120px' : '160px',
                                }}>
                                  "{token.description}"
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '4px' }}>
                              <div style={{ 
                                fontSize: isMobile ? (Math.abs(token.priceChange) >= 100 ? '12px' : '14px') : (Math.abs(token.priceChange) >= 1000 ? '13px' : Math.abs(token.priceChange) >= 100 ? '15px' : '17px'), 
                                fontWeight: 'bold', 
                                color: token.priceChange >= 0 ? '#00d26a' : '#ff3b69' 
                              }}>
                                {formatPercent(token.priceChange)}
                              </div>
                              <div style={{ fontSize: isMobile ? '9px' : '10px', color: getAgeColor(token.birthTime, token.lifespanType), marginTop: '2px' }}>
                                🕐 {formatAge(token.birthTime)}
                              </div>
                              {/* ⏱️ Last trade timer */}
                              {token.lastTradeTime && (() => {
                                const secAgo = Math.floor((Date.now() - token.lastTradeTime) / 1000);
                                const isRecent = secAgo < 30;
                                const timeStr = secAgo < 60 ? `${secAgo}s` : secAgo < 3600 ? `${Math.floor(secAgo/60)}m` : `${Math.floor(secAgo/3600)}h`;
                                return (
                                  <div style={{ fontSize: '9px', color: isRecent ? '#00d26a' : '#555', marginTop: '1px' }}>
                                    ⚡ {timeStr} ago
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 2: PROMINENT Market Cap, Volume & Liquidity */}
                          <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr 1fr 1fr' : '1fr 1fr 1fr',
                            justifyContent: 'space-between', 
                            marginBottom: isMobile ? '6px' : '10px', 
                            padding: isMobile ? '6px' : '8px 10px', 
                            backgroundColor: '#0a0a0f', 
                            borderRadius: '6px',
                            border: '1px solid #1a1a2e',
                            gap: isMobile ? '4px' : '8px',
                          }}>
                            <div>
                              <div style={{ fontSize: isMobile ? '8px' : '10px', color: '#666', marginBottom: '1px' }}>MCAP</div>
                              <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: 'bold', color: '#f0b90b' }}>{formatPB(token.marketCap || 50000)}</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: isMobile ? '8px' : '10px', color: '#666', marginBottom: '1px' }}>VOL</div>
                              <div style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 'bold', color: '#9b59b6' }}>
                                {formatPB((token.totalBuyVolume || 0) + (token.totalSellVolume || 0))}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: isMobile ? '8px' : '10px', color: '#666', marginBottom: '1px' }}>LIQ</div>
                              <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: 'bold', color: '#00d26a' }}>{formatPB(token.liquidityPool || token.liquidity || 10000)}</div>
                            </div>
                          </div>
                          
                          {/* Row 3: Watchers, Players, Multiplier */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: isMobile ? '10px' : '12px', color: '#888', marginBottom: isMobile ? '6px' : '10px' }}>
                            <span title="Watching">👁️ {(token.spectatorCount || 0) + (token.botCount || 0)}</span>
                            <span title="Holding" style={{ color: token.playerCount > 0 ? '#00d26a' : '#666' }}>💰 {token.playerCount || 0}</span>
                            <span style={{ color: '#aaa', fontWeight: 'bold' }}>{formatMultiplier(token.price)}</span>
                          </div>
                          
                          {/* 🧠 Health Bar - Premium Feature */}
                          {isPremium && token.health && token.state === 'active' && (
                            <div style={{ marginBottom: isMobile ? '6px' : '10px' }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginBottom: '4px',
                              }}>
                                <span style={{ fontSize: '9px', color: '#666' }} title="Token vitals: volume, liquidity, activity. Low health = high rug risk!">🧠 HEALTH</span>
                                <span style={{ 
                                  fontSize: '10px', 
                                  fontWeight: 'bold',
                                  color: token.health.score > 70 ? '#00d26a' : 
                                         token.health.score > 40 ? '#f0b90b' : 
                                         token.health.score > 20 ? '#ff6b00' : '#ff3b69'
                                }}>
                                  {token.health.score}/100
                                </span>
                              </div>
                              {/* Health bar */}
                              <div style={{ 
                                height: '4px', 
                                backgroundColor: '#1a1a2e', 
                                borderRadius: '2px',
                                overflow: 'hidden',
                              }}>
                                <div style={{ 
                                  width: `${token.health.score}%`, 
                                  height: '100%',
                                  backgroundColor: token.health.score > 70 ? '#00d26a' : 
                                                   token.health.score > 40 ? '#f0b90b' : 
                                                   token.health.score > 20 ? '#ff6b00' : '#ff3b69',
                                  borderRadius: '2px',
                                  transition: 'width 0.3s ease, background-color 0.3s ease',
                                }} />
                              </div>
                              {/* Death signals - hide on mobile to save space */}
                              {!isMobile && token.health.signals && token.health.signals.length > 0 && token.health.score < 50 && (
                                <div style={{ 
                                  fontSize: '8px', 
                                  color: token.health.score < 30 ? '#ff3b69' : '#ff6b00',
                                  marginTop: '3px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  ⚠️ {token.health.signals.slice(0, 2).join(', ').replace(/_/g, ' ')}
                                </div>
                              )}
                            </div>
                          )}
                          {/* Free users see locked health indicator */}
                          {!isPremium && token.health && token.state === 'active' && (
                            <div style={{ 
                              marginBottom: isMobile ? '6px' : '10px',
                              padding: '6px',
                              backgroundColor: 'rgba(240,185,11,0.05)',
                              borderRadius: '4px',
                              border: '1px dashed #333',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}>
                              <span style={{ fontSize: '9px', color: '#666' }} title="Token vitals: volume, liquidity, activity. Low health = high rug risk!">🧠 HEALTH</span>
                              <span style={{ fontSize: '9px', color: '#f0b90b' }}>👑 PRO</span>
                            </div>
                          )}
                          
                          {/* Row 4: Activity Badge - based on volume, NOT lifecycle */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {(() => {
                              // Calculate activity level from volume (doesn't reveal lifecycle!)
                              const totalVolume = (token.totalBuyVolume || 0) + (token.totalSellVolume || 0);
                              const ageMinutes = Math.floor((Date.now() - token.birthTime) / 60000);
                              const volumePerMinute = ageMinutes > 0 ? totalVolume / ageMinutes : totalVolume;
                              
                              let badge, bgColor, textColor;
                              if (showVettedBadge) {
                                badge = '🔮 VETTED';
                                bgColor = '#1a0a20';
                                textColor = '#9b59b6';
                              } else if (volumePerMinute > 50000) {
                                badge = '🔥 HOT';
                                bgColor = '#200a0a';
                                textColor = '#ff6b6b';
                              } else if (volumePerMinute > 20000) {
                                badge = isMobile ? '📈' : '📈 ACTIVE';
                                bgColor = '#0a2010';
                                textColor = '#00d26a';
                              } else if (ageMinutes < 2) {
                                badge = isMobile ? '🆕' : '🆕 NEW';
                                bgColor = '#0a1520';
                                textColor = '#00a8ff';
                              } else {
                                badge = isMobile ? '📊' : '📊 TRADING';
                                bgColor = '#15150a';
                                textColor = '#888';
                              }
                              
                              return (
                                <div style={{ 
                                  padding: isMobile ? '3px 6px' : '4px 10px', 
                                  borderRadius: '6px', 
                                  fontSize: isMobile ? '10px' : '11px', 
                                  fontWeight: 'bold',
                                  backgroundColor: bgColor,
                                  color: textColor,
                                }}>
                                  {badge}
                                </div>
                              );
                            })()}
                            {isLocked && <span style={{ fontSize: '10px', color: '#f0b90b' }}>🔒 Premium</span>}
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
              </>
              )}
            </div>
          </div>
          
          {/* 📡 LIVE ACTIVITY FEED */}
          {liveActivity.length > 0 && (
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '8px', padding: '10px', marginBottom: '12px' }}>
              <div style={{ color: '#555', fontSize: '10px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#00d26a', animation: 'pulse 1.5s infinite' }}></span>
                📡 LIVE ACTIVITY
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '80px', overflowY: 'auto' }}>
                {liveActivity.slice(0, 8).map((activity, idx) => (
                  <div key={activity.id || idx} style={{ 
                    fontSize: '10px', 
                    padding: '4px 6px',
                    borderRadius: '4px',
                    backgroundColor: activity.type === 'rugged' ? 'rgba(255,59,105,0.1)' : 
                                    activity.type === 'win' ? 'rgba(0,210,106,0.1)' : 
                                    activity.type === 'buy' ? 'rgba(0,210,106,0.05)' :
                                    activity.type === 'sell' ? 'rgba(240,185,11,0.05)' : 'transparent',
                    border: activity.type === 'rugged' ? '1px solid rgba(255,59,105,0.2)' : 
                           activity.type === 'win' ? '1px solid rgba(0,210,106,0.2)' : 
                           'none',
                  }}>
                    {activity.type === 'rugged' && (
                      <span style={{ color: '#ff3b69' }}>💀 ${activity.handle} got rugged on {activity.data?.token} (-{formatPB(activity.data?.loss || 0)})</span>
                    )}
                    {activity.type === 'win' && (
                      <span style={{ color: '#00d26a' }}>🎉 ${activity.handle} won +{formatPB(activity.data?.profit || 0)} on {activity.data?.token}</span>
                    )}
                    {activity.type === 'buy' && (
                      <span style={{ color: '#888' }}>🦍 ${activity.handle} aped into {activity.data?.token}</span>
                    )}
                    {activity.type === 'sell' && (
                      <span style={{ color: '#f0b90b' }}>💰 ${activity.handle} sold {activity.data?.token}</span>
                    )}
                    {activity.type === 'join' && (
                      <span style={{ color: '#666' }}>👋 ${activity.handle} joined the lobby</span>
                    )}
                    {!['rugged', 'win', 'buy', 'sell', 'join'].includes(activity.type) && (
                      <span style={{ color: '#666' }}>{activity.message || `${activity.handle}: ${activity.type}`}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Activity Feed - Bot trades, rugs, wins */}
          <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '8px', padding: '8px', marginBottom: '6px' }}>
            <div style={{ color: '#555', fontSize: '9px', marginBottom: '4px' }}>📊 ACTIVITY</div>
            <div style={{ height: '50px', overflowY: 'auto' }}>
              {lobbyChat.filter(m => m.isTradeAnnouncement || m.isBot || (m.handle && !m.handle.startsWith('$'))).slice(-20).reverse().map((m, i) => (
                <div 
                  key={m.id || i} 
                  style={{ 
                    fontSize: '9px', 
                    marginBottom: '2px',
                    color: m.isRug ? '#ff3b69' : m.isWin ? '#00d26a' : '#555'
                  }}
                >
                  {m.text}
                </div>
              ))}
              {lobbyChat.filter(m => m.isTradeAnnouncement || m.isBot || (m.handle && !m.handle.startsWith('$'))).length === 0 && (
                <div style={{ color: '#333', fontSize: '9px', fontStyle: 'italic', textAlign: 'center', padding: '15px 0' }}>
                  No activity yet...
                </div>
              )}
            </div>
          </div>
          
          {/* Player Chat */}
          <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '8px', padding: '8px' }}>
            <div style={{ color: '#555', fontSize: '9px', marginBottom: '4px' }}>💬 LOBBY CHAT {!isPremium && <span style={{ color: '#f0b90b' }}>• 🔒 Premium</span>}</div>
            
            <div ref={lobbyChatRef} style={{ height: isMobile ? '80px' : '50px', overflowY: 'auto', marginBottom: '6px' }}>
              {lobbyChat.filter(m => !m.isTradeAnnouncement && !m.isBot && m.handle && m.handle.startsWith('$')).length === 0 ? (
                <div style={{ color: '#333', fontSize: '9px', fontStyle: 'italic', textAlign: 'center', padding: '15px 0' }}>
                  No player messages yet...
                </div>
              ) : (
                lobbyChat.filter(m => !m.isTradeAnnouncement && !m.isBot && m.handle && m.handle.startsWith('$')).map(m => (
                  <div 
                    key={m.id} 
                    style={{ fontSize: '10px', marginBottom: '2px' }}
                  >
                    <span style={{ color: m.isUser ? '#f0b90b' : m.isPremiumUser ? '#f0b90b' : '#666' }}>
                      {m.isPremiumUser && '👑 '}{m.handle}:
                    </span>{' '}
                    <span style={{ color: m.isUser ? '#fff' : '#999' }}>{renderClickableTickers(m.text, m.isUser ? '#fff' : '#999')}</span>
                  </div>
                ))
              )}
            </div>
            
            {isPremium ? (
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  value={lobbyInput}
                  onChange={e => setLobbyInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && sendLobbyChat()}
                  placeholder="Chat..."
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #1a1a2e', backgroundColor: '#111', color: '#fff', fontSize: isMobile ? '16px' : '11px', outline: 'none' }}
                />
                <button onClick={sendLobbyChat} style={{ padding: '8px 12px', borderRadius: '6px', border: 'none', backgroundColor: '#f0b90b', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px', minHeight: '44px' }}>Send</button>
              </div>
            ) : (
              <div 
                onClick={() => setShowDeposit(true)}
                style={{ 
                  padding: '8px', 
                  borderRadius: '6px', 
                  border: '1px solid #f0b90b', 
                  backgroundColor: 'rgba(240,185,11,0.05)', 
                  color: '#f0b90b', 
                  fontSize: '10px',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
              >
                🔒 Premium to chat
              </div>
            )}
          </div>
        </div>
        
        {/* 🐸 UNIVERSAL PEPE TOAST */}
        {pepeToast && (
          <div style={{ 
            position: 'fixed', 
            inset: 0, 
            pointerEvents: 'none', 
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: pepeToast.direction === 'left' ? 'flex-start' : 'flex-end',
          }}>
            {/* Pepe sliding in */}
            <div style={{
              fontSize: '80px',
              animation: pepeToast.direction === 'left' 
                ? 'pepeSlideFromLeft 0.5s ease-out forwards, pepeFloatGentle 2s ease-in-out infinite 0.5s'
                : 'pepeSlideFromRight 0.5s ease-out forwards, pepeFloatGentle 2s ease-in-out infinite 0.5s',
            }}>
              🐸
            </div>
            
            {/* Speech bubble */}
            <div style={{
              backgroundColor: '#1a1a2e',
              border: '2px solid #00d26a',
              borderRadius: '16px',
              padding: '12px 20px',
              marginLeft: pepeToast.direction === 'left' ? '0' : '-20px',
              marginRight: pepeToast.direction === 'left' ? '-20px' : '0',
              maxWidth: '200px',
              animation: 'bubblePop 0.3s ease-out 0.4s both',
              position: 'relative',
            }}>
              <div style={{ 
                color: '#00d26a', 
                fontSize: '14px', 
                fontWeight: 'bold',
                textAlign: 'center',
              }}>
                {pepeToast.message}
              </div>
              {/* Speech bubble arrow */}
              <div style={{
                position: 'absolute',
                top: '50%',
                [pepeToast.direction === 'left' ? 'left' : 'right']: '-10px',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '10px solid transparent',
                borderBottom: '10px solid transparent',
                [pepeToast.direction === 'left' ? 'borderRight' : 'borderLeft']: '10px solid #00d26a',
              }} />
            </div>
            
            <style>{`
              @keyframes pepeSlideFromRight {
                from { transform: translateX(100px); opacity: 0; }
                to { transform: translateX(-20px); opacity: 1; }
              }
              @keyframes pepeSlideFromLeft {
                from { transform: translateX(-100px); opacity: 0; }
                to { transform: translateX(20px); opacity: 1; }
              }
              @keyframes pepeFloatGentle {
                0%, 100% { transform: rotate(-3deg); }
                50% { transform: translateY(-10px) rotate(3deg); }
              }
              @keyframes bubblePop {
                from { transform: scale(0.5); opacity: 0; }
                to { transform: scale(1); opacity: 1; }
              }
            `}</style>
          </div>
        )}
        
        {/* Real Money Warning Modal */}
        <RealMoneyWarningModal />
        
        {/* First-Time Rake Consent Modal (one-time, permanent) */}
        <RakeConsentModal />
        
        {/* AMM Casino Tutorial Modal (after consent) */}
        <TutorialModal />
        
        {/* Rake Disclosure Modal (daily per token type) */}
        <RakeDisclosureModal />
        
        {/* 🔐 INACTIVITY WARNING MODAL */}
        {showInactivityWarning && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5001, padding: '12px' }}>
            <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #ff8c00', borderRadius: '16px', padding: '24px', maxWidth: '360px', width: '90%', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>😴</div>
              <h2 style={{ color: '#ff8c00', fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Going Inactive?
              </h2>
              <p style={{ color: '#888', fontSize: '14px', marginBottom: '8px' }}>
                You'll be logged out in
              </p>
              <div style={{ color: '#ff8c00', fontSize: '32px', fontWeight: 'bold', marginBottom: '16px' }}>
                {sessionTimeRemaining ? `${Math.ceil(sessionTimeRemaining / 60000)} min` : '5 min'}
              </div>
              <p style={{ color: '#666', fontSize: '12px', marginBottom: '24px' }}>
                due to inactivity. Click below to stay online!
              </p>
              <button
                onClick={() => {
                  setShowInactivityWarning(false);
                  // The next /api/live poll will refresh the session
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#00d26a',
                  color: '#000',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                ✓ I'm Still Here!
              </button>
            </div>
          </div>
        )}
        
        {/* 💰 WALLET MODAL - Tap balance to open */}
        {showWallet && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px' }} onClick={() => setShowWallet(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#0a0a0f', border: '1px solid #00d26a', borderRadius: '12px', padding: isMobile ? '16px' : '24px', maxWidth: '360px', width: '100%' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>💰 Wallet</span>
                  {isPremium && (
                    <span style={{ backgroundColor: '#f0b90b', color: '#000', padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold' }}>
                      👑 PRO
                    </span>
                  )}
                </div>
                <button onClick={() => setShowWallet(false)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>

              {/* Premium Status - show timer for mobile users */}
              {isPremium && premiumTimeLeft && (
                <div style={{ backgroundColor: 'rgba(240,185,11,0.1)', border: '1px solid #f0b90b33', borderRadius: '8px', padding: '10px', marginBottom: '16px', textAlign: 'center' }}>
                  <div style={{ color: '#f0b90b', fontSize: '11px', fontWeight: 'bold' }}>👑 Premium Active</div>
                  <div style={{ color: '#888', fontSize: '10px' }}>⏱️ {premiumTimeLeft} remaining</div>
                </div>
              )}

              {/* Balance Display */}
              <div style={{ textAlign: 'center', padding: '20px 0', marginBottom: '16px', backgroundColor: '#111', borderRadius: '10px' }}>
                <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>PAPER BUCKS BALANCE</div>
                <div style={{ color: '#00d26a', fontSize: '32px', fontWeight: 'bold' }}>${formatSats(gameWallet)}</div>
                <div style={{ color: '#666', fontSize: '12px' }}>≈ ${(gameWallet * satToUsd).toFixed(2)} USD</div>
              </div>
              
              {/* HandCash Wallet Balance */}
              <div style={{ textAlign: 'center', padding: '12px', marginBottom: '16px', backgroundColor: '#0a1518', borderRadius: '10px', border: '1px solid #00bcd433' }}>
                <div style={{ color: '#00bcd4', fontSize: '10px', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  💳 HANDCASH WALLET
                  <button 
                    onClick={() => refreshBalances()}
                    style={{ 
                      backgroundColor: 'transparent', 
                      border: 'none', 
                      color: '#00bcd4', 
                      cursor: 'pointer',
                      fontSize: '12px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                    }}
                    title="Refresh balance"
                  >
                    🔄
                  </button>
                </div>
                <div style={{ color: '#00bcd4', fontSize: '28px', fontWeight: 'bold' }}>
                  ${mainWalletFiat > 0 ? mainWalletFiat.toFixed(2) : (mainWallet * satToUsd).toFixed(2)}
                </div>
                <div style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>
                  {formatSats(mainWallet)} sats total
                </div>
                {mainWalletSpendable > 0 && mainWalletSpendable < mainWallet && (
                  <div style={{ color: '#ff8c00', fontSize: '10px', marginTop: '6px', padding: '4px 8px', backgroundColor: '#1a1508', borderRadius: '4px', display: 'inline-block' }}>
                    ⚠️ Daily limit: ${(mainWalletSpendable * satToUsd).toFixed(2)} available to deposit
                  </div>
                )}
              </div>
              
              {/* 💰 OWNER REVENUE - Only for h2o4daze */}
              {handcashUser?.handle?.toLowerCase().replace('$', '') === 'h2o4daze' && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '12px', 
                  marginBottom: '16px', 
                  backgroundColor: '#0a150a', 
                  borderRadius: '10px', 
                  border: '1px solid #00d26a55',
                  background: 'linear-gradient(135deg, #0a150a 0%, #0d1a0d 100%)'
                }}>
                  <div style={{ color: '#00d26a', fontSize: '10px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    👑 OWNER REVENUE
                  </div>
                  <div style={{ color: '#00d26a', fontSize: '11px', marginBottom: '4px' }}>
                    Your HandCash balance above = your profit!
                  </div>
                  <div style={{ color: '#666', fontSize: '10px', marginTop: '8px', padding: '8px', backgroundColor: '#111', borderRadius: '6px' }}>
                    <div style={{ marginBottom: '4px' }}>💡 Revenue sources:</div>
                    <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: '9px', color: '#888' }}>
                      <span>1¢ trades</span>
                      <span>•</span>
                      <span>Premiums</span>
                      <span>•</span>
                      <span>Launch fees</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Sign Out Button - right with the balances */}
              <button
                onClick={() => {
                  if (confirm('Sign out of HandCash?')) {
                    setShowWallet(false);
                    disconnectHandCash();
                  }
                }}
                style={{
                  marginBottom: '16px',
                  padding: '10px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                🚪 Sign Out of HandCash
              </button>
              
              {/* HandCash Warning */}
              {(handcashDown && !isTestPilot) && (
                <div style={{ backgroundColor: '#1a1008', border: '1px solid #ff8c0033', borderRadius: '8px', padding: '10px', marginBottom: '16px', textAlign: 'center' }}>
                  <div style={{ color: '#ff8c00', fontSize: '11px' }}>⚠️ HandCash temporarily unavailable</div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button
                  onClick={() => {
                    if (handcashDown && !isTestPilot) {
                      alert('⚠️ HandCash is temporarily unavailable.\n\nDeposits will be available when HandCash services are restored.');
                    } else {
                      setShowWallet(false);
                      setShowDeposit(true);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    backgroundColor: (handcashDown && !isTestPilot) ? '#333' : '#00d26a',
                    color: (handcashDown && !isTestPilot) ? '#666' : '#000',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: (handcashDown && !isTestPilot) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  ⬆️ Deposit
                </button>
                <button
                  onClick={() => {
                    if (handcashDown && !isTestPilot) {
                      alert('⚠️ HandCash is temporarily unavailable.\n\nWithdrawals will be available when HandCash services are restored.');
                    } else if (gameWallet <= 0) {
                      alert('No balance to withdraw');
                    } else {
                      refreshBalances(); // Refresh to get latest withdrawable amount
                      setShowWallet(false);
                      setShowWithdraw(true);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    backgroundColor: 'transparent',
                    color: (handcashDown && !isTestPilot) || gameWallet <= 0 ? '#555' : '#fff',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: (handcashDown && !isTestPilot) || gameWallet <= 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  ⬇️ Withdraw
                </button>
              </div>

              {/* Quick Stats */}
              <div style={{ display: 'flex', justifyContent: 'space-around', padding: '12px', backgroundColor: '#111', borderRadius: '8px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#666', fontSize: '9px' }}>DEPOSITED</div>
                  <div style={{ color: '#00d26a', fontSize: '13px', fontWeight: 'bold' }}>{formatPB(totalDeposited || 0)}</div>
                </div>
                <div style={{ width: '1px', backgroundColor: '#333' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#666', fontSize: '9px' }}>WITHDRAWN</div>
                  <div style={{ color: '#ff8c00', fontSize: '13px', fontWeight: 'bold' }}>{formatPB(totalWithdrawn || 0)}</div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* 🤖 TRADING BOT MODAL */}
        {showTradingBot && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'center', zIndex: 1001, padding: '12px', overflowY: 'auto' }} onClick={() => setShowTradingBot(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#0a0a0f', border: '2px solid #a855f7', borderRadius: '12px', padding: '16px', maxWidth: '420px', width: '100%', maxHeight: isMobile ? 'none' : '90vh', overflow: isMobile ? 'visible' : 'hidden', display: 'flex', flexDirection: 'column', marginTop: isMobile ? '20px' : 0, marginBottom: isMobile ? '20px' : 0 }}>

              {tradingBotLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#a855f7' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>🤖</div>
                  <div>Loading bot status...</div>
                </div>
              ) : !tradingBotData ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>🤖</div>
                  <div style={{ color: '#888', marginBottom: '16px' }}>Connecting to bot...</div>
                  <button 
                    onClick={() => {
                      console.log('Load Bot Status clicked');
                      setTradingBotLoading(true);
                      setTimeout(() => fetchTradingBotStatus(), 100);
                    }} 
                    disabled={tradingBotLoading}
                    style={{ 
                      backgroundColor: tradingBotLoading ? '#666' : '#a855f7', 
                      border: 'none', 
                      borderRadius: '8px', 
                      padding: '12px 24px', 
                      color: '#fff', 
                      fontWeight: 'bold', 
                      cursor: tradingBotLoading ? 'not-allowed' : 'pointer' 
                    }}
                  >
                    {tradingBotLoading ? '⟳ Loading...' : '🔄 Load Bot Status'}
                  </button>
                </div>
              ) : tradingBotData.error ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
                  <div style={{ color: '#ff6b6b', marginBottom: '8px', fontWeight: 'bold' }}>
                    {tradingBotData.error.includes('fetch') ? 'Connection Error' : 'Error Loading Bot'}
                  </div>
                  <div style={{ color: '#888', marginBottom: '16px', fontSize: '12px' }}>
                    {tradingBotData.error.includes('fetch') 
                      ? 'Could not connect to server. Check your internet connection.' 
                      : tradingBotData.error}
                  </div>
                  <button 
                    onClick={() => {
                      setTradingBotData(null);
                      setTradingBotLoading(true);
                      setTimeout(() => fetchTradingBotStatus(), 100);
                    }} 
                    style={{ backgroundColor: '#a855f7', border: 'none', borderRadius: '8px', padding: '12px 24px', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    🔄 Retry
                  </button>
                </div>
              ) : (
                <>
                  {/* ═══ HEADER: Title + Status + Activate/Stop ═══ */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #222' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>🤖</span>
                      <div>
                        <div style={{ color: '#a855f7', fontWeight: 'bold', fontSize: '14px' }}>Trading Bot</div>
                        <div style={{ color: tradingBotData.isTrading ? '#00d26a' : tradingBotData.active ? '#f0b90b' : '#666', fontSize: '10px' }}>
                          {tradingBotData.isTrading ? '● TRADING' : tradingBotData.active ? (
                            tradingBotData.pauseReason === 'tab_unfocused' ? '⏸️ TAB UNFOCUSED' :
                            tradingBotData.pauseReason === 'user_idle' ? '⏸️ AFK DETECTED' :
                            tradingBotData.pauseReason === 'low_funds' ? '⏸️ LOW FUNDS' :
                            tradingBotData.pauseReason === 'daily_limit' ? '⏸️ DAILY LIMIT' :
                            '● PAUSED'
                          ) : '○ OFF'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* Status indicator - shows bot state */}
                      <div style={{
                        padding: '4px 10px',
                        borderRadius: '16px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        backgroundColor: tradingBotData.active ? '#00d26a' : '#333',
                        color: tradingBotData.active ? '#000' : '#888'
                      }}>{tradingBotData.active ? 'ACTIVE' : 'IDLE'}</div>
                      <button onClick={() => setShowTradingBot(false)} style={{ backgroundColor: 'transparent', border: 'none', color: '#666', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}>✕</button>
                    </div>
                  </div>

                  {/* ═══ MODE SELECTOR - Auto-nav vs Mini Player ═══ */}
                  {tradingBotData.hasFunds && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ color: '#888', fontSize: '9px', marginBottom: '6px', textAlign: 'center' }}>VIEW MODE</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => setBotViewMode('auto-nav')}
                          style={{
                            flex: 1,
                            padding: '10px 8px',
                            backgroundColor: botViewMode === 'auto-nav' ? '#a855f7' : '#222',
                            border: botViewMode === 'auto-nav' ? '2px solid #a855f7' : '1px solid #333',
                            borderRadius: '8px',
                            color: botViewMode === 'auto-nav' ? '#fff' : '#888',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                        >
                          <div>AUTO-NAV</div>
                          <div style={{ fontSize: '8px', fontWeight: 'normal', marginTop: '2px', opacity: 0.7 }}>Follow bot trades</div>
                        </button>
                        <button
                          onClick={() => setBotViewMode('mini-player')}
                          style={{
                            flex: 1,
                            padding: '10px 8px',
                            backgroundColor: botViewMode === 'mini-player' ? '#a855f7' : '#222',
                            border: botViewMode === 'mini-player' ? '2px solid #a855f7' : '1px solid #333',
                            borderRadius: '8px',
                            color: botViewMode === 'mini-player' ? '#fff' : '#888',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                        >
                          <div>MINI PLAYER</div>
                          <div style={{ fontSize: '8px', fontWeight: 'normal', marginTop: '2px', opacity: 0.7 }}>Browse freely</div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ═══ START & WATCH BUTTON - Top of modal ═══ */}
                  {tradingBotData.hasFunds && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Start the bot if not already running
                        if (!tradingBotData.active) {
                          subscribeTradingBot(7);
                        }
                        // Set auto-nav based on selected mode
                        setBotControlEnabled(botViewMode === 'auto-nav');
                        setShowTradingBot(false);
                        if (botViewMode === 'auto-nav') {
                          setView('lobby');
                        }
                        Sounds.buy();
                      }}
                      style={{ width: '100%', marginBottom: '12px', padding: '14px', backgroundColor: '#00d26a', border: 'none', borderRadius: '8px', color: '#000', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                    >{tradingBotData.active ? (botViewMode === 'auto-nav' ? '👀 WATCH BOT' : '🤖 RUN IN BACKGROUND') : '🚀 START BOT'}</button>
                  )}

                  {/* ═══ BALANCE ROW: Fund + Deposit/Withdraw ═══ */}
                  <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', marginBottom: '10px', border: '1px solid #a855f733' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div>
                        <div style={{ color: '#888', fontSize: '9px' }}>BOT FUND</div>
                        <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{formatPB(tradingBotData.fundBalance || 0)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={async () => {
                            const amount = prompt('Deposit PB to bot fund (min 10K):', '100000');
                            if (!amount) return;
                            const sats = parseInt(amount);
                            if (isNaN(sats) || sats < 10000) { alert('Min 10K PB'); return; }
                            try {
                              const res = await fetch(`${API_URL}/api/trading-bot/deposit`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-session-id': sessionId }, body: JSON.stringify({ amount: sats }) });
                              const data = await res.json();
                              if (data.error) { alert(data.error); return; }
                              if (data.newBalance !== undefined) setGameWallet(data.newBalance);
                              fetchTradingBotStatus();
                            } catch (e) { alert(e.message); }
                          }}
                          style={{ padding: '6px 12px', backgroundColor: '#00d26a', color: '#000', border: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '10px', cursor: 'pointer' }}
                        >+ Deposit</button>
                        <button
                          onClick={async () => {
                            const max = tradingBotData.availableToWithdraw || 0;
                            const amount = prompt(`Withdraw PB (max ${max.toLocaleString()}):`, String(max));
                            if (!amount) return;
                            const sats = parseInt(amount);
                            if (isNaN(sats) || sats < 1000) { alert('Min 1K PB'); return; }
                            try {
                              const res = await fetch(`${API_URL}/api/trading-bot/withdraw`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-session-id': sessionId }, body: JSON.stringify({ amount: sats }) });
                              const data = await res.json();
                              if (data.error) { alert(data.error); return; }
                              if (data.newBalance !== undefined) setGameWallet(data.newBalance);
                              fetchTradingBotStatus();
                            } catch (e) { alert(e.message); }
                          }}
                          style={{ padding: '6px 12px', backgroundColor: '#ff8c00', color: '#000', border: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '10px', cursor: 'pointer' }}
                        >- Withdraw</button>
                      </div>
                    </div>
                    {(tradingBotData.fundBalance || 0) < 10000 && (
                      <div style={{ color: '#ff3b69', fontSize: '9px', textAlign: 'center' }}>⚠️ Need 10K+ PB to trade</div>
                    )}
                  </div>

                  {/* ═══ STATS ROW: Trades, Win%, P&L, ROI ═══ */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginBottom: '10px' }}>
                    <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '8px' }}>TRADES</div>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{tradingBotData.stats?.totalTrades || 0}</div>
                    </div>
                    <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '8px' }}>WIN %</div>
                      <div style={{ color: (tradingBotData.stats?.wins || 0) > (tradingBotData.stats?.losses || 0) ? '#00d26a' : '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>
                        {tradingBotData.stats?.totalTrades > 0 ? Math.round((tradingBotData.stats.wins / tradingBotData.stats.totalTrades) * 100) : 0}%
                      </div>
                    </div>
                    <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '8px' }}>P&L</div>
                      <div style={{ color: (tradingBotData.stats?.netProfit || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>
                        {(tradingBotData.stats?.netProfit || 0) >= 0 ? '+' : ''}{((tradingBotData.stats?.netProfit || 0) / 1000).toFixed(1)}K
                      </div>
                    </div>
                    <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '8px' }}>ROI</div>
                      <div style={{ color: (tradingBotData.roi || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>
                        {(tradingBotData.roi || 0) >= 0 ? '+' : ''}{tradingBotData.roi || 0}%
                      </div>
                    </div>
                  </div>
                  
                  {/* Reset Stats Button */}
                  <div style={{ textAlign: 'right', marginBottom: '8px' }}>
                    <button
                      onClick={async () => {
                        if (!confirm('Reset all bot stats? This clears P&L, trade history, and starts fresh. Fund balance stays the same.')) return;
                        try {
                          const res = await fetch(`${API_URL}/api/trading-bot/reset-stats`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'x-session-id': sessionId }
                          });
                          const data = await res.json();
                          if (data.success) {
                            alert(`✅ ${data.message}`);
                          } else {
                            alert('❌ ' + data.error);
                          }
                        } catch (e) {
                          alert('❌ Error: ' + e.message);
                        }
                      }}
                      style={{ padding: '3px 8px', backgroundColor: '#1a1a2e', color: '#888', border: '1px solid #333', borderRadius: '4px', fontSize: '8px', cursor: 'pointer' }}
                    >
                      🔄 Reset Stats
                    </button>
                  </div>

                  {/* ═══ FEE TRACKER: 1¢ per trade ═══ */}
                  <div style={{ backgroundColor: '#1a1a0a', borderRadius: '8px', padding: '8px 10px', marginBottom: '10px', border: '1px solid #f0b90b33' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px' }}>💰</span>
                        <div>
                          <div style={{ color: '#f0b90b', fontSize: '10px', fontWeight: 'bold' }}>FEES PAID</div>
                          <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                            {tradingBotData.stats?.totalTrades || 0}¢
                            <span style={{ color: '#666', fontSize: '9px', marginLeft: '4px' }}>
                              (${((tradingBotData.stats?.totalTrades || 0) / 100).toFixed(2)})
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#666', fontSize: '9px' }}>1¢ per trade</div>
                        <div style={{ color: (tradingBotData.stats?.netProfit || 0) > 0 ? '#00d26a' : '#ff3b69', fontSize: '10px', fontWeight: 'bold' }}>
                          {(tradingBotData.stats?.netProfit || 0) > 0 
                            ? `+${(((tradingBotData.stats?.netProfit || 0) * 0.00002) / ((tradingBotData.stats?.totalTrades || 1) * 0.01) * 100).toFixed(0)}% ROI`
                            : 'Keep trading!'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ═══ SETTINGS ROW: Risk + Markets ═══ */}
                  <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                      {['low', 'medium', 'high'].map(level => (
                        <button
                          key={level}
                          onClick={() => updateBotSettings({ riskLevel: level })}
                          style={{
                            flex: 1, padding: '6px',
                            backgroundColor: tradingBotData.settings?.riskLevel === level ? (level === 'high' ? '#ff3b69' : level === 'low' ? '#00d26a' : '#f0b90b') : '#1a1a2e',
                            color: tradingBotData.settings?.riskLevel === level ? '#000' : '#666',
                            border: 'none', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold', cursor: 'pointer',
                          }}
                        >{level === 'low' ? '🐢' : level === 'high' ? '🔥' : '⚖️'} {level.toUpperCase()}</button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {/* PvP Toggle - requires acknowledgment */}
                      {!tradingBotData.settings?.acknowledgedPvPRake ? (
                        <button onClick={() => updateBotSettings({ acknowledgedPvPRake: true })} style={{ flex: 1, padding: '6px', backgroundColor: '#1a0a0a', color: '#ff6b6b', border: '1px solid #ff6b6b', borderRadius: '4px', fontSize: '9px', cursor: 'pointer' }}>
                          ☐ Accept 1¢ PvP Fee
                        </button>
                      ) : (
                        <button onClick={() => updateBotSettings({ tradePvP: !tradingBotData.settings?.tradePvP })} style={{ flex: 1, padding: '6px', backgroundColor: tradingBotData.settings?.tradePvP ? '#1a1508' : '#1a1a2e', color: tradingBotData.settings?.tradePvP ? '#f0b90b' : '#666', border: tradingBotData.settings?.tradePvP ? '1px solid #f0b90b' : '1px solid #333', borderRadius: '4px', fontSize: '9px', cursor: 'pointer' }}>
                          {tradingBotData.settings?.tradePvP ? '✅' : '◻️'} PvP Tokens
                        </button>
                      )}
                      {/* AI Toggle - requires acknowledgment */}
                      {!tradingBotData.settings?.acknowledgedAIRake ? (
                        <button onClick={() => updateBotSettings({ acknowledgedAIRake: true })} style={{ flex: 1, padding: '6px', backgroundColor: '#0a1a0a', color: '#00d26a', border: '1px solid #00d26a', borderRadius: '4px', fontSize: '9px', cursor: 'pointer' }}>
                          ☐ Accept 1¢ AI Fee
                        </button>
                      ) : (
                        <button onClick={() => updateBotSettings({ tradeAI: !tradingBotData.settings?.tradeAI })} style={{ flex: 1, padding: '6px', backgroundColor: tradingBotData.settings?.tradeAI ? '#0a1a0a' : '#1a1a2e', color: tradingBotData.settings?.tradeAI ? '#00d26a' : '#666', border: tradingBotData.settings?.tradeAI ? '1px solid #00d26a' : '1px solid #333', borderRadius: '4px', fontSize: '9px', cursor: 'pointer' }}>
                          {tradingBotData.settings?.tradeAI ? '✅' : '◻️'} 🤖 AI Tokens
                        </button>
                      )}
                    </div>
                    
                    {/* Promote My Token Toggle */}
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #333' }}>
                      <button 
                        onClick={() => updateBotSettings({ promoteMyToken: !tradingBotData.settings?.promoteMyToken })} 
                        style={{ 
                          width: '100%',
                          padding: '8px', 
                          backgroundColor: tradingBotData.settings?.promoteMyToken ? '#1a0a1a' : '#1a1a2e', 
                          color: tradingBotData.settings?.promoteMyToken ? '#ff8c00' : '#666', 
                          border: tradingBotData.settings?.promoteMyToken ? '1px solid #ff8c00' : '1px solid #333', 
                          borderRadius: '6px', 
                          fontSize: '10px', 
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        {tradingBotData.settings?.promoteMyToken ? '📣' : '📢'} 
                        {tradingBotData.settings?.promoteMyToken ? '✅ PROMOTING MY TOKENS' : 'Promote My PvP Tokens'}
                      </button>
                      <div style={{ color: '#555', fontSize: '8px', textAlign: 'center', marginTop: '4px' }}>
                        Bot pumps your PvP tokens to attract buyers
                      </div>
                    </div>
                    
                    {/* 🤖 MARKET MAKER CONTROLS - Admin Only */}
                    {['zcool', 'blackwolf', 'h2o4daze'].includes(handcashUser?.handle?.replace('$', '').toLowerCase()) && (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '2px solid #00d26a' }}>
                        <div style={{ backgroundColor: '#0a1a0a', borderRadius: '8px', padding: '10px', border: '1px solid #00d26a' }}>
                          {/* Header with main toggle */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '16px' }}>🤖</span>
                              <span style={{ color: '#00d26a', fontSize: '11px', fontWeight: 'bold' }}>MARKET MAKER</span>
                            </div>
                            <button 
                              onClick={() => updateBotSettings({ marketMaker: !tradingBotData.settings?.marketMaker })} 
                              style={{ 
                                padding: '4px 12px', 
                                backgroundColor: tradingBotData.settings?.marketMaker ? '#00d26a' : '#333', 
                                color: tradingBotData.settings?.marketMaker ? '#000' : '#fff', 
                                border: 'none', 
                                borderRadius: '4px', 
                                fontSize: '9px', 
                                cursor: 'pointer',
                                fontWeight: 'bold'
                              }}
                            >
                              {tradingBotData.settings?.marketMaker ? '🟢 ON' : '⚫ OFF'}
                            </button>
                          </div>
                          
                          {/* Status - Only show when enabled */}
                          {tradingBotData.settings?.marketMaker && (
                            <>
                              {/* Stats Display */}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', marginBottom: '6px' }}>
                                <div style={{ backgroundColor: '#0a0a0f', borderRadius: '4px', padding: '6px', textAlign: 'center' }}>
                                  <div style={{ color: '#f0b90b', fontSize: '11px', fontWeight: 'bold' }}>
                                    {((tradingBotData.fundBalance || 0) / 1000000).toFixed(1)}M
                                  </div>
                                  <div style={{ color: '#666', fontSize: '7px' }}>Fund</div>
                                </div>
                                <div style={{ backgroundColor: '#0a0a0f', borderRadius: '4px', padding: '6px', textAlign: 'center' }}>
                                  <div style={{ color: '#00d26a', fontSize: '11px', fontWeight: 'bold' }}>
                                    {tradingBotData.stats?.mmBuys || 0}
                                  </div>
                                  <div style={{ color: '#666', fontSize: '7px' }}>Buys</div>
                                </div>
                                <div style={{ backgroundColor: '#0a0a0f', borderRadius: '4px', padding: '6px', textAlign: 'center' }}>
                                  <div style={{ color: '#ff3b69', fontSize: '11px', fontWeight: 'bold' }}>
                                    {tradingBotData.stats?.mmSells || 0}
                                  </div>
                                  <div style={{ color: '#666', fontSize: '7px' }}>Sells</div>
                                </div>
                              </div>
                              
                              {/* Last Action */}
                              {tradingBotData.stats?.mmLastToken && (
                                <div style={{ textAlign: 'center', fontSize: '8px', color: '#888' }}>
                                  Last: {tradingBotData.stats?.mmLastAction} ${tradingBotData.stats?.mmLastToken}
                                </div>
                              )}
                            </>
                          )}
                          
                          <div style={{ color: '#00d26a66', fontSize: '7px', textAlign: 'center', marginTop: '6px' }}>
                            ✅ Uses deposited funds • 50/50 buy/sell • Makes charts alive
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* ═══ CURRENT POSITIONS: What the bot is holding ═══ */}
                  {tradingBotData.positions?.length > 0 && (
                    <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '8px', marginBottom: '10px', maxHeight: '150px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ color: '#888', fontSize: '9px', fontWeight: 'bold' }}>
                          📦 POSITIONS ({tradingBotData.positions.length})
                        </div>
                        <button
                          onClick={async () => {
                            if (!confirm(`Close ALL ${tradingBotData.positions.length} positions?`)) return;
                            try {
                              const res = await fetch(`${API_URL}/api/trading-bot/close-all`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'x-session-id': sessionId }
                              });
                              const data = await res.json();
                              if (data.success) {
                                alert(`✅ ${data.message}`);
                                fetchTradingBotStatus(); // Refresh UI
                              } else {
                                alert('❌ ' + data.error);
                              }
                            } catch (e) {
                              alert('❌ Error: ' + e.message);
                            }
                          }}
                          style={{ padding: '2px 6px', backgroundColor: '#2a1a1a', color: '#ff3b69', border: '1px solid #ff3b69', borderRadius: '4px', fontSize: '8px', cursor: 'pointer' }}
                        >
                          CLOSE ALL
                        </button>
                      </div>
                      <div style={{ flex: 1, overflowY: 'auto' }}>
                        {tradingBotData.positions.map((pos, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', borderBottom: '1px solid #1a1a2e', fontSize: '10px' }}>
                            <span style={{ color: '#f0b90b', flex: 1 }}>
                              ${pos.ticker || pos.tokenId?.slice(0,6)}
                            </span>
                            <span style={{ color: '#888', width: '40px', textAlign: 'right' }}>
                              {(pos.invested / 1000).toFixed(0)}K
                            </span>
                            <span style={{ color: parseFloat(pos.profitPercent) >= 0 ? '#00d26a' : '#ff3b69', width: '50px', textAlign: 'right' }}>
                              {parseFloat(pos.profitPercent) >= 0 ? '+' : ''}{pos.profitPercent}%
                            </span>
                            <button
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API_URL}/api/trading-bot/close-position`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'x-session-id': sessionId },
                                    body: JSON.stringify({ tokenId: pos.tokenId })
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    alert(`✅ ${data.message}`);
                                    fetchTradingBotStatus(); // Refresh UI
                                  } else {
                                    alert('❌ ' + data.error);
                                  }
                                } catch (e) {
                                  alert('❌ Error: ' + e.message);
                                }
                              }}
                              style={{ marginLeft: '6px', padding: '1px 4px', backgroundColor: '#1a1a1a', color: '#ff8c00', border: '1px solid #ff8c0066', borderRadius: '3px', fontSize: '8px', cursor: 'pointer' }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ═══ RECENT TRADES: Scrollable list ═══ */}
                  <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '8px', flex: 1, minHeight: '80px', maxHeight: '140px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ color: '#888', fontSize: '9px', fontWeight: 'bold', marginBottom: '6px' }}>📜 RECENT TRADES</div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      {tradingBotData.tradeHistory?.length > 0 ? tradingBotData.tradeHistory.slice(0, 15).map((trade, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #1a1a2e', fontSize: '10px' }}>
                          <span style={{ color: trade.type === 'buy' ? '#00d26a' : trade.type === 'rugged' ? '#ff3b69' : '#ff8c00' }}>
                            {trade.type === 'buy' ? '🟢' : trade.type === 'rugged' ? '💀' : '🔴'} {trade.type.toUpperCase()} ${trade.ticker}
                          </span>
                          <span style={{ color: (trade.profit || 0) >= 0 ? '#00d26a' : '#ff3b69' }}>
                            {trade.profit !== undefined ? ((trade.profit >= 0 ? '+' : '') + (trade.profit / 1000).toFixed(1) + 'K') : ''}
                          </span>
                        </div>
                      )) : (
                        <div style={{ color: '#444', fontSize: '10px', textAlign: 'center', padding: '20px 0' }}>No trades yet</div>
                      )}
                    </div>
                  </div>

                  {/* ═══ DISCLAIMER ═══ */}
                  <div style={{ marginTop: '8px', color: '#555', fontSize: '8px', textAlign: 'center' }}>
                    ⚠️ Bot trading is risky. This is entertainment, not financial advice.
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* 🚀 RUG RUSH (CRASH GAME) MODAL */}
        {showRugRush && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.97)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '12px'
            }}
            onClick={() => setShowRugRush(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#0a0a0f',
                border: '3px solid #ff6b35',
                borderRadius: '20px',
                padding: isMobile ? '16px' : '24px',
                maxWidth: '420px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 0 60px rgba(255, 107, 53, 0.4), inset 0 0 60px rgba(255, 107, 53, 0.1)',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <div style={{ color: '#ff6b35', fontSize: '24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    🚀 RUG RUSH
                    <span style={{
                      fontSize: '10px',
                      backgroundColor: rugRushState?.round?.phase === 'betting' ? '#00d26a' : rugRushState?.round?.phase === 'running' ? '#ff6b35' : '#ff3b69',
                      color: '#000',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      animation: rugRushState?.round?.phase === 'running' ? 'pulse 0.5s ease-in-out infinite' : 'none'
                    }}>
                      {rugRushState?.round?.phase === 'betting' ? 'PLACE BETS' : rugRushState?.round?.phase === 'running' ? 'LIVE!' : 'CRASHED'}
                    </span>
                  </div>
                  <div style={{ color: '#666', fontSize: '11px' }}>Cash out before the rug! 💀</div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setRugRushShowHelp(!rugRushShowHelp)}
                    style={{ backgroundColor: rugRushShowHelp ? '#ff6b35' : 'transparent', border: '1px solid #ff6b35', color: rugRushShowHelp ? '#000' : '#ff6b35', fontSize: '14px', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontWeight: 'bold' }}
                  >?</button>
                  <button
                    onClick={() => setShowRugRush(false)}
                    style={{ backgroundColor: 'transparent', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer' }}
                  >×</button>
                </div>
              </div>
              
              {/* Help / Instructions */}
              {rugRushShowHelp && (
                <div style={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #ff6b35',
                  borderRadius: '12px',
                  padding: '14px',
                  marginBottom: '16px',
                  fontSize: '12px',
                  lineHeight: '1.6'
                }}>
                  <div style={{ color: '#ff6b35', fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>📖 HOW TO PLAY</div>

                  <div style={{ color: '#fff', marginBottom: '12px' }}>
                    <div style={{ color: '#00d26a', fontWeight: 'bold' }}>1️⃣ BETTING PHASE (6 seconds)</div>
                    <div style={{ color: '#888', marginLeft: '20px' }}>Place your bet (10K - 100K PB)</div>
                  </div>

                  <div style={{ color: '#fff', marginBottom: '12px' }}>
                    <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>2️⃣ ROUND STARTS</div>
                    <div style={{ color: '#888', marginLeft: '20px' }}>Multiplier climbs from 1.00x... 1.50x... 2.00x...</div>
                  </div>

                  <div style={{ color: '#fff', marginBottom: '12px' }}>
                    <div style={{ color: '#ff6b35', fontWeight: 'bold' }}>3️⃣ CASH OUT</div>
                    <div style={{ color: '#888', marginLeft: '20px' }}>Hit CASH OUT to lock in your multiplier!</div>
                    <div style={{ color: '#888', marginLeft: '20px' }}>Your bet × multiplier = your payout</div>
                  </div>

                  <div style={{ color: '#fff', marginBottom: '12px' }}>
                    <div style={{ color: '#ff3b69', fontWeight: 'bold' }}>4️⃣ RUG! 💀</div>
                    <div style={{ color: '#888', marginLeft: '20px' }}>Still in when it crashes? You lose your bet!</div>
                  </div>

                  <div style={{ borderTop: '1px solid #333', paddingTop: '12px', marginTop: '12px' }}>
                    <div style={{ color: '#9b59b6', fontWeight: 'bold', marginBottom: '6px' }}>🎟️ BONUS SCRATCHERS</div>
                    <div style={{ color: '#888', fontSize: '11px' }}>
                      Win at special multipliers (2.00x, 4.20x, 5x+) to earn scratch-off tickets!
                      Scratch 3 cards to reveal prizes up to <span style={{ color: '#f0b90b' }}>2M PB!</span>
                    </div>
                  </div>

                  <div style={{ color: '#555', fontSize: '10px', marginTop: '12px', textAlign: 'center' }}>
                    Max payout: 2M PB • 1¢ fee per bet • 🎁 Scratchers are FREE bonuses!
                  </div>
                </div>
              )}

              {/* GIANT MULTIPLIER DISPLAY */}
              <div style={{
                backgroundColor: '#111',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '16px',
                textAlign: 'center',
                border: rugRushState?.round?.phase === 'crashed' ? '2px solid #ff3b69' : '2px solid #1a1a2e',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Background gradient based on multiplier */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: rugRushState?.round?.phase === 'crashed'
                    ? 'radial-gradient(circle at center, rgba(255,59,105,0.3) 0%, transparent 70%)'
                    : `radial-gradient(circle at center, rgba(${Math.min(255, (rugRushState?.round?.currentMultiplier || 1) * 50)}, ${Math.max(0, 210 - (rugRushState?.round?.currentMultiplier || 1) * 30)}, 106, 0.3) 0%, transparent 70%)`,
                  transition: 'all 0.1s'
                }} />
                
                {rugRushState?.round?.phase === 'crashed' ? (
                  <>
                    <div style={{ fontSize: '48px', marginBottom: '8px' }}>💀</div>
                    <div style={{ color: '#ff3b69', fontSize: '48px', fontWeight: 'bold', fontFamily: 'monospace', position: 'relative' }}>
                      {(rugRushState?.round?.crashPoint || 1).toFixed(2)}x
                    </div>
                    <div style={{ color: '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>RUGGED!</div>
                  </>
                ) : rugRushState?.round?.phase === 'running' ? (
                  <>
                    <div style={{ 
                      color: (rugRushState?.round?.currentMultiplier || 1) < 2 ? '#00d26a' : (rugRushState?.round?.currentMultiplier || 1) < 5 ? '#f0b90b' : '#ff6b35', 
                      fontSize: '64px', 
                      fontWeight: 'bold', 
                      fontFamily: 'monospace',
                      textShadow: `0 0 20px ${(rugRushState?.round?.currentMultiplier || 1) < 2 ? 'rgba(0,210,106,0.5)' : (rugRushState?.round?.currentMultiplier || 1) < 5 ? 'rgba(240,185,11,0.5)' : 'rgba(255,107,53,0.5)'}`,
                      position: 'relative',
                      transform: `scale(${1 + Math.min(0.2, (rugRushState?.round?.currentMultiplier || 1) * 0.02)})`,
                      transition: 'transform 0.1s'
                    }}>
                      {(rugRushState?.round?.currentMultiplier || 1).toFixed(2)}x
                    </div>
                    <div style={{ color: '#888', fontSize: '12px' }}>
                      {rugRushMyBet && !rugRushMyBet.cashedOut 
                        ? `Your payout: ${formatSats(Math.min(rugRushState?.config?.maxPayout || 2000000, Math.floor(rugRushMyBet.amount * (rugRushState?.round?.currentMultiplier || 1))))}` 
                        : 'Climbing...'}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ color: '#666', fontSize: '48px', fontWeight: 'bold', fontFamily: 'monospace', position: 'relative' }}>
                      1.00x
                    </div>
                    <div style={{ color: '#00d26a', fontSize: '14px' }}>
                      {rugRushState?.round?.bettingEndsAt
                        ? `Starts in ${Math.max(0, Math.ceil((rugRushState.round.bettingEndsAt - Date.now()) / 1000))}s...`
                        : 'Waiting...'}
                    </div>
                  </>
                )}
              </div>

              {/* Result Message */}
              {rugRushResult && (
                <div style={{
                  backgroundColor: rugRushResult.type === 'win' ? '#0a1a0a' : rugRushResult.type === 'error' ? '#1a0a0a' : '#1a1508',
                  border: `1px solid ${rugRushResult.type === 'win' ? '#00d26a' : rugRushResult.type === 'error' ? '#ff3b69' : '#f0b90b'}`,
                  borderRadius: '8px',
                  padding: '10px',
                  marginBottom: '16px',
                  textAlign: 'center',
                  color: rugRushResult.type === 'win' ? '#00d26a' : rugRushResult.type === 'error' ? '#ff3b69' : '#f0b90b',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}>
                  {rugRushResult.message}
                </div>
              )}

              {/* Bet Controls */}
              <div style={{ backgroundColor: '#111', borderRadius: '12px', padding: '14px', marginBottom: '16px' }}>
                {/* Your current bet */}
                {rugRushMyBet && (
                  <div style={{
                    backgroundColor: rugRushMyBet.cashedOut ? '#0a1a0a' : '#1a1508',
                    border: `1px solid ${rugRushMyBet.cashedOut ? '#00d26a' : '#f0b90b'}`,
                    borderRadius: '8px',
                    padding: '10px',
                    marginBottom: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px' }}>YOUR BET</div>
                      <div style={{ color: '#f0b90b', fontSize: '16px', fontWeight: 'bold' }}>{formatSats(rugRushMyBet.amount)}</div>
                    </div>
                    {rugRushMyBet.cashedOut ? (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>✓ CASHED OUT</div>
                        <div style={{ color: '#00d26a', fontSize: '12px' }}>@ {rugRushMyBet.cashoutMultiplier?.toFixed(2)}x → {formatSats(rugRushMyBet.payout)}</div>
                      </div>
                    ) : rugRushState?.round?.phase === 'running' ? (
                      <button
                        onClick={cashOutRugRush}
                        disabled={rugRushCashingOut}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#00d26a',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#000',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: rugRushCashingOut ? 'not-allowed' : 'pointer',
                          animation: 'pulse 0.3s ease-in-out infinite'
                        }}
                      >
                        {rugRushCashingOut ? '...' : `CASH OUT @ ${(rugRushState?.round?.currentMultiplier || 1).toFixed(2)}x`}
                      </button>
                    ) : (
                      <div style={{ color: '#888', fontSize: '12px' }}>Waiting for round...</div>
                    )}
                  </div>
                )}

                {/* Place bet controls (only during betting phase) */}
                {rugRushState?.round?.phase === 'betting' && !rugRushMyBet && (
                  <>
                    <div style={{ color: '#888', fontSize: '10px', marginBottom: '8px' }}>BET AMOUNT</div>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      {[10000, 25000, 50000, 100000].map(amt => (
                        <button
                          key={amt}
                          onClick={() => setRugRushBetAmount(amt)}
                          style={{
                            flex: 1,
                            minWidth: '60px',
                            padding: '10px 8px',
                            backgroundColor: rugRushBetAmount === amt ? '#1a1508' : '#0a0a0f',
                            border: rugRushBetAmount === amt ? '2px solid #f0b90b' : '1px solid #333',
                            borderRadius: '8px',
                            color: rugRushBetAmount === amt ? '#f0b90b' : '#888',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                        >
                          {formatSats(amt)}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => placeRugRushBet(rugRushBetAmount)}
                      disabled={rugRushBetting || gameWallet < rugRushBetAmount}
                      style={{
                        width: '100%',
                        padding: '14px',
                        backgroundColor: gameWallet < rugRushBetAmount ? '#333' : '#ff6b35',
                        border: 'none',
                        borderRadius: '10px',
                        color: gameWallet < rugRushBetAmount ? '#666' : '#000',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: rugRushBetting || gameWallet < rugRushBetAmount ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {rugRushBetting ? 'Placing bet...' : gameWallet < rugRushBetAmount ? 'Insufficient balance' : `🚀 BET ${formatSats(rugRushBetAmount)}`}
                    </button>
                    <div style={{ color: '#888', fontSize: '11px', textAlign: 'center', marginTop: '10px' }}>
                      Balance: {formatSats(gameWallet)} • <span style={{ color: '#f0b90b' }}>💰 1¢ BSV fee per bet</span>
                    </div>
                  </>
                )}

                {/* Waiting message when round is running and no bet */}
                {rugRushState?.round?.phase === 'running' && !rugRushMyBet && (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                    <div>Round in progress - wait for next betting phase</div>
                  </div>
                )}
              </div>

              {/* Recent Crashes History */}
              <div style={{ backgroundColor: '#111', borderRadius: '12px', padding: '14px' }}>
                <div style={{ color: '#888', fontSize: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>RECENT CRASHES</span>
                  <span>{rugRushState?.round?.playerCount || 0} players this round</span>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {(rugRushState?.history || []).slice(0, 12).map((h, i) => (
                    <div
                      key={h.roundId || i}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: h.crashPoint < 1.5 ? '#1a0a0a' : h.crashPoint < 2 ? '#1a1508' : h.crashPoint < 5 ? '#0a1a0a' : '#0a0a1a',
                        border: `1px solid ${h.crashPoint < 1.5 ? '#ff3b69' : h.crashPoint < 2 ? '#f0b90b' : h.crashPoint < 5 ? '#00d26a' : '#9b59b6'}`,
                        borderRadius: '6px',
                        color: h.crashPoint < 1.5 ? '#ff3b69' : h.crashPoint < 2 ? '#f0b90b' : h.crashPoint < 5 ? '#00d26a' : '#9b59b6',
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}
                    >
                      {h.crashPoint?.toFixed(2)}x
                    </div>
                  ))}
                  {(!rugRushState?.history || rugRushState.history.length === 0) && (
                    <div style={{ color: '#444', fontSize: '11px' }}>No history yet</div>
                  )}
                </div>
              </div>

              {/* Info */}
              <div style={{ marginTop: '12px', color: '#555', fontSize: '9px', textAlign: 'center' }}>
                Max payout: {formatSats(rugRushState?.config?.maxPayout || 2000000)} • House edge: 4% • 🎟️ Win scratchers at special multipliers!
              </div>
            </div>
          </div>
        )}

        {/* 🏔️ THE LAST DEGEN - Battle Royale Crash Game */}
        <MountainClimber
          isOpen={showMountainClimber}
          onClose={() => setShowMountainClimber(false)}
          gameWallet={gameWallet}
          setGameWallet={setGameWallet}
          sessionId={sessionId}
          API_URL={API_URL}
          handcashUser={handcashUser}
          triggerRakeNotification={triggerRakeNotification}
          Sounds={Sounds}
          wsRef={wsRef}
          bsvPrice={bsvPrice}
        />

        {/* 🎟️ SCRATCH-OFF MODAL */}
        {showScratcher && scratcherOffer && (
          <div 
            style={{ 
              position: 'fixed', 
              inset: 0, 
              backgroundColor: 'rgba(0,0,0,0.97)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              zIndex: 1001, 
              padding: '12px' 
            }} 
            onClick={() => {
              if (scratcherResult) {
                setShowScratcher(false);
                setScratcherOffer(null);
                setScratcherResult(null);
              }
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()} 
              style={{ 
                backgroundColor: '#0a0a0f', 
                border: '3px solid #9b59b6', 
                borderRadius: '20px', 
                padding: isMobile ? '20px' : '28px', 
                maxWidth: '360px', 
                width: '100%',
                textAlign: 'center',
                boxShadow: '0 0 60px rgba(155, 89, 182, 0.4), inset 0 0 60px rgba(155, 89, 182, 0.1)',
              }}
            >
              {/* Header */}
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>🎟️</div>
              <div style={{ color: '#9b59b6', fontSize: '22px', fontWeight: 'bold', marginBottom: '4px' }}>
                BONUS SCRATCHER!
              </div>
              <div style={{ color: '#f0b90b', fontSize: '14px', marginBottom: '16px' }}>
                {scratcherOffer.reason}
              </div>
              
              {/* Scratch Cards Grid */}
              {!scratcherResult ? (
                <>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)', 
                    gap: '10px', 
                    marginBottom: '16px' 
                  }}>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        onClick={() => scratchCard(i)}
                        style={{
                          aspectRatio: '1',
                          backgroundColor: scratcherCards[i] ? '#1a0a1a' : '#9b59b6',
                          border: `2px solid ${scratcherCards[i] ? '#9b59b6' : '#bf7fd6'}`,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: scratcherCards[i] ? 'default' : 'pointer',
                          fontSize: '28px',
                          transition: 'all 0.2s',
                          transform: scratcherCards[i] ? 'scale(0.95)' : 'scale(1)',
                          boxShadow: scratcherCards[i] ? 'none' : '0 4px 12px rgba(155, 89, 182, 0.3)',
                        }}
                        onMouseEnter={e => { if (!scratcherCards[i]) e.currentTarget.style.transform = 'scale(1.05)'; }}
                        onMouseLeave={e => { if (!scratcherCards[i]) e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        {scratcherCards[i] ? '✨' : '❓'}
                      </div>
                    ))}
                  </div>
                  
                  <div style={{ color: '#888', fontSize: '12px', marginBottom: '12px' }}>
                    {scratcherRevealing ? '✨ Revealing prize...' : `Scratch ${Math.max(0, 3 - scratcherCards.filter(Boolean).length)} more to reveal!`}
                  </div>
                  
                  <div style={{ color: '#555', fontSize: '10px' }}>
                    🎁 FREE bonus • Win up to 2M PB!
                  </div>
                </>
              ) : (
                <>
                  {/* Prize Reveal */}
                  <div style={{ 
                    backgroundColor: scratcherResult.prize?.value > 0 ? '#0a1a0a' : '#1a1a1a',
                    border: `2px solid ${scratcherResult.prize?.value > 0 ? '#00d26a' : '#666'}`,
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '16px',
                  }}>
                    <div style={{ fontSize: '56px', marginBottom: '12px' }}>
                      {scratcherResult.prize?.id === 'jackpot' ? '🎰' :
                       scratcherResult.prize?.id === 'big_win' ? '🌙' :
                       scratcherResult.prize?.id === 'medium_win' ? '💎' :
                       scratcherResult.prize?.id === 'small_win' ? '💰' :
                       scratcherResult.prize?.id === 'free_bet' ? '🎫' : '💨'}
                    </div>
                    <div style={{ 
                      color: scratcherResult.prize?.value > 0 ? '#00d26a' : '#888', 
                      fontSize: '24px', 
                      fontWeight: 'bold',
                      marginBottom: '8px'
                    }}>
                      {scratcherResult.prize?.label || 'Try Again'}
                    </div>
                    {scratcherResult.prize?.value > 0 && (
                      <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>
                        +{formatSats(scratcherResult.prize.value)}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowScratcher(false);
                      setScratcherOffer(null);
                      setScratcherResult(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '14px',
                      backgroundColor: '#9b59b6',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Continue Playing
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* 💸 LOW BALANCE TOP-OFF MODAL */}
        {showLowBalance && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px' }} onClick={() => setShowLowBalance(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#0a0a0f', border: '2px solid #f0b90b', borderRadius: '16px', padding: isMobile ? '20px' : '28px', maxWidth: '400px', width: '100%', textAlign: 'center' }}>
              {/* Header */}
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>💸</div>
              <div style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>Running Low!</div>
              <div style={{ color: '#888', fontSize: '13px', marginBottom: '20px' }}>
                Your Paper Bucks balance is under $1M.<br/>
                Top off to keep playing!
              </div>
              
              {/* Current Balance */}
              <div style={{ backgroundColor: '#111', borderRadius: '10px', padding: '12px', marginBottom: '20px' }}>
                <div style={{ color: '#666', fontSize: '10px' }}>CURRENT BALANCE</div>
                <div style={{ color: gameWallet < 100000 ? '#ff3b69' : '#f0b90b', fontSize: '28px', fontWeight: 'bold' }}>${formatSats(gameWallet)}</div>
              </div>
              
              {/* Quick Top-Off Options */}
              <div style={{ color: '#888', fontSize: '11px', marginBottom: '10px' }}>QUICK TOP-OFF</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                {[
                  { amount: 1000000, label: '$1M', usd: ((1000000 * (bsvPrice || 20)) / 100000000) },
                  { amount: 2500000, label: '$2.5M', usd: ((2500000 * (bsvPrice || 20)) / 100000000) },
                  { amount: 5000000, label: '$5M', usd: ((5000000 * (bsvPrice || 20)) / 100000000), popular: true },
                  { amount: 10000000, label: '$10M', usd: ((10000000 * (bsvPrice || 20)) / 100000000) },
                ].map(opt => (
                  <button
                    key={opt.amount}
                    onClick={() => {
                      setShowLowBalance(false);
                      setPendingDeposit({ amount: opt.amount, type: 'topoff' });
                      setShowDeposit(true);
                    }}
                    style={{
                      padding: '14px 12px',
                      backgroundColor: opt.popular ? '#1a1508' : '#111',
                      border: opt.popular ? '2px solid #f0b90b' : '1px solid #333',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1a1a2e'; e.currentTarget.style.borderColor = '#00d26a'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = opt.popular ? '#1a1508' : '#111'; e.currentTarget.style.borderColor = opt.popular ? '#f0b90b' : '#333'; }}
                  >
                    {opt.popular && (
                      <div style={{ position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#f0b90b', color: '#000', fontSize: '8px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '4px' }}>POPULAR</div>
                    )}
                    <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{opt.label}</div>
                    <div style={{ color: '#666', fontSize: '11px' }}>~${isNaN(opt.usd) ? '0.00' : opt.usd.toFixed(2)} USD</div>
                  </button>
                ))}
              </div>
              
              {/* Custom Amount Button */}
              <button
                onClick={() => {
                  setShowLowBalance(false);
                  setShowDeposit(true);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#00d26a',
                  color: '#000',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginBottom: '12px',
                }}
              >
                ⬆️ Custom Deposit Amount
              </button>
              
              {/* Dismiss */}
              <button
                onClick={() => setShowLowBalance(false)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        )}

        {/* 🚫 IDLE KICK MODAL - Stop polling after 5 min inactivity */}
        {isIdleKicked && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.98)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '12px' }}>
            <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #f0b90b', borderRadius: '16px', padding: isMobile ? '24px' : '32px', maxWidth: '380px', width: '100%', textAlign: 'center' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>😴</div>
              <div style={{ color: '#f0b90b', fontSize: '22px', fontWeight: 'bold', marginBottom: '12px' }}>You fell asleep!</div>
              <div style={{ color: '#888', fontSize: '14px', marginBottom: '8px', lineHeight: '1.5' }}>
                No activity for 5 minutes.<br/>
                Updates paused to save bandwidth.
              </div>
              <div style={{ color: '#ff3b69', fontSize: '18px', fontWeight: 'bold', marginBottom: '24px' }}>
                Logging out in {idleKickCountdown}s...
              </div>
              <button
                onClick={() => {
                  lastUserActivityRef.current = Date.now();
                  setIdleKickCountdown(60);
                  setIsIdleKicked(false);
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#f0b90b',
                  color: '#000',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                👋 I'm Back!
              </button>
            </div>
          </div>
        )}

        {/* Unified Deposit Modal */}
        <DepositModal />
        
        {/* Withdraw Modal */}
        <WithdrawModal />
        
        {/* Pending Withdrawal Modal */}
        <PendingWithdrawalModal />

        {/* 💼 Portfolio Modal - View active positions and rugged bags */}
        {showBags && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #333', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b' }}>💼 Your Portfolio</h2>
                <button onClick={() => setShowBags(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* 📊 Daily Stats Summary */}
              <div style={{ 
                backgroundColor: '#111', 
                border: '1px solid #1a1a2e', 
                borderRadius: '8px', 
                padding: '12px', 
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-around',
                textAlign: 'center'
              }}>
                <div>
                  <div style={{ color: '#666', fontSize: '10px' }}>24h P/L</div>
                  <div style={{ 
                    color: dailyStats.totalProfit >= 0 ? '#00d26a' : '#ff3b69', 
                    fontWeight: 'bold', 
                    fontSize: '14px' 
                  }}>
                    {dailyStats.totalProfit >= 0 ? '+' : ''}{formatPB(dailyStats.totalProfit)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '10px' }}>Wins</div>
                  <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '14px' }}>{dailyStats.wins}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '10px' }}>Losses</div>
                  <div style={{ color: '#ff3b69', fontWeight: 'bold', fontSize: '14px' }}>{dailyStats.losses}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '10px' }}>Rugged</div>
                  <div style={{ color: '#ff3b69', fontWeight: 'bold', fontSize: '14px' }}>{dailyStats.rugs}</div>
                </div>
              </div>
              
              {/* 🚀 YOUR CREATED TOKENS - Tokens you launched */}
              {myLaunchedTokens.filter(t => t.state === 'active').length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ 
                    color: '#ff8c00', 
                    fontSize: '12px', 
                    fontWeight: 'bold', 
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🚀 YOUR CREATED TOKENS ({myLaunchedTokens.filter(t => t.state === 'active').length})
                  </div>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {myLaunchedTokens.filter(t => t.state === 'active').map(token => {
                      const liveToken = userLaunchedTokens.find(t => t.id === token.tokenId || t.id === token.id) || token;
                      return (
                        <div 
                          key={token.tokenId || token.id}
                          style={{ 
                            backgroundColor: '#1a0f08', 
                            border: '2px solid #ff8c00', 
                            borderRadius: '8px', 
                            padding: '12px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div 
                              onClick={() => {
                                if (liveToken) {
                                  setShowBags(false);
                                  setCurrentToken(liveToken);
                                  setView('token');
                                }
                              }}
                              style={{ cursor: 'pointer', flex: 1 }}
                            >
                              <div style={{ color: '#ff8c00', fontWeight: 'bold', fontSize: '14px' }}>${token.ticker}</div>
                              <div style={{ color: '#666', fontSize: '10px' }}>Created by you</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ color: '#00d26a', fontSize: '11px' }}>● LIVE</div>
                                <div style={{ color: '#888', fontSize: '10px' }}>Initial: {formatPB(token.initialBuy || liveToken?.initialBuy || 0)}</div>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); setDeleteConfirmToken({ ...token, id: token.tokenId || token.id }); }}
                                style={{ backgroundColor: '#1a0808', border: '1px solid #ff3b6944', borderRadius: '4px', padding: '6px 10px', color: '#ff3b69', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Active Positions Section - Split by AI vs User tokens */}
              {(() => {
                const activeTokenIds = activeTokens.map(t => t.id);
                // Include both active AND rugged positions (rugged show inline with CLEAR)
                const allPositions = positions.filter(p => p.status === 'active' || p.status === 'rugged');

                // Separate AI and User token positions
                const aiPositions = allPositions.filter(p => {
                  const token = activeTokens.find(t => t.id === p.tokenId);
                  return !token?.isUserLaunched && !p.isUserLaunched;
                });
                const userPositions = allPositions.filter(p => {
                  const token = activeTokens.find(t => t.id === p.tokenId);
                  return token?.isUserLaunched || p.isUserLaunched;
                });
                
                // Pending Rug Prophet bets
                const pendingBets = rugProphetBets.filter(b => b.status === 'pending');
                
                // Collapsible only if >2 items
                const aiCollapsible = aiPositions.length > 2;
                const userCollapsible = userPositions.length > 2;
                const betsCollapsible = pendingBets.length > 2;
                
                // Render a position card
                const renderPositionCard = (pos) => {
                  const token = activeTokens.find(t => t.id === pos.tokenId);
                  // 🔥 Position is rugged if: explicitly rugged OR token no longer exists (expired/deleted)
                  const tokenExists = activeTokenIds.includes(pos.tokenId);
                  const isRugged = pos.status === 'rugged' || (pos.status === 'active' && !tokenExists);
                  const tokenPrice = token?.price || pos.entry || 1.0;
                  const multiplier = isRugged ? 0 : (pos.entry > 0 ? tokenPrice / pos.entry : 1);
                  const pnl = isRugged ? -pos.invested : (Math.floor(pos.invested * multiplier) - pos.invested);
                  const tokenStillActive = tokenExists && !isRugged;
                  const isUserToken = token?.isUserLaunched || pos.isUserLaunched;
                  const isCreator = token?.creator === handcashUser?.handle?.replace('$', '');

                  // Rugged positions get special styling
                  if (isRugged) {
                    return (
                      <div
                        key={pos.id}
                        style={{
                          backgroundColor: '#1a0508',
                          border: '1px solid #5a1a2a',
                          borderLeft: '3px solid #ff3b69',
                          borderRadius: '8px',
                          padding: '12px',
                          opacity: 1,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ color: '#ff3b69', fontWeight: 'bold', fontSize: '14px' }}>
                              {pos.tokenTicker} 💀
                            </div>
                            <div style={{ color: '#555', fontSize: '10px' }}>{pos.tokenCategory}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ color: '#ff3b69', fontWeight: 'bold', fontSize: '14px' }}>LOST {formatPB(pos.invested)}</div>
                              {pos.tradeCount > 0 && (
                                <div style={{ color: '#888', fontSize: '10px' }}>
                                  Rake: <span style={{ color: '#f0b90b' }}>{pos.tradeCount}¢</span>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPositions(prev => prev.filter(p => p.id !== pos.id));
                                clearRugs();
                              }}
                              style={{
                                backgroundColor: '#ff3b69',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                              }}
                            >
                              CLEAR
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={pos.id}
                      style={{
                        backgroundColor: tokenStillActive ? (isUserToken ? '#0d0d1a' : '#0d1a0d') : '#1a1a0d',
                        border: tokenStillActive ? `1px solid ${isUserToken ? '#ff8c00' : '#00d26a'}` : '1px solid #f0b90b',
                        borderRadius: '8px',
                        padding: '12px',
                        opacity: tokenStillActive ? 1 : 0.7,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div 
                          onClick={() => { 
                            if (token) {
                              setShowBags(false); 
                              setCurrentToken(token);
                              setCurrentPrice(tokenPrice);
                              setGameState('playing');
                              setView('token');
                            }
                          }}
                          style={{ cursor: tokenStillActive ? 'pointer' : 'default', flex: 1 }}
                        >
                          <div style={{ color: tokenStillActive ? (isUserToken ? '#ff8c00' : '#00d26a') : '#f0b90b', fontWeight: 'bold', fontSize: '14px' }}>
                            {pos.tokenTicker} {!tokenStillActive && '⏳'} {isCreator && <span style={{ color: '#ff8c00', fontSize: '10px' }}>👑</span>}
                          </div>
                          <div style={{ color: '#555', fontSize: '10px' }}>{pos.tokenCategory}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: multiplier >= 1 ? '#00d26a' : '#ff3b69', fontWeight: 'bold', fontSize: '16px' }}>
                              {formatMultiplier(multiplier)}
                            </div>
                            <div style={{ color: pnl >= 0 ? '#00d26a' : '#ff3b69', fontSize: '11px' }}>
                              {pnl >= 0 ? '+' : ''}{formatPB(pnl)}
                            </div>
                          </div>
                          {isCreator && tokenStillActive && (
                            <button
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setDeleteConfirmToken({ id: token.id, tokenId: token.id, ticker: token.ticker }); 
                              }}
                              style={{ backgroundColor: '#1a0808', border: '1px solid #ff3b6944', borderRadius: '4px', padding: '4px 8px', color: '#ff3b69', fontSize: '10px', cursor: 'pointer' }}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                      <div 
                        onClick={() => { 
                          if (token) {
                            setShowBags(false); 
                            setCurrentToken(token);
                            setCurrentPrice(tokenPrice);
                            setGameState('playing');
                            setView('token');
                          }
                        }}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${isUserToken ? '#1a1a3f' : '#0f2f0f'}`, cursor: tokenStillActive ? 'pointer' : 'default' }}
                      >
                        <div>
                          <span style={{ color: '#555', fontSize: '10px' }}>Invested: </span>
                          <span style={{ color: '#f0b90b', fontSize: '10px' }}>{formatPB(pos.invested)}</span>
                        </div>
                        <div style={{ color: tokenStillActive ? (isUserToken ? '#ff8c00' : '#00d26a') : '#888', fontSize: '10px' }}>
                          {tokenStillActive ? 'TAP TO TRADE →' : 'RESOLVING...'}
                        </div>
                      </div>
                    </div>
                  );
                };
                
                return (
                  <div style={{ marginBottom: '20px' }}>
                    {/* 🤖 AI TOKEN POSITIONS (rugged positions now display inline with PositionRow) */}
                    {aiPositions.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div 
                          onClick={() => aiCollapsible && setCollapseAIPositions(!collapseAIPositions)}
                          style={{ 
                            color: '#00d26a', 
                            fontSize: '12px', 
                            fontWeight: 'bold', 
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: aiCollapsible ? 'pointer' : 'default'
                          }}
                        >
                          🤖 AI TOKEN POSITIONS ({aiPositions.length})
                          {aiCollapsible && (
                            <span style={{ color: '#666', fontSize: '10px' }}>
                              {collapseAIPositions ? '▶ tap to expand' : '▼'}
                            </span>
                          )}
                        </div>
                        {!collapseAIPositions && (
                          <div style={{ display: 'grid', gap: '10px', maxHeight: '200px', overflowY: 'auto', paddingRight: '8px' }}>
                            {aiPositions.map(renderPositionCard)}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* 🔮 RUG PROPHET BETS */}
                    {pendingBets.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div 
                          onClick={() => betsCollapsible && setCollapseRugBets(!collapseRugBets)}
                          style={{ 
                            color: '#9b59b6', 
                            fontSize: '12px', 
                            fontWeight: 'bold', 
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: betsCollapsible ? 'pointer' : 'default'
                          }}
                        >
                          🔮 RUG PROPHET BETS ({pendingBets.length})
                          {betsCollapsible && (
                            <span style={{ color: '#666', fontSize: '10px' }}>
                              {collapseRugBets ? '▶ tap to expand' : '▼'}
                            </span>
                          )}
                        </div>
                        {!collapseRugBets && (
                          <div style={{ display: 'grid', gap: '10px', maxHeight: '150px', overflowY: 'auto', paddingRight: '8px' }}>
                            {pendingBets.map(bet => {
                              const token = activeTokens.find(t => t.id === bet.tokenId);
                              return (
                                <div key={bet.id} style={{ 
                                  backgroundColor: '#150f1a', 
                                  border: '1px solid #9b59b6', 
                                  borderRadius: '8px', 
                                  padding: '10px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <div>
                                    <div style={{ color: '#9b59b6', fontWeight: 'bold', fontSize: '12px' }}>{bet.tokenTicker || token?.ticker || '???'}</div>
                                    <div style={{ color: '#666', fontSize: '10px' }}>Bet: {bet.prediction === 'rug' ? '💀 RUG' : '🌙 MOON'}</div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '12px' }}>{formatPB(bet.amount)}</div>
                                    <div style={{ color: '#666', fontSize: '10px' }}>→ {formatPB(bet.potentialWin || bet.amount * 2)}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* 🚀 USER TOKEN POSITIONS */}
                    {userPositions.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div 
                          onClick={() => userCollapsible && setCollapseUserPositions(!collapseUserPositions)}
                          style={{ 
                            color: '#ff8c00', 
                            fontSize: '12px', 
                            fontWeight: 'bold', 
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: userCollapsible ? 'pointer' : 'default'
                          }}
                        >
                          🚀 USER TOKEN POSITIONS ({userPositions.length})
                          {userCollapsible && (
                            <span style={{ color: '#666', fontSize: '10px' }}>
                              {collapseUserPositions ? '▶ tap to expand' : '▼'}
                            </span>
                          )}
                        </div>
                        {!collapseUserPositions && (
                          <div style={{ display: 'grid', gap: '10px', maxHeight: '200px', overflowY: 'auto', paddingRight: '8px' }}>
                            {userPositions.map(renderPositionCard)}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Empty state */}
                    {aiPositions.length === 0 && userPositions.length === 0 && pendingBets.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#555' }}>
                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📭</div>
                        <div style={{ fontSize: '12px' }}>No active positions</div>
                      </div>
                    )}
                  </div>
                );
              })()}
              
              {/* Closed Positions (Today) */}
              {(() => {
                const closedPositions = positions.filter(p => p.status === 'closed');
                return closedPositions.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: '#888', fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>
                      ✅ CLOSED TODAY ({closedPositions.length})
                    </div>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {closedPositions.slice(-5).map(pos => (
                        <div key={pos.id} style={{ 
                          backgroundColor: '#111', 
                          border: '1px solid #1a1a2e', 
                          borderRadius: '6px', 
                          padding: '10px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{ color: '#888', fontSize: '12px' }}>{pos.tokenTicker}</div>
                          <div style={{ 
                            color: pos.profit >= 0 ? '#00d26a' : '#ff3b69', 
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}>
                            {pos.profit >= 0 ? '+' : ''}{formatPB(pos.profit)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              
            </div>
          </div>
        )}


        {/* 🚀 LAUNCH TOKEN MODAL - Compact, no scroll */}
        {showLaunchToken && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px' }} onClick={() => setShowLaunchToken(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#0a0a0f', border: '1px solid #f0b90b', borderRadius: '12px', padding: isMobile ? '14px' : '20px', maxWidth: '420px', width: '100%' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ color: '#f0b90b', fontSize: isMobile ? '15px' : '17px', fontWeight: 'bold' }}>🚀 BSV.pump</div>
                  <span 
                    onClick={() => setShowBsvPumpInfo(true)}
                    style={{ cursor: 'pointer', backgroundColor: '#333', borderRadius: '50%', width: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#888', fontWeight: 'bold' }}
                  >?</span>
                </div>
                <button onClick={() => setShowLaunchToken(false)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Row 1: Ticker + Category */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>TICKER *</div>
                  <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', padding: '8px 10px' }}>
                    <span style={{ color: '#ff8c00', fontWeight: 'bold', marginRight: '4px' }}>$</span>
                    <input
                      type="text"
                      value={launchForm.ticker}
                      onChange={(e) => setLaunchForm(prev => ({ ...prev, ticker: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) }))}
                      placeholder="MOON"
                      maxLength={10}
                      style={{ flex: 1, backgroundColor: 'transparent', border: 'none', color: '#fff', fontSize: '16px', fontWeight: 'bold', outline: 'none', width: '100%' }}
                    />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>CATEGORY</div>
                  <select
                    value={launchForm.category}
                    onChange={(e) => setLaunchForm(prev => ({ ...prev, category: e.target.value }))}
                    style={{ width: '100%', backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', padding: '8px 10px', color: '#fff', fontSize: '16px', outline: 'none', cursor: 'pointer' }}
                  >
                    <option value="Meme Coins">🐸 Meme Coins</option>
                    <option value="Animal Tokens">🐕 Animal</option>
                    <option value="Food Tokens">🍕 Food</option>
                    <option value="AI Tokens">🤖 AI</option>
                    <option value="Gaming">🎮 Gaming</option>
                    <option value="DeFi Degen">📈 DeFi</option>
                    <option value="Culture">✨ Culture</option>
                    <option value="Actions">🚀 Actions</option>
                  </select>
                </div>
              </div>
              
              {/* Row 2: Description */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>DESCRIPTION (optional)</div>
                <input
                  type="text"
                  value={launchForm.description}
                  onChange={(e) => setLaunchForm(prev => ({ ...prev, description: e.target.value.slice(0, 60) }))}
                  placeholder="The next 1000x gem..."
                  maxLength={60}
                  style={{ width: '100%', backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', padding: '8px 10px', color: '#fff', fontSize: '16px', outline: 'none' }}
                />
              </div>
              
              {/* Row 3: Initial Buy */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div style={{ color: '#666', fontSize: '9px' }}>INITIAL BUY * <span style={{ color: '#888' }}>(becomes your liquidity pool)</span></div>
                  <div style={{ color: gameWallet >= launchForm.initialBuyAmount ? '#00d26a' : '#ff3b69', fontSize: '10px' }}>Bal: {formatPB(gameWallet)}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                  {[
                    { value: 250000, label: '$250K', usd: '~5¢' },
                    { value: 1000000, label: '$1M', usd: '~20¢' },
                    { value: 2500000, label: '$2.5M', usd: '~50¢' },
                    { value: 5000000, label: '$5M', usd: '~$1' },
                    { value: 10000000, label: '$10M', usd: '~$2' },
                    { value: 25000000, label: '$25M', usd: '~$5' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setLaunchForm(prev => ({ ...prev, initialBuyAmount: opt.value }))}
                      disabled={gameWallet < opt.value}
                      style={{
                        padding: '8px 4px',
                        borderRadius: '6px',
                        border: launchForm.initialBuyAmount === opt.value ? '2px solid #f0b90b' : '1px solid #333',
                        backgroundColor: launchForm.initialBuyAmount === opt.value ? '#1a1508' : gameWallet < opt.value ? '#0a0a0a' : '#111',
                        cursor: gameWallet < opt.value ? 'not-allowed' : 'pointer',
                        color: gameWallet < opt.value ? '#444' : launchForm.initialBuyAmount === opt.value ? '#f0b90b' : '#888',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        opacity: gameWallet < opt.value ? 0.5 : 1,
                      }}
                    >
                      <div>{opt.label}</div>
                      <div style={{ fontSize: '9px', color: launchForm.initialBuyAmount === opt.value ? '#f0b90b' : '#555', fontWeight: 'normal' }}>{opt.usd}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Row 4: Supply */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ color: '#666', fontSize: '9px', marginBottom: '6px' }}>SUPPLY</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { value: '1000000', label: '1M' },
                    { value: '10000000', label: '10M' },
                    { value: '100000000', label: '100M' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setLaunchForm(prev => ({ ...prev, supply: opt.value }))}
                      style={{
                        flex: 1,
                        padding: '8px 0',
                        borderRadius: '6px',
                        border: launchForm.supply === opt.value ? '2px solid #ff8c00' : '1px solid #333',
                        backgroundColor: launchForm.supply === opt.value ? '#1a1005' : '#111',
                        cursor: 'pointer',
                        color: launchForm.supply === opt.value ? '#ff8c00' : '#666',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Row 5: Summary */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', backgroundColor: '#111', borderRadius: '6px', marginBottom: '14px' }}>
                <div>
                  <div style={{ color: '#666', fontSize: '9px' }}>BSV LAUNCH FEE</div>
                  <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>10¢ <span style={{ color: '#666', fontWeight: 'normal' }}>(500,000 sats)</span></div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#666', fontSize: '9px' }}>PB INITIAL BUY</div>
                  <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold' }}>{formatPB(launchForm.initialBuyAmount)}</div>
                </div>
              </div>
              
              {/* Launch Button */}
              <button
                onClick={async () => {
                  if (launchForm.ticker.length < 3) {
                    alert('Ticker must be at least 3 characters');
                    return;
                  }
                  if (gameWallet < launchForm.initialBuyAmount) {
                    alert(`Not enough Paper Bucks! Need ${formatPB(launchForm.initialBuyAmount)}`);
                    return;
                  }
                  if (!confirm(`Launch $${launchForm.ticker}?\n\n• 10¢ BSV launch fee (500,000 sats)\n• ${formatPB(launchForm.initialBuyAmount)} initial buy from Paper Bucks`)) {
                    return;
                  }
                  setLaunchingToken(true);
                  try {
                    const res = await fetch(`${API_URL}/api/token/launch`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                      body: JSON.stringify({
                        ticker: launchForm.ticker,
                        description: launchForm.description,
                        category: launchForm.category,
                        supply: parseInt(launchForm.supply),
                        initialBuyAmount: launchForm.initialBuyAmount,
                      }),
                    });
                    const data = await res.json();
                    if (res.ok && data.success) {
                      Sounds.buy();
                      setUserLaunchedTokens(prev => [...prev, data.token]);
                      setMyLaunchedTokens(prev => [...prev, data.token]);
                      setGameWallet(prev => prev - launchForm.initialBuyAmount);
                      
                      // 🔥 ADD POSITION so sell button works immediately!
                      const newPosition = {
                        id: 'pos_' + Date.now() + '_launch',
                        tokenId: data.token.id,
                        tokenTicker: data.token.ticker,
                        tokens: data.position?.tokens || (launchForm.initialBuyAmount / data.token.currentPrice),
                        size: data.position?.tokens || (launchForm.initialBuyAmount / data.token.currentPrice),
                        invested: launchForm.initialBuyAmount,
                        entry: data.token.currentPrice || data.token.initialPrice,
                        entryPrice: data.token.currentPrice || data.token.initialPrice,
                        status: 'active',
                        openedAt: Date.now(),
                        isUserLaunched: true,
                      };
                      setPositions(prev => [...prev, newPosition]);
                      
                      setShowLaunchToken(false);
                      setLaunchForm({ ticker: '', description: '', supply: '10000000', category: 'Meme Coins', initialBuyAmount: 250000 });
                      // Switch to User Tokens view so they can see their token
                      setTokenViewMode('user');
                      // Refresh user tokens from server
                      setTimeout(async () => {
                        try {
                          const refreshRes = await fetch(`${API_URL}/api/tokens/user`);
                          if (refreshRes.ok) {
                            const refreshData = await refreshRes.json();
                            setUserLaunchedTokens(refreshData.tokens || []);
                          }
                        } catch (e) { console.log('Refresh error:', e); }
                      }, 500);
                      alert(`🚀 $${data.token.ticker} launched!\n\nYou'll find it in BSV.pump tab.`);
                    } else {
                      alert(data.error || 'Launch failed');
                    }
                  } catch (err) {
                    alert('Error: ' + err.message);
                  }
                  setLaunchingToken(false);
                }}
                disabled={launchingToken || launchForm.ticker.length < 3}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: launchForm.ticker.length >= 3 ? '#ff8c00' : '#333',
                  color: launchForm.ticker.length >= 3 ? '#000' : '#666',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: launchForm.ticker.length >= 3 ? 'pointer' : 'not-allowed',
                }}
              >
                {launchingToken ? '⏳ Launching...' : `🚀 Launch $${launchForm.ticker || 'TOKEN'}`}
              </button>
              
              {/* Active Token Section (if user has one) */}
              {myLaunchedTokens.filter(t => t.state === 'active').length > 0 && (
                <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #1a1a2e' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ color: '#666', fontSize: '10px' }}>YOUR TOKEN</div>
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API_URL}/api/token/history`, { headers: { 'X-Session-Id': sessionId } });
                          setTokenHistory(await res.json());
                          setShowTokenHistory(true);
                        } catch (e) { console.error(e); }
                      }}
                      style={{ backgroundColor: 'transparent', border: '1px solid #333', color: '#666', fontSize: '9px', padding: '3px 6px', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      📊 History
                    </button>
                  </div>
                  {myLaunchedTokens.filter(t => t.state === 'active').slice(0, 1).map((token, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#111', borderRadius: '6px', padding: '10px' }}>
                      <div>
                        <span style={{ color: '#ff8c00', fontWeight: 'bold' }}>${token.ticker}</span>
                        <span style={{ color: '#00d26a', fontSize: '10px', marginLeft: '8px' }}>🟢 LIVE</span>
                      </div>
                      <button
                        onClick={() => setDeleteConfirmToken(token)}
                        style={{ backgroundColor: '#1a0808', border: '1px solid #ff3b6944', borderRadius: '4px', padding: '4px 8px', color: '#ff3b69', fontSize: '10px', cursor: 'pointer' }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 📋 BULK CREDIT MODAL */}
        {showBulkCredit && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.98)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1003, padding: '20px' }}>
            <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #00d26a', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ color: '#00d26a', margin: 0, fontSize: '18px' }}>📋 Bulk Credit Users</h2>
                <button onClick={() => setShowBulkCredit(false)} style={{ background: 'none', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer' }}>×</button>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Handles (one per line, with or without $)</label>
                <textarea 
                  value={bulkCreditHandles}
                  onChange={e => setBulkCreditHandles(e.target.value)}
                  placeholder="user1&#10;user2&#10;user3"
                  style={{ 
                    width: '100%', 
                    height: '150px', 
                    backgroundColor: '#111', 
                    border: '1px solid #333', 
                    borderRadius: '8px', 
                    padding: '12px', 
                    color: '#fff', 
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                />
                <div style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>
                  {bulkCreditHandles.split('\n').filter(h => h.trim()).length} handles entered
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Paper Bucks</label>
                  <input 
                    type="number" 
                    value={bulkCreditAmount}
                    onChange={e => setBulkCreditAmount(parseInt(e.target.value) || 0)}
                    style={{ width: '100%', backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', padding: '10px', color: '#fff', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Premium Days (0 = none)</label>
                  <input 
                    type="number" 
                    value={bulkCreditPremiumDays}
                    onChange={e => setBulkCreditPremiumDays(parseInt(e.target.value) || 0)}
                    style={{ width: '100%', backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', padding: '10px', color: '#fff', fontSize: '14px' }}
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888', fontSize: '12px', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={bulkCreditSkipExisting}
                    onChange={e => setBulkCreditSkipExisting(e.target.checked)}
                  />
                  Skip users who already have balance (avoid double-crediting)
                </label>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={async () => {
                    const handles = bulkCreditHandles.split('\n').map(h => h.trim()).filter(h => h);
                    if (handles.length === 0) { alert('No handles entered'); return; }
                    if (!confirm(`Credit ${handles.length} users with ${bulkCreditAmount.toLocaleString()} PB each${bulkCreditPremiumDays > 0 ? ` + ${bulkCreditPremiumDays} days premium` : ''}?`)) return;
                    try {
                      const res = await fetch(`${API_URL}/api/admin/bulk-credit`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                        body: JSON.stringify({ 
                          handles, 
                          amount: bulkCreditAmount, 
                          premiumDays: bulkCreditPremiumDays,
                          skipExisting: bulkCreditSkipExisting
                        })
                      });
                      const data = await res.json();
                      if (data.error) { alert('Error: ' + data.error); return; }
                      alert(`✅ Bulk Credit Complete!\n\n📊 Summary:\n• Credited: ${data.summary.credited}\n• Skipped: ${data.summary.skipped}\n• New users created: ${data.summary.created}\n• Premium granted: ${data.summary.premiumGranted}`);
                      setShowBulkCredit(false);
                      setBulkCreditHandles('');
                      fetchAdminDashboard();
                    } catch (e) { alert('Error: ' + e.message); }
                  }}
                  style={{ flex: 1, padding: '14px', backgroundColor: '#00d26a', border: 'none', borderRadius: '8px', color: '#000', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer' }}
                >
                  ✓ Credit {bulkCreditHandles.split('\n').filter(h => h.trim()).length} Users
                </button>
                <button
                  onClick={() => setShowBulkCredit(false)}
                  style={{ padding: '14px 20px', backgroundColor: 'transparent', border: '1px solid #333', borderRadius: '8px', color: '#888', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 💭 PENNY PING EXPANDED MODAL */}
        {showPennyThoughts && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.98)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1003, padding: '10px', overflowY: 'auto' }}>
            <div style={{ backgroundColor: '#08080c', border: '1px solid #6366f1', borderRadius: '12px', maxWidth: '600px', width: '98%', margin: '20px 0', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ 
                padding: '16px 20px', 
                borderBottom: '1px solid #1a1a2e',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'linear-gradient(90deg, #6366f111 0%, transparent 100%)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {thoughtsView !== 'menu' && (
                    <button
                      onClick={() => setThoughtsView('menu')}
                      style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: '18px', cursor: 'pointer', padding: '0' }}
                    >
                      ←
                    </button>
                  )}
                  <span style={{ fontSize: '28px' }}>🏓</span>
                  <div>
                    <h2 style={{ margin: 0, color: '#6366f1', fontSize: '18px' }}>Penny Ping</h2>
                    <div style={{ color: '#666', fontSize: '11px' }}>Ping the world • On-chain forever</div>
                  </div>
                </div>
                <button 
                  onClick={() => { setShowPennyThoughts(false); setThoughtsView('menu'); }}
                  style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: '50%', 
                    border: '1px solid #333', 
                    backgroundColor: '#1a1a2e', 
                    color: '#888', 
                    fontSize: '18px', 
                    cursor: 'pointer' 
                  }}
                >
                  ×
                </button>
              </div>
              
              {/* MENU VIEW */}
              {thoughtsView === 'menu' && (
                <div style={{ padding: '20px' }}>
                  {/* Option 1: Browse Feed */}
                  <button
                    onClick={() => setThoughtsView('feed')}
                    style={{
                      width: '100%',
                      padding: '20px',
                      marginBottom: '12px',
                      backgroundColor: '#1a1a2e',
                      border: '1px solid #333',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.backgroundColor = '#1a1a3e'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.backgroundColor = '#1a1a2e'; }}
                  >
                    <span style={{ fontSize: '32px' }}>🌍</span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>Browse Feed</div>
                      <div style={{ color: '#888', fontSize: '12px' }}>See what the world is pinging</div>
                    </div>
                    <span style={{ marginLeft: 'auto', color: '#666', fontSize: '12px' }}>
                      {gameThoughts.length + worldThoughts.length} pings
                    </span>
                  </button>
                  
                  {/* Option 2: My Chain */}
                  <button
                    onClick={() => {
                      if (handcashUser?.handle) {
                        loadProfile(handcashUser.handle);
                        setShowPennyThoughts(false);
                        setThoughtsView('menu');
                      } else {
                        alert('Connect HandCash to view your chain');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '20px',
                      marginBottom: '12px',
                      backgroundColor: '#1a1a2e',
                      border: '1px solid #333',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.backgroundColor = '#1a1a3e'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.backgroundColor = '#1a1a2e'; }}
                  >
                    <span style={{ fontSize: '32px' }}>👤</span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>My Chain</div>
                      <div style={{ color: '#888', fontSize: '12px' }}>View your ping history</div>
                    </div>
                    {handcashUser && (
                      <span style={{ marginLeft: 'auto', color: '#6366f1', fontSize: '12px' }}>
                        {[...gameThoughts, ...worldThoughts].filter(t => 
                          t.handle?.toLowerCase().replace(/^\$/, '') === handcashUser.handle?.toLowerCase().replace(/^\$/, '')
                        ).length} pings
                      </span>
                    )}
                  </button>
                  
                  {/* Option 3: Ping or Dethrone */}
                  <button
                    onClick={() => setThoughtsView('feed')}
                    style={{
                      width: '100%',
                      padding: '20px',
                      marginBottom: '12px',
                      backgroundColor: '#6366f1',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#5558e3'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#6366f1'}
                  >
                    <span style={{ fontSize: '32px' }}>🏓</span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>Ping the World</div>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>1¢ to ping • 2¢ to dethrone 👑</div>
                    </div>
                    <span style={{ marginLeft: 'auto', color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>
                      →
                    </span>
                  </button>
                  
                  {/* Tutorial */}
                  <div style={{ 
                    marginTop: '20px',
                    padding: '20px',
                    backgroundColor: '#0a0a10',
                    borderRadius: '12px',
                    border: '1px solid #6366f133'
                  }}>
                    <div style={{ color: '#6366f1', fontSize: '14px', fontWeight: 'bold', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>📖</span> How Penny Ping Works
                    </div>
                    
                    {/* Two buttons explained */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '12px', backgroundColor: '#6366f122', borderRadius: '8px', border: '1px solid #6366f144' }}>
                        <div style={{ fontSize: '20px', marginBottom: '6px' }}>🏓</div>
                        <div style={{ color: '#6366f1', fontWeight: 'bold', fontSize: '13px' }}>Ping = 1¢</div>
                        <div style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>Add to feed. Build your chain. Everyone sees it in the stream.</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f0b90b22', borderRadius: '8px', border: '1px solid #f0b90b44' }}>
                        <div style={{ fontSize: '20px', marginBottom: '6px' }}>👑</div>
                        <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '13px' }}>Dethrone = 2¢</div>
                        <div style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>Take the throne! Featured at the top. Previous king gets 1¢!</div>
                      </div>
                    </div>
                    
                    {/* The Chain */}
                    <div style={{ padding: '12px', backgroundColor: '#00000033', borderRadius: '8px', marginBottom: '12px' }}>
                      <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '12px', marginBottom: '6px' }}>⛓️ Your Chain = Your Identity</div>
                      <div style={{ color: '#888', fontSize: '11px', lineHeight: '1.5' }}>
                        Every ping is inscribed on Bitcoin via <strong style={{ color: '#00d26a' }}>OP_RETURN</strong>. Each ping contains a link to your previous ping's transaction ID. Your <strong style={{ color: '#6366f1' }}>first ping</strong> is your <strong style={{ color: '#6366f1' }}>genesis</strong>. Anyone can verify your entire chain on WhatsOnChain - no trust required.
                      </div>
                    </div>
                    
                    {/* Dethrone mechanic */}
                    <div style={{ padding: '12px', backgroundColor: '#f0b90b11', borderRadius: '8px', border: '1px solid #f0b90b33' }}>
                      <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '12px', marginBottom: '6px' }}>💰 Dethrone Economics</div>
                      <div style={{ color: '#888', fontSize: '11px', lineHeight: '1.5' }}>
                        When you dethrone someone:<br/>
                        • <strong style={{ color: '#00d26a' }}>1¢ goes to the dethroned king</strong> (they get paid!)<br/>
                        • 1¢ goes to the house<br/>
                        • You become the new throne holder
                      </div>
                    </div>
                    
                    {/* Two feeds - clickable */}
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', fontSize: '11px' }}>
                      <button 
                        onClick={() => { setThoughtFeed('game'); setThoughtsView('feed'); }}
                        style={{ flex: 1, padding: '10px 8px', backgroundColor: '#1a1a2e', borderRadius: '6px', textAlign: 'center', border: '1px solid #333', cursor: 'pointer', transition: 'all 0.2s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.backgroundColor = '#1a1a3e'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.backgroundColor = '#1a1a2e'; }}
                      >
                        <span style={{ marginRight: '4px' }}>🎮</span>
                        <span style={{ color: '#888' }}>Game Feed</span>
                      </button>
                      <button 
                        onClick={() => { setThoughtFeed('world'); setThoughtsView('feed'); }}
                        style={{ flex: 1, padding: '10px 8px', backgroundColor: '#1a1a2e', borderRadius: '6px', textAlign: 'center', border: '1px solid #333', cursor: 'pointer', transition: 'all 0.2s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.backgroundColor = '#1a1a3e'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.backgroundColor = '#1a1a2e'; }}
                      >
                        <span style={{ marginRight: '4px' }}>🌍</span>
                        <span style={{ color: '#888' }}>World Feed</span>
                      </button>
                    </div>
                    
                    {/* Chain Decoder Button */}
                    <button
                      onClick={() => setShowChainDecoder(true)}
                      style={{
                        marginTop: '12px',
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #00d26a44',
                        backgroundColor: '#0a1a0a',
                        color: '#00d26a',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#0a2a0a'; e.currentTarget.style.borderColor = '#00d26a'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#0a1a0a'; e.currentTarget.style.borderColor = '#00d26a44'; }}
                    >
                      <span>🔍</span> VERIFY ANY CHAIN - Public Decoder
                    </button>
                  </div>
                </div>
              )}
              
              {/* FEED VIEW */}
              {thoughtsView === 'feed' && (
                <>
                  {/* Feed tabs */}
                  <div style={{ display: 'flex', borderBottom: '1px solid #1a1a2e' }}>
                    <button 
                      onClick={() => setThoughtFeed('game')}
                      style={{ 
                        flex: 1, 
                        padding: '12px', 
                        border: 'none',
                        backgroundColor: thoughtFeed === 'game' ? '#6366f111' : 'transparent',
                        borderBottom: thoughtFeed === 'game' ? '2px solid #6366f1' : '2px solid transparent',
                        color: thoughtFeed === 'game' ? '#6366f1' : '#666',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      🎮 Game Feed ({gameThoughts.length})
                    </button>
                    <button 
                      onClick={() => setThoughtFeed('world')}
                      style={{ 
                        flex: 1, 
                        padding: '12px', 
                        border: 'none',
                        backgroundColor: thoughtFeed === 'world' ? '#6366f111' : 'transparent',
                        borderBottom: thoughtFeed === 'world' ? '2px solid #6366f1' : '2px solid transparent',
                        color: thoughtFeed === 'world' ? '#6366f1' : '#666',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      🌍 World Feed ({worldThoughts.length})
                    </button>
                  </div>
              
              {/* Post input */}
              {handcashUser && (
                <div style={{ 
                  padding: '14px 20px', 
                  borderBottom: '1px solid #1a1a2e',
                  backgroundColor: '#0a0a10'
                }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <textarea
                        value={newThought}
                        onChange={(e) => setNewThought(e.target.value.slice(0, 280))}
                        placeholder={thoughtFeed === 'game' ? "What's the play? 🎮" : "Ping the world... 🌍"}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '12px',
                          border: '1px solid #333',
                          backgroundColor: '#0a0a0f',
                          color: '#fff',
                          fontSize: '14px',
                          outline: 'none',
                          resize: 'none',
                          minHeight: '60px',
                          fontFamily: 'inherit'
                        }}
                        maxLength={280}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', alignItems: 'center' }}>
                        <span style={{ color: newThought.length > 250 ? '#ff8c00' : '#444', fontSize: '11px' }}>
                          {newThought.length}/280
                        </span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => postPing(false)}
                            disabled={postingThought || !newThought.trim()}
                            style={{
                              padding: '10px 16px',
                              borderRadius: '20px',
                              border: 'none',
                              backgroundColor: postingThought || !newThought.trim() ? '#333' : '#6366f1',
                              color: '#fff',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              cursor: postingThought || !newThought.trim() ? 'not-allowed' : 'pointer'
                            }}
                          >
                            🏓 Ping 1¢
                          </button>
                          <button
                            onClick={() => postPing(true)}
                            disabled={postingThought || !newThought.trim()}
                            style={{
                              padding: '10px 16px',
                              borderRadius: '20px',
                              border: 'none',
                              backgroundColor: postingThought || !newThought.trim() ? '#333' : '#f0b90b',
                              color: '#000',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              cursor: postingThought || !newThought.trim() ? 'not-allowed' : 'pointer'
                            }}
                          >
                            👑 Dethrone 2¢
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* 👑 THE THRONE */}
              {(() => {
                const throne = thoughtFeed === 'world' ? worldThrone : gameThrone;
                if (throne) {
                  return (
                    <div style={{
                      margin: '12px',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #f0b90b22 0%, #0a0a15 100%)',
                      borderRadius: '12px',
                      border: '2px solid #f0b90b44'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '18px' }}>👑</span>
                        <span style={{ color: '#f0b90b', fontSize: '11px', fontWeight: 'bold' }}>THE THRONE</span>
                        <span style={{ color: '#666', fontSize: '10px', marginLeft: 'auto' }}>2¢ to dethrone</span>
                      </div>
                      <div style={{ color: '#fff', fontSize: '15px', lineHeight: '1.4', marginBottom: '10px', wordBreak: 'break-word' }}>
                        "{throne.thought}"
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', flexWrap: 'wrap' }}>
                        <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>${throne.handle}</span>
                        {throne.txId && (
                          <a href={`https://whatsonchain.com/tx/${throne.txId}`} target="_blank" rel="noopener noreferrer" style={{ color: '#00d26a', textDecoration: 'none' }}>
                            ⛓️ on-chain
                          </a>
                        )}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div style={{
                      margin: '12px',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #33333322 0%, #0a0a15 100%)',
                      borderRadius: '12px',
                      border: '2px dashed #333',
                      textAlign: 'center'
                    }}>
                      <span style={{ fontSize: '24px' }}>👑</span>
                      <div style={{ color: '#666', fontSize: '12px', marginTop: '6px' }}>The throne is empty!</div>
                      <div style={{ color: '#f0b90b', fontSize: '11px', marginTop: '2px' }}>Pay 2¢ to claim it</div>
                    </div>
                  );
                }
              })()}
              
              {/* Pings list */}
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {(thoughtFeed === 'game' ? gameThoughts : worldThoughts).length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#444' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏓</div>
                    <div>No pings yet in this feed.</div>
                    <div style={{ fontSize: '12px', marginTop: '8px' }}>Be the first to ping!</div>
                  </div>
                ) : (
                  (thoughtFeed === 'game' ? gameThoughts : worldThoughts).map((t, i) => (
                    <div 
                      key={t.id || i}
                      style={{ 
                        padding: '14px 20px', 
                        borderBottom: '1px solid #1a1a2e',
                        backgroundColor: t.isThrone ? '#f0b90b11' : (i === 0 ? '#6366f108' : 'transparent'),
                        borderLeft: t.isThrone ? '3px solid #f0b90b' : 'none'
                      }}
                    >
                      <div style={{ display: 'flex', gap: '12px' }}>
                        {/* Avatar */}
                        <div 
                          onClick={() => loadProfile(t.handle)}
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '50%', 
                            overflow: 'hidden',
                            backgroundColor: '#1a1a2e',
                            flexShrink: 0,
                            cursor: 'pointer',
                            border: t.isThrone ? '2px solid #f0b90b' : '2px solid #6366f133'
                          }}
                        >
                          <img 
                            src={t.avatarUrl || `https://cloud.handcash.io/v2/users/profilePicture/${t.handle?.replace(/^\$/, '')}`}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          {/* Dethrone badge */}
                          {t.isThrone && (
                            <div style={{ 
                              display: 'inline-block',
                              backgroundColor: '#f0b90b22',
                              color: '#f0b90b',
                              fontSize: '9px',
                              fontWeight: 'bold',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              marginBottom: '6px'
                            }}>
                              👑 DETHRONED (2¢)
                            </div>
                          )}
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px', 
                            lineHeight: '1.5',
                            marginBottom: '8px',
                            wordBreak: 'break-word'
                          }}>
                            {t.thought}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', flexWrap: 'wrap' }}>
                            <span 
                              onClick={() => loadProfile(t.handle)}
                              style={{ color: t.isThrone ? '#f0b90b' : '#6366f1', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                              ${t.handle?.replace(/^\$/, '')}
                            </span>
                            {t.isPremium && <span style={{ color: '#f0b90b' }}>👑</span>}
                            {t.chainPosition && (
                              <span style={{ color: '#444', fontSize: '9px' }}>⛓️{t.chainPosition}</span>
                            )}
                            <span style={{ color: '#444' }}>•</span>
                            <span style={{ color: '#666' }}>
                              {(() => {
                                if (!t.timestamp) return '';
                                const diff = Date.now() - t.timestamp;
                                if (diff < 60000) return 'just now';
                                if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                                if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
                                return `${Math.floor(diff / 86400000)}d ago`;
                              })()}
                            </span>
                            {t.txId && (
                              <>
                                <span style={{ color: '#444' }}>•</span>
                                <a 
                                  href={`https://whatsonchain.com/tx/${t.txId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#00d26a', textDecoration: 'none' }}
                                >
                                  ⛓️
                                </a>
                                <button
                                  onClick={() => copyThoughtLink(t)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#666',
                                    cursor: 'pointer',
                                    padding: '0',
                                    fontSize: '11px'
                                  }}
                                  title="Copy link"
                                >
                                  🔗
                                </button>
                              </>
                            )}
                            {/* 🗳️ VOTING */}
                            <span style={{ color: '#444' }}>•</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <button
                                onClick={() => voteOnPing(t.id, t.myVote === 'up' ? 'none' : 'up')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: t.myVote === 'up' ? '#00d26a' : '#666',
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  fontSize: '12px',
                                  borderRadius: '4px',
                                  transition: 'all 0.2s'
                                }}
                                title="Upvote"
                              >
                                ▲
                              </button>
                              <span style={{ 
                                color: (t.upvotes || 0) - (t.downvotes || 0) > 0 ? '#00d26a' : 
                                       (t.upvotes || 0) - (t.downvotes || 0) < 0 ? '#ff3b69' : '#666',
                                fontSize: '11px',
                                minWidth: '20px',
                                textAlign: 'center',
                                fontWeight: 'bold'
                              }}>
                                {(t.upvotes || 0) - (t.downvotes || 0)}
                              </span>
                              <button
                                onClick={() => voteOnPing(t.id, t.myVote === 'down' ? 'none' : 'down')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: t.myVote === 'down' ? '#ff3b69' : '#666',
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  fontSize: '12px',
                                  borderRadius: '4px',
                                  transition: 'all 0.2s'
                                }}
                                title="Downvote"
                              >
                                ▼
                              </button>
                              {/* 🐦 SHARE TO TWITTER */}
                              <button
                                onClick={() => {
                                  const pingText = t.thought || '';
                                  const author = t.handle?.replace(/^\$/, '') || 'someone';
                                  const isGameFeed = thoughtFeed === 'game';
                                  const tweetText = pingText 
                                    ? `"${pingText.slice(0, 180)}${pingText.length > 180 ? '...' : ''}"\n\n- $${author} ${isGameFeed ? '🎮 trading on Paper Hands' : '🌍 on Paper Hands World'}\n\n${isGameFeed ? '🎰 Learn to spot rugs before you get rugged:' : '💬 Join the conversation:'}`
                                    : `${isGameFeed ? '🎰 Learn to spot rugs before you get rugged:' : '🌍 Check out Paper Hands World:'}`;
                                  const url = 'https://paperhand.fun';
                                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(url)}`, '_blank');
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#1DA1F2',
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  fontSize: '11px',
                                  marginLeft: '6px'
                                }}
                                title="Share to Twitter"
                              >
                                𝕏
                              </button>
                              {/* 💰 TIP CREATOR */}
                              {t.handle?.replace(/^\$/, '') !== handcashUser?.handle?.replace(/^\$/, '') && (
                                <button
                                  onClick={async () => {
                                    const tipAmount = prompt(`Tip $${t.handle?.replace(/^\$/, '')} how many Paper Bucks?\n\nSuggested: 10,000 (1¢)`, '10000');
                                    if (!tipAmount) return;
                                    const amount = parseInt(tipAmount.replace(/,/g, ''));
                                    if (isNaN(amount) || amount <= 0) { alert('Invalid amount'); return; }
                                    if (amount > gameWallet) { alert('Insufficient balance'); return; }
                                    try {
                                      const res = await fetch(`${API_URL}/api/tip`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ toHandle: t.handle?.replace(/^\$/, ''), amount })
                                      });
                                      const data = await res.json();
                                      if (data.success) {
                                        setGameWallet(data.newBalance);
                                        Sounds.tip();
                                        alert(`💸 Tipped ${amount.toLocaleString()} PB to $${t.handle?.replace(/^\$/, '')}!`);
                                      } else {
                                        alert('Tip failed: ' + data.error);
                                      }
                                    } catch (e) {
                                      alert('Tip error: ' + e.message);
                                    }
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#f0b90b',
                                    cursor: 'pointer',
                                    padding: '2px 4px',
                                    fontSize: '11px'
                                  }}
                                  title={`Tip $${t.handle?.replace(/^\$/, '')}`}
                                >
                                  💰
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Footer stats */}
              <div style={{ 
                padding: '12px 20px', 
                borderTop: '1px solid #1a1a2e',
                backgroundColor: '#0a0a10',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '11px',
                color: '#666'
              }}>
                <span>Total: {(thoughtFeed === 'game' ? gameThoughts : worldThoughts).length} pings</span>
                <span>💰 Cost: 1¢ per ping</span>
              </div>
              
              {/* HOW IT WORKS */}
              <div style={{ 
                padding: '16px 20px', 
                borderTop: '1px solid #1a1a2e',
                backgroundColor: '#0a0a10'
              }}>
                <h3 style={{ color: '#6366f1', margin: '0 0 12px 0', fontSize: '14px' }}>💡 How Penny Ping Works</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', fontSize: '11px' }}>
                  <div style={{ padding: '10px', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
                    <div style={{ color: '#6366f1', fontWeight: 'bold', marginBottom: '4px' }}>🏓 Ping = 1¢</div>
                    <div style={{ color: '#888' }}>Add to the feed, continue your chain.</div>
                  </div>
                  <div style={{ padding: '10px', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
                    <div style={{ color: '#f0b90b', fontWeight: 'bold', marginBottom: '4px' }}>👑 Dethrone = 2¢</div>
                    <div style={{ color: '#888' }}>Take the throne, be seen first.</div>
                  </div>
                  <div style={{ padding: '10px', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
                    <div style={{ color: '#6366f1', fontWeight: 'bold', marginBottom: '4px' }}>On-Chain Forever</div>
                    <div style={{ color: '#888' }}>Every ping is a BSV transaction.</div>
                  </div>
                  <div style={{ padding: '10px', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
                    <div style={{ color: '#6366f1', fontWeight: 'bold', marginBottom: '4px' }}>Your Chain</div>
                    <div style={{ color: '#888' }}>OP_RETURN links each ping.</div>
                  </div>
                </div>
                <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#00d26a11', borderRadius: '8px', border: '1px solid #00d26a33' }}>
                  <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '12px' }}>⛓️ TRUE ON-CHAIN (OP_RETURN)</div>
                  <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                    Every ping inscribes your previousTxId on Bitcoin. Anyone can verify your chain on WhatsOnChain. No app needed - the blockchain IS the source of truth.
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        )}

        {/* 🔍 CHAIN DECODER MODAL (Global - accessible from modal) */}
        {showChainDecoder && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.95)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: "'Courier New', monospace"
          }}>
            <style>{`
              @keyframes blink {
                0%, 50% { opacity: 1; }
                51%, 100% { opacity: 0; }
              }
            `}</style>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #00d26a33',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(180deg, #0a1a0a 0%, #050508 100%)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>🔍</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', color: '#00d26a' }}>PENNY PING CHAIN DECODER</h2>
                  <div style={{ fontSize: '10px', color: '#00d26a88' }}>Verify any on-chain identity • No login required</div>
                </div>
              </div>
              <button
                onClick={() => { setShowChainDecoder(false); setDecoderLines([]); setDecoderInput(''); setDecoderChain([]); }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #333',
                  backgroundColor: '#1a1a1a',
                  color: '#888',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                ✕ Close
              </button>
            </div>
            
            {/* Input Section */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #1a1a1a' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  value={decoderInput}
                  onChange={(e) => setDecoderInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') decodeChain(); }}
                  placeholder="Paste WhatsOnChain URL or Transaction ID..."
                  style={{
                    flex: 1,
                    padding: '14px 16px',
                    borderRadius: '8px',
                    border: '1px solid #00d26a44',
                    backgroundColor: '#0a0a0f',
                    color: '#00d26a',
                    fontSize: '14px',
                    fontFamily: "'Courier New', monospace",
                    outline: 'none'
                  }}
                />
                <button
                  onClick={decodeChain}
                  disabled={decoderLoading || !decoderInput.trim()}
                  style={{
                    padding: '14px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: decoderLoading || !decoderInput.trim() ? '#1a1a1a' : '#00d26a',
                    color: decoderLoading || !decoderInput.trim() ? '#666' : '#000',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: decoderLoading || !decoderInput.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  {decoderLoading ? '⏳ DECODING...' : '▶ DECODE'}
                </button>
              </div>
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#666' }}>
                Example: https://whatsonchain.com/tx/abc123... or just the transaction ID
              </div>
            </div>
            
            {/* Terminal Output */}
            <div style={{
              flex: 1,
              padding: '16px 20px',
              overflowY: 'auto',
              backgroundColor: '#020204',
              position: 'relative'
            }}>
              {decoderLines.length === 0 ? (
                <div style={{ color: '#00d26a44', fontSize: '13px' }}>
                  <div style={{ marginBottom: '12px' }}>{'>'} Waiting for input...</div>
                  <div style={{ color: '#333', fontSize: '11px', lineHeight: '1.8' }}>
                    <div>┌─────────────────────────────────────────────────┐</div>
                    <div>│  PENNY PING PROTOCOL DECODER v1.0               │</div>
                    <div>├─────────────────────────────────────────────────┤</div>
                    <div>│  • Paste any WhatsOnChain transaction URL       │</div>
                    <div>│  • Decoder will fetch and parse OP_RETURN       │</div>
                    <div>│  • Follows previousTxId links to genesis        │</div>
                    <div>│  • Verifies complete on-chain identity          │</div>
                    <div>│                                                 │</div>
                    <div>│  No login required - 100% trustless             │</div>
                    <div>└─────────────────────────────────────────────────┘</div>
                  </div>
                </div>
              ) : (
                <div style={{ fontFamily: "'Courier New', monospace", fontSize: '12px', lineHeight: '1.6' }}>
                  {decoderLines.map((line, i) => (
                    <div 
                      key={i} 
                      style={{ 
                        color: line.startsWith('>')  ? '#00d26a' : 
                               line.startsWith('❌') ? '#ff3b69' : 
                               line.startsWith('✅') ? '#00d26a' :
                               line.startsWith('══') ? '#f0b90b' :
                               line.includes('GENESIS') ? '#f0b90b' :
                               line.includes('│') ? '#00d26a88' :
                               line.includes('┌') || line.includes('└') || line.includes('├') ? '#00d26a44' :
                               '#888',
                        whiteSpace: 'pre'
                      }}
                    >
                      {line}
                      {i === decoderLines.length - 1 && decoderLoading && <span style={{ animation: 'blink 1s infinite' }}>_</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Chain Summary */}
            {decoderChain.length > 0 && !decoderLoading && (
              <div style={{
                padding: '16px 20px',
                borderTop: '1px solid #00d26a33',
                backgroundColor: '#0a1a0a'
              }}>
                <div style={{ fontSize: '11px', color: '#00d26a', marginBottom: '8px', fontWeight: 'bold' }}>
                  CHAIN VISUALIZATION ({decoderChain.length} ping{decoderChain.length > 1 ? 's' : ''})
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
                  {[...decoderChain].reverse().map((item, i) => (
                    <React.Fragment key={item.txId}>
                      <a
                        href={`https://whatsonchain.com/tx/${item.txId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '8px 12px',
                          borderRadius: '8px',
                          backgroundColor: item.isGenesis ? '#1a1508' : item.data.type === 'dethrone' ? '#1a1508' : '#0a0a15',
                          border: `1px solid ${item.isGenesis ? '#f0b90b44' : item.data.type === 'dethrone' ? '#f0b90b66' : '#6366f144'}`,
                          textDecoration: 'none',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          minWidth: '80px'
                        }}
                      >
                        <div style={{ fontSize: '16px', marginBottom: '4px' }}>
                          {item.isGenesis ? '🌟' : item.data.type === 'dethrone' ? '👑' : '🏓'}
                        </div>
                        <div style={{ fontSize: '10px', color: item.isGenesis ? '#f0b90b' : item.data.type === 'dethrone' ? '#f0b90b' : '#6366f1', fontWeight: 'bold' }}>
                          {item.isGenesis ? 'GENESIS' : item.data.type === 'dethrone' ? 'DETHRONE' : `#${item.data.chainPosition || (decoderChain.length - i)}`}
                        </div>
                        <div style={{ fontSize: '9px', color: '#666' }}>
                          {item.txId.slice(0, 8)}...
                        </div>
                      </a>
                      {i < decoderChain.length - 1 && (
                        <div style={{ color: '#333', fontSize: '16px' }}>→</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 👤 USER PROFILE MODAL - View ping chain */}
        {viewingProfile && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.98)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1004, padding: '10px', overflowY: 'auto' }}>
            <div style={{ backgroundColor: '#08080c', border: '1px solid #6366f1', borderRadius: '12px', maxWidth: '500px', width: '98%', margin: '20px 0', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ 
                padding: '20px', 
                background: 'linear-gradient(135deg, #0a0820 0%, #1a0a30 100%)',
                borderBottom: '1px solid #1a1a2e',
                textAlign: 'center'
              }}>
                <button 
                  onClick={() => setViewingProfile(null)}
                  style={{ 
                    position: 'absolute',
                    right: '20px',
                    top: '20px',
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    border: '1px solid #333', 
                    backgroundColor: '#1a1a2e', 
                    color: '#888', 
                    fontSize: '16px', 
                    cursor: 'pointer' 
                  }}
                >
                  ×
                </button>
                
                {/* Avatar */}
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  overflow: 'hidden',
                  backgroundColor: '#1a1a2e',
                  margin: '0 auto 12px',
                  border: '3px solid #6366f1'
                }}>
                  <img 
                    src={viewingProfile.data?.avatarUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                
                <h2 style={{ color: '#fff', margin: '0 0 4px 0', fontSize: '20px' }}>
                  ${viewingProfile.handle?.replace(/^\$/, '')}
                </h2>
                
                {/* Chain Stats */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#6366f1', fontSize: '20px', fontWeight: 'bold' }}>
                      {viewingProfile.data?.chain?.totalThoughts || 0}
                    </div>
                    <div style={{ color: '#666', fontSize: '10px' }}>Pings</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#00d26a', fontSize: '20px', fontWeight: 'bold' }}>
                      ⛓️
                    </div>
                    <div style={{ color: '#666', fontSize: '10px' }}>On-Chain</div>
                  </div>
                </div>
                
                {/* Genesis Link */}
                {viewingProfile.data?.chain?.firstTxId && (
                  <a 
                    href={viewingProfile.data.chain.genesisLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'inline-block',
                      marginTop: '12px',
                      padding: '6px 12px',
                      backgroundColor: '#6366f111',
                      border: '1px solid #6366f133',
                      borderRadius: '20px',
                      color: '#6366f1',
                      fontSize: '10px',
                      textDecoration: 'none'
                    }}
                  >
                    🔗 View Genesis Ping
                  </a>
                )}
              </div>
              
              {/* Pings List */}
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {profileLoading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    Loading...
                  </div>
                ) : viewingProfile.data?.thoughts?.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    No pings yet
                  </div>
                ) : (
                  viewingProfile.data?.thoughts?.map((t, i) => (
                    <div 
                      key={t.id || i}
                      style={{ 
                        padding: '12px 20px', 
                        borderBottom: '1px solid #1a1a2e'
                      }}
                    >
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '13px', 
                        lineHeight: '1.4',
                        marginBottom: '6px'
                      }}>
                        {t.thought}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px' }}>
                        <span style={{ color: t.feed === 'world' ? '#00d26a' : '#f0b90b' }}>
                          {t.feed === 'world' ? '🌍' : '🎮'}
                        </span>
                        {t.chainPosition && (
                          <span style={{ color: '#6366f1' }}>#{t.chainPosition}</span>
                        )}
                        <span style={{ color: '#666' }}>
                          {(() => {
                            if (!t.timestamp) return '';
                            const diff = Date.now() - t.timestamp;
                            if (diff < 60000) return 'just now';
                            if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                            if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
                            return `${Math.floor(diff / 86400000)}d ago`;
                          })()}
                        </span>
                        {t.txId && (
                          <>
                            <a 
                              href={`https://whatsonchain.com/tx/${t.txId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#00d26a', textDecoration: 'none' }}
                            >
                              ⛓️
                            </a>
                            <button
                              onClick={() => copyThoughtLink(t)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#666',
                                cursor: 'pointer',
                                padding: '0',
                                fontSize: '10px'
                              }}
                            >
                              🔗
                            </button>
                          </>
                        )}
                        {/* 🗳️ VOTING */}
                        <span style={{ color: '#444' }}>•</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <button
                            onClick={() => voteOnPing(t.id, t.myVote === 'up' ? 'none' : 'up')}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: t.myVote === 'up' ? '#00d26a' : '#666',
                              cursor: 'pointer',
                              padding: '2px',
                              fontSize: '10px'
                            }}
                          >
                            ▲
                          </button>
                          <span style={{ 
                            color: (t.upvotes || 0) - (t.downvotes || 0) > 0 ? '#00d26a' : 
                                   (t.upvotes || 0) - (t.downvotes || 0) < 0 ? '#ff3b69' : '#666',
                            fontSize: '10px',
                            minWidth: '16px',
                            textAlign: 'center'
                          }}>
                            {(t.upvotes || 0) - (t.downvotes || 0)}
                          </span>
                          <button
                            onClick={() => voteOnPing(t.id, t.myVote === 'down' ? 'none' : 'down')}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: t.myVote === 'down' ? '#ff3b69' : '#666',
                              cursor: 'pointer',
                              padding: '2px',
                              fontSize: '10px'
                            }}
                          >
                            ▼
                          </button>
                          {/* 🐦 SHARE TO TWITTER */}
                          <button
                            onClick={() => {
                              const pingText = t.thought || '';
                              const author = t.handle?.replace(/^\$/, '') || 'someone';
                              const isGameFeed = t.feed !== 'world';
                              const tweetText = pingText 
                                ? `"${pingText.slice(0, 180)}${pingText.length > 180 ? '...' : ''}"\n\n- $${author} ${isGameFeed ? '🎮 trading on Paper Hands' : '🌍 on Paper Hands World'}\n\n${isGameFeed ? '🎰 Learn to spot rugs before you get rugged:' : '💬 Join the conversation:'}`
                                : `${isGameFeed ? '🎰 Learn to spot rugs before you get rugged:' : '🌍 Check out Paper Hands World:'}`;
                              const url = 'https://paperhand.fun';
                              window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(url)}`, '_blank');
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#1DA1F2',
                              cursor: 'pointer',
                              padding: '2px',
                              fontSize: '10px',
                              marginLeft: '4px'
                            }}
                            title="Share to Twitter"
                          >
                            𝕏
                          </button>
                          {/* 💰 TIP CREATOR */}
                          {t.handle?.replace(/^\$/, '') !== handcashUser?.handle?.replace(/^\$/, '') && (
                            <button
                              onClick={async () => {
                                const tipAmount = prompt(`Tip $${t.handle?.replace(/^\$/, '')} how many Paper Bucks?\n\nSuggested: 10,000 (1¢)`, '10000');
                                if (!tipAmount) return;
                                const amount = parseInt(tipAmount.replace(/,/g, ''));
                                if (isNaN(amount) || amount <= 0) { alert('Invalid amount'); return; }
                                if (amount > gameWallet) { alert('Insufficient balance'); return; }
                                try {
                                  const res = await fetch(`${API_URL}/api/tip`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                    body: JSON.stringify({ toHandle: t.handle?.replace(/^\$/, ''), amount })
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    setGameWallet(data.newBalance);
                                    Sounds.tip();
                                    alert(`💸 Tipped ${amount.toLocaleString()} PB to $${t.handle?.replace(/^\$/, '')}!`);
                                  } else {
                                    alert('Tip failed: ' + data.error);
                                  }
                                } catch (e) {
                                  alert('Tip error: ' + e.message);
                                }
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#f0b90b',
                                cursor: 'pointer',
                                padding: '2px',
                                fontSize: '10px'
                              }}
                              title={`Tip $${t.handle?.replace(/^\$/, '')}`}
                            >
                              💰
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Footer */}
              <div style={{ 
                padding: '12px 20px', 
                borderTop: '1px solid #1a1a2e',
                backgroundColor: '#0a0a10',
                textAlign: 'center',
                fontSize: '10px',
                color: '#666'
              }}>
                Identity = Transaction Chain • Provable • Immutable • Forever
              </div>
            </div>
          </div>
        )}

        {/* 🛡️ ADMIN DASHBOARD MODAL */}
        {showAdminDashboard && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.98)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1002, padding: '10px', overflowY: 'auto' }}>
            <div style={{ backgroundColor: '#08080c', border: '1px solid #f0b90b', borderRadius: '12px', maxWidth: '1400px', width: '98%', margin: '10px 0', overflow: 'hidden' }}>
              {/* Compact Header Bar */}
              <div style={{
                background: adminData?.userRole === 'moderator' ? 'linear-gradient(90deg, #0a0a0f 0%, #0a0820 50%, #0a0a0f 100%)' : 'linear-gradient(90deg, #0a0a0f 0%, #1a1508 50%, #0a0a0f 100%)',
                borderBottom: `1px solid ${adminData?.userRole === 'moderator' ? '#6366f133' : '#f0b90b33'}`,
                padding: '10px 16px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px'
              }}>
                {/* Left: Title + Live Stats */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <h2 style={{ color: adminData?.userRole === 'moderator' ? '#6366f1' : '#f0b90b', margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {adminData?.userRole === 'moderator' ? '👁️' : '🛡️'} 
                    <span style={{ background: adminData?.userRole === 'moderator' ? 'linear-gradient(90deg, #6366f1, #8b5cf6)' : 'linear-gradient(90deg, #f0b90b, #ffd700)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                      {adminData?.userRole === 'moderator' ? 'MODERATOR' : 'ADMIN'}
                    </span>
                    {adminData?.userRole === 'moderator' && <span style={{ fontSize: '10px', color: '#666' }}>(view only)</span>}
                  </h2>
                  {/* Quick Stats Pills */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ backgroundColor: '#00d26a22', border: '1px solid #00d26a44', borderRadius: '12px', padding: '2px 10px', fontSize: '10px' }}>
                      <span style={{ color: '#00d26a' }}>●</span> <span style={{ color: '#888' }}>{adminData?.activeSessions?.length || 0} online</span>
                    </div>
                    <div style={{ backgroundColor: '#f0b90b22', border: '1px solid #f0b90b44', borderRadius: '12px', padding: '2px 10px', fontSize: '10px' }}>
                      <span style={{ color: '#888' }}>BSV</span> <span style={{ color: '#f0b90b' }}>${bsvPrice?.toFixed(2) || '?'}</span>
                    </div>
                    {pendingWithdrawals?.length > 0 && adminData?.userRole !== 'moderator' && (
                      <div style={{ backgroundColor: '#ff8c0022', border: '1px solid #ff8c0044', borderRadius: '12px', padding: '2px 10px', fontSize: '10px', cursor: 'pointer' }} onClick={fetchPendingWithdrawals}>
                        <span style={{ color: '#ff8c00' }}>💸 {pendingWithdrawals.length} pending withdrawal{pendingWithdrawals.length > 1 ? 's' : ''}</span>
                      </div>
                    )}
                    {adminData?.suspicious?.length > 0 && (
                      <div style={{ backgroundColor: '#ff3b6922', border: '1px solid #ff3b6944', borderRadius: '12px', padding: '2px 10px', fontSize: '10px' }}>
                        <span style={{ color: '#ff3b69' }}>⚠ {adminData.suspicious.length} alerts</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Actions */}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <button
                    onClick={async () => {
                      const confirm1 = window.confirm('⚠️ NUCLEAR OPTION ⚠️\n\nThis will:\n• Close ALL tokens (AI + PvP)\n• Refund ALL player positions as Paper Bucks\n• Clear ALL bot positions\n\nAre you sure?');
                      if (!confirm1) return;
                      const confirm2 = window.prompt('Type REFUND_ALL_PLAYERS to confirm:');
                      if (confirm2 !== 'REFUND_ALL_PLAYERS') { alert('Cancelled'); return; }
                      try {
                        const res = await fetch(`${API_URL}/api/admin/refund-all`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                          body: JSON.stringify({ confirmReset: 'REFUND_ALL_PLAYERS' })
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else { alert(`✅ Refunded ${data.positionsClosed} positions, closed ${data.tokensClosed} tokens`); fetchAdminDashboard(); }
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ backgroundColor: '#2a0a0a', border: '1px solid #ff3b69', borderRadius: '6px', padding: '5px 10px', color: '#ff3b69', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' }}
                  >☢️ REFUND ALL</button>
                  {/* HALT TRADING */}
                  <button
                    onClick={async () => {
                      if (!window.confirm('🛑 HALT all trading? Players cannot buy/sell until resumed.')) return;
                      try {
                        const res = await fetch(`${API_URL}/api/admin/halt-trading`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                          body: JSON.stringify({ halted: true })
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else { alert('🛑 Trading HALTED'); fetchAdminDashboard(); }
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ 
                      backgroundColor: '#2a0a0a', 
                      border: '1px solid #ff3b69', 
                      borderRadius: '6px', padding: '5px 10px', 
                      color: '#ff3b69', 
                      cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' 
                    }}
                  >⏸️ HALT</button>
                  {/* RESUME TRADING */}
                  <button
                    onClick={async () => {
                      if (!window.confirm('✅ RESUME trading for all players?')) return;
                      try {
                        const res = await fetch(`${API_URL}/api/admin/halt-trading`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                          body: JSON.stringify({ halted: false })
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else { alert('✅ Trading RESUMED'); fetchAdminDashboard(); }
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ 
                      backgroundColor: '#0a2a0a', 
                      border: '1px solid #00d26a', 
                      borderRadius: '6px', padding: '5px 10px', 
                      color: '#00d26a', 
                      cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' 
                    }}
                  >▶️ RESUME</button>
                  {/* KICK ALL SESSIONS */}
                  <button
                    onClick={async () => {
                      if (!window.confirm('🔴 KICK ALL PLAYERS?\n\nThis will clear all sessions and force everyone to re-login. Use before deploying updates.')) return;
                      try {
                        const res = await fetch(`${API_URL}/api/admin/kick-all`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId }
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else { alert(`🔴 Kicked ${data.sessionsCleared} sessions`); fetchAdminDashboard(); }
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ 
                      backgroundColor: '#2a0a1a', 
                      border: '1px solid #f0b90b', 
                      borderRadius: '6px', padding: '5px 10px', 
                      color: '#f0b90b', 
                      cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' 
                    }}
                  >🔴 KICK ALL</button>
                  {/* CREDIT USER */}
                  <button
                    onClick={async () => {
                      const handle = window.prompt('Enter handle to credit (without $):');
                      if (!handle) return;
                      const amount = window.prompt('Enter PaperBucks amount to credit:');
                      if (!amount || isNaN(parseInt(amount))) return;
                      const reason = window.prompt('Reason for credit (optional):') || '';
                      try {
                        const res = await fetch(`${API_URL}/api/admin/credit-user`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                          body: JSON.stringify({ handle: handle.toLowerCase(), amount: parseInt(amount), reason })
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else alert(`✅ ${data.isNewUser ? 'Created & ' : ''} Credited $${handle} with ${parseInt(amount).toLocaleString()} PB\nNew balance: ${data.newBalance.toLocaleString()}${data.isNewUser ? '\n\n👤 New user created! Balance will be waiting when they log in.' : ''}`);
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ backgroundColor: '#0a1a2a', border: '1px solid #3498db', borderRadius: '6px', padding: '5px 10px', color: '#3498db', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' }}
                  >💰 CREDIT</button>
                  {/* CREDIT ALL */}
                  <button
                    onClick={async () => {
                      const amount = window.prompt('Credit ALL players with how many PaperBucks?');
                      if (!amount || isNaN(parseInt(amount))) return;
                      const confirm1 = window.confirm(`⚠️ Credit ALL players with ${parseInt(amount).toLocaleString()} PB?\n\nThis affects everyone!`);
                      if (!confirm1) return;
                      const reason = window.prompt('Reason (shown to players):') || 'Bonus from Paper Hands';
                      try {
                        const res = await fetch(`${API_URL}/api/admin/credit-all`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                          body: JSON.stringify({ amount: parseInt(amount), reason })
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else alert(`✅ Credited ${data.credited} players with ${parseInt(amount).toLocaleString()} PB each!`);
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ backgroundColor: '#1a0a2a', border: '1px solid #a855f7', borderRadius: '6px', padding: '5px 10px', color: '#a855f7', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' }}
                  >💸 CREDIT ALL</button>
                  {/* BULK CREDIT - Import list of users */}
                  <button
                    onClick={() => setShowBulkCredit(true)}
                    style={{ backgroundColor: '#0a2a1a', border: '1px solid #00d26a', borderRadius: '6px', padding: '5px 10px', color: '#00d26a', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' }}
                  >📋 BULK</button>
                  {/* GRANT PREMIUM */}
                  <button
                    onClick={async () => {
                      const handle = window.prompt('Enter handle to grant premium (without $):');
                      if (!handle) return;
                      const days = window.prompt('How many days of premium?', '7');
                      if (!days || isNaN(parseInt(days))) return;
                      try {
                        const res = await fetch(`${API_URL}/api/admin/grant-premium`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                          body: JSON.stringify({ handle: handle.toLowerCase(), days: parseInt(days) })
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else alert(`✅ Granted ${data.days} days premium to ${data.handle}\nExpires: ${data.expiresFormatted}`);
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ backgroundColor: '#2a1a0a', border: '1px solid #f0b90b', borderRadius: '6px', padding: '5px 10px', color: '#f0b90b', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' }}
                  >👑 PREMIUM</button>
                  {/* FORCE SAVE TO REDIS */}
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/admin/force-save`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId }
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else alert(`✅ Saved to Redis!\n\n${data.saved}/${data.total} data sets\n\n👥 ${data.users} users\n🪙 ${data.tokens} tokens\n📒 ${data.ledger} ledger entries\n🏓 ${data.pennyPings} pings\n💸 ${data.pendingWithdrawals} pending withdrawals\n👁️ ${data.moderators} moderators\n🎯 Aggression: ${data.aggression}`);
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ backgroundColor: '#0a0a1a', border: '1px solid #e74c3c', borderRadius: '6px', padding: '5px 10px', color: '#e74c3c', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' }}
                  >💾 SAVE DB</button>
                  {/* WIPE PENNY PING */}
                  <button
                    onClick={async () => {
                      const confirm1 = window.confirm('🗑️ WIPE PENNY PING\\n\\nThis will delete:\\n• All pings (game + world)\\n• All user chains\\n• Both thrones\\n\\nAre you sure?');
                      if (!confirm1) return;
                      const confirm2 = window.prompt('Type WIPE_PINGS to confirm:');
                      if (confirm2 !== 'WIPE_PINGS') { alert('Cancelled'); return; }
                      try {
                        const res = await fetch(`${API_URL}/api/admin/wipe-penny-ping`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId }
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else alert(`✅ Penny Ping Wiped!\\n\\n${data.wiped.gamePingsWiped} game pings\\n${data.wiped.worldPingsWiped} world pings\\n${data.wiped.chainsWiped} chains`);
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ backgroundColor: '#2a0a2a', border: '1px solid #a855f7', borderRadius: '6px', padding: '5px 10px', color: '#a855f7', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' }}
                  >🏓 WIPE PINGS</button>
                  <button
                    onClick={async () => {
                      if (!confirm('Fix all users who were credited but missing totalDeposited?')) return;
                      try {
                        const res = await fetch(`${API_URL}/api/admin/fix-credits`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId }
                        });
                        const data = await res.json();
                        if (data.error) alert('Error: ' + data.error);
                        else alert(`✅ Fixed ${data.fixed} users!`);
                      } catch (e) { alert('Error: ' + e.message); }
                    }}
                    style={{ backgroundColor: '#1a1a0a', border: '1px solid #f0b90b', borderRadius: '6px', padding: '5px 10px', color: '#f0b90b', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' }}
                  >🔧 FIX</button>
                  <button onClick={fetchAdminDashboard} style={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '6px', padding: '5px 10px', color: '#888', cursor: 'pointer', fontSize: '12px' }}>🔄</button>
                  <button onClick={() => setShowAdminDashboard(false)} style={{ backgroundColor: 'transparent', border: '1px solid #ff3b6944', borderRadius: '6px', padding: '5px 10px', color: '#ff3b69', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                </div>
              </div>

              {/* Tabs Bar */}
              <div style={{
                backgroundColor: '#0a0a0f',
                borderBottom: '1px solid #222',
                padding: '0 12px',
                display: 'flex', gap: '2px', overflowX: 'auto'
              }}>
                {[
                  { id: 'overview', icon: '📊', label: 'Overview', adminOnly: true },
                  { id: 'live', icon: '⚡', label: 'Live' },
                  { id: 'mountain', icon: '🏔️', label: 'Last Degen', adminOnly: true },
                  { id: 'rugrush', icon: '🎰', label: 'Rug Rush', adminOnly: true },
                  { id: 'users', icon: '👥', label: 'Users' },
                  { id: 'consent', icon: '✅', label: 'Consent', adminOnly: true },
                  { id: 'tokens', icon: '🚀', label: 'Tokens' },
                  { id: 'bots', icon: '🤖', label: 'Bots' },
                  { id: 'withdrawals', icon: '💸', label: 'Withdrawals', alert: pendingWithdrawals?.length > 0, adminOnly: true },
                  { id: 'fraud', icon: '🚨', label: 'Fraud', alert: adminData?.fraudAlerts?.length > 0 || adminData?.suspicious?.length > 0 },
                  { id: 'watchlist', icon: '👁️', label: 'Watch' },
                  { id: 'exploits', icon: '🛡️', label: 'Anti-Exploit' },
                  { id: 'hunters', icon: '🎯', label: 'Hunters', adminOnly: true },
                  { id: 'wars', icon: '🏆', label: 'Wars', adminOnly: true },
                  { id: 'business', icon: '📈', label: 'Business', adminOnly: true },
                  { id: 'babysitting', icon: '👶', label: 'Baby Sitting', adminOnly: true },
                ].filter(tab => !tab.adminOnly || adminData?.userRole === 'admin').map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setAdminTab(tab.id)}
                    style={{
                      padding: '10px 14px',
                      backgroundColor: 'transparent',
                      color: adminTab === tab.id ? '#f0b90b' : '#666',
                      border: 'none',
                      borderBottom: adminTab === tab.id ? '2px solid #f0b90b' : '2px solid transparent',
                      cursor: 'pointer',
                      fontWeight: adminTab === tab.id ? 'bold' : 'normal',
                      fontSize: '11px',
                      display: 'flex', alignItems: 'center', gap: '4px',
                      transition: 'all 0.15s',
                      position: 'relative',
                    }}
                  >
                    <span>{tab.icon}</span> {tab.label}
                    {tab.alert && <span style={{ position: 'absolute', top: '6px', right: '4px', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ff3b69' }} />}
                  </button>
                ))}
              </div>

              {/* Content Area */}
              <div style={{ padding: '12px', maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' }}>
              
              {/* 🩺 SYSTEM HEALTH BAR - One glance to know everything */}
              {adminData && (
                <div style={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  marginBottom: '12px',
                }}>
                  {/* Health Indicators Row */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Left: System Status Dots */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {[
                        { 
                          label: 'Redis', 
                          ok: adminData.serverStatus?.redisLoaded, 
                          icon: '💾',
                          okText: 'Loaded',
                          badText: 'Loading...'
                        },
                        { 
                          label: 'Trading', 
                          ok: !adminData.tradingHalted, 
                          icon: '📈',
                          okText: 'Active',
                          badText: 'HALTED'
                        },
                        { 
                          label: 'House', 
                          ok: (adminData.houseWallet?.balanceSats || 0) >= (adminData.houseStats?.totalPaperBucksInUserWallets || 0) * 0.4,
                          icon: '🏠',
                          okText: `${((adminData.houseWallet?.balanceSats || 0) / Math.max(1, adminData.houseStats?.totalPaperBucksInUserWallets || 1) * 100).toFixed(0)}%`,
                          badText: 'LOW'
                        },
                        { 
                          label: 'Supply', 
                          ok: (adminData.houseStats?.supplyRatio || 1) < 2,
                          warn: (adminData.houseStats?.supplyRatio || 1) >= 1.5 && (adminData.houseStats?.supplyRatio || 1) < 2,
                          icon: '⚖️',
                          okText: `${(adminData.houseStats?.supplyRatio || 1).toFixed(1)}x`,
                          badText: `${(adminData.houseStats?.supplyRatio || 1).toFixed(1)}x HIGH`
                        },
                        { 
                          label: 'Bots', 
                          ok: (adminData.botStats?.activeBotUsers || 0) > 0 || true, // Always OK if system running
                          icon: '🤖',
                          okText: `${adminData.botStats?.activeBotUsers || 0} active`,
                          badText: 'None'
                        },
                        { 
                          label: 'Tokens', 
                          ok: (adminData.serverStatus?.tokenCount || 0) > 0,
                          icon: '🪙',
                          okText: `${adminData.businessMetrics?.totalActiveTokens || 0} live`,
                          badText: 'None'
                        },
                      ].map(item => (
                        <div key={item.label} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          backgroundColor: item.ok ? (item.warn ? '#1a1a0a' : '#0a1a0a') : '#1a0a0a',
                          border: `1px solid ${item.ok ? (item.warn ? '#f0b90b33' : '#00d26a33') : '#ff3b6933'}`
                        }}>
                          <span style={{ fontSize: '10px' }}>{item.icon}</span>
                          <span style={{ 
                            fontSize: '9px', 
                            color: item.ok ? (item.warn ? '#f0b90b' : '#00d26a') : '#ff3b69',
                            fontWeight: 'bold'
                          }}>
                            {item.ok ? item.okText : item.badText}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Right: Alerts + Auto-refresh toggle */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* Alert Badges */}
                      {pendingWithdrawals?.length > 0 && (
                        <div style={{ 
                          padding: '3px 8px', 
                          borderRadius: '6px', 
                          backgroundColor: '#1a0a0a', 
                          border: '1px solid #ff8c0033',
                          fontSize: '9px',
                          color: '#ff8c00',
                          fontWeight: 'bold'
                        }}>
                          💸 {pendingWithdrawals.length} pending
                        </div>
                      )}
                      {adminData.fraudAlerts?.length > 0 && (
                        <div style={{ 
                          padding: '3px 8px', 
                          borderRadius: '6px', 
                          backgroundColor: '#1a0a0a', 
                          border: '1px solid #ff3b6933',
                          fontSize: '9px',
                          color: '#ff3b69',
                          fontWeight: 'bold'
                        }}>
                          🚨 {adminData.fraudAlerts.length} fraud
                        </div>
                      )}
                      {adminData.suspicious?.length > 0 && (
                        <div style={{ 
                          padding: '3px 8px', 
                          borderRadius: '6px', 
                          backgroundColor: '#1a0a0a', 
                          border: '1px solid #f0b90b33',
                          fontSize: '9px',
                          color: '#f0b90b',
                          fontWeight: 'bold'
                        }}>
                          ⚠️ {adminData.suspicious.length} sus
                        </div>
                      )}
                      
                      {/* Uptime + Memory */}
                      <div style={{ fontSize: '9px', color: '#666', padding: '0 8px' }}>
                        ⏱️ {Math.floor((adminData.serverStatus?.uptime || 0) / 60)}m | 💾 {adminData.serverStatus?.memoryMB || 0}MB
                      </div>
                      
                      {/* Auto-refresh toggle */}
                      <button
                        onClick={() => setAdminAutoRefresh(!adminAutoRefresh)}
                        style={{
                          padding: '3px 8px',
                          borderRadius: '6px',
                          backgroundColor: adminAutoRefresh ? '#0a1a0a' : '#1a1a1a',
                          border: `1px solid ${adminAutoRefresh ? '#00d26a33' : '#33333'}`,
                          color: adminAutoRefresh ? '#00d26a' : '#666',
                          fontSize: '9px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        {adminAutoRefresh ? '🔄 LIVE' : '⏸️ PAUSED'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Overall Status Summary */}
                  {(() => {
                    const issues = [];
                    if (!adminData.serverStatus?.redisLoaded) issues.push('Redis loading');
                    if (adminData.tradingHalted) issues.push('Trading halted');
                    if ((adminData.houseStats?.supplyRatio || 1) >= 2) issues.push('Supply inflated');
                    if (pendingWithdrawals?.length > 0) issues.push(`${pendingWithdrawals.length} withdrawals`);
                    if (adminData.fraudAlerts?.length > 0) issues.push(`${adminData.fraudAlerts.length} fraud alerts`);
                    
                    const allGood = issues.length === 0;
                    
                    return (
                      <div style={{ 
                        marginTop: '8px', 
                        paddingTop: '8px', 
                        borderTop: '1px solid #222',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{ 
                          fontSize: '11px', 
                          fontWeight: 'bold',
                          color: allGood ? '#00d26a' : '#ff8c00'
                        }}>
                          {allGood ? '✅ ALL SYSTEMS NOMINAL' : `⚠️ ${issues.join(' • ')}`}
                        </div>
                        <div style={{ fontSize: '9px', color: '#444' }}>
                          Last update: {new Date(adminData.timestamp).toLocaleTimeString()} 
                          {adminAutoRefresh && <span style={{ color: '#00d26a' }}> • Auto-refresh {adminTab === 'live' ? '10s' : '30s'}</span>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              {adminLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading...</div>
              ) : adminData ? (
                <>
                  {/* OVERVIEW TAB */}
                  {adminTab === 'overview' && adminData?.userRole === 'admin' && (
                    <>
                      {/* Backup Button */}
                      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_URL}/api/admin/backup`, {
                                headers: { 'x-session-id': sessionId }
                              });
                              if (res.ok) {
                                const data = await res.json();
                                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `paper-hands-backup-${Date.now()}.json`;
                                a.click();
                                URL.revokeObjectURL(url);
                                alert('✅ Backup downloaded!');
                              } else {
                                alert('❌ Backup failed');
                              }
                            } catch (e) {
                              alert('❌ Error: ' + e.message);
                            }
                          }}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#1a2f1a',
                            color: '#00d26a',
                            border: '1px solid #00d26a',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}
                        >
                          💾 Download Full Backup
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm('⚠️ Restore user balances from JSON file?\n\nThis will overwrite Redis with the server filesystem backup.\n\nUse this if playthrough/bonus data was lost.\n\nType RESTORE_FROM_JSON to confirm.')) return;
                            const confirmText = prompt('Type RESTORE_FROM_JSON to confirm:');
                            if (confirmText !== 'RESTORE_FROM_JSON') {
                              alert('Cancelled');
                              return;
                            }
                            try {
                              const res = await fetch(`${API_URL}/api/admin/restore-from-json`, {
                                method: 'POST',
                                headers: { 
                                  'Content-Type': 'application/json',
                                  'x-session-id': sessionId 
                                },
                                body: JSON.stringify({ confirm: 'RESTORE_FROM_JSON' })
                              });
                              const data = await res.json();
                              if (res.ok) {
                                alert(`✅ Restored from JSON!\n\nUsers: ${data.stats.oldUserCount} → ${data.stats.newUserCount}\nUsers with locked bonus: ${data.stats.usersWithLockedBonus}\nTotal locked PB: ${(data.stats.totalLockedPB / 1000000).toFixed(2)}M`);
                              } else {
                                alert('❌ Restore failed: ' + data.error);
                              }
                            } catch (e) {
                              alert('❌ Error: ' + e.message);
                            }
                          }}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#2f1a1a',
                            color: '#f0b90b',
                            border: '1px solid #f0b90b',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}
                        >
                          🔄 Restore from JSON
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_URL}/api/admin/toggle-visibility`, {
                                method: 'POST',
                                headers: { 'X-Session-Id': sessionId }
                              });
                              const data = await res.json();
                              if (data.success) {
                                setAdminVisible(data.visible);
                              }
                            } catch (e) {
                              console.error('Toggle visibility error:', e);
                            }
                          }}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: adminVisible ? '#1a1a2f' : '#2f1a2f',
                            color: adminVisible ? '#00d26a' : '#ff3b69',
                            border: `1px solid ${adminVisible ? '#00d26a' : '#ff3b69'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}
                        >
                          {adminVisible ? '👁️ VISIBLE Online' : '👻 HIDDEN from Online'}
                        </button>
                        <button
                          onClick={async () => {
                            // First do a dry run
                            try {
                              const dryRes = await fetch(`${API_URL}/api/admin/migrate-positions-to-redis`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'x-session-id': sessionId
                                },
                                body: JSON.stringify({ dryRun: true })
                              });
                              const dryData = await dryRes.json();

                              if (!dryRes.ok) {
                                alert('❌ Dry run failed: ' + dryData.error);
                                return;
                              }

                              // Show dry run results and confirm
                              const confirmMigrate = confirm(
                                `📦 POSITION MIGRATION PREVIEW (Dry Run)\n\n` +
                                `Users with active positions: ${dryData.usersWithPositions}\n` +
                                `Positions to migrate: ${dryData.totalPositionsMigrated}\n` +
                                `Already in Redis (skip): ${dryData.totalPositionsSkipped}\n\n` +
                                `Click OK to run the actual migration.\n` +
                                `Click Cancel to abort.`
                              );

                              if (!confirmMigrate) {
                                alert('Migration cancelled');
                                return;
                              }

                              // Run actual migration
                              const res = await fetch(`${API_URL}/api/admin/migrate-positions-to-redis`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'x-session-id': sessionId
                                },
                                body: JSON.stringify({ dryRun: false })
                              });
                              const data = await res.json();

                              if (res.ok) {
                                alert(
                                  `✅ MIGRATION COMPLETE!\n\n` +
                                  `Users migrated: ${data.usersWithPositions}\n` +
                                  `Positions migrated: ${data.totalPositionsMigrated}\n` +
                                  `Skipped (already existed): ${data.totalPositionsSkipped}\n\n` +
                                  `All positions are now backed up in Redis!`
                                );
                              } else {
                                alert('❌ Migration failed: ' + data.error);
                              }
                            } catch (e) {
                              alert('❌ Error: ' + e.message);
                            }
                          }}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#1a2f2f',
                            color: '#00bcd4',
                            border: '1px solid #00bcd4',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}
                        >
                          📦 Migrate Positions to Redis
                        </button>
                      </div>

                      {/* House Stats */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                          <div style={{ color: '#666', fontSize: '10px' }}>💰 PB in Circulation</div>
                          <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>{(adminData.houseStats?.totalPaperBucksInCirculation / 1000000 || 0).toFixed(2)}M</div>
                          {bsvPrice && <div style={{ color: '#888', fontSize: '10px' }}>${((adminData.houseStats?.totalPaperBucksInCirculation / 100000000 || 0) * bsvPrice).toFixed(2)} USD</div>}
                        </div>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #00d26a' }}>
                          <div style={{ color: '#666', fontSize: '10px' }}>🏠 Rake Collected</div>
                          <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{(adminData.houseStats?.totalRakeCollected / 1000000 || 0).toFixed(2)}M</div>
                          {bsvPrice && <div style={{ color: '#888', fontSize: '10px' }}>${((adminData.houseStats?.totalRakeCollected / 100000000 || 0) * bsvPrice).toFixed(2)} USD</div>}
                        </div>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                          <div style={{ color: '#666', fontSize: '10px' }}>⬇️ Total Deposited</div>
                          <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{((adminData.houseStats?.totalDepositSats || 0) / 1000000).toFixed(2)}M</div>
                          {bsvPrice && <div style={{ color: '#888', fontSize: '10px' }}>${(((adminData.houseStats?.totalDepositSats || 0) / 100000000) * bsvPrice).toFixed(2)} USD</div>}
                        </div>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                          <div style={{ color: '#666', fontSize: '10px' }}>⬆️ Total Withdrawn</div>
                          <div style={{ color: '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>{((adminData.houseStats?.totalWithdrawalSats || 0) / 1000000).toFixed(2)}M</div>
                          {bsvPrice && <div style={{ color: '#888', fontSize: '10px' }}>${(((adminData.houseStats?.totalWithdrawalSats || 0) / 100000000) * bsvPrice).toFixed(2)} USD</div>}
                        </div>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: (adminData.aggregates?.netFlow || 0) >= 0 ? '1px solid #00d26a' : '1px solid #ff3b69' }}>
                          <div style={{ color: '#666', fontSize: '10px' }}>📊 Net Flow (House {(adminData.aggregates?.netFlow || 0) >= 0 ? 'Up' : 'Down'})</div>
                          <div style={{ color: (adminData.aggregates?.netFlow || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>{((adminData.aggregates?.netFlow || 0) / 1000000).toFixed(2)}M</div>
                          {bsvPrice && <div style={{ color: '#888', fontSize: '10px' }}>${(((adminData.aggregates?.netFlow || 0) / 100000000) * bsvPrice).toFixed(2)} USD</div>}
                        </div>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                          <div style={{ color: '#666', fontSize: '10px' }}>👥 Total Users</div>
                          <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{adminData.houseStats?.totalUsers || 0}</div>
                          <div style={{ color: '#888', fontSize: '10px' }}>{adminData.businessMetrics?.premiumUsers || 0} premium</div>
                        </div>
                      </div>
                      
                      {/* 🎰 PLAYTHROUGH STATUS - Locked vs Unlocked */}
                      <div style={{ backgroundColor: '#0a1a1a', borderRadius: '12px', padding: '16px', border: '2px solid #a855f7', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div>
                            <div style={{ color: '#a855f7', fontSize: '14px', fontWeight: 'bold' }}>🎰 PLAYTHROUGH STATUS</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>PvP profits locked until traded (3x playthrough)</div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={async () => {
                                const confirm1 = window.confirm('⚠️ GRANDFATHER RESET\n\nThis will:\n• Reset ALL playthrough tracking to 0\n• Make all current balances fully withdrawable\n• Draw a line in the sand - new PvP profits will require fresh playthrough\n\nAre you sure?');
                                if (!confirm1) return;
                                const confirmText = prompt('Type GRANDFATHER_RESET to confirm:');
                                if (confirmText !== 'GRANDFATHER_RESET') {
                                  alert('Cancelled');
                                  return;
                                }
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/grandfather-reset`, {
                                    method: 'POST',
                                    headers: { 
                                      'Content-Type': 'application/json',
                                      'X-Session-Id': sessionId 
                                    },
                                    body: JSON.stringify({ confirm: 'GRANDFATHER_RESET' })
                                  });
                                  const data = await res.json();
                                  if (res.ok) {
                                    alert(`✅ Grandfather Reset Complete!\n\nUsers reset: ${data.usersReset}\nBonus PB cleared: ${formatPB(data.totalBonusCleared)}\n\nAll current balances are now fully withdrawable.\nNew PvP profits will require fresh 3x playthrough.`);
                                    // Refresh stats
                                    const statsRes = await fetch(`${API_URL}/api/admin/playthrough-stats`, {
                                      headers: { 'X-Session-Id': sessionId }
                                    });
                                    const statsData = await statsRes.json();
                                    setPlaythroughStats(statsData);
                                  } else {
                                    alert('❌ Error: ' + data.error);
                                  }
                                } catch (e) {
                                  alert('❌ Error: ' + e.message);
                                }
                              }}
                              style={{ padding: '6px 12px', backgroundColor: '#2a0a0a', color: '#ff8c00', border: '1px solid #ff8c00', borderRadius: '6px', cursor: 'pointer', fontSize: '10px' }}
                            >
                              🔄 Grandfather Reset
                            </button>
                            <button
                              onClick={async () => {
                                const confirmText = prompt('🔧 RECALCULATE PLAYTHROUGH\n\nThis will fix users who have bonusBalance > 0 but bonusRequired = 0\n(they would show 100% and could withdraw locked funds)\n\nType RECALCULATE to confirm:');
                                if (confirmText !== 'RECALCULATE') {
                                  if (confirmText !== null) alert('Cancelled');
                                  return;
                                }
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/recalculate-playthrough`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'X-Session-Id': sessionId
                                    },
                                    body: JSON.stringify({ confirm: 'RECALCULATE' })
                                  });
                                  const data = await res.json();
                                  if (res.ok) {
                                    if (data.fixedCount > 0) {
                                      const userList = data.fixed.map(u => `• ${u.handle}: ${(u.bonusBalance/1000000).toFixed(2)}M → requires ${(u.newBonusRequired/1000000).toFixed(2)}M playthrough`).join('\n');
                                      alert(`✅ Fixed ${data.fixedCount} users!\n\n${userList}`);
                                    } else {
                                      alert('✅ No users needed fixing. All playthrough requirements are set correctly.');
                                    }
                                    // Refresh stats
                                    const statsRes = await fetch(`${API_URL}/api/admin/playthrough-stats`, {
                                      headers: { 'X-Session-Id': sessionId }
                                    });
                                    const statsData = await statsRes.json();
                                    setPlaythroughStats(statsData);
                                  } else {
                                    alert('❌ Error: ' + data.error);
                                  }
                                } catch (e) {
                                  alert('❌ Error: ' + e.message);
                                }
                              }}
                              style={{ padding: '6px 12px', backgroundColor: '#0a2a1a', color: '#00d26a', border: '1px solid #00d26a', borderRadius: '6px', cursor: 'pointer', fontSize: '10px' }}
                            >
                              🔧 Recalculate
                            </button>
                            <button
                              onClick={async () => {
                                const choice = prompt('⚡ SETTLE POSITIONS\n\nThis updates entry prices to current prices, unlocking unrealized profits.\n\nOptions:\n• Type a handle (e.g. gametight) for single user\n• Type ALL to settle all grandfathered users\n\nEnter choice:');
                                if (!choice || choice.trim() === '') {
                                  return;
                                }
                                const confirmText = prompt('Type SETTLE to confirm:');
                                if (confirmText !== 'SETTLE') {
                                  if (confirmText !== null) alert('Cancelled');
                                  return;
                                }
                                try {
                                  const isAll = choice.trim().toUpperCase() === 'ALL';
                                  const res = await fetch(`${API_URL}/api/admin/settle-positions`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'X-Session-Id': sessionId
                                    },
                                    body: JSON.stringify(isAll
                                      ? { confirm: 'SETTLE', mode: 'grandfathered' }
                                      : { confirm: 'SETTLE', handle: choice.trim() }
                                    )
                                  });
                                  const data = await res.json();
                                  if (res.ok) {
                                    if (data.settledCount > 0) {
                                      const userList = data.settled.map(u => `• ${u.handle}: ${u.positionsSettled} positions, ${(u.profitSettled/1000000).toFixed(2)}M PB unlocked`).join('\n');
                                      alert(`✅ Settled ${data.settledCount} users!\n\nTotal unlocked: ${(data.totalProfitSettled/1000000).toFixed(2)}M PB\n\n${userList}`);
                                    } else {
                                      alert('✅ No positions needed settling.');
                                    }
                                    // Refresh stats
                                    const statsRes = await fetch(`${API_URL}/api/admin/playthrough-stats`, {
                                      headers: { 'X-Session-Id': sessionId }
                                    });
                                    const statsData = await statsRes.json();
                                    setPlaythroughStats(statsData);
                                  } else {
                                    alert('❌ Error: ' + data.error);
                                  }
                                } catch (e) {
                                  alert('❌ Error: ' + e.message);
                                }
                              }}
                              style={{ padding: '6px 12px', backgroundColor: '#2a1a0a', color: '#f0b90b', border: '1px solid #f0b90b', borderRadius: '6px', cursor: 'pointer', fontSize: '10px' }}
                            >
                              ⚡ Settle Positions
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/playthrough-stats`, {
                                    headers: { 'X-Session-Id': sessionId }
                                  });
                                  const data = await res.json();
                                  setPlaythroughStats(data);
                                } catch (e) {
                                  console.error('Failed to load playthrough stats:', e);
                                }
                              }}
                              style={{ padding: '6px 12px', backgroundColor: '#1a0a2a', color: '#a855f7', border: '1px solid #a855f7', borderRadius: '6px', cursor: 'pointer', fontSize: '10px' }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                        
                        {playthroughStatsLoading ? (
                          <div style={{ textAlign: 'center', padding: '20px', color: '#a855f7' }}>
                            Loading playthrough stats...
                          </div>
                        ) : playthroughStats ? (
                          <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '12px' }}>
                              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                                <div style={{ color: '#666', fontSize: '9px' }}>Users w/ Bonus</div>
                                <div style={{ color: playthroughStats.usersWithBonus > 0 ? '#a855f7' : '#666', fontSize: '16px', fontWeight: 'bold' }}>
                                  {playthroughStats.usersWithBonus || 0}
                                </div>
                                <div style={{ color: '#ff3b69', fontSize: '9px' }}>{playthroughStats.usersWithLocked || 0} locked</div>
                              </div>
                              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                                <div style={{ color: '#666', fontSize: '9px' }}>🔒 Total Locked</div>
                                <div style={{ color: '#ff3b69', fontSize: '16px', fontWeight: 'bold' }}>
                                  {formatPB(playthroughStats.totalLocked)}
                                </div>
                              </div>
                              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                                <div style={{ color: '#666', fontSize: '9px' }}>Total Bonus PB</div>
                                <div style={{ color: '#f0b90b', fontSize: '16px', fontWeight: 'bold' }}>
                                  {formatPB(playthroughStats.totalBonusBalance)}
                                </div>
                              </div>
                              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                                <div style={{ color: '#666', fontSize: '9px' }}>Multiplier</div>
                                <div style={{ color: '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>
                                  {playthroughStats.multiplier}x
                                </div>
                              </div>
                            </div>
                            
                            {/* Detailed user table */}
                            {playthroughStats.topUsers?.length > 0 && (
                              <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', overflow: 'hidden' }}>
                                <div style={{ color: '#888', fontSize: '10px', marginBottom: '8px' }}>Playthrough Details (Top 20):</div>
                                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                                    <thead>
                                      <tr style={{ color: '#666', textAlign: 'left', position: 'sticky', top: 0, backgroundColor: '#111' }}>
                                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #333' }}>User</th>
                                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #333', textAlign: 'right' }}>Balance</th>
                                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #333', textAlign: 'right' }}>Withdrawable</th>
                                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #333', textAlign: 'right' }}>Locked</th>
                                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #333', textAlign: 'right' }}>Traded</th>
                                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #333', textAlign: 'right' }}>Required</th>
                                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #333', textAlign: 'right' }}>%</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {playthroughStats.topUsers.map((u, i) => (
                                        <tr key={i} style={{ backgroundColor: u.locked > 0 ? '#1a0a0a' : 'transparent' }}>
                                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #1a1a1a', color: '#fff' }}>${u.handle}</td>
                                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right', color: '#f0b90b' }}>{formatPB(u.gameBalance)}</td>
                                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right', color: '#00d26a' }}>{formatPB(u.withdrawable)}</td>
                                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right', color: u.locked > 0 ? '#ff3b69' : '#333' }}>{u.locked > 0 ? formatPB(u.locked) : '-'}</td>
                                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right', color: '#888' }}>{formatPB(u.playthrough)}</td>
                                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right', color: '#888' }}>{formatPB(u.required)}</td>
                                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right', color: u.percent >= 100 ? '#00d26a' : '#ff8c00' }}>{u.percent}%</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            
                            {playthroughStats.usersWithBonus === 0 && (
                              <div style={{ color: '#666', fontSize: '11px', textAlign: 'center', padding: '12px' }}>
                                ✅ No users have playthrough tracking yet (no PvP profits recorded)
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{ color: '#666', fontSize: '11px', textAlign: 'center', padding: '12px' }}>
                            Click Refresh to load playthrough stats
                          </div>
                        )}
                      </div>
                      
                      {/* 🚨 CIRCUIT BREAKER - House Safety Net */}
                      <div style={{ backgroundColor: '#1a0a0a', borderRadius: '12px', padding: '16px', border: '2px solid #ff3b69', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div>
                            <div style={{ color: '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>🚨 CIRCUIT BREAKER</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>Blocks withdrawals if house at risk</div>
                          </div>
                          <button
                            onClick={async () => {
                              setCircuitBreakerLoading(true);
                              try {
                                const res = await fetch(`${API_URL}/api/admin/circuit-breaker`, {
                                  headers: { 'X-Session-Id': sessionId }
                                });
                                const data = await res.json();
                                setCircuitBreakerData(data);
                              } catch (e) {
                                console.error('Failed to load circuit breaker:', e);
                              }
                              setCircuitBreakerLoading(false);
                            }}
                            style={{ padding: '6px 12px', backgroundColor: '#2a0a0a', color: '#ff3b69', border: '1px solid #ff3b69', borderRadius: '6px', cursor: 'pointer', fontSize: '10px' }}
                          >
                            {circuitBreakerLoading ? '⏳' : '🔄 Refresh'}
                          </button>
                        </div>
                        
                        {circuitBreakerData ? (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                            <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                              <div style={{ color: '#666', fontSize: '9px' }}>Status</div>
                              <div style={{ 
                                color: circuitBreakerData.status === 'healthy' ? '#00d26a' : 
                                       circuitBreakerData.status === 'warning' ? '#f0b90b' : '#ff3b69',
                                fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase'
                              }}>
                                {circuitBreakerData.status === 'healthy' ? '✅ HEALTHY' :
                                 circuitBreakerData.status === 'warning' ? '⚠️ WARNING' : '🚨 CRITICAL'}
                              </div>
                            </div>
                            <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                              <div style={{ color: '#666', fontSize: '9px' }}>Deposited</div>
                              <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>
                                {formatPB(circuitBreakerData.totalDeposited || 0)}
                              </div>
                            </div>
                            <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                              <div style={{ color: '#666', fontSize: '9px' }}>Withdrawn</div>
                              <div style={{ color: '#ff3b69', fontSize: '12px', fontWeight: 'bold' }}>
                                {formatPB(circuitBreakerData.totalWithdrawn || 0)}
                              </div>
                            </div>
                            <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                              <div style={{ color: '#666', fontSize: '9px' }}>Buffer Remaining</div>
                              <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold' }}>
                                {formatPB(circuitBreakerData.remaining || 0)}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: '#666', fontSize: '11px', textAlign: 'center', padding: '12px' }}>
                            Click Refresh to load circuit breaker status
                          </div>
                        )}
                      </div>
                      
                      {/* 🏠 HOUSE WALLET (zcool) - Liquidity Pool */}
                      <div style={{ backgroundColor: '#0a0a1a', borderRadius: '12px', padding: '16px', border: '2px solid #3b82f6', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div>
                            <div style={{ color: '#3b82f6', fontSize: '14px', fontWeight: 'bold' }}>🏠 HOUSE WALLET ($zcool)</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>Liquidity pool - DON'T TOUCH!</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#3b82f6', fontSize: '24px', fontWeight: 'bold' }}>
                              {adminData.houseWallet?.bsvBalance || '0.0000'} BSV
                            </div>
                            {bsvPrice && (
                              <div style={{ color: '#888', fontSize: '11px' }}>
                                ≈ ${((adminData.houseWallet?.bsvBalanceSats || 0) / 100000000 * bsvPrice).toFixed(2)} USD
                              </div>
                            )}
                          </div>
                        </div>

                        {/* House Stats Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '12px' }}>
                          <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                            <div style={{ color: '#666', fontSize: '9px' }}>Expected Balance</div>
                            <div style={{ color: '#3b82f6', fontSize: '12px', fontWeight: 'bold' }}>
                              {(((adminData.houseStats?.totalDepositSats || 0) - (adminData.houseStats?.totalWithdrawalSats || 0)) / 100000000).toFixed(4)} BSV
                            </div>
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                            <div style={{ color: '#666', fontSize: '9px' }}>Variance</div>
                            <div style={{ color: Math.abs((adminData.houseWallet?.bsvBalanceSats || 0) - ((adminData.houseStats?.totalDepositSats || 0) - (adminData.houseStats?.totalWithdrawalSats || 0))) < 10000 ? '#00d26a' : '#ff3b69', fontSize: '12px', fontWeight: 'bold' }}>
                              {(((adminData.houseWallet?.bsvBalanceSats || 0) - ((adminData.houseStats?.totalDepositSats || 0) - (adminData.houseStats?.totalWithdrawalSats || 0))) / 100000000).toFixed(4)} BSV
                            </div>
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                            <div style={{ color: '#666', fontSize: '9px' }}>Active Positions</div>
                            <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold' }}>
                              {adminData.houseStats?.activePositions || adminData.aggregates?.totalPositions || 0}
                            </div>
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                            <div style={{ color: '#666', fontSize: '9px' }}>PB at Risk</div>
                            <div style={{ color: '#ff8c00', fontSize: '12px', fontWeight: 'bold' }}>
                              {formatPB(adminData.houseStats?.totalPBAtRisk || adminData.aggregates?.totalInvested || 0)}
                            </div>
                          </div>
                        </div>

                        <div style={{ color: '#555', fontSize: '10px', textAlign: 'center' }}>
                          Holds player deposits for withdrawals & sells • Variance should be near $0
                        </div>
                      </div>

                      {/* 💰 OWNER WALLET (h2O4daze) - YOUR PROFIT IN BSV! */}
                      <div style={{ backgroundColor: '#0a1a0a', borderRadius: '12px', padding: '16px', border: '2px solid #00d26a', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div>
                            <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>💰 OWNER WALLET ($h2o4daze)</div>
                            <div style={{ color: '#ff8c00', fontSize: '10px' }}>⚠️ Estimated only - h2o4daze should check their own HandCash</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#666', fontSize: '18px', fontWeight: 'bold' }}>
                              ~{adminData.ownerWallet?.bsvBalance || '0.0000'} BSV
                            </div>
                            {bsvPrice && (
                              <div style={{ color: '#888', fontSize: '12px' }}>
                                ≈ ${((adminData.ownerWallet?.bsvBalanceSats || 0) / 100000000 * bsvPrice).toFixed(2)} USD (estimate)
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Revenue Breakdown - IN BSV */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '12px' }}>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #333' }}>
                            <div style={{ color: '#888', fontSize: '9px' }}>🤖 Bot Rake</div>
                            <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>
                              {((adminData.ownerWallet?.totalBotRake || 0) / 100000000).toFixed(4)}
                            </div>
                            <div style={{ color: '#666', fontSize: '9px' }}>BSV</div>
                            {bsvPrice && <div style={{ color: '#888', fontSize: '10px' }}>${(((adminData.ownerWallet?.totalBotRake || 0) / 100000000) * bsvPrice).toFixed(2)}</div>}
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #00d26a44' }}>
                            <div style={{ color: '#888', fontSize: '9px' }}>📈 Trade Rake (1%)</div>
                            <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>
                              {((adminData.ownerWallet?.totalTradeRake || 0) / 100000000).toFixed(4)}
                            </div>
                            <div style={{ color: '#666', fontSize: '9px' }}>BSV</div>
                            {bsvPrice && <div style={{ color: '#888', fontSize: '10px' }}>${(((adminData.ownerWallet?.totalTradeRake || 0) / 100000000) * bsvPrice).toFixed(2)}</div>}
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #333' }}>
                            <div style={{ color: '#888', fontSize: '9px' }}>🚀 Launch Fees</div>
                            <div style={{ color: '#f0b90b', fontSize: '14px', fontWeight: 'bold' }}>
                              {((adminData.ownerWallet?.totalLaunchFees || 0) / 100000000).toFixed(4)}
                            </div>
                            <div style={{ color: '#666', fontSize: '9px' }}>BSV</div>
                            {bsvPrice && <div style={{ color: '#888', fontSize: '10px' }}>${(((adminData.ownerWallet?.totalLaunchFees || 0) / 100000000) * bsvPrice).toFixed(2)}</div>}
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #333' }}>
                            <div style={{ color: '#888', fontSize: '9px' }}>👑 Premium Subs</div>
                            <div style={{ color: '#9b59b6', fontSize: '14px', fontWeight: 'bold' }}>
                              {((adminData.ownerWallet?.totalPremiumRevenue || adminData.ownerWallet?.totalBotRevenue || 0) / 100000000).toFixed(4)}
                            </div>
                            <div style={{ color: '#666', fontSize: '9px' }}>BSV</div>
                            {bsvPrice && <div style={{ color: '#888', fontSize: '10px' }}>${(((adminData.ownerWallet?.totalPremiumRevenue || adminData.ownerWallet?.totalBotRevenue || 0) / 100000000) * bsvPrice).toFixed(2)}</div>}
                          </div>
                        </div>

                        {/* Daily/Weekly Stats */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '12px' }}>
                          <div style={{ backgroundColor: '#0a1a0a', borderRadius: '6px', padding: '8px', textAlign: 'center', border: '1px solid #00d26a33' }}>
                            <div style={{ color: '#666', fontSize: '9px' }}>Today's Rake</div>
                            <div style={{ color: '#00d26a', fontSize: '13px', fontWeight: 'bold' }}>
                              {((adminData.ownerWallet?.todayRake || 0) / 100000000).toFixed(4)} BSV
                            </div>
                            {bsvPrice && <div style={{ color: '#888', fontSize: '9px' }}>${(((adminData.ownerWallet?.todayRake || 0) / 100000000) * bsvPrice).toFixed(2)}</div>}
                          </div>
                          <div style={{ backgroundColor: '#0a1a0a', borderRadius: '6px', padding: '8px', textAlign: 'center', border: '1px solid #00d26a33' }}>
                            <div style={{ color: '#666', fontSize: '9px' }}>This Week</div>
                            <div style={{ color: '#00d26a', fontSize: '13px', fontWeight: 'bold' }}>
                              {((adminData.ownerWallet?.weekRake || 0) / 100000000).toFixed(4)} BSV
                            </div>
                            {bsvPrice && <div style={{ color: '#888', fontSize: '9px' }}>${(((adminData.ownerWallet?.weekRake || 0) / 100000000) * bsvPrice).toFixed(2)}</div>}
                          </div>
                          <div style={{ backgroundColor: '#0a1a0a', borderRadius: '6px', padding: '8px', textAlign: 'center', border: '1px solid #00d26a33' }}>
                            <div style={{ color: '#666', fontSize: '9px' }}>All Time</div>
                            <div style={{ color: '#00d26a', fontSize: '13px', fontWeight: 'bold' }}>
                              {((adminData.ownerWallet?.gameBalance || 0) / 100000000).toFixed(4)} BSV
                            </div>
                            {bsvPrice && <div style={{ color: '#888', fontSize: '9px' }}>${(((adminData.ownerWallet?.gameBalance || 0) / 100000000) * bsvPrice).toFixed(2)}</div>}
                          </div>
                        </div>

                        {/* Recent Credits - Show BSV amounts */}
                        {adminData.ownerWallet?.recentCredits?.length > 0 && (
                          <div>
                            <div style={{ color: '#666', fontSize: '10px', marginBottom: '6px' }}>Recent Rake Credits:</div>
                            <div style={{ maxHeight: '100px', overflowY: 'auto', fontSize: '10px', backgroundColor: '#111', borderRadius: '6px', padding: '8px' }}>
                              {adminData.ownerWallet.recentCredits.slice(-8).reverse().map((c, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #222' }}>
                                  <span style={{ color: '#888' }}>
                                    <span style={{ color: c.type?.includes('buy') ? '#00d26a' : '#ff3b69' }}>{c.type?.includes('buy') ? 'BUY' : 'SELL'}</span>
                                    {' '}${c.ticker} by {c.from}
                                  </span>
                                  <span style={{ color: '#00d26a', fontWeight: 'bold' }}>
                                    +{((c.amount || 0) / 100000000).toFixed(6)} BSV
                                    {bsvPrice && <span style={{ color: '#666' }}> (${((c.amount / 100000000) * bsvPrice).toFixed(3)})</span>}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 🔔 PENNY PING - Send 1 sat to all players */}
                      <div style={{ backgroundColor: '#1a0a1a', borderRadius: '12px', padding: '16px', border: '2px solid #9b59b6', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div>
                            <div style={{ color: '#9b59b6', fontSize: '14px', fontWeight: 'bold' }}>🔔 PENNY PING</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>Send 1 sat to all players with a message to get their attention!</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#9b59b6', fontSize: '18px', fontWeight: 'bold' }}>
                              {adminData.users?.length || 0} players
                            </div>
                            <div style={{ color: '#888', fontSize: '10px' }}>
                              Cost: ~{adminData.users?.length || 0} sats
                            </div>
                          </div>
                        </div>

                        {/* Message Input */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>Message (max 25 chars - shows in HandCash notification):</div>
                          <input
                            type="text"
                            value={pennyPingMessage}
                            onChange={(e) => setPennyPingMessage(e.target.value.slice(0, 25))}
                            maxLength={25}
                            placeholder="Come play PaperHands!"
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              backgroundColor: '#111',
                              border: '1px solid #333',
                              borderRadius: '6px',
                              color: '#fff',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                            }}
                          />
                          <div style={{ color: '#666', fontSize: '10px', marginTop: '4px', textAlign: 'right' }}>
                            {pennyPingMessage.length}/25 characters
                          </div>
                        </div>

                        {/* Options */}
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', alignItems: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={pennyPingExcludeOnline}
                              onChange={(e) => setPennyPingExcludeOnline(e.target.checked)}
                              style={{ accentColor: '#9b59b6' }}
                            />
                            <span style={{ color: '#888', fontSize: '11px' }}>Skip players already online</span>
                          </label>
                        </div>

                        {/* Preview/Send Buttons */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API_URL}/api/admin/penny-ping`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                  body: JSON.stringify({ message: pennyPingMessage, excludeOnline: pennyPingExcludeOnline })
                                });
                                const data = await res.json();
                                if (data.error) { alert('Error: ' + data.error); return; }
                                setPennyPingPreview(data);
                              } catch (e) { alert('Error: ' + e.message); }
                            }}
                            style={{
                              flex: 1,
                              padding: '10px',
                              backgroundColor: '#222',
                              border: '1px solid #9b59b6',
                              borderRadius: '6px',
                              color: '#9b59b6',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '12px',
                            }}
                          >
                            👁️ Preview Recipients
                          </button>
                          <button
                            onClick={async () => {
                              if (!pennyPingPreview) {
                                alert('Click Preview first to see who will receive the ping');
                                return;
                              }
                              const confirm1 = window.confirm(`🔔 PENNY PING\n\nSend 1 sat to ${pennyPingPreview.totalRecipients} players?\n\nMessage: "${pennyPingMessage}"\nTotal Cost: ${pennyPingPreview.totalCostSats} sats (~$${((pennyPingPreview.totalCostSats / 100000000) * (bsvPrice || 50)).toFixed(4)})\n\nThis will deduct from House Wallet ($zcool).`);
                              if (!confirm1) return;

                              setPennyPingSending(true);
                              try {
                                const res = await fetch(`${API_URL}/api/admin/penny-ping`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                  body: JSON.stringify({ message: pennyPingMessage, excludeOnline: pennyPingExcludeOnline, confirmSend: true })
                                });
                                const data = await res.json();
                                if (data.error) { alert('Error: ' + data.error); return; }
                                alert(`✅ Penny Ping Sent!\n\n✓ ${data.sent} players received 1 sat\n${data.failed > 0 ? `✗ ${data.failed} failed` : ''}\n\nMessage: "${data.message}"`);
                                setPennyPingPreview(null);
                                fetchAdminDashboard(); // Refresh to update house balance
                              } catch (e) { alert('Error: ' + e.message); }
                              finally { setPennyPingSending(false); }
                            }}
                            disabled={pennyPingSending || !pennyPingPreview}
                            style={{
                              flex: 1,
                              padding: '10px',
                              backgroundColor: pennyPingSending ? '#333' : '#9b59b6',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              cursor: pennyPingSending || !pennyPingPreview ? 'not-allowed' : 'pointer',
                              fontWeight: 'bold',
                              fontSize: '12px',
                              opacity: pennyPingPreview ? 1 : 0.5,
                            }}
                          >
                            {pennyPingSending ? '⏳ Sending...' : '🔔 Send Penny Ping'}
                          </button>
                        </div>

                        {/* Preview Results */}
                        {pennyPingPreview && (
                          <div style={{ marginTop: '12px', backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #9b59b644' }}>
                            <div style={{ color: '#9b59b6', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
                              Preview: {pennyPingPreview.totalRecipients} recipients
                            </div>
                            <div style={{ color: '#888', fontSize: '11px', marginBottom: '8px' }}>
                              Message: "{pennyPingPreview.message}" • Cost: {pennyPingPreview.totalCostSats} sats
                            </div>
                            <div style={{ maxHeight: '100px', overflowY: 'auto', fontSize: '10px' }}>
                              {pennyPingPreview.recipients?.map((handle, i) => (
                                <span key={handle} style={{ color: '#666', marginRight: '8px' }}>${handle}</span>
                              ))}
                              {pennyPingPreview.hasMore && (
                                <span style={{ color: '#9b59b6' }}>... and {pennyPingPreview.totalRecipients - 50} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Quick Alerts Row */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
                        {/* Online Now */}
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                          <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>🟢 Online Now ({adminData.activeSessions?.length || 0})</div>
                          <div style={{ maxHeight: '100px', overflowY: 'auto', fontSize: '11px' }}>
                            {(adminData.activeSessions || []).slice(0, 10).map((s, i) => (
                              <span key={i} style={{ display: 'inline-block', backgroundColor: '#1a1a2e', padding: '2px 6px', borderRadius: '3px', marginRight: '4px', marginBottom: '4px', color: '#fff' }}>${s.handle}</span>
                            ))}
                            {(!adminData.activeSessions || adminData.activeSessions.length === 0) && <span style={{ color: '#666' }}>No one online</span>}
                          </div>
                        </div>
                        
                        {/* Suspicious Count */}
                        <div style={{ backgroundColor: adminData.suspicious?.length > 0 ? '#1a0808' : '#111', borderRadius: '8px', padding: '12px', border: adminData.suspicious?.length > 0 ? '1px solid #ff3b69' : '1px solid #333' }}>
                          <div style={{ color: '#ff3b69', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>🚨 Suspicious ({adminData.suspicious?.length || 0})</div>
                          <div style={{ fontSize: '11px' }}>
                            {(adminData.suspicious || []).slice(0, 3).map((u, i) => (
                              <div key={i} style={{ color: '#ff3b69' }}>${u.handle} - {(u.gameBalance/1000000).toFixed(1)}M bal, {u.wins}W/{u.losses}L</div>
                            ))}
                            {adminData.suspicious?.length > 3 && <div style={{ color: '#666' }}>+{adminData.suspicious.length - 3} more...</div>}
                          </div>
                        </div>
                        
                        {/* Hot Tokens */}
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #f0b90b' }}>
                          <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>🔥 Hot Tokens (1hr vol)</div>
                          <div style={{ fontSize: '11px' }}>
                            {(adminData.hotTokens || []).slice(0, 5).map((t, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: '#fff' }}>
                                <span>${t.ticker}</span>
                                <span style={{ color: '#f0b90b' }}>{(t.recentVolume/1000000).toFixed(2)}M</span>
                              </div>
                            ))}
                            {(!adminData.hotTokens || adminData.hotTokens.length === 0) && <span style={{ color: '#666' }}>No activity</span>}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* LIVE TAB - Real-time Activity Monitor */}
                  {adminTab === 'live' && (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '12px' }}>
                      {/* Left: Activity Feed */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Live Trades */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #222', overflow: 'hidden' }}>
                          <div style={{ backgroundColor: '#111', padding: '8px 12px', borderBottom: '1px solid #222', display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>⚡ Live Trades</span>
                            <span style={{ color: '#00d26a', fontSize: '9px' }}>● LIVE</span>
                          </div>
                          <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                            {(adminData.liveActivity || []).map((t, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid #1a1a1a', backgroundColor: i === 0 ? (t.type === 'buy' ? '#00d26a08' : '#ff3b6908') : 'transparent' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ color: t.type === 'buy' ? '#00d26a' : '#ff3b69', fontSize: '9px', fontWeight: 'bold', padding: '1px 4px', backgroundColor: t.type === 'buy' ? '#00d26a22' : '#ff3b6922', borderRadius: '3px' }}>{t.type === 'buy' ? 'BUY' : 'SELL'}</span>
                                  <span style={{ color: '#888', fontSize: '10px' }}>${t.handle}</span>
                                  <span style={{ color: '#f0b90b', fontSize: '10px' }}>${t.ticker}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>{formatPB(t.sats || 0)}</span>
                                  <span style={{ color: '#555', fontSize: '8px' }}>
                                    {t.timestamp ? (
                                      Date.now() - t.timestamp < 60000 
                                        ? `${Math.floor((Date.now() - t.timestamp) / 1000)}s` 
                                        : Date.now() - t.timestamp < 3600000 
                                          ? `${Math.floor((Date.now() - t.timestamp) / 60000)}m`
                                          : `${Math.floor((Date.now() - t.timestamp) / 3600000)}h`
                                    ) : ''}
                                  </span>
                                </div>
                              </div>
                            ))}
                            {(!adminData.liveActivity || adminData.liveActivity.length === 0) && <div style={{ padding: '20px', textAlign: 'center', color: '#444', fontSize: '10px' }}>No recent trades</div>}
                          </div>
                        </div>
                        {/* Whale Alerts */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #f0b90b33', overflow: 'hidden' }}>
                          <div style={{ backgroundColor: '#1a1508', padding: '8px 12px', borderBottom: '1px solid #f0b90b33' }}>
                            <span style={{ color: '#f0b90b', fontSize: '11px', fontWeight: 'bold' }}>🐋 Whale Movements (500K+)</span>
                          </div>
                          <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
                            {(adminData.whaleAlerts || []).map((w, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid #1a1a1a' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span>{w.type === 'deposit' ? '⬇️' : '⬆️'}</span>
                                  <span style={{ color: '#fff', fontSize: '10px' }}>${w.handle}</span>
                                </div>
                                <span style={{ color: '#f0b90b', fontSize: '10px', fontWeight: 'bold' }}>{formatPB(w.amount)}</span>
                              </div>
                            ))}
                            {(!adminData.whaleAlerts || adminData.whaleAlerts.length === 0) && <div style={{ padding: '16px', textAlign: 'center', color: '#444', fontSize: '10px' }}>No whale activity</div>}
                          </div>
                        </div>
                      </div>
                      {/* Right: Sessions */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          <div style={{ backgroundColor: '#0a1a0a', borderRadius: '6px', padding: '10px', border: '1px solid #00d26a33', textAlign: 'center' }}>
                            <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{adminData.activeSessions?.length || 0}</div>
                            <div style={{ color: '#666', fontSize: '9px' }}>Online</div>
                          </div>
                          <div style={{ backgroundColor: '#1a1508', borderRadius: '6px', padding: '10px', border: '1px solid #f0b90b33', textAlign: 'center' }}>
                            <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>{adminData.liveActivity?.length || 0}</div>
                            <div style={{ color: '#666', fontSize: '9px' }}>Trades</div>
                          </div>
                        </div>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #00d26a33', overflow: 'hidden', flex: 1 }}>
                          <div style={{ backgroundColor: '#0a1a0a', padding: '8px 12px', borderBottom: '1px solid #00d26a33' }}>
                            <span style={{ color: '#00d26a', fontSize: '11px', fontWeight: 'bold' }}>🟢 Active Sessions</span>
                          </div>
                          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                            {(adminData.activeSessions || []).map((s, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 12px', borderBottom: '1px solid #1a1a1a' }}>
                                <span style={{ color: '#fff', fontSize: '10px' }}>${s.handle}</span>
                                <span style={{ color: s.currentToken ? '#f0b90b' : '#444', fontSize: '9px' }}>{s.currentToken ? `$${s.currentToken.substring(0,8)}` : 'Lobby'}</span>
                              </div>
                            ))}
                            {(!adminData.activeSessions || adminData.activeSessions.length === 0) && <div style={{ padding: '20px', textAlign: 'center', color: '#444', fontSize: '10px' }}>No sessions</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* CONSENT TAB - Manage player trading gates */}
                  {adminTab === 'consent' && (
                    <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #222', overflow: 'hidden' }}>
                      {/* Header */}
                      <div style={{ backgroundColor: '#111', padding: '12px 16px', borderBottom: '1px solid #222' }}>
                        <span style={{ color: '#fff', fontSize: '13px', fontWeight: 'bold' }}>✅ Trading Consent Management</span>
                        <p style={{ color: '#888', fontSize: '11px', margin: '6px 0 0' }}>
                          Players must complete consent + tutorial before trading. Use this to check status or fix stuck players.
                        </p>
                      </div>
                      
                      <div style={{ padding: '16px' }}>
                        {/* Single Player Check */}
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #333' }}>
                          <h4 style={{ color: '#f0b90b', margin: '0 0 12px', fontSize: '12px' }}>🔍 Check Single Player</h4>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <input 
                              type="text" 
                              id="consentCheckHandle"
                              placeholder="Enter handle (e.g. clunkykong)"
                              style={{ flex: 1, minWidth: '150px', backgroundColor: '#0a0a0f', border: '1px solid #333', borderRadius: '6px', padding: '8px 12px', color: '#fff', fontSize: '12px' }}
                            />
                            <button
                              onClick={async () => {
                                const handle = document.getElementById('consentCheckHandle').value.trim();
                                if (!handle) { alert('Enter a handle'); return; }
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/consent/${handle}`, {
                                    headers: { 'X-Session-Id': sessionId }
                                  });
                                  const data = await res.json();
                                  if (data.error) { alert('Error: ' + data.error); return; }
                                  document.getElementById('consentResult').innerHTML = `
                                    <div style="color: #fff; font-size: 11px;">
                                      <strong style="color: #f0b90b">$${data.handle}</strong><br/>
                                      <span style="color: ${data.rakeConsent ? '#00d26a' : '#ff3b69'}">● Rake Consent: ${data.rakeConsent ? 'YES' : 'NO'}</span>
                                      ${data.rakeConsentAt ? `<span style="color: #666"> (${new Date(data.rakeConsentAt).toLocaleDateString()})</span>` : ''}<br/>
                                      <span style="color: ${data.tutorialComplete ? '#00d26a' : '#ff3b69'}">● Tutorial: ${data.tutorialComplete ? 'DONE' : 'NOT DONE'}</span>
                                      ${data.tutorialCompletedAt ? `<span style="color: #666"> (${new Date(data.tutorialCompletedAt).toLocaleDateString()})</span>` : ''}<br/>
                                      <span style="color: #888">● Daily rake warnings: stored in player's browser</span>
                                    </div>
                                  `;
                                } catch (e) { alert('Error: ' + e.message); }
                              }}
                              style={{ backgroundColor: '#1a1a2e', border: '1px solid #f0b90b', borderRadius: '6px', padding: '8px 16px', color: '#f0b90b', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                            >🔍 Check</button>
                            <button
                              onClick={async () => {
                                const handle = document.getElementById('consentCheckHandle').value.trim();
                                if (!handle) { alert('Enter a handle'); return; }
                                if (!window.confirm(`Grant consent + tutorial complete to $${handle}?`)) return;
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/consent`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                    body: JSON.stringify({ handle })
                                  });
                                  const data = await res.json();
                                  if (data.error) { alert('Error: ' + data.error); return; }
                                  alert(`✅ Granted consent to $${data.handle}`);
                                  document.getElementById('consentResult').innerHTML = `<span style="color: #00d26a">✅ $${data.handle} can now trade!</span>`;
                                } catch (e) { alert('Error: ' + e.message); }
                              }}
                              style={{ backgroundColor: '#0a2a0a', border: '1px solid #00d26a', borderRadius: '6px', padding: '8px 16px', color: '#00d26a', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                            >✅ Grant Consent</button>
                          </div>
                          <div id="consentResult" style={{ marginTop: '12px', padding: '10px', backgroundColor: '#0a0a0f', borderRadius: '6px', minHeight: '40px' }}></div>
                        </div>
                        
                        {/* Mass Consent */}
                        <div style={{ backgroundColor: '#1a0a0a', borderRadius: '8px', padding: '16px', border: '1px solid #ff3b6944' }}>
                          <h4 style={{ color: '#ff3b69', margin: '0 0 12px', fontSize: '12px' }}>⚠️ Mass Consent (All Players)</h4>
                          <p style={{ color: '#888', fontSize: '11px', margin: '0 0 12px' }}>
                            Grant consent + tutorial complete to ALL players who don't have it yet.
                          </p>
                          <button
                            onClick={async () => {
                              if (!window.confirm('⚠️ Grant consent to ALL players?\n\nThis will allow everyone to trade immediately.')) return;
                              if (!window.confirm('Are you SURE? This affects all users.')) return;
                              try {
                                const res = await fetch(`${API_URL}/api/admin/consent`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                  body: JSON.stringify({ all: true })
                                });
                                const data = await res.json();
                                if (data.error) { alert('Error: ' + data.error); return; }
                                alert(data.message);
                              } catch (e) { alert('Error: ' + e.message); }
                            }}
                            style={{ backgroundColor: '#2a0a0a', border: '1px solid #ff3b69', borderRadius: '6px', padding: '10px 20px', color: '#ff3b69', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >🔓 GRANT ALL CONSENT</button>
                        </div>
                        
                        {/* Info Box */}
                        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#0a0a1a', borderRadius: '8px', border: '1px solid #33336666' }}>
                          <h5 style={{ color: '#6366f1', margin: '0 0 8px', fontSize: '11px' }}>ℹ️ Trading Gates Explained</h5>
                          <div style={{ fontSize: '10px', color: '#888', lineHeight: '1.6' }}>
                            <strong style={{ color: '#fff' }}>Permanent (server-side):</strong><br/>
                            • <strong>rakeConsent</strong> - Accepted 1¢ fee structure<br/>
                            • <strong>tutorialComplete</strong> - Finished 4-step tutorial<br/><br/>
                            <strong style={{ color: '#fff' }}>Daily (browser localStorage):</strong><br/>
                            • <strong>hasSeenAIRakeToday</strong> - AI token warning acknowledged<br/>
                            • <strong>hasSeenPvPRakeToday</strong> - PvP token warning acknowledged<br/><br/>
                            <em>If daily gates are stuck, player needs to clear browser cache or wait until tomorrow.</em>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* MOUNTAIN CLIMBER TAB - Battle Royale Crash */}
                  {adminTab === 'mountain' && adminData?.userRole === 'admin' && (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                      {/* Left: Current Round + Controls */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Kill Switch */}
                        <div style={{ backgroundColor: '#0a0f1a', borderRadius: '8px', border: `1px solid ${adminData?.mountainHalted ? '#ff3b69' : '#4ecdc4'}`, padding: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>🛑 Kill Switch</div>
                              <div style={{ color: '#666', fontSize: '10px' }}>
                                {adminData?.mountainHalted ? 'Mountain Climber is HALTED' : 'Mountain Climber is LIVE'}
                              </div>
                            </div>
                            <button
                              onClick={async () => {
                                await fetch(`${API_URL}/api/admin/halt-mountain`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                  body: JSON.stringify({ halted: !adminData?.mountainHalted })
                                });
                                fetchAdminDashboard();
                              }}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: adminData?.mountainHalted ? '#4ecdc4' : '#ff3b69',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#000',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              {adminData?.mountainHalted ? 'RESUME' : 'HALT'}
                            </button>
                          </div>
                        </div>

                        {/* Visibility Toggle */}
                        <div style={{ backgroundColor: '#0a0f1a', borderRadius: '8px', border: `1px solid ${adminData?.gameVisibility?.mountainVisible ? '#4ecdc4' : '#ff3b69'}`, padding: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>👁️ Button Visibility</div>
                              <div style={{ color: '#666', fontSize: '10px' }}>
                                {adminData?.gameVisibility?.mountainVisible ? 'Button visible to all users' : 'Button hidden from regular users'}
                              </div>
                            </div>
                            <button
                              onClick={async () => {
                                await fetch(`${API_URL}/api/admin/game-visibility`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                  body: JSON.stringify({ mountainVisible: !adminData?.gameVisibility?.mountainVisible })
                                });
                                fetchAdminDashboard();
                              }}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: adminData?.gameVisibility?.mountainVisible ? '#ff3b69' : '#4ecdc4',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#000',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              {adminData?.gameVisibility?.mountainVisible ? 'HIDE' : 'SHOW'}
                            </button>
                          </div>
                        </div>

                        {/* Current Round */}
                        <div style={{ backgroundColor: '#0a0f1a', borderRadius: '8px', border: '1px solid #4ecdc4', padding: '12px' }}>
                          <div style={{ color: '#4ecdc4', fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>🏔️ Current Round</div>
                          {adminData?.mountainStats?.currentRound ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                              <div><span style={{ color: '#666' }}>Phase:</span> <span style={{ color: adminData.mountainStats.currentRound.phase === 'climbing' ? '#4ecdc4' : adminData.mountainStats.currentRound.phase === 'betting' ? '#f0b90b' : '#ff3b69' }}>{adminData.mountainStats.currentRound.phase?.toUpperCase()}</span></div>
                              <div><span style={{ color: '#666' }}>Climbers:</span> <span style={{ color: '#fff' }}>{adminData.mountainStats.currentRound.climberCount}/{adminData.mountainStats.config?.maxClimbers || 15}</span></div>
                              <div><span style={{ color: '#666' }}>Level:</span> <span style={{ color: '#4ecdc4' }}>{(adminData.mountainStats.currentRound.currentLevel || 1).toFixed(2)}x</span></div>
                              <div><span style={{ color: '#666' }}>Crash Point:</span> <span style={{ color: '#ff3b69' }}>{adminData.mountainStats.currentRound.crashPoint ? adminData.mountainStats.currentRound.crashPoint.toFixed(2) + 'x' : '???'}</span></div>
                            </div>
                          ) : (
                            <div style={{ color: '#666', fontSize: '10px' }}>No active round</div>
                          )}
                        </div>

                        {/* Config - Editable */}
                        <div style={{ backgroundColor: '#0a0f1a', borderRadius: '8px', border: '1px solid #4ecdc4', padding: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <div style={{ color: '#4ecdc4', fontSize: '12px', fontWeight: 'bold' }}>⚙️ Live Config</div>
                            <button
                              onClick={async () => {
                                const config = {
                                  minBet: parseInt(document.getElementById('mc-minBet').value) * 1000,
                                  maxBet: parseInt(document.getElementById('mc-maxBet').value) * 1000,
                                  maxPayout: parseFloat(document.getElementById('mc-maxPayout').value) * 1000000,
                                  maxClimbers: parseInt(document.getElementById('mc-maxClimbers').value),
                                  bettingPhaseMs: parseInt(document.getElementById('mc-bettingPhase').value) * 1000,
                                  climbDurationMs: parseInt(document.getElementById('mc-climbDuration').value) * 1000,
                                };
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/mountain-config`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                    body: JSON.stringify(config)
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    alert('Config updated!');
                                    fetchAdminDashboard();
                                  } else {
                                    alert('Error: ' + data.error);
                                  }
                                } catch (e) {
                                  alert('Failed: ' + e.message);
                                }
                              }}
                              style={{
                                padding: '4px 12px',
                                backgroundColor: '#4ecdc4',
                                border: 'none',
                                borderRadius: '4px',
                                color: '#000',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              💾 Save
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ color: '#666', width: '70px' }}>Min Bet:</label>
                              <input id="mc-minBet" type="number" defaultValue={(adminData?.mountainStats?.config?.minBet || 10000) / 1000} style={{ width: '60px', backgroundColor: '#1a2233', border: '1px solid #333', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '10px' }} />
                              <span style={{ color: '#444' }}>K</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ color: '#666', width: '70px' }}>Max Bet:</label>
                              <input id="mc-maxBet" type="number" defaultValue={(adminData?.mountainStats?.config?.maxBet || 500000) / 1000} style={{ width: '60px', backgroundColor: '#1a2233', border: '1px solid #333', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '10px' }} />
                              <span style={{ color: '#444' }}>K</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ color: '#666', width: '70px' }}>Max Payout:</label>
                              <input id="mc-maxPayout" type="number" step="0.1" defaultValue={(adminData?.mountainStats?.config?.maxPayout || 2500000) / 1000000} style={{ width: '60px', backgroundColor: '#1a2233', border: '1px solid #333', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '10px' }} />
                              <span style={{ color: '#444' }}>M</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ color: '#666', width: '70px' }}>Max Players:</label>
                              <input id="mc-maxClimbers" type="number" min="5" max="30" defaultValue={adminData?.mountainStats?.config?.maxClimbers || 15} style={{ width: '60px', backgroundColor: '#1a2233', border: '1px solid #333', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '10px' }} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ color: '#666', width: '70px' }}>Bet Phase:</label>
                              <input id="mc-bettingPhase" type="number" min="5" max="60" defaultValue={(adminData?.mountainStats?.config?.bettingPhaseMs || 15000) / 1000} style={{ width: '60px', backgroundColor: '#1a2233', border: '1px solid #333', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '10px' }} />
                              <span style={{ color: '#444' }}>s</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ color: '#666', width: '70px' }}>Climb Time:</label>
                              <input id="mc-climbDuration" type="number" min="30" max="180" defaultValue={(adminData?.mountainStats?.config?.climbDurationMs || 60000) / 1000} style={{ width: '60px', backgroundColor: '#1a2233', border: '1px solid #333', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '10px' }} />
                              <span style={{ color: '#444' }}>s</span>
                            </div>
                          </div>
                          <div style={{ marginTop: '8px', padding: '6px', backgroundColor: '#0d1520', borderRadius: '4px', fontSize: '9px', color: '#666' }}>
                            House Edge: {((adminData?.mountainStats?.config?.houseEdge || 0.04) * 100).toFixed(0)}% • Max Multiplier: {adminData?.mountainStats?.config?.maxMultiplier || 5}x
                          </div>
                        </div>

                        {/* Tier Fees */}
                        <div style={{ backgroundColor: '#0a0f1a', borderRadius: '8px', border: '1px solid #c9a227', padding: '12px' }}>
                          <div style={{ color: '#c9a227', fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>💰 Tier Fees</div>
                          <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ color: '#cd7f32' }}>🥉 Bronze</div>
                              <div style={{ color: '#fff' }}>1¢ / 1 atk</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ color: '#c0c0c0' }}>🥈 Silver</div>
                              <div style={{ color: '#fff' }}>2¢ / 2 atk</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ color: '#ffd700' }}>🥇 Gold</div>
                              <div style={{ color: '#fff' }}>3¢ / 3 atk</div>
                            </div>
                          </div>
                        </div>

                        {/* Revenue Stats */}
                        <div style={{ backgroundColor: '#0a0f1a', borderRadius: '8px', border: '1px solid #00d26a', padding: '12px' }}>
                          <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>📊 Revenue</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                            <div><span style={{ color: '#666' }}>Total Bets:</span> <span style={{ color: '#fff' }}>{adminData?.mountainStats?.totalBets || 0}</span></div>
                            <div><span style={{ color: '#666' }}>Volume:</span> <span style={{ color: '#fff' }}>{formatPB(adminData?.mountainStats?.totalVolume || 0)}</span></div>
                            <div><span style={{ color: '#666' }}>Tier Fees:</span> <span style={{ color: '#fff' }}>{formatPB(adminData?.mountainStats?.revenue?.tierFees || 0)}</span></div>
                            <div><span style={{ color: '#666' }}>House Edge:</span> <span style={{ color: '#fff' }}>{formatPB(adminData?.mountainStats?.revenue?.houseEdge || 0)}</span></div>
                            <div style={{ gridColumn: 'span 2', borderTop: '1px solid #222', paddingTop: '6px', marginTop: '4px' }}>
                              <span style={{ color: '#00d26a' }}>TOTAL:</span> <span style={{ color: '#00d26a', fontWeight: 'bold' }}>{formatPB(adminData?.mountainStats?.revenue?.total || 0)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Recent Avalanches + Leaderboard */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Recent Avalanches */}
                        <div style={{ backgroundColor: '#0a0f1a', borderRadius: '8px', border: '1px solid #222', overflow: 'hidden' }}>
                          <div style={{ backgroundColor: '#0d1520', padding: '8px 12px', borderBottom: '1px solid #222' }}>
                            <span style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>❄️ Recent Avalanches</span>
                          </div>
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {(adminData?.mountainStats?.history || []).slice(0, 15).map((round, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #1a2233' }}>
                                <span style={{
                                  color: round.crashLevel >= 4 ? '#4ecdc4' : round.crashLevel >= 2.5 ? '#f0b90b' : '#ff3b69',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  {round.crashLevel?.toFixed(2)}x
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px' }}>
                                  <span style={{ color: '#666' }}>{round.climberCount || 0} climbers</span>
                                  <span style={{ color: '#4ecdc4' }}>{round.winners || 0} safe</span>
                                  <span style={{ color: '#ff3b69' }}>{round.losers || 0} fell</span>
                                </div>
                              </div>
                            ))}
                            {(!adminData?.mountainStats?.history || adminData.mountainStats.history.length === 0) && (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#444', fontSize: '10px' }}>No rounds yet</div>
                            )}
                          </div>
                        </div>

                        {/* Leaderboard */}
                        <div style={{ backgroundColor: '#0a0f1a', borderRadius: '8px', border: '1px solid #222', overflow: 'hidden' }}>
                          <div style={{ backgroundColor: '#0d1520', padding: '8px 12px', borderBottom: '1px solid #222' }}>
                            <span style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>🏆 Top Climbers</span>
                          </div>
                          <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                            {(adminData?.mountainStats?.leaderboard || []).map((player, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #1a2233' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ color: i < 3 ? '#ffd700' : '#666', fontSize: '10px' }}>{i + 1}.</span>
                                  <span style={{ color: '#fff', fontSize: '11px' }}>{player.handle}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px' }}>
                                  <span style={{ color: player.points >= 0 ? '#4ecdc4' : '#ff3b69' }}>
                                    {player.points >= 0 ? '+' : ''}{(player.points / 1000).toFixed(0)}K
                                  </span>
                                  <span style={{ color: '#666' }}>{player.wins} wins</span>
                                </div>
                              </div>
                            ))}
                            {(!adminData?.mountainStats?.leaderboard || adminData.mountainStats.leaderboard.length === 0) && (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#444', fontSize: '10px' }}>No climbers yet</div>
                            )}
                          </div>
                        </div>

                        {/* Live Climbers */}
                        {adminData?.mountainStats?.currentRound?.climbers && Object.keys(adminData.mountainStats.currentRound.climbers).length > 0 && (
                          <div style={{ backgroundColor: '#0a0f1a', borderRadius: '8px', border: '1px solid #4ecdc4', overflow: 'hidden' }}>
                            <div style={{ backgroundColor: '#0d1520', padding: '8px 12px', borderBottom: '1px solid #222' }}>
                              <span style={{ color: '#4ecdc4', fontSize: '11px', fontWeight: 'bold' }}>⛰️ Live Climbers</span>
                            </div>
                            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                              {Object.entries(adminData.mountainStats.currentRound.climbers).map(([handle, c]) => (
                                <div key={handle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 12px', borderBottom: '1px solid #1a2233', fontSize: '10px' }}>
                                  <span style={{ color: c.cashedOut ? '#4ecdc4' : '#fff' }}>
                                    {c.tier === 'gold' ? '🥇' : c.tier === 'silver' ? '🥈' : '🥉'} {handle}
                                    {c.cashedOut && ' ⛳'}
                                  </span>
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <span style={{ color: '#666' }}>{(c.amount / 1000).toFixed(0)}K</span>
                                    <span style={{ color: c.cashedOut ? '#4ecdc4' : '#888' }}>{(c.level || 1).toFixed(2)}x</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* RUG RUSH TAB - Crash Game Monitoring */}
                  {adminTab === 'rugrush' && adminData?.userRole === 'admin' && (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                      {/* Left: Current Round + Controls */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Kill Switch */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: `1px solid ${adminData?.rugRushHalted ? '#ff3b69' : '#00d26a'}`, padding: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>🛑 Kill Switch</div>
                              <div style={{ color: '#666', fontSize: '10px' }}>
                                {adminData?.rugRushHalted ? 'Rug Rush is HALTED' : 'Rug Rush is LIVE'}
                              </div>
                            </div>
                            <button
                              onClick={async () => {
                                await fetch(`${API_URL}/api/admin/halt-rugrush`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                  body: JSON.stringify({ halted: !adminData?.rugRushHalted })
                                });
                                fetchAdminDashboard();
                              }}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: adminData?.rugRushHalted ? '#00d26a' : '#ff3b69',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#000',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              {adminData?.rugRushHalted ? 'RESUME' : 'HALT'}
                            </button>
                          </div>
                        </div>

                        {/* Visibility Toggle */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: `1px solid ${adminData?.gameVisibility?.rugRushVisible ? '#00d26a' : '#ff3b69'}`, padding: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>👁️ Button Visibility</div>
                              <div style={{ color: '#666', fontSize: '10px' }}>
                                {adminData?.gameVisibility?.rugRushVisible ? 'Button visible to all users' : 'Button hidden from regular users'}
                              </div>
                            </div>
                            <button
                              onClick={async () => {
                                await fetch(`${API_URL}/api/admin/game-visibility`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                  body: JSON.stringify({ rugRushVisible: !adminData?.gameVisibility?.rugRushVisible })
                                });
                                fetchAdminDashboard();
                              }}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: adminData?.gameVisibility?.rugRushVisible ? '#ff3b69' : '#00d26a',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#000',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              {adminData?.gameVisibility?.rugRushVisible ? 'HIDE' : 'SHOW'}
                            </button>
                          </div>
                        </div>

                        {/* Current Round */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #ff6b35', padding: '12px' }}>
                          <div style={{ color: '#ff6b35', fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>🎰 Current Round</div>
                          {adminData?.rugRushStats?.currentRound ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                              <div><span style={{ color: '#666' }}>Phase:</span> <span style={{ color: adminData.rugRushStats.currentRound.phase === 'running' ? '#00d26a' : '#f0b90b' }}>{adminData.rugRushStats.currentRound.phase?.toUpperCase()}</span></div>
                              <div><span style={{ color: '#666' }}>Tier:</span> <span style={{ color: '#fff' }}>{adminData.rugRushStats.currentRound.tierEmoji} {adminData.rugRushStats.currentRound.tierName}</span></div>
                              <div><span style={{ color: '#666' }}>Players:</span> <span style={{ color: '#fff' }}>{adminData.rugRushStats.currentRound.playerCount}</span></div>
                              <div><span style={{ color: '#666' }}>Pot:</span> <span style={{ color: '#f0b90b' }}>{formatPB(adminData.rugRushStats.currentRound.totalPot || 0)}</span></div>
                              <div><span style={{ color: '#666' }}>Multiplier:</span> <span style={{ color: '#00d26a' }}>{(adminData.rugRushStats.currentRound.currentMultiplier || 1).toFixed(2)}x</span></div>
                              <div><span style={{ color: '#666' }}>Crash Point:</span> <span style={{ color: '#ff3b69' }}>{adminData.rugRushStats.currentRound.crashPoint?.toFixed(2)}x</span></div>
                            </div>
                          ) : (
                            <div style={{ color: '#666', fontSize: '10px' }}>No active round</div>
                          )}
                        </div>

                        {/* Revenue Stats */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #00d26a', padding: '12px' }}>
                          <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>💰 Revenue</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                            <div><span style={{ color: '#666' }}>Bet Fees:</span> <span style={{ color: '#fff' }}>{formatPB(adminData?.rugRushStats?.revenue?.betFees || 0)}</span></div>
                            <div><span style={{ color: '#666' }}>Entry Fees:</span> <span style={{ color: '#fff' }}>{formatPB(adminData?.rugRushStats?.revenue?.entryFees || 0)}</span></div>
                            <div><span style={{ color: '#666' }}>Taunt Fees:</span> <span style={{ color: '#fff' }}>{formatPB(adminData?.rugRushStats?.revenue?.tauntFees || 0)}</span></div>
                            <div><span style={{ color: '#f0b90b' }}>TOTAL:</span> <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>{formatPB(adminData?.rugRushStats?.revenue?.total || 0)}</span></div>
                          </div>
                          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #222' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                              <span style={{ color: '#666' }}>Total Bets: {adminData?.rugRushStats?.totalBets || 0}</span>
                              <span style={{ color: '#666' }}>Total Taunts: {adminData?.rugRushStats?.totalTaunts || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Recent Crashes + Top Players */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Recent Crashes */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #222', overflow: 'hidden' }}>
                          <div style={{ backgroundColor: '#111', padding: '8px 12px', borderBottom: '1px solid #222' }}>
                            <span style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>💀 Recent Crashes</span>
                          </div>
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {(adminData?.rugRushStats?.recentCrashes || []).slice(0, 15).map((crash, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #1a1a1a' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '10px' }}>{crash.tierEmoji || '🚀'}</span>
                                  <span style={{
                                    color: crash.crashPoint < 1.5 ? '#ff3b69' : crash.crashPoint < 3 ? '#f0b90b' : '#00d26a',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                  }}>
                                    {crash.crashPoint?.toFixed(2)}x
                                  </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px' }}>
                                  <span style={{ color: '#666' }}>{crash.players || 0}p</span>
                                  {crash.lastManStanding && (
                                    <span style={{ color: '#f0b90b' }}>👑 {crash.lastManStanding.handle}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Top Players */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #222', overflow: 'hidden' }}>
                          <div style={{ backgroundColor: '#111', padding: '8px 12px', borderBottom: '1px solid #222' }}>
                            <span style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>🏆 Top Players</span>
                          </div>
                          <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                            {(adminData?.rugRushStats?.topPlayers || []).map((player, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #1a1a1a' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ color: i < 3 ? '#f0b90b' : '#666', fontSize: '10px' }}>{i + 1}.</span>
                                  <span style={{ color: '#fff', fontSize: '11px' }}>${player.handle}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px' }}>
                                  <span style={{ color: '#00d26a' }}>{player.wins} wins</span>
                                  <span style={{ color: '#9b59b6' }}>{player.tauntsUsed || 0} taunts</span>
                                </div>
                              </div>
                            ))}
                            {(!adminData?.rugRushStats?.topPlayers || adminData.rugRushStats.topPlayers.length === 0) && (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#444', fontSize: '10px' }}>No players yet</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* USERS TAB - Enhanced User Management */}
                  {adminTab === 'users' && (
                    <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #222', overflow: 'hidden' }}>
                      {/* Header with stats */}
                      <div style={{ backgroundColor: '#111', padding: '10px 12px', borderBottom: '1px solid #222', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <span style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>👥 All Users ({adminData.users?.length || 0})</span>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '10px' }}>
                          <span style={{ color: '#f0b90b' }}>👑 {adminData.users?.filter(u => u.isPremium).length || 0} premium</span>
                          <span style={{ color: '#00d26a' }}>💰 {formatPB((adminData.users || []).reduce((s, u) => s + (u.gameBalance || 0), 0))} total bal</span>
                          <span style={{ color: '#ff3b69' }}>🚫 {adminData.users?.filter(u => u.banned).length || 0} banned</span>
                        </div>
                      </div>
                      <div style={{ maxHeight: '480px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                          <thead>
                            <tr style={{ color: '#666', textAlign: 'left', position: 'sticky', top: 0, backgroundColor: '#0d0d12', zIndex: 1 }}>
                              <th style={{ padding: '8px 12px', borderBottom: '1px solid #333', fontWeight: 'normal' }}>User</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #333', fontWeight: 'normal', textAlign: 'right' }}>Balance</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #333', fontWeight: 'normal', textAlign: 'right' }}>Deposited</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #333', fontWeight: 'normal', textAlign: 'center' }}>W/L</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #333', fontWeight: 'normal', textAlign: 'right' }}>P&L</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #333', fontWeight: 'normal', textAlign: 'right' }}>🔓/🔒</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #333', fontWeight: 'normal', textAlign: 'right' }}>Fee ROI</th>
                              <th style={{ padding: '8px 12px', borderBottom: '1px solid #333', fontWeight: 'normal', textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(adminData.users || []).map(u => (
                              <tr key={u.handle} style={{ backgroundColor: u.suspicious ? '#1a080811' : u.banned ? '#11111188' : 'transparent' }}>
                                <td style={{ padding: '6px 12px', borderBottom: '1px solid #1a1a1a' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ color: u.banned ? '#666' : u.suspicious ? '#ff3b69' : '#fff', fontWeight: 'bold' }}>${u.handle}</span>
                                    {u.rakeConsent && u.tutorialComplete ? 
                                      <span style={{ fontSize: '10px' }} title="Consent complete">✅</span> : 
                                      <span style={{ fontSize: '10px', opacity: 0.5 }} title="Missing consent">⚪</span>
                                    }
                                    {u.isAdmin && <span style={{ fontSize: '9px', color: '#f0b90b', padding: '1px 4px', backgroundColor: '#f0b90b22', borderRadius: '3px' }}>ADMIN</span>}
                                    {u.isModerator && <span style={{ fontSize: '9px', color: '#6366f1', padding: '1px 4px', backgroundColor: '#6366f122', borderRadius: '3px' }}>MOD</span>}
                                    {u.isPremium && <span style={{ fontSize: '10px' }}>👑</span>}
                                    {u.banned && <span style={{ fontSize: '9px', color: '#ff3b69', padding: '1px 4px', backgroundColor: '#ff3b6922', borderRadius: '3px' }}>BANNED</span>}
                                    {u.suspicious && !u.banned && <span style={{ fontSize: '9px', color: '#ff8c00', padding: '1px 4px', backgroundColor: '#ff8c0022', borderRadius: '3px' }}>SUS</span>}
                                  </div>
                                </td>
                                <td style={{ padding: '6px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right' }}>
                                  <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>{formatPB(u.gameBalance || 0)}</div>
                                  {bsvPrice && <div style={{ color: '#666', fontSize: '9px' }}>${((u.gameBalance / 100000000) * bsvPrice).toFixed(2)}</div>}
                                </td>
                                <td style={{ padding: '6px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right' }}>
                                  <div style={{ color: '#888' }}>{formatPB(u.totalDeposited || 0)}</div>
                                  <div style={{ color: '#666', fontSize: '9px' }}>{u.depositTxCount || 0} txs</div>
                                </td>
                                <td style={{ padding: '6px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'center' }}>
                                  <span style={{ color: '#00d26a' }}>{u.wins || 0}</span>
                                  <span style={{ color: '#666' }}>/</span>
                                  <span style={{ color: '#ff3b69' }}>{u.losses || 0}</span>
                                  <div style={{ color: (u.winRate || 0) > 60 ? '#ff3b69' : '#666', fontSize: '9px' }}>{u.winRate || 0}%</div>
                                </td>
                                <td style={{ padding: '6px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right' }}>
                                  <div style={{ color: (u.totalProfit || 0) >= 0 ? '#00d26a' : '#ff3b69', fontWeight: 'bold' }}>
                                    {(u.totalProfit || 0) >= 0 ? '+' : ''}{formatPB(u.totalProfit || 0)}
                                  </div>
                                </td>
                                <td style={{ padding: '6px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right' }}>
                                  {(u.locked || 0) > 0 ? (
                                    <div title={`Playthrough: ${u.playthroughPercent || 0}% complete`}>
                                      <div style={{ color: '#00d26a', fontSize: '9px' }}>🔓 {formatPB(u.withdrawable || 0)}</div>
                                      <div style={{ color: '#ff8c00', fontSize: '9px' }}>🔒 {formatPB(u.locked || 0)}</div>
                                      <div style={{ color: '#666', fontSize: '8px' }}>{u.playthroughPercent || 0}%</div>
                                    </div>
                                  ) : (
                                    <div style={{ color: '#00d26a', fontSize: '9px' }}>🔓 {formatPB(u.withdrawable || u.gameBalance || 0)}</div>
                                  )}
                                </td>
                                <td style={{ padding: '6px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right' }}>
                                  {(u.totalRakePaid || 0) > 0 ? (
                                    <div>
                                      <div style={{ color: u.feeROI >= 100 ? '#00d26a' : u.feeROI >= 0 ? '#f0b90b' : '#ff3b69', fontWeight: 'bold', fontSize: '10px' }}>
                                        {u.feeROI >= 0 ? '+' : ''}{u.feeROI || 0}%
                                      </div>
                                      <div style={{ color: '#666', fontSize: '8px' }}>{u.totalRakePaid} sats</div>
                                    </div>
                                  ) : (
                                    <span style={{ color: '#444', fontSize: '9px' }}>-</span>
                                  )}
                                </td>
                                <td style={{ padding: '6px 12px', borderBottom: '1px solid #1a1a1a', textAlign: 'right' }}>
                                  {adminData?.userRole !== 'moderator' ? (
                                  <div style={{ display: 'flex', gap: '3px', justifyContent: 'flex-end' }}>
                                    <button onClick={() => adminSetBalance(u.handle)} style={{ backgroundColor: '#1a1508', border: '1px solid #f0b90b44', borderRadius: '4px', padding: '3px 6px', color: '#f0b90b', fontSize: '9px', cursor: 'pointer' }} title="Set Balance">💰</button>
                                    <button onClick={() => adminZeroBalance(u.handle)} style={{ backgroundColor: '#1a0808', border: '1px solid #ff3b6944', borderRadius: '4px', padding: '3px 6px', color: '#ff3b69', fontSize: '9px', cursor: 'pointer' }} title="Zero">0</button>
                                    <button onClick={() => adminBanUser(u.handle, u.banned)} style={{ backgroundColor: u.banned ? '#0a1a0a' : '#1a0808', border: `1px solid ${u.banned ? '#00d26a44' : '#ff3b6944'}`, borderRadius: '4px', padding: '3px 6px', color: u.banned ? '#00d26a' : '#ff3b69', fontSize: '9px', cursor: 'pointer' }} title={u.banned ? 'Unban' : 'Ban'}>{u.banned ? '✓' : '🚫'}</button>
                                    <button onClick={async () => {
                                      if (!confirm(`DELETE user $${u.handle}?\n\nBalance: ${formatPB(u.gameBalance || 0)}\n\nThis cannot be undone!`)) return;
                                      try {
                                        const res = await fetch(`${API_URL}/api/admin/delete-user`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                          body: JSON.stringify({ handle: u.handle })
                                        });
                                        const data = await res.json();
                                        if (data.error) alert('Error: ' + data.error);
                                        else { alert(`✅ Deleted $${u.handle}`); fetchAdminDashboard(); }
                                      } catch (e) { alert('Error: ' + e.message); }
                                    }} style={{ backgroundColor: '#2a0808', border: '1px solid #ff3b69', borderRadius: '4px', padding: '3px 6px', color: '#ff3b69', fontSize: '9px', cursor: 'pointer' }} title="Delete User">🗑</button>
                                    {/* Moderator Toggle - only show for non-admins */}
                                    {!u.isAdmin && (
                                      <button onClick={async () => {
                                        try {
                                          const res = await fetch(`${API_URL}/api/admin/toggle-moderator`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                            body: JSON.stringify({ handle: u.handle })
                                          });
                                          const data = await res.json();
                                          if (data.error) alert('Error: ' + data.error);
                                          else { alert(`✅ ${data.message}`); fetchAdminDashboard(); }
                                        } catch (e) { alert('Error: ' + e.message); }
                                      }} style={{ backgroundColor: u.isModerator ? '#0a0820' : '#1a1a2e', border: `1px solid ${u.isModerator ? '#6366f1' : '#66666644'}`, borderRadius: '4px', padding: '3px 6px', color: u.isModerator ? '#6366f1' : '#666', fontSize: '9px', cursor: 'pointer' }} title={u.isModerator ? 'Remove Moderator' : 'Make Moderator'}>👁️</button>
                                    )}
                                    {/* Watch button - add to watchlist */}
                                    {!u.isAdmin && (
                                      <button onClick={() => addToWatchlist(u.handle)} style={{ backgroundColor: '#1a1a2e', border: '1px solid #6366f133', borderRadius: '4px', padding: '3px 6px', color: '#6366f1', fontSize: '9px', cursor: 'pointer' }} title="Add to Watchlist">🔍</button>
                                    )}
                                  </div>
                                  ) : (
                                    <span style={{ color: '#666', fontSize: '9px' }}>View only</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* TOKENS TAB - PvP Token Management */}
                  {adminTab === 'tokens' && (
                    <>
                    <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #f0b90b33', overflow: 'hidden' }}>
                      {/* Header */}
                      <div style={{ backgroundColor: '#1a1508', padding: '10px 12px', borderBottom: '1px solid #f0b90b33', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <span style={{ color: '#f0b90b', fontSize: '11px', fontWeight: 'bold' }}>🚀 BSV.pump Tokens ({adminData.userTokens?.length || 0})</span>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '10px' }}>
                          <span style={{ color: '#00d26a' }}>💰 {formatPB((adminData.userTokens || []).reduce((s, t) => s + (t.liquidityPool || 0), 0))} total liq</span>
                          <span style={{ color: '#888' }}>👥 {(adminData.userTokens || []).reduce((s, t) => s + (t.positions?.length || 0), 0)} positions</span>
                        </div>
                      </div>
                      <div style={{ maxHeight: '480px', overflowY: 'auto', padding: '8px' }}>
                        {(adminData.userTokens || []).map(t => {
                          const isExp = expandedAdminTokens[t.id];
                          const totInv = (t.positions || []).reduce((s, p) => s + (p.invested || 0), 0);
                          return (
                            <div key={t.id} style={{ backgroundColor: t.paused ? '#1a1a0811' : '#111', borderRadius: '6px', marginBottom: '6px', border: t.paused ? '1px solid #f0b90b' : '1px solid #222', overflow: 'hidden' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', cursor: 'pointer' }} onClick={() => setExpandedAdminTokens(p => ({ ...p, [t.id]: !p[t.id] }))}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                                  <span style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '12px' }}>${t.ticker}</span>
                                  {t.paused && <span style={{ fontSize: '9px', color: '#f0b90b', padding: '2px 6px', backgroundColor: '#f0b90b22', borderRadius: '3px' }}>PAUSED</span>}
                                  <span style={{ color: '#666', fontSize: '10px' }}>by {t.creator}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                  <div style={{ textAlign: 'right', fontSize: '10px' }}>
                                    <div style={{ color: '#00d26a' }}>{formatPB(t.liquidityPool || 0)} liq</div>
                                    <div style={{ color: '#666' }}>{(t.positions || []).length} holders</div>
                                  </div>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    <button onClick={(e) => { e.stopPropagation(); adminPauseToken(t.id, t.ticker, t.paused); }} style={{ backgroundColor: t.paused ? '#0a1a0a' : '#1a1508', border: `1px solid ${t.paused ? '#00d26a44' : '#f0b90b44'}`, borderRadius: '4px', padding: '4px 8px', color: t.paused ? '#00d26a' : '#f0b90b', fontSize: '10px', cursor: 'pointer' }}>{t.paused ? '▶' : '⏸'}</button>
                                    <button onClick={(e) => { e.stopPropagation(); adminForceClosePositions(t.id, t.ticker); }} style={{ backgroundColor: '#1a0808', border: '1px solid #ff3b6944', borderRadius: '4px', padding: '4px 8px', color: '#ff3b69', fontSize: '10px', cursor: 'pointer' }}>💥</button>
                                    <button onClick={(e) => { e.stopPropagation(); adminDeleteToken(t.id, t.ticker); }} style={{ backgroundColor: '#ff3b69', border: 'none', borderRadius: '4px', padding: '4px 8px', color: '#fff', fontSize: '10px', cursor: 'pointer' }}>🗑</button>
                                  </div>
                                  <span style={{ color: '#444', fontSize: '10px' }}>{isExp ? '▼' : '▶'}</span>
                                </div>
                              </div>
                              {isExp && (
                                <div style={{ backgroundColor: '#0a0a0f', borderTop: '1px solid #222', padding: '8px 12px' }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '8px', fontSize: '9px' }}>
                                    <div style={{ backgroundColor: '#111', padding: '6px', borderRadius: '4px', textAlign: 'center' }}>
                                      <div style={{ color: '#666' }}>Price</div>
                                      <div style={{ color: '#fff' }}>{t.currentPrice?.toFixed(6) || '?'}</div>
                                    </div>
                                    <div style={{ backgroundColor: '#111', padding: '6px', borderRadius: '4px', textAlign: 'center' }}>
                                      <div style={{ color: '#666' }}>Invested</div>
                                      <div style={{ color: '#f0b90b' }}>{formatPB(totInv)}</div>
                                    </div>
                                    <div style={{ backgroundColor: '#111', padding: '6px', borderRadius: '4px', textAlign: 'center' }}>
                                      <div style={{ color: '#666' }}>Supply</div>
                                      <div style={{ color: '#888' }}>{((t.tokenReserve || 0) / 1000000).toFixed(1)}M</div>
                                    </div>
                                    <div style={{ backgroundColor: '#111', padding: '6px', borderRadius: '4px', textAlign: 'center' }}>
                                      <div style={{ color: '#666' }}>Trades</div>
                                      <div style={{ color: '#888' }}>{t.tradeCount || 0}</div>
                                    </div>
                                  </div>
                                  <div style={{ fontSize: '10px', maxHeight: '120px', overflowY: 'auto' }}>
                                    {(t.positions || []).map((p, i) => (
                                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #1a1a1a' }}>
                                        <span style={{ color: p.handle === t.creator ? '#f0b90b' : '#fff' }}>${p.handle} {p.handle === t.creator && '👑'}</span>
                                        <span style={{ color: '#00d26a' }}>{(p.tokens || 0).toLocaleString()} tkns ({formatPB(p.invested || 0)})</span>
                                      </div>
                                    ))}
                                    {(!t.positions || t.positions.length === 0) && <div style={{ color: '#666', textAlign: 'center', padding: '8px' }}>No holders</div>}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {(!adminData.userTokens || adminData.userTokens.length === 0) && <div style={{ color: '#666', textAlign: 'center', padding: '40px' }}>No active BSV.pump tokens</div>}
                      </div>
                    </div>

                    {/* AI TOKENS - Bleed Control */}
                    <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', border: '1px solid #ff3b6933', overflow: 'hidden', marginTop: '16px' }}>
                      <div style={{ backgroundColor: '#1a0808', padding: '10px 12px', borderBottom: '1px solid #ff3b6933', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <span style={{ color: '#ff3b69', fontSize: '11px', fontWeight: 'bold' }}>🤖 AI Tokens - Bleed Control ({adminData.aiTokensList?.length || 0})</span>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '10px' }}>
                          <span style={{ color: '#ff8c00' }}>🩸 {(adminData.aiTokensList || []).filter(t => t.isBleeding).length} bleeding</span>
                          <span style={{ color: '#ff3b69' }}>💸 {formatPB((adminData.aiTokensList || []).filter(t => t.playerNetPnL > 0).reduce((s, t) => s + t.playerNetPnL, 0))} player profit</span>
                        </div>
                      </div>
                      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px' }}>
                        {(adminData.aiTokensList || [])
                          .sort((a, b) => b.playerNetPnL - a.playerNetPnL) // Sort by highest player profit
                          .map(t => (
                          <div key={t.id} style={{
                            backgroundColor: t.isBleeding ? '#1a0808' : '#111',
                            borderRadius: '6px',
                            marginBottom: '6px',
                            border: t.isBleeding ? '1px solid #ff3b69' : t.playerNetPnL > (bleedConfig?.profitThreshold || 500000) ? '1px solid #ff8c00' : '1px solid #222',
                            padding: '10px 12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                              <span style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '12px' }}>${t.ticker}</span>
                              {t.isBleeding && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '9px', color: '#ff3b69', padding: '2px 6px', backgroundColor: '#ff3b6922', borderRadius: '3px' }}>🩸 BLEEDING</span>
                                  <div style={{ width: '60px', height: '4px', backgroundColor: '#333', borderRadius: '2px', overflow: 'hidden' }}>
                                    <div style={{ width: `${t.bleedProgress}%`, height: '100%', backgroundColor: '#ff3b69', transition: 'width 1s linear' }} />
                                  </div>
                                  <span style={{ fontSize: '8px', color: '#888' }}>{Math.floor(t.bleedProgress)}%</span>
                                </div>
                              )}
                              {!t.isBleeding && t.playerNetPnL > (bleedConfig?.profitThreshold || 500000) && (
                                <span style={{ fontSize: '9px', color: '#ff8c00', padding: '2px 6px', backgroundColor: '#ff8c0022', borderRadius: '3px' }}>⚠️ THRESHOLD</span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <div style={{ textAlign: 'right', fontSize: '10px' }}>
                                <div style={{ color: t.playerNetPnL >= 0 ? '#00d26a' : '#ff3b69' }}>
                                  {t.playerNetPnL >= 0 ? '+' : ''}{formatPB(t.playerNetPnL)} PnL
                                </div>
                                <div style={{ color: '#666' }}>{t.positionCount} holders · {formatPB(t.liquidityPool || 0)} liq</div>
                              </div>
                              {!t.isBleeding && (
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Start splash bleed on $${t.ticker}? Price will decay until profitable players exit. Token recovers after.`)) {
                                      fetch(`${API_URL}/api/admin/bleed-token`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ tokenId: t.id })
                                      }).then(r => r.json()).then(resp => {
                                        if (resp.success) {
                                          alert(`🩸 Bleed started on $${t.ticker}`);
                                        } else {
                                          alert(`❌ ${resp.error || 'Failed to start bleed'}`);
                                        }
                                      }).catch(e => alert(`❌ Error: ${e.message}`));
                                    }
                                  }}
                                  style={{
                                    backgroundColor: '#1a0808',
                                    border: '1px solid #ff3b69',
                                    borderRadius: '4px',
                                    padding: '6px 12px',
                                    color: '#ff3b69',
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  🩸 BLEED
                                </button>
                              )}
                              {t.isBleeding && (
                                <span style={{ color: '#ff3b69', fontSize: '10px', padding: '6px 12px' }}>⏳ In Progress</span>
                              )}
                            </div>
                          </div>
                        ))}
                        {(!adminData.aiTokensList || adminData.aiTokensList.length === 0) && (
                          <div style={{ color: '#666', textAlign: 'center', padding: '40px' }}>No active AI tokens</div>
                        )}
                      </div>
                    </div>
                    </>
                  )}

                  {/* BOTS TAB - Enhanced */}
                  {adminTab === 'bots' && (
                    <div>
                      {/* Bot Performance Header */}
                      <div style={{ background: 'linear-gradient(135deg, #1a0a2e 0%, #0a0a1a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #a855f7' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '24px' }}>🤖</span>
                            <div>
                              <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>Bot Trading System</div>
                              <div style={{ color: '#a855f7', fontSize: '11px' }}>{adminData.botStats?.activeBotUsers || 0} active / {adminData.botStats?.totalBotUsers || 0} total users</div>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>{(adminData.botStats?.totalUserTrades || 0).toLocaleString()}</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>total trades</div>
                          </div>
                        </div>

                        {/* House vs Users P&L Comparison */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div style={{ backgroundColor: 'rgba(0,210,106,0.1)', borderRadius: '8px', padding: '12px', border: '1px solid rgba(0,210,106,0.3)' }}>
                            <div style={{ color: '#00d26a', fontSize: '10px', marginBottom: '4px' }}>House Revenue</div>
                            <div style={{ color: '#00d26a', fontSize: '22px', fontWeight: 'bold' }}>
                              {((adminData.botStats?.houseNetFromBots || 0) / 100000000).toFixed(4)} BSV
                            </div>
                            <div style={{ color: '#666', fontSize: '9px' }}>≈ ${(((adminData.botStats?.houseNetFromBots || 0) / 100000000) * (bsvPrice || 0)).toFixed(2)} USD</div>
                          </div>
                          <div style={{ backgroundColor: (adminData.botStats?.userNetFromBots || 0) >= 0 ? 'rgba(0,210,106,0.1)' : 'rgba(255,59,105,0.1)', borderRadius: '8px', padding: '12px', border: (adminData.botStats?.userNetFromBots || 0) >= 0 ? '1px solid rgba(0,210,106,0.3)' : '1px solid rgba(255,59,105,0.3)' }}>
                            <div style={{ color: (adminData.botStats?.userNetFromBots || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '10px', marginBottom: '4px' }}>User Net P&L</div>
                            <div style={{ color: (adminData.botStats?.userNetFromBots || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '22px', fontWeight: 'bold' }}>
                              {(adminData.botStats?.userNetFromBots || 0) >= 0 ? '+' : ''}{((adminData.botStats?.userNetFromBots || 0) / 100000000).toFixed(4)} BSV
                            </div>
                            <div style={{ color: '#666', fontSize: '9px' }}>{adminData.botStats?.winningTrades || 0} wins / {adminData.botStats?.losingTrades || 0} losses</div>
                          </div>
                        </div>
                      </div>

                      {/* 🤖 MARKET MAKER STATS - HIDDEN (not in use) */}

                      {/* 📊 SUPPLY HEALTH */}
                      {adminData.houseStats?.supplyMetrics && (
                        <div style={{ background: 'linear-gradient(135deg, #0a1a0a 0%, #0a0a1a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: adminData.houseStats.supplyMetrics.rapidInflation ? '2px solid #ff3b69' : '1px solid #00d26a' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '24px' }}>📊</span>
                              <div>
                                <div style={{ color: '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>Paper Buck Supply</div>
                                <div style={{ color: '#888', fontSize: '11px' }}>Ratio: {adminData.houseStats.supplyMetrics.supplyRatio}x deposits</div>
                              </div>
                            </div>
                            {adminData.houseStats.supplyMetrics.rapidInflation && (
                              <div style={{ padding: '6px 12px', backgroundColor: 'rgba(255,59,105,0.2)', borderRadius: '8px', border: '1px solid #ff3b69' }}>
                                <span style={{ color: '#ff3b69', fontSize: '11px', fontWeight: 'bold' }}>⚠️ RAPID INFLATION</span>
                              </div>
                            )}
                          </div>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ backgroundColor: 'rgba(0,210,106,0.1)', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid rgba(0,210,106,0.3)' }}>
                              <div style={{ color: '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>
                                {((adminData.houseStats.supplyMetrics.totalMinted || 0) / 1000000).toFixed(0)}M
                              </div>
                              <div style={{ color: '#666', fontSize: '9px' }}>Total Minted</div>
                            </div>
                            <div style={{ backgroundColor: 'rgba(255,59,105,0.1)', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid rgba(255,59,105,0.3)' }}>
                              <div style={{ color: '#ff3b69', fontSize: '16px', fontWeight: 'bold' }}>
                                🔥 {((adminData.houseStats.supplyMetrics.totalBurned || 0) / 1000000).toFixed(0)}M
                              </div>
                              <div style={{ color: '#666', fontSize: '9px' }}>Total Burned</div>
                            </div>
                            <div style={{ backgroundColor: adminData.houseStats.supplyMetrics.netSupply > 0 ? 'rgba(255,140,0,0.1)' : 'rgba(0,210,106,0.1)', borderRadius: '8px', padding: '10px', textAlign: 'center', border: adminData.houseStats.supplyMetrics.netSupply > 0 ? '1px solid rgba(255,140,0,0.3)' : '1px solid rgba(0,210,106,0.3)' }}>
                              <div style={{ color: adminData.houseStats.supplyMetrics.netSupply > 0 ? '#ff8c00' : '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>
                                {adminData.houseStats.supplyMetrics.netSupply >= 0 ? '+' : ''}{((adminData.houseStats.supplyMetrics.netSupply || 0) / 1000000).toFixed(0)}M
                              </div>
                              <div style={{ color: '#666', fontSize: '9px' }}>Net Change</div>
                            </div>
                          </div>
                          
                          {/* Inflation/Deflation Sources */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                            <div style={{ backgroundColor: '#0a0f0a', borderRadius: '6px', padding: '8px', border: '1px solid #1a2a1a' }}>
                              <div style={{ color: '#666', fontSize: '8px', marginBottom: '4px' }}>📈 INFLATION (token spawns)</div>
                              <div style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#888' }}>Total:</span>
                                <span style={{ color: '#00d26a', fontWeight: 'bold' }}>+{((adminData.houseStats.supplyMetrics.tokenSpawnMinted || 0) / 1000000).toFixed(0)}M</span>
                              </div>
                            </div>
                            <div style={{ backgroundColor: '#0f0a0a', borderRadius: '6px', padding: '8px', border: '1px solid #2a1a1a' }}>
                              <div style={{ color: '#666', fontSize: '8px', marginBottom: '4px' }}>📉 DEFLATION (token endings)</div>
                              <div style={{ fontSize: '9px', display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#888' }}>Rugs:</span>
                                <span style={{ color: '#ff3b69' }}>-{((adminData.houseStats.supplyMetrics.tokenRugBurned || 0) / 1000000).toFixed(0)}M</span>
                              </div>
                              <div style={{ fontSize: '9px', display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#888' }}>Graduated/Expired:</span>
                                <span style={{ color: '#ff3b69' }}>-{((adminData.houseStats.supplyMetrics.tokenEndBurned || 0) / 1000000).toFixed(0)}M</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Supply Breakdown - Where is PB right now? */}
                          {adminData.houseStats.supplyBreakdown && (
                            <div style={{ backgroundColor: '#0a0a0f', borderRadius: '6px', padding: '8px', marginBottom: '12px', border: '1px solid #222' }}>
                              <div style={{ color: '#888', fontSize: '8px', marginBottom: '6px' }}>📍 WHERE IS PB NOW?</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', fontSize: '9px' }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>{((adminData.houseStats.supplyBreakdown.userWallets || 0) / 1000000).toFixed(0)}M</div>
                                  <div style={{ color: '#666', fontSize: '7px' }}>User Wallets</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ color: '#a855f7', fontWeight: 'bold' }}>{((adminData.houseStats.supplyBreakdown.liquidityPools || 0) / 1000000).toFixed(0)}M</div>
                                  <div style={{ color: '#666', fontSize: '7px' }}>Liquidity Pools ({adminData.houseStats.supplyBreakdown.activeTokenCount})</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ color: '#00d26a', fontWeight: 'bold' }}>{((adminData.houseStats.supplyBreakdown.botFunds || 0) / 1000000).toFixed(0)}M</div>
                                  <div style={{ color: '#666', fontSize: '7px' }}>Bot Funds</div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Last Hour */}
                          <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', padding: '10px', border: '1px solid #222' }}>
                            <div style={{ color: '#888', fontSize: '10px', marginBottom: '6px', fontWeight: 'bold' }}>⏱️ Last Hour</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                              <span style={{ color: '#00d26a' }}>+{((adminData.houseStats.supplyMetrics.lastHourMinted || 0) / 1000000).toFixed(1)}M</span>
                              <span style={{ color: '#ff3b69' }}>-{((adminData.houseStats.supplyMetrics.lastHourBurned || 0) / 1000000).toFixed(1)}M</span>
                              <span style={{ color: adminData.houseStats.supplyMetrics.lastHourNet > 0 ? '#ff8c00' : '#00d26a', fontWeight: 'bold' }}>
                                = {adminData.houseStats.supplyMetrics.lastHourNet >= 0 ? '+' : ''}{((adminData.houseStats.supplyMetrics.lastHourNet || 0) / 1000000).toFixed(1)}M net
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Warning Banner */}
                      {(adminData.botStats?.userNetFromBots || 0) > (adminData.botStats?.houseNetFromBots || 0) && (
                        <div style={{ background: 'linear-gradient(90deg, #2a1a0a 0%, #1a0a00 100%)', borderRadius: '8px', padding: '12px', marginBottom: '16px', border: '2px solid #ff8c00', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '24px' }}>⚠️</span>
                          <div>
                            <div style={{ color: '#ff8c00', fontSize: '13px', fontWeight: 'bold' }}>Users profiting more than house!</div>
                            <div style={{ color: '#888', fontSize: '10px' }}>Users: +{((adminData.botStats?.userNetFromBots || 0) / 100000000).toFixed(4)} BSV vs House: +{((adminData.botStats?.houseNetFromBots || 0) / 100000000).toFixed(4)} BSV</div>
                          </div>
                        </div>
                      )}

                      {/* Quick Stats Row */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #222' }}>
                          <div style={{ color: '#ff8c00', fontSize: '18px', fontWeight: 'bold' }}>{((adminData.houseStats?.totalRakeCollected || 0) / 100000000).toFixed(4)}</div>
                          <div style={{ color: '#666', fontSize: '9px' }}>BSV Rake</div>
                        </div>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #222' }}>
                          <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>{((adminData.houseWallet?.bsvBalanceSats || 0) / 100000000).toFixed(4)}</div>
                          <div style={{ color: '#666', fontSize: '9px' }}>BSV In Funds</div>
                        </div>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #222' }}>
                          <div style={{ color: '#a855f7', fontSize: '18px', fontWeight: 'bold' }}>{((adminData.houseStats?.totalDepositSats || 0) / 100000000).toFixed(4)}</div>
                          <div style={{ color: '#666', fontSize: '9px' }}>BSV Volume</div>
                        </div>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #222' }}>
                          <div style={{ color: '#00bcd4', fontSize: '18px', fontWeight: 'bold' }}>{adminData.activeSessions?.length || 0}</div>
                          <div style={{ color: '#666', fontSize: '9px' }}>Active Now</div>
                        </div>
                      </div>

                      {/* Winners vs Losers */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '1px solid #00d26a', overflow: 'hidden' }}>
                          <div style={{ background: 'linear-gradient(90deg, rgba(0,210,106,0.2) 0%, transparent 100%)', padding: '10px 12px', borderBottom: '1px solid #1a1a1a' }}>
                            <span style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>🏆 Top Winners</span>
                          </div>
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {(adminData.botStats?.topBotUsers || []).slice(0, 10).map((u, i) => (
                              <div key={u.handle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #111', fontSize: '11px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ color: i < 3 ? '#f0b90b' : '#666', fontWeight: i < 3 ? 'bold' : 'normal' }}>{i+1}</span>
                                  <span style={{ color: '#ccc' }}>${u.handle}</span>
                                </div>
                                <div style={{ color: '#00d26a', fontWeight: 'bold' }}>+{(u.netProfit / 100000000).toFixed(4)}</div>
                              </div>
                            ))}
                            {(!adminData.botStats?.topBotUsers || adminData.botStats.topBotUsers.length === 0) && <div style={{ color: '#666', padding: '20px', textAlign: 'center' }}>No winners yet</div>}
                          </div>
                        </div>

                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '1px solid #ff3b69', overflow: 'hidden' }}>
                          <div style={{ background: 'linear-gradient(90deg, rgba(255,59,105,0.2) 0%, transparent 100%)', padding: '10px 12px', borderBottom: '1px solid #1a1a1a' }}>
                            <span style={{ color: '#ff3b69', fontSize: '12px', fontWeight: 'bold' }}>📉 Biggest Losers</span>
                          </div>
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {(adminData.botStats?.bottomBotUsers || []).slice(0, 10).map((u, i) => (
                              <div key={u.handle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #111', fontSize: '11px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ color: '#666' }}>{i+1}</span>
                                  <span style={{ color: '#ccc' }}>${u.handle}</span>
                                </div>
                                <div style={{ color: '#ff3b69', fontWeight: 'bold' }}>{(u.netProfit / 100000000).toFixed(4)}</div>
                              </div>
                            ))}
                            {(!adminData.botStats?.bottomBotUsers || adminData.botStats.bottomBotUsers.length === 0) && <div style={{ color: '#666', padding: '20px', textAlign: 'center' }}>No losers yet</div>}
                          </div>
                        </div>
                      </div>

                      {/* Full Bot Users Table */}
                      <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '1px solid #333', overflow: 'hidden' }}>
                        <div style={{ background: 'linear-gradient(90deg, rgba(168,85,247,0.2) 0%, transparent 100%)', padding: '10px 12px', borderBottom: '1px solid #222', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#a855f7', fontSize: '12px', fontWeight: 'bold' }}>🤖 All Bot Users</span>
                          <span style={{ color: '#666', fontSize: '10px' }}>{((adminData.botStats?.topBotUsers || []).length + (adminData.botStats?.bottomBotUsers || []).length)} users</span>
                        </div>
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#111' }}>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '8px', color: '#666', borderBottom: '1px solid #222' }}>User</th>
                                <th style={{ textAlign: 'right', padding: '8px', color: '#666', borderBottom: '1px solid #222' }}>Fund (BSV)</th>
                                <th style={{ textAlign: 'right', padding: '8px', color: '#666', borderBottom: '1px solid #222' }}>Trades</th>
                                <th style={{ textAlign: 'right', padding: '8px', color: '#666', borderBottom: '1px solid #222' }}>W/L</th>
                                <th style={{ textAlign: 'right', padding: '8px', color: '#666', borderBottom: '1px solid #222' }}>Net P&L</th>
                                <th style={{ textAlign: 'right', padding: '8px', color: '#666', borderBottom: '1px solid #222' }}>Rake</th>
                                <th style={{ textAlign: 'right', padding: '8px', color: '#666', borderBottom: '1px solid #222' }}>Days</th>
                                <th style={{ textAlign: 'center', padding: '8px', color: '#666', borderBottom: '1px solid #222' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(adminData.botStats?.topBotUsers || []).concat(adminData.botStats?.bottomBotUsers || [])
                                .filter((u, i, arr) => arr.findIndex(x => x.handle === u.handle) === i)
                                .sort((a, b) => b.fundBalance - a.fundBalance)
                                .map(u => (
                                <tr key={u.handle} style={{ borderBottom: '1px solid #1a1a1a' }}>
                                  <td style={{ padding: '8px', color: '#ccc' }}>
                                    <span style={{ color: u.active ? '#00d26a' : '#666' }}>{u.active ? '●' : '○'}</span> ${u.handle}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '8px', color: '#f0b90b' }}>{(u.fundBalance / 100000000).toFixed(4)}</td>
                                  <td style={{ textAlign: 'right', padding: '8px', color: '#888' }}>{u.trades}</td>
                                  <td style={{ textAlign: 'right', padding: '8px', color: '#888' }}>{u.wins}/{u.losses}</td>
                                  <td style={{ textAlign: 'right', padding: '8px', color: u.netProfit >= 0 ? '#00d26a' : '#ff3b69', fontWeight: 'bold' }}>
                                    {u.netProfit >= 0 ? '+' : ''}{(u.netProfit / 100000000).toFixed(4)}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '8px', color: '#ff8c00' }}>{(u.rakePaid / 100000000).toFixed(4)}</td>
                                  <td style={{ textAlign: 'right', padding: '8px', color: '#f0b90b' }}>{u.loyaltyDays}d</td>
                                  <td style={{ textAlign: 'center', padding: '8px' }}>
                                    {u.active ? (
                                      <button
                                        onClick={async () => {
                                          if (!confirm(`Stop ${u.handle}'s bot and close all positions?`)) return;
                                          try {
                                            const res = await fetch(`${API_URL}/api/admin/stop-user-bot`, {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json', 'x-session-id': sessionId },
                                              body: JSON.stringify({ handle: u.handle })
                                            });
                                            const data = await res.json();
                                            if (data.success) {
                                              alert(data.message);
                                              fetchAdminDashboard();
                                            } else {
                                              alert(`Error: ${data.error}`);
                                            }
                                          } catch (e) {
                                            alert(`Error: ${e.message}`);
                                          }
                                        }}
                                        style={{ backgroundColor: '#ff3b69', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '9px', fontWeight: 'bold', cursor: 'pointer' }}
                                      >STOP</button>
                                    ) : (
                                      <button
                                        onClick={async () => {
                                          if (!confirm(`Start ${u.handle}'s bot?`)) return;
                                          try {
                                            const res = await fetch(`${API_URL}/api/admin/start-user-bot`, {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json', 'x-session-id': sessionId },
                                              body: JSON.stringify({ handle: u.handle })
                                            });
                                            const data = await res.json();
                                            if (data.success) {
                                              alert(data.message);
                                              fetchAdminDashboard();
                                            } else {
                                              alert(`Error: ${data.error}`);
                                            }
                                          } catch (e) {
                                            alert(`Error: ${e.message}`);
                                          }
                                        }}
                                        style={{ backgroundColor: '#00d26a', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '9px', fontWeight: 'bold', cursor: 'pointer' }}
                                      >START</button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* WITHDRAWALS TAB - Pending Approval Management */}
                  {adminTab === 'withdrawals' && adminData?.userRole === 'admin' && (
                    <div>
                      {/* Header */}
                      <div style={{ background: 'linear-gradient(135deg, #1a1505 0%, #0a0a1a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #ff8c00' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>💸</span>
                            <div>
                              <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>Withdrawal Approval Queue</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Large withdrawals (&gt;$10) require admin approval</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={async () => {
                                if (!confirm('Clear all pending withdrawals? This cannot be undone.')) return;
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/clear-pending-withdrawals`, {
                                    method: 'POST',
                                    headers: { 'x-session-id': sessionId }
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    alert(data.message);
                                    fetchPendingWithdrawals();
                                  } else {
                                    alert('Error: ' + data.error);
                                  }
                                } catch (e) {
                                  alert('Error: ' + e.message);
                                }
                              }}
                              style={{ backgroundColor: '#ff3b6922', border: '1px solid #ff3b69', borderRadius: '6px', padding: '6px 12px', color: '#ff3b69', cursor: 'pointer', fontSize: '11px' }}
                            >
                              🗑️ Clear
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/withdrawal-history`, {
                                    headers: { 'x-session-id': sessionId }
                                  });
                                  const data = await res.json();
                                  if (data.history) {
                                    const totalUsd = (data.totalWithdrawn * satToUsd).toFixed(2);
                                    alert(`📜 WITHDRAWAL HISTORY\n\nTotal: ${data.totalCount} withdrawals\nTotal Withdrawn: ${formatPB(data.totalWithdrawn)} ($${totalUsd})\n\nLast 5:\n${data.history.slice(-5).map(w => `• $${w.handle}: ${formatPB(w.amount)} - ${new Date(w.approvedAt).toLocaleDateString()}`).join('\n')}`);
                                  }
                                } catch (e) {
                                  alert('Error: ' + e.message);
                                }
                              }}
                              style={{ backgroundColor: '#9b59b622', border: '1px solid #9b59b6', borderRadius: '6px', padding: '6px 12px', color: '#9b59b6', cursor: 'pointer', fontSize: '11px' }}
                            >
                              📜 History
                            </button>
                            <button
                              onClick={fetchPendingWithdrawals}
                              style={{ backgroundColor: '#ff8c0022', border: '1px solid #ff8c00', borderRadius: '6px', padding: '6px 12px', color: '#ff8c00', cursor: 'pointer', fontSize: '11px' }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats Row */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ backgroundColor: '#ff8c0011', border: '1px solid #ff8c0033', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ color: '#ff8c00', fontSize: '24px', fontWeight: 'bold' }}>{pendingWithdrawals?.length || 0}</div>
                          <div style={{ color: '#888', fontSize: '10px' }}>PENDING</div>
                        </div>
                        <div style={{ backgroundColor: '#f0b90b11', border: '1px solid #f0b90b33', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ color: '#f0b90b', fontSize: '24px', fontWeight: 'bold' }}>
                            {formatPB(pendingWithdrawals?.reduce((sum, w) => sum + w.amount, 0) || 0)}
                          </div>
                          <div style={{ color: '#888', fontSize: '10px' }}>TOTAL PENDING</div>
                        </div>
                        <div style={{ backgroundColor: '#00d26a11', border: '1px solid #00d26a33', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ color: '#00d26a', fontSize: '24px', fontWeight: 'bold' }}>
                            ${((pendingWithdrawals?.reduce((sum, w) => sum + w.amount, 0) || 0) * satToUsd).toFixed(2)}
                          </div>
                          <div style={{ color: '#888', fontSize: '10px' }}>USD VALUE</div>
                        </div>
                      </div>
                      
                      {/* Pending Withdrawals List */}
                      {pendingWithdrawals?.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {pendingWithdrawals.map((w, i) => (
                            <div key={w.id || i} style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>${w.handle}</div>
                                  <div style={{ color: '#888', fontSize: '10px' }}>
                                    Requested: {new Date(w.requestedAt).toLocaleString()}
                                  </div>
                                  {w.reason && (
                                    <div style={{ color: '#ff8c00', fontSize: '10px', marginTop: '2px' }}>
                                      Reason: {w.reason}
                                    </div>
                                  )}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ color: '#f0b90b', fontSize: '16px', fontWeight: 'bold' }}>{formatPB(w.amount)}</div>
                                  <div style={{ color: '#00d26a', fontSize: '12px' }}>${(w.amount * satToUsd).toFixed(2)}</div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => approveWithdrawal(w.id)}
                                  style={{ flex: 1, padding: '8px', backgroundColor: '#00d26a22', border: '1px solid #00d26a', borderRadius: '6px', color: '#00d26a', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                                >
                                  ✅ Approve & Send
                                </button>
                                <button
                                  onClick={() => rejectWithdrawal(w.id)}
                                  style={{ flex: 1, padding: '8px', backgroundColor: '#ff3b6922', border: '1px solid #ff3b69', borderRadius: '6px', color: '#ff3b69', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                                >
                                  ❌ Reject
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '40px', textAlign: 'center' }}>
                          <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                          <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>No Pending Withdrawals</div>
                          <div style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>All withdrawal requests have been processed</div>
                        </div>
                      )}
                      
                      {/* Daily Limit Info */}
                      <div style={{ marginTop: '16px', backgroundColor: '#0a0a0f', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                        <div style={{ color: '#f0b90b', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>📋 Withdrawal Limits</div>
                        <div style={{ color: '#888', fontSize: '10px', lineHeight: '1.6' }}>
                          • Instant withdrawals: Up to 500K sats (~$10) per transaction<br/>
                          • Daily limit: 500K sats (~$10) total per user<br/>
                          • Over limit: Queued for admin approval<br/>
                          • Admins: Unlimited withdrawals
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* FRAUD TAB - Enhanced */}
                  {adminTab === 'fraud' && (
                    <div>
                      {/* Fraud Detection Header */}
                      <div style={{ background: 'linear-gradient(135deg, #1a0505 0%, #0a0a1a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #ff3b69' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>🛡️</span>
                            <div>
                              <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>Fraud Detection Center</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Real-time monitoring & alerts</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            {/* Clear All Button */}
                            {((adminData.fraudAlerts?.length || 0) > 0 || (adminData.suspicious?.length || 0) > 0) && (
                              <button
                                onClick={async () => {
                                  if (!confirm('Clear ALL fraud alerts and suspicious flags? This cannot be undone.')) return;
                                  try {
                                    const res = await fetch(`${API_URL}/api/admin/clear-fraud-alerts`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId }
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      alert(`✅ Cleared ${data.clearedAlerts} alerts and ${data.clearedSuspicious} suspicious flags`);
                                      // Refresh admin data
                                      fetchAdminDashboard();
                                    } else {
                                      alert(data.error || 'Failed to clear');
                                    }
                                  } catch (e) { alert('Error: ' + e.message); }
                                }}
                                style={{ padding: '8px 12px', backgroundColor: '#ff3b69', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                              >
                                🗑️ Clear All
                              </button>
                            )}
                            <div style={{ textAlign: 'center', padding: '8px 16px', backgroundColor: (adminData.fraudAlerts?.length || 0) > 0 ? 'rgba(255,59,105,0.2)' : 'rgba(0,210,106,0.2)', borderRadius: '8px', border: (adminData.fraudAlerts?.length || 0) > 0 ? '1px solid #ff3b69' : '1px solid #00d26a' }}>
                              <div style={{ color: (adminData.fraudAlerts?.length || 0) > 0 ? '#ff3b69' : '#00d26a', fontSize: '20px', fontWeight: 'bold' }}>{adminData.fraudAlerts?.length || 0}</div>
                              <div style={{ color: '#666', fontSize: '9px' }}>Alerts</div>
                            </div>
                            <div style={{ textAlign: 'center', padding: '8px 16px', backgroundColor: 'rgba(255,140,0,0.1)', borderRadius: '8px', border: '1px solid rgba(255,140,0,0.3)' }}>
                              <div style={{ color: '#ff8c00', fontSize: '20px', fontWeight: 'bold' }}>{adminData.suspicious?.length || 0}</div>
                              <div style={{ color: '#666', fontSize: '9px' }}>Suspicious</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status Banner */}
                      {(!adminData.fraudAlerts || adminData.fraudAlerts.length === 0) && (!adminData.suspicious || adminData.suspicious.length === 0) && (
                        <div style={{ background: 'linear-gradient(90deg, rgba(0,210,106,0.1) 0%, transparent 100%)', borderRadius: '10px', padding: '16px', marginBottom: '16px', border: '1px solid rgba(0,210,106,0.3)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '24px' }}>✅</span>
                          <div>
                            <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>All Clear</div>
                            <div style={{ color: '#888', fontSize: '11px' }}>No fraud or suspicious activity detected</div>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        {/* Active Fraud Alerts */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '1px solid #ff3b69', overflow: 'hidden' }}>
                          <div style={{ background: 'linear-gradient(90deg, rgba(255,59,105,0.3) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a0a0a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#ff3b69', fontSize: '13px', fontWeight: 'bold' }}>🚨 Active Alerts</span>
                            <span style={{ backgroundColor: '#ff3b69', color: '#fff', fontSize: '10px', padding: '2px 8px', borderRadius: '10px' }}>{adminData.fraudAlerts?.length || 0}</span>
                          </div>
                          <div style={{ maxHeight: '350px', overflowY: 'auto', padding: '8px' }}>
                            {(adminData.fraudAlerts || []).map((a, i) => (
                              <div key={i} style={{ backgroundColor: '#1a0808', borderRadius: '8px', padding: '12px', marginBottom: '8px', border: '1px solid rgba(255,59,105,0.3)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                  <div>
                                    <div style={{ color: '#ff3b69', fontSize: '13px', fontWeight: 'bold' }}>${a.handle}</div>
                                    <div style={{ color: '#ff8c00', fontSize: '10px', marginTop: '2px' }}>{a.type}</div>
                                  </div>
                                  <span style={{ fontSize: '16px' }}>🚩</span>
                                </div>
                                <div style={{ color: '#888', fontSize: '11px', marginBottom: '10px', lineHeight: '1.4' }}>{a.detail}</div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button onClick={() => adminBanUser(a.handle, false)} style={{ flex: 1, backgroundColor: '#ff3b69', border: 'none', borderRadius: '6px', padding: '8px', color: '#fff', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>🚫 Ban User</button>
                                  <button onClick={() => adminZeroBalance(a.handle)} style={{ flex: 1, backgroundColor: '#1a1a2e', border: '1px solid #ff3b69', borderRadius: '6px', padding: '8px', color: '#ff3b69', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>💸 Zero Balance</button>
                                </div>
                              </div>
                            ))}
                            {(!adminData.fraudAlerts || adminData.fraudAlerts.length === 0) && (
                              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                                <div>No active fraud alerts</div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Suspicious Users */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '1px solid #ff8c00', overflow: 'hidden' }}>
                          <div style={{ background: 'linear-gradient(90deg, rgba(255,140,0,0.2) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#ff8c00', fontSize: '13px', fontWeight: 'bold' }}>⚠️ Suspicious Activity</span>
                            <span style={{ backgroundColor: '#ff8c00', color: '#000', fontSize: '10px', padding: '2px 8px', borderRadius: '10px' }}>{adminData.suspicious?.length || 0}</span>
                          </div>
                          <div style={{ maxHeight: '350px', overflowY: 'auto', padding: '8px' }}>
                            {(adminData.suspicious || []).map((u, i) => (
                              <div key={i} style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', marginBottom: '8px', border: '1px solid rgba(255,140,0,0.2)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                  <div style={{ color: '#ff8c00', fontSize: '13px', fontWeight: 'bold' }}>${u.handle}</div>
                                  <span style={{ color: '#666', fontSize: '10px' }}>Score: {u.suspicionScore || '?'}</span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '10px' }}>
                                  <div style={{ backgroundColor: '#0a0a0f', padding: '6px 8px', borderRadius: '4px' }}>
                                    <span style={{ color: '#666' }}>Balance:</span>
                                    <span style={{ color: '#f0b90b', marginLeft: '4px' }}>{((u.gameBalance || 0) / 100000000).toFixed(4)} BSV</span>
                                  </div>
                                  <div style={{ backgroundColor: '#0a0a0f', padding: '6px 8px', borderRadius: '4px' }}>
                                    <span style={{ color: '#666' }}>Deposited:</span>
                                    <span style={{ color: '#00bcd4', marginLeft: '4px' }}>{((u.totalDeposited || 0) / 100000000).toFixed(4)} BSV</span>
                                  </div>
                                  <div style={{ backgroundColor: '#0a0a0f', padding: '6px 8px', borderRadius: '4px' }}>
                                    <span style={{ color: '#666' }}>W/L:</span>
                                    <span style={{ color: '#888', marginLeft: '4px' }}>{u.wins || 0}/{u.losses || 0}</span>
                                  </div>
                                  <div style={{ backgroundColor: '#0a0a0f', padding: '6px 8px', borderRadius: '4px' }}>
                                    <span style={{ color: '#666' }}>Win Rate:</span>
                                    <span style={{ color: (u.winRate || 0) > 70 ? '#ff3b69' : '#00d26a', marginLeft: '4px' }}>{u.winRate || 0}%</span>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                  <button onClick={() => adminBanUser(u.handle, false)} style={{ flex: 1, backgroundColor: '#1a1a2e', border: '1px solid #ff3b69', borderRadius: '6px', padding: '6px', color: '#ff3b69', fontSize: '10px', cursor: 'pointer' }}>Ban</button>
                                  <button onClick={() => adminZeroBalance(u.handle)} style={{ flex: 1, backgroundColor: '#1a1a2e', border: '1px solid #ff8c00', borderRadius: '6px', padding: '6px', color: '#ff8c00', fontSize: '10px', cursor: 'pointer' }}>Zero</button>
                                </div>
                              </div>
                            ))}
                            {(!adminData.suspicious || adminData.suspicious.length === 0) && (
                              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>👀</div>
                                <div>No suspicious users flagged</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* WATCHLIST TAB - Track suspicious players */}
                  {adminTab === 'watchlist' && (
                    <div>
                      {/* Header */}
                      <div style={{ background: 'linear-gradient(135deg, #0a0820 0%, #1a0a30 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #6366f1' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>👁️</span>
                            <div>
                              <h3 style={{ color: '#6366f1', margin: 0, fontSize: '18px' }}>Player Watchlist</h3>
                              <div style={{ color: '#888', fontSize: '11px' }}>Track suspicious players & learn their patterns</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={fetchWatchlist} style={{ padding: '8px 12px', backgroundColor: '#1a1a2e', border: '1px solid #6366f1', borderRadius: '6px', color: '#6366f1', fontSize: '11px', cursor: 'pointer' }}>🔄 Refresh</button>
                            <button onClick={() => addToWatchlist()} style={{ padding: '8px 12px', backgroundColor: '#6366f1', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}>+ Add Player</button>
                          </div>
                        </div>
                        <div style={{ color: '#aaa', fontSize: '10px', padding: '8px', backgroundColor: '#00000033', borderRadius: '6px' }}>
                          🎯 Players on watchlist have ALL their trades tracked. The system learns their patterns and flags suspicious behavior like perfect rug exits, unusually high win rates, and quick flips.
                        </div>
                      </div>
                      
                      {/* Watchlist Players */}
                      {watchlistData.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          <div style={{ fontSize: '48px', marginBottom: '12px' }}>👁️</div>
                          <div>No players on watchlist</div>
                          <button onClick={() => addToWatchlist()} style={{ marginTop: '12px', padding: '10px 20px', backgroundColor: '#6366f1', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' }}>Add First Player</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {watchlistData.map(player => (
                            <div key={player.handle} style={{ backgroundColor: '#0a0a15', borderRadius: '12px', border: `1px solid ${player.score > 50 ? '#ff3b69' : player.score > 25 ? '#ff8c00' : '#6366f144'}`, overflow: 'hidden' }}>
                              {/* Player Header */}
                              <div style={{ padding: '12px 16px', backgroundColor: player.score > 50 ? '#1a080822' : '#0a082022', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <span style={{ fontSize: '24px' }}>{player.score > 50 ? '🚨' : player.score > 25 ? '⚠️' : '👁️'}</span>
                                  <div>
                                    <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px' }}>${player.handle}</div>
                                    <div style={{ color: '#888', fontSize: '10px' }}>
                                      Score: <span style={{ color: player.score > 50 ? '#ff3b69' : player.score > 25 ? '#ff8c00' : '#6366f1', fontWeight: 'bold' }}>{player.score}</span>
                                      {' • '}Trades: {player.totalTrades}{' • '}Win Rate: <span style={{ color: parseFloat(player.winRate) > 60 ? '#ff8c00' : '#00d26a' }}>{player.winRate}%</span>
                                    </div>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold' }}>{formatPB(player.gameBalance)}</div>
                                    <div style={{ color: '#666', fontSize: '9px' }}>Balance</div>
                                  </div>
                                  <button onClick={() => removeFromWatchlist(player.handle)} style={{ padding: '4px 8px', backgroundColor: '#1a0808', border: '1px solid #ff3b6944', borderRadius: '4px', color: '#ff3b69', fontSize: '10px', cursor: 'pointer' }}>✕</button>
                                </div>
                              </div>
                              
                              {/* Suspicious Events */}
                              {player.suspiciousEvents?.length > 0 && (
                                <div style={{ padding: '8px 16px', backgroundColor: '#1a080811', borderTop: '1px solid #ff3b6933' }}>
                                  <div style={{ color: '#ff3b69', fontSize: '10px', fontWeight: 'bold', marginBottom: '6px' }}>🚨 SUSPICIOUS EVENTS</div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {player.suspiciousEvents.slice(-5).map((event, i) => (
                                      <div key={i} style={{ color: '#ff8c00', fontSize: '10px', padding: '4px 8px', backgroundColor: '#ff8c0011', borderRadius: '4px' }}>
                                        {event.type === 'PERFECT_RUG_EXIT' && `🎯 Perfect exit from $${event.ticker} at ${(event.progress * 100).toFixed(0)}% lifecycle`}
                                        {event.type === 'QUICK_FLIP' && `⚡ Quick flip on $${event.ticker}: +${formatPB(event.profit)} in ${(event.holdTime / 1000).toFixed(1)}s`}
                                        {event.type === 'HIGH_WIN_RATE' && `📈 Win rate ${event.value} over ${event.trades} trades`}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Recent Trades */}
                              {player.recentTrades?.length > 0 && (
                                <div style={{ padding: '8px 16px', borderTop: '1px solid #1a1a2e' }}>
                                  <div style={{ color: '#888', fontSize: '10px', fontWeight: 'bold', marginBottom: '6px' }}>📊 RECENT TRADES</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {player.recentTrades.slice(-10).map((trade, i) => (
                                      <div key={i} style={{ 
                                        padding: '3px 6px', 
                                        backgroundColor: trade.action === 'buy' ? '#00d26a11' : '#ff3b6911', 
                                        border: `1px solid ${trade.action === 'buy' ? '#00d26a33' : '#ff3b6933'}`,
                                        borderRadius: '4px', 
                                        fontSize: '9px',
                                        color: trade.action === 'buy' ? '#00d26a' : '#ff3b69'
                                      }}>
                                        {trade.action === 'buy' ? '🟢' : '🔴'} ${trade.ticker}
                                        {trade.profit !== undefined && ` (${trade.profit >= 0 ? '+' : ''}${formatPB(trade.profit)})`}
                                        {trade.tokenFate && <span style={{ color: '#666' }}> [{trade.tokenFate}]</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Stats Footer */}
                              <div style={{ padding: '8px 16px', backgroundColor: '#00000022', borderTop: '1px solid #1a1a2e', display: 'flex', gap: '16px', fontSize: '9px', color: '#666' }}>
                                <span>First seen: {player.firstSeen ? new Date(player.firstSeen).toLocaleDateString() : 'Never'}</span>
                                <span>Last active: {player.lastSeen ? new Date(player.lastSeen).toLocaleString() : 'Never'}</span>
                                <span>W/L: {player.wins}/{player.losses}</span>
                                <span>Alerts: {player.alerts?.length || 0}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* ANTI-EXPLOIT TAB */}
                  {adminTab === 'exploits' && (
                    <div>
                      {/* Header */}
                      <div style={{ background: 'linear-gradient(135deg, #1a0a1a 0%, #0a1a0a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #9b59b6' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>🛡️</span>
                            <div>
                              <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>Anti-Exploit System</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Toggle remedies for player edge strategies</div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              setAntiExploitLoading(true);
                              try {
                                const res = await fetch(`${API_URL}/api/admin/anti-exploit`, {
                                  headers: { 'X-Session-Id': sessionId }
                                });
                                const data = await res.json();
                                setAntiExploitSettings(data);
                              } catch (e) {
                                console.error('Failed to load anti-exploit settings:', e);
                              }
                              setAntiExploitLoading(false);
                            }}
                            style={{ padding: '8px 16px', backgroundColor: '#9b59b6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            {antiExploitLoading ? '⏳ Loading...' : '🔄 Refresh'}
                          </button>
                        </div>
                      </div>
                      
                      {/* Load settings on first view */}
                      {!antiExploitSettings && !antiExploitLoading && (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <button
                            onClick={async () => {
                              setAntiExploitLoading(true);
                              try {
                                const res = await fetch(`${API_URL}/api/admin/anti-exploit`, {
                                  headers: { 'X-Session-Id': sessionId }
                                });
                                const data = await res.json();
                                setAntiExploitSettings(data);
                              } catch (e) {
                                console.error('Failed to load anti-exploit settings:', e);
                              }
                              setAntiExploitLoading(false);
                            }}
                            style={{ padding: '16px 32px', backgroundColor: '#9b59b6', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                          >
                            🛡️ Load Anti-Exploit Settings
                          </button>
                        </div>
                      )}
                      
                      {antiExploitLoading && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                          ⏳ Loading settings...
                        </div>
                      )}
                      
                      {antiExploitSettings && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {/* Remedy Cards */}
                          {[
                            { 
                              key: 'BOT_RANDOM_DELAY', 
                              icon: '🤖', 
                              title: '1. Front-Running Bots',
                              exploit: 'Players watch bot "analyzing" messages and buy before it does',
                              remedy: 'Random 2-8s delay before bot buys',
                              color: '#3498db'
                            },
                            { 
                              key: 'DYNAMIC_SLIPPAGE', 
                              icon: '💧', 
                              title: '2. Liquidity Sniping',
                              exploit: 'Players buy when pool is low, sell into any pump',
                              remedy: 'Bigger trades get more slippage (up to 15%)',
                              color: '#00d26a'
                            },
                            { 
                              key: 'NEW_TOKEN_DELAY', 
                              icon: '⏱️', 
                              title: '3. PvP Timing',
                              exploit: 'Players buy right after token launch, sell into FOMO',
                              remedy: '5s delay before non-creators can trade new tokens',
                              color: '#f39c12'
                            },
                            { 
                              key: 'HEALTH_SCORE_NOISE', 
                              icon: '💚', 
                              title: '4. Health Score Gaming',
                              exploit: 'Players only buy >70 health, sell at first red signal',
                              remedy: '+/- 15 point noise on displayed health scores',
                              color: '#e74c3c'
                            },
                            { 
                              key: 'FATE_TIMING_VARIANCE', 
                              icon: '📊', 
                              title: '5. Chart Pattern Reading',
                              exploit: 'Players learn AI token timing patterns and predict rugs',
                              remedy: '70%-150% variance on AI token lifespans',
                              color: '#9b59b6'
                            },
                            { 
                              key: 'RUG_RATE_BOOST', 
                              icon: '💀', 
                              title: '6. Rug Rate Boost',
                              exploit: 'House edge too low, players winning too much',
                              remedy: '1.25x rug probability (target ~28% house edge)',
                              color: '#ff3b69',
                              isNumeric: true  // Special flag for numeric toggle
                            },
                            { 
                              key: 'SPLASH_TRADES', 
                              icon: '💦', 
                              title: '7. Splash Trades',
                              exploit: 'Players make large trades with no price impact',
                              remedy: 'Counter-trades after big player buys/sells',
                              color: '#1abc9c'
                            },
                            { 
                              key: 'BOT_HEALTH_JITTER', 
                              icon: '🎲', 
                              title: '8. Bot Health Jitter',
                              exploit: 'Players copy exact bot health score readings',
                              remedy: 'Bots see +/- 10 point noise on health',
                              color: '#e67e22'
                            },
                            { 
                              key: 'BOT_AGGRESSION_JITTER', 
                              icon: '📈', 
                              title: '9. Bot Aggression Jitter',
                              exploit: 'Players predict exact bot trade sizes',
                              remedy: '0.7x-1.3x randomization on bot trade sizing',
                              color: '#2ecc71'
                            },
                            { 
                              key: 'SPAWN_RANDOM', 
                              icon: '🎰', 
                              title: '10. Spawn Randomization',
                              exploit: 'Players time new token spawns perfectly',
                              remedy: 'Random delays on token spawn timing',
                              color: '#8e44ad'
                            },
                          ].map(remedy => {
                            // Helper: check if setting is ON (handles numeric RUG_RATE_BOOST)
                            const isOn = remedy.isNumeric 
                              ? antiExploitSettings.settings?.[remedy.key] > 1
                              : !!antiExploitSettings.settings?.[remedy.key];
                            
                            return (
                            <div 
                              key={remedy.key}
                              style={{ 
                                backgroundColor: '#0a0a0f', 
                                borderRadius: '12px', 
                                border: `1px solid ${isOn ? remedy.color : '#333'}`,
                                padding: '16px',
                                opacity: isOn ? 1 : 0.6
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '20px' }}>{remedy.icon}</span>
                                    <span style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{remedy.title}</span>
                                    <span style={{ 
                                      padding: '2px 8px', 
                                      borderRadius: '4px', 
                                      fontSize: '10px', 
                                      fontWeight: 'bold',
                                      backgroundColor: isOn ? 'rgba(0,210,106,0.2)' : 'rgba(255,59,105,0.2)',
                                      color: isOn ? '#00d26a' : '#ff3b69'
                                    }}>
                                      {isOn ? 'ON' : 'OFF'}
                                    </span>
                                  </div>
                                  <div style={{ color: '#ff8c00', fontSize: '11px', marginBottom: '4px' }}>
                                    <strong>Exploit:</strong> {remedy.exploit}
                                  </div>
                                  <div style={{ color: '#00d26a', fontSize: '11px' }}>
                                    <strong>Remedy:</strong> {remedy.remedy}
                                  </div>
                                </div>
                                <button
                                  onClick={async () => {
                                    // Special handling for RUG_RATE_BOOST (numeric toggle)
                                    let newValue;
                                    if (remedy.isNumeric) {
                                      // Toggle between 1.25 (on) and 1 (off/no boost)
                                      newValue = isOn ? 1 : 1.25;
                                    } else {
                                      newValue = !antiExploitSettings.settings?.[remedy.key];
                                    }
                                    try {
                                      const res = await fetch(`${API_URL}/api/admin/anti-exploit`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ setting: remedy.key, value: newValue })
                                      });
                                      const data = await res.json();
                                      if (data.success) {
                                        setAntiExploitSettings(prev => ({
                                          ...prev,
                                          settings: data.allSettings
                                        }));
                                      }
                                    } catch (e) {
                                      console.error('Failed to toggle setting:', e);
                                    }
                                  }}
                                  style={{ 
                                    padding: '10px 20px', 
                                    backgroundColor: isOn ? '#ff3b69' : '#00d26a',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    minWidth: '80px'
                                  }}
                                >
                                  {isOn ? '🔴 Turn OFF' : '🟢 Turn ON'}
                                </button>
                              </div>
                            </div>
                          )})}
                          
                          {/* Info Box */}
                          <div style={{ backgroundColor: 'rgba(155,89,182,0.1)', borderRadius: '10px', padding: '16px', border: '1px solid #9b59b6', marginTop: '8px' }}>
                            <div style={{ color: '#9b59b6', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>💡 How to Use</div>
                            <div style={{ color: '#888', fontSize: '11px', lineHeight: '1.6' }}>
                              <p style={{ margin: '0 0 8px 0' }}>1. <strong>All remedies are ON by default</strong> - provides baseline protection</p>
                              <p style={{ margin: '0 0 8px 0' }}>2. <strong>Find out what the winning player is doing</strong> - check their trade patterns in Fraud tab</p>
                              <p style={{ margin: '0' }}>3. <strong>Toggle specific remedy</strong> - turn off others to isolate the edge they found</p>
                            </div>
                          </div>
                          
                          {/* Analyze User Quick Link */}
                          <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: '1px solid #f0b90b' }}>
                            <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px' }}>🔍 Analyze Suspicious User</div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="text"
                                placeholder="Enter handle (e.g. ransack)"
                                id="analyzeUserInput"
                                style={{ 
                                  flex: 1, 
                                  padding: '10px 12px', 
                                  backgroundColor: '#1a1a2e', 
                                  border: '1px solid #333', 
                                  borderRadius: '8px', 
                                  color: '#fff',
                                  fontSize: '12px'
                                }}
                              />
                              <button
                                onClick={async () => {
                                  const handle = document.getElementById('analyzeUserInput').value.trim().replace('$', '');
                                  if (!handle) return;
                                  try {
                                    const res = await fetch(`${API_URL}/api/admin/analyze-user/${handle}`, {
                                      headers: { 'X-Session-Id': sessionId }
                                    });
                                    const data = await res.json();
                                    console.log('🔍 User Analysis:', data);
                                    alert(JSON.stringify(data, null, 2));
                                  } catch (e) {
                                    alert('Error: ' + e.message);
                                  }
                                }}
                                style={{ padding: '10px 20px', backgroundColor: '#f0b90b', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                              >
                                🔍 Analyze
                              </button>
                            </div>
                            <div style={{ color: '#666', fontSize: '10px', marginTop: '8px' }}>
                              Shows: trade patterns, win streaks, token preferences, timing analysis
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* HUNTERS TAB - Winner Hunter Settings */}
                  {adminTab === 'hunters' && adminData?.userRole === 'admin' && (
                    <div>
                      {/* 📢 ADMIN BROADCAST - Send message to all online players */}
                      <div style={{ backgroundColor: '#0a1020', borderRadius: '12px', padding: '16px', border: '2px solid #6366f1', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div>
                            <div style={{ color: '#6366f1', fontSize: '14px', fontWeight: 'bold' }}>📢 BROADCAST MESSAGE</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>Send a message to all connected players instantly</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#6366f1', fontSize: '18px', fontWeight: 'bold' }}>
                              {onlineUsers?.length || 0} online
                            </div>
                          </div>
                        </div>

                        {/* Message Type */}
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                          {[
                            { type: 'error', emoji: '🚨', label: 'Alert', color: '#ff3b69' },
                            { type: 'warning', emoji: '⚠️', label: 'Warning', color: '#f0b90b' },
                            { type: 'success', emoji: '🎉', label: 'Success', color: '#00d26a' },
                            { type: 'info', emoji: '📢', label: 'Info', color: '#6366f1' },
                          ].map(opt => (
                            <button
                              key={opt.type}
                              onClick={() => setBroadcastType(opt.type)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                backgroundColor: broadcastType === opt.type ? opt.color + '33' : '#111',
                                border: `2px solid ${broadcastType === opt.type ? opt.color : '#333'}`,
                                borderRadius: '6px',
                                color: broadcastType === opt.type ? opt.color : '#666',
                                fontWeight: 'bold',
                                fontSize: '11px',
                                cursor: 'pointer',
                              }}
                            >
                              {opt.emoji} {opt.label}
                            </button>
                          ))}
                        </div>

                        {/* Message Input */}
                        <div style={{ marginBottom: '12px' }}>
                          <textarea
                            value={broadcastText}
                            onChange={(e) => setBroadcastText(e.target.value.slice(0, 500))}
                            maxLength={500}
                            placeholder="Type your message here..."
                            rows={3}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              backgroundColor: '#111',
                              border: '1px solid #333',
                              borderRadius: '6px',
                              color: '#fff',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              resize: 'none',
                              fontFamily: 'inherit',
                            }}
                          />
                          <div style={{ color: '#666', fontSize: '10px', marginTop: '4px', textAlign: 'right' }}>
                            {broadcastText.length}/500 characters
                          </div>
                        </div>

                        {/* Send Button */}
                        <button
                          onClick={() => {
                            if (!broadcastText.trim()) {
                              alert('Please enter a message');
                              return;
                            }
                            if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
                              alert('WebSocket not connected. Please refresh.');
                              return;
                            }
                            setShowBroadcastConfirm(true);
                          }}
                          disabled={!broadcastText.trim()}
                          style={{
                            width: '100%',
                            padding: '12px',
                            backgroundColor: broadcastText.trim() ? '#6366f1' : '#333',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            cursor: broadcastText.trim() ? 'pointer' : 'not-allowed',
                          }}
                        >
                          📢 SEND BROADCAST
                        </button>

                        {/* Broadcast Confirm Modal */}
                        {showBroadcastConfirm && (
                          <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999,
                          }}>
                            <div style={{
                              backgroundColor: '#0a0a15',
                              border: '3px solid #6366f1',
                              borderRadius: '16px',
                              padding: '24px',
                              maxWidth: '400px',
                              textAlign: 'center',
                            }}>
                              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📢</div>
                              <div style={{ color: '#6366f1', fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>
                                BROADCAST MESSAGE
                              </div>
                              <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>
                                Send to {onlineUsers?.length || 0} online players?
                              </div>
                              <div style={{
                                backgroundColor: '#111',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '16px',
                                border: `1px solid ${
                                  broadcastType === 'error' ? '#ff3b69' :
                                  broadcastType === 'warning' ? '#f0b90b' :
                                  broadcastType === 'success' ? '#00d26a' : '#6366f1'
                                }`,
                              }}>
                                <div style={{
                                  color: broadcastType === 'error' ? '#ff3b69' :
                                         broadcastType === 'warning' ? '#f0b90b' :
                                         broadcastType === 'success' ? '#00d26a' : '#6366f1',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  marginBottom: '4px',
                                }}>
                                  {broadcastType.toUpperCase()}
                                </div>
                                <div style={{ color: '#fff', fontSize: '14px' }}>
                                  "{broadcastText}"
                                </div>
                              </div>
                              <div style={{ color: '#666', fontSize: '11px', marginBottom: '20px' }}>
                                Click DEGEN to agree and send
                              </div>
                              <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                  onClick={() => {
                                    setShowBroadcastConfirm(false);
                                    alert('😤 Too bad! Message not sent.');
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: '14px',
                                    backgroundColor: '#1a1a2a',
                                    border: '2px solid #444',
                                    borderRadius: '8px',
                                    color: '#888',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ❌ CANCEL
                                </button>
                                <button
                                  onClick={() => {
                                    wsRef.current.send(JSON.stringify({
                                      type: 'admin_broadcast',
                                      text: broadcastText.trim(),
                                      msgType: broadcastType,
                                    }));
                                    setBroadcastText('');
                                    setShowBroadcastConfirm(false);
                                    alert('✅ Broadcast sent!');
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: '14px',
                                    backgroundColor: '#6366f1',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  🎰 DEGEN
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 🎲 LIFECYCLE DISTRIBUTION - Control token timing (TOP OF TAB) */}
                      <div style={{ background: '#111', borderRadius: '12px', padding: '16px', marginBottom: '24px', border: '1px solid #444' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '24px' }}>🎲</span>
                            <div>
                              <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>Lifecycle Distribution</div>
                              <div style={{ color: '#666', fontSize: '10px' }}>Control spawn rates, lifespans, and rug chances</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/lifecycle-distribution`, {
                                    headers: { 'X-Session-Id': sessionId }
                                  });
                                  const data = await res.json();
                                  setLifecycleConfig(data);
                                } catch (e) {
                                  console.error('Failed to load lifecycle config:', e);
                                }
                              }}
                              style={{ padding: '6px 12px', backgroundColor: '#333', color: '#fff', border: '1px solid #555', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                            >
                              Load
                            </button>
                            {lifecycleConfig && (
                              <button
                                onClick={async () => {
                                  try {
                                    const payload = {
                                      distribution: lifecycleConfig.distribution,
                                      lifecycleTypes: {}
                                    };
                                    for (const key of Object.keys(lifecycleConfig.lifecycleInfo || {})) {
                                      const info = lifecycleConfig.lifecycleInfo[key];
                                      payload.lifecycleTypes[key] = {
                                        lifespanMin: info.lifespanMin,
                                        lifespanMax: info.lifespanMax,
                                        rugChance: info.rugChance
                                      };
                                    }
                                    const res = await fetch(`${API_URL}/api/admin/lifecycle-distribution`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify(payload)
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      setLifecycleConfig(data);
                                      alert('Saved to Redis!');
                                    }
                                  } catch (e) {
                                    console.error('Failed to save lifecycle config:', e);
                                  }
                                }}
                                style={{ padding: '6px 12px', backgroundColor: '#00d26a', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                              >
                                Save All
                              </button>
                            )}
                          </div>
                        </div>

                        {lifecycleConfig ? (
                          <div>
                            {/* Difficulty Preset Buttons */}
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                              {[
                                {
                                  name: 'Easy',
                                  emoji: '🟢',
                                  color: '#00d26a',
                                  distribution: { quickPump: 2, mediumRunner: 15, longRunner: 25, moonshot: 15, marathon: 15, hourly: 12, epic: 8, legendary: 5, megaMoon: 3 },
                                  lifecycleInfo: {
                                    quickPump: { lifespanMin: 600000, lifespanMax: 900000, rugChance: 60 },
                                    mediumRunner: { lifespanMin: 720000, lifespanMax: 1200000, rugChance: 50 },
                                    longRunner: { lifespanMin: 900000, lifespanMax: 1800000, rugChance: 45 },
                                    moonshot: { lifespanMin: 1800000, lifespanMax: 2400000, rugChance: 35 },
                                    marathon: { lifespanMin: 1200000, lifespanMax: 2400000, rugChance: 40 },
                                    hourly: { lifespanMin: 5400000, lifespanMax: 14400000, rugChance: 35 },
                                    epic: { lifespanMin: 14400000, lifespanMax: 25200000, rugChance: 30 },
                                    legendary: { lifespanMin: 28800000, lifespanMax: 43200000, rugChance: 25 },
                                    megaMoon: { lifespanMin: 21600000, lifespanMax: 57600000, rugChance: 20 }
                                  }
                                },
                                {
                                  name: 'Default',
                                  emoji: '⚪',
                                  color: '#888',
                                  distribution: { quickPump: 4, mediumRunner: 20, longRunner: 28, moonshot: 12, marathon: 12, hourly: 10, epic: 6, legendary: 4, megaMoon: 4 },
                                  lifecycleInfo: {
                                    quickPump: { lifespanMin: 480000, lifespanMax: 720000, rugChance: 75 },
                                    mediumRunner: { lifespanMin: 600000, lifespanMax: 1020000, rugChance: 65 },
                                    longRunner: { lifespanMin: 720000, lifespanMax: 1500000, rugChance: 58 },
                                    moonshot: { lifespanMin: 1500000, lifespanMax: 2100000, rugChance: 48 },
                                    marathon: { lifespanMin: 960000, lifespanMax: 2100000, rugChance: 52 },
                                    hourly: { lifespanMin: 4800000, lifespanMax: 12600000, rugChance: 48 },
                                    epic: { lifespanMin: 12600000, lifespanMax: 22500000, rugChance: 44 },
                                    legendary: { lifespanMin: 25200000, lifespanMax: 39600000, rugChance: 40 },
                                    megaMoon: { lifespanMin: 18000000, lifespanMax: 50400000, rugChance: 36 }
                                  }
                                },
                                {
                                  name: 'Challenging',
                                  emoji: '🟡',
                                  color: '#f0b90b',
                                  distribution: { quickPump: 5, mediumRunner: 24, longRunner: 30, moonshot: 11, marathon: 10, hourly: 9, epic: 5, legendary: 3, megaMoon: 3 },
                                  lifecycleInfo: {
                                    quickPump: { lifespanMin: 420000, lifespanMax: 660000, rugChance: 85 },
                                    mediumRunner: { lifespanMin: 540000, lifespanMax: 960000, rugChance: 75 },
                                    longRunner: { lifespanMin: 660000, lifespanMax: 1380000, rugChance: 68 },
                                    moonshot: { lifespanMin: 1380000, lifespanMax: 1860000, rugChance: 58 },
                                    marathon: { lifespanMin: 840000, lifespanMax: 1920000, rugChance: 58 },
                                    hourly: { lifespanMin: 4200000, lifespanMax: 11700000, rugChance: 54 },
                                    epic: { lifespanMin: 11700000, lifespanMax: 20700000, rugChance: 52 },
                                    legendary: { lifespanMin: 23400000, lifespanMax: 37800000, rugChance: 48 },
                                    megaMoon: { lifespanMin: 16200000, lifespanMax: 48600000, rugChance: 44 }
                                  }
                                },
                                {
                                  name: 'Hard',
                                  emoji: '🔴',
                                  color: '#ff3b69',
                                  distribution: { quickPump: 5, mediumRunner: 26, longRunner: 31, moonshot: 11, marathon: 9, hourly: 10, epic: 4, legendary: 2, megaMoon: 2 },
                                  lifecycleInfo: {
                                    quickPump: { lifespanMin: 360000, lifespanMax: 600000, rugChance: 92 },
                                    mediumRunner: { lifespanMin: 480000, lifespanMax: 900000, rugChance: 82 },
                                    longRunner: { lifespanMin: 600000, lifespanMax: 1260000, rugChance: 74 },
                                    moonshot: { lifespanMin: 1260000, lifespanMax: 1680000, rugChance: 62 },
                                    marathon: { lifespanMin: 720000, lifespanMax: 1740000, rugChance: 60 },
                                    hourly: { lifespanMin: 3900000, lifespanMax: 11250000, rugChance: 58 },
                                    epic: { lifespanMin: 11250000, lifespanMax: 19350000, rugChance: 55 },
                                    legendary: { lifespanMin: 22500000, lifespanMax: 35100000, rugChance: 52 },
                                    megaMoon: { lifespanMin: 15300000, lifespanMax: 45900000, rugChance: 48 }
                                  }
                                },
                                {
                                  name: 'Brutal',
                                  emoji: '💀',
                                  color: '#9333ea',
                                  distribution: { quickPump: 6, mediumRunner: 27, longRunner: 32, moonshot: 11, marathon: 9, hourly: 10, epic: 3, legendary: 1, megaMoon: 1 },
                                  lifecycleInfo: {
                                    quickPump: { lifespanMin: 300000, lifespanMax: 540000, rugChance: 99 },
                                    mediumRunner: { lifespanMin: 420000, lifespanMax: 840000, rugChance: 88 },
                                    longRunner: { lifespanMin: 540000, lifespanMax: 1140000, rugChance: 79 },
                                    moonshot: { lifespanMin: 1200000, lifespanMax: 1500000, rugChance: 66 },
                                    marathon: { lifespanMin: 660000, lifespanMax: 1560000, rugChance: 62 },
                                    hourly: { lifespanMin: 3600000, lifespanMax: 10800000, rugChance: 61 },
                                    epic: { lifespanMin: 10800000, lifespanMax: 18000000, rugChance: 58 },
                                    legendary: { lifespanMin: 21600000, lifespanMax: 32400000, rugChance: 55 },
                                    megaMoon: { lifespanMin: 14400000, lifespanMax: 43200000, rugChance: 53 }
                                  }
                                }
                              ].map(preset => {
                                const isSelected = lifecyclePreset === preset.name;
                                return (
                                <button
                                  key={preset.name}
                                  onClick={() => {
                                    const newTotal = Object.values(preset.distribution).reduce((a, b) => a + b, 0);
                                    setLifecycleConfig({
                                      ...lifecycleConfig,
                                      distribution: preset.distribution,
                                      lifecycleInfo: preset.lifecycleInfo,
                                      total: newTotal
                                    });
                                    setLifecyclePreset(preset.name);
                                  }}
                                  style={{
                                    padding: '8px 14px',
                                    backgroundColor: isSelected ? `${preset.color}33` : '#1a1a1a',
                                    border: isSelected ? `2px solid ${preset.color}` : `1px solid ${preset.color}44`,
                                    borderRadius: '8px',
                                    color: preset.color,
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    transition: 'all 0.15s ease',
                                    boxShadow: isSelected ? `0 0 12px ${preset.color}66` : 'none'
                                  }}
                                  onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.backgroundColor = `${preset.color}22`; e.currentTarget.style.borderColor = preset.color; }}}
                                  onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.backgroundColor = '#1a1a1a'; e.currentTarget.style.borderColor = `${preset.color}44`; }}}
                                >
                                  <span>{preset.emoji}</span>
                                  <span>{preset.name}</span>
                                  {isSelected && <span style={{ marginLeft: '4px' }}>✓</span>}
                                </button>
                              );})}
                            </div>

                            {/* Total indicator */}
                            <div style={{
                              backgroundColor: lifecycleConfig.total === 100 ? '#0a1a0f' : '#1a0a0f',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              border: `1px solid ${lifecycleConfig.total === 100 ? '#00d26a' : '#ff3b69'}`,
                              marginBottom: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{ color: '#888', fontSize: '10px' }}>Total %</span>
                              <span style={{ color: lifecycleConfig.total === 100 ? '#00d26a' : '#ff3b69', fontWeight: 'bold', fontSize: '12px' }}>
                                {lifecycleConfig.total}% {lifecycleConfig.total === 100 ? '✓' : '(need 100%)'}
                              </span>
                            </div>

                            {/* Table header */}
                            <div style={{ display: 'grid', gridTemplateColumns: '140px 60px 70px 70px 55px', gap: '6px', padding: '8px 10px', backgroundColor: '#1a1a1a', borderRadius: '6px', fontSize: '9px', color: '#888', fontWeight: 'bold', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              <div>Type</div>
                              <div>Spawn %</div>
                              <div>Min Life</div>
                              <div>Max Life</div>
                              <div>Rug %</div>
                            </div>

                            {/* Lifecycle rows */}
                            {[
                              { key: 'quickPump', label: 'Quick Pump', emoji: '⚡', desc: 'Fast & risky - most common' },
                              { key: 'mediumRunner', label: 'Medium Runner', emoji: '🏃', desc: 'Balanced risk/reward' },
                              { key: 'longRunner', label: 'Long Runner', emoji: '🚶', desc: 'More time to decide' },
                              { key: 'moonshot', label: 'Moonshot', emoji: '🚀', desc: 'Low rug chance - potential winner' },
                              { key: 'marathon', label: 'Marathon', emoji: '🏃‍♂️', desc: 'Long play - 10-30 min' },
                              { key: 'hourly', label: 'Hourly', emoji: '⏰', desc: 'Lives for hours' },
                              { key: 'epic', label: 'Epic', emoji: '⭐', desc: 'Rare - 4-8 hour tokens' },
                              { key: 'legendary', label: 'Legendary', emoji: '👑', desc: 'Very rare - 8-12 hours' },
                              { key: 'megaMoon', label: 'Mega Moon', emoji: '🌙', desc: 'Ultra rare whale bait - hold tight' },
                            ].map(({ key, label, emoji, desc }) => {
                              const info = lifecycleConfig.lifecycleInfo?.[key] || {};
                              const minMins = Math.round((info.lifespanMin || 60000) / 60000);
                              const maxMins = Math.round((info.lifespanMax || 120000) / 60000);
                              const formatTime = (mins) => mins >= 60 ? `${Math.round(mins/60)}h` : `${mins}m`;
                              return (
                                <div key={key} style={{
                                  backgroundColor: '#0d0d0d',
                                  borderRadius: '8px',
                                  border: '1px solid #222',
                                  marginBottom: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '140px 60px 70px 70px 55px',
                                    gap: '6px',
                                    padding: '8px 10px',
                                    alignItems: 'center',
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ fontSize: '14px' }}>{emoji}</span>
                                      <span style={{ color: '#ddd', fontSize: '11px', fontWeight: 'bold' }}>{label}</span>
                                    </div>
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={lifecycleConfig.distribution?.[key] || 0}
                                      onChange={(e) => {
                                        const newVal = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                                        const newDist = { ...lifecycleConfig.distribution, [key]: newVal };
                                        const newTotal = Object.values(newDist).reduce((a, b) => a + b, 0);
                                        setLifecycleConfig({ ...lifecycleConfig, distribution: newDist, total: newTotal });
                                      }}
                                      style={{ width: '45px', padding: '5px', backgroundColor: '#1a1a1a', color: '#f0b90b', border: '1px solid #333', borderRadius: '4px', fontSize: '11px', textAlign: 'center', fontWeight: 'bold' }}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <input
                                        type="number"
                                        min="1"
                                        value={minMins}
                                        onChange={(e) => {
                                          const newMinMs = Math.max(1, parseInt(e.target.value) || 1) * 60000;
                                          setLifecycleConfig({
                                            ...lifecycleConfig,
                                            lifecycleInfo: {
                                              ...lifecycleConfig.lifecycleInfo,
                                              [key]: { ...info, lifespanMin: newMinMs }
                                            }
                                          });
                                        }}
                                        style={{ width: '40px', padding: '5px', backgroundColor: '#1a1a1a', color: '#4a9eff', border: '1px solid #333', borderRadius: '4px', fontSize: '11px', textAlign: 'center' }}
                                      />
                                      <span style={{ color: '#555', fontSize: '9px' }}>{formatTime(minMins)}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <input
                                        type="number"
                                        min="1"
                                        value={maxMins}
                                        onChange={(e) => {
                                          const newMaxMs = Math.max(1, parseInt(e.target.value) || 1) * 60000;
                                          setLifecycleConfig({
                                            ...lifecycleConfig,
                                            lifecycleInfo: {
                                              ...lifecycleConfig.lifecycleInfo,
                                              [key]: { ...info, lifespanMax: newMaxMs }
                                            }
                                          });
                                        }}
                                        style={{ width: '40px', padding: '5px', backgroundColor: '#1a1a1a', color: '#4a9eff', border: '1px solid #333', borderRadius: '4px', fontSize: '11px', textAlign: 'center' }}
                                      />
                                      <span style={{ color: '#555', fontSize: '9px' }}>{formatTime(maxMins)}</span>
                                    </div>
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={info.rugChance || 0}
                                      onChange={(e) => {
                                        const newRug = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                                        setLifecycleConfig({
                                          ...lifecycleConfig,
                                          lifecycleInfo: {
                                            ...lifecycleConfig.lifecycleInfo,
                                            [key]: { ...info, rugChance: newRug }
                                          }
                                        });
                                      }}
                                      style={{ width: '40px', padding: '5px', backgroundColor: '#1a1a1a', color: info.rugChance > 50 ? '#ff3b69' : '#00d26a', border: '1px solid #333', borderRadius: '4px', fontSize: '11px', textAlign: 'center', fontWeight: 'bold' }}
                                    />
                                  </div>
                                  <div style={{ padding: '4px 10px 8px 36px', color: '#555', fontSize: '9px', fontStyle: 'italic' }}>
                                    {desc}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ color: '#555', fontSize: '10px', textAlign: 'center', padding: '20px' }}>
                            Click Load to view lifecycle settings
                          </div>
                        )}
                      </div>

                      {/* Header */}
                      <div style={{ background: 'linear-gradient(135deg, #1a0808 0%, #0a0a1a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #ff3b69' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>🎯</span>
                            <div>
                              <div style={{ color: '#ff3b69', fontSize: '16px', fontWeight: 'bold' }}>Winner Hunter Bots</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Target profitable players with counter-trades</div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              setWinnerHunterLoading(true);
                              try {
                                const res = await fetch(`${API_URL}/api/admin/winner-hunter`, {
                                  headers: { 'X-Session-Id': sessionId }
                                });
                                const data = await res.json();
                                setWinnerHunterConfig(data);
                              } catch (e) {
                                console.error('Failed to load winner hunter config:', e);
                              }
                              setWinnerHunterLoading(false);
                            }}
                            style={{ padding: '8px 16px', backgroundColor: '#ff3b69', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            {winnerHunterLoading ? '⏳ Loading...' : '🔄 Refresh'}
                          </button>
                        </div>
                      </div>
                      
                      {/* Load on first view */}
                      {!winnerHunterConfig && !winnerHunterLoading && (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <button
                            onClick={async () => {
                              setWinnerHunterLoading(true);
                              try {
                                const res = await fetch(`${API_URL}/api/admin/winner-hunter`, {
                                  headers: { 'X-Session-Id': sessionId }
                                });
                                const data = await res.json();
                                setWinnerHunterConfig(data);
                              } catch (e) {
                                console.error('Failed to load winner hunter config:', e);
                              }
                              setWinnerHunterLoading(false);
                            }}
                            style={{ padding: '16px 32px', backgroundColor: '#ff3b69', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                          >
                            🎯 Load Hunter Settings
                          </button>
                        </div>
                      )}
                      
                      {winnerHunterLoading && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                          ⏳ Loading...
                        </div>
                      )}
                      
                      {winnerHunterConfig && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {/* Master Toggle */}
                          <div style={{ backgroundColor: '#0d0d15', borderRadius: '12px', padding: '16px', border: `1px solid ${winnerHunterConfig.enabled ? '#00d26a' : '#ff3b69'}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ color: '#fff', fontWeight: 'bold' }}>🎯 Winner Hunter</div>
                                <div style={{ color: '#888', fontSize: '11px' }}>Counter-trade profitable players</div>
                              </div>
                              <button
                                onClick={async () => {
                                  const newVal = !winnerHunterConfig.enabled;
                                  try {
                                    await fetch(`${API_URL}/api/admin/winner-hunter`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({ enabled: newVal })
                                    });
                                    setWinnerHunterConfig({ ...winnerHunterConfig, enabled: newVal });
                                  } catch (e) {
                                    alert('Failed to update');
                                  }
                                }}
                                style={{ padding: '10px 20px', backgroundColor: winnerHunterConfig.enabled ? '#00d26a' : '#333', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                              >
                                {winnerHunterConfig.enabled ? '✅ ENABLED' : '❌ DISABLED'}
                              </button>
                            </div>
                          </div>
                          
                          {/* Aggression Slider */}
                          <div style={{ backgroundColor: '#0d0d15', borderRadius: '12px', padding: '16px', border: '1px solid #333' }}>
                            <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: '12px' }}>😈 Aggression Level</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={(winnerHunterConfig.aggression || 0.5) * 100}
                                onChange={(e) => setWinnerHunterConfig({ ...winnerHunterConfig, aggression: e.target.value / 100 })}
                                style={{ flex: 1 }}
                              />
                              <span style={{ color: '#f0b90b', fontWeight: 'bold', minWidth: '50px' }}>{((winnerHunterConfig.aggression || 0.5) * 100).toFixed(0)}%</span>
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/api/admin/winner-hunter`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({ aggression: winnerHunterConfig.aggression })
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      alert(`✅ Saved to Redis!\n\nAggression: ${(data.aggression * 100).toFixed(0)}%\n\nThis will persist across deploys.`);
                                    } else {
                                      alert('❌ Save failed: ' + (data.error || 'Unknown error'));
                                    }
                                  } catch (e) {
                                    alert('❌ Failed: ' + e.message);
                                  }
                                }}
                                style={{ padding: '6px 12px', backgroundColor: '#f0b90b', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                              >
                                Save
                              </button>
                            </div>
                            <div style={{ color: '#666', fontSize: '10px', marginTop: '8px' }}>Higher = more counter-trades, bigger dumps</div>
                          </div>
                          
                          {/* Profit Threshold */}
                          <div style={{ backgroundColor: '#0d0d15', borderRadius: '12px', padding: '16px', border: '1px solid #333' }}>
                            <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: '12px' }}>💰 Profit Threshold to Target</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <input
                                type="number"
                                value={(winnerHunterConfig.profitThreshold || 10000000) / 1000000}
                                onChange={(e) => setWinnerHunterConfig({ ...winnerHunterConfig, profitThreshold: e.target.value * 1000000 })}
                                style={{ flex: 1, padding: '10px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', color: '#fff' }}
                              />
                              <span style={{ color: '#888' }}>M PB</span>
                              <button
                                onClick={async () => {
                                  try {
                                    await fetch(`${API_URL}/api/admin/winner-hunter`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({ profitThreshold: winnerHunterConfig.profitThreshold })
                                    });
                                    alert('✅ Saved');
                                  } catch (e) {
                                    alert('Failed');
                                  }
                                }}
                                style={{ padding: '6px 12px', backgroundColor: '#f0b90b', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                              >
                                Save
                              </button>
                            </div>
                            <div style={{ color: '#666', fontSize: '10px', marginTop: '8px' }}>Players with profit above this get targeted</div>
                          </div>
                          
                          {/* Win Rate Threshold */}
                          <div style={{ backgroundColor: '#0d0d15', borderRadius: '12px', padding: '16px', border: '1px solid #333' }}>
                            <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: '12px' }}>📊 Win Rate Threshold</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <input
                                type="number"
                                min="50"
                                max="95"
                                value={winnerHunterConfig.winRateThreshold || 60}
                                onChange={(e) => setWinnerHunterConfig({ ...winnerHunterConfig, winRateThreshold: parseInt(e.target.value) })}
                                style={{ flex: 1, padding: '10px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', color: '#fff' }}
                              />
                              <span style={{ color: '#888' }}>%</span>
                              <button
                                onClick={async () => {
                                  try {
                                    await fetch(`${API_URL}/api/admin/winner-hunter`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({ winRateThreshold: winnerHunterConfig.winRateThreshold })
                                    });
                                    alert('✅ Saved');
                                  } catch (e) {
                                    alert('Failed');
                                  }
                                }}
                                style={{ padding: '6px 12px', backgroundColor: '#f0b90b', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                              >
                                Save
                              </button>
                            </div>
                            <div style={{ color: '#666', fontSize: '10px', marginTop: '8px' }}>Players with win rate above this get targeted (min 10 trades)</div>
                          </div>
                          
                          {/* Current Targets */}
                          <div style={{ backgroundColor: '#0d0d15', borderRadius: '12px', padding: '16px', border: '1px solid #ff3b69' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                              <div style={{ color: '#ff3b69', fontWeight: 'bold' }}>🎯 Current Targets ({winnerHunterConfig.targetList?.length || 0})</div>
                            </div>
                            
                            {/* Add Target Input */}
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                              <input
                                type="text"
                                placeholder="Enter handle to target..."
                                id="addTargetInput"
                                style={{ flex: 1, padding: '8px 12px', backgroundColor: '#1a1a2e', border: '1px solid #ff3b69', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                              />
                              <button
                                onClick={async () => {
                                  const input = document.getElementById('addTargetInput');
                                  const handle = input.value.trim().toLowerCase().replace(/^\$/, '');
                                  if (!handle) return alert('Enter a handle');
                                  
                                  try {
                                    const res = await fetch(`${API_URL}/api/admin/winner-hunter/target`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({ action: 'add', handle })
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      setWinnerHunterConfig({ ...winnerHunterConfig, targetList: data.targetList });
                                      input.value = '';
                                    } else {
                                      alert(data.error || 'Failed');
                                    }
                                  } catch (e) { alert('Error adding target'); }
                                }}
                                style={{ padding: '8px 16px', backgroundColor: '#ff3b69', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                              >
                                + Add
                              </button>
                            </div>
                            
                            {/* Target List with Remove Buttons */}
                            {winnerHunterConfig.targetList?.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {winnerHunterConfig.targetList.map(handle => (
                                  <div 
                                    key={handle} 
                                    style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '4px',
                                      padding: '4px 6px 4px 10px', 
                                      backgroundColor: '#ff3b6922', 
                                      border: '1px solid #ff3b69', 
                                      borderRadius: '12px', 
                                      color: '#ff3b69', 
                                      fontSize: '11px' 
                                    }}
                                  >
                                    <span>${handle}</span>
                                    <button
                                      onClick={async () => {
                                        if (!confirm(`Remove $${handle} from targets?`)) return;
                                        try {
                                          const res = await fetch(`${API_URL}/api/admin/winner-hunter/target`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                            body: JSON.stringify({ action: 'remove', handle })
                                          });
                                          const data = await res.json();
                                          if (data.success) {
                                            setWinnerHunterConfig({ ...winnerHunterConfig, targetList: data.targetList });
                                          }
                                        } catch (e) { console.error(e); }
                                      }}
                                      style={{ 
                                        background: 'none', 
                                        border: 'none', 
                                        color: '#ff3b69', 
                                        cursor: 'pointer', 
                                        padding: '2px 4px',
                                        fontSize: '14px',
                                        lineHeight: 1,
                                      }}
                                      title="Remove target"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ color: '#666', fontSize: '11px' }}>No targets currently (add manually or auto via thresholds)</div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* 🤖 USER BOT CONFIG SECTION */}
                      <div style={{ background: 'linear-gradient(135deg, #0a1a1a 0%, #0d0612 100%)', borderRadius: '12px', padding: '16px', marginTop: '24px', border: '1px solid #a855f7' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>🤖</span>
                            <div>
                              <div style={{ color: '#a855f7', fontSize: '16px', fontWeight: 'bold' }}>User Bot Config</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Smart trading constraints for player bots</div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API_URL}/api/admin/user-bot-config`, {
                                  headers: { 'X-Session-Id': sessionId }
                                });
                                const data = await res.json();
                                setUserBotConfig(data);
                              } catch (e) {
                                console.error('Failed to load user bot config:', e);
                              }
                            }}
                            style={{ padding: '8px 16px', backgroundColor: '#a855f7', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            🔄 Load
                          </button>
                        </div>
                        
                        {userBotConfig ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {/* Min Profit to Sell */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div>
                                  <div style={{ color: '#a855f7', fontWeight: 'bold', fontSize: '12px' }}>💰 Min Profit to Sell</div>
                                  <div style={{ color: '#666', fontSize: '10px' }}>Bot won't sell for profit less than this (covers penny fee)</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <input
                                    type="number"
                                    value={userBotConfig.minProfitToSell || 100000}
                                    onChange={e => setUserBotConfig({...userBotConfig, minProfitToSell: parseInt(e.target.value) || 0})}
                                    style={{ width: '100px', padding: '6px', backgroundColor: '#0a0a0f', border: '1px solid #a855f7', borderRadius: '4px', color: '#fff', fontSize: '12px', textAlign: 'right' }}
                                  />
                                  <button
                                    onClick={async () => {
                                      try {
                                        const res = await fetch(`${API_URL}/api/admin/user-bot-config`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                          body: JSON.stringify({ minProfitToSell: userBotConfig.minProfitToSell })
                                        });
                                        const data = await res.json();
                                        if (data.success) setUserBotConfig(data);
                                      } catch (e) { console.error(e); }
                                    }}
                                    style={{ padding: '6px 12px', backgroundColor: '#a855f7', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                              <div style={{ color: '#888', fontSize: '10px' }}>Current: {formatPB(userBotConfig.minProfitToSell || 0)} (1¢ fee = ~50K sats)</div>
                            </div>
                            
                            {/* Min Trade Size */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div>
                                  <div style={{ color: '#a855f7', fontWeight: 'bold', fontSize: '12px' }}>📊 Min Trade Size</div>
                                  <div style={{ color: '#666', fontSize: '10px' }}>Minimum position size for bot trades</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <input
                                    type="number"
                                    value={userBotConfig.minTradeSize || 200000}
                                    onChange={e => setUserBotConfig({...userBotConfig, minTradeSize: parseInt(e.target.value) || 0})}
                                    style={{ width: '100px', padding: '6px', backgroundColor: '#0a0a0f', border: '1px solid #a855f7', borderRadius: '4px', color: '#fff', fontSize: '12px', textAlign: 'right' }}
                                  />
                                  <button
                                    onClick={async () => {
                                      try {
                                        const res = await fetch(`${API_URL}/api/admin/user-bot-config`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                          body: JSON.stringify({ minTradeSize: userBotConfig.minTradeSize })
                                        });
                                        const data = await res.json();
                                        if (data.success) setUserBotConfig(data);
                                      } catch (e) { console.error(e); }
                                    }}
                                    style={{ padding: '6px 12px', backgroundColor: '#a855f7', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>

                              {/* Trade Cooldown */}
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '11px' }}>⏱️ Trade Cooldown</div>
                                  <div style={{ color: '#666', fontSize: '10px' }}>Seconds between bot trades</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={(userBotConfig.tradeCooldownMs || 3000) / 1000}
                                    onChange={e => setUserBotConfig({...userBotConfig, tradeCooldownMs: Math.max(1000, Math.min(10000, (parseFloat(e.target.value) || 3) * 1000))})}
                                    style={{ width: '60px', padding: '6px', backgroundColor: '#0a0a0f', border: '1px solid #a855f7', borderRadius: '4px', color: '#fff', fontSize: '12px', textAlign: 'right' }}
                                  />
                                  <span style={{ color: '#888', fontSize: '11px' }}>sec</span>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const res = await fetch(`${API_URL}/api/admin/user-bot-config`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                          body: JSON.stringify({ tradeCooldownMs: userBotConfig.tradeCooldownMs })
                                        });
                                        const data = await res.json();
                                        if (data.success) setUserBotConfig(data);
                                      } catch (e) { console.error(e); }
                                    }}
                                    style={{ padding: '6px 12px', backgroundColor: '#a855f7', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                                  >
                                    Save
                                  </button>
                                </div>
                                <div style={{ color: '#888', fontSize: '10px', marginTop: '4px' }}>Default: 3s (range 1-10s) | Prevents rapid-fire bot trading</div>
                              </div>
                            </div>

                            {/* Toggles Row */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                              {/* Smart Fee Awareness */}
                              <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: `1px solid ${userBotConfig.smartFeeAwareness ? '#00d26a' : '#ff3b69'}` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '11px' }}>🧠 Smart Fee Awareness</div>
                                    <div style={{ color: '#666', fontSize: '9px' }}>Considers fees before selling</div>
                                  </div>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const res = await fetch(`${API_URL}/api/admin/user-bot-config`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                          body: JSON.stringify({ smartFeeAwareness: !userBotConfig.smartFeeAwareness })
                                        });
                                        const data = await res.json();
                                        if (data.success) setUserBotConfig(data);
                                      } catch (e) { console.error(e); }
                                    }}
                                    style={{ padding: '6px 12px', backgroundColor: userBotConfig.smartFeeAwareness ? '#00d26a' : '#ff3b69', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold' }}
                                  >
                                    {userBotConfig.smartFeeAwareness ? 'ON' : 'OFF'}
                                  </button>
                                </div>
                              </div>
                              
                              {/* Luck Boost Chance */}
                              <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #f0b90b' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '11px' }}>🍀 Luck Boost</div>
                                    <div style={{ color: '#666', fontSize: '9px' }}>{((userBotConfig.luckBoostChance || 0) * 100).toFixed(0)}% chance</div>
                                  </div>
                                  <input
                                    type="range"
                                    min="0"
                                    max="50"
                                    value={(userBotConfig.luckBoostChance || 0) * 100}
                                    onChange={async (e) => {
                                      const val = parseInt(e.target.value) / 100;
                                      setUserBotConfig({...userBotConfig, luckBoostChance: val});
                                      try {
                                        await fetch(`${API_URL}/api/admin/user-bot-config`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                          body: JSON.stringify({ luckBoostChance: val })
                                        });
                                      } catch (e) { console.error(e); }
                                    }}
                                    style={{ width: '80px' }}
                                  />
                                </div>
                              </div>
                            </div>
                            
                            {/* Rug Sense Chance */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #ff6b6b' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <div style={{ color: '#ff6b6b', fontWeight: 'bold', fontSize: '11px' }}>🔮 Rug Sense Chance</div>
                                  <div style={{ color: '#666', fontSize: '9px' }}>Chance bot detects rug fate early: {((userBotConfig.rugSenseChance || 0) * 100).toFixed(0)}%</div>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="30"
                                  value={(userBotConfig.rugSenseChance || 0) * 100}
                                  onChange={async (e) => {
                                    const val = parseInt(e.target.value) / 100;
                                    setUserBotConfig({...userBotConfig, rugSenseChance: val});
                                    try {
                                      await fetch(`${API_URL}/api/admin/user-bot-config`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ rugSenseChance: val })
                                      });
                                    } catch (e) { console.error(e); }
                                  }}
                                  style={{ width: '100px' }}
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: '#666', fontSize: '11px', textAlign: 'center', padding: '20px' }}>
                            Click Load to view/edit user bot settings
                          </div>
                        )}
                      </div>

                      {/* 🎰 AI TOKEN CONFIG - Control how many AI tokens run at once */}
                      <div style={{ background: 'linear-gradient(135deg, #1a0a1a 0%, #120612 100%)', borderRadius: '12px', padding: '16px', marginTop: '24px', border: '2px solid #ff6b35' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>🎰</span>
                            <div>
                              <div style={{ color: '#ff6b35', fontSize: '16px', fontWeight: 'bold' }}>AI Token Spawner</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Control how many AI tokens run at once</div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API_URL}/api/admin/ai-token-config`, {
                                  headers: { 'X-Session-Id': sessionId }
                                });
                                const data = await res.json();
                                setAiTokenConfig(data);
                              } catch (e) {
                                console.error('Failed to load AI token config:', e);
                              }
                            }}
                            style={{ padding: '8px 16px', backgroundColor: '#ff6b35', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            🔄 Load
                          </button>
                        </div>

                        {aiTokenConfig ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {/* Current Status */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <div style={{ color: '#ff6b35', fontWeight: 'bold', fontSize: '12px' }}>📊 Current Status</div>
                                  <div style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>
                                    AI Tokens: <span style={{ color: '#fff' }}>{aiTokenConfig.currentAITokens || 0}</span> |
                                    User Tokens: <span style={{ color: '#fff' }}>{aiTokenConfig.currentUserTokens || 0}</span>
                                  </div>
                                </div>
                                <div style={{
                                  padding: '6px 12px',
                                  backgroundColor: aiTokenConfig.enabled ? '#00d26a' : '#ff3b69',
                                  borderRadius: '20px',
                                  color: '#fff',
                                  fontSize: '11px',
                                  fontWeight: 'bold'
                                }}>
                                  {aiTokenConfig.enabled ? 'SPAWNING ON' : 'SPAWNING OFF'}
                                </div>
                              </div>
                            </div>

                            {/* Min Tokens Slider */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div>
                                  <div style={{ color: '#ff6b35', fontWeight: 'bold', fontSize: '12px' }}>🎯 Min AI Tokens</div>
                                  <div style={{ color: '#666', fontSize: '10px' }}>Spawns new tokens when below this count</div>
                                </div>
                                <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>{aiTokenConfig.minTokens || 16}</div>
                              </div>
                              <input
                                type="range"
                                min="0"
                                max="50"
                                value={aiTokenConfig.minTokens || 16}
                                onChange={async (e) => {
                                  const val = parseInt(e.target.value);
                                  setAiTokenConfig({...aiTokenConfig, minTokens: val});
                                  try {
                                    const res = await fetch(`${API_URL}/api/admin/ai-token-config`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({ minTokens: val })
                                    });
                                    const data = await res.json();
                                    if (data.success) setAiTokenConfig(data);
                                  } catch (e) { console.error(e); }
                                }}
                                style={{ width: '100%', accentColor: '#ff6b35' }}
                              />
                              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666', fontSize: '10px', marginTop: '4px' }}>
                                <span>0 (disabled)</span>
                                <span>25</span>
                                <span>50</span>
                              </div>
                            </div>

                            {/* Enable/Disable Toggle */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: `1px solid ${aiTokenConfig.enabled ? '#00d26a' : '#ff3b69'}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '11px' }}>⚡ AI Token Spawning</div>
                                  <div style={{ color: '#666', fontSize: '9px' }}>Toggle AI token creation on/off</div>
                                </div>
                                <button
                                  onClick={async () => {
                                    const newEnabled = !aiTokenConfig.enabled;
                                    setAiTokenConfig({...aiTokenConfig, enabled: newEnabled});
                                    try {
                                      const res = await fetch(`${API_URL}/api/admin/ai-token-config`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ enabled: newEnabled })
                                      });
                                      const data = await res.json();
                                      if (data.success) setAiTokenConfig(data);
                                    } catch (e) { console.error(e); }
                                  }}
                                  style={{
                                    padding: '8px 16px',
                                    backgroundColor: aiTokenConfig.enabled ? '#ff3b69' : '#00d26a',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {aiTokenConfig.enabled ? '⏸️ DISABLE' : '▶️ ENABLE'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: '#666', fontSize: '11px', textAlign: 'center', padding: '20px' }}>
                            Click Load to view/edit AI token spawner settings
                          </div>
                        )}
                      </div>

                      {/* 🩸 BLEED SYSTEM - Auto-rug AI tokens giving too much */}
                      <div style={{ background: 'linear-gradient(135deg, #1a0a0a 0%, #120606 100%)', borderRadius: '12px', padding: '16px', marginTop: '24px', border: '2px solid #8b0000' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>🩸</span>
                            <div>
                              <div style={{ color: '#8b0000', fontSize: '16px', fontWeight: 'bold' }}>Bleed System</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Auto-rug AI tokens that give too much to players</div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API_URL}/api/admin/bleed-config`, {
                                  headers: { 'X-Session-Id': sessionId }
                                });
                                const data = await res.json();
                                setBleedConfig(data);
                              } catch (e) {
                                console.error('Failed to load bleed config:', e);
                              }
                            }}
                            style={{ padding: '8px 16px', backgroundColor: '#8b0000', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            🔄 Load
                          </button>
                        </div>

                        {bleedConfig ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {/* Current Status */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <div style={{ color: '#8b0000', fontWeight: 'bold', fontSize: '12px' }}>📊 Status</div>
                                  <div style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>
                                    Bleeding: <span style={{ color: bleedConfig.bleedingTokens?.length > 0 ? '#ff3b69' : '#666' }}>{bleedConfig.bleedingTokens?.length || 0} tokens</span>
                                  </div>
                                </div>
                                <div style={{
                                  padding: '6px 12px',
                                  backgroundColor: bleedConfig.enabled ? '#8b0000' : '#333',
                                  borderRadius: '20px',
                                  color: '#fff',
                                  fontSize: '11px',
                                  fontWeight: 'bold'
                                }}>
                                  {bleedConfig.enabled ? 'SPLASH ON' : 'SPLASH OFF'}
                                </div>
                              </div>
                            </div>

                            {/* Splash Bleed Multiplier */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div>
                                  <div style={{ color: '#8b0000', fontWeight: 'bold', fontSize: '12px' }}>🌊 Profit Multiplier</div>
                                  <div style={{ color: '#666', fontSize: '10px' }}>Bleed at Nx player's investment</div>
                                </div>
                                <select
                                  value={bleedConfig.profitMultiplier || 3.0}
                                  onChange={async (e) => {
                                    const val = parseFloat(e.target.value);
                                    setBleedConfig({...bleedConfig, profitMultiplier: val});
                                    try {
                                      const res = await fetch(`${API_URL}/api/admin/bleed-config`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ profitMultiplier: val })
                                      });
                                      const data = await res.json();
                                      if (data.success) setBleedConfig({...bleedConfig, ...data});
                                    } catch (e) { console.error(e); }
                                  }}
                                  style={{ padding: '6px 12px', backgroundColor: '#222', color: '#fff', border: '1px solid #444', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold' }}
                                >
                                  <option value={0.5}>0.5x</option>
                                  <option value={1}>1x</option>
                                  <option value={2}>2x</option>
                                  <option value={3}>3x</option>
                                  <option value={4}>4x</option>
                                  <option value={5}>5x</option>
                                </select>
                              </div>
                              <div style={{ color: '#888', fontSize: '10px', marginTop: '8px', padding: '8px', backgroundColor: '#0a0a0a', borderRadius: '4px' }}>
                                💡 Example: Player invests 100K PB → bleed triggers at {((bleedConfig.profitMultiplier || 3.0) * 100).toFixed(0)}K profit
                              </div>
                            </div>

                            {/* Min/Max Threshold */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                              <div style={{ color: '#8b0000', fontWeight: 'bold', fontSize: '12px', marginBottom: '12px' }}>📏 Threshold Limits</div>

                              {/* Min Threshold */}
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                  <div style={{ color: '#666', fontSize: '10px' }}>Floor (min profit for bleed)</div>
                                  <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold' }}>{((bleedConfig.minThreshold || 10000) / 1000).toFixed(0)}K PB</div>
                                </div>
                                <input
                                  type="range"
                                  min="1000"
                                  max="500000"
                                  step="1000"
                                  value={bleedConfig.minThreshold || 10000}
                                  onChange={async (e) => {
                                    const val = parseInt(e.target.value);
                                    setBleedConfig({...bleedConfig, minThreshold: val});
                                    try {
                                      const res = await fetch(`${API_URL}/api/admin/bleed-config`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ minThreshold: val })
                                      });
                                      const data = await res.json();
                                      if (data.success) setBleedConfig({...bleedConfig, ...data});
                                    } catch (e) { console.error(e); }
                                  }}
                                  style={{ width: '100%', accentColor: '#8b0000' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#555', fontSize: '9px', marginTop: '2px' }}>
                                  <span>1K</span>
                                  <span>500K</span>
                                </div>
                              </div>

                              {/* Max Threshold */}
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                  <div style={{ color: '#666', fontSize: '10px' }}>Cap (max profit threshold)</div>
                                  <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold' }}>{((bleedConfig.maxThreshold || 5000000) / 1000000).toFixed(1)}M PB</div>
                                </div>
                                <input
                                  type="range"
                                  min="100000"
                                  max="50000000"
                                  step="100000"
                                  value={bleedConfig.maxThreshold || 5000000}
                                  onChange={async (e) => {
                                    const val = parseInt(e.target.value);
                                    setBleedConfig({...bleedConfig, maxThreshold: val});
                                    try {
                                      const res = await fetch(`${API_URL}/api/admin/bleed-config`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ maxThreshold: val })
                                      });
                                      const data = await res.json();
                                      if (data.success) setBleedConfig({...bleedConfig, ...data});
                                    } catch (e) { console.error(e); }
                                  }}
                                  style={{ width: '100%', accentColor: '#8b0000' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#555', fontSize: '9px', marginTop: '2px' }}>
                                  <span>100K</span>
                                  <span>50M</span>
                                </div>
                              </div>

                              {/* Variance Slider */}
                              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #222' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                  <div style={{ color: '#666', fontSize: '10px' }}>🎲 Randomization</div>
                                  <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold' }}>±{Math.round((bleedConfig.thresholdVariance || 0.2) * 100)}%</div>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="0.5"
                                  step="0.05"
                                  value={bleedConfig.thresholdVariance || 0.2}
                                  onChange={async (e) => {
                                    const val = parseFloat(e.target.value);
                                    setBleedConfig({...bleedConfig, thresholdVariance: val});
                                    try {
                                      const res = await fetch(`${API_URL}/api/admin/bleed-config`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ thresholdVariance: val })
                                      });
                                      const data = await res.json();
                                      if (data.success) setBleedConfig({...bleedConfig, ...data});
                                    } catch (e) { console.error(e); }
                                  }}
                                  style={{ width: '100%', accentColor: '#8b0000' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#555', fontSize: '9px', marginTop: '2px' }}>
                                  <span>0% (exact)</span>
                                  <span>50% (max)</span>
                                </div>
                              </div>
                            </div>

                            {/* Bleed Duration */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div>
                                  <div style={{ color: '#8b0000', fontWeight: 'bold', fontSize: '12px' }}>⏱️ Max Bleed Duration</div>
                                  <div style={{ color: '#666', fontSize: '10px' }}>Fallback if players don't exit</div>
                                </div>
                                <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>{Math.floor((bleedConfig.bleedDurationMs || 300000) / 60000)}m</div>
                              </div>
                              <input
                                type="range"
                                min="60000"
                                max="1800000"
                                step="60000"
                                value={bleedConfig.bleedDurationMs || 300000}
                                onChange={async (e) => {
                                  const val = parseInt(e.target.value);
                                  setBleedConfig({...bleedConfig, bleedDurationMs: val});
                                  try {
                                    const res = await fetch(`${API_URL}/api/admin/bleed-config`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({ bleedDurationMs: val })
                                    });
                                    const data = await res.json();
                                    if (data.success) setBleedConfig({...bleedConfig, ...data});
                                  } catch (e) { console.error(e); }
                                }}
                                style={{ width: '100%', accentColor: '#8b0000' }}
                              />
                              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666', fontSize: '10px', marginTop: '4px' }}>
                                <span>1m</span>
                                <span>15m</span>
                                <span>30m</span>
                              </div>
                            </div>

                            {/* Enable/Disable Toggle */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: `1px solid ${bleedConfig.enabled ? '#8b0000' : '#333'}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '11px' }}>🌊 Splash Bleed System</div>
                                  <div style={{ color: '#666', fontSize: '9px' }}>Per-player profit limits (recoverable)</div>
                                </div>
                                <button
                                  onClick={async () => {
                                    const newEnabled = !bleedConfig.enabled;
                                    setBleedConfig({...bleedConfig, enabled: newEnabled});
                                    try {
                                      const res = await fetch(`${API_URL}/api/admin/bleed-config`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ enabled: newEnabled })
                                      });
                                      const data = await res.json();
                                      if (data.success) setBleedConfig({...bleedConfig, ...data});
                                    } catch (e) { console.error(e); }
                                  }}
                                  style={{
                                    padding: '8px 16px',
                                    backgroundColor: bleedConfig.enabled ? '#333' : '#8b0000',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {bleedConfig.enabled ? '⏸️ DISABLE' : '▶️ ENABLE'}
                                </button>
                              </div>
                            </div>

                            {/* Currently Bleeding Tokens */}
                            {bleedConfig.bleedingTokens && bleedConfig.bleedingTokens.length > 0 && (
                              <div style={{ backgroundColor: '#1a0505', borderRadius: '8px', padding: '12px', border: '1px solid #8b0000' }}>
                                <div style={{ color: '#8b0000', fontWeight: 'bold', fontSize: '12px', marginBottom: '8px' }}>🩸 Currently Bleeding</div>
                                {bleedConfig.bleedingTokens.map(t => (
                                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #300' }}>
                                    <span style={{ color: '#fff', fontSize: '12px' }}>${t.ticker}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ color: '#ff3b69', fontSize: '11px' }}>{((t.playerNetPnL || 0) / 1000).toFixed(0)}k PnL</span>
                                      <span style={{ color: '#666', fontSize: '9px' }}>@{typeof t.thresholdUsed === 'number' ? `${(t.thresholdUsed/1000).toFixed(0)}k` : t.thresholdUsed}</span>
                                      <span style={{ color: '#8b0000', fontSize: '11px', fontWeight: 'bold' }}>{t.bleedProgress}%</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Bleed History (Dead Tokens) */}
                            {bleedConfig.bleedHistory && bleedConfig.bleedHistory.length > 0 && (
                              <div style={{ backgroundColor: '#0a0a0a', borderRadius: '8px', padding: '12px', marginTop: '12px', border: '1px solid #333' }}>
                                <div style={{ color: '#666', fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>☠️ Bleed History</div>
                                {bleedConfig.bleedHistory.slice(0, 10).map((h, i) => (
                                  <div key={h.id + i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #222' }}>
                                    <span style={{ color: h.status === 'rugged' ? '#555' : '#ff3b69', fontSize: '11px' }}>${h.ticker}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ color: '#555', fontSize: '10px' }}>{((h.pnlAtBleed || 0) / 1000).toFixed(0)}k</span>
                                      <span style={{ color: '#444', fontSize: '9px' }}>@{typeof h.thresholdUsed === 'number' ? `${(h.thresholdUsed/1000).toFixed(0)}k` : h.thresholdUsed}</span>
                                      <span style={{
                                        color: h.status === 'rugged' ? '#4a4' : '#ff3b69',
                                        fontSize: '10px',
                                        fontWeight: 'bold'
                                      }}>
                                        {h.status === 'rugged' ? '✓ DEAD' : '🩸 ACTIVE'}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ color: '#666', fontSize: '11px', textAlign: 'center', padding: '20px' }}>
                            Click Load to view/edit bleed system settings
                          </div>
                        )}
                      </div>

                      {/* 💰 ADMIN MARKET MAKER - Sip Liquidity Without Nuking Charts */}
                      <div style={{ background: 'linear-gradient(135deg, #0a1a0f 0%, #0d1208 100%)', borderRadius: '12px', padding: '16px', marginTop: '24px', border: '2px solid #00d26a' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>💰</span>
                            <div>
                              <div style={{ color: '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>Admin Market Maker</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Dump pumps, buy dips - sip liquidity without nuking charts</div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              setAdminMmLoading(true);
                              try {
                                const res = await fetch(`${API_URL}/api/admin/market-maker`, {
                                  headers: { 'X-Session-Id': sessionId }
                                });
                                const data = await res.json();
                                setAdminMmConfig(data);
                              } catch (e) {
                                console.error('Failed to load Admin MM config:', e);
                              }
                              setAdminMmLoading(false);
                            }}
                            style={{ padding: '8px 16px', backgroundColor: '#00d26a', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            {adminMmLoading ? '⏳' : '🔄'} Load
                          </button>
                        </div>

                        {!adminMmConfig && !adminMmLoading && (
                          <div style={{ color: '#666', fontSize: '11px', textAlign: 'center', padding: '20px' }}>
                            Click Load to view/edit Admin Market Maker settings
                          </div>
                        )}
                        
                        {adminMmConfig && (
                          <>
                            {/* Master Toggle */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', marginBottom: '12px', border: `1px solid ${adminMmConfig.enabled ? '#00d26a' : '#333'}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: '#fff', fontWeight: 'bold' }}>Auto Market Making</span>
                                <button
                                  onClick={async () => {
                                    const newVal = !adminMmConfig.enabled;
                                    try {
                                      await fetch(`${API_URL}/api/admin/market-maker`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ enabled: newVal })
                                      });
                                      setAdminMmConfig({ ...adminMmConfig, enabled: newVal });
                                    } catch (e) { alert('Failed'); }
                                  }}
                                  style={{ padding: '8px 16px', backgroundColor: adminMmConfig.enabled ? '#00d26a' : '#333', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                  {adminMmConfig.enabled ? '✅ ENABLED' : '❌ DISABLED'}
                                </button>
                              </div>
                            </div>
                        
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '12px' }}>
                              {/* Take Profit Settings */}
                              <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #00d26a44' }}>
                                <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '12px', marginBottom: '8px' }}>📈 Take Profit</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                  <span style={{ color: '#888', fontSize: '10px', width: '80px' }}>Trigger at:</span>
                                  <input 
                                    type="number" 
                                    value={adminMmConfig.takeProfitPercent || 50}
                                    onChange={(e) => setAdminMmConfig({...adminMmConfig, takeProfitPercent: parseInt(e.target.value)})}
                                    style={{ width: '60px', padding: '4px 8px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '11px' }} 
                                  />
                                  <span style={{ color: '#666', fontSize: '10px' }}>% gain</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ color: '#888', fontSize: '10px', width: '80px' }}>Sell:</span>
                                  <input 
                                    type="number" 
                                    value={adminMmConfig.sellPercent || 25}
                                    onChange={(e) => setAdminMmConfig({...adminMmConfig, sellPercent: parseInt(e.target.value)})}
                                    style={{ width: '60px', padding: '4px 8px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '11px' }} 
                                  />
                                  <span style={{ color: '#666', fontSize: '10px' }}>% of position</span>
                                </div>
                              </div>
                              
                              {/* Buy Dip Settings */}
                              <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #3b82f644' }}>
                                <div style={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '12px', marginBottom: '8px' }}>📉 Buy Dips</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                  <span style={{ color: '#888', fontSize: '10px', width: '80px' }}>Trigger at:</span>
                                  <input 
                                    type="number" 
                                    value={adminMmConfig.buyDipPercent || 20}
                                    onChange={(e) => setAdminMmConfig({...adminMmConfig, buyDipPercent: parseInt(e.target.value)})}
                                    style={{ width: '60px', padding: '4px 8px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '11px' }} 
                                  />
                                  <span style={{ color: '#666', fontSize: '10px' }}>% drop</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ color: '#888', fontSize: '10px', width: '80px' }}>Buy:</span>
                                  <input 
                                    type="number" 
                                    value={adminMmConfig.buyDipAmount || 100000}
                                    onChange={(e) => setAdminMmConfig({...adminMmConfig, buyDipAmount: parseInt(e.target.value)})}
                                    style={{ width: '80px', padding: '4px 8px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '11px' }} 
                                  />
                                  <span style={{ color: '#666', fontSize: '10px' }}>PB</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Execution Settings */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #333', marginBottom: '12px' }}>
                              <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '12px', marginBottom: '8px' }}>⚙️ Execution</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ color: '#888', fontSize: '10px' }}>Chunk size:</span>
                                  <input 
                                    type="number" 
                                    value={adminMmConfig.chunkPercent || 10}
                                    onChange={(e) => setAdminMmConfig({...adminMmConfig, chunkPercent: parseInt(e.target.value)})}
                                    style={{ width: '50px', padding: '4px 8px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '11px' }} 
                                  />
                                  <span style={{ color: '#666', fontSize: '10px' }}>%</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ color: '#888', fontSize: '10px' }}>Delay:</span>
                                  <input 
                                    type="number" 
                                    value={adminMmConfig.delaySeconds || 30}
                                    onChange={(e) => setAdminMmConfig({...adminMmConfig, delaySeconds: parseInt(e.target.value)})}
                                    style={{ width: '50px', padding: '4px 8px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '11px' }} 
                                  />
                                  <span style={{ color: '#666', fontSize: '10px' }}>sec</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ color: '#888', fontSize: '10px' }}>Max daily:</span>
                                  <input 
                                    type="number" 
                                    value={adminMmConfig.maxDailyTrades || 5}
                                    onChange={(e) => setAdminMmConfig({...adminMmConfig, maxDailyTrades: parseInt(e.target.value)})}
                                    style={{ width: '50px', padding: '4px 8px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '11px' }} 
                                  />
                                  <span style={{ color: '#666', fontSize: '10px' }}>trades/token</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Save Button */}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                onClick={async () => {
                                  try {
                                    await fetch(`${API_URL}/api/admin/market-maker`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({
                                        takeProfitPercent: adminMmConfig.takeProfitPercent,
                                        sellPercent: adminMmConfig.sellPercent,
                                        buyDipPercent: adminMmConfig.buyDipPercent,
                                        buyDipAmount: adminMmConfig.buyDipAmount,
                                        chunkPercent: adminMmConfig.chunkPercent,
                                        delaySeconds: adminMmConfig.delaySeconds,
                                        maxDailyTrades: adminMmConfig.maxDailyTrades,
                                      })
                                    });
                                    alert('✅ Settings saved!');
                                  } catch (e) { alert('Failed to save'); }
                                }}
                                style={{ flex: 1, padding: '12px', backgroundColor: '#f0b90b', border: 'none', borderRadius: '8px', color: '#000', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
                              >
                                💾 Save Settings
                              </button>
                              <button
                                onClick={() => {
                                  const stats = adminMmConfig.stats || {};
                                  alert(`📊 Market Maker Stats\n\nTotal Profit Taken: ${formatPB(stats.totalProfitTaken || 0)}\nDips Bought: ${stats.dipsBought || 0}\nTrades Today: ${stats.tradesToday || 0}`);
                                }}
                                style={{ padding: '12px 16px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', color: '#888', cursor: 'pointer', fontSize: '12px' }}
                              >
                                📊 Stats
                              </button>
                            </div>
                          </>
                        )}
                        
                        <div style={{ color: '#666', fontSize: '9px', marginTop: '8px', textAlign: 'center' }}>
                          💡 Tip: Small chunks (10%) over time = natural price action. Big dumps = panic sells.
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* WARS TAB - Trading Wars Management */}
                  {adminTab === 'wars' && adminData?.userRole === 'admin' && (
                    <div>
                      {/* Header */}
                      <div style={{ background: 'linear-gradient(135deg, #1a1508 0%, #0a0a1a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #ffd700' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>🏆</span>
                            <div>
                              <div style={{ color: '#ffd700', fontSize: '16px', fontWeight: 'bold' }}>Trading Wars</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Manage weekly competitions & prize pool</div>
                            </div>
                          </div>
                          <button
                            onClick={fetchWarPot}
                            disabled={warPotLoading}
                            style={{ padding: '8px 16px', backgroundColor: '#1a1508', border: '1px solid #ffd700', borderRadius: '6px', color: '#ffd700', cursor: 'pointer', fontSize: '11px' }}
                          >
                            {warPotLoading ? '⏳' : '🔄'} Refresh
                          </button>
                        </div>
                      </div>
                      
                      {/* Load data on first view */}
                      {!warPotData && !warPotLoading && (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <button
                            onClick={fetchWarPot}
                            style={{ padding: '12px 24px', backgroundColor: '#ffd700', color: '#000', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                          >
                            Load War Pot Data
                          </button>
                        </div>
                      )}
                      
                      {warPotData && (
                        <>
                          {/* Current War Status */}
                          {warPotData.currentWar && (
                            <div style={{ backgroundColor: '#111', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #333' }}>
                              <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>CURRENT WAR</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '18px' }}>War #{warPotData.currentWar.warNumber}</div>
                                  <div style={{ color: '#666', fontSize: '11px' }}>
                                    Ends: {new Date(warPotData.currentWar.endTime).toLocaleDateString()} {new Date(warPotData.currentWar.endTime).toLocaleTimeString()}
                                  </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '20px' }}>{formatPB(warPotData.currentWar.prizePool)}</div>
                                  <div style={{ color: '#888', fontSize: '10px' }}>Current Prize Pool</div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* War Pot Stats */}
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                            <div style={{ backgroundColor: '#0f1a0f', borderRadius: '12px', padding: '16px', border: '1px solid #00d26a' }}>
                              <div style={{ color: '#888', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>🏆 PRIZE POOL</div>
                              <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: isMobile ? '18px' : '24px' }}>{formatPB(warPotData.calculatedPot || 0)}</div>
                              <div style={{ color: '#666', fontSize: '9px' }}>What players see</div>
                            </div>
                            
                            <div style={{ backgroundColor: '#111', borderRadius: '12px', padding: '16px', border: '1px solid #3498db' }}>
                              <div style={{ color: '#888', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>✅ YOU'VE PAID</div>
                              <div style={{ color: '#3498db', fontWeight: 'bold', fontSize: isMobile ? '18px' : '24px' }}>{formatPB(warPotData.potBalance || 0)}</div>
                              <div style={{ color: '#666', fontSize: '9px' }}>Deposited so far</div>
                            </div>
                            
                            <div style={{ backgroundColor: (warPotData.outstanding || 0) > 0 ? '#1a0f0f' : '#0f1a0f', borderRadius: '12px', padding: '16px', border: `1px solid ${(warPotData.outstanding || 0) > 0 ? '#ff8c00' : '#00d26a'}` }}>
                              <div style={{ color: '#888', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>💸 OUTSTANDING</div>
                              <div style={{ color: (warPotData.outstanding || 0) > 0 ? '#ff8c00' : '#00d26a', fontWeight: 'bold', fontSize: isMobile ? '18px' : '24px' }}>{formatPB(warPotData.outstanding || 0)}</div>
                              <div style={{ color: '#666', fontSize: '9px' }}>{(warPotData.outstanding || 0) > 0 ? 'Still owed' : 'All paid!'}</div>
                            </div>
                            
                            <div style={{ backgroundColor: '#1a1508', borderRadius: '12px', padding: '16px', border: '1px solid #f0b90b' }}>
                              <div style={{ color: '#888', fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>📊 TODAY</div>
                              <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: isMobile ? '18px' : '24px' }}>{warPotData.todayTrades || 0} trades</div>
                              <div style={{ color: '#666', fontSize: '9px' }}>+{formatPB(warPotData.suggestedContribution || 0)}</div>
                            </div>
                          </div>
                          
                          {/* Add to Pot */}
                          <div style={{ backgroundColor: '#111', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #333' }}>
                            <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>💰 RECORD PAYMENT (from h2o4daze)</div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                              {[
                                { label: '🔥 Outstanding', amount: warPotData.outstanding, highlight: (warPotData.outstanding || 0) > 0 },
                                { label: 'Today', amount: warPotData.suggestedContribution },
                                { label: '100K', amount: 100000 },
                                { label: '500K', amount: 500000 },
                              ].filter(opt => opt.amount > 0).map(opt => (
                                <button
                                  key={opt.label}
                                  onClick={() => setWarPotAddAmount((opt.amount || 0).toString())}
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: opt.highlight ? '#ff8c00' : warPotAddAmount === (opt.amount || 0).toString() ? '#ffd700' : '#0a0a0f',
                                    color: opt.highlight || warPotAddAmount === (opt.amount || 0).toString() ? '#000' : '#ffd700',
                                    border: `1px solid ${opt.highlight ? '#ff8c00' : '#ffd700'}`,
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: opt.highlight ? 'bold' : 'normal',
                                  }}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="number"
                                value={warPotAddAmount}
                                onChange={e => setWarPotAddAmount(e.target.value)}
                                placeholder="Amount in sats..."
                                style={{
                                  flex: 1,
                                  padding: '10px 12px',
                                  backgroundColor: '#0a0a0f',
                                  border: '1px solid #333',
                                  borderRadius: '6px',
                                  color: '#fff',
                                  fontSize: '14px',
                                }}
                              />
                              <button
                                onClick={() => addToWarPot(warPotAddAmount)}
                                disabled={!warPotAddAmount || parseInt(warPotAddAmount) <= 0}
                                style={{
                                  padding: '10px 20px',
                                  backgroundColor: warPotAddAmount && parseInt(warPotAddAmount) > 0 ? '#00d26a' : '#333',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontWeight: 'bold',
                                  cursor: warPotAddAmount && parseInt(warPotAddAmount) > 0 ? 'pointer' : 'not-allowed',
                                }}
                              >
                                Add to Pot
                              </button>
                            </div>
                          </div>
                          
                          {/* Daily History */}
                          <div style={{ backgroundColor: '#111', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #333' }}>
                            <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>📅 DAILY TRADE HISTORY (Last 7 Days)</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {(warPotData.dailyHistory || []).map((day, i) => (
                                <div key={day.date} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: i === 0 ? '#1a1508' : '#0a0a0f', borderRadius: '6px', border: i === 0 ? '1px solid #f0b90b' : '1px solid transparent' }}>
                                  <span style={{ color: i === 0 ? '#f0b90b' : '#888', fontSize: '12px' }}>{day.date} {i === 0 && '(Today)'}</span>
                                  <div style={{ display: 'flex', gap: '20px' }}>
                                    <span style={{ color: '#666', fontSize: '12px' }}>{day.trades} trades</span>
                                    <span style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>{formatPB(day.suggested)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Contribution History */}
                          {warPotData.contributions?.length > 0 && (
                            <div style={{ backgroundColor: '#111', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #333' }}>
                              <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>📝 CONTRIBUTION HISTORY</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflow: 'auto' }}>
                                {warPotData.contributions.map((c, i) => (
                                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#0a0a0f', borderRadius: '6px' }}>
                                    <span style={{ color: '#888', fontSize: '11px' }}>{c.date} by {c.addedBy}</span>
                                    <span style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>+{formatPB(c.amount)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Admin Actions */}
                          <div style={{ backgroundColor: '#1a0f0f', borderRadius: '12px', padding: '16px', border: '1px solid #ff3b69' }}>
                            <div style={{ color: '#ff3b69', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>⚠️ ADMIN ACTIONS</div>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                              <button
                                onClick={async () => {
                                  if (!confirm('End current war and finalize winners?')) return;
                                  try {
                                    const res = await fetch(`${API_URL}/api/admin/wars/end`, {
                                      method: 'POST',
                                      headers: { 'X-Session-Id': sessionId }
                                    });
                                    if (res.ok) {
                                      alert('✅ War ended! Check winners in the Wars modal.');
                                      fetchWarPot();
                                      fetchTradingWars();
                                    }
                                  } catch (e) { alert('Error: ' + e.message); }
                                }}
                                style={{ padding: '10px 16px', backgroundColor: '#ff8c00', color: '#000', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px' }}
                              >
                                🏁 End War Now
                              </button>
                              <button
                                onClick={resetWarPot}
                                style={{ padding: '10px 16px', backgroundColor: '#ff3b69', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px' }}
                              >
                                🗑️ Reset Pot (After Paying)
                              </button>
                            </div>
                            <div style={{ color: '#888', fontSize: '10px', marginTop: '10px' }}>
                              💡 Pot auto-calculates from trades (10K per trade). Record your h2o4daze payments here to track what you've paid vs owed.
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* BUSINESS TAB - Enhanced KPIs */}
                  {adminTab === 'business' && adminData?.userRole === 'admin' && (
                    <div>
                      {/* Business Overview Header */}
                      <div style={{ background: 'linear-gradient(135deg, #1a1a0a 0%, #0a0a1a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #f0b90b' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>📊</span>
                            <div>
                              <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>Business Intelligence</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Key Performance Indicators</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: adminData.houseStats?.isProfitable ? '#00d26a' : '#ff8c00', animation: 'pulse 2s infinite' }}></div>
                            <span style={{ color: adminData.houseStats?.isProfitable ? '#00d26a' : '#ff8c00', fontSize: '12px', fontWeight: 'bold' }}>
                              {adminData.houseStats?.isProfitable ? 'Profitable' : 'Review Needed'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* 🔒 Lock Balance Panel - Exploit Remediation */}
                      <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '2px solid #ff3b69', overflow: 'hidden', marginBottom: '16px' }}>
                        <div style={{ background: 'linear-gradient(90deg, rgba(255,59,105,0.2) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a' }}>
                          <span style={{ color: '#ff3b69', fontSize: '13px', fontWeight: 'bold' }}>🔒 Lock Balance (Exploit Remediation)</span>
                          <span style={{ color: '#888', fontSize: '10px', marginLeft: '8px' }}>Force playthrough on exploiter accounts</span>
                        </div>
                        <div style={{ padding: '16px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 1fr auto', gap: '12px', alignItems: 'end' }}>
                            <div>
                              <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>Handle</div>
                              <input
                                type="text"
                                value={lockBalanceHandle}
                                onChange={(e) => setLockBalanceHandle(e.target.value)}
                                placeholder="$peweeroots"
                                style={{ width: '100%', padding: '8px 12px', backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontSize: '13px' }}
                              />
                            </div>
                            <div>
                              <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>Multiplier</div>
                              <select
                                value={lockBalanceMultiplier}
                                onChange={(e) => setLockBalanceMultiplier(e.target.value)}
                                style={{ width: '100%', padding: '8px', backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontSize: '13px' }}
                              >
                                <option value="0.5">0.5x (lenient)</option>
                                <option value="1">1x</option>
                                <option value="3">3x (normal)</option>
                                <option value="5">5x</option>
                                <option value="10">10x (nuclear)</option>
                                <option value="20">20x (frozen)</option>
                              </select>
                            </div>
                            <div>
                              <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>Reason</div>
                              <input
                                type="text"
                                value={lockBalanceReason}
                                onChange={(e) => setLockBalanceReason(e.target.value)}
                                placeholder="Rake exploit"
                                style={{ width: '100%', padding: '8px 12px', backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontSize: '13px' }}
                              />
                            </div>
                            <button
                              onClick={adminLockBalance}
                              disabled={lockBalanceLoading || !lockBalanceHandle.trim()}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: lockBalanceLoading ? '#333' : '#ff3b69',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                fontWeight: 'bold',
                                cursor: lockBalanceLoading || !lockBalanceHandle.trim() ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                opacity: lockBalanceLoading || !lockBalanceHandle.trim() ? 0.5 : 1
                              }}
                            >
                              {lockBalanceLoading ? '...' : '🔒 LOCK'}
                            </button>
                          </div>
                          <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#111', borderRadius: '6px', fontSize: '10px', color: '#888' }}>
                            💡 Locks user's ENTIRE balance with custom playthrough. 10x = user needs to trade 10× their balance to unlock. For $peweeroots (10x), for $ransack (0.5x).
                          </div>
                        </div>
                      </div>

                      {/* ⛓️ Set On-Chain Count Panel */}
                      <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '2px solid #00d26a', overflow: 'hidden', marginBottom: '16px' }}>
                        <div style={{ background: 'linear-gradient(90deg, rgba(0,210,106,0.2) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a' }}>
                          <span style={{ color: '#00d26a', fontSize: '13px', fontWeight: 'bold' }}>⛓️ Set On-Chain Count</span>
                          <span style={{ color: '#888', fontSize: '10px', marginLeft: '8px' }}>Sync with HandCash billing</span>
                        </div>
                        <div style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'end' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>Current: <span style={{ color: '#00d26a', fontWeight: 'bold' }}>{(globalStats.onChainTransactions || 0).toLocaleString()}</span></div>
                              <input
                                type="number"
                                value={onChainCountInput}
                                onChange={(e) => setOnChainCountInput(e.target.value)}
                                placeholder="11472"
                                style={{ width: '100%', padding: '8px 12px', backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontSize: '13px' }}
                              />
                            </div>
                            <button
                              onClick={async () => {
                                if (!onChainCountInput.trim()) return;
                                setOnChainCountLoading(true);
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/set-onchain-count`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                    body: JSON.stringify({ count: parseInt(onChainCountInput) })
                                  });
                                  const data = await res.json();
                                  if (data.error) alert('Error: ' + data.error);
                                  else {
                                    alert(`✅ On-Chain Count Updated!\n\n${data.oldCount.toLocaleString()} → ${data.newCount.toLocaleString()}`);
                                    setOnChainCountInput('');
                                    // Update local state
                                    setGlobalStats(prev => ({ ...prev, onChainTransactions: data.newCount }));
                                  }
                                } catch (e) { alert('Error: ' + e.message); }
                                setOnChainCountLoading(false);
                              }}
                              disabled={onChainCountLoading || !onChainCountInput.trim()}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: onChainCountLoading ? '#333' : '#00d26a',
                                color: '#000',
                                border: 'none',
                                borderRadius: '6px',
                                fontWeight: 'bold',
                                cursor: onChainCountLoading || !onChainCountInput.trim() ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                opacity: onChainCountLoading || !onChainCountInput.trim() ? 0.5 : 1
                              }}
                            >
                              {onChainCountLoading ? '...' : '⛓️ SET'}
                            </button>
                          </div>
                          <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#111', borderRadius: '6px', fontSize: '10px', color: '#888' }}>
                            💡 Sync on-chain count with HandCash billing. Check your HandCash merchant portal for total transactions and enter the count here.
                          </div>
                        </div>
                      </div>

                      {/* User Metrics Row */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '14px', border: '1px solid #333', textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '4px' }}>👥</div>
                          <div style={{ color: '#fff', fontSize: '26px', fontWeight: 'bold' }}>{adminData.businessMetrics?.totalUsers || 0}</div>
                          <div style={{ color: '#666', fontSize: '10px' }}>Total Users</div>
                        </div>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '14px', border: '1px solid #f0b90b', textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '4px' }}>👑</div>
                          <div style={{ color: '#f0b90b', fontSize: '26px', fontWeight: 'bold' }}>{adminData.businessMetrics?.premiumUsers || 0}</div>
                          <div style={{ color: '#666', fontSize: '10px' }}>Premium</div>
                          <div style={{ color: '#888', fontSize: '9px' }}>{adminData.businessMetrics?.totalUsers > 0 ? ((adminData.businessMetrics?.premiumUsers || 0) / adminData.businessMetrics.totalUsers * 100).toFixed(1) : 0}% conversion</div>
                        </div>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '14px', border: '1px solid #00d26a', textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '4px' }}>📅</div>
                          <div style={{ color: '#00d26a', fontSize: '26px', fontWeight: 'bold' }}>{adminData.businessMetrics?.activeToday || 0}</div>
                          <div style={{ color: '#666', fontSize: '10px' }}>Active Today</div>
                          <div style={{ color: '#888', fontSize: '9px' }}>DAU</div>
                        </div>
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '14px', border: '1px solid #00bcd4', textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '4px' }}>📆</div>
                          <div style={{ color: '#00bcd4', fontSize: '26px', fontWeight: 'bold' }}>{adminData.businessMetrics?.activeThisWeek || 0}</div>
                          <div style={{ color: '#666', fontSize: '10px' }}>Active Week</div>
                          <div style={{ color: '#888', fontSize: '9px' }}>WAU</div>
                        </div>
                      </div>

                      {/* Platform Metrics */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        {/* Token Ecosystem */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '1px solid #a855f7', overflow: 'hidden' }}>
                          <div style={{ background: 'linear-gradient(90deg, rgba(168,85,247,0.2) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a' }}>
                            <span style={{ color: '#a855f7', fontSize: '13px', fontWeight: 'bold' }}>🎰 Token Ecosystem</span>
                          </div>
                          <div style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                              <div>
                                <div style={{ color: '#666', fontSize: '10px' }}>AI Tokens</div>
                                <div style={{ color: '#fff', fontSize: '22px', fontWeight: 'bold' }}>{adminData.businessMetrics?.aiTokens || 0}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ color: '#666', fontSize: '10px' }}>BSV.pump Tokens</div>
                                <div style={{ color: '#f0b90b', fontSize: '22px', fontWeight: 'bold' }}>{adminData.businessMetrics?.userLaunchedTokens || 0}</div>
                              </div>
                            </div>
                            <div style={{ backgroundColor: '#111', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
                              <div style={{
                                backgroundColor: '#a855f7',
                                height: '100%',
                                width: `${((adminData.businessMetrics?.aiTokens || 0) / ((adminData.businessMetrics?.aiTokens || 0) + (adminData.businessMetrics?.userLaunchedTokens || 1))) * 100}%`,
                                transition: 'width 0.3s'
                              }}></div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '9px', color: '#666' }}>
                              <span>AI: {((adminData.businessMetrics?.aiTokens || 0) / ((adminData.businessMetrics?.aiTokens || 0) + (adminData.businessMetrics?.userLaunchedTokens || 1)) * 100).toFixed(0)}%</span>
                              <span>User: {((adminData.businessMetrics?.userLaunchedTokens || 0) / ((adminData.businessMetrics?.aiTokens || 0) + (adminData.businessMetrics?.userLaunchedTokens || 1)) * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>

                        {/* House Health */}
                        <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '1px solid #00d26a', overflow: 'hidden' }}>
                          <div style={{ background: 'linear-gradient(90deg, rgba(0,210,106,0.2) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a' }}>
                            <span style={{ color: '#00d26a', fontSize: '13px', fontWeight: 'bold' }}>💰 House Health</span>
                          </div>
                          <div style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '12px' }}>
                              <div style={{
                                width: '80px', height: '80px',
                                borderRadius: '50%',
                                border: `4px solid ${(adminData.houseStats?.reserveHealth || 100) >= 100 ? '#00d26a' : (adminData.houseStats?.reserveHealth || 100) >= 80 ? '#ff8c00' : '#ff3b69'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'
                              }}>
                                <div style={{ color: (adminData.houseStats?.reserveHealth || 100) >= 100 ? '#00d26a' : (adminData.houseStats?.reserveHealth || 100) >= 80 ? '#ff8c00' : '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>
                                  {adminData.houseStats?.reserveHealth || 100}%
                                </div>
                                <div style={{ color: '#666', fontSize: '8px' }}>COVERAGE</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: adminData.houseStats?.isProfitable ? '#00d26a' : '#ff3b69', fontSize: '18px' }}>
                                  {adminData.houseStats?.isProfitable ? '✅' : '⚠️'}
                                </div>
                                <div style={{ color: '#666', fontSize: '9px' }}>{adminData.houseStats?.isProfitable ? 'Profitable' : 'Review'}</div>
                              </div>
                            </div>
                            <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '9px', color: '#666' }}>
                              Player Retention: {adminData.houseStats?.playerRetention || 0}%
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 🎰 House Profitability Analysis - AI vs PvP */}
                      <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '2px solid #f0b90b', overflow: 'hidden', marginBottom: '16px' }}>
                        <div style={{ background: 'linear-gradient(90deg, rgba(240,185,11,0.2) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a' }}>
                          <span style={{ color: '#f0b90b', fontSize: '13px', fontWeight: 'bold' }}>🎰 House Profitability Analysis</span>
                          <span style={{ color: '#888', fontSize: '10px', marginLeft: '8px' }}>AI Edge + PvP Playthrough + Fees</span>
                        </div>
                        <div style={{ padding: '16px' }}>
                          {/* Main metrics grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                            {/* AI Token Edge */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #a855f7' }}>
                              <div style={{ color: '#a855f7', fontSize: '10px', marginBottom: '4px' }}>🤖 AI Token House Edge</div>
                              <div style={{ 
                                color: (adminData.houseStats?.profitability?.aiTokenHouseEdge || 0) >= 0 ? '#00d26a' : '#ff3b69', 
                                fontSize: '18px', 
                                fontWeight: 'bold' 
                              }}>
                                {(adminData.houseStats?.profitability?.aiTokenHouseEdge || 0) >= 0 ? '+' : ''}{formatPB(adminData.houseStats?.profitability?.aiTokenHouseEdge || 0)}
                              </div>
                              <div style={{ color: '#666', fontSize: '9px', marginTop: '4px' }}>
                                Losses: {formatPB(adminData.houseStats?.profitability?.aiTokenPlayerLosses || 0)} | Wins: {formatPB(adminData.houseStats?.profitability?.aiTokenPlayerWins || 0)}
                              </div>
                              <div style={{ color: '#888', fontSize: '9px' }}>
                                {adminData.houseStats?.profitability?.aiTokenTrades || 0} trades
                              </div>
                            </div>

                            {/* PvP Bonus Created */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #ff8c00' }}>
                              <div style={{ color: '#ff8c00', fontSize: '10px', marginBottom: '4px' }}>🎯 PvP Bonus Created</div>
                              <div style={{ color: '#ff8c00', fontSize: '18px', fontWeight: 'bold' }}>
                                {formatPB(adminData.houseStats?.profitability?.pvpBonusCreated || 0)}
                              </div>
                              <div style={{ color: '#666', fontSize: '9px', marginTop: '4px' }}>
                                Requires 3x playthrough
                              </div>
                              <div style={{ color: '#888', fontSize: '9px' }}>
                                Locked until traded
                              </div>
                            </div>

                            {/* Fee Revenue */}
                            <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px', border: '1px solid #00d26a' }}>
                              <div style={{ color: '#00d26a', fontSize: '10px', marginBottom: '4px' }}>💰 Fee Revenue (BSV)</div>
                              <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>
                                {((adminData.houseStats?.profitability?.totalFeeRevenue || 0) / 100000000).toFixed(4)} BSV
                              </div>
                              <div style={{ color: '#666', fontSize: '9px', marginTop: '4px' }}>
                                ≈ ${(((adminData.houseStats?.profitability?.totalFeeRevenue || 0) / 100000000) * (bsvPrice || 20)).toFixed(2)} USD
                              </div>
                              <div style={{ color: '#888', fontSize: '9px' }}>
                                {adminData.houseStats?.profitability?.totalFeeCount || 0} transactions
                              </div>
                            </div>
                          </div>

                          {/* Net House Position */}
                          {(() => {
                            // Pull from correct backend paths
                            const totalDeposited = adminData.houseStats?.totalDepositSats || adminData.houseStats?.totalDeposits || 0;
                            const totalWithdrawn = adminData.houseStats?.totalWithdrawalSats || adminData.houseStats?.totalWithdrawals || 0;
                            const feeRevenueBSV = (adminData.houseStats?.profitability?.totalFeeRevenue || 0) / 100000000;
                            const feeRevenueUSD = feeRevenueBSV * (bsvPrice || 20);
                            const rakeCollected = adminData.houseStats?.totalRakeCollected || 0;
                            const rakeUSD = (rakeCollected / 100000000) * (bsvPrice || 20);
                            const premiumUsers = adminData.businessMetrics?.premiumUsers || 0;
                            const premiumRevenueUSD = premiumUsers * 0.25; // $0.25 per premium
                            
                            // Real USD calculations
                            const depositedUSD = (totalDeposited / 100000000) * (bsvPrice || 20);
                            const withdrawnUSD = (totalWithdrawn / 100000000) * (bsvPrice || 20);
                            const totalRevenueUSD = feeRevenueUSD + rakeUSD + premiumRevenueUSD;
                            const netRealUSD = depositedUSD - withdrawnUSD + totalRevenueUSD;
                            
                            const aiEdge = adminData.houseStats?.profitability?.aiTokenHouseEdge || 0;
                            const pvpLocked = adminData.houseStats?.profitability?.pvpBonusCreated || 0;
                            // Use playthroughStats state (from /api/admin/playthrough-stats) OR fallback to houseStats
                            const totalLocked = playthroughStats?.totalLocked || adminData.houseStats?.playthroughStats?.totalLocked || 0;
                            
                            return (
                              <div style={{ 
                                backgroundColor: '#0a1a0a', 
                                borderRadius: '8px', 
                                padding: '16px', 
                                border: '2px solid #00d26a',
                              }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                  {/* Left: Real USD */}
                                  <div style={{ textAlign: 'center', borderRight: '1px solid #333', paddingRight: '16px' }}>
                                    <div style={{ color: '#00d26a', fontSize: '10px', marginBottom: '4px', fontWeight: 'bold' }}>💵 REAL USD EXPOSURE</div>
                                    <div style={{ color: '#00d26a', fontSize: '28px', fontWeight: 'bold' }}>
                                      +${netRealUSD.toFixed(2)}
                                    </div>
                                    <div style={{ color: '#666', fontSize: '9px', marginTop: '8px', textAlign: 'left' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                        <span>⬇️ Deposited:</span>
                                        <span style={{ color: '#00d26a' }}>${depositedUSD.toFixed(2)}</span>
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                        <span>⬆️ Withdrawn:</span>
                                        <span style={{ color: '#ff3b69' }}>-${withdrawnUSD.toFixed(2)}</span>
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                        <span>💰 Fees + Rake:</span>
                                        <span style={{ color: '#00d26a' }}>+${(feeRevenueUSD + rakeUSD).toFixed(2)}</span>
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>👑 Premium ({premiumUsers}):</span>
                                        <span style={{ color: '#f0b90b' }}>+${premiumRevenueUSD.toFixed(2)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Right: Paper Bucks Status */}
                                  <div style={{ textAlign: 'center', paddingLeft: '16px' }}>
                                    <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px', fontWeight: 'bold' }}>🎮 PAPER BUCKS (Not Real $)</div>
                                    <div style={{ color: '#888', fontSize: '9px', marginTop: '8px', textAlign: 'left' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span>🤖 AI Edge:</span>
                                        <span style={{ color: aiEdge >= 0 ? '#00d26a' : '#ff8c00' }}>{formatPB(aiEdge)}</span>
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span>🔒 PvP Locked:</span>
                                        <span style={{ color: '#ff8c00' }}>{formatPB(pvpLocked)}</span>
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span>🔒 Total Locked:</span>
                                        <span style={{ color: '#f0b90b' }}>{formatPB(totalLocked)}</span>
                                      </div>
                                      <div style={{ marginTop: '8px', padding: '6px', backgroundColor: '#111', borderRadius: '4px', color: '#666', fontSize: '8px' }}>
                                        ℹ️ AI losses = bait to hook players<br/>
                                        Players win PB → Play PvP → Get LOCKED
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#0d1a0d', borderRadius: '6px', border: '1px solid #00d26a44', textAlign: 'center' }}>
                                  <span style={{ color: '#00d26a', fontSize: '11px', fontWeight: 'bold' }}>✅ HOUSE IS HEALTHY</span>
                                  <span style={{ color: '#666', fontSize: '10px', marginLeft: '8px' }}>
                                    Real cash in your favor • PB losses are bait • Locked funds = trapped players
                                  </span>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Info explanation */}
                          <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#0a0a0f', borderRadius: '6px', border: '1px solid #333' }}>
                            <div style={{ color: '#666', fontSize: '9px', fontFamily: 'monospace' }}>
                              Real USD = Deposited - Withdrawn + Fees + Rake + Premium
                            </div>
                            <div style={{ color: '#888', fontSize: '9px', marginTop: '4px' }}>
                              AI Token losses aren't real $ - they're Paper Bucks that hook players into PvP where profits get locked in 3x playthrough
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Engagement Metrics */}
                      <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '1px solid #333', overflow: 'hidden' }}>
                        <div style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a' }}>
                          <span style={{ color: '#fff', fontSize: '13px', fontWeight: 'bold' }}>📈 Engagement Overview</span>
                        </div>
                        <div style={{ padding: '16px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', textAlign: 'center' }}>
                            <div>
                              <div style={{ color: '#00d26a', fontSize: '20px', fontWeight: 'bold' }}>
                                {adminData.businessMetrics?.totalUsers > 0 ? ((adminData.businessMetrics?.activeToday || 0) / adminData.businessMetrics.totalUsers * 100).toFixed(1) : 0}%
                              </div>
                              <div style={{ color: '#666', fontSize: '10px' }}>Daily Active Rate</div>
                            </div>
                            <div>
                              <div style={{ color: '#00bcd4', fontSize: '20px', fontWeight: 'bold' }}>
                                {adminData.businessMetrics?.totalUsers > 0 ? ((adminData.businessMetrics?.activeThisWeek || 0) / adminData.businessMetrics.totalUsers * 100).toFixed(1) : 0}%
                              </div>
                              <div style={{ color: '#666', fontSize: '10px' }}>Weekly Active Rate</div>
                            </div>
                            <div>
                              <div style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold' }}>
                                {adminData.businessMetrics?.premiumUsers > 0 && adminData.businessMetrics?.totalUsers > 0 ? ((adminData.businessMetrics.premiumUsers / adminData.businessMetrics.totalUsers) * 100).toFixed(1) : 0}%
                              </div>
                              <div style={{ color: '#666', fontSize: '10px' }}>Premium Rate</div>
                            </div>
                            <div>
                              <div style={{ color: '#a855f7', fontSize: '20px', fontWeight: 'bold' }}>
                                {(adminData.businessMetrics?.aiTokens || 0) + (adminData.businessMetrics?.userLaunchedTokens || 0)}
                              </div>
                              <div style={{ color: '#666', fontSize: '10px' }}>Total Tokens</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 👶 BABY SITTING TAB - House Protection Dashboard */}
                  {adminTab === 'babysitting' && adminData?.userRole === 'admin' && (
                    <div>
                      {/* Header */}
                      <div style={{ background: 'linear-gradient(135deg, #1a1a0a 0%, #0a1a1a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '2px solid #f0b90b' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '28px' }}>👶</span>
                            <div>
                              <div style={{ color: '#f0b90b', fontSize: '16px', fontWeight: 'bold' }}>Baby Sitting Mode</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>Automated house protection - let the system do the work</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={async () => {
                                if (!confirm('Clear ALL dead bot positions from Redis? This removes stuck positions for all users.')) return;
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/clear-dead-positions`, { method: 'POST', headers: { 'X-Session-Id': sessionId } });
                                  const data = await res.json();
                                  if (data.success) {
                                    showToast('success', `🧹 ${data.message}`);
                                  } else {
                                    showToast('error', '❌ ' + data.error);
                                  }
                                } catch (e) {
                                  showToast('error', '❌ Failed: ' + e.message);
                                }
                              }}
                              style={{ padding: '8px 16px', backgroundColor: '#ff4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}
                            >
                              🧹 Clear Dead Positions
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await fetch(`${API_URL}/api/admin/babysitting/test-alert`, { method: 'POST', headers: { 'X-Session-Id': sessionId } });
                                  showToast('success', '✅ Test alert sent to Discord!');
                                } catch (e) {
                                  showToast('error', '❌ Failed to send test alert');
                                }
                              }}
                              style={{ padding: '8px 16px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}
                            >
                              🧪 Test Discord
                            </button>
                            <button
                              onClick={async () => {
                                setBabysittingLoading(true);
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/babysitting`, { headers: { 'X-Session-Id': sessionId } });
                                  setBabysittingData(await res.json());
                                } catch (e) { console.error('Failed to load babysitting data:', e); }
                                setBabysittingLoading(false);
                              }}
                              style={{ padding: '8px 16px', backgroundColor: '#f0b90b', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                            >
                              {babysittingLoading ? '⏳ Loading...' : '🔄 Refresh'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {babysittingLoading && <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>⏳ Loading protection status...</div>}

                      {babysittingData && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {/* Top Row - Key Metrics */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                            <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: `2px solid ${parseFloat(babysittingData.houseEdge) >= 20 ? '#00d26a' : parseFloat(babysittingData.houseEdge) >= 10 ? '#f0b90b' : '#ff3b69'}` }}>
                              <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>📊 House Edge</div>
                              <div style={{ color: parseFloat(babysittingData.houseEdge) >= 20 ? '#00d26a' : parseFloat(babysittingData.houseEdge) >= 10 ? '#f0b90b' : '#ff3b69', fontSize: '24px', fontWeight: 'bold' }}>{babysittingData.houseEdge}%</div>
                              <div style={{ color: '#666', fontSize: '9px' }}>Target: {babysittingData.targetHouseEdge}%</div>
                            </div>
                            <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: `2px solid ${babysittingData.circuitBreaker?.status === 'healthy' ? '#00d26a' : '#ff3b69'}` }}>
                              <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>🚨 Circuit Breaker</div>
                              <div style={{ color: babysittingData.circuitBreaker?.status === 'healthy' ? '#00d26a' : '#ff3b69', fontSize: '18px', fontWeight: 'bold', textTransform: 'uppercase' }}>{babysittingData.circuitBreaker?.status || 'unknown'}</div>
                            </div>
                            <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: '1px solid #333' }}>
                              <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>🛡️ Anti-Exploit</div>
                              <div style={{ color: '#00d26a', fontSize: '24px', fontWeight: 'bold' }}>{babysittingData.antiExploitOn}/{babysittingData.antiExploitTotal}</div>
                            </div>
                            <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: `2px solid ${babysittingData.discordEnabled ? '#00d26a' : '#ff3b69'}` }}>
                              <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>📢 Discord</div>
                              <div style={{ color: babysittingData.discordEnabled ? '#00d26a' : '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>{babysittingData.discordEnabled ? '✅ ON' : '❌ OFF'}</div>
                            </div>
                          </div>

                          {/* Bleed System */}
                          <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: '1px solid #333' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                              <div style={{ color: '#ff3b69', fontSize: '13px', fontWeight: 'bold' }}>🩸 Bleed System</div>
                              <span style={{ color: babysittingData.bleedConfig?.enabled ? '#00d26a' : '#ff3b69', fontSize: '11px', fontWeight: 'bold' }}>{babysittingData.bleedConfig?.enabled ? '✅ ENABLED' : '❌ DISABLED'}</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                              <div><div style={{ color: '#888', fontSize: '9px' }}>Threshold</div><div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>{formatPB(babysittingData.bleedConfig?.profitThreshold || 0)}</div></div>
                              <div><div style={{ color: '#888', fontSize: '9px' }}>Variance</div><div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>±{((babysittingData.bleedConfig?.thresholdVariance || 0) * 100).toFixed(0)}%</div></div>
                              <div><div style={{ color: '#888', fontSize: '9px' }}>Duration</div><div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>{((babysittingData.bleedConfig?.bleedDurationMs || 0) / 60000).toFixed(0)}m</div></div>
                              <div><div style={{ color: '#888', fontSize: '9px' }}>Unrealized</div><div style={{ color: babysittingData.bleedConfig?.checkUnrealized ? '#00d26a' : '#888', fontSize: '16px', fontWeight: 'bold' }}>{babysittingData.bleedConfig?.checkUnrealized ? '✅' : '❌'}</div></div>
                            </div>
                          </div>

                          {/* 📉 Slippage Control */}
                          <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: '2px solid #4a9eff' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                              <div style={{ color: '#4a9eff', fontSize: '13px', fontWeight: 'bold' }}>📉 Slippage Control</div>
                              <span style={{ color: '#4a9eff', fontSize: '14px', fontWeight: 'bold' }}>{((babysittingData.antiExploitSettings?.SLIPPAGE_BASE || 0.10) * 100).toFixed(0)}% base</span>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <input
                                type="range"
                                min="0"
                                max="25"
                                step="1"
                                value={(babysittingData.antiExploitSettings?.SLIPPAGE_BASE || 0.10) * 100}
                                onChange={async (e) => {
                                  const newSlippage = parseInt(e.target.value) / 100;
                                  try {
                                    const res = await fetch(`${API_URL}/api/admin/babysitting/set-slippage`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({ slippageBase: newSlippage })
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      // Update local state
                                      setBabysittingData(prev => ({
                                        ...prev,
                                        antiExploitSettings: { ...prev.antiExploitSettings, SLIPPAGE_BASE: newSlippage }
                                      }));
                                      showToast('success', `📉 Slippage: ${(newSlippage * 100).toFixed(0)}%`);
                                    }
                                  } catch (err) {
                                    showToast('error', '❌ Failed to update slippage');
                                  }
                                }}
                                style={{ width: '100%', cursor: 'pointer', accentColor: '#4a9eff' }}
                              />
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#666', marginTop: '4px' }}>
                                <span>0%</span>
                                <span>5%</span>
                                <span>10%</span>
                                <span>15%</span>
                                <span>20%</span>
                                <span>25%</span>
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '10px' }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#888' }}>Buy Fee</div>
                                <div style={{ color: '#ff3b69', fontWeight: 'bold' }}>{((babysittingData.antiExploitSettings?.SLIPPAGE_BASE || 0.10) * 100).toFixed(0)}%</div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#888' }}>Sell Fee</div>
                                <div style={{ color: '#ff3b69', fontWeight: 'bold' }}>{((babysittingData.antiExploitSettings?.SLIPPAGE_BASE || 0.10) * 100).toFixed(0)}%</div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#888' }}>Round Trip</div>
                                <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>~{(((babysittingData.antiExploitSettings?.SLIPPAGE_BASE || 0.10) * 2 - Math.pow(babysittingData.antiExploitSettings?.SLIPPAGE_BASE || 0.10, 2)) * 100).toFixed(0)}%</div>
                              </div>
                            </div>
                            <div style={{ color: '#666', fontSize: '9px', marginTop: '8px', textAlign: 'center' }}>
                              🔥 House burns slippage (deflationary) • Max 25% cap
                            </div>
                          </div>

                          {/* Winner Hunter */}
                          <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: '1px solid #333' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                              <div style={{ color: '#f0b90b', fontSize: '13px', fontWeight: 'bold' }}>🎯 Winner Hunter</div>
                              <span style={{ color: babysittingData.winnerHunter?.enabled ? '#00d26a' : '#ff3b69', fontSize: '11px', fontWeight: 'bold' }}>{babysittingData.winnerHunter?.enabled ? '✅ HUNTING' : '❌ DISABLED'}</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                              <div><div style={{ color: '#888', fontSize: '9px' }}>Aggression</div><div style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold' }}>{((babysittingData.winnerHunter?.aggression || 0) * 100).toFixed(0)}%</div></div>
                              <div><div style={{ color: '#888', fontSize: '9px' }}>Targets</div><div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>{babysittingData.winnerHunter?.targetCount || 0}</div></div>
                            </div>
                          </div>

                          {/* 🎯 GAMETIGHT TUNE BUTTON */}
                          <div style={{ backgroundColor: '#0a1a0a', borderRadius: '12px', padding: '20px', border: '2px solid #f0b90b' }}>
                            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                              <div style={{ color: '#f0b90b', fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>🎯 Gametight Optimal Tuning</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>One-click configuration for maximum locked profits</div>
                            </div>
                            <button
                              onClick={async () => {
                                if (!confirm('🎯 APPLY OPTIMAL TUNING?\n\nThis will configure:\n• Rug rate: 0.85x\n• Bleed threshold: 3M PB\n• Hunter aggression: 35%\n• Moonshots: 15%\n\n(Slippage NOT touched - use slider above)\n\nAll changes saved to Redis.')) return;
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/babysitting/tune-house-edge`, { method: 'POST', headers: { 'X-Session-Id': sessionId } });
                                  const data = await res.json();
                                  if (data.success) {
                                    showToast('success', '🎯 Optimal tuning applied!');
                                    const refreshRes = await fetch(`${API_URL}/api/admin/babysitting`, { headers: { 'X-Session-Id': sessionId } });
                                    setBabysittingData(await refreshRes.json());
                                  } else { showToast('error', '❌ Tuning failed'); }
                                } catch (e) { showToast('error', '❌ Tuning failed'); }
                              }}
                              style={{ width: '100%', padding: '14px 20px', background: 'linear-gradient(135deg, #f0b90b 0%, #d4a00a 100%)', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                            >
                              🎯 APPLY OPTIMAL TUNING
                            </button>
                          </div>

                          {/* 🔥 Keep Warm Bot */}
                          <div style={{ backgroundColor: '#0a0a0f', borderRadius: '12px', padding: '20px', border: `2px solid ${babysittingData.keepWarmBot?.enabled ? '#ff6b35' : '#333'}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '24px' }}>🔥</span>
                                <div>
                                  <div style={{ color: '#ff6b35', fontSize: '14px', fontWeight: 'bold' }}>Keep Warm Bot</div>
                                  <div style={{ color: '#888', fontSize: '10px' }}>Keeps token engine running during business hours</div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: babysittingData.keepWarmBot?.isActive ? '#00d26a' : '#666', fontSize: '11px' }}>
                                  {babysittingData.keepWarmBot?.isActive ? '🟢 ACTIVE' : '⚫ IDLE'}
                                </span>
                                <button
                                  onClick={async () => {
                                    try {
                                      const newEnabled = !babysittingData.keepWarmBot?.enabled;
                                      const res = await fetch(`${API_URL}/api/admin/babysitting/keep-warm`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ enabled: newEnabled })
                                      });
                                      const data = await res.json();
                                      if (data.success) {
                                        setBabysittingData(prev => ({ ...prev, keepWarmBot: data.keepWarmBot }));
                                        showToast('success', `🔥 Keep Warm Bot ${newEnabled ? 'ENABLED' : 'DISABLED'}`);
                                      }
                                    } catch (e) { showToast('error', '❌ Failed to toggle warm bot'); }
                                  }}
                                  style={{ padding: '8px 16px', backgroundColor: babysittingData.keepWarmBot?.enabled ? '#ff3b69' : '#00d26a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                                >
                                  {babysittingData.keepWarmBot?.enabled ? '🛑 DISABLE' : '🔥 ENABLE'}
                                </button>
                              </div>
                            </div>

                            {/* Always On Toggle */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', padding: '10px', backgroundColor: babysittingData.keepWarmBot?.alwaysOn ? 'rgba(240, 185, 11, 0.1)' : 'transparent', borderRadius: '8px', border: babysittingData.keepWarmBot?.alwaysOn ? '1px solid #f0b90b' : '1px solid transparent' }}>
                              <input
                                type="checkbox"
                                id="keepWarmAlwaysOn"
                                checked={babysittingData.keepWarmBot?.alwaysOn || false}
                                onChange={async (e) => {
                                  const alwaysOn = e.target.checked;
                                  try {
                                    const res = await fetch(`${API_URL}/api/admin/babysitting/keep-warm`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                      body: JSON.stringify({ alwaysOn })
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      setBabysittingData(prev => ({ ...prev, keepWarmBot: data.keepWarmBot }));
                                      showToast('success', alwaysOn ? '🔥 24/7 Mode ON' : '🔥 Using hour range');
                                    }
                                  } catch (e) { showToast('error', '❌ Failed'); }
                                }}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                              <label htmlFor="keepWarmAlwaysOn" style={{ color: babysittingData.keepWarmBot?.alwaysOn ? '#f0b90b' : '#888', fontSize: '12px', fontWeight: babysittingData.keepWarmBot?.alwaysOn ? 'bold' : 'normal', cursor: 'pointer' }}>
                                Always On (24/7) - ignores hour range
                              </label>
                            </div>

                            {/* Business Hours Config */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px', opacity: babysittingData.keepWarmBot?.alwaysOn ? 0.4 : 1, pointerEvents: babysittingData.keepWarmBot?.alwaysOn ? 'none' : 'auto' }}>
                              <div>
                                <div style={{ color: '#888', fontSize: '9px', marginBottom: '4px' }}>Start Hour</div>
                                <select
                                  value={babysittingData.keepWarmBot?.startHour ?? 8}
                                  onChange={async (e) => {
                                    const startHour = parseInt(e.target.value);
                                    try {
                                      const res = await fetch(`${API_URL}/api/admin/babysitting/keep-warm`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ startHour })
                                      });
                                      const data = await res.json();
                                      if (data.success) {
                                        setBabysittingData(prev => ({ ...prev, keepWarmBot: data.keepWarmBot }));
                                        showToast('success', `🔥 Start hour: ${startHour}:00`);
                                      }
                                    } catch (e) { showToast('error', '❌ Failed'); }
                                  }}
                                  style={{ width: '100%', padding: '8px', backgroundColor: '#1a1a2e', color: '#fff', border: '1px solid #333', borderRadius: '6px', fontSize: '14px' }}
                                >
                                  {[...Array(24)].map((_, i) => (
                                    <option key={i} value={i}>{i}:00 {i < 12 ? 'AM' : 'PM'}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <div style={{ color: '#888', fontSize: '9px', marginBottom: '4px' }}>End Hour</div>
                                <select
                                  value={babysittingData.keepWarmBot?.endHour ?? 22}
                                  onChange={async (e) => {
                                    const endHour = parseInt(e.target.value);
                                    try {
                                      const res = await fetch(`${API_URL}/api/admin/babysitting/keep-warm`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                        body: JSON.stringify({ endHour })
                                      });
                                      const data = await res.json();
                                      if (data.success) {
                                        setBabysittingData(prev => ({ ...prev, keepWarmBot: data.keepWarmBot }));
                                        showToast('success', `🔥 End hour: ${endHour}:00`);
                                      }
                                    } catch (e) { showToast('error', '❌ Failed'); }
                                  }}
                                  style={{ width: '100%', padding: '8px', backgroundColor: '#1a1a2e', color: '#fff', border: '1px solid #333', borderRadius: '6px', fontSize: '14px' }}
                                >
                                  {[...Array(25)].map((_, i) => (
                                    <option key={i} value={i}>{i}:00 {i < 12 ? 'AM' : (i === 24 ? 'midnight' : 'PM')}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <div style={{ color: '#888', fontSize: '9px', marginBottom: '4px' }}>Timezone</div>
                                <div style={{ padding: '8px', backgroundColor: '#1a1a2e', color: '#666', border: '1px solid #333', borderRadius: '6px', fontSize: '12px', textAlign: 'center' }}>
                                  {babysittingData.keepWarmBot?.timezone || 'America/New_York'}
                                </div>
                              </div>
                            </div>

                            <div style={{ color: babysittingData.keepWarmBot?.alwaysOn ? '#f0b90b' : '#666', fontSize: '9px', marginTop: '12px', textAlign: 'center' }}>
                              {babysittingData.keepWarmBot?.alwaysOn ? '🔥 24/7 Mode: Server always warm, no deep sleep' : '🌙 Outside business hours: deep sleep (warmup protection kicks in)'}
                            </div>
                          </div>
                        </div>
                      )}

                      {!babysittingData && !babysittingLoading && (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <button onClick={async () => { setBabysittingLoading(true); try { const res = await fetch(`${API_URL}/api/admin/babysitting`, { headers: { 'X-Session-Id': sessionId } }); setBabysittingData(await res.json()); } catch (e) {} setBabysittingLoading(false); }} style={{ padding: '16px 32px', backgroundColor: '#f0b90b', color: '#000', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                            👶 Load Baby Sitting Dashboard
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <button onClick={fetchAdminDashboard} style={{ backgroundColor: '#f0b90b', border: 'none', borderRadius: '8px', padding: '12px 24px', color: '#000', fontWeight: 'bold', cursor: 'pointer' }}>
                    Load Dashboard
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        )}

        {/* OLD BABY SITTING TAB REMOVED - NOW INSIDE MODAL */}
        {false && (
          <div>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #1a1a0a 0%, #0a1a1a 100%)', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '2px solid #f0b90b' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>👶</span>
                  <div>
                    <div style={{ color: '#f0b90b', fontSize: '16px', fontWeight: 'bold' }}>Baby Sitting Mode</div>
                    <div style={{ color: '#888', fontSize: '11px' }}>Automated house protection - let the system do the work</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={async () => {
                      try {
                        await fetch(`${API_URL}/api/admin/babysitting/test-alert`, {
                          method: 'POST',
                          headers: { 'X-Session-Id': sessionId }
                        });
                        showToast('success', '✅ Test alert sent to Discord!');
                      } catch (e) {
                        showToast('error', '❌ Failed to send test alert');
                      }
                    }}
                    style={{ padding: '8px 16px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    🧪 Test Discord
                  </button>
                  <button
                    onClick={async () => {
                      setBabysittingLoading(true);
                      try {
                        const res = await fetch(`${API_URL}/api/admin/babysitting`, {
                          headers: { 'X-Session-Id': sessionId }
                        });
                        const data = await res.json();
                        setBabysittingData(data);
                      } catch (e) {
                        console.error('Failed to load babysitting data:', e);
                      }
                      setBabysittingLoading(false);
                    }}
                    style={{ padding: '8px 16px', backgroundColor: '#f0b90b', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                  >
                    {babysittingLoading ? '⏳ Loading...' : '🔄 Refresh'}
                  </button>
                </div>
              </div>
            </div>

            {/* Load on first view */}
            {!babysittingData && !babysittingLoading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <button
                  onClick={async () => {
                    setBabysittingLoading(true);
                    try {
                      const res = await fetch(`${API_URL}/api/admin/babysitting`, {
                        headers: { 'X-Session-Id': sessionId }
                      });
                      const data = await res.json();
                      setBabysittingData(data);
                    } catch (e) {
                      console.error('Failed to load babysitting data:', e);
                    }
                    setBabysittingLoading(false);
                  }}
                  style={{ padding: '16px 32px', backgroundColor: '#f0b90b', color: '#000', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                >
                  👶 Load Baby Sitting Dashboard
                </button>
              </div>
            )}

            {babysittingLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>⏳ Loading protection status...</div>
            )}

            {babysittingData && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Top Row - Key Metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  {/* House Edge */}
                  <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: `2px solid ${parseFloat(babysittingData.houseEdge) >= 20 ? '#00d26a' : parseFloat(babysittingData.houseEdge) >= 10 ? '#f0b90b' : '#ff3b69'}` }}>
                    <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>📊 House Edge</div>
                    <div style={{ color: parseFloat(babysittingData.houseEdge) >= 20 ? '#00d26a' : parseFloat(babysittingData.houseEdge) >= 10 ? '#f0b90b' : '#ff3b69', fontSize: '24px', fontWeight: 'bold' }}>
                      {babysittingData.houseEdge}%
                    </div>
                    <div style={{ color: '#666', fontSize: '9px' }}>Target: {babysittingData.targetHouseEdge}%</div>
                  </div>

                  {/* Circuit Breaker */}
                  <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: `2px solid ${babysittingData.circuitBreaker?.status === 'healthy' ? '#00d26a' : babysittingData.circuitBreaker?.status === 'warning' ? '#f0b90b' : '#ff3b69'}` }}>
                    <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>🚨 Circuit Breaker</div>
                    <div style={{ color: babysittingData.circuitBreaker?.status === 'healthy' ? '#00d26a' : babysittingData.circuitBreaker?.status === 'warning' ? '#f0b90b' : '#ff3b69', fontSize: '18px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      {babysittingData.circuitBreaker?.status || 'unknown'}
                    </div>
                    <div style={{ color: '#666', fontSize: '9px', marginBottom: '6px' }}>Withdrawals: {babysittingData.circuitBreaker?.blockWithdrawals ? 'Protected' : 'Open'}</div>
                    {babysittingData.circuitBreaker?.status !== 'healthy' && (
                      <button
                        onClick={async () => {
                          if (!confirm('Reset circuit breaker to HEALTHY?\n\nThis will re-enable withdrawals.')) return;
                          try {
                            await fetch(`${API_URL}/api/admin/babysitting/reset-circuit-breaker`, {
                              method: 'POST',
                              headers: { 'X-Session-Id': sessionId }
                            });
                            showToast('success', '✅ Circuit breaker reset!');
                            // Refresh data
                            const res = await fetch(`${API_URL}/api/admin/babysitting`, { headers: { 'X-Session-Id': sessionId } });
                            setBabysittingData(await res.json());
                          } catch (e) {
                            showToast('error', '❌ Reset failed');
                          }
                        }}
                        style={{ padding: '4px 8px', backgroundColor: '#00d26a', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '9px', fontWeight: 'bold' }}
                      >
                        🔄 Reset
                      </button>
                    )}
                  </div>

                  {/* Anti-Exploit */}
                  <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: '2px solid #9b59b6' }}>
                    <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>🛡️ Anti-Exploit</div>
                    <div style={{ color: '#9b59b6', fontSize: '24px', fontWeight: 'bold' }}>
                      {babysittingData.antiExploitOn}/{babysittingData.antiExploitTotal}
                    </div>
                    <div style={{ color: '#666', fontSize: '9px' }}>Protections Active</div>
                  </div>

                  {/* Discord */}
                  <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', padding: '16px', border: `2px solid ${babysittingData.discordEnabled ? '#00d26a' : '#ff3b69'}` }}>
                    <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>📢 Discord Alerts</div>
                    <div style={{ color: babysittingData.discordEnabled ? '#00d26a' : '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>
                      {babysittingData.discordEnabled ? '✅ ACTIVE' : '❌ OFF'}
                    </div>
                    <div style={{ color: '#666', fontSize: '9px' }}>Webhook connected</div>
                  </div>
                </div>

                {/* Bleed System Status */}
                <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '2px solid #ff3b69', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(90deg, rgba(255,59,105,0.2) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#ff3b69', fontSize: '13px', fontWeight: 'bold' }}>🩸 Auto-Bleed System</span>
                    <span style={{ color: babysittingData.bleedConfig?.enabled ? '#00d26a' : '#ff3b69', fontSize: '11px', fontWeight: 'bold' }}>
                      {babysittingData.bleedConfig?.enabled ? '✅ ENABLED' : '❌ DISABLED'}
                    </span>
                  </div>
                  <div style={{ padding: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#666', fontSize: '10px' }}>Threshold</div>
                        <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>{formatPB(babysittingData.bleedConfig?.profitThreshold || 0)}</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#666', fontSize: '10px' }}>Variance</div>
                        <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>±{((babysittingData.bleedConfig?.thresholdVariance || 0) * 100).toFixed(0)}%</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#666', fontSize: '10px' }}>Duration</div>
                        <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>{((babysittingData.bleedConfig?.bleedDurationMs || 0) / 60000).toFixed(0)}m</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#666', fontSize: '10px' }}>Check Unrealized</div>
                        <div style={{ color: babysittingData.bleedConfig?.checkUnrealized ? '#00d26a' : '#888', fontSize: '16px', fontWeight: 'bold' }}>
                          {babysittingData.bleedConfig?.checkUnrealized ? '✅' : '❌'}
                        </div>
                      </div>
                    </div>

                    {/* Currently Bleeding Tokens */}
                    {babysittingData.bleedingTokens?.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ color: '#ff3b69', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>🩸 Currently Bleeding ({babysittingData.bleedingTokens.length})</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {babysittingData.bleedingTokens.map(t => (
                            <div key={t.id} style={{ backgroundColor: '#1a0a0a', padding: '8px 12px', borderRadius: '8px', border: '1px solid #ff3b6944' }}>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>${t.ticker}</div>
                              <div style={{ color: '#ff3b69', fontSize: '10px' }}>{t.bleedProgress.toFixed(0)}% bled</div>
                              <div style={{ color: '#888', fontSize: '9px' }}>{t.bleedStyle}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bleed History */}
                    {babysittingData.bleedHistory?.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>📜 Recent Bleed History</div>
                        <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          {babysittingData.bleedHistory.slice().reverse().slice(0, 10).map((h, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', backgroundColor: i % 2 === 0 ? '#111' : 'transparent', borderRadius: '4px', fontSize: '10px' }}>
                              <span style={{ color: '#fff' }}>${h.ticker}</span>
                              <span style={{ color: '#888' }}>{formatPB(h.pnlAtBleed)} PnL</span>
                              <span style={{ color: h.status === 'rugged' ? '#ff3b69' : '#f0b90b' }}>{h.status}</span>
                              <span style={{ color: '#666' }}>{new Date(h.bledAt).toLocaleTimeString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Winner Hunter Status */}
                <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '2px solid #f0b90b', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(90deg, rgba(240,185,11,0.2) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#f0b90b', fontSize: '13px', fontWeight: 'bold' }}>🎯 Winner Hunter</span>
                    <span style={{ color: babysittingData.winnerHunter?.enabled ? '#00d26a' : '#ff3b69', fontSize: '11px', fontWeight: 'bold' }}>
                      {babysittingData.winnerHunter?.enabled ? '✅ HUNTING' : '❌ DISABLED'}
                    </span>
                  </div>
                  <div style={{ padding: '16px', display: 'flex', gap: '24px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '10px' }}>Aggression</div>
                      <div style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold' }}>{((babysittingData.winnerHunter?.aggression || 0) * 100).toFixed(0)}%</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '10px' }}>Targets</div>
                      <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>{babysittingData.winnerHunter?.targetCount || 0}</div>
                    </div>
                  </div>
                </div>

                {/* 🎯 OPTIMAL TUNING - Auto-configure house systems */}
                <div style={{ backgroundColor: '#0a0a0f', borderRadius: '10px', border: '2px solid #f0b90b', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(90deg, rgba(240,185,11,0.3) 0%, transparent 100%)', padding: '12px', borderBottom: '1px solid #1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#f0b90b', fontSize: '13px', fontWeight: 'bold' }}>🎯 Optimal Tuning</span>
                    <span style={{ color: '#888', fontSize: '11px' }}>Validated</span>
                  </div>
                  <div style={{ padding: '16px' }}>
                    <div style={{ color: '#888', fontSize: '11px', marginBottom: '12px', lineHeight: '1.5' }}>
                      One-click configuration of all house protection systems with <strong style={{ color: '#f0b90b' }}>optimal settings</strong>.
                      Configures 4 systems.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px', backgroundColor: '#050508', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#666', fontSize: '9px' }}>Slippage</div>
                        <div style={{ color: '#9b59b6', fontSize: '14px', fontWeight: 'bold' }}>1.5%</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#666', fontSize: '9px' }}>Rug Rate</div>
                        <div style={{ color: '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>0.85x</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#666', fontSize: '9px' }}>Bleed</div>
                        <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>3M</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#666', fontSize: '9px' }}>Hunter</div>
                        <div style={{ color: '#f0b90b', fontSize: '14px', fontWeight: 'bold' }}>35%</div>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        if (!confirm('🎯 APPLY OPTIMAL TUNING?\n\nThis will configure:\n• Slippage: 1.5% base\n• Rug rate: 0.85x\n• Bleed threshold: 3M PB\n• Hunter aggression: 35%\n• Moonshots: 15%\n\nAll changes saved to Redis.')) return;
                        try {
                          const res = await fetch(`${API_URL}/api/admin/babysitting/tune-house-edge`, {
                            method: 'POST',
                            headers: { 'X-Session-Id': sessionId }
                          });
                          const data = await res.json();
                          if (data.success) {
                            showToast('success', '🎯 Optimal tuning applied! 4 systems configured.');
                            // Refresh babysitting data
                            const refreshRes = await fetch(`${API_URL}/api/admin/babysitting`, { headers: { 'X-Session-Id': sessionId } });
                            setBabysittingData(await refreshRes.json());
                          } else {
                            showToast('error', '❌ Tune failed: ' + (data.error || 'Unknown'));
                          }
                        } catch (e) {
                          showToast('error', '❌ Tune failed: ' + e.message);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '14px 20px',
                        background: 'linear-gradient(135deg, #f0b90b 0%, #d4a00a 100%)',
                        color: '#000',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'transform 0.1s, box-shadow 0.1s',
                      }}
                      onMouseOver={(e) => { e.target.style.transform = 'scale(1.02)'; e.target.style.boxShadow = '0 4px 20px rgba(240,185,11,0.4)'; }}
                      onMouseOut={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = 'none'; }}
                    >
                      🎯 APPLY OPTIMAL TUNING
                    </button>
                  </div>
                </div>

                {/* Info Box */}
                <div style={{ backgroundColor: 'rgba(240,185,11,0.1)', borderRadius: '10px', padding: '16px', border: '1px solid #f0b90b33' }}>
                  <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>👶 How Baby Sitting Works</div>
                  <div style={{ color: '#888', fontSize: '11px', lineHeight: '1.6' }}>
                    <p style={{ margin: '0 0 8px 0' }}>• <strong>Auto-Bleed:</strong> Automatically triggers when player profits exceed threshold (checks both realized & unrealized gains)</p>
                    <p style={{ margin: '0 0 8px 0' }}>• <strong>Circuit Breaker:</strong> Pauses withdrawals if house gets too close to negative</p>
                    <p style={{ margin: '0 0 8px 0' }}>• <strong>Discord Alerts:</strong> Sends notifications when bleeds trigger, circuit breaker activates, or house edge drops</p>
                    <p style={{ margin: '0' }}>• <strong>Winner Hunter:</strong> Automatically counter-trades against profitable players</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Token Confirmation Modal */}
        {deleteConfirmToken && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '20px' }}>
            <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #ff3b69', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '95%' }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</div>
                <h2 style={{ color: '#ff3b69', margin: 0, fontSize: '18px' }}>Delete ${deleteConfirmToken.ticker}?</h2>
                <p style={{ color: '#888', fontSize: '12px', marginTop: '8px' }}>This action cannot be undone.</p>
              </div>
              
              <div style={{ backgroundColor: '#1a0a0a', borderRadius: '10px', padding: '12px', marginBottom: '20px' }}>
                <div style={{ color: '#ff3b69', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>What happens:</div>
                <ul style={{ color: '#888', fontSize: '11px', margin: 0, paddingLeft: '16px', lineHeight: '1.6' }}>
                  <li>Token will be removed from trading</li>
                  <li>Stats saved to your creator history</li>
                  <li>You can launch a new token immediately</li>
                  <li>Other holders' positions become worthless</li>
                </ul>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setDeleteConfirmToken(null)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#1a1a2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#888',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setDeletingToken(true);
                    try {
                      const res = await fetch(`${API_URL}/api/token/delete`, {
                        method: 'POST',
                        headers: { 
                          'Content-Type': 'application/json',
                          'X-Session-Id': sessionId 
                        },
                        body: JSON.stringify({ tokenId: deleteConfirmToken.id || deleteConfirmToken.tokenId }),
                      });
                      const data = await res.json();
                      if (res.ok) {
                        // Remove from local state
                        setMyLaunchedTokens(prev => prev.filter(t => (t.id || t.tokenId) !== (deleteConfirmToken.id || deleteConfirmToken.tokenId)));
                        setUserLaunchedTokens(prev => prev.filter(t => (t.id || t.tokenId) !== (deleteConfirmToken.id || deleteConfirmToken.tokenId)));
                        setDeleteConfirmToken(null);
                        Sounds.sell(); // Sad sound
                        alert(`💀 $${deleteConfirmToken.ticker} has been deleted.\n\nStats saved to your history. You can now launch a new token!`);
                      } else {
                        alert(data.error || 'Failed to delete token');
                      }
                    } catch (e) {
                      console.error('Delete error:', e);
                      alert('Failed to delete token: ' + e.message);
                    }
                    setDeletingToken(false);
                  }}
                  disabled={deletingToken}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: deletingToken ? '#333' : '#ff3b69',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    cursor: deletingToken ? 'wait' : 'pointer',
                  }}
                >
                  {deletingToken ? 'Deleting...' : '🗑️ Delete Forever'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Token History Modal */}
        {showTokenHistory && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '20px', overflowY: 'auto' }}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #e91e63', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '95%', maxHeight: '85vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ color: '#e91e63', margin: 0, fontSize: '18px' }}>📊 Your Token History</h2>
                <button onClick={() => setShowTokenHistory(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Stats Summary */}
              {tokenHistory.stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#888', fontSize: '10px' }}>Tokens Launched</div>
                    <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>{tokenHistory.stats.totalLaunched || 0}</div>
                  </div>
                  <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#888', fontSize: '10px' }}>Avg Peak Multiple</div>
                    <div style={{ color: '#00d26a', fontSize: '20px', fontWeight: 'bold' }}>{(tokenHistory.stats.avgPeakMultiple || 1).toFixed(2)}x</div>
                  </div>
                  <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#888', fontSize: '10px' }}>Dump Rate</div>
                    <div style={{ color: (tokenHistory.stats.dumpRate || 0) > 0.5 ? '#ff3b69' : '#00d26a', fontSize: '20px', fontWeight: 'bold' }}>{((tokenHistory.stats.dumpRate || 0) * 100).toFixed(0)}%</div>
                  </div>
                  <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#888', fontSize: '10px' }}>Avg Duration</div>
                    <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>{Math.floor((tokenHistory.stats.avgDuration || 0) / 60000)}m</div>
                  </div>
                </div>
              )}
              
              {/* Token List */}
              {tokenHistory.history && tokenHistory.history.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {tokenHistory.history.slice().reverse().map((token, i) => (
                    <div key={i} style={{ backgroundColor: '#111', borderRadius: '10px', padding: '12px', border: `1px solid ${token.creatorDumped ? '#ff3b6933' : '#00d26a33'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div>
                          <span style={{ color: '#e91e63', fontWeight: 'bold' }}>${token.ticker}</span>
                          {token.creatorDumped && <span style={{ color: '#ff3b69', fontSize: '10px', marginLeft: '8px' }}>⚠️ DUMPED</span>}
                        </div>
                        <div style={{ color: '#666', fontSize: '10px' }}>{new Date(token.deletedAt).toLocaleDateString()}</div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '10px' }}>
                        <div>
                          <div style={{ color: '#666' }}>Peak</div>
                          <div style={{ color: '#00d26a', fontWeight: 'bold' }}>{(token.peakMultiple || 1).toFixed(2)}x</div>
                        </div>
                        <div>
                          <div style={{ color: '#666' }}>Volume</div>
                          <div style={{ color: '#fff' }}>{formatPB(token.totalVolume || 0)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#666' }}>Duration</div>
                          <div style={{ color: '#fff' }}>{Math.floor((token.duration || 0) / 60000)}m</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
                  <div style={{ fontSize: '14px' }}>No token history yet</div>
                  <div style={{ fontSize: '11px', marginTop: '4px' }}>Launch and delete tokens to build your reputation</div>
                </div>
              )}
              
              {/* Reputation Info */}
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#1a0d15', borderRadius: '8px', border: '1px solid #e91e6333' }}>
                <div style={{ color: '#e91e63', fontSize: '11px', fontWeight: 'bold', marginBottom: '6px' }}>💡 Creator Reputation</div>
                <div style={{ color: '#888', fontSize: '10px', lineHeight: '1.5' }}>
                  Other players can see your history! High dump rates = fewer traders. Build trust by holding through pumps.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rewards Modal */}
        {showRewards && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #f0b90b', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '90%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b' }}>🎁 Premium Rewards</h2>
                <button onClick={() => setShowRewards(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              <div style={{ display: 'grid', gap: '12px' }}>
                {/* Daily Claim Success Message */}
                {showClaimSuccess && dailyClaimResult && (
                  <div style={{ 
                    backgroundColor: '#0a1f0a', 
                    border: '2px solid #00d26a', 
                    padding: '16px', 
                    borderRadius: '12px',
                    animation: 'popIn 0.3s ease-out',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    <button
                      onClick={() => setShowClaimSuccess(false)}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'none',
                        border: 'none',
                        color: '#666',
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        lineHeight: 1
                      }}
                    >
                      ✕
                    </button>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>🎉</div>
                    <div style={{ color: '#00d26a', fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>
                      +{formatPB(dailyClaimResult.reward)}!
                    </div>
                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '12px' }}>
                      Added to your game wallet
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', fontSize: '11px' }}>
                      <div>
                        <div style={{ color: '#555' }}>Base</div>
                        <div style={{ color: '#fff' }}>{formatPB(dailyClaimResult.baseReward)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#555' }}>Streak</div>
                        <div style={{ color: '#f0b90b' }}>+{formatPB(dailyClaimResult.streakBonus)}</div>
                      </div>
                      {dailyClaimResult.premiumMultiplier > 1 && (
                        <div>
                          <div style={{ color: '#555' }}>Premium</div>
                          <div style={{ color: '#00d26a' }}>×{dailyClaimResult.premiumMultiplier}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: '12px', color: '#f0b90b', fontSize: '12px' }}>
                      🔥 {dailyClaimResult.streakDays} day streak!
                    </div>
                  </div>
                )}
                
                {/* Daily Rewards - Enhanced */}
                <div style={{ backgroundColor: '#111', padding: '16px', borderRadius: '12px', border: canClaimDaily() ? '2px solid #00d26a' : '1px solid #1a1a2e' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>🎁 DAILY REWARD</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{ color: '#fff', fontSize: '24px', fontWeight: 'bold' }}>
                          {formatPB(getPotentialDailyReward().total)}
                        </span>
                      </div>
                    </div>
                    {canClaimDaily() && (
                      <div style={{ 
                        backgroundColor: '#00d26a', 
                        color: '#000', 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '10px', 
                        fontWeight: 'bold',
                        animation: 'pulse 2s infinite'
                      }}>
                        READY!
                      </div>
                    )}
                  </div>
                  
                  {/* Reward Breakdown */}
                  <div style={{ backgroundColor: '#0a0a0f', borderRadius: '8px', padding: '10px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px' }}>
                      <span style={{ color: '#666' }}>Base reward</span>
                      <span style={{ color: '#fff' }}>{formatPB(getPotentialDailyReward().base)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px' }}>
                      <span style={{ color: '#666' }}>Streak bonus ({totalDailyClaims} days)</span>
                      <span style={{ color: '#f0b90b' }}>+{formatPB(getPotentialDailyReward().streakBonus)}</span>
                    </div>
                    {isPremium && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', paddingTop: '6px', borderTop: '1px solid #1a1a2e' }}>
                        <span style={{ color: '#00d26a' }}>👑 Premium 2× bonus</span>
                        <span style={{ color: '#00d26a' }}>×2</span>
                      </div>
                    )}
                    {!isPremium && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', paddingTop: '6px', borderTop: '1px solid #1a1a2e' }}>
                        <span style={{ color: '#555' }}>🔒 Premium 2× bonus</span>
                        <span style={{ color: '#555' }}>×1</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Streak Progress */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '4px' }}>
                      <span style={{ color: '#666' }}>🔥 Streak Progress (bonus multiplier)</span>
                      <span style={{ color: totalDailyClaims >= 10 ? '#00d26a' : '#f0b90b' }}>
                        {totalDailyClaims >= 10 ? '✓ MAX BONUS!' : `${totalDailyClaims}/10 days`}
                      </span>
                    </div>
                    <div style={{ backgroundColor: '#1a1a2e', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                      <div style={{ 
                        backgroundColor: totalDailyClaims >= 10 ? '#00d26a' : '#f0b90b', 
                        width: `${Math.min(totalDailyClaims * 10, 100)}%`, 
                        height: '100%',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                    <div style={{ color: '#555', fontSize: '9px', marginTop: '4px', textAlign: 'center' }}>
                      This bar shows your streak bonus level, not claim availability
                    </div>
                  </div>
                  
                  {/* Status Messages */}
                  {getClaimStatus() === 'needsPremium' && (
                    <div style={{ color: '#ff6b6b', fontSize: '11px', marginBottom: '12px', textAlign: 'center' }}>
                      🔒 Buy premium to unlock daily rewards (7 days of claims per purchase)
                    </div>
                  )}
                  {getClaimStatus() === 'alreadyClaimed' && (
                    <div style={{ color: '#f0b90b', fontSize: '11px', marginBottom: '12px', textAlign: 'center' }}>
                      ⏰ Next claim available in: <strong>{getNextClaimTime()}</strong>
                    </div>
                  )}
                  {hasRecentPremium() && canClaimDaily() && (
                    <div style={{ color: '#00d26a', fontSize: '10px', marginBottom: '12px', textAlign: 'center' }}>
                      ✓ {Math.ceil((premiumExpiresAt - Date.now()) / (24*60*60*1000))} days of claims remaining
                    </div>
                  )}
                  
                  {/* Claim Button */}
                  <button 
                    onClick={claimDailyReward}
                    disabled={!canClaimDaily() || dailyClaimPending}
                    style={{ 
                      width: '100%',
                      padding: '14px', 
                      borderRadius: '8px', 
                      border: 'none', 
                      backgroundColor: canClaimDaily() && !dailyClaimPending ? '#00d26a' : '#333', 
                      color: canClaimDaily() && !dailyClaimPending ? '#000' : '#666', 
                      fontWeight: 'bold', 
                      cursor: canClaimDaily() && !dailyClaimPending ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {dailyClaimPending ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ 
                          width: '14px', 
                          height: '14px', 
                          border: '2px solid #666', 
                          borderTopColor: '#000', 
                          borderRadius: '50%', 
                          animation: 'spin 1s linear infinite' 
                        }}></span>
                        Claiming...
                      </span>
                    ) : getClaimStatus() === 'needsPremium' ? (
                      '🔒 Premium Required'
                    ) : canClaimDaily() ? (
                      <>🎁 Claim {formatPB(getPotentialDailyReward().total)}</>
                    ) : (
                      '✓ Claimed Today'
                    )}
                  </button>
                </div>
                
                <div style={{ backgroundColor: '#111', padding: '14px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#888', fontSize: '11px' }}>💰 WEEKLY RAKEBACK (5%)</div>
                    <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>$0.00</div>
                    <div style={{ color: '#555', fontSize: '10px' }}>Play more to earn rakeback!</div>
                  </div>
                  <button disabled style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#333', color: '#666', cursor: 'not-allowed' }}>Claim</button>
                </div>
                
                <div style={{ backgroundColor: '#111', padding: '14px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold' }}>💎 YOUR BENEFITS</div>
                    {isPremium && premiumTimeLeft && (
                      <div style={{ 
                        backgroundColor: '#1a1a0a', 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '10px', 
                        color: '#f0b90b',
                        border: '1px solid #f0b90b33'
                      }}>
                        ⏱️ {premiumTimeLeft} left
                      </div>
                    )}
                  </div>
                  {isPremium ? (
                    <div style={{ display: 'grid', gap: '6px', fontSize: '11px', color: '#888' }}>
                      <div>✅ +10% profit boost on all wins</div>
                      <div>✅ 88% accurate alpha hints</div>
                      <div>✅ Moonshot access (85% survival)</div>
                      <div>✅ 1% volume cashback</div>
                      <div>✅ Leaderboard prize eligibility</div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'grid', gap: '6px', fontSize: '11px', color: '#555', marginBottom: '12px' }}>
                        <div>❌ +10% profit boost on all wins</div>
                        <div>❌ 88% accurate alpha hints</div>
                        <div>❌ Moonshot access (85% survival)</div>
                        <div>❌ 1% volume cashback</div>
                        <div>❌ Leaderboard prize eligibility</div>
                      </div>
                      <button 
                        onClick={() => { setShowRewards(false); setShowDeposit(true); }}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          borderRadius: '6px', 
                          border: 'none', 
                          backgroundColor: '#f0b90b', 
                          color: '#000', 
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        🔓 Unlock 24hr Premium Pass
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Modal */}
        {showLeaderboard && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b', fontWeight: '700' }}>🏆 Leaderboard</h2>
                <button onClick={() => setShowLeaderboard(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* BSV Verification Badge */}
              <div style={{ 
                backgroundColor: '#0a1510', 
                border: '1px solid #00d26a33', 
                borderRadius: '8px', 
                padding: '10px', 
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ fontSize: '20px' }}>⛓️</span>
                <div>
                  <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>Real Players Only</div>
                  <div style={{ color: '#666', fontSize: '10px' }}>Only HandCash users recorded on-chain • No bots!</div>
                </div>
              </div>
              
              {/* YOUR STATS - Always show first if logged in */}
              {handcashUser && (
                <div style={{ 
                  backgroundColor: '#0a1a0a', 
                  border: '2px solid #00d26a', 
                  borderRadius: '10px', 
                  padding: '14px', 
                  marginBottom: '16px',
                  boxShadow: '0 0 20px rgba(0,210,106,0.2)'
                }}>
                  <div style={{ color: '#00d26a', fontSize: '10px', fontWeight: 'bold', marginBottom: '8px' }}>⭐ YOUR STATS</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {handcashUser.avatarUrl && (
                      <img src={handcashUser.avatarUrl} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%', border: '2px solid #00d26a' }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>
                        {handcashUser.handle} {isPremium && '👑'}
                      </div>
                      <div style={{ color: '#666', fontSize: '11px' }}>Real HandCash Player</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: stats.totalProfit >= 0 ? '#00d26a' : '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>
                        {stats.totalProfit >= 0 ? '+' : ''}{formatPB(stats.totalProfit)}
                      </div>
                      <div style={{ color: '#666', fontSize: '10px' }}>Lifetime P/L</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '12px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{stats.totalTrades}</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Trades</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>{stats.wins}</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Wins</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>{stats.rugs}</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Rugs</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#f0b90b', fontSize: '14px', fontWeight: 'bold' }}>{stats.highestMultiplier.toFixed(1)}x</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Best</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Leaderboard Tabs */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {[
                  { id: 'today', label: '🔴 Today', color: '#00d26a' },
                  { id: 'topWinners', label: '💰 Top Winners', color: '#f0b90b' },
                  { id: 'unruggable', label: '🛡️ Unruggable', color: '#00d26a' },
                  { id: 'bestWinRate', label: '🎯 Win Rate', color: '#3498db' },
                  { id: 'hallOfShame', label: '💀 Shame', color: '#ff3b69' },
                  { id: 'mostRugged', label: '🤡 Rugged', color: '#9b59b6' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setLeaderboardTab(tab.id)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: leaderboardTab === tab.id ? `1px solid ${tab.color}` : '1px solid #333',
                      backgroundColor: leaderboardTab === tab.id ? `${tab.color}15` : 'transparent',
                      color: leaderboardTab === tab.id ? tab.color : '#666',
                      fontSize: '10px',
                      fontWeight: leaderboardTab === tab.id ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              
              {/* Today's Live Rankings */}
              {leaderboardTab === 'today' && (
                <>
                  {liveLeaderboard.length > 0 ? (
                    <>
                      <div style={{ color: '#00d26a', fontWeight: 'bold', marginBottom: '8px', fontSize: '11px' }}>
                        🔴 LIVE TODAY'S RANKINGS
                      </div>
                      {liveLeaderboard.slice(0, 10).map((player, index) => {
                        const winRate = player.trades > 0 ? Math.round((player.wins / player.trades) * 100) : 0;
                        return (
                          <div key={player.handle} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            padding: '10px', 
                            backgroundColor: player.handle === handcashUser?.handle?.replace('$', '') ? '#1a2a1a' : '#111', 
                            border: player.handle === handcashUser?.handle?.replace('$', '') ? '1px solid #00d26a' : '1px solid transparent',
                            borderRadius: '6px', 
                            marginBottom: '6px'
                          }}>
                            <span style={{ 
                              color: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#666', 
                              fontWeight: 'bold', 
                              width: '24px' 
                            }}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold' }}>
                                ${player.handle} {player.isPremium && '👑'}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', fontSize: '9px', marginTop: '2px' }}>
                                <span style={{ color: '#888' }}>{player.trades} trades</span>
                                <span style={{ color: winRate >= 50 ? '#00d26a' : '#ff3b69' }}>{winRate}% WR</span>
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ color: player.profit >= 0 ? '#00d26a' : '#ff3b69', fontSize: '13px', fontWeight: 'bold' }}>
                                {player.profit >= 0 ? '+' : ''}{formatPB(player.profit)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>🏆</div>
                      <div style={{ fontSize: '12px' }}>Be today's first champion!</div>
                    </div>
                  )}
                </>
              )}
              
              {/* 🛡️ Unruggable Tab - Survival Rate Leaderboard */}
              {leaderboardTab === 'unruggable' && (
                <>
                  {/* Your Survival Profile */}
                  <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #0f2f0f', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '12px' }}>📊 Your Survival Profile</div>
                      <div style={{ 
                        padding: '4px 10px', 
                        borderRadius: '12px', 
                        backgroundColor: stats.survivalRate >= 80 ? '#00d26a22' : stats.survivalRate >= 50 ? '#f0b90b22' : '#ff3b6922',
                        color: stats.survivalRate >= 80 ? '#00d26a' : stats.survivalRate >= 50 ? '#f0b90b' : '#ff3b69',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {stats.survivalRate.toFixed(1)}%
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>{stats.totalTrades}</div>
                        <div style={{ color: '#555', fontSize: '8px' }}>TRADES</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>{stats.wins}</div>
                        <div style={{ color: '#555', fontSize: '8px' }}>WINS</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#ff3b69', fontSize: '16px', fontWeight: 'bold' }}>{stats.rugs}</div>
                        <div style={{ color: '#555', fontSize: '8px' }}>RUGGED</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#f0b90b', fontSize: '16px', fontWeight: 'bold' }}>{stats.rugsAvoided}</div>
                        <div style={{ color: '#555', fontSize: '8px' }}>AVOIDED</div>
                      </div>
                    </div>
                    
                    {/* Weakness Analysis */}
                    {stats.rugs > 0 && (
                      <div style={{ backgroundColor: '#1a0d0d', border: '1px solid #2f0f0f', borderRadius: '8px', padding: '10px' }}>
                        <div style={{ color: '#ff3b69', fontWeight: 'bold', fontSize: '10px', marginBottom: '6px' }}>⚠️ YOUR WEAKNESS</div>
                        {(() => {
                          const weakness = getWeakness();
                          if (!weakness || !weakness.type) return <div style={{ color: '#666', fontSize: '10px' }}>Not enough data yet</div>;
                          const rugInfo = RUG_ENCYCLOPEDIA[weakness.type];
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '20px' }}>{rugInfo?.icon || '💀'}</span>
                              <div>
                                <div style={{ color: '#ff6b6b', fontSize: '11px', fontWeight: 'bold' }}>
                                  {weakness.type} - {weakness.percent.toFixed(0)}% of your rugs
                                </div>
                                <div style={{ color: '#666', fontSize: '9px' }}>
                                  💡 {rugInfo?.howToProtect?.[0]?.substring(0, 50) || 'Study this rug type'}...
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  
                  {/* Global Unruggable Leaderboard */}
                  <div style={{ color: '#888', fontSize: '10px', marginBottom: '8px' }}>🌍 GLOBAL SURVIVAL RANKING</div>
                  {unruggableLeaderboard.length > 0 ? (
                    unruggableLeaderboard.map((player, i) => (
                      <div key={player.rank} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        padding: '10px', 
                        backgroundColor: player.handle === handcashUser?.handle?.replace('$', '') ? '#1a2a1a' : '#111', 
                        border: player.handle === handcashUser?.handle?.replace('$', '') ? '1px solid #00d26a' : '1px solid transparent',
                        borderRadius: '6px', 
                        marginBottom: '6px'
                      }}>
                        <span style={{ 
                          color: i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : '#666', 
                          fontWeight: 'bold', 
                          width: '24px' 
                        }}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: player.isPremium ? '#f0b90b' : '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                            {player.isPremium && '👑 '}${player.handle?.replace('$', '')}
                          </div>
                          <div style={{ color: '#555', fontSize: '9px' }}>
                            {player.trades} trades • {player.rugs || 0} rugs
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>{player.survivalRate}%</div>
                          <div style={{ color: '#555', fontSize: '8px' }}>survival</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', color: '#555', padding: '20px', fontSize: '11px' }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>🛡️</div>
                      No players with 10+ trades yet
                    </div>
                  )}
                </>
              )}
              
              {/* All-Time Leaderboards */}
              {leaderboardTab !== 'today' && leaderboardTab !== 'unruggable' && (
                <>
                  {leaderboardLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading leaderboards...</div>
                  ) : !leaderboardData ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <button onClick={fetchLeaderboards} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #f0b90b', backgroundColor: 'transparent', color: '#f0b90b', cursor: 'pointer' }}>
                        Load Leaderboards
                      </button>
                    </div>
                  ) : (
                    <>
                      {(() => {
                        const tabConfig = {
                          topWinners: { data: leaderboardData.topWinners, label: 'Profit', format: v => `${v >= 0 ? '+' : ''}${formatPB(v)}`, color: v => v >= 0 ? '#00d26a' : '#ff3b69' },
                          hallOfShame: { data: leaderboardData.hallOfShame, label: 'Loss', format: v => `${v >= 0 ? '+' : ''}${formatPB(v)}`, color: v => v >= 0 ? '#00d26a' : '#ff3b69' },
                          bestWinRate: { data: leaderboardData.bestWinRate, label: 'Win Rate', format: v => `${v}%`, color: () => '#3498db' },
                          mostRugged: { data: leaderboardData.mostRugged, label: 'Rugs', format: v => `${v} rugs`, color: () => '#9b59b6' },
                        };
                        const config = tabConfig[leaderboardTab];
                        if (!config || !config.data) return <div style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No data available</div>;
                        
                        return config.data.map((player, index) => (
                          <div key={player.handle} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            padding: '10px', 
                            backgroundColor: player.handle === handcashUser?.handle?.replace('$', '') ? '#1a1a2a' : '#111', 
                            borderRadius: '6px', 
                            marginBottom: '6px'
                          }}>
                            <span style={{ 
                              color: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#666', 
                              fontWeight: 'bold', 
                              width: '24px' 
                            }}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                            </span>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold' }}>
                                ${player.handle} {player.isPremium && '👑'}
                              </div>
                            </div>
                            <div style={{ color: config.color(player.value), fontSize: '13px', fontWeight: 'bold' }}>
                              {config.format(player.value)}
                            </div>
                          </div>
                        ));
                      })()}
                    </>
                  )}
                </>
              )}
              
              {/* Hall of Fame Button */}
              <button
                onClick={() => { fetchHallOfFame(); setShowHallOfFame(true); }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#1a1508',
                  border: '1px solid #f0b90b',
                  borderRadius: '8px',
                  color: '#f0b90b',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '12px',
                  fontSize: '12px'
                }}
              >
                🏆 View Hall of Fame (On-Chain Champions)
              </button>
              
              {/* Info about on-chain recording */}
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#111', borderRadius: '8px' }}>
                <div style={{ color: '#888', fontSize: '11px', marginBottom: '8px' }}>⛓️ On-Chain Champion Recording:</div>
                <ul style={{ color: '#666', fontSize: '10px', margin: 0, paddingLeft: '16px' }}>
                  <li>Daily champion recorded to BSV via OP_RETURN</li>
                  <li>Your HandCash handle + profit stored on-chain forever</li>
                  <li>Anyone can verify on WhatsOnChain</li>
                  <li>Bots NEVER recorded - only real HandCash players!</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* 📄 WHITEPAPER MODAL - View docs without leaving lobby */}
        {showWhitepaperModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #f0b90b', borderRadius: '16px', padding: '24px', maxWidth: '600px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b' }}>📄 Paper Hands Docs</h2>
                <button onClick={() => setShowWhitepaperModal(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              <div style={{ color: '#888', lineHeight: '1.6', fontSize: '13px' }}>
                <h3 style={{ color: '#f0b90b', marginTop: 0 }}>🎮 What is Paper Hands?</h3>
                <p>Paper Hands is a crypto trading simulator where you practice spotting rugs before they happen. Trade simulated memecoins with real sats - learn the patterns that 99% of traders miss.</p>
                
                <h3 style={{ color: '#f0b90b' }}>💡 How It Works</h3>
                <ul style={{ paddingLeft: '20px' }}>
                  <li><strong>Deposit sats</strong> from your HandCash wallet</li>
                  <li><strong>Trade tokens</strong> that simulate real memecoin behavior</li>
                  <li><strong>Spot the rugs</strong> - look for warning signs!</li>
                  <li><strong>Take profits</strong> or get rekt trying</li>
                  <li><strong>Withdraw winnings</strong> back to HandCash</li>
                </ul>
                
                <h3 style={{ color: '#ff3b69' }}>💀 Rug Types</h3>
                <ul style={{ paddingLeft: '20px' }}>
                  <li><strong style={{ color: '#ff3b69' }}>HARD RUG:</strong> Dev drains liquidity instantly</li>
                  <li><strong style={{ color: '#ff6b00' }}>SOFT RUG:</strong> Slow dump over time</li>
                  <li><strong style={{ color: '#9b59b6' }}>HONEYPOT:</strong> Can buy but can't sell</li>
                  <li><strong style={{ color: '#e74c3c' }}>TEAM DUMP:</strong> Insiders dump on you</li>
                </ul>
                
                <h3 style={{ color: '#00d26a' }}>👑 Premium Benefits</h3>
                <ul style={{ paddingLeft: '20px' }}>
                  <li>+10% profit bonus on all trades</li>
                  <li>AlphaBot warnings 5x more frequent</li>
                  <li>Access to "Vetted" moonshot tokens (95% safe)</li>
                  <li>Daily 50 sat bonus</li>
                  <li>Leaderboard glow effect</li>
                </ul>
                
                <h3 style={{ color: '#f0b90b' }}>⛓️ On-Chain Verification</h3>
                <p>All deposits, withdrawals, and leaderboard wins are recorded on the BSV blockchain. Verify any transaction on <a href="https://whatsonchain.com" target="_blank" rel="noreferrer" style={{ color: '#00d26a' }}>WhatsOnChain</a>.</p>
                
                <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#1a1508', borderRadius: '8px', border: '1px solid #f0b90b44' }}>
                  <div style={{ color: '#f0b90b', fontWeight: 'bold', marginBottom: '8px' }}>⚠️ Disclaimer</div>
                  <div style={{ color: '#888', fontSize: '11px' }}>This is a game for educational purposes. Real crypto trading involves significant risk. Never invest more than you can afford to lose. Paper Hands is not financial advice.</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 👑 CREATOR DASHBOARD MODAL - Flip through cards style */}
        {showCreatorDashboard && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, overflowY: 'auto', padding: '12px' }} onClick={() => setShowCreatorDashboard(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#0a0a0f', border: '2px solid #ff8c00', borderRadius: '16px', padding: '16px', maxWidth: '400px', width: '100%' }}>
              
              {(() => {
                // Combine myLaunchedTokens with any from userLaunchedTokens that belong to this user
                const myHandle = handcashUser?.handle?.replace('$', '').toLowerCase() || '';
                const myTokensFromGlobal = userLaunchedTokens.filter(t => 
                  (t.creator?.replace('$', '').toLowerCase() === myHandle) ||
                  (t.creatorHandle?.replace('$', '').toLowerCase() === myHandle)
                );
                
                // Merge - prefer myLaunchedTokens but add any missing from global
                const mergedTokens = [...myLaunchedTokens];
                myTokensFromGlobal.forEach(globalToken => {
                  if (!mergedTokens.find(t => (t.id || t.tokenId) === globalToken.id)) {
                    mergedTokens.push(globalToken);
                  }
                });
                
                const allMyTokens = mergedTokens;
                
                if (allMyTokens.length === 0) {
                  return (
                    /* Empty State */
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div style={{ fontSize: '50px', marginBottom: '12px' }}>🚀</div>
                      <div style={{ color: '#fff', fontSize: '16px', marginBottom: '8px' }}>No tokens launched yet</div>
                      <div style={{ color: '#666', fontSize: '12px', marginBottom: '20px' }}>Create your first PvP token!</div>
                      <button
                        onClick={() => { setShowCreatorDashboard(false); setShowLaunchToken(true); }}
                        style={{ padding: '12px 24px', backgroundColor: '#f0b90b', border: 'none', borderRadius: '8px', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}
                      >
                        🚀 Launch Token
                      </button>
                    </div>
                  );
                }
                
                // Get current token to display
                const safeIndex = Math.min(creatorDashboardIndex, allMyTokens.length - 1);
                const token = allMyTokens[safeIndex];
                const liveToken = userLaunchedTokens.find(t => t.id === token?.tokenId || t.id === token?.id) || token;
                const isActive = token?.state === 'active' || liveToken?.state === 'active';
                
                // Get token players/positions
                const tokenPositions = liveToken?.positions ? Object.entries(liveToken.positions).map(([handle, pos]) => ({
                  handle,
                  ...pos,
                  isActive: pos.tokens > 0
                })) : [];
                
                // Calculate stats like classic view
                const creatorHandle = (token?.creator || liveToken?.creator || '').replace('$', '').toLowerCase();
                const otherHolders = tokenPositions.filter(p => {
                  const handle = p.handle?.replace('$', '').toLowerCase();
                  return p.isActive && handle !== creatorHandle;
                });
                
                const totalBagValue = otherHolders.reduce((sum, p) => sum + (p.invested || 0), 0);
                const holderCount = otherHolders.length;
                const biggestHolder = otherHolders.sort((a, b) => (b.invested || 0) - (a.invested || 0))[0];
                const poolShare = liveToken?.liquidityPool || token?.liquidityPool || 0;
                const totalVolume = (liveToken?.totalBuyVolume || 0) + (liveToken?.totalSellVolume || 0);
                const currentPrice = liveToken?.price || liveToken?.currentPrice || token?.price || 0;
                
                // Your position
                const myPosition = tokenPositions.find(p => p.handle?.replace('$', '').toLowerCase() === creatorHandle);
                const myTokens = myPosition?.tokens || 0;
                const myValue = myTokens * currentPrice;
                const profitIfRug = poolShare;
                
                return (
                  <>
                    {/* Header with Navigation */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #ff8c0033' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}>👑</span>
                        <span style={{ color: '#f0b90b', fontSize: '14px', fontWeight: 'bold' }}>CREATOR DASHBOARD</span>
                      </div>
                      <button onClick={() => setShowCreatorDashboard(false)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                    </div>
                    
                    {/* Token Navigation - Only show if multiple tokens */}
                    {allMyTokens.length > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '14px' }}>
                        <button
                          onClick={() => setCreatorDashboardIndex(i => i > 0 ? i - 1 : allMyTokens.length - 1)}
                          style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #ff8c00', backgroundColor: 'transparent', color: '#ff8c00', fontSize: '16px', cursor: 'pointer' }}
                        >←</button>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#ff8c00', fontSize: '18px', fontWeight: 'bold' }}>${token?.ticker}</div>
                          <div style={{ color: '#666', fontSize: '10px' }}>({safeIndex + 1} of {allMyTokens.length})</div>
                        </div>
                        <button
                          onClick={() => setCreatorDashboardIndex(i => i < allMyTokens.length - 1 ? i + 1 : 0)}
                          style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #ff8c00', backgroundColor: 'transparent', color: '#ff8c00', fontSize: '16px', cursor: 'pointer' }}
                        >→</button>
                      </div>
                    )}
                    
                    {/* Single token - just show ticker */}
                    {allMyTokens.length === 1 && (
                      <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                        <div style={{ color: '#ff8c00', fontSize: '20px', fontWeight: 'bold' }}>${token?.ticker}</div>
                        <div style={{ color: isActive ? '#00d26a' : '#ff3b69', fontSize: '11px' }}>{isActive ? '● LIVE' : '● ENDED'}</div>
                      </div>
                    )}
                    
                    {/* Status Badge for multi-token */}
                    {allMyTokens.length > 1 && (
                      <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                        <span style={{ backgroundColor: isActive ? '#00d26a22' : '#ff3b6922', color: isActive ? '#00d26a' : '#ff3b69', padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                          {isActive ? '● LIVE' : '● ENDED'}
                        </span>
                      </div>
                    )}
                    
                    {/* Stats Grid - Same as Classic */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {/* Holders & Total Bags */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #222' }}>
                          <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>👥 HOLDERS</div>
                          <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{holderCount}</div>
                        </div>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #00d26a33' }}>
                          <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>💰 TOTAL BAGS</div>
                          <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{formatPB(totalBagValue)}</div>
                        </div>
                      </div>
                      
                      {/* Biggest Holder */}
                      {biggestHolder && (
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #9b59b633' }}>
                          <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>🐋 BIGGEST HOLDER</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#9b59b6', fontSize: '13px', fontWeight: 'bold' }}>${biggestHolder.handle}</span>
                            <span style={{ color: '#fff', fontSize: '13px' }}>{formatPB(biggestHolder.invested || 0)}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Volume & Pool */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #222' }}>
                          <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>📊 TOTAL VOLUME</div>
                          <div style={{ color: '#00a8ff', fontSize: '14px', fontWeight: 'bold' }}>{formatPB(totalVolume)}</div>
                        </div>
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #222' }}>
                          <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>💧 POOL SIZE</div>
                          <div style={{ color: '#3498db', fontSize: '14px', fontWeight: 'bold' }}>{formatPB(poolShare)}</div>
                        </div>
                      </div>
                      
                      {/* Your Holdings */}
                      <div style={{ backgroundColor: '#0a0d0a', borderRadius: '8px', padding: '10px', border: '1px solid #00d26a44' }}>
                        <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>🎯 YOUR HOLDINGS</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#888', fontSize: '11px' }}>{(myTokens / 1000).toFixed(0)}K tokens</span>
                          <span style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>{formatPB(myValue)}</span>
                        </div>
                      </div>
                      
                      {/* Rug Stats */}
                      {isActive && (
                        <div style={{ backgroundColor: '#140a0a', borderRadius: '8px', padding: '10px', border: '1px solid #ff3b6944' }}>
                          <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>💀 IF YOU RUG NOW</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#ff3b69', fontSize: '10px' }}>Pool drain →</span>
                            <span style={{ color: '#ff3b69', fontSize: '16px', fontWeight: 'bold' }}>{formatPB(profitIfRug)}</span>
                          </div>
                          <div style={{ color: '#ff3b6966', fontSize: '9px', marginTop: '4px' }}>
                            ≈ ${((profitIfRug / 100000000) * (bsvPrice || 20)).toFixed(2)} USD
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                      {isActive && (
                        <>
                          <button
                            onClick={() => { 
                              setShowCreatorDashboard(false); 
                              if (liveToken) {
                                setCurrentToken(liveToken);
                                setView('token');
                              }
                            }}
                            style={{ flex: 1, padding: '10px', backgroundColor: '#f0b90b', border: 'none', borderRadius: '8px', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
                          >
                            📈 View Chart
                          </button>
                          <button
                            onClick={() => { setShowCreatorDashboard(false); setDeleteConfirmToken({ ...token, id: token.tokenId || token.id }); }}
                            style={{ padding: '10px 16px', backgroundColor: '#ff3b6922', border: '1px solid #ff3b69', borderRadius: '8px', color: '#ff3b69', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
                          >
                            💀 Rug
                          </button>
                        </>
                      )}
                      {!isActive && (
                        <button
                          onClick={() => { setShowCreatorDashboard(false); setShowLaunchToken(true); }}
                          style={{ flex: 1, padding: '10px', backgroundColor: '#f0b90b', border: 'none', borderRadius: '8px', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
                        >
                          🚀 Launch New Token
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* 💵 PAPER BUCKS INFO MODAL */}
        {/* 🚀 BSV.PUMP INFO MODAL */}
        {showBsvPumpInfo && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, overflowY: 'auto', padding: '12px' }} onTouchMove={(e) => e.stopPropagation()} onClick={() => setShowBsvPumpInfo(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#0a0a0f', border: '2px solid #f0b90b', borderRadius: '16px', padding: '24px', maxWidth: '440px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '20px' }}>
                  🚀 BSV.pump
                </h2>
                <button onClick={() => setShowBsvPumpInfo(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              <div style={{ color: '#ccc', lineHeight: '1.7', fontSize: '13px' }}>
                <p style={{ marginTop: 0, fontSize: '14px' }}>Launch your own meme token on BSV for just <span style={{ color: '#00d26a', fontWeight: 'bold' }}>10¢</span>! Pure player-vs-player trading.</p>
                
                {/* How It Works */}
                <div style={{ backgroundColor: '#1a1508', border: '1px solid #f0b90b44', borderRadius: '12px', padding: '14px', margin: '14px 0' }}>
                  <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '13px', marginBottom: '10px' }}>⚙️ HOW IT WORKS</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>1.</span>
                      <span>Pay <strong style={{ color: '#00d26a' }}>10¢ BSV</strong> launch fee (goes to house)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>2.</span>
                      <span>Choose your <strong>initial buy</strong> with Paper Bucks</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>3.</span>
                      <span>Your PB becomes the <strong>liquidity pool</strong></span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>4.</span>
                      <span>Other players can buy/sell your token</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>5.</span>
                      <span>Sell your tokens whenever you want!</span>
                    </div>
                  </div>
                </div>
                
                {/* Key Features */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #00d26a44', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>🎮</div>
                    <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '11px' }}>PURE PvP</div>
                    <div style={{ color: '#888', fontSize: '10px' }}>No bots, no fake liquidity</div>
                  </div>
                  <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #00d26a44', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>💰</div>
                    <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '11px' }}>SELF-FUNDED</div>
                    <div style={{ color: '#888', fontSize: '10px' }}>Your PB = the pool</div>
                  </div>
                  <div style={{ backgroundColor: '#111', border: '1px solid #33333366', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>🏠</div>
                    <div style={{ color: '#888', fontWeight: 'bold', fontSize: '11px' }}>1% RAKE</div>
                    <div style={{ color: '#666', fontSize: '10px' }}>On every trade</div>
                  </div>
                  <div style={{ backgroundColor: '#111', border: '1px solid #33333366', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>♾️</div>
                    <div style={{ color: '#888', fontWeight: 'bold', fontSize: '11px' }}>NO EXPIRY</div>
                    <div style={{ color: '#666', fontSize: '10px' }}>Tokens live forever</div>
                  </div>
                </div>
                
                {/* Example Scenario */}
                <div style={{ backgroundColor: '#111', border: '1px solid #333', borderRadius: '10px', padding: '12px', marginBottom: '14px' }}>
                  <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '12px', marginBottom: '8px' }}>📊 EXAMPLE</div>
                  <div style={{ fontSize: '11px', color: '#aaa' }}>
                    <div style={{ marginBottom: '6px' }}>You launch <span style={{ color: '#f0b90b' }}>$MOON</span> with <span style={{ color: '#00d26a' }}>$250 PB</span> initial buy</div>
                    <div style={{ marginBottom: '6px' }}>→ You own tokens, your PB is in the pool</div>
                    <div style={{ marginBottom: '6px' }}>→ Another player buys <span style={{ color: '#00d26a' }}>$500 PB</span> worth</div>
                    <div style={{ marginBottom: '6px' }}>→ Price pumps! 📈</div>
                    <div>→ You sell for <span style={{ color: '#00d26a' }}>$400 PB</span> profit! 🎉</div>
                  </div>
                </div>
                
                {/* Warning */}
                <div style={{ backgroundColor: '#1a0d0d', border: '1px solid #ff3b6944', borderRadius: '8px', padding: '10px', marginBottom: '14px' }}>
                  <div style={{ color: '#ff3b69', fontSize: '11px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <span>⚠️</span>
                    <span>If no one buys your token, you can only sell back for what's in the pool (your own PB minus fees). Only profit when others buy in!</span>
                  </div>
                </div>
                
                {/* Slippage Explanation */}
                <div style={{ backgroundColor: '#0d0d1a', border: '1px solid #4a9eff44', borderRadius: '10px', padding: '12px', marginBottom: '14px' }}>
                  <div style={{ color: '#4a9eff', fontWeight: 'bold', fontSize: '12px', marginBottom: '8px' }}>📉 SLIPPAGE: YOUR FRIEND & ENEMY</div>
                  <div style={{ fontSize: '11px', color: '#aaa', lineHeight: '1.6' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#fff' }}>What is slippage?</strong><br/>
                      Price moves AGAINST you as you trade. Bigger trade = more slippage.
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#fff' }}>Why it happens:</strong><br/>
                      Selling removes liquidity from the pool. Less liquidity = lower price per token.
                    </div>
                    <div style={{ marginBottom: '8px', backgroundColor: '#111', padding: '8px', borderRadius: '6px' }}>
                      <strong style={{ color: '#f0b90b' }}>Example:</strong> Try to sell 50% of the pool in one trade? You might only get ~25% of expected value because price crashes as you sell.
                    </div>
                    <div style={{ color: '#00d26a' }}>
                      <strong>The game:</strong> First to exit wins most. Last holder gets rekt by slippage. Sell in smaller chunks or accept the hit!
                    </div>
                  </div>
                </div>
                
                {/* Cost Summary */}
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#0d1a0d', borderRadius: '8px' }}>
                  <div style={{ color: '#666', fontSize: '10px', marginBottom: '4px' }}>LAUNCH COST</div>
                  <div style={{ color: '#00d26a', fontSize: '22px', fontWeight: 'bold' }}>10¢ BSV</div>
                  <div style={{ color: '#666', fontSize: '10px' }}>+ your initial Paper Bucks buy</div>
                </div>
              </div>
              
              <button 
                onClick={() => setShowBsvPumpInfo(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  marginTop: '16px',
                  backgroundColor: '#f0b90b',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Got It! 🚀
              </button>
            </div>
          </div>
        )}
        
        {showPaperBucksInfo && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #00d26a', borderRadius: '16px', padding: '24px', maxWidth: '420px', width: '95%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#00d26a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  💵 Paper Bucks ($PB)
                </h2>
                <button onClick={() => setShowPaperBucksInfo(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              <div style={{ color: '#ccc', lineHeight: '1.8', fontSize: '14px' }}>
                <p style={{ marginTop: 0 }}>Paper Bucks is our in-game currency for cleaner, easier-to-read numbers!</p>
                
                <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #00d26a44', borderRadius: '12px', padding: '16px', margin: '16px 0' }}>
                  <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>💱 CONVERSION RATE</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff', textAlign: 'center' }}>
                    1,000 sats = $1 PB
                  </div>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>EXAMPLES:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                    <div style={{ backgroundColor: '#111', padding: '8px', borderRadius: '6px' }}>
                      <span style={{ color: '#666' }}>Deposit</span><br/>
                      <span style={{ color: '#fff' }}>100K sats → <span style={{ color: '#00d26a' }}>$100 PB</span></span>
                    </div>
                    <div style={{ backgroundColor: '#111', padding: '8px', borderRadius: '6px' }}>
                      <span style={{ color: '#666' }}>Trade</span><br/>
                      <span style={{ color: '#fff' }}>5K sats → <span style={{ color: '#00d26a' }}>$5 PB</span></span>
                    </div>
                    <div style={{ backgroundColor: '#111', padding: '8px', borderRadius: '6px' }}>
                      <span style={{ color: '#666' }}>Market Cap</span><br/>
                      <span style={{ color: '#fff' }}>1.2M sats → <span style={{ color: '#f0b90b' }}>$1.2K PB</span></span>
                    </div>
                    <div style={{ backgroundColor: '#111', padding: '8px', borderRadius: '6px' }}>
                      <span style={{ color: '#666' }}>Withdraw</span><br/>
                      <span style={{ color: '#fff' }}><span style={{ color: '#00d26a' }}>$50 PB</span> → 50K sats</span>
                    </div>
                  </div>
                </div>
                
                <div style={{ backgroundColor: '#0f1a1f', border: '1px solid #38cb7c44', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <img src="https://i.ibb.co/23X7r7j/logo.png" alt="" style={{ width: '16px', height: '16px', borderRadius: '50%' }} />
                    <span style={{ color: '#38cb7c', fontWeight: 'bold', fontSize: '12px' }}>HANDCASH DEPOSITS/WITHDRAWALS</span>
                  </div>
                  <div style={{ color: '#888', fontSize: '11px' }}>
                    Real sats move in and out of your HandCash wallet. Paper Bucks is just how we display amounts in-game for easier reading!
                  </div>
                </div>
                
                <div style={{ color: '#666', fontSize: '11px', textAlign: 'center' }}>
                  Pure PvP trading - compete fairly! 🎮
                </div>
              </div>
              
              <button 
                onClick={() => setShowPaperBucksInfo(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  marginTop: '16px',
                  backgroundColor: '#00d26a',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Got it! 💪
              </button>
            </div>
          </div>
        )}

        {/* 🏆 HALL OF FAME MODAL - On-Chain Champions */}
        {showHallOfFame && hallOfFame && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #f0b90b', borderRadius: '16px', padding: '24px', maxWidth: '600px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b', fontSize: '22px' }}>🏆 Hall of Fame</h2>
                <button onClick={() => setShowHallOfFame(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              <div style={{ color: '#888', fontSize: '11px', marginBottom: '16px', textAlign: 'center' }}>
                ⛓️ Champions recorded forever on BSV blockchain
              </div>
              
              {/* Current Streak */}
              {hallOfFame.currentStreak && (
                <div style={{ 
                  backgroundColor: '#1a0808', 
                  border: '1px solid #ff3b69', 
                  borderRadius: '8px', 
                  padding: '12px', 
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#ff3b69', fontSize: '12px' }}>🔥 CURRENT STREAK</div>
                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>
                    ${hallOfFame.currentStreak.handle} - {hallOfFame.currentStreak.days} days!
                  </div>
                </div>
              )}
              
              {/* Most Titles */}
              {hallOfFame.mostTitles && hallOfFame.mostTitles.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: '#f0b90b', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>👑 MOST TITLES</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {hallOfFame.mostTitles.slice(0, 5).map((p, i) => (
                      <div key={p.handle} style={{ 
                        backgroundColor: '#111', 
                        padding: '8px 12px', 
                        borderRadius: '20px',
                        border: i === 0 ? '1px solid #ffd700' : '1px solid #333'
                      }}>
                        <span style={{ color: i === 0 ? '#ffd700' : '#888' }}>${p.handle}</span>
                        <span style={{ color: '#f0b90b', marginLeft: '6px', fontWeight: 'bold' }}>{p.titles}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Champions List */}
              <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>📜 RECENT CHAMPIONS</div>
              {hallOfFame.champions && hallOfFame.champions.length > 0 ? (
                hallOfFame.champions.slice(0, 15).map((champ, i) => (
                  <div key={champ.date} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    padding: '10px', 
                    backgroundColor: i === 0 ? '#1a1508' : '#111', 
                    border: i === 0 ? '1px solid #f0b90b' : 'none',
                    borderRadius: '6px', 
                    marginBottom: '6px'
                  }}>
                    <span style={{ color: '#666', fontSize: '11px', width: '70px' }}>{champ.date}</span>
                    <span style={{ color: '#f0b90b', flex: 1, fontWeight: 'bold' }}>${champ.champion?.handle}</span>
                    <span style={{ color: '#00d26a', fontSize: '12px' }}>
                      +{formatPB(champ.champion?.profit || 0)}
                    </span>
                    {champ.txUrl && (
                      <a 
                        href={champ.txUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        style={{ 
                          color: '#00d26a', 
                          fontSize: '10px',
                          textDecoration: 'none',
                          padding: '4px 8px',
                          backgroundColor: '#0a1a0a',
                          borderRadius: '4px',
                          border: '1px solid #00d26a44'
                        }}
                      >
                        ⛓️ View TX
                      </a>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
                  No champions recorded yet. Be the first!
                </div>
              )}
              
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#111', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ color: '#666', fontSize: '10px' }}>
                  Champions are recorded automatically at midnight UTC each day.<br/>
                  All records are permanently stored on the BSV blockchain.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 📢 ANNOUNCEMENT TOAST - DISABLED FOR NOW
        {announcementToast && !showSidebar && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            padding: '10px 16px',
            backgroundColor: announcementToast.type === 'whale' ? 'rgba(26, 21, 8, 0.98)' : 
                            announcementToast.type === 'killfeed' ? 'rgba(26, 8, 8, 0.98)' :
                            announcementToast.type === 'champion' ? 'rgba(8, 26, 8, 0.98)' : 
                            announcementToast.type === 'tip' ? 'rgba(8, 26, 8, 0.98)' :
                            announcementToast.type === 'moon' ? 'rgba(26, 21, 8, 0.98)' : 'rgba(17, 17, 17, 0.98)',
            borderBottom: `2px solid ${
              announcementToast.type === 'whale' ? '#f0b90b' : 
              announcementToast.type === 'killfeed' ? '#ff3b69' :
              announcementToast.type === 'champion' ? '#00d26a' : 
              announcementToast.type === 'tip' ? '#00d26a' :
              announcementToast.type === 'moon' ? '#f0b90b' : '#333'
            }`,
            zIndex: 9999,
            animation: 'slideDown 0.3s ease-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}>
            <div style={{ 
              color: announcementToast.type === 'whale' ? '#f0b90b' : 
                     announcementToast.type === 'killfeed' ? '#ff3b69' :
                     announcementToast.type === 'champion' ? '#00d26a' : 
                     announcementToast.type === 'tip' ? '#00d26a' :
                     announcementToast.type === 'moon' ? '#f0b90b' : '#fff',
              fontSize: '12px',
              fontWeight: 'bold',
              textAlign: 'center',
            }}>
              {announcementToast.message}
            </div>
            <button 
              onClick={() => setAnnouncementToast(null)}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#666', 
                fontSize: '16px', 
                cursor: 'pointer',
                padding: '0 4px',
              }}
            >×</button>
          </div>
        )}
        */}
        
        {/* 💸 TIP RECEIVED TOAST - Special notification when you get tipped */}
        {tipReceived && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(8, 35, 8, 0.98)',
            border: '3px solid #00d26a',
            borderRadius: '20px',
            padding: '30px 50px',
            zIndex: 3000,
            animation: 'tipPop 0.5s ease-out',
            boxShadow: '0 0 60px rgba(0,210,106,0.5)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '50px', marginBottom: '10px' }}>💸</div>
            <div style={{ color: '#00d26a', fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
              TIP RECEIVED!
            </div>
            <div style={{ color: '#fff', fontSize: '36px', fontWeight: 'bold', marginBottom: '4px' }}>
              +{formatPB(tipReceived.amount)}
            </div>
            <div style={{ color: '#666', fontSize: '12px', marginBottom: '8px' }}>
              {formatUsd(tipReceived.amount)} real value
            </div>
            <div style={{ color: '#888', fontSize: '14px' }}>
              from <span style={{ color: '#f0b90b' }}>${tipReceived.from}</span>
            </div>
            <style>{`
              @keyframes tipPop {
                0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
                50% { transform: translate(-50%, -50%) scale(1.1); }
                100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
              }
            `}</style>
          </div>
        )}

        {/* 🐛 REMOVED: Purple DM notification popup - Paper Chat handles DMs with unread badges */}

        {/* 📢 Admin Broadcast Toast */}
        {receivedBroadcast && (
          <div
            onClick={() => setReceivedBroadcast(null)}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: receivedBroadcast.msgType === 'error' ? 'rgba(40, 10, 15, 0.98)' :
                               receivedBroadcast.msgType === 'warning' ? 'rgba(40, 30, 10, 0.98)' :
                               receivedBroadcast.msgType === 'success' ? 'rgba(10, 40, 25, 0.98)' :
                               'rgba(20, 20, 35, 0.98)',
              border: `3px solid ${
                receivedBroadcast.msgType === 'error' ? '#ff3b69' :
                receivedBroadcast.msgType === 'warning' ? '#f0b90b' :
                receivedBroadcast.msgType === 'success' ? '#00d26a' :
                '#6366f1'
              }`,
              borderRadius: '16px',
              padding: '24px 32px',
              zIndex: 9999,
              animation: 'broadcastPulse 0.5s ease-out',
              boxShadow: `0 0 60px ${
                receivedBroadcast.msgType === 'error' ? 'rgba(255,59,105,0.5)' :
                receivedBroadcast.msgType === 'warning' ? 'rgba(240,185,11,0.5)' :
                receivedBroadcast.msgType === 'success' ? 'rgba(0,210,106,0.5)' :
                'rgba(99,102,241,0.5)'
              }`,
              cursor: 'pointer',
              maxWidth: '500px',
              textAlign: 'center',
            }}
          >
            <div style={{
              fontSize: '32px',
              marginBottom: '12px',
            }}>
              {receivedBroadcast.msgType === 'error' ? '🚨' :
               receivedBroadcast.msgType === 'warning' ? '⚠️' :
               receivedBroadcast.msgType === 'success' ? '🎉' :
               '📢'}
            </div>
            <div style={{
              color: receivedBroadcast.msgType === 'error' ? '#ff3b69' :
                     receivedBroadcast.msgType === 'warning' ? '#f0b90b' :
                     receivedBroadcast.msgType === 'success' ? '#00d26a' :
                     '#6366f1',
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '2px',
            }}>
              {receivedBroadcast.msgType === 'error' ? 'ALERT' :
               receivedBroadcast.msgType === 'warning' ? 'WARNING' :
               receivedBroadcast.msgType === 'success' ? 'ANNOUNCEMENT' :
               'INFO'}
            </div>
            <div style={{
              color: '#fff',
              fontSize: '18px',
              fontWeight: 'bold',
              lineHeight: '1.5',
              marginBottom: '12px',
            }}>
              {receivedBroadcast.text}
            </div>
            <div style={{ color: '#666', fontSize: '11px' }}>
              From: ${receivedBroadcast.from} • Tap to dismiss
            </div>
            <style>{`
              @keyframes broadcastPulse {
                0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
                50% { transform: translate(-50%, -50%) scale(1.05); }
                100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
              }
            `}</style>
          </div>
        )}

        {/* 💰 System Notifications (Penny Pings, etc) */}
        {systemNotifications.length > 0 && (
          <div 
            onClick={async () => {
              // Mark as read and dismiss
              try {
                await fetch(`${API_URL}/api/notifications/read`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                  body: JSON.stringify({ notificationIds: systemNotifications.map(n => n.id) })
                });
              } catch (e) {
                console.log('Mark notifications read error:', e.message);
              }
              setSystemNotifications([]);
            }}
            style={{
              position: 'fixed',
              top: '80px',
              right: '20px',
              backgroundColor: 'rgba(15, 31, 26, 0.98)',
              border: '2px solid #00d26a',
              borderRadius: '12px',
              padding: '16px 20px',
              zIndex: 3000,
              animation: 'dmSlideIn 0.3s ease-out',
              boxShadow: '0 0 30px rgba(0,210,106,0.4)',
              cursor: 'pointer',
              maxWidth: '320px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '28px' }}>💰</div>
              <div>
                <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                  YOU'VE GOT MONEY!
                </div>
                {systemNotifications.slice(0, 3).map((notif, idx) => (
                  <div key={notif.id || idx} style={{ 
                    color: '#fff', 
                    fontSize: '12px',
                    marginTop: idx > 0 ? '4px' : 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {notif.message}
                  </div>
                ))}
                {systemNotifications.length > 3 && (
                  <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>
                    +{systemNotifications.length - 3} more
                  </div>
                )}
              </div>
            </div>
            <div style={{ color: '#666', fontSize: '10px', marginTop: '8px', textAlign: 'center' }}>
              Tap to dismiss
            </div>
          </div>
        )}

        {/* 💰 Trade Mini Toast Notification */}
        {tradeNotification && (
          <div
            onClick={() => setTradeNotification(null)}
            style={{
              position: 'fixed',
              top: '80px',
              right: '20px',
              backgroundColor: tradeNotification.type === 'buy'
                ? 'rgba(0, 40, 20, 0.95)'
                : 'rgba(40, 10, 20, 0.95)',
              border: `1px solid ${tradeNotification.type === 'buy' ? '#00d26a' : '#ff3b69'}`,
              borderRadius: '8px',
              padding: '10px 14px',
              zIndex: 2999,
              animation: 'tradeSlideIn 0.3s ease-out',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '11px',
              minWidth: '180px',
            }}
          >
            <span style={{ fontSize: '16px' }}>
              {tradeNotification.isBot ? '🤖' : (tradeNotification.type === 'buy' ? '💚' : '💔')}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                <span style={{ color: tradeNotification.type === 'buy' ? '#00d26a' : '#ff3b69', fontWeight: 'bold' }}>
                  {tradeNotification.isBot ? 'BOT ' : ''}{tradeNotification.type === 'buy' ? 'BUY' : 'SELL'}
                </span>
                <span style={{ color: '#fff', fontWeight: 'bold' }}>
                  ${tradeNotification.ticker || '???'}
                </span>
                {tradeNotification.isPvP && (
                  <span style={{ backgroundColor: '#9b59b6', color: '#fff', fontSize: '8px', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>PvP</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888', fontSize: '10px' }}>
                <span>{formatPB(tradeNotification.amount)} PB</span>
                {tradeNotification.rake > 0 && (
                  <span style={{ color: '#f0b90b' }}>• 1% rake: {formatPB(tradeNotification.rake)}</span>
                )}
                {tradeNotification.profit !== undefined && tradeNotification.type === 'sell' && (
                  <span style={{ color: tradeNotification.profit >= 0 ? '#00d26a' : '#ff3b69' }}>
                    • {tradeNotification.profit >= 0 ? '+' : ''}{formatPB(tradeNotification.profit)}
                  </span>
                )}
              </div>
            </div>
            <style>{`
              @keyframes tradeSlideIn {
                0% { opacity: 0; transform: translateX(50px); }
                100% { opacity: 1; transform: translateX(0); }
              }
            `}</style>
          </div>
        )}

        {/* 🪙 Rake Fee Notification Toast */}
        {rakeNotification && (
          <div
            style={{
              position: 'fixed',
              bottom: '100px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(20, 15, 10, 0.98)',
              border: '2px solid #f0b90b',
              borderRadius: '12px',
              padding: '14px 24px',
              zIndex: 9999,
              animation: 'rakeToastIn 0.3s ease-out',
              boxShadow: '0 0 30px rgba(240, 185, 11, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <span style={{ fontSize: '24px' }}>🪙</span>
            <div>
              <div style={{ 
                color: '#f0b90b', 
                fontSize: '15px', 
                fontWeight: 'bold',
                marginBottom: '2px',
              }}>
                -{rakeNotification.usdCents}¢ RAKE
              </div>
              <div style={{ 
                color: '#888', 
                fontSize: '11px',
              }}>
                {(rakeNotification.sats / 1000).toFixed(1)}k sats → house 🏠
              </div>
            </div>
            <style>{`
              @keyframes rakeToastIn {
                0% { opacity: 0; transform: translateX(-50%) scale(0.8) translateY(20px); }
                50% { opacity: 1; transform: translateX(-50%) scale(1.05) translateY(-5px); }
                100% { opacity: 1; transform: translateX(-50%) scale(1) translateY(0); }
              }
            `}</style>
          </div>
        )}

        {/* Premium Comparison Modal - Redesigned */}
        {showPremiumComparison && (
          <div 
            style={{ 
              position: 'fixed', 
              inset: 0, 
              backgroundColor: 'rgba(0,0,0,0.95)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              zIndex: 1100, 
              overflowY: 'auto',
              padding: '20px'
            }} 
            onClick={(e) => { if (e.target === e.currentTarget) setShowPremiumComparison(false); }}
          >
            <div style={{ 
              backgroundColor: '#0d0d12', 
              borderRadius: '24px', 
              padding: '0', 
              maxWidth: '440px', 
              width: '100%', 
              maxHeight: '90vh', 
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              boxShadow: '0 0 80px rgba(240,185,11,0.2), 0 0 0 1px rgba(240,185,11,0.3)',
            }}>
              
              {/* Hero Header with gradient */}
              <div style={{
                background: 'linear-gradient(135deg, #1a1508 0%, #0d0d12 100%)',
                padding: isMobile ? '16px 20px 12px' : '28px 24px 20px',
                textAlign: 'center',
                position: 'relative',
                borderBottom: '1px solid #f0b90b22'
              }}>
                {/* Close button */}
                <button 
                  onClick={() => setShowPremiumComparison(false)} 
                  style={{ 
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    width: '28px', 
                    height: '28px', 
                    borderRadius: '50%', 
                    border: 'none', 
                    backgroundColor: 'rgba(255,255,255,0.1)', 
                    color: '#666', 
                    fontSize: '16px', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                  }}
                >×</button>
                
                {/* Crown icon with glow */}
                <div style={{ 
                  fontSize: isMobile ? '36px' : '48px', 
                  marginBottom: '8px',
                  filter: 'drop-shadow(0 0 20px rgba(240,185,11,0.5))'
                }}>👑</div>
                
                <h2 style={{ 
                  margin: '0 0 4px 0', 
                  color: '#fff', 
                  fontSize: isMobile ? '18px' : '22px',
                  fontWeight: '700',
                  letterSpacing: '-0.5px'
                }}>Unlock Premium</h2>
                
                <p style={{ 
                  margin: 0, 
                  color: '#888', 
                  fontSize: '12px' 
                }}>Trade smarter, win more</p>
              </div>
              
              {/* Stats comparison - cleaner cards */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                padding: isMobile ? '12px 16px' : '20px 24px',
                borderBottom: '1px solid #1a1a2e'
              }}>
                <div style={{ 
                  flex: 1, 
                  backgroundColor: '#111', 
                  borderRadius: '10px', 
                  padding: isMobile ? '10px' : '16px', 
                  textAlign: 'center',
                  border: '1px solid #222'
                }}>
                  <div style={{ color: '#666', fontSize: '9px', marginBottom: '2px', letterSpacing: '1px' }}>FREE</div>
                  <div style={{ color: '#888', fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold' }}>57%</div>
                  <div style={{ color: '#555', fontSize: '9px' }}>win rate</div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: '#f0b90b',
                  fontSize: '18px'
                }}>→</div>
                <div style={{ 
                  flex: 1, 
                  background: 'linear-gradient(135deg, #1a2008 0%, #0d1a0d 100%)', 
                  borderRadius: '10px', 
                  padding: isMobile ? '10px' : '16px', 
                  textAlign: 'center',
                  border: '1px solid #2a3a1a',
                  boxShadow: '0 0 20px rgba(0,210,106,0.1)'
                }}>
                  <div style={{ color: '#00d26a', fontSize: '9px', marginBottom: '2px', letterSpacing: '1px' }}>PREMIUM</div>
                  <div style={{ color: '#00d26a', fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold' }}>76%</div>
                  <div style={{ color: '#00d26a88', fontSize: '9px' }}>win rate</div>
                </div>
              </div>
              
              {/* Features list - clean cards */}
              <div style={{ padding: isMobile ? '12px 16px' : '16px 24px', maxHeight: isMobile ? '180px' : '280px', overflowY: 'auto' }}>
                {[
                  { icon: '🤖', title: 'Auto Trading Bot', desc: 'AI trades PvP tokens for you', hot: true, isNew: true },
                  { icon: '🕵️', title: 'Insider Early Access', desc: 'See tokens 5 seconds early', hot: true },
                  { icon: '🔮', title: 'AlphaBot Signals', desc: '95% safe tokens visible', hot: true },
                  { icon: '🌙', title: 'Moonshot Access', desc: '85% safe high-potential tokens', hot: true },
                  { icon: '💰', title: '2× Daily Rewards', desc: 'Double bonus claims' },
                  { icon: '📉', title: '50% Less Slippage', desc: 'Keep more profits' },
                  { icon: '💬', title: 'Full Chat Access', desc: 'Send messages everywhere' },
                ].map((item, i) => (
                  <div key={i} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    padding: isMobile ? '8px' : '12px',
                    marginBottom: '6px',
                    backgroundColor: item.hot ? (item.isNew ? '#1a0d1a' : '#0d1a0d') : '#111',
                    borderRadius: '8px',
                    border: item.hot ? (item.isNew ? '1px solid #a855f733' : '1px solid #00d26a33') : '1px solid #1a1a2e',
                  }}>
                    <div style={{ 
                      fontSize: isMobile ? '16px' : '20px',
                      width: isMobile ? '28px' : '36px',
                      height: isMobile ? '28px' : '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: item.hot ? (item.isNew ? '#a855f715' : '#00d26a15') : '#0a0a0f',
                      borderRadius: '6px'
                    }}>{item.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        color: item.hot ? (item.isNew ? '#a855f7' : '#00d26a') : '#fff', 
                        fontSize: isMobile ? '11px' : '13px', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {item.title}
                        {item.isNew && <span style={{ 
                          fontSize: '8px', 
                          backgroundColor: '#a855f7', 
                          color: '#fff', 
                          padding: '1px 4px', 
                          borderRadius: '3px',
                          fontWeight: 'bold'
                        }}>NEW</span>}
                        {item.hot && !item.isNew && <span style={{ 
                          fontSize: '8px', 
                          backgroundColor: '#00d26a', 
                          color: '#000', 
                          padding: '1px 4px', 
                          borderRadius: '3px',
                          fontWeight: 'bold'
                        }}>HOT</span>}
                      </div>
                      <div style={{ color: '#666', fontSize: isMobile ? '9px' : '11px', marginTop: '1px' }}>{item.desc}</div>
                    </div>
                    <div style={{ color: item.isNew ? '#a855f7' : '#00d26a', fontSize: '14px' }}>✓</div>
                  </div>
                ))}
              </div>
              
              {/* CTA Section - sticky bottom */}
              <div style={{ 
                padding: isMobile ? '12px 16px 16px' : '20px 24px 24px',
                background: 'linear-gradient(to top, #0d0d12 0%, #0d0d12 80%, transparent 100%)',
                borderTop: '1px solid #1a1a2e'
              }}>
                {/* Monthly Value Explainer */}
                {premiumPricing?.monthly && (
                  <div style={{ 
                    backgroundColor: '#0a1a0a', 
                    border: '1px solid #00d26a33', 
                    borderRadius: '8px', 
                    padding: '10px 12px', 
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#00d26a', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>
                      💡 Why Monthly is Better
                    </div>
                    <div style={{ color: '#888', fontSize: '10px', lineHeight: '1.4' }}>
                      Monthly is <span style={{ color: '#fff' }}>prorated</span> - buy early in the month, get more days!
                      {premiumPricing.monthly.daysRemaining >= 21 && (
                        <span style={{ color: '#00d26a' }}> Buy now: {premiumPricing.monthly.daysRemaining} days for just ${premiumPricing.monthly.cost.toFixed(2)}!</span>
                      )}
                      {premiumPricing.monthly.savings > 0 && (
                        <div style={{ color: '#00d26a', marginTop: '4px', fontWeight: 'bold' }}>
                          ✨ Save ${premiumPricing.monthly.savings.toFixed(2)} vs buying weekly!
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Plan Options */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                  {/* Weekly Plan */}
                  <div 
                    onClick={() => { setShowPremiumComparison(false); buyPremium('weekly'); }}
                    style={{ 
                      flex: 1, 
                      padding: '12px', 
                      borderRadius: '10px', 
                      border: '2px solid #333', 
                      backgroundColor: '#111',
                      cursor: transactionPending ? 'wait' : 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                      opacity: 0.8
                    }}
                  >
                    <div style={{ color: '#888', fontSize: '18px', fontWeight: 'bold' }}>$0.25</div>
                    <div style={{ color: '#555', fontSize: '11px' }}>1 Week</div>
                    <div style={{ color: '#444', fontSize: '9px', marginTop: '2px' }}>7 days</div>
                  </div>
                  
                  {/* Monthly Plan - Best Value (disabled if premium extends past month end) */}
                  {(() => {
                    // Disable if pricing not loaded OR if premium extends past month end
                    const monthlyDisabled = !premiumPricing?.monthly?.endOfMonth || premiumExpiresAt > premiumPricing.monthly.endOfMonth;
                    return (
                      <div 
                        onClick={() => { 
                          if (monthlyDisabled) {
                            alert('Your premium already extends past this month. Wait for it to expire or buy weekly to extend.');
                            return;
                          }
                          setShowPremiumComparison(false); 
                          buyPremium('monthly'); 
                        }}
                        style={{ 
                          flex: 1.3, 
                          padding: '12px', 
                          borderRadius: '10px', 
                          border: monthlyDisabled ? '2px solid #333' : '2px solid #00d26a', 
                          backgroundColor: monthlyDisabled ? '#111' : '#0a1a0a',
                          cursor: transactionPending ? 'wait' : monthlyDisabled ? 'not-allowed' : 'pointer',
                          textAlign: 'center',
                          position: 'relative',
                          transition: 'all 0.2s',
                          boxShadow: monthlyDisabled ? 'none' : '0 0 20px rgba(0,210,106,0.2)',
                          opacity: monthlyDisabled ? 0.5 : 1
                        }}
                      >
                        {!monthlyDisabled && (
                          <div style={{ 
                            position: 'absolute', 
                            top: '-8px', 
                            right: '10px', 
                            backgroundColor: '#00d26a', 
                            color: '#000', 
                            fontSize: '8px', 
                            fontWeight: 'bold', 
                            padding: '2px 6px', 
                            borderRadius: '4px' 
                          }}>BEST VALUE</div>
                        )}
                        <div style={{ color: monthlyDisabled ? '#555' : '#00d26a', fontSize: '22px', fontWeight: 'bold' }}>
                          ${premiumPricing?.monthly?.cost?.toFixed(2) || '0.75'}
                        </div>
                        <div style={{ color: monthlyDisabled ? '#444' : '#00d26a99', fontSize: '11px' }}>
                          {premiumPricing?.monthly?.label || 'Rest of Month'}
                        </div>
                        <div style={{ color: monthlyDisabled ? '#666' : '#00d26a', fontSize: '9px', marginTop: '2px' }}>
                          {monthlyDisabled 
                            ? '🔒 Premium extends past month'
                            : premiumPricing?.monthly?.daysRemaining 
                              ? `${premiumPricing.monthly.daysRemaining} days remaining` 
                              : '🎁 Up to 2 weeks FREE!'}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                
                {isPremium && (
                  <div style={{ textAlign: 'center', marginBottom: '10px', color: '#f0b90b', fontSize: '11px' }}>
                    ✨ Extend your premium time!
                  </div>
                )}
                
                {/* Trust badges - hide on mobile */}
                {!isMobile && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '16px', 
                  marginTop: '16px',
                  color: '#555',
                  fontSize: '10px'
                }}>
                  <span>🔒 Secure payment</span>
                  <span>⚡ Instant activation</span>
                  <span>🚫 No auto-renew</span>
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 🎖️ ACHIEVEMENTS MODAL */}
        {showAchievements && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b' }}>🎖️ Achievements</h2>
                <button onClick={() => setShowAchievements(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Stats Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
                <div style={{ backgroundColor: '#111', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#555', fontSize: '9px' }}>TRADES</div>
                  <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{stats.totalTrades}</div>
                </div>
                <div style={{ backgroundColor: '#0d1a0d', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#555', fontSize: '9px' }}>WINS</div>
                  <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{stats.wins}</div>
                </div>
                <div style={{ backgroundColor: '#1a0d0d', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#555', fontSize: '9px' }}>RUGS</div>
                  <div style={{ color: '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>{stats.rugs}</div>
                </div>
                <div style={{ backgroundColor: '#111', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#555', fontSize: '9px' }}>BEST MULTI</div>
                  <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>{stats.highestMultiplier.toFixed(1)}x</div>
                </div>
              </div>
              
              {/* Achievement Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {Object.entries(ACHIEVEMENT_INFO).map(([key, info]) => {
                  const unlocked = achievements[key];
                  const tierColors = { bronze: '#cd7f32', silver: '#c0c0c0', gold: '#ffd700', legendary: '#ff6b00', shame: '#ff3b69' };
                  return (
                    <div 
                      key={key} 
                      onClick={() => setSelectedAchievement({ key, ...info, unlocked })}
                      style={{ 
                        backgroundColor: unlocked ? '#111' : '#0a0a0a',
                        border: `1px solid ${unlocked ? tierColors[info.tier] : '#1a1a2e'}`,
                        borderRadius: '10px',
                        padding: '12px',
                        textAlign: 'center',
                        opacity: unlocked ? 1 : 0.4,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <div style={{ fontSize: '32px', marginBottom: '4px', filter: unlocked ? 'none' : 'grayscale(100%)' }}>{info.icon}</div>
                      <div style={{ color: unlocked ? tierColors[info.tier] : '#444', fontSize: '11px', fontWeight: 'bold' }}>{info.name}</div>
                      <div style={{ color: '#555', fontSize: '9px', marginTop: '4px' }}>{info.desc}</div>
                    </div>
                  );
                })}
              </div>
              
              <div style={{ marginTop: '16px', textAlign: 'center', color: '#555', fontSize: '11px' }}>
                {Object.values(achievements).filter(v => v).length} / {Object.keys(achievements).length} unlocked
              </div>
              
              {/* Achievement Detail Popup */}
              {selectedAchievement && (
                <div 
                  onClick={() => setSelectedAchievement(null)}
                  style={{ 
                    position: 'fixed', 
                    inset: 0, 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    zIndex: 1100 
                  }}
                >
                  <div 
                    onClick={e => e.stopPropagation()}
                    style={{ 
                      backgroundColor: '#0a0a0f', 
                      border: `2px solid ${
                        selectedAchievement.unlocked 
                          ? { bronze: '#cd7f32', silver: '#c0c0c0', gold: '#ffd700', legendary: '#ff6b00', shame: '#ff3b69' }[selectedAchievement.tier]
                          : '#333'
                      }`, 
                      borderRadius: '16px', 
                      padding: '32px', 
                      maxWidth: '350px', 
                      width: '90%',
                      textAlign: 'center',
                      boxShadow: selectedAchievement.unlocked ? `0 0 40px ${
                        { bronze: 'rgba(205,127,50,0.3)', silver: 'rgba(192,192,192,0.3)', gold: 'rgba(255,215,0,0.4)', legendary: 'rgba(255,107,0,0.4)', shame: 'rgba(255,59,105,0.3)' }[selectedAchievement.tier]
                      }` : 'none'
                    }}
                  >
                    {/* Big Icon */}
                    <div style={{ 
                      fontSize: '80px', 
                      marginBottom: '16px',
                      filter: selectedAchievement.unlocked ? 'none' : 'grayscale(100%)',
                      animation: selectedAchievement.unlocked ? 'pulse 2s ease-in-out infinite' : 'none'
                    }}>
                      {selectedAchievement.icon}
                    </div>
                    
                    {/* Name */}
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      color: selectedAchievement.unlocked 
                        ? { bronze: '#cd7f32', silver: '#c0c0c0', gold: '#ffd700', legendary: '#ff6b00', shame: '#ff3b69' }[selectedAchievement.tier]
                        : '#555',
                      marginBottom: '8px'
                    }}>
                      {selectedAchievement.name}
                    </div>
                    
                    {/* Tier Badge */}
                    <div style={{ 
                      display: 'inline-block',
                      padding: '4px 12px', 
                      borderRadius: '12px', 
                      fontSize: '10px', 
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      backgroundColor: selectedAchievement.unlocked 
                        ? { bronze: 'rgba(205,127,50,0.2)', silver: 'rgba(192,192,192,0.2)', gold: 'rgba(255,215,0,0.2)', legendary: 'rgba(255,107,0,0.2)', shame: 'rgba(255,59,105,0.2)' }[selectedAchievement.tier]
                        : '#111',
                      color: selectedAchievement.unlocked 
                        ? { bronze: '#cd7f32', silver: '#c0c0c0', gold: '#ffd700', legendary: '#ff6b00', shame: '#ff3b69' }[selectedAchievement.tier]
                        : '#444',
                      marginBottom: '16px'
                    }}>
                      {selectedAchievement.tier}
                    </div>
                    
                    {/* Description */}
                    <div style={{ color: '#888', fontSize: '14px', marginBottom: '20px' }}>
                      {selectedAchievement.desc}
                    </div>
                    
                    {/* Progress / Status */}
                    {selectedAchievement.unlocked ? (
                      <div style={{ 
                        backgroundColor: '#0d1a0d', 
                        border: '1px solid #00d26a', 
                        borderRadius: '8px', 
                        padding: '12px',
                        color: '#00d26a',
                        fontWeight: 'bold'
                      }}>
                        ✅ UNLOCKED!
                      </div>
                    ) : (
                      <div style={{ 
                        backgroundColor: '#111', 
                        border: '1px solid #333', 
                        borderRadius: '8px', 
                        padding: '12px'
                      }}>
                        <div style={{ color: '#666', fontSize: '11px', marginBottom: '8px' }}>PROGRESS</div>
                        {(() => {
                          const info = selectedAchievement;
                          let current = 0;
                          let target = info.requirement || 1;
                          
                          if (info.stat === 'wins') current = stats.wins;
                          else if (info.stat === 'rugs') current = stats.rugs;
                          else if (info.stat === 'totalTrades') current = stats.totalTrades;
                          else if (info.stat === 'highestMultiplier') { current = stats.highestMultiplier; target = 5; }
                          else if (info.stat === 'special') current = 0; // Can't track these
                          
                          const percent = Math.min(100, (current / target) * 100);
                          
                          return (
                            <>
                              <div style={{ 
                                width: '100%', 
                                height: '8px', 
                                backgroundColor: '#1a1a2e', 
                                borderRadius: '4px',
                                overflow: 'hidden',
                                marginBottom: '8px'
                              }}>
                                <div style={{ 
                                  width: `${percent}%`, 
                                  height: '100%', 
                                  backgroundColor: { bronze: '#cd7f32', silver: '#c0c0c0', gold: '#ffd700', legendary: '#ff6b00', shame: '#ff3b69' }[info.tier],
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <div style={{ color: '#888', fontSize: '12px' }}>
                                {info.stat === 'special' ? (
                                  <span style={{ color: '#666' }}>🎲 Requires special action</span>
                                ) : (
                                  <span>{current} / {target} ({Math.floor(percent)}%)</span>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    
                    {/* Close hint */}
                    <div style={{ marginTop: '16px', color: '#444', fontSize: '10px' }}>
                      Tap anywhere to close
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 🏟️ TOURNAMENTS MODAL */}
        {/* 🎯 DAILY CHALLENGES MODAL */}
        {showChallenges && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #f0b90b', borderRadius: '16px', padding: '24px', maxWidth: '450px', width: '90%', maxHeight: '80vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b' }}>🎯 Daily Challenges</h2>
                <button onClick={() => setShowChallenges(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Summary */}
              <div style={{ 
                backgroundColor: '#1a1508', 
                border: '1px solid #f0b90b44', 
                borderRadius: '10px', 
                padding: '14px', 
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#f0b90b', fontSize: '12px', marginBottom: '4px' }}>
                  {dailyChallenges.filter(c => c.claimed).length} / {dailyChallenges.length} COMPLETED
                </div>
                <div style={{ color: '#888', fontSize: '10px' }}>
                  Resets at midnight • Earn Paper Bucks for completing!
                </div>
              </div>
              
              {/* Challenge List */}
              <div style={{ display: 'grid', gap: '10px' }}>
                {dailyChallenges.map(c => {
                  const complete = c.progress >= c.target;
                  const canClaim = complete && !c.claimed;
                  
                  return (
                    <div 
                      key={c.id}
                      style={{ 
                        backgroundColor: c.claimed ? '#0d1a0d' : (canClaim ? '#1a1508' : '#111'),
                        border: `1px solid ${c.claimed ? '#00d26a' : (canClaim ? '#f0b90b' : '#1a1a2e')}`,
                        borderRadius: '10px',
                        padding: '14px',
                        opacity: c.claimed ? 0.7 : 1
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <div style={{ 
                            color: c.claimed ? '#00d26a' : (canClaim ? '#f0b90b' : '#fff'), 
                            fontWeight: 'bold', 
                            fontSize: '14px' 
                          }}>
                            {c.label || c.name} {c.claimed && '✅'}
                          </div>
                          <div style={{ color: '#666', fontSize: '11px' }}>{c.label || c.desc}</div>
                        </div>
                        <div style={{ 
                          backgroundColor: canClaim ? '#f0b90b' : (c.claimed ? '#00d26a' : '#333'),
                          color: canClaim ? '#000' : (c.claimed ? '#000' : '#888'),
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          +{formatPB(c.reward)}
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      {!c.claimed && (
                        <div style={{ marginBottom: '10px' }}>
                          <div style={{ 
                            width: '100%', 
                            height: '6px', 
                            backgroundColor: '#1a1a2e', 
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{ 
                              width: `${Math.min(100, (c.progress / c.target) * 100)}%`, 
                              height: '100%', 
                              backgroundColor: complete ? '#00d26a' : '#f0b90b',
                              borderRadius: '3px',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          <div style={{ 
                            color: '#666', 
                            fontSize: '10px', 
                            marginTop: '4px',
                            textAlign: 'right'
                          }}>
                            {c.progress} / {c.target}
                          </div>
                        </div>
                      )}
                      
                      {/* Claim button */}
                      {canClaim && (
                        <button
                          onClick={() => claimChallengeReward(c.id)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: '#f0b90b',
                            color: '#000',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          💰 Claim {formatPB(c.reward)}!
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Total potential earnings */}
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#111', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#666', fontSize: '10px' }}>TOTAL DAILY REWARDS</div>
                <div style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold' }}>
                  {formatPB(dailyChallenges.reduce((sum, c) => sum + c.reward, 0))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🏟️ TOURNAMENTS MODAL (Legacy) */}
        {showTournaments && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b' }}>🏟️ Tournaments</h2>
                <button onClick={() => setShowTournaments(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {tournaments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏗️</div>
                  <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Coming Soon!</div>
                  <div style={{ color: '#666', fontSize: '13px', lineHeight: '1.6' }}>
                    Tournaments are under construction.<br/>
                    Compete for massive prize pools against other players!
                  </div>
                  <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#111', borderRadius: '8px', color: '#888', fontSize: '11px' }}>
                    🎁 Freerolls • 💰 Buy-in Events • 👑 King of the Hill
                  </div>
                </div>
              ) : (
                <>
                  {tournaments.map(t => {
                    const isRegistered = registeredTournaments.includes(t.id);
                    return (
                      <div key={t.id} style={{ 
                        backgroundColor: t.status === 'live' ? '#0d1a0d' : '#111', 
                        border: `1px solid ${t.status === 'live' ? '#00d26a' : '#1a1a2e'}`,
                        borderRadius: '10px', 
                        padding: '14px', 
                        marginBottom: '12px' 
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div>
                            <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px' }}>{t.name}</div>
                            <div style={{ color: '#555', fontSize: '10px' }}>
                              {t.type === 'freeroll' && '🎁 Free Entry'}
                              {t.type === 'buyIn' && `💰 ${formatPB(t.buyIn)} buy-in`}
                              {t.type === 'koth' && '👑 Highest single trade wins'}
                              {t.type === 'survival' && '💀 Last one standing wins'}
                            </div>
                          </div>
                          <div style={{ 
                            padding: '4px 8px', 
                            borderRadius: '4px', 
                            fontSize: '10px', 
                            fontWeight: 'bold',
                            backgroundColor: t.status === 'live' ? '#00d26a' : '#f0b90b',
                            color: '#000'
                          }}>
                            {t.status === 'live' ? '🔴 LIVE' : `Starts in ${Math.floor(t.startsIn / 60)}m`}
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <div>
                            <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>{formatPB(t.prizePool)}</div>
                            <div style={{ color: '#555', fontSize: '10px' }}>Prize Pool</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#888', fontSize: '14px' }}>{t.players}{t.maxPlayers ? `/${t.maxPlayers}` : ''}</div>
                            <div style={{ color: '#555', fontSize: '10px' }}>Players</div>
                          </div>
                        </div>
                        
                        {t.status === 'registering' && (
                          <button 
                            onClick={() => {
                              if (!isRegistered && (t.buyIn === 0 || gameWallet >= t.buyIn)) {
                                if (t.buyIn > 0) setGameWallet(prev => prev - t.buyIn);
                                setRegisteredTournaments(prev => [...prev, t.id]);
                              }
                            }}
                            disabled={isRegistered || (t.buyIn > 0 && gameWallet < t.buyIn)}
                            style={{ 
                              width: '100%', 
                              padding: '10px', 
                              borderRadius: '6px', 
                              border: 'none', 
                              cursor: isRegistered ? 'default' : 'pointer',
                              backgroundColor: isRegistered ? '#0d1a0d' : t.buyIn === 0 ? '#00d26a' : '#f0b90b',
                              color: isRegistered ? '#00d26a' : '#000',
                              fontWeight: 'bold',
                              fontSize: '12px'
                            }}
                          >
                            {isRegistered ? '✓ Registered' : t.buyIn === 0 ? 'Join Free' : `Register (${formatPB(t.buyIn)})`}
                          </button>
                        )}
                        {t.status === 'live' && (
                          <button style={{ width: '100%', padding: '10px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#00d26a', color: '#000', fontWeight: 'bold', fontSize: '12px' }}>
                            🎮 Play Now
                          </button>
                        )}
                      </div>
                    );
                  })}
                  
                  <div style={{ textAlign: 'center', color: '#555', fontSize: '11px', marginTop: '12px' }}>
                    New tournaments every hour • Freerolls every 4 hours
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* 📊 PROFILE STATS MODAL */}
        {showProfile && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #f0b90b', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b' }}>📊 Your Stats</h2>
                <button onClick={() => setShowProfile(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Profile Header */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px', 
                padding: '16px', 
                backgroundColor: '#111', 
                borderRadius: '12px',
                marginBottom: '20px'
              }}>
                {handcashUser?.avatarUrl ? (
                  <img src={handcashUser.avatarUrl} alt="" style={{ width: '60px', height: '60px', borderRadius: '50%', border: '3px solid #f0b90b' }} />
                ) : (
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' }}>🤚</div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold' }}>${handcashUser?.handle || 'trader'}</div>
                  <div style={{ color: '#666', fontSize: '11px' }}>Paper Hands Player</div>
                  {isPremium && (
                    <div style={{ display: 'inline-block', backgroundColor: '#f0b90b', color: '#000', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', marginTop: '4px' }}>
                      👑 PREMIUM
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{Object.values(achievements).filter(v => v).length}</div>
                  <div style={{ color: '#666', fontSize: '10px' }}>Badges</div>
                </div>
              </div>
              
              {/* ⛓️ BSV Transaction Stats - THE MAIN FEATURE */}
              <div style={{ 
                backgroundColor: '#0a1510', 
                border: '1px solid #00d26a44', 
                borderRadius: '12px', 
                padding: '16px', 
                marginBottom: '16px' 
              }}>
                <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  ⛓️ BSV BLOCKCHAIN ACTIVITY
                  <span style={{ color: '#666', fontSize: '10px', fontWeight: 'normal' }}>(Lifetime)</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ backgroundColor: '#0d1a0d', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#00d26a', fontSize: '24px', fontWeight: 'bold' }}>{formatSats(totalDeposited)}</div>
                    <div style={{ color: '#666', fontSize: '10px' }}>↓ Total Deposited</div>
                    <div style={{ color: '#00d26a', fontSize: '11px' }}>${(totalDeposited * (bsvPrice || 20) / 100000000).toFixed(2)} USD</div>
                  </div>
                  <div style={{ backgroundColor: '#1a1508', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#f0b90b', fontSize: '24px', fontWeight: 'bold' }}>{formatSats(totalWithdrawn)}</div>
                    <div style={{ color: '#666', fontSize: '10px' }}>↑ Total Withdrawn</div>
                    <div style={{ color: '#f0b90b', fontSize: '11px' }}>${(totalWithdrawn * (bsvPrice || 20) / 100000000).toFixed(2)} USD</div>
                  </div>
                </div>
                <div style={{ 
                  marginTop: '12px', 
                  padding: '10px', 
                  backgroundColor: totalWithdrawn > totalDeposited ? '#0d1a0d' : '#1a0d0d', 
                  borderRadius: '6px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#666', fontSize: '10px' }}>NET POSITION</div>
                  <div style={{ 
                    color: totalWithdrawn >= totalDeposited ? '#00d26a' : '#ff3b69', 
                    fontSize: '18px', 
                    fontWeight: 'bold' 
                  }}>
                    {totalWithdrawn >= totalDeposited ? '+' : ''}{formatSats(totalWithdrawn - totalDeposited + gameWallet)} sats
                  </div>
                  <div style={{ color: '#666', fontSize: '9px', marginTop: '2px' }}>
                    (withdrawn - deposited + current balance)
                  </div>
                </div>
                <button 
                  onClick={() => { fetchTransactionHistory(); setShowTransactionHistory(true); }}
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '10px',
                    backgroundColor: '#1a1a2e',
                    border: '1px solid #00d26a44',
                    borderRadius: '8px',
                    color: '#00d26a',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  ⛓️ View On-Chain Transactions
                </button>
              </div>
              
              {/* 💰 RAKE ROI TRACKER */}
              <div style={{ 
                backgroundColor: '#1a1508', 
                border: '1px solid #f0b90b44', 
                borderRadius: '12px', 
                padding: '16px', 
                marginBottom: '16px' 
              }}>
                <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  💰 FEE ROI TRACKER
                  <span style={{ color: '#666', fontSize: '10px', fontWeight: 'normal' }}>1¢ per trade</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div style={{ backgroundColor: '#0d0d0d', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#ff6b6b', fontSize: '18px', fontWeight: 'bold' }}>{totalRakePaid}</div>
                    <div style={{ color: '#666', fontSize: '9px' }}>Sats Paid</div>
                    <div style={{ color: '#555', fontSize: '10px' }}>~${(totalRakePaid / 5000).toFixed(2)}</div>
                  </div>
                  <div style={{ backgroundColor: '#0d0d0d', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>{rakeTransactions}</div>
                    <div style={{ color: '#666', fontSize: '9px' }}>Trades</div>
                  </div>
                  <div style={{ backgroundColor: '#0d0d0d', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: stats.grossProfit >= 0 ? '#00d26a' : '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>
                      {stats.grossProfit >= 0 ? '+' : ''}{formatPB(stats.grossProfit || 0)}
                    </div>
                    <div style={{ color: '#666', fontSize: '9px' }}>Net Profit</div>
                  </div>
                </div>
                {totalRakePaid > 0 && (
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '10px', 
                    backgroundColor: stats.grossProfit > 0 ? '#0d1a0d' : '#1a0d0d', 
                    borderRadius: '6px',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#666', fontSize: '10px' }}>FEE ROI</div>
                    <div style={{ 
                      color: stats.grossProfit >= totalRakePaid ? '#00d26a' : '#ff6b6b', 
                      fontSize: '20px', 
                      fontWeight: 'bold' 
                    }}>
                      {stats.grossProfit > 0 ? '+' : ''}{((stats.grossProfit / Math.max(totalRakePaid, 1)) * 100).toFixed(0)}%
                    </div>
                    <div style={{ color: '#555', fontSize: '9px', marginTop: '2px' }}>
                      {stats.grossProfit >= totalRakePaid ? '🚀 Fees covered!' : '📈 Keep trading!'}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Trading Stats */}
              <div style={{ 
                backgroundColor: '#111', 
                borderRadius: '12px', 
                padding: '16px', 
                marginBottom: '16px' 
              }}>
                <div style={{ color: '#888', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px' }}>📈 TRADING PERFORMANCE</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#0a0a0f', borderRadius: '6px' }}>
                    <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{stats.totalTrades}</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>Trades</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#0d1a0d', borderRadius: '6px' }}>
                    <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{stats.wins}</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>Wins</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1a0d0d', borderRadius: '6px' }}>
                    <div style={{ color: '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>{stats.rugs}</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>Rugs</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1a1508', borderRadius: '6px' }}>
                    <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>{stats.highestMultiplier.toFixed(1)}x</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>Best Multi</div>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: stats.totalProfit >= 0 ? '#00d26a' : '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>
                      {stats.totalProfit >= 0 ? '+' : ''}{formatPB(stats.totalProfit)}
                    </div>
                    <div style={{ color: '#555', fontSize: '9px' }}>Total P/L</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>+{formatPB(stats.biggestWin)}</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>Biggest Win</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>-{formatPB(stats.biggestLoss)}</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>Biggest Loss</div>
                  </div>
                </div>
                
                {/* Win Rate */}
                <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#0a0a0f', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#666', fontSize: '10px' }}>WIN RATE</span>
                    <span style={{ color: stats.totalTrades > 0 ? (stats.wins / stats.totalTrades >= 0.5 ? '#00d26a' : '#ff3b69') : '#666', fontSize: '12px', fontWeight: 'bold' }}>
                      {stats.totalTrades > 0 ? ((stats.wins / stats.totalTrades) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div style={{ width: '100%', height: '6px', backgroundColor: '#1a1a2e', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${stats.totalTrades > 0 ? (stats.wins / stats.totalTrades) * 100 : 0}%`, 
                      height: '100%', 
                      backgroundColor: '#00d26a',
                      borderRadius: '3px'
                    }} />
                  </div>
                </div>
              </div>

              {/* 🤖 BOT ROI - Always shows if bot data exists */}
              {tradingBotData && (
                <div style={{
                  backgroundColor: '#0d0612',
                  border: '1px solid #a855f744',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ color: '#a855f7', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🤖 BOT PERFORMANCE
                    <span style={{ color: '#666', fontSize: '10px', fontWeight: 'normal' }}>Fund ROI</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#111', borderRadius: '6px' }}>
                      <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{tradingBotData.stats?.totalTrades || 0}</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Trades</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#111', borderRadius: '6px' }}>
                      <div style={{ color: (tradingBotData.stats?.netProfit || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>
                        {(tradingBotData.stats?.netProfit || 0) >= 0 ? '+' : ''}{formatPB(tradingBotData.stats?.netProfit || 0)}
                      </div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Profit</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#111', borderRadius: '6px' }}>
                      <div style={{ color: '#f0b90b', fontSize: '18px', fontWeight: 'bold' }}>
                        {(tradingBotData.stats?.totalTrades || 0) > 0
                          ? Math.round(((tradingBotData.stats?.wins || 0) / tradingBotData.stats.totalTrades) * 100)
                          : 0}%
                      </div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Win Rate</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px', backgroundColor: (tradingBotData.roi || 0) >= 0 ? '#0d1a0d' : '#1a0d0d', borderRadius: '6px' }}>
                      <div style={{ color: (tradingBotData.roi || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>
                        {(tradingBotData.roi || 0) >= 0 ? '+' : ''}{tradingBotData.roi || 0}%
                      </div>
                      <div style={{ color: '#555', fontSize: '9px' }}>ROI</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Streaks & Records */}
              <div style={{ 
                backgroundColor: '#111', 
                borderRadius: '12px', 
                padding: '16px', 
                marginBottom: '16px' 
              }}>
                <div style={{ color: '#888', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px' }}>🔥 STREAKS & RECORDS</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>🔥</span>
                    <div>
                      <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>{stats.bestWinStreak}</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Best Win Streak</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>❄️</span>
                    <div>
                      <div style={{ color: '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>{stats.worstLossStreak}</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Worst Loss Streak</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>📄</span>
                    <div>
                      <div style={{ color: '#888', fontSize: '14px', fontWeight: 'bold' }}>{stats.paperHandsSells}</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Paper Hand Sells</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>🏃</span>
                    <div>
                      <div style={{ color: '#f0b90b', fontSize: '14px', fontWeight: 'bold' }}>{stats.rugsAvoided}</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>Rugs Escaped</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 💸 Tip Stats */}
              {(stats.tipsSent > 0 || stats.tipsReceived > 0) && (
                <div style={{ 
                  backgroundColor: '#111', 
                  borderRadius: '12px', 
                  padding: '16px', 
                  marginBottom: '16px' 
                }}>
                  <div style={{ color: '#888', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px' }}>💸 TIP ACTIVITY</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>📤</span>
                      <div>
                        <div style={{ color: '#f0b90b', fontSize: '14px', fontWeight: 'bold' }}>{formatPB(stats.tipsSent)}</div>
                        <div style={{ color: '#555', fontSize: '9px' }}>Tips Sent</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>📥</span>
                      <div>
                        <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>{formatPB(stats.tipsReceived)}</div>
                        <div style={{ color: '#555', fontSize: '9px' }}>Tips Received</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '8px', 
                    backgroundColor: '#0a0a0f', 
                    borderRadius: '6px', 
                    textAlign: 'center' 
                  }}>
                    <div style={{ color: '#666', fontSize: '9px' }}>NET TIP FLOW</div>
                    <div style={{ 
                      color: stats.tipsReceived >= stats.tipsSent ? '#00d26a' : '#ff3b69', 
                      fontSize: '12px', 
                      fontWeight: 'bold' 
                    }}>
                      {stats.tipsReceived >= stats.tipsSent ? '+' : ''}{formatPB(stats.tipsReceived - stats.tipsSent)}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Weakness Analysis */}
              {getWeakness() && getWeakness().times > 0 && (
                <div style={{ 
                  backgroundColor: '#1a0d0d', 
                  border: '1px solid #ff3b6944',
                  borderRadius: '12px', 
                  padding: '16px'
                }}>
                  <div style={{ color: '#ff3b69', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>⚠️ YOUR WEAKNESS</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '32px' }}>
                      {getWeakness().type === 'HARD' && '⚡'}
                      {getWeakness().type === 'TRAP' && '🪤'}
                      {getWeakness().type === 'SLOW' && '🐌'}
                      {getWeakness().type === 'HONEYPOT' && '🍯'}
                    </span>
                    <div>
                      <div style={{ color: '#ff3b69', fontSize: '16px', fontWeight: 'bold' }}>{getWeakness().type} RUGS</div>
                      <div style={{ color: '#888', fontSize: '11px' }}>
                        {getWeakness().times} times ({getWeakness().percent.toFixed(0)}% of your rugs)
                      </div>
                      <div style={{ color: '#666', fontSize: '10px' }}>
                        Lost: {formatPB(getWeakness().lost)} to this type
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div style={{ marginTop: '16px', textAlign: 'center', color: '#444', fontSize: '10px' }}>
                Stats are stored locally and persist forever
              </div>
            </div>
          </div>
        )}

        {/* ⛓️ TRANSACTION HISTORY MODAL */}
        {showTransactionHistory && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #00d26a44', borderRadius: '16px', padding: '24px', maxWidth: '550px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#00d26a' }}>⛓️ On-Chain Transactions</h2>
                <button onClick={() => setShowTransactionHistory(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Summary Stats */}
              {transactionSummary && (
                <div style={{ 
                  backgroundColor: '#0a1510', 
                  border: '1px solid #00d26a44', 
                  borderRadius: '12px', 
                  padding: '16px', 
                  marginBottom: '16px' 
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', textAlign: 'center' }}>
                    <div>
                      <div style={{ color: '#00d26a', fontSize: '20px', fontWeight: 'bold' }}>{transactionSummary.totalDeposits}</div>
                      <div style={{ color: '#666', fontSize: '10px' }}>Deposits</div>
                      <div style={{ color: '#00d26a', fontSize: '11px' }}>{formatSats(transactionSummary.totalDepositSats)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold' }}>{transactionSummary.totalWithdrawals}</div>
                      <div style={{ color: '#666', fontSize: '10px' }}>Withdrawals</div>
                      <div style={{ color: '#f0b90b', fontSize: '11px' }}>{formatSats(transactionSummary.totalWithdrawSats)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#a855f7', fontSize: '20px', fontWeight: 'bold' }}>{transactionSummary.totalPremium}</div>
                      <div style={{ color: '#666', fontSize: '10px' }}>Premium</div>
                      <div style={{ color: '#a855f7', fontSize: '11px' }}>Purchases</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Transaction List */}
              <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>TRANSACTION HISTORY</div>
              
              {loadingTransactions ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                  Loading transactions...
                </div>
              ) : transactionHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>📭</div>
                  No on-chain transactions yet
                  <div style={{ fontSize: '11px', marginTop: '8px' }}>Deposit or withdraw to create your first!</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflow: 'auto' }}>
                  {transactionHistory.map((tx, i) => (
                    <div key={i} style={{ 
                      backgroundColor: '#111', 
                      borderRadius: '8px', 
                      padding: '12px',
                      border: `1px solid ${tx.type === 'deposit' ? '#00d26a33' : tx.type === 'withdraw' ? '#f0b90b33' : '#a855f733'}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '20px' }}>
                            {tx.type === 'deposit' ? '📥' : tx.type === 'withdraw' ? '📤' : '👑'}
                          </span>
                          <div>
                            <div style={{ 
                              color: tx.type === 'deposit' ? '#00d26a' : tx.type === 'withdraw' ? '#f0b90b' : '#a855f7', 
                              fontSize: '14px', 
                              fontWeight: 'bold',
                              textTransform: 'capitalize'
                            }}>
                              {tx.type}
                            </div>
                            <div style={{ color: '#666', fontSize: '10px' }}>
                              {new Date(tx.timestamp).toLocaleDateString()} {new Date(tx.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ 
                            color: tx.type === 'deposit' ? '#00d26a' : '#f0b90b', 
                            fontSize: '14px', 
                            fontWeight: 'bold' 
                          }}>
                            {tx.type === 'deposit' ? '+' : '-'}{formatSats(tx.amount)} sats
                          </div>
                          <a 
                            href={tx.wocUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#3b82f6', fontSize: '10px', textDecoration: 'none' }}
                          >
                            View on WoC ↗
                          </a>
                        </div>
                      </div>
                      {tx.txId && (
                        <div style={{ 
                          marginTop: '8px', 
                          padding: '6px', 
                          backgroundColor: '#0a0a0f', 
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '9px',
                          color: '#555',
                          wordBreak: 'break-all'
                        }}>
                          TX: {tx.txId}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <div style={{ marginTop: '16px', textAlign: 'center', color: '#444', fontSize: '10px' }}>
                All transactions verifiable on WhatsOnChain.com
              </div>
            </div>
          </div>
        )}

        {/* 🔗 REFERRALS MODAL */}
        {showReferrals && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '16px', padding: '24px', maxWidth: '420px', width: '90%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#f0b90b' }}>🔗 Refer Friends</h2>
                <button onClick={() => setShowReferrals(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Referral Link */}
              <div style={{ backgroundColor: '#111', padding: '16px', borderRadius: '10px', marginBottom: '16px' }}>
                <div style={{ color: '#555', fontSize: '10px', marginBottom: '8px' }}>YOUR REFERRAL LINK</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    type="text" 
                    readOnly 
                    value={referralStats.referralLink || `https://paperhand.fun/?ref=${referralCode}`}
                    style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #1a1a2e', backgroundColor: '#0a0a0f', color: '#f0b90b', fontSize: '16px' }}
                  />
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(referralStats.referralLink || `https://paperhand.fun/?ref=${referralCode}`);
                      alert('Link copied!');
                    }}
                    style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#f0b90b', color: '#000', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              {/* Rewards Info */}
              <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #0f2f0f', padding: '14px', borderRadius: '10px', marginBottom: '16px' }}>
                <div style={{ color: '#00d26a', fontWeight: 'bold', marginBottom: '10px' }}>💰 Earn Rewards</div>
                <div style={{ display: 'grid', gap: '8px', fontSize: '12px', color: '#888' }}>
                  <div>✅ You get <strong style={{ color: '#00d26a' }}>$47.6K</strong> Paper Bucks (~$0.01)</div>
                  <div>✅ Friend gets <strong style={{ color: '#00d26a' }}>$23.8K</strong> Paper Bucks (~$0.005)</div>
                  <div>✅ Instant credit when they join!</div>
                </div>
              </div>
              
              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '16px' }}>
                <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#f0b90b', fontSize: '24px', fontWeight: 'bold' }}>{referralStats.referred}</div>
                  <div style={{ color: '#555', fontSize: '10px' }}>Friends Referred</div>
                </div>
                <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#00d26a', fontSize: '24px', fontWeight: 'bold' }}>{formatPB(referralStats.earned)}</div>
                  <div style={{ color: '#555', fontSize: '10px' }}>Total Earned</div>
                </div>
              </div>
              
              {/* Recent Referrals */}
              {referralStats.referrals && referralStats.referrals.length > 0 && (
                <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                  <div style={{ color: '#888', fontSize: '10px', marginBottom: '8px' }}>RECENT REFERRALS</div>
                  {referralStats.referrals.slice(-5).reverse().map((r, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < Math.min(4, referralStats.referrals.length - 1) ? '1px solid #1a1a2e' : 'none' }}>
                      <span style={{ color: '#fff' }}>{r.handle}</span>
                      <span style={{ color: '#00d26a', fontSize: '11px' }}>+{formatPB(r.bonusPaid)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Share Buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={() => {
                    const link = referralStats.referralLink || `https://paperhand.fun/?ref=${referralCode}`;
                    const text = encodeURIComponent(`🐸 I'm playing Paper Hands - the BSV trading game!\n\nJoin with my link and we both get free Paper Bucks:\n${link}`);
                    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
                  }}
                  style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', backgroundColor: '#1da1f2', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  🐦 Tweet
                </button>
                <button 
                  onClick={() => {
                    const link = referralStats.referralLink || `https://paperhand.fun/?ref=${referralCode}`;
                    const text = encodeURIComponent(`🐸 Come play Paper Hands with me! BSV trading game. Join with my link and we both get bonus: ${link}`);
                    window.open(`https://wa.me/?text=${text}`, '_blank');
                  }}
                  style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', backgroundColor: '#25d366', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  💬 WhatsApp
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 👥 FRIENDS MODAL */}
        {showFriends && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #3498db', borderRadius: '16px', padding: '24px', maxWidth: '420px', width: '90%', maxHeight: '80vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#3498db', fontWeight: '700' }}>👥 Friends</h2>
                <button onClick={() => setShowFriends(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Add Friend */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                <input 
                  type="text" 
                  placeholder="Enter HandCash handle..."
                  value={friendInput}
                  onChange={(e) => setFriendInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addFriend(friendInput)}
                  style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #1a1a2e', backgroundColor: '#111', color: '#fff', fontSize: '16px' }}
                />
                <button 
                  onClick={() => addFriend(friendInput)}
                  disabled={!friendInput.trim()}
                  style={{ 
                    padding: '10px 16px', 
                    borderRadius: '8px', 
                    border: 'none', 
                    backgroundColor: friendInput.trim() ? '#3498db' : '#333', 
                    color: '#fff', 
                    fontWeight: 'bold', 
                    cursor: friendInput.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '13px'
                  }}
                >
                  Add
                </button>
              </div>
              
              {/* Friends List */}
              {friendsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading friends...</div>
              ) : friends.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                  <div style={{ color: '#888', fontSize: '14px', marginBottom: '8px' }}>No friends yet!</div>
                  <div style={{ color: '#555', fontSize: '12px' }}>Add friends by their HandCash handle</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {friends.map(friend => (
                    <div key={friend.handle} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      padding: '12px', 
                      backgroundColor: '#111', 
                      borderRadius: '10px',
                      border: friend.isOnline ? '1px solid #00d26a33' : '1px solid transparent'
                    }}>
                      <div style={{ position: 'relative' }}>
                        <img 
                          src={friend.avatarUrl} 
                          alt="" 
                          style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                          onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/identicon/svg?seed=${friend.handle}`; }}
                        />
                        {friend.isOnline && (
                          <div style={{ 
                            position: 'absolute', 
                            bottom: '0', 
                            right: '0', 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: '#00d26a', 
                            borderRadius: '50%', 
                            border: '2px solid #0a0a0f' 
                          }} />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: friend.isPremium ? '#f0b90b' : '#fff', fontWeight: 'bold', fontSize: '14px' }}>
                          ${friend.handle} {friend.isPremium && '👑'}
                        </div>
                        <div style={{ color: '#666', fontSize: '11px' }}>
                          {friend.isOnline ? '🟢 Online' : '⚫ Offline'} • {friend.stats?.trades || 0} trades • {friend.stats?.totalProfit >= 0 ? '+' : ''}{formatPB(friend.stats?.totalProfit || 0)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button 
                          onClick={() => { setPaperChatView(friend.handle.replace(/^\$/, '')); setSelectedDMUser(friend.handle.replace(/^\$/, '')); setShowFriends(false); setShowPaperChat(true); fetchDMs(); }}
                          style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #f0b90b', backgroundColor: 'transparent', color: '#f0b90b', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          💬
                        </button>
                        <button 
                          onClick={() => { setTipTarget({ handle: friend.handle, avatarUrl: friend.avatarUrl }); setShowFriends(false); }}
                          style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', backgroundColor: '#f0b90b', color: '#000', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          💸 Tip
                        </button>
                        <button 
                          onClick={() => removeFriend(friend.handle)}
                          style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ff3b69', backgroundColor: 'transparent', color: '#ff3b69', fontSize: '11px', cursor: 'pointer' }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Friend Count */}
              {friends.length > 0 && (
                <div style={{ textAlign: 'center', marginTop: '16px', color: '#555', fontSize: '11px' }}>
                  {friends.filter(f => f.isOnline).length} online • {friends.length}/50 friends
                </div>
              )}
            </div>
          </div>
        )}

        {/* 💬 DIRECT MESSAGES MODAL */}
        {showDMs && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #9b59b6', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '95%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {selectedDMUser && (
                    <button 
                      onClick={() => setSelectedDMUser(null)} 
                      style={{ background: 'none', border: 'none', color: '#888', fontSize: '16px', cursor: 'pointer', padding: '4px' }}
                    >
                      ←
                    </button>
                  )}
                  <h2 style={{ margin: 0, color: '#9b59b6', fontWeight: '700' }}>
                    💬 {selectedDMUser ? `Chat with $${selectedDMUser}` : 'Messages'}
                  </h2>
                </div>
                <button onClick={() => { setShowDMs(false); setSelectedDMUser(null); }} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {!selectedDMUser ? (
                // Conversation List
                <div style={{ flex: 1, overflow: 'auto' }}>
                  {dmConversations.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
                      <div style={{ color: '#888', fontSize: '14px', marginBottom: '8px' }}>No messages yet!</div>
                      <div style={{ color: '#555', fontSize: '12px' }}>Click on a player to start a conversation</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {/* Sort conversations: unread first, then by last message time */}
                      {[...dmConversations].sort((a, b) => {
                        if ((a.unreadCount || 0) > 0 && (b.unreadCount || 0) === 0) return -1;
                        if ((a.unreadCount || 0) === 0 && (b.unreadCount || 0) > 0) return 1;
                        return (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0);
                      }).map(convo => (
                        <div
                          key={convo.handle}
                          onClick={() => { setSelectedDMUser(convo.handle); markDMsRead(convo.handle); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px',
                            backgroundColor: convo.unreadCount > 0 ? '#2d1a3d' : '#111',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            border: convo.unreadCount > 0 ? '2px solid #9b59b6' : '1px solid transparent',
                            boxShadow: convo.unreadCount > 0 ? '0 0 15px rgba(155,89,182,0.3)' : 'none',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: convo.unreadCount > 0 ? '#9b59b6' : '#1a1a2e',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px'
                          }}>
                            💬
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              color: convo.unreadCount > 0 ? '#fff' : '#ccc',
                              fontWeight: 'bold',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              ${convo.handle}
                              {convo.unreadCount > 0 && (
                                <span style={{
                                  backgroundColor: '#9b59b6',
                                  color: '#fff',
                                  fontSize: '9px',
                                  fontWeight: 'bold',
                                  padding: '2px 6px',
                                  borderRadius: '8px'
                                }}>
                                  {convo.unreadCount} NEW
                                </span>
                              )}
                            </div>
                            <div style={{ color: convo.unreadCount > 0 ? '#aaa' : '#666', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {convo.lastMessage?.text || 'No messages'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Chat View
                <>
                  <div style={{ flex: 1, overflow: 'auto', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {(() => {
                      const convo = dmConversations.find(c => c.handle === selectedDMUser);
                      const messages = convo?.messages?.sort((a, b) => a.timestamp - b.timestamp) || [];
                      return messages.map(msg => (
                        <div 
                          key={msg.id} 
                          style={{ 
                            alignSelf: msg.from === handcashUser?.handle?.replace('$', '') ? 'flex-end' : 'flex-start',
                            maxWidth: '75%',
                            backgroundColor: msg.from === handcashUser?.handle?.replace('$', '') ? '#9b59b6' : '#1a1a2e',
                            padding: '10px 14px',
                            borderRadius: '12px',
                            borderBottomRightRadius: msg.from === handcashUser?.handle?.replace('$', '') ? '4px' : '12px',
                            borderBottomLeftRadius: msg.from === handcashUser?.handle?.replace('$', '') ? '12px' : '4px',
                          }}
                        >
                          <div style={{ color: '#fff', fontSize: '13px', wordBreak: 'break-word' }}>{msg.text}</div>
                          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '9px', marginTop: '4px', textAlign: 'right' }}>
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                  
                  {/* Message Input */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input 
                      type="text" 
                      placeholder="Type a message..."
                      value={dmInput}
                      onChange={(e) => setDmInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !sendingDM && sendDM(selectedDMUser, dmInput)}
                      maxLength={500}
                      style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #1a1a2e', backgroundColor: '#111', color: '#fff', fontSize: '16px' }}
                    />
                    <button 
                      onClick={() => sendDM(selectedDMUser, dmInput)}
                      disabled={!dmInput.trim() || sendingDM}
                      style={{ 
                        padding: '12px 20px', 
                        borderRadius: '8px', 
                        border: 'none', 
                        backgroundColor: dmInput.trim() && !sendingDM ? '#9b59b6' : '#333', 
                        color: '#fff', 
                        fontWeight: 'bold', 
                        cursor: dmInput.trim() && !sendingDM ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        minHeight: '44px'
                      }}
                    >
                      {sendingDM ? '...' : 'Send'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* 🔮 RUG PROPHET MODAL */}
        {showRugProphet && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #9b59b6', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#9b59b6', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🔮 Rug Prophet
                    <span style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: '#00d4aa22', color: '#00d4aa', borderRadius: '4px', fontWeight: 'normal' }}>AI TOKENS</span>
                  </h2>
                  <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>Bet on AI-generated tokens - will they RUG or MOON?</div>
                </div>
                <button onClick={() => setShowRugProphet(false)} style={{ width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #9b59b633', backgroundColor: '#1a1a2e', color: '#888', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
              </div>
              
              {/* Your Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
                <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#9b59b6', fontSize: '18px', fontWeight: 'bold' }}>{rugProphetStats.totalBets}</div>
                  <div style={{ color: '#555', fontSize: '9px' }}>Total Bets</div>
                </div>
                <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{rugProphetStats.wins}</div>
                  <div style={{ color: '#555', fontSize: '9px' }}>Wins</div>
                </div>
                <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>{rugProphetStats.losses}</div>
                  <div style={{ color: '#555', fontSize: '9px' }}>Losses</div>
                </div>
                <div style={{ backgroundColor: '#111', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ color: rugProphetStats.totalWon - rugProphetStats.totalLost >= 0 ? '#00d26a' : '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>
                    {rugProphetStats.totalWon - rugProphetStats.totalLost >= 0 ? '+' : ''}{formatPB(rugProphetStats.totalWon - rugProphetStats.totalLost)}
                  </div>
                  <div style={{ color: '#555', fontSize: '9px' }}>Net P/L</div>
                </div>
              </div>
              
              {/* Payout Info */}
              <div style={{ backgroundColor: '#1a0f1f', border: '1px solid #9b59b633', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#ff3b69', fontWeight: 'bold' }}>💀 RUG Bet</div>
                    <div style={{ color: '#888' }}>Pays <span style={{ color: '#f0b90b' }}>1.8x</span></div>
                    <div style={{ color: '#555', fontSize: '9px', marginTop: '2px' }}>Most rugs</div>
                  </div>
                  <div style={{ width: '1px', backgroundColor: '#333' }}></div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#00d26a', fontWeight: 'bold' }}>🌙 MOON Bet</div>
                    <div style={{ color: '#888' }}>Pays <span style={{ color: '#f0b90b' }}>2.5x</span></div>
                    <div style={{ color: '#555', fontSize: '9px', marginTop: '2px' }}>Higher risk</div>
                  </div>
                </div>
              </div>
              
              {/* How To Play Instructions */}
              <div style={{ backgroundColor: '#0f0a15', border: '1px solid #9b59b633', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ color: '#9b59b6', fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>🎯 HOW TO PLAY</div>
                <div style={{ fontSize: '11px', color: '#888', lineHeight: '1.6' }}>
                  <div style={{ marginBottom: '4px' }}>1️⃣ <strong style={{ color: '#fff' }}>Select a token</strong> from the list below</div>
                  <div style={{ marginBottom: '4px' }}>2️⃣ <strong style={{ color: '#fff' }}>Predict</strong> if it will RUG 💀 or MOON 🌙</div>
                  <div style={{ marginBottom: '4px' }}>3️⃣ <strong style={{ color: '#fff' }}>Choose your bet</strong> amount ($0.10 - $50 PB)</div>
                  <div>4️⃣ <strong style={{ color: '#fff' }}>Win</strong> when the token resolves your way!</div>
                </div>
              </div>
              
              {/* Active Tokens to Bet On */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold' }}>SELECT A TOKEN</div>
                  <div style={{ color: '#9b59b6', fontSize: '10px' }}>👆 Tap to bet</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflow: 'auto' }}>
                  {(() => {
                    // Filter eligible tokens - AI tokens only (no user-launched BSV.pump tokens)
                    const eligibleTokens = activeTokens.filter(t => {
                      const age = Date.now() - t.birthTime;
                      const progress = age / (t.lifespan || 300000);
                      const hasBet = rugProphetBets.some(b => b.tokenId === t.id && b.status === 'pending');
                      // Only AI tokens - exclude user-launched tokens
                      return progress < 0.6 && !hasBet && t.state === 'active' && !t.isUserLaunched;
                    });

                    if (eligibleTokens.length === 0) {
                      return (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontSize: '12px' }}>
                          No AI tokens available to bet on right now.<br/>
                          <span style={{ fontSize: '10px' }}>Tokens must be &lt;60% through lifespan. User tokens excluded.</span>
                        </div>
                      );
                    }
                    
                    // Get friends' handles (without $)
                    const friendHandles = friends.map(f => f.handle?.replace('$', '') || f.replace('$', ''));
                    
                    // Debug: Log friends and online users with their currentToken
                    console.log('🔮 Rug Prophet debug:', {
                      friendHandles,
                      onlineUsersWithTokens: onlineUsers.map(u => ({ handle: u.handle, currentToken: u.currentToken }))
                    });
                    
                    // Helper: Find friends in a token
                    const getFriendsInToken = (tokenId) => {
                      return onlineUsers.filter(u => 
                        u.currentToken === tokenId && 
                        friendHandles.includes(u.handle?.replace('$', ''))
                      ).map(u => u.handle);
                    };
                    
                    // Sort: tokens with friends first
                    const sortedTokens = [...eligibleTokens].sort((a, b) => {
                      const friendsInA = getFriendsInToken(a.id).length;
                      const friendsInB = getFriendsInToken(b.id).length;
                      if (friendsInA > 0 && friendsInB === 0) return -1;
                      if (friendsInB > 0 && friendsInA === 0) return 1;
                      return friendsInB - friendsInA; // More friends = higher
                    });
                    
                    return sortedTokens.map(token => {
                      const friendsInToken = getFriendsInToken(token.id);
                      const hasFriends = friendsInToken.length > 0;
                      
                      return (
                        <div 
                          key={token.id} 
                          onClick={() => setSelectedBetToken(selectedBetToken?.id === token.id ? null : token)}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '12px', 
                            backgroundColor: selectedBetToken?.id === token.id ? '#1f1a2e' : hasFriends ? '#1a1520' : '#111', 
                            borderRadius: '8px',
                            border: selectedBetToken?.id === token.id ? '1px solid #9b59b6' : hasFriends ? '1px solid #e91e6333' : '1px solid transparent',
                            cursor: 'pointer'
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '14px' }}>{token.ticker}</span>
                              {hasFriends && <span style={{ color: '#e91e63', fontSize: '12px' }}>💗</span>}
                            </div>
                            <div style={{ color: '#666', fontSize: '10px' }}>
                              {hasFriends ? (
                                <span style={{ color: '#e91e63' }}>
                                  {friendsInToken.length === 1 
                                    ? `${friendsInToken[0]} is here` 
                                    : `${friendsInToken.length} friends here`}
                                </span>
                              ) : token.category}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ color: token.priceChange >= 0 ? '#00d26a' : '#ff3b69', fontSize: '13px', fontWeight: 'bold' }}>
                                {token.priceChange >= 0 ? '+' : ''}{(token.priceChange || 0).toFixed(1)}%
                              </div>
                              <div style={{ color: '#555', fontSize: '10px' }}>{formatPB(token.liquidityPool || 0)} liq</div>
                            </div>
                            <div style={{ 
                              color: selectedBetToken?.id === token.id ? '#9b59b6' : '#555', 
                              fontSize: '16px',
                              transition: 'transform 0.2s',
                              transform: selectedBetToken?.id === token.id ? 'rotate(180deg)' : 'rotate(0deg)'
                            }}>
                              ▼
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              
              {/* Bet Form */}
              {selectedBetToken && (
                <div style={{ backgroundColor: '#111', borderRadius: '10px', padding: '16px', marginBottom: '20px', border: '1px solid #9b59b6' }}>
                  <div style={{ color: '#f0b90b', fontWeight: 'bold', marginBottom: '12px' }}>
                    🎯 Betting on {selectedBetToken.ticker}
                  </div>
                  
                  {/* Prediction */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <button 
                      onClick={() => setBetPrediction('rug')}
                      style={{ 
                        flex: 1, 
                        padding: '12px', 
                        borderRadius: '8px', 
                        border: betPrediction === 'rug' ? '2px solid #ff3b69' : '1px solid #333',
                        backgroundColor: betPrediction === 'rug' ? '#1a0a0f' : 'transparent',
                        color: '#ff3b69',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      💀 RUG (1.8x)
                    </button>
                    <button 
                      onClick={() => setBetPrediction('moon')}
                      style={{ 
                        flex: 1, 
                        padding: '12px', 
                        borderRadius: '8px', 
                        border: betPrediction === 'moon' ? '2px solid #00d26a' : '1px solid #333',
                        backgroundColor: betPrediction === 'moon' ? '#0a1a0f' : 'transparent',
                        color: '#00d26a',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      🌙 MOON (2.5x)
                    </button>
                  </div>
                  
                  {/* Amount */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ color: '#888', fontSize: '10px', marginBottom: '6px' }}>BET AMOUNT</div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {[10000, 50000, 100000, 500000].map(amt => (
                        <button
                          key={amt}
                          onClick={() => setBetAmount(amt)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '6px',
                            border: betAmount === amt ? '1px solid #9b59b6' : '1px solid #333',
                            backgroundColor: betAmount === amt ? '#1a0f1f' : 'transparent',
                            color: betAmount === amt ? '#9b59b6' : '#888',
                            fontSize: '11px',
                            cursor: 'pointer'
                          }}
                        >
                          {formatPB(amt)}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Potential Payout */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', padding: '10px', backgroundColor: '#0a0a0f', borderRadius: '6px' }}>
                    <span style={{ color: '#888', fontSize: '12px' }}>Potential Win:</span>
                    <span style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '14px' }}>
                      {formatPB(Math.floor(betAmount * (betPrediction === 'rug' ? 1.8 : 2.5)))}
                    </span>
                  </div>
                  
                  {/* Place Bet Button */}
                  <button
                    onClick={() => placeRugProphetBet(selectedBetToken.id, betPrediction, betAmount)}
                    disabled={placingBet || gameWallet < betAmount}
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '10px',
                      border: 'none',
                      backgroundColor: gameWallet < betAmount ? '#333' : '#9b59b6',
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      cursor: gameWallet < betAmount ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {placingBet ? 'Placing Bet...' : gameWallet < betAmount ? 'Insufficient Balance' : `🔮 Place ${formatPB(betAmount)} Bet`}
                  </button>
                </div>
              )}
              
              {/* Recent Bets */}
              {rugProphetBets.length > 0 && (
                <div>
                  <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>YOUR BETS</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflow: 'auto' }}>
                    {rugProphetBets.slice(0, 10).map(bet => {
                      // Find the token in activeTokens - try by ID first, then by ticker
                      let token = activeTokens.find(t => t.id === bet.tokenId);
                      if (!token) {
                        token = activeTokens.find(t => t.ticker === bet.tokenTicker);
                      }
                      const isPending = bet.status === 'pending';
                      const canWatch = isPending && token;
                      const isStale = isPending && !token; // Token gone but bet still pending
                      
                      // Debug log
                      if (isPending && !token) {
                        console.log('🔮 Bet token not found:', bet.tokenTicker, bet.tokenId, 'Active tokens:', activeTokens.map(t => ({ id: t.id, ticker: t.ticker })));
                      }
                      
                      return (
                        <div 
                          key={bet.id} 
                          onClick={() => {
                            // Open bet details modal
                            setSelectedBet(bet);
                          }}
                          style={{ 
                            backgroundColor: canWatch ? '#1a0f1f' : isStale ? '#1a1a0f' : '#111', 
                            borderRadius: '8px',
                            opacity: isPending ? 1 : 0.7,
                            border: canWatch ? '2px solid #9b59b6' : isStale ? '1px solid #f0b90b55' : isPending ? '1px solid #9b59b633' : '1px solid transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '10px', 
                          }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ color: isStale ? '#f0b90b' : '#f0b90b', fontSize: '12px', fontWeight: 'bold' }}>{bet.tokenTicker}</span>
                                {canWatch && <span style={{ color: '#9b59b6', fontSize: '10px' }}>👁️</span>}
                                {isStale && <span style={{ color: '#f0b90b', fontSize: '10px' }}>🔄</span>}
                              </div>
                              <div style={{ color: bet.prediction === 'rug' ? '#ff3b69' : '#00d26a', fontSize: '10px' }}>
                                {bet.prediction === 'rug' ? '💀 RUG' : '🌙 MOON'}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {/* Clear button for stale bets */}
                              {isStale && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const lostAmount = bet.amount;
                                    
                                    // Remove from state immediately
                                    setRugProphetBets(prev => prev.filter(b => b.id !== bet.id));
                                    
                                    // Update stats
                                    setRugProphetStats(prev => ({
                                      ...prev,
                                      losses: prev.losses + 1,
                                      totalLost: prev.totalLost + lostAmount
                                    }));
                                    
                                    // Resolve on server first, then clear
                                    if (API_URL && sessionId) {
                                      try {
                                        // Resolve the bet
                                        await fetch(`${API_URL}/api/rug-prophet/resolve`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                          body: JSON.stringify({ tokenId: bet.tokenId, tokenTicker: bet.tokenTicker, outcome: 'expired' })
                                        });
                                        // Then delete it from server
                                        await fetch(`${API_URL}/api/rug-prophet/clear`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                                          body: JSON.stringify({ betId: bet.id })
                                        });
                                      } catch (e) {
                                        console.log('Clear bet error:', e.message);
                                      }
                                    }
                                  }}
                                  style={{
                                    backgroundColor: '#ff3b6922',
                                    border: '1px solid #ff3b69',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    color: '#ff3b69',
                                    fontSize: '9px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                  }}
                                >
                                  🗑️ Clear
                                </button>
                              )}
                              <div>
                                <div style={{ 
                                  color: bet.status === 'won' ? '#00d26a' : bet.status === 'lost' ? '#ff3b69' : isStale ? '#f0b90b' : '#f0b90b',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  {bet.status === 'pending' ? `${formatPB(bet.amount)} pending` : 
                                   bet.status === 'won' ? `+${formatPB(bet.payout)}` : 
                                   `-${formatPB(bet.amount)}`}
                                </div>
                                <div style={{ color: '#555', fontSize: '9px' }}>
                                  {bet.status === 'pending' ? (
                                    canWatch ? (
                                      <span style={{ color: '#9b59b6' }}>
                                        {(token.priceChange || 0) >= 0 ? '📈' : '📉'} {(token.priceChange || 0) >= 0 ? '+' : ''}{(token.priceChange || 0).toFixed(1)}% • 👆 Tap for options
                                      </span>
                                    ) : <span style={{ color: '#f0b90b' }}>🔄 Checking outcome... • 👆 Tap to check now</span>
                                  ) : bet.status === 'won' ? '✅ Won' : '❌ Lost'}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Show result for resolved bets */}
                          {bet.status !== 'pending' && (
                            <div style={{
                              padding: '6px 10px',
                              backgroundColor: bet.status === 'won' ? '#0a1a0f' : '#1a0a0f',
                              borderTop: `1px solid ${bet.status === 'won' ? '#00d26a33' : '#ff3b6933'}`,
                              borderRadius: '0 0 8px 8px',
                              fontSize: '10px',
                              color: bet.status === 'won' ? '#00d26a' : '#ff3b69',
                              textAlign: 'center'
                            }}>
                              {bet.status === 'won' 
                                ? `🎉 Won ${formatPB(bet.payout - bet.amount)} profit!` 
                                : bet.outcome === 'expired' ? '💀 Token expired before resolving'
                                : `Token ${bet.prediction === 'rug' ? 'mooned' : 'rugged'} instead`}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 🎯 BET DETAILS MODAL */}
        {selectedBet && (() => {
          // Use LIVE bet data from state (updates when background sync runs)
          const bet = rugProphetBets.find(b => b.id === selectedBet.id) || selectedBet;
          let token = activeTokens.find(t => t.id === bet.tokenId);
          if (!token) token = activeTokens.find(t => t.ticker === bet.tokenTicker);
          const isPending = bet.status === 'pending';
          const isStale = isPending && !token;
          const canWatch = isPending && token;
          
          // Calculate potential outcomes
          const potentialWin = bet.prediction === 'rug' 
            ? Math.floor(bet.amount * 1.8) 
            : Math.floor(bet.amount * 2.5);
          const profit = potentialWin - bet.amount;
          
          return (
            <div style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3000,
            }} onClick={() => setSelectedBet(null)}>
              <div style={{
                backgroundColor: '#0a0a0f',
                border: `2px solid ${isPending ? '#9b59b6' : bet.status === 'won' ? '#00d26a' : '#ff3b69'}`,
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '400px',
                width: '90%',
                position: 'relative',
              }} onClick={e => e.stopPropagation()}>
                
                {/* Close button */}
                <button
                  onClick={() => setSelectedBet(null)}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'none',
                    border: 'none',
                    color: '#666',
                    fontSize: '24px',
                    cursor: 'pointer',
                  }}
                >×</button>
                
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '8px' }}>
                    {isPending ? '🔮' : bet.status === 'won' ? '🎉' : '💀'}
                  </div>
                  <div style={{ color: '#f0b90b', fontSize: '24px', fontWeight: 'bold' }}>
                    {bet.tokenTicker}
                  </div>
                  <div style={{ 
                    color: bet.prediction === 'rug' ? '#ff3b69' : '#00d26a',
                    fontSize: '14px',
                    marginTop: '4px',
                  }}>
                    {bet.prediction === 'rug' ? '💀 RUG Prediction' : '🌙 MOON Prediction'}
                  </div>
                </div>
                
                {/* Bet Details */}
                <div style={{
                  backgroundColor: '#111',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ color: '#666' }}>Bet Amount</span>
                    <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>{formatPB(bet.amount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ color: '#666' }}>Multiplier</span>
                    <span style={{ color: '#9b59b6', fontWeight: 'bold' }}>{bet.prediction === 'rug' ? '1.8x' : '2.5x'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ color: '#666' }}>Potential Win</span>
                    <span style={{ color: '#00d26a', fontWeight: 'bold' }}>+{formatPB(profit)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>Status</span>
                    <span style={{ 
                      color: isPending ? (isStale ? '#f0b90b' : '#9b59b6') : bet.status === 'won' ? '#00d26a' : '#ff3b69',
                      fontWeight: 'bold' 
                    }}>
                      {isPending ? (isStale ? '🔄 Checking...' : '⏳ Pending') : bet.status === 'won' ? '✅ Won' : '❌ Lost'}
                    </span>
                  </div>
                  
                  {/* Token price info if available */}
                  {canWatch && token && (
                    <div style={{ 
                      marginTop: '12px', 
                      paddingTop: '12px', 
                      borderTop: '1px solid #222',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ color: '#666' }}>Current Price</span>
                      <span style={{ 
                        color: (token.priceChange || 0) >= 0 ? '#00d26a' : '#ff3b69',
                        fontWeight: 'bold' 
                      }}>
                        {(token.priceChange || 0) >= 0 ? '+' : ''}{(token.priceChange || 0).toFixed(1)}%
                      </span>
                    </div>
                  )}
                  
                  {/* Explanation for expired tokens */}
                  {isStale && (
                    <div style={{ 
                      marginTop: '12px', 
                      paddingTop: '12px', 
                      borderTop: '1px solid #222',
                    }}>
                      <div style={{ 
                        backgroundColor: '#1a1a0f',
                        border: '1px solid #f0b90b44',
                        borderRadius: '8px',
                        padding: '12px',
                        textAlign: 'center',
                      }}>
                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                        <div style={{ color: '#f0b90b', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
                          Checking outcome...
                        </div>
                        <div style={{ color: '#888', fontSize: '11px', lineHeight: '1.5' }}>
                          This token ended while you were away. The game is checking if you won - this happens automatically every few seconds!
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  
                  {/* Watch Token - if still active */}
                  {canWatch && (
                    <button
                      onClick={() => {
                        setSelectedBet(null);
                        setShowRugProphet(false);
                        // Navigate to token trading view
                        setCurrentToken(token);
                        setCurrentPrice(token.currentPrice || token.price || 1);
                        setView('token');
                      }}
                      style={{
                        padding: '14px',
                        borderRadius: '10px',
                        border: 'none',
                        backgroundColor: '#9b59b6',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      👁️ Watch Token
                    </button>
                  )}
                  
                  {/* Get Results - for bets where token already resolved */}
                  {isStale && (
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        console.log('🔄 Get Results clicked!');
                        console.log('API_URL:', API_URL);
                        console.log('sessionId:', sessionId);
                        console.log('bet:', bet);
                        
                        // Show loading
                        const btn = e.currentTarget;
                        const originalText = btn.innerText;
                        btn.innerText = '⏳ Checking...';
                        btn.disabled = true;
                        
                        try {
                          // Fetch latest bets from server (server auto-resolves orphaned bets)
                          console.log('🔄 Fetching bets...');
                          const betsRes = await fetch(`${API_URL}/api/rug-prophet/bets`, {
                            headers: { 'X-Session-Id': sessionId }
                          });
                          console.log('🔄 Bets response:', betsRes.status);
                          
                          let resolved = false;
                          let won = false;
                          let payout = 0;
                          
                          if (betsRes.ok) {
                            const betsData = await betsRes.json();
                            console.log('🔄 Bets data:', betsData);
                            
                            // Update state with fresh data
                            setRugProphetBets(betsData.bets || []);
                            setRugProphetStats(betsData.stats || { totalBets: 0, wins: 0, losses: 0, totalWon: 0, totalLost: 0 });
                            
                            // Check if this specific bet was resolved
                            const updatedBet = (betsData.bets || []).find(b => b.id === bet.id);
                            console.log('🔄 Updated bet:', updatedBet);
                            
                            if (updatedBet && updatedBet.status !== 'pending') {
                              resolved = true;
                              won = updatedBet.status === 'won';
                              payout = updatedBet.payout || 0;
                              
                              // Update the selected bet to show new status
                              setSelectedBet(updatedBet);
                            }
                          } else {
                            console.error('🔄 Bets fetch failed:', betsRes.status);
                          }
                          
                          // Fetch wallet balance
                          console.log('🔄 Fetching wallet...');
                          const authRes = await fetch(`${API_URL}/api/user`, {
                            headers: { 'X-Session-Id': sessionId }
                          });
                          console.log('🔄 Auth response:', authRes.status);
                          
                          if (authRes.ok) {
                            const authData = await authRes.json();
                            console.log('🔄 Auth data:', authData);
                            if (authData.gameBalance !== undefined) {
                              setGameWallet(authData.gameBalance);
                            }
                          }
                          
                          // Show feedback based on result
                          if (resolved) {
                            if (won) {
                              Sounds.cashout();
                              const profit = payout - bet.amount;
                              showBalanceChange(profit);
                              btn.innerText = '✅ You Won!';
                              btn.style.backgroundColor = '#00d26a';
                            } else {
                              Sounds.error();
                              showBalanceChange(-bet.amount);
                              btn.innerText = '💀 Lost';
                              btn.style.backgroundColor = '#ff3b69';
                            }
                            // Keep modal open briefly to show result
                            setTimeout(() => setSelectedBet(null), 1500);
                          } else {
                            // Still pending - token might still be processing
                            btn.innerText = '⏳ Still checking...';
                            btn.style.backgroundColor = '#f0b90b';
                            setTimeout(() => {
                              btn.innerText = originalText;
                              btn.disabled = false;
                            }, 2000);
                          }
                        } catch (e) {
                          console.error('🔄 Sync error:', e);
                          alert('Error syncing: ' + e.message);
                          btn.innerText = originalText;
                          btn.disabled = false;
                        }
                      }}
                      style={{
                        padding: '14px',
                        borderRadius: '10px',
                        border: 'none',
                        backgroundColor: '#f0b90b',
                        color: '#000',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      🎯 Get Results
                    </button>
                  )}
                  
                  {/* Close button - handles resolved bet feedback */}
                  <button
                    onClick={async () => {
                      // If bet was lost, show the loss feedback
                      if (bet.status === 'lost') {
                        // Show red balance change indicator
                        showBalanceChange(-bet.amount);
                        Sounds.error();
                        
                        // Remove this bet from the visible list
                        setRugProphetBets(prev => prev.filter(b => b.id !== bet.id));
                        
                        // Clear from server
                        if (API_URL && sessionId) {
                          fetch(`${API_URL}/api/rug-prophet/clear`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                            body: JSON.stringify({ betId: bet.id })
                          }).catch(() => {});
                        }
                      }
                      // If bet was won, show win feedback and clear
                      else if (bet.status === 'won') {
                        // Show green balance change indicator (profit only)
                        const profit = bet.payout - bet.amount;
                        showBalanceChange(profit);
                        Sounds.cashout();
                        
                        // Remove this bet from the visible list
                        setRugProphetBets(prev => prev.filter(b => b.id !== bet.id));
                        
                        // Clear from server
                        if (API_URL && sessionId) {
                          fetch(`${API_URL}/api/rug-prophet/clear`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId },
                            body: JSON.stringify({ betId: bet.id })
                          }).catch(() => {});
                        }
                      }
                      setSelectedBet(null);
                    }}
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      border: bet.status === 'lost' ? '1px solid #ff3b69' : bet.status === 'won' ? '1px solid #00d26a' : '1px solid #333',
                      backgroundColor: bet.status === 'lost' ? '#1a0f0f' : bet.status === 'won' ? '#0f1a0f' : '#111',
                      color: bet.status === 'lost' ? '#ff3b69' : bet.status === 'won' ? '#00d26a' : '#666',
                      fontSize: '12px',
                      cursor: 'pointer',
                      marginTop: '4px',
                    }}
                  >
                    {bet.status === 'lost' ? '💀 Dismiss Loss' : bet.status === 'won' ? '🎉 Collect Win' : 'Close'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* 🤖 FLOATING BOT WIDGET - Non-intrusive when auto-nav is OFF */}
        {tradingBotData?.active && (
          <div style={{
            position: 'fixed',
            bottom: isMobile ? '80px' : '20px',
            right: isMobile ? '10px' : '20px',
            backgroundColor: '#0d0612',
            border: `2px solid ${tradingBotData.isTrading ? '#a855f7' : '#666'}`,
            borderRadius: '16px',
            padding: '12px 16px',
            zIndex: 900,
            minWidth: '200px',
            maxWidth: '280px',
            boxShadow: tradingBotData.isTrading ? '0 0 30px rgba(168, 85, 247, 0.4)' : '0 4px 20px rgba(0,0,0,0.5)',
          }}>
            {/* Header Row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>🤖</span>
                <div>
                  <div style={{ color: tradingBotData.isTrading ? '#a855f7' : '#666', fontSize: '11px', fontWeight: 'bold' }}>
                    {tradingBotData.isTrading ? 'BOT RUNNING' : 'BOT PAUSED'}
                  </div>
                  <div style={{ color: '#888', fontSize: '9px' }}>
                    {tradingBotData.currentCommand?.action === 'buying' ? '⚡ Buying...' :
                     tradingBotData.currentCommand?.action === 'selling' ? '⚡ Selling...' :
                     tradingBotData.currentCommand?.action === 'analyzing' ? '🧠 Analyzing...' :
                     tradingBotData.currentCommand?.action === 'watching' ? '👁️ Watching...' :
                     tradingBotData.currentCommand?.action === 'idle' ? '⏸️ Paused' :
                     tradingBotData.currentCommand?.action === 'paused' ? '⏸️ Paused' :
                     tradingBotData.isTrading ? '📡 Scanning...' : 'Waiting...'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setShowTradingBot(true)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '8px',
                    border: '1px solid #a855f7',
                    backgroundColor: 'rgba(168,85,247,0.1)',
                    color: '#a855f7',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  ⚙️
                </button>
                <button
                  onClick={stopTradingBot}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #ff3b69',
                    backgroundColor: 'rgba(255,59,105,0.1)',
                    color: '#ff3b69',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  Stop
                </button>
              </div>
            </div>
            
            {/* Stats Row */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
              <div>
                <div style={{ color: '#888', fontSize: '9px' }}>TRADES</div>
                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{tradingBotData.stats?.totalTrades || 0}</div>
              </div>
              <div>
                <div style={{ color: '#888', fontSize: '9px' }}>PROFIT</div>
                <div style={{ color: (tradingBotData.stats?.netProfit || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>
                  {(tradingBotData.stats?.netProfit || 0) >= 0 ? '+' : ''}{formatPB(tradingBotData.stats?.netProfit || 0)}
                </div>
              </div>
              <div>
                <div style={{ color: '#888', fontSize: '9px' }}>WIN RATE</div>
                <div style={{ color: '#f0b90b', fontSize: '14px', fontWeight: 'bold' }}>
                  {(tradingBotData.stats?.totalTrades || 0) > 0
                    ? Math.round(((tradingBotData.stats?.wins || 0) / tradingBotData.stats.totalTrades) * 100)
                    : 0}%
                </div>
              </div>
              <div>
                <div style={{ color: '#888', fontSize: '9px' }}>ROI</div>
                <div style={{ color: (tradingBotData.roi || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>
                  {(tradingBotData.roi || 0) >= 0 ? '+' : ''}{tradingBotData.roi || 0}%
                </div>
              </div>
            </div>

            {/* Current Token + View Button */}
            {tradingBotData.currentCommand?.tokenId && tradingBotData.currentCommand?.ticker && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                backgroundColor: '#111',
                borderRadius: '8px',
                padding: '8px 10px',
                marginBottom: '10px'
              }}>
                <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                  ${tradingBotData.currentCommand.ticker || 'Unknown'}
                </div>
                <button
                  onClick={() => {
                    const token = activeTokens.find(t => t.id === tradingBotData.currentCommand.tokenId);
                    if (token) {
                      setCurrentToken(token);
                      setView('token');
                    }
                  }}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    border: '1px solid #a855f7',
                    backgroundColor: 'rgba(168,85,247,0.1)',
                    color: '#a855f7',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  View →
                </button>
              </div>
            )}
            
            {/* Bot Positions - Expandable */}
            {tradingBotData.positions?.length > 0 && (
              <details style={{ marginBottom: '10px' }}>
                <summary style={{ 
                  color: '#888', 
                  fontSize: '10px', 
                  cursor: 'pointer',
                  padding: '6px 0',
                  borderTop: '1px solid #222',
                  marginTop: '4px'
                }}>
                  📊 {tradingBotData.positions.length} Position{tradingBotData.positions.length > 1 ? 's' : ''} (tap to expand)
                </summary>
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {tradingBotData.positions.slice(0, 5).map((pos, idx) => {
                    const profitPct = parseFloat(pos.profitPercent) || 0;
                    const isProfit = profitPct >= 0;
                    return (
                      <div key={idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#111',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        fontSize: '10px',
                      }}>
                        <span style={{ color: '#fff', fontWeight: 'bold' }}>${pos.tokenTicker || '???'}</span>
                        <span style={{ color: isProfit ? '#00d26a' : '#ff3b69', fontWeight: 'bold' }}>
                          {isProfit ? '+' : ''}{profitPct}%
                        </span>
                      </div>
                    );
                  })}
                  {tradingBotData.positions.length > 5 && (
                    <div style={{ color: '#666', fontSize: '9px', textAlign: 'center' }}>
                      +{tradingBotData.positions.length - 5} more...
                    </div>
                  )}
                </div>
              </details>
            )}
            
            {/* Auto-Nav Toggle */}
            <button
              onClick={() => setBotControlEnabled(true)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '8px',
                border: '1px solid #333',
                backgroundColor: '#1a1a2e',
                color: '#888',
                fontSize: '10px',
                cursor: 'pointer',
              }}
            >
              🎬 Enable Auto-Navigation
            </button>
          </div>
        )}
        
        {/* 🤖 BOT TRADE TOAST - Shows briefly on each trade */}
        {botLastTrade && Date.now() - botLastTrade.timestamp < 4000 && !botControlEnabled && (
          <div style={{
            position: 'fixed',
            top: '80px',
            right: isMobile ? '10px' : '20px',
            backgroundColor: botLastTrade.action === 'buying' ? 'rgba(0,210,106,0.95)' : 'rgba(255,59,105,0.95)',
            borderRadius: '12px',
            padding: '12px 16px',
            zIndex: 950,
            animation: 'slideIn 0.3s ease-out',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>{botLastTrade.action === 'buying' ? '🤖📈' : '🤖📉'}</span>
              <div>
                <div style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>
                  Bot {botLastTrade.action === 'buying' ? 'bought' : 'sold'} ${botLastTrade.ticker}
                </div>
                {botLastTrade.narration && (
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '10px', maxWidth: '200px' }}>
                    "{botLastTrade.narration}"
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 🏆 TRADING WARS MODAL */}
        {showTradingWars && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '2px solid #ffd700', borderRadius: '16px', padding: '24px', maxWidth: '600px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', fontFamily: 'monospace' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#ffd700', fontWeight: 'bold', fontSize: '24px' }}>🏆 TRADING WARS</h2>
                  <div style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>Weekly competition • Volume + Escapes = Victory</div>
                </div>
                <button onClick={() => setShowTradingWars(false)} style={{ width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #ffd70033', backgroundColor: '#1a1a2e', color: '#888', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
              </div>
              
              {/* How To Win - Collapsible */}
              <details style={{ marginBottom: '16px', backgroundColor: '#111', borderRadius: '12px', border: '1px solid #333' }}>
                <summary style={{ padding: '14px 16px', cursor: 'pointer', color: '#ffd700', fontWeight: 'bold', fontSize: '13px', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📖 HOW TO WIN</span>
                  <span style={{ color: '#666', fontSize: '10px', fontWeight: 'normal' }}>(tap to expand)</span>
                </summary>
                <div style={{ padding: '0 16px 16px 16px', fontSize: '11px', color: '#aaa' }}>
                  {/* The Game */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ color: '#ffd700', fontWeight: 'bold', marginBottom: '6px' }}>🎮 THE GAME</div>
                    <div style={{ color: '#888', lineHeight: '1.5' }}>
                      Every week, traders compete for a prize pool funded by 20% of all trading fees. 
                      Trade big, escape rugs, and climb the leaderboard!
                    </div>
                  </div>
                  
                  {/* Schedule */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ color: '#ffd700', fontWeight: 'bold', marginBottom: '6px' }}>📅 SCHEDULE</div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ backgroundColor: '#0a0a0f', padding: '8px 12px', borderRadius: '6px', border: '1px solid #333' }}>
                        <div style={{ color: '#00d26a', fontSize: '10px' }}>STARTS</div>
                        <div style={{ color: '#fff', fontWeight: 'bold' }}>Monday 00:00 UTC</div>
                      </div>
                      <div style={{ backgroundColor: '#0a0a0f', padding: '8px 12px', borderRadius: '6px', border: '1px solid #333' }}>
                        <div style={{ color: '#ff3b69', fontSize: '10px' }}>ENDS</div>
                        <div style={{ color: '#fff', fontWeight: 'bold' }}>Sunday 23:59 UTC</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Scoring */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ color: '#ffd700', fontWeight: 'bold', marginBottom: '6px' }}>📊 SCORING</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#00d26a', fontWeight: 'bold', width: '80px' }}>Base</span>
                        <span>1 point per $10K volume traded</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#00d4aa', fontWeight: 'bold', width: '80px' }}>🛡️ Escape</span>
                        <span>+10% bonus per profitable exit</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#ff3b69', fontWeight: 'bold', width: '80px' }}>💀 Rug</span>
                        <span>-5% penalty per rug</span>
                      </div>
                    </div>
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#0a0a0f', borderRadius: '6px', border: '1px solid #333', fontFamily: 'monospace', fontSize: '10px' }}>
                      Score = (Volume ÷ 10K) × (1 + Escapes×0.10 - Rugs×0.05)
                    </div>
                  </div>
                  
                  {/* Prizes */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ color: '#ffd700', fontWeight: 'bold', marginBottom: '6px' }}>💰 PRIZES</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ backgroundColor: 'rgba(255,215,0,0.1)', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ffd700', textAlign: 'center' }}>
                        <div style={{ fontSize: '18px' }}>🥇</div>
                        <div style={{ color: '#ffd700', fontWeight: 'bold' }}>50%</div>
                      </div>
                      <div style={{ backgroundColor: 'rgba(192,192,192,0.1)', padding: '10px 14px', borderRadius: '8px', border: '1px solid #c0c0c0', textAlign: 'center' }}>
                        <div style={{ fontSize: '18px' }}>🥈</div>
                        <div style={{ color: '#c0c0c0', fontWeight: 'bold' }}>30%</div>
                      </div>
                      <div style={{ backgroundColor: 'rgba(205,127,50,0.1)', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cd7f32', textAlign: 'center' }}>
                        <div style={{ fontSize: '18px' }}>🥉</div>
                        <div style={{ color: '#cd7f32', fontWeight: 'bold' }}>20%</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Requirements */}
                  <div style={{ backgroundColor: '#1a1508', padding: '10px 12px', borderRadius: '6px', border: '1px solid #f0b90b44' }}>
                    <div style={{ color: '#f0b90b', fontWeight: 'bold', marginBottom: '4px' }}>⚠️ REQUIREMENTS</div>
                    <div>• Minimum <strong style={{ color: '#fff' }}>10 trades</strong> to qualify for prizes</div>
                    <div>• Only trades made during the war week count</div>
                    <div>• Real BSV prizes paid to winners!</div>
                  </div>
                </div>
              </details>
              
              {tradingWarsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
              ) : !tradingWarsData?.war ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>No active war. Check back soon!</div>
              ) : (
                <>
                  {/* War Info */}
                  <div style={{ backgroundColor: 'rgba(255,215,0,0.1)', border: '1px solid #ffd70033', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '18px' }}>War #{tradingWarsData.war.warNumber}</div>
                        <div style={{ color: '#888', fontSize: '11px' }}>
                          {tradingWarsData.war.qualifiedCount} qualified • {tradingWarsData.war.participantCount} participants
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '20px' }}>{formatPB(tradingWarsData.war.prizePool)}</div>
                        <div style={{ color: '#888', fontSize: '10px' }}>~${tradingWarsData.war.prizePoolUSD?.toFixed(2)} Prize Pool</div>
                      </div>
                    </div>
                    
                    {/* Countdown */}
                    <div style={{ marginTop: '12px', textAlign: 'center', backgroundColor: '#0a0a0f', borderRadius: '8px', padding: '10px' }}>
                      <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>TIME REMAINING</div>
                      <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '18px' }}>
                        {(() => {
                          const ms = tradingWarsData.timeRemaining || 0;
                          const days = Math.floor(ms / 86400000);
                          const hours = Math.floor((ms % 86400000) / 3600000);
                          const mins = Math.floor((ms % 3600000) / 60000);
                          return `${days}d ${hours}h ${mins}m`;
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  {/* Your Stats */}
                  {tradingWarsData.myStats && (
                    <div style={{ backgroundColor: '#111', border: '1px solid #333', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                      <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>YOUR STATS</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', textAlign: 'center' }}>
                        <div>
                          <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '16px' }}>#{tradingWarsData.myStats.rank || '-'}</div>
                          <div style={{ color: '#666', fontSize: '9px' }}>RANK</div>
                        </div>
                        <div>
                          <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '16px' }}>{formatPB(tradingWarsData.myStats.volume || 0)}</div>
                          <div style={{ color: '#666', fontSize: '9px' }}>VOLUME</div>
                        </div>
                        <div>
                          <div style={{ color: '#00d4aa', fontWeight: 'bold', fontSize: '16px' }}>{tradingWarsData.myStats.escapes || 0} 🛡️</div>
                          <div style={{ color: '#666', fontSize: '9px' }}>ESCAPES</div>
                        </div>
                        <div>
                          <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '16px' }}>{tradingWarsData.myStats.score || 0}</div>
                          <div style={{ color: '#666', fontSize: '9px' }}>SCORE</div>
                        </div>
                      </div>
                      <div style={{ marginTop: '10px', textAlign: 'center' }}>
                        {tradingWarsData.myStats.qualified ? (
                          <span style={{ color: '#00d26a', fontSize: '11px' }}>✅ Qualified ({tradingWarsData.myStats.trades} trades)</span>
                        ) : (
                          <span style={{ color: '#ff3b69', fontSize: '11px' }}>❌ Need {10 - (tradingWarsData.myStats.trades || 0)} more trades to qualify</span>
                        )}
                      </div>
                      <div style={{ marginTop: '6px', textAlign: 'center', color: '#888', fontSize: '10px' }}>
                        🛡️ Escape = Sell at profit before rug • +10% score bonus per escape!
                      </div>
                    </div>
                  )}
                  
                  {/* Prize Breakdown */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                    <div style={{ flex: 1, backgroundColor: '#1a1508', border: '1px solid #ffd700', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px' }}>🥇</div>
                      <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '14px' }}>{formatPB(Math.floor(tradingWarsData.war.prizePool * 0.5))}</div>
                      <div style={{ color: '#888', fontSize: '10px' }}>50%</div>
                    </div>
                    <div style={{ flex: 1, backgroundColor: '#111', border: '1px solid #c0c0c0', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px' }}>🥈</div>
                      <div style={{ color: '#c0c0c0', fontWeight: 'bold', fontSize: '14px' }}>{formatPB(Math.floor(tradingWarsData.war.prizePool * 0.3))}</div>
                      <div style={{ color: '#888', fontSize: '10px' }}>30%</div>
                    </div>
                    <div style={{ flex: 1, backgroundColor: '#0f0a08', border: '1px solid #cd7f32', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px' }}>🥉</div>
                      <div style={{ color: '#cd7f32', fontWeight: 'bold', fontSize: '14px' }}>{formatPB(Math.floor(tradingWarsData.war.prizePool * 0.2))}</div>
                      <div style={{ color: '#888', fontSize: '10px' }}>20%</div>
                    </div>
                  </div>
                  
                  {/* Leaderboard */}
                  <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>LEADERBOARD (Min 10 trades to qualify)</div>
                  <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                    {tradingWarsData.leaderboard?.length > 0 ? (
                      tradingWarsData.leaderboard.map((player, i) => (
                        <div 
                          key={player.handle}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            backgroundColor: player.handle === handcashUser?.handle?.replace('$', '') ? 'rgba(255,215,0,0.15)' : i % 2 === 0 ? '#111' : '#0a0a0f',
                            borderRadius: '6px',
                            marginBottom: '4px',
                            border: player.handle === handcashUser?.handle?.replace('$', '') ? '1px solid #ffd700' : '1px solid transparent',
                            opacity: player.qualified ? 1 : 0.5
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ 
                              width: '24px', 
                              textAlign: 'center',
                              color: i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : '#666',
                              fontWeight: 'bold',
                              fontSize: '14px'
                            }}>
                              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                            </span>
                            <div>
                              <div style={{ color: player.handle === handcashUser?.handle?.replace('$', '') ? '#ffd700' : '#fff', fontWeight: 'bold', fontSize: '13px' }}>
                                ${player.handle}
                                {!player.qualified && <span style={{ color: '#ff3b69', fontSize: '10px', marginLeft: '6px' }}>⏳</span>}
                              </div>
                              <div style={{ color: '#888', fontSize: '10px' }}>
                                {player.trades} trades • {player.escapes} 🛡️ escapes
                              </div>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '14px' }}>{player.score} pts</div>
                            <div style={{ color: '#666', fontSize: '10px' }}>{formatPB(player.volume)} vol</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>No participants yet. Be the first!</div>
                    )}
                  </div>
                  
                  {/* Refresh Button */}
                  <button
                    onClick={() => fetchTradingWars()}
                    style={{
                      width: '100%',
                      marginTop: '16px',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #ffd70033',
                      backgroundColor: '#1a1508',
                      color: '#ffd700',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    🔄 Refresh Leaderboard
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ✉️ INSCRIPTIONS MODAL - Create OP_RETURN messages */}
        {showInscriptions && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #00d26a', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#00d26a', fontWeight: '700' }}>✉️ On-Chain Inscriptions</h2>
                  <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>Write your message on the BSV blockchain forever!</div>
                </div>
                <button onClick={() => setShowInscriptions(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Cost Info */}
              <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #00d26a33', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '12px' }}>⛓️ Permanent Blockchain Record</div>
                    <div style={{ color: '#666', fontSize: '10px', marginTop: '2px' }}>Your message stored in OP_RETURN on BSV</div>
                    <div style={{ color: '#f0b90b', fontSize: '9px', marginTop: '4px' }}>💳 Paid from your HandCash wallet</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '16px' }}>100K sats</div>
                    <div style={{ color: '#666', fontSize: '9px' }}>~${((100000 * (bsvPrice || 20)) / 100000000).toFixed(2)} USD</div>
                  </div>
                </div>
              </div>
              
              {/* HandCash Balance Warning */}
              {mainWallet < 100000 && (
                <div style={{ backgroundColor: '#1a0f0f', border: '1px solid #ff3b69', borderRadius: '8px', padding: '10px', marginBottom: '16px', fontSize: '11px', color: '#ff3b69' }}>
                  ⚠️ Your HandCash balance ({formatSats(mainWallet)}) is less than 100K sats. Top up your HandCash wallet to inscribe.
                </div>
              )}
              
              {/* Message Input */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label style={{ color: '#888', fontSize: '11px', fontWeight: 'bold' }}>YOUR MESSAGE</label>
                  <span style={{ color: inscriptionMessage.length > 180 ? '#ff3b69' : '#666', fontSize: '10px' }}>
                    {inscriptionMessage.length}/200
                  </span>
                </div>
                <textarea
                  value={inscriptionMessage}
                  onChange={(e) => setInscriptionMessage(e.target.value.slice(0, 200))}
                  placeholder="Write something that will live on the blockchain forever..."
                  style={{
                    width: '100%',
                    height: '100px',
                    backgroundColor: '#111',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    padding: '12px',
                    color: '#fff',
                    fontSize: '16px',
                    resize: 'none',
                    fontFamily: 'inherit',
                  }}
                />
              </div>
              
              {/* Inscribe Button */}
              <button
                onClick={async () => {
                  if (!inscriptionMessage.trim()) return;
                  if (mainWallet < 100000) {
                    alert('Insufficient HandCash balance. Need 100K sats.');
                    return;
                  }
                  
                  // Confirm before paying - show USD value
                  const usdValue = ((100000 * (bsvPrice || 20)) / 100000000).toFixed(2);
                  if (!confirm(`This will pay 100,000 sats (~$${usdValue} USD) from your HandCash wallet to inscribe your message on-chain. Continue?`)) {
                    return;
                  }
                  
                  setInscribing(true);
                  try {
                    const res = await fetch(`${API_URL}/api/inscribe`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json',
                        'X-Session-Id': sessionId 
                      },
                      body: JSON.stringify({ message: inscriptionMessage.trim() })
                    });
                    const data = await res.json();
                    
                    if (data.success) {
                      Sounds.success();
                      setInscriptionMessage('');
                      // Update HandCash balance (deduct 100K)
                      setMainWallet(prev => Math.max(0, prev - 100000));
                      // Open WhatsOnChain
                      if (data.inscription?.txUrl) {
                        window.open(data.inscription.txUrl, '_blank');
                      }
                      alert('✅ Inscription successful! Your message is now on the blockchain forever!');
                      // Refresh inscriptions list
                      fetchInscriptions();
                    } else {
                      alert(`❌ ${data.error}`);
                    }
                  } catch (e) {
                    alert(`Error: ${e.message}`);
                  }
                  setInscribing(false);
                }}
                disabled={inscribing || !inscriptionMessage.trim() || mainWallet < 100000}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: !inscriptionMessage.trim() || mainWallet < 100000 ? '#333' : '#00d26a',
                  color: !inscriptionMessage.trim() || mainWallet < 100000 ? '#666' : '#000',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  cursor: !inscriptionMessage.trim() || mainWallet < 100000 ? 'not-allowed' : 'pointer',
                  marginBottom: '20px',
                }}
              >
                {inscribing ? '⏳ Processing payment...' : mainWallet < 100000 ? 'Need 100K sats in HandCash' : '💳 Pay 100K sats to Inscribe'}
              </button>
              
              {/* Recent Inscriptions */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold' }}>RECENT INSCRIPTIONS</div>
                  <button 
                    onClick={fetchInscriptions}
                    style={{ background: 'none', border: 'none', color: '#00d26a', fontSize: '10px', cursor: 'pointer' }}
                  >
                    🔄 Refresh
                  </button>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflow: 'auto' }}>
                  {inscriptions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontSize: '12px' }}>
                      No inscriptions yet. Be the first!
                    </div>
                  ) : inscriptions.slice(0, 20).map(insc => (
                    <div 
                      key={insc.id}
                      style={{
                        backgroundColor: '#111',
                        border: '1px solid #222',
                        borderRadius: '8px',
                        padding: '10px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                        <span style={{ color: '#00d26a', fontSize: '11px', fontWeight: 'bold' }}>${insc.handle}</span>
                        <a 
                          href={insc.txUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#666', fontSize: '9px', textDecoration: 'none' }}
                        >
                          ⛓️ TX ↗
                        </a>
                      </div>
                      <div style={{ color: '#ccc', fontSize: '12px', wordBreak: 'break-word' }}>
                        {insc.message}
                      </div>
                      <div style={{ color: '#444', fontSize: '9px', marginTop: '6px' }}>
                        {new Date(insc.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🎵 GLOBAL MUSIC STATION SELECTOR */}
        {showMusicSelector && (
          <div 
            onClick={() => setShowMusicSelector(false)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              padding: '80px 20px 20px 20px',
              zIndex: 2500,
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#0d0d12',
                border: '2px solid #9b59b6',
                borderRadius: '16px',
                padding: '16px',
                width: '280px',
                boxShadow: '0 20px 60px rgba(155, 89, 182, 0.4)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ color: '#9b59b6', fontSize: '14px', fontWeight: 'bold' }}>
                  🎵 MUSIC STATION
                </div>
                <button 
                  onClick={() => setShowMusicSelector(false)}
                  style={{ background: 'none', border: 'none', color: '#666', fontSize: '18px', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>
              
              {/* Station List */}
              {Object.entries(Music.getStations() || {}).map(([id, station]) => {
                if (!station) return null;
                const hasNoTracks = id !== 'off' && (!station.tracks || station.tracks.length === 0);
                return (
                <button
                  key={id}
                  onClick={() => {
                    if (hasNoTracks) return; // Don't allow selecting empty stations
                    Music.setStation(id);
                    setCurrentStation(id);
                    if (id !== 'off') {
                      setMusicEnabled(true);
                      Music.play();
                    } else {
                      // Explicitly stop music
                      Music.stop();
                      setMusicEnabled(false);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    marginBottom: '8px',
                    borderRadius: '10px',
                    border: currentStation === id ? '2px solid #9b59b6' : '1px solid #333',
                    backgroundColor: id === 'off' 
                      ? (currentStation === id ? '#1a0a0a' : '#111')
                      : (currentStation === id ? '#1a0f1f' : '#111'),
                    color: currentStation === id ? '#fff' : '#888',
                    fontSize: '13px',
                    fontWeight: currentStation === id ? 'bold' : 'normal',
                    cursor: hasNoTracks ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s',
                    opacity: hasNoTracks ? 0.4 : 1,
                  }}
                >
                  <div>
                    <div style={{ color: currentStation === id ? '#9b59b6' : '#888' }}>{station.name}</div>
                    <div style={{ fontSize: '10px', color: '#555', marginTop: '3px' }}>
                      {hasNoTracks ? '(No tracks configured)' : station.description}
                    </div>
                  </div>
                  {currentStation === id && <span style={{ color: '#9b59b6' }}>✓</span>}
                </button>
              );
              })}
              
              {/* Volume Slider */}
              {currentStation !== 'off' && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #333' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ color: '#888', fontSize: '11px' }}>🔊 Volume</span>
                    <span style={{ color: '#9b59b6', fontSize: '11px', fontWeight: 'bold' }}>{Math.round(musicVolume * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={musicVolume * 100}
                    onChange={(e) => {
                      const vol = parseInt(e.target.value) / 100;
                      setMusicVolume(vol);
                      Music.setVolume(vol);
                    }}
                    style={{ width: '100%', cursor: 'pointer', accentColor: '#9b59b6' }}
                  />
                </div>
              )}
              
              {/* Skip Button */}
              {currentStation !== 'off' && musicEnabled && (
                <button
                  onClick={() => Music.skip()}
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '12px',
                    borderRadius: '8px',
                    border: '1px solid #555',
                    backgroundColor: '#1a1a2e',
                    color: '#888',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  ⏭️ Skip Track
                </button>
              )}
              
              {/* Now Playing */}
              {currentStation !== 'off' && musicEnabled && (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '10px', 
                  backgroundColor: '#0a0a0f', 
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#555', fontSize: '9px', marginBottom: '4px' }}>NOW PLAYING</div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '6px'
                  }}>
                    <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '12px' }}>
                      <div style={{ width: '3px', backgroundColor: '#9b59b6', animation: 'soundBar1 0.4s infinite', height: '4px', borderRadius: '1px' }}/>
                      <div style={{ width: '3px', backgroundColor: '#9b59b6', animation: 'soundBar2 0.4s infinite', height: '10px', borderRadius: '1px' }}/>
                      <div style={{ width: '3px', backgroundColor: '#9b59b6', animation: 'soundBar3 0.4s infinite', height: '6px', borderRadius: '1px' }}/>
                      <div style={{ width: '3px', backgroundColor: '#9b59b6', animation: 'soundBar1 0.4s infinite', height: '8px', borderRadius: '1px' }}/>
                    </div>
                    <span style={{ color: '#9b59b6', fontSize: '11px' }}>{Music.getStations()[currentStation]?.name}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 🎖️ NEW ACHIEVEMENT POPUP */}
        {newAchievement && (
          <div style={{ 
            position: 'fixed', 
            top: '20px', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            zIndex: 3000,
            animation: 'slideDown 0.5s ease-out'
          }}>
            <div style={{ 
              backgroundColor: '#111', 
              border: '2px solid #f0b90b', 
              borderRadius: '12px', 
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 0 30px rgba(240,185,11,0.3)'
            }}>
              <div style={{ fontSize: '40px' }}>{newAchievement.icon}</div>
              <div>
                <div style={{ color: '#f0b90b', fontSize: '10px', fontWeight: 'bold' }}>🎖️ ACHIEVEMENT UNLOCKED</div>
                <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>{newAchievement.name}</div>
                <div style={{ color: '#666', fontSize: '11px' }}>{newAchievement.desc}</div>
              </div>
            </div>
            <style>{`
              @keyframes slideDown {
                from { transform: translateX(-50%) translateY(-100px); opacity: 0; }
                to { transform: translateX(-50%) translateY(0); opacity: 1; }
              }
            `}</style>
          </div>
        )}

        {/* 🎯 Challenge Complete Toast */}
        {challengeToast && (
          <div style={{ 
            position: 'fixed', 
            top: '20px', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            zIndex: 3000,
            animation: 'slideDown 0.5s ease-out'
          }}>
            <div style={{ 
              backgroundColor: '#1a1508', 
              border: '2px solid #f0b90b', 
              borderRadius: '12px', 
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 0 30px rgba(240,185,11,0.4)',
              cursor: 'pointer'
            }}
            onClick={() => { setChallengeToast(null); setShowChallenges(true); }}
            >
              <div style={{ fontSize: '36px' }}>🎯</div>
              <div>
                <div style={{ color: '#f0b90b', fontSize: '10px', fontWeight: 'bold' }}>CHALLENGE COMPLETE!</div>
                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{challengeToast.name}</div>
                <div style={{ color: '#00d26a', fontSize: '12px' }}>+{challengeToast.reward} sats ready to claim!</div>
              </div>
            </div>
          </div>
        )}

        {/* 📖 TRADING GUIDE MODAL */}
        {showTradingGuide && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '16px', padding: '24px', maxWidth: '700px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#00a8ff' }}>📖 Trading Guide</h2>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '11px' }}>Master the market before it masters you</p>
                </div>
                <button onClick={() => setShowTradingGuide(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Guide Sections */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {Object.entries(TRADING_GUIDE).map(([key, section]) => (
                  <div key={key} style={{ backgroundColor: '#111', borderRadius: '10px', padding: '16px', border: '1px solid #1a1a2e' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '24px' }}>{section.icon}</span>
                      <h3 style={{ margin: 0, color: '#fff', fontSize: '16px' }}>{section.title}</h3>
                    </div>
                    {section.desc && (
                      <p style={{ color: '#f0b90b', fontSize: '11px', margin: '0 0 12px 0', fontStyle: 'italic' }}>{section.desc}</p>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {section.items.map((item, i) => (
                        <div key={i} style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
                          <span style={{ color: '#00a8ff', fontWeight: 'bold', minWidth: '120px' }}>{item.label}</span>
                          <span style={{ color: '#888' }}>{item.desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Bottom tip */}
              <div style={{ backgroundColor: '#0a1520', border: '1px solid #00a8ff33', borderRadius: '8px', padding: '12px', marginTop: '16px', textAlign: 'center' }}>
                <div style={{ color: '#00a8ff', fontWeight: 'bold', marginBottom: '4px' }}>💡 Remember</div>
                <div style={{ color: '#888', fontSize: '11px' }}>The game doesn't tell you if a token will rug. The chart does. Learn to read it!</div>
              </div>
            </div>
          </div>
        )}

        {/* 📚 RUG ENCYCLOPEDIA MODAL */}
        {showRugEncyclopedia && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '16px', padding: '24px', maxWidth: '700px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#ff3b69' }}>📚 Rug Encyclopedia</h2>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '11px' }}>Learn to identify scams before they get you</p>
                </div>
                <button onClick={() => { setShowRugEncyclopedia(false); setSelectedRug(null); }} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {!selectedRug ? (
                <>
                  {/* Rug Type Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(140px, 1fr))' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: isMobile ? '8px' : '12px', marginBottom: '20px' }}>
                    {Object.entries(RUG_ENCYCLOPEDIA).map(([key, rug]) => (
                      <div 
                        key={key} 
                        onClick={() => setSelectedRug(key)}
                        style={{ 
                          backgroundColor: '#111', 
                          border: `1px solid ${rug.color}33`,
                          borderRadius: '10px', 
                          padding: '16px', 
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div style={{ fontSize: '32px' }}>{rug.icon}</div>
                          <div style={{ 
                            padding: '2px 6px', 
                            borderRadius: '4px', 
                            fontSize: '9px', 
                            fontWeight: 'bold',
                            backgroundColor: `${rug.color}22`,
                            color: rug.color
                          }}>
                            {rug.severity}
                          </div>
                        </div>
                        <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>{rug.name}</div>
                        <div style={{ color: rug.color, fontSize: '11px', fontStyle: 'italic', marginBottom: '8px' }}>"{rug.tagline}"</div>
                        <div style={{ color: '#666', fontSize: '10px', lineHeight: '1.4' }}>{rug.description.substring(0, 80)}...</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '9px', color: '#555' }}>
                          <span>👥 {rug.victimCount} victims</span>
                          <span>💸 {rug.totalStolen} stolen</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div style={{ backgroundColor: '#1a0d0d', border: '1px solid #2f0f0f', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ color: '#ff3b69', fontWeight: 'bold', marginBottom: '4px' }}>⚠️ Real Talk</div>
                    <div style={{ color: '#888', fontSize: '11px' }}>These aren't just game mechanics. These are REAL tactics that have stolen BILLIONS from crypto investors. Learn them here so you don't learn them the hard way.</div>
                  </div>
                </>
              ) : (
                <>
                  {/* Detailed Rug View */}
                  {(() => {
                    const rug = RUG_ENCYCLOPEDIA[selectedRug];
                    return (
                      <div>
                        <button onClick={() => setSelectedRug(null)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '12px', cursor: 'pointer', marginBottom: '16px' }}>← Back to all rugs</button>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                          <div style={{ fontSize: '64px' }}>{rug.icon}</div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <h3 style={{ margin: 0, color: '#fff', fontSize: '24px' }}>{rug.name}</h3>
                              <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', backgroundColor: `${rug.color}22`, color: rug.color }}>{rug.severity}</span>
                            </div>
                            <div style={{ color: rug.color, fontSize: '14px', fontStyle: 'italic', marginTop: '4px' }}>"{rug.tagline}"</div>
                          </div>
                        </div>
                        
                        <div style={{ color: '#ccc', fontSize: '13px', lineHeight: '1.6', marginBottom: '20px' }}>{rug.description}</div>
                        
                        {/* Real World Example */}
                        <div style={{ backgroundColor: '#1a1508', border: '1px solid #3d2e0a', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                          <div style={{ color: '#f0b90b', fontWeight: 'bold', fontSize: '12px', marginBottom: '6px' }}>📰 Real World Example</div>
                          <div style={{ color: '#888', fontSize: '12px' }}>{rug.realWorldExample}</div>
                        </div>
                        
                        {/* How It Works */}
                        <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                          <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '12px', marginBottom: '10px' }}>🔧 How It Works</div>
                          {rug.howItWorks.map((step, i) => (
                            <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', fontSize: '11px' }}>
                              <span style={{ color: rug.color, fontWeight: 'bold' }}>{i + 1}.</span>
                              <span style={{ color: '#888' }}>{step}</span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Warning Signs */}
                        <div style={{ backgroundColor: '#1a0d0d', border: '1px solid #2f0f0f', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                          <div style={{ color: '#ff3b69', fontWeight: 'bold', fontSize: '12px', marginBottom: '10px' }}>🚩 Warning Signs (IRL)</div>
                          {rug.warningSignsReal.map((sign, i) => (
                            <div key={i} style={{ color: '#ff6b6b', fontSize: '11px', marginBottom: '6px' }}>{sign}</div>
                          ))}
                        </div>
                        
                        {/* How To Protect */}
                        <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #0f2f0f', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                          <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '12px', marginBottom: '10px' }}>🛡️ How To Protect Yourself</div>
                          {rug.howToProtect.map((tip, i) => (
                            <div key={i} style={{ color: '#00ff88', fontSize: '11px', marginBottom: '6px' }}>{tip}</div>
                          ))}
                        </div>
                        
                        {/* In-Game Tip */}
                        <div style={{ backgroundColor: '#0d0d1a', border: '1px solid #0f0f2f', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                          <div style={{ color: '#7b68ee', fontWeight: 'bold', fontSize: '12px', marginBottom: '6px' }}>🎮 In Paper Hands</div>
                          <div style={{ color: '#a0a0ff', fontSize: '11px' }}>{rug.inGameTip}</div>
                        </div>
                        
                        {/* Stats */}
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{ flex: 1, backgroundColor: '#111', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ color: '#ff3b69', fontSize: '20px', fontWeight: 'bold' }}>{rug.victimCount}</div>
                            <div style={{ color: '#555', fontSize: '10px' }}>Estimated Victims</div>
                          </div>
                          <div style={{ flex: 1, backgroundColor: '#111', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <div style={{ color: '#ff3b69', fontSize: '20px', fontWeight: 'bold' }}>{rug.totalStolen}</div>
                            <div style={{ color: '#555', fontSize: '10px' }}>Total Stolen</div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        )}

        {/* 🛡️ UNRUGGABLE LEADERBOARD */}
        {showUnruggable && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
            <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #00d26a33', borderRadius: '16px', padding: '24px', maxWidth: '600px', width: '95%', maxHeight: '85vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#00d26a' }}>🛡️ The Unruggables</h2>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '11px' }}>Who in the world has the highest survival rate?</p>
                </div>
                <button onClick={() => setShowUnruggable(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#1a1a2e', color: '#888', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              
              {/* Your Stats Card */}
              <div style={{ backgroundColor: '#0d1a0d', border: '1px solid #0f2f0f', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ color: '#00d26a', fontWeight: 'bold' }}>📊 Your Survival Profile</div>
                  <div style={{ 
                    padding: '4px 10px', 
                    borderRadius: '12px', 
                    backgroundColor: stats.survivalRate >= 80 ? '#00d26a22' : stats.survivalRate >= 50 ? '#f0b90b22' : '#ff3b6922',
                    color: stats.survivalRate >= 80 ? '#00d26a' : stats.survivalRate >= 50 ? '#f0b90b' : '#ff3b69',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {stats.survivalRate.toFixed(1)}% Survival
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>{stats.totalTrades}</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>TRADES</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#00d26a', fontSize: '20px', fontWeight: 'bold' }}>{stats.wins}</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>WINS</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#ff3b69', fontSize: '20px', fontWeight: 'bold' }}>{stats.rugs}</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>RUGGED</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold' }}>{stats.rugsAvoided}</div>
                    <div style={{ color: '#555', fontSize: '9px' }}>AVOIDED</div>
                  </div>
                </div>
                
                {/* Weakness Analysis */}
                {stats.rugs > 0 && (
                  <div style={{ backgroundColor: '#1a0d0d', border: '1px solid #2f0f0f', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                    <div style={{ color: '#ff3b69', fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>⚠️ YOUR WEAKNESS</div>
                    {(() => {
                      const weakness = getWeakness();
                      if (!weakness || !weakness.type) return <div style={{ color: '#666', fontSize: '11px' }}>Not enough data yet</div>;
                      const rugInfo = RUG_ENCYCLOPEDIA[weakness.type];
                      return (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <span style={{ fontSize: '24px' }}>{rugInfo?.icon || '💀'}</span>
                            <div>
                              <div style={{ color: '#ff6b6b', fontSize: '13px', fontWeight: 'bold' }}>
                                {weakness.type} Rugs - {weakness.percent.toFixed(0)}% of your rugs
                              </div>
                              <div style={{ color: '#888', fontSize: '10px' }}>
                                {weakness.times} times, lost {formatPB(weakness.lost)} total
                              </div>
                            </div>
                          </div>
                          <div style={{ color: '#666', fontSize: '10px', marginTop: '8px', fontStyle: 'italic' }}>
                            💡 Tip: {rugInfo?.howToProtect?.[0] || 'Study this rug type in the encyclopedia'}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
                
                {/* Rug breakdown by type */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  {Object.entries(stats.rugsByType).map(([type, data]) => {
                    const rugInfo = RUG_ENCYCLOPEDIA[type];
                    return (
                      <div key={type} style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '20px' }}>{rugInfo?.icon || '💀'}</div>
                        <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{data.times}</div>
                        <div style={{ color: '#555', fontSize: '8px' }}>{type}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Global Leaderboard */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#888', fontSize: '11px', marginBottom: '10px' }}>🌍 GLOBAL UNRUGGABLE RANKING</div>
                
                {unruggableLeaderboard.map((player, i) => (
                  <div key={player.rank} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px', 
                    padding: '12px', 
                    backgroundColor: player.isPremium ? '#1a1508' : (i < 3 ? '#0d1a0d' : '#111'), 
                    border: player.isPremium 
                      ? '1px solid #f0b90b44' 
                      : (i < 3 ? '1px solid #0f2f0f' : '1px solid #1a1a2e'),
                    borderRadius: '8px', 
                    marginBottom: '8px',
                    boxShadow: player.isPremium ? '0 0 15px rgba(240,185,11,0.15), inset 0 0 10px rgba(240,185,11,0.03)' : 'none',
                  }}>
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%', 
                      backgroundColor: i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : '#333',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: i < 3 ? '#000' : '#888',
                      fontWeight: 'bold',
                      fontSize: '12px'
                    }}>
                      {i < 3 ? ['🥇', '🥈', '🥉'][i] : player.rank}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: player.isPremium ? '#f0b90b' : '#fff', fontWeight: 'bold', fontSize: '13px' }}>
                          {player.isPremium && '👑 '}{player.handle?.startsWith('$') ? player.handle : `$${player.handle}`}
                        </span>
                      </div>
                      <div style={{ color: '#555', fontSize: '10px' }}>
                        {player.trades} trades • {player.rugs || 0} rugs • 🔥{player.streak || 0} streak
                      </div>
                    </div>
                    
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ 
                        color: '#00d26a', 
                        fontWeight: 'bold', 
                        fontSize: '16px' 
                      }}>
                        {player.survivalRate}%
                      </div>
                      <div style={{ color: '#555', fontSize: '9px' }}>survival</div>
                    </div>
                    
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: player.profit >= 0 ? '#00d26a' : '#ff3b69', fontWeight: 'bold', fontSize: '12px' }}>
                        {player.profit >= 0 ? '+' : ''}{formatPB(player.profit)}
                      </div>
                      <div style={{ color: '#555', fontSize: '9px' }}>profit</div>
                    </div>
                  </div>
                ))}
                
                {unruggableLeaderboard.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#555', padding: '30px', fontSize: '12px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>🛡️</div>
                    No players with 10+ trades yet<br/>
                    <span style={{ color: '#00d26a' }}>Be the first!</span>
                  </div>
                )}
              </div>
              
              {/* Call to action */}
              <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                <div style={{ color: '#00d26a', fontWeight: 'bold', marginBottom: '4px' }}>🎯 Can you become Unruggable?</div>
                <div style={{ color: '#666', fontSize: '11px', marginBottom: '12px' }}>
                  Trade smarter, spot the signs, and climb the global rankings
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button onClick={() => { setShowUnruggable(false); setShowRugEncyclopedia(true); }} style={{ padding: '10px 16px', borderRadius: '6px', border: '1px solid #ff3b69', backgroundColor: 'transparent', color: '#ff3b69', fontSize: '11px', cursor: 'pointer' }}>
                    📚 Study Rug Types
                  </button>
                  <button onClick={() => setShowUnruggable(false)} style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#00d26a', color: '#000', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}>
                    🎮 Start Trading
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🐸 SMUG PEPE CELEBRATION */}
        {showPepeCelebration && (
          <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2000, overflow: 'hidden' }}>
            {/* Dark overlay */}
            <div style={{ 
              position: 'absolute', 
              inset: 0, 
              backgroundColor: 'rgba(0,0,0,0.7)',
              animation: 'fadeIn 0.3s ease-out'
            }} />
            
            {/* Smug Pepe sliding in from right */}
            <div style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              animation: 'slideInPepe 0.5s ease-out forwards, pepeFloat 2s ease-in-out infinite 0.5s',
              fontSize: '150px',
              filter: 'drop-shadow(0 0 30px rgba(0,255,100,0.5))'
            }}>
              🐸
            </div>
            
            {/* Premium unlocked text */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '35%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
              animation: 'popIn 0.4s ease-out 0.3s both'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>💎</div>
              <div style={{ 
                fontSize: '32px', 
                fontWeight: '900', 
                color: '#f0b90b',
                textShadow: '0 0 20px rgba(240,185,11,0.5)'
              }}>
                PREMIUM UNLOCKED
              </div>
              <div style={{ 
                fontSize: '16px', 
                color: '#888',
                marginTop: '12px'
              }}>
                Welcome to the inner circle, fren
              </div>
            </div>
            
            {/* Benefits list */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '55%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
              animation: 'popIn 0.4s ease-out 0.6s both'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px', color: '#00d26a' }}>
                <span>✅ +10% Profit Boost</span>
                <span>✅ 88% Accurate Alpha Hints</span>
                <span>✅ Moonshot Access (85% Safe)</span>
                <span>✅ Daily Free Plays</span>
                <span>✅ 5% Weekly Rakeback</span>
              </div>
            </div>
            
            {/* Confetti/sparkles */}
            {[...Array(20)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                left: `${Math.random() * 100}%`,
                top: '-20px',
                fontSize: `${12 + Math.random() * 16}px`,
                animation: `confettiFall ${2 + Math.random() * 2}s linear ${Math.random() * 0.5}s infinite`,
                opacity: 0.8
              }}>
                {['✨', '💎', '🚀', '💰', '⭐'][Math.floor(Math.random() * 5)]}
              </div>
            ))}
            
            {/* CSS Animations */}
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideInPepe {
                from { 
                  transform: translateY(-50%) translateX(100%);
                  opacity: 0;
                }
                to { 
                  transform: translateY(-50%) translateX(-20px);
                  opacity: 1;
                }
              }
              @keyframes pepeFloat {
                0%, 100% { transform: translateY(-50%) translateX(-20px) rotate(-5deg); }
                50% { transform: translateY(-55%) translateX(-20px) rotate(5deg); }
              }
              @keyframes popIn {
                from { 
                  transform: translateX(-50%) scale(0.5);
                  opacity: 0;
                }
                to { 
                  transform: translateX(-50%) scale(1);
                  opacity: 1;
                }
              }
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
              @keyframes confettiFall {
                0% { 
                  transform: translateY(0) rotate(0deg);
                  opacity: 1;
                }
                100% { 
                  transform: translateY(100vh) rotate(720deg);
                  opacity: 0;
                }
              }
              @keyframes pulse {
                0%, 100% { 
                  box-shadow: 0 0 12px rgba(155, 89, 182, 0.8);
                  transform: scale(1);
                }
                50% { 
                  box-shadow: 0 0 20px rgba(155, 89, 182, 1), 0 0 30px rgba(155, 89, 182, 0.6);
                  transform: scale(1.05);
                }
              }
            `}</style>
          </div>
        )}
        
        {/* 🐸 ESCAPE PEPE CELEBRATION - Avoided a rug! */}
        {showEscapePepe && (
          <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2000, overflow: 'hidden' }}>
            {/* Dark overlay with green tint */}
            <div style={{ 
              position: 'absolute', 
              inset: 0, 
              backgroundColor: 'rgba(0,20,0,0.85)',
              animation: 'fadeIn 0.3s ease-out'
            }} />
            
            {/* Smug Pepe sliding in from right */}
            <div style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              animation: 'slideInPepe 0.5s ease-out forwards, pepeFloat 2s ease-in-out infinite 0.5s',
              fontSize: '150px',
              filter: 'drop-shadow(0 0 30px rgba(0,255,100,0.8))'
            }}>
              🐸
            </div>
            
            {/* Based messages */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '30%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
              animation: 'popIn 0.4s ease-out 0.3s both'
            }}>
              {(() => {
                const basedMessages = [
                  { title: 'BASED EXIT', subtitle: 'You saw the rug coming' },
                  { title: 'GIGABRAIN', subtitle: 'Sold before the dump' },
                  { title: 'ABSOLUTE UNIT', subtitle: 'Rug? What rug?' },
                  { title: 'CHAD MOVE', subtitle: 'Paper hands saved you' },
                  { title: 'BUILT DIFFERENT', subtitle: 'Your instincts are elite' },
                  { title: '200 IQ PLAY', subtitle: 'Dev is crying rn' },
                  { title: 'MATRIX DODGED', subtitle: 'Neo would be proud' },
                ];
                const msg = basedMessages[Math.floor(Math.random() * basedMessages.length)];
                return (
                  <>
                    <div style={{ fontSize: '48px', marginBottom: '8px' }}>🏃💨</div>
                    <div style={{ 
                      fontSize: '36px', 
                      fontWeight: '900', 
                      color: '#00ff88',
                      textShadow: '0 0 20px rgba(0,255,136,0.5)'
                    }}>
                      {msg.title}
                    </div>
                    <div style={{ 
                      fontSize: '18px', 
                      color: '#00d26a',
                      marginTop: '8px'
                    }}>
                      {msg.subtitle}
                    </div>
                  </>
                );
              })()}
            </div>
            
            {/* Escape stats */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '55%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
              animation: 'popIn 0.4s ease-out 0.6s both'
            }}>
              <div style={{ 
                padding: '16px 24px', 
                backgroundColor: 'rgba(0,255,100,0.1)', 
                borderRadius: '12px',
                border: '1px solid rgba(0,255,100,0.3)'
              }}>
                <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>
                  {showEscapePepe.tokenTicker} rugged {showEscapePepe.timeBefore}s after you sold
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: showEscapePepe.profit >= 0 ? '#00ff88' : '#ff6b00' }}>
                  {showEscapePepe.profit >= 0 ? '+' : ''}{formatPB(showEscapePepe.profit)}
                </div>
                <div style={{ fontSize: '12px', color: '#00d26a', marginTop: '8px' }}>
                  🏆 Escape Artist Achievement Unlocked!
                </div>
              </div>
            </div>
            
            {/* Confetti/sparkles */}
            {[...Array(25)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                left: `${Math.random() * 100}%`,
                top: '-20px',
                fontSize: `${14 + Math.random() * 18}px`,
                animation: `confettiFall ${2 + Math.random() * 2}s linear ${Math.random() * 0.5}s infinite`,
                opacity: 0.9
              }}>
                {['🏃', '💨', '🐸', '✨', '💚', '🧠'][Math.floor(Math.random() * 6)]}
              </div>
            ))}
            
            {/* CSS Animations */}
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideInPepe {
                from { 
                  transform: translateY(-50%) translateX(100%);
                  opacity: 0;
                }
                to { 
                  transform: translateY(-50%) translateX(-20px);
                  opacity: 1;
                }
              }
              @keyframes pepeFloat {
                0%, 100% { transform: translateY(-50%) translateX(-20px) rotate(-5deg); }
                50% { transform: translateY(-55%) translateX(-20px) rotate(5deg); }
              }
              @keyframes popIn {
                from { 
                  transform: translateX(-50%) scale(0.5);
                  opacity: 0;
                }
                to { 
                  transform: translateX(-50%) scale(1);
                  opacity: 1;
                }
              }
              @keyframes confettiFall {
                0% { 
                  transform: translateY(0) rotate(0deg);
                  opacity: 1;
                }
                100% { 
                  transform: translateY(100vh) rotate(720deg);
                  opacity: 0;
                }
              }
            `}</style>
          </div>
        )}
      </div>
      </div>
      
      {/* 🎮 PLAYER PORTAL MODAL */}
      {showPlayerPortal && (
        <div 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            backgroundColor: 'rgba(0,0,0,0.95)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 10003 
          }}
          onClick={() => setShowPlayerPortal(false)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#0a0a0f',
              border: '2px solid #00d26a',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '600px',
              width: '95%',
              maxHeight: '85vh',
              overflow: 'auto',
              fontFamily: 'monospace',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ color: '#00d26a', margin: 0, fontSize: '20px' }}>🎮 Player Portal</h2>
                <p style={{ color: '#666', margin: '4px 0 0 0', fontSize: '11px' }}>{handcashUser?.handle || 'Player'}</p>
              </div>
              <button 
                onClick={() => setShowPlayerPortal(false)}
                style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  border: '1px solid #ff3b69', backgroundColor: 'transparent',
                  color: '#ff3b69', fontSize: '16px', cursor: 'pointer'
                }}
              >✕</button>
            </div>
            
            {/* Trader Rating */}
            {(() => {
              const winRate = (stats.wins + stats.losses) > 0 ? (stats.wins / (stats.wins + stats.losses) * 100) : 0;
              const avgProfit = stats.totalTrades > 0 ? stats.totalProfit / stats.totalTrades : 0;
              const rugAvoidRate = (stats.totalTrades - stats.rugs) > 0 ? ((stats.totalTrades - stats.rugs) / stats.totalTrades * 100) : 100;
              
              let score = 0;
              score += Math.min(winRate * 0.4, 40);
              score += avgProfit > 0 ? Math.min(20, avgProfit / 10000) : Math.max(-20, avgProfit / 10000);
              score += Math.min(rugAvoidRate * 0.3, 30);
              score += stats.bestWinStreak * 2;
              score = Math.max(0, Math.min(100, score));
              
              let rating, ratingColor, ratingEmoji;
              if (score >= 80) { rating = 'ELITE'; ratingColor = '#f0b90b'; ratingEmoji = '🏆'; }
              else if (score >= 60) { rating = 'STRONG'; ratingColor = '#00d26a'; ratingEmoji = '💪'; }
              else if (score >= 40) { rating = 'AVERAGE'; ratingColor = '#888'; ratingEmoji = '😐'; }
              else if (score >= 20) { rating = 'WEAK'; ratingColor = '#ff8c00'; ratingEmoji = '😬'; }
              else { rating = 'PAPER HANDS'; ratingColor = '#ff3b69'; ratingEmoji = '🧻'; }
              
              return (
                <div style={{ 
                  backgroundColor: '#111', 
                  borderRadius: '12px', 
                  padding: '16px', 
                  marginBottom: '16px',
                  border: `2px solid ${ratingColor}`,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '40px', marginBottom: '8px' }}>{ratingEmoji}</div>
                  <div style={{ color: ratingColor, fontSize: '24px', fontWeight: 'bold' }}>{rating}</div>
                  <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>Trader Rating: {score.toFixed(0)}/100</div>
                  <div style={{ marginTop: '12px', height: '8px', backgroundColor: '#222', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${score}%`, height: '100%', backgroundColor: ratingColor, transition: 'width 0.5s' }}></div>
                  </div>
                </div>
              );
            })()}
            
            {/* Balance & Playthrough */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px' }}>
                <div style={{ color: '#666', fontSize: '10px' }}>💰 Total Balance</div>
                <div style={{ color: '#f0b90b', fontSize: '20px', fontWeight: 'bold' }}>{formatPB(gameWallet)}</div>
                <div style={{ color: '#888', fontSize: '9px' }}>{bsvPrice && `≈ $${((gameWallet / 100000000) * bsvPrice).toFixed(2)} USD`}</div>
              </div>
              <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '12px' }}>
                <div style={{ color: '#666', fontSize: '10px' }}>✅ Available</div>
                <div style={{ color: '#00d26a', fontSize: '20px', fontWeight: 'bold' }}>{formatPB(gameWallet - lockedBalance)}</div>
                <div style={{ color: '#888', fontSize: '9px' }}>Ready to withdraw</div>
              </div>
            </div>
            
            {/* Locked & Pending */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#1a0a0a', borderRadius: '8px', padding: '12px', border: '1px solid #ff8c0033' }}>
                <div style={{ color: '#ff8c00', fontSize: '10px' }}>🔒 Locked</div>
                <div style={{ color: '#ff8c00', fontSize: '18px', fontWeight: 'bold' }}>{formatPB(lockedBalance)}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Needs playthrough</div>
              </div>
              <div style={{ backgroundColor: '#0a0a1a', borderRadius: '8px', padding: '12px', border: '1px solid #3498db33' }}>
                <div style={{ color: '#3498db', fontSize: '10px' }}>⏳ Pending</div>
                <div style={{ color: '#3498db', fontSize: '18px', fontWeight: 'bold' }}>{pendingWithdrawals?.length || 0}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Withdrawal{pendingWithdrawals?.length !== 1 ? 's' : ''} in queue</div>
              </div>
            </div>
            
            {/* Deposit/Withdraw History */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#0a1a0a', borderRadius: '6px', padding: '10px', textAlign: 'center', border: '1px solid #00d26a22' }}>
                <div style={{ color: '#00d26a', fontSize: '16px', fontWeight: 'bold' }}>{formatPB(totalDeposited || 0)}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>📥 Deposited</div>
              </div>
              <div style={{ backgroundColor: '#1a0a0a', borderRadius: '6px', padding: '10px', textAlign: 'center', border: '1px solid #ff3b6922' }}>
                <div style={{ color: '#ff3b69', fontSize: '16px', fontWeight: 'bold' }}>{formatPB(totalWithdrawn || 0)}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>📤 Withdrawn</div>
              </div>
              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                <div style={{ color: '#a855f7', fontSize: '16px', fontWeight: 'bold' }}>{positions.filter(p => p.status === 'active').length}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>📊 Positions</div>
              </div>
            </div>
            
            {/* Playthrough Progress - Always show for clarity */}
            {(() => {
              const hasPlaythrough = (playerPortalData?.bonusRequired || 0) > 0;
              const hasLockedBalance = lockedBalance > 0;
              const showProgress = hasPlaythrough || hasLockedBalance;
              const displayLocked = hasPlaythrough ? (playerPortalData?.bonusBalance || 0) : lockedBalance;
              const displayRequired = playerPortalData?.bonusRequired || (lockedBalance * 3); // Default 3x if no bonusRequired
              const displayTraded = playerPortalData?.bonusPlaythrough || 0;
              const displayPercent = displayRequired > 0 ? Math.min(100, Math.floor((displayTraded / displayRequired) * 100)) : 0;
              const toGo = Math.max(0, displayRequired - displayTraded);

              return (
                <div style={{
                  backgroundColor: showProgress ? '#1a0a2a' : '#0a1a0a',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  border: `1px solid ${showProgress ? '#a855f7' : '#00d26a33'}`
                }}>
                  {showProgress ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ color: '#a855f7', fontSize: '11px', fontWeight: 'bold' }}>🎰 Playthrough Progress</div>
                        <div style={{ color: displayPercent >= 100 ? '#00d26a' : '#a855f7', fontSize: '14px', fontWeight: 'bold' }}>
                          {displayPercent >= 100 ? '✅ COMPLETE!' : `${displayPercent}%`}
                        </div>
                      </div>
                      <div style={{ height: '10px', backgroundColor: '#222', borderRadius: '5px', overflow: 'hidden', marginBottom: '10px' }}>
                        <div style={{
                          width: `${Math.min(100, displayPercent)}%`,
                          height: '100%',
                          backgroundColor: displayPercent >= 100 ? '#00d26a' : '#a855f7',
                          transition: 'width 0.5s',
                          boxShadow: displayPercent >= 100 ? '0 0 10px #00d26a' : 'none'
                        }}></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#ff8c00', fontSize: '14px', fontWeight: 'bold' }}>{formatPB(displayLocked)}</div>
                          <div style={{ color: '#666', fontSize: '8px' }}>🔒 Locked PvP</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#3498db', fontSize: '14px', fontWeight: 'bold' }}>{formatPB(displayTraded)}</div>
                          <div style={{ color: '#666', fontSize: '8px' }}>📈 Traded</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: displayPercent >= 100 ? '#00d26a' : '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>
                            {formatPB(toGo)}
                          </div>
                          <div style={{ color: '#666', fontSize: '8px' }}>🎯 To Go</div>
                        </div>
                      </div>
                      <div style={{ color: '#888', fontSize: '9px', textAlign: 'center', marginBottom: '6px' }}>
                        {displayPercent >= 100
                          ? '✅ All PvP profits unlocked and withdrawable!'
                          : `Trade ${formatPB(toGo)} more to unlock ${formatPB(displayLocked)}`}
                      </div>
                      <div style={{
                        backgroundColor: '#0a0a15',
                        borderRadius: '4px',
                        padding: '8px',
                        border: '1px solid #333',
                        marginTop: '4px'
                      }}>
                        <div style={{ color: '#666', fontSize: '8px', lineHeight: '1.4' }}>
                          <strong style={{ color: '#a855f7' }}>How it works:</strong> When you win PvP profits (trading against others),
                          that money is locked until you trade 3x the volume. You can still trade and HODL -
                          just keep playing normally and it unlocks automatically. This prevents instant cash-outs that would drain the house.
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ fontSize: '24px' }}>✅</div>
                      <div>
                        <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>No Playthrough Required</div>
                        <div style={{ color: '#666', fontSize: '9px' }}>All your balance is fully withdrawable. PvP profits lock until traded 3x.</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{stats.totalTrades}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Trades</div>
              </div>
              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{stats.wins}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Wins</div>
              </div>
              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                <div style={{ color: '#ff3b69', fontSize: '18px', fontWeight: 'bold' }}>{stats.losses}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Losses</div>
              </div>
              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                <div style={{ color: '#ff8c00', fontSize: '18px', fontWeight: 'bold' }}>{stats.rugs}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Rugged</div>
              </div>
            </div>

            {/* Bot Performance - only shows if tradingBotData exists */}
            {tradingBotData && (
              <div style={{
                backgroundColor: '#0d0612',
                border: '1px solid #a855f744',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <div style={{ color: '#a855f7', fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>
                  🤖 BOT PERFORMANCE
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                  <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{tradingBotData.stats?.totalTrades || 0}</div>
                    <div style={{ color: '#666', fontSize: '8px' }}>Trades</div>
                  </div>
                  <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                    <div style={{ color: (tradingBotData.stats?.netProfit || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>
                      {(tradingBotData.stats?.netProfit || 0) >= 0 ? '+' : ''}{formatPB(tradingBotData.stats?.netProfit || 0)}
                    </div>
                    <div style={{ color: '#666', fontSize: '8px' }}>Profit</div>
                  </div>
                  <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#f0b90b', fontSize: '14px', fontWeight: 'bold' }}>
                      {(tradingBotData.stats?.totalTrades || 0) > 0
                        ? Math.round(((tradingBotData.stats?.wins || 0) / tradingBotData.stats.totalTrades) * 100)
                        : 0}%
                    </div>
                    <div style={{ color: '#666', fontSize: '8px' }}>Win Rate</div>
                  </div>
                  <div style={{ backgroundColor: (tradingBotData.roi || 0) >= 0 ? '#0d1a0d' : '#1a0d0d', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                    <div style={{ color: (tradingBotData.roi || 0) >= 0 ? '#00d26a' : '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>
                      {(tradingBotData.roi || 0) >= 0 ? '+' : ''}{tradingBotData.roi || 0}%
                    </div>
                    <div style={{ color: '#666', fontSize: '8px' }}>ROI</div>
                  </div>
                </div>
              </div>
            )}

            {/* Win Rate & P/L */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                <div style={{ color: '#3498db', fontSize: '16px', fontWeight: 'bold' }}>{(stats.wins + stats.losses) > 0 ? ((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1) : 0}%</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Win Rate</div>
              </div>
              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                <div style={{ color: stats.totalProfit >= 0 ? '#00d26a' : '#ff3b69', fontSize: '16px', fontWeight: 'bold' }}>{stats.totalProfit >= 0 ? '+' : ''}{formatPB(stats.totalProfit)}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Total P/L</div>
              </div>
              <div style={{ backgroundColor: '#111', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                <div style={{ color: '#f0b90b', fontSize: '16px', fontWeight: 'bold' }}>{stats.bestWinStreak}</div>
                <div style={{ color: '#666', fontSize: '9px' }}>Best Streak</div>
              </div>
            </div>

            {/* Fees Paid */}
            <div style={{ backgroundColor: '#0a1a0a', borderRadius: '8px', padding: '12px', marginBottom: '16px', border: '1px solid #00d26a33' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: '#666', fontSize: '10px' }}>💰 Total Fees Paid (BSV)</div>
                  <div style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>{((playerPortalData?.totalRakePaid || 0) / 100000000).toFixed(6)} BSV</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#666', fontSize: '10px' }}>Transactions</div>
                  <div style={{ color: '#888', fontSize: '14px' }}>{playerPortalData?.rakeTransactions || 0}</div>
                </div>
              </div>
              {stats.totalProfit !== 0 && (playerPortalData?.totalRakePaid || 0) > 0 && (
                <div style={{ marginTop: '8px', color: '#888', fontSize: '9px', textAlign: 'center' }}>
                  Fee ROI: {((stats.totalProfit / (playerPortalData?.totalRakePaid || 1)) * 100).toFixed(0)}%
                  <span style={{ color: '#666' }}> (profit per sat of fees)</span>
                </div>
              )}
            </div>
            
            {/* Debug Log Generator */}
            <div style={{ backgroundColor: '#1a0a0a', borderRadius: '8px', padding: '12px', border: '1px solid #ff3b6933' }}>
              <div style={{ color: '#ff3b69', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>🐛 Report Issue</div>
              <div style={{ color: '#666', fontSize: '10px', marginBottom: '12px' }}>Click to generate a debug log you can send to support</div>
              <button
                onClick={() => {
                  const log = {
                    timestamp: new Date().toISOString(),
                    handle: handcashUser?.handle,
                    sessionId: sessionId?.substring(0, 20) + '...',
                    wallet: {
                      balance: gameWallet,
                      available: gameWallet - lockedBalance,
                      locked: lockedBalance,
                      pendingWithdrawals: pendingWithdrawals?.length || 0,
                    },
                    history: {
                      totalDeposited: totalDeposited || 0,
                      totalWithdrawn: totalWithdrawn || 0,
                      playthroughPercent: playerPortalData?.playthroughPercent || 0,
                      bonusPlaythrough: playerPortalData?.bonusPlaythrough || 0,
                      bonusRequired: playerPortalData?.bonusRequired || 0,
                    },
                    stats: { 
                      trades: stats.totalTrades, 
                      wins: stats.wins, 
                      losses: stats.losses, 
                      rugs: stats.rugs, 
                      totalProfit: stats.totalProfit,
                      winRate: (stats.wins + stats.losses) > 0 ? ((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1) + '%' : '0%',
                      feesPaid: playerPortalData?.totalRakePaid || 0,
                    },
                    positions: positions.filter(p => p.status === 'active').slice(0, 5).map(p => ({ 
                      ticker: p.tokenTicker, 
                      invested: p.invested, 
                      tokens: Math.floor(p.tokens),
                      entry: p.entry,
                    })),
                    activePositionCount: positions.filter(p => p.status === 'active').length,
                    device: navigator.userAgent.substring(0, 50),
                    screen: `${window.innerWidth}x${window.innerHeight}`,
                  };
                  const logText = '```\\nPAPER HANDS DEBUG LOG\\n' + JSON.stringify(log, null, 2) + '\\n```';
                  navigator.clipboard.writeText(logText);
                  alert('✅ Debug log copied to clipboard!\\n\\nPaste this in Discord or send to support.');
                }}
                style={{ width: '100%', padding: '10px', backgroundColor: '#ff3b6922', border: '1px solid #ff3b69', borderRadius: '6px', color: '#ff3b69', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
              >📋 Copy Debug Log</button>
            </div>
          </div>
        </div>
      )}

      {/* 🤖 DEGEN AI - Chat Window for LOBBY (button moved to nav bar) */}
      {handcashConnected && (
        <>
          {/* Chat Window */}
          {showDegenAI && (
            <div
              style={{
                position: 'fixed',
                bottom: isMobile ? '0' : '24px',
                right: isMobile ? '0' : '24px',
                width: isMobile ? '100%' : '380px',
                height: isMobile ? '70vh' : '500px',
                backgroundColor: 'rgba(15, 15, 25, 0.98)',
                border: '2px solid #f0b90b',
                borderRadius: isMobile ? '16px 16px 0 0' : '16px',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 9999,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                overflow: 'hidden',
                fontFamily: "'SF Mono', monospace",
              }}
            >
              {/* Header */}
              <div style={{
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #f0b90b 0%, #d4a30a 100%)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>🤖</span>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#000', fontSize: '14px' }}>
                      DEGEN AI {degenAIStatus?.isSubscriber && <span style={{ fontSize: '10px', background: '#000', color: '#f0b90b', padding: '2px 6px', borderRadius: '4px', marginLeft: '4px' }}>UNLIMITED</span>}
                    </div>
                    <div style={{ fontSize: '10px', color: '#333' }}>
                      {degenAIStatus?.isSubscriber
                        ? 'Unlimited questions'
                        : degenAIStatus?.freeRemaining > 0
                          ? `${degenAIStatus.freeRemaining} free questions left`
                          : 'Subscribe for unlimited access'
                      }
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDegenAI(false)}
                  style={{
                    background: 'rgba(0,0,0,0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Messages */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                {/* Welcome message if no messages */}
                {degenAIMessages.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    color: '#888',
                    padding: '20px',
                    fontSize: '13px',
                  }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>🤖💬</div>
                    <div style={{ color: '#f0b90b', fontWeight: 'bold', marginBottom: '8px' }}>
                      Yo degen! I'm your AI assistant.
                    </div>
                    <div>
                      Ask me about game mechanics, strategy tips, or how things work around here.
                    </div>
                    <div style={{ marginTop: '12px', fontSize: '11px', color: '#666' }}>
                      I can't tell you when tokens will rug though... nobody can predict that!
                    </div>
                  </div>
                )}

                {/* Chat messages */}
                {degenAIMessages.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <div style={{
                      maxWidth: '85%',
                      padding: '10px 14px',
                      borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      backgroundColor: msg.role === 'user'
                        ? '#f0b90b'
                        : msg.isError
                          ? 'rgba(255, 59, 105, 0.2)'
                          : 'rgba(255, 255, 255, 0.1)',
                      color: msg.role === 'user' ? '#000' : msg.isError ? '#ff3b69' : '#fff',
                      fontSize: '13px',
                      lineHeight: '1.4',
                    }}>
                      {msg.content}
                      {msg.role === 'assistant' && msg.cost > 0 && (
                        <div style={{
                          marginTop: '6px',
                          fontSize: '10px',
                          color: '#f0b90b',
                          opacity: 0.8,
                        }}>
                          -{formatPB(msg.cost)} Paper Bucks
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {degenAILoading && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                  }}>
                    <div style={{
                      padding: '10px 14px',
                      borderRadius: '16px 16px 16px 4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#888',
                      fontSize: '13px',
                    }}>
                      <span style={{ animation: 'degenTyping 1s infinite' }}>🤖 Thinking...</span>
                    </div>
                  </div>
                )}

                <div ref={degenAIMessagesEndRef} />
              </div>

              {/* UPSELL MODAL - Shows when free questions run out */}
              {showDegenAIUpsell && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                  zIndex: 10,
                }}>
                  <div style={{
                    backgroundColor: 'rgba(20, 20, 35, 0.98)',
                    border: '2px solid #f0b90b',
                    borderRadius: '16px',
                    padding: '24px',
                    maxWidth: '320px',
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🤖</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f0b90b', marginBottom: '8px' }}>
                      Out of free questions!
                    </div>
                    <div style={{ fontSize: '13px', color: '#ccc', marginBottom: '16px', lineHeight: '1.5' }}>
                      That token you're watching? It could rug any second, ser. Do you <span style={{ color: '#ff3b69' }}>REALLY</span> want to trade without your AI wingman?
                    </div>

                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fff', marginBottom: '4px' }}>
                      DEGEN AI Unlimited
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f0b90b', marginBottom: '16px' }}>
                      $5/month
                    </div>

                    <div style={{ textAlign: 'left', marginBottom: '20px', fontSize: '12px', color: '#aaa' }}>
                      <div style={{ marginBottom: '6px' }}>→ One saved rug pays for a YEAR</div>
                      <div style={{ marginBottom: '6px' }}>→ Ask anything, anytime, no limits</div>
                      <div>→ Your edge while others guess</div>
                    </div>

                    <button
                      onClick={subscribeToDegenAI}
                      disabled={degenAISubscribing}
                      style={{
                        width: '100%',
                        padding: '14px 20px',
                        background: degenAISubscribing
                          ? 'rgba(240, 185, 11, 0.5)'
                          : 'linear-gradient(135deg, #f0b90b 0%, #d4a30a 100%)',
                        border: 'none',
                        borderRadius: '12px',
                        color: '#000',
                        fontWeight: 'bold',
                        fontSize: '15px',
                        cursor: degenAISubscribing ? 'wait' : 'pointer',
                        marginBottom: '12px',
                      }}
                    >
                      {degenAISubscribing ? 'Processing...' : '🚀 Unlock Now - $5/mo'}
                    </button>

                    <button
                      onClick={() => setShowDegenAIUpsell(false)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: 'transparent',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#888',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      I'll risk it alone
                    </button>
                  </div>
                </div>
              )}

              {/* Input */}
              <div style={{
                padding: '12px',
                borderTop: '1px solid rgba(240, 185, 11, 0.3)',
                display: 'flex',
                gap: '8px',
              }}>
                <input
                  type="text"
                  value={degenAIInput}
                  onChange={(e) => setDegenAIInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      askDegenAI(degenAIInput);
                    }
                  }}
                  placeholder="Ask me anything..."
                  disabled={degenAILoading}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(240, 185, 11, 0.3)',
                    borderRadius: '20px',
                    color: '#fff',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                />
                <button
                  onClick={() => askDegenAI(degenAIInput)}
                  disabled={degenAILoading || !degenAIInput.trim()}
                  style={{
                    padding: '10px 16px',
                    background: degenAILoading || !degenAIInput.trim()
                      ? 'rgba(240, 185, 11, 0.3)'
                      : 'linear-gradient(135deg, #f0b90b 0%, #d4a30a 100%)',
                    border: 'none',
                    borderRadius: '20px',
                    color: degenAILoading || !degenAIInput.trim() ? '#666' : '#000',
                    fontWeight: 'bold',
                    cursor: degenAILoading || !degenAIInput.trim() ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                  }}
                >
                  {degenAILoading ? '...' : 'Ask'}
                </button>
              </div>
            </div>
          )}

          {/* Animations */}
          <style>{`
            @keyframes degenPulse {
              0%, 100% { transform: scale(1); box-shadow: 0 4px 20px rgba(240, 185, 11, 0.5); }
              50% { transform: scale(1.05); box-shadow: 0 4px 30px rgba(240, 185, 11, 0.7); }
            }
            @keyframes degenTyping {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
        </>
      )}
      </>
    );
  }

  // ============== RENDER: TOKEN ROOM ==============
  if (view === 'token' && currentToken) {
    // P/L = current value - invested
    // Current value = invested * (currentPrice / entryPrice)
    // This gives us: P/L = invested * (multiplier - 1)
    const multiplier = position.entry > 0 ? currentPrice / position.entry : 1;
    const currentValue = Math.floor(position.invested * multiplier);
    const pnl = gameState === 'playing' ? currentValue - position.invested : 0;
    
    return (
      <>
      <TestRecorderUI />
      {showPaperChat && handcashUser && <PaperChatContent />}
      <TipModal />
      <ProfileModal />
      
      {/* 💰 HOUSE CREDIT ANIMATION - Shows when zcool receives rake */}
      {houseCreditAnimation && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 10001,
          animation: 'houseCreditSlideIn 0.5s ease-out',
        }}>
          <div style={{
            backgroundColor: '#0a1a0a',
            border: '2px solid #00d26a',
            borderRadius: '12px',
            padding: '16px 20px',
            boxShadow: '0 0 30px rgba(0, 210, 106, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{ fontSize: '32px' }}>💰</div>
            <div>
              <div style={{ color: '#00d26a', fontSize: '12px', fontWeight: 'bold' }}>HOUSE CREDIT</div>
              <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>
                +{formatPB(houseCreditAnimation.amount)}
              </div>
              <div style={{ color: '#888', fontSize: '11px' }}>
                {houseCreditAnimation.from}'s bot {houseCreditAnimation.type?.includes('buy') ? 'BUY' : 'SELL'} on ${houseCreditAnimation.ticker}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div style={{ minHeight: '100vh', backgroundColor: '#050508', color: '#fff', padding: isMobile ? '8px' : '12px', fontFamily: "'SF Mono', monospace" }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>

          {/* ============== UNIFIED TRADING VIEW HEADER ============== */}

          {/* SINGLE ROW: Lobby (tucked) | Token Badge | Balance | Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            gap: '8px',
            padding: isMobile ? '0' : '0 4px',
          }}>
            {/* LEFT: Lobby (small) + Token Identity */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px' }}>
              {/* Lobby - small home icon, not prominent */}
              <button onClick={leaveToken} style={{
                backgroundColor: 'transparent',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: '6px 8px',
                color: '#555',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#666'; e.currentTarget.style.color = '#888'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.color = '#555'; }}
              title="Back to Lobby"
              >
                🏠
              </button>

              {/* Token Badge - Compact */}
              <div style={{
                backgroundColor: gameState === 'rugged' ? '#1a0808' : currentToken?.hasMooned ? '#081a08' : '#0d0d12',
                border: `2px solid ${gameState === 'rugged' ? '#ff3b69' : currentToken?.hasMooned ? '#00d26a' : '#f0b90b'}`,
                borderRadius: '10px',
                padding: isMobile ? '6px 10px' : '8px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '8px' : '10px',
                boxShadow: gameState === 'rugged' ? '0 0 15px rgba(255,59,105,0.2)' : currentToken?.hasMooned ? '0 0 15px rgba(0,210,106,0.2)' : 'none',
              }}>
                <div style={{
                  width: isMobile ? '28px' : '32px',
                  height: isMobile ? '28px' : '32px',
                  borderRadius: '50%',
                  backgroundColor: '#1a1a2e',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '14px' : '16px',
                }}>
                  {gameState === 'rugged' ? '💀' : currentToken?.hasMooned ? '🌙' : '🔥'}
                </div>
                <div>
                  <div style={{ color: gameState === 'rugged' ? '#ff3b69' : currentToken?.hasMooned ? '#00d26a' : '#f0b90b', fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold' }}>
                    {currentToken.ticker}
                  </div>
                  <div style={{ color: '#666', fontSize: '9px' }}>
                    {gameState === 'rugged' ? '💀 RUGGED' : currentToken?.hasMooned ? '🌙 MOONED!' : currentToken.category}
                  </div>
                </div>
                {/* Time + Viewers - only on desktop */}
                {!isMobile && (
                  <div style={{ borderLeft: '1px solid #333', paddingLeft: '10px', marginLeft: '4px' }}>
                    <div style={{ color: getAgeColor(currentToken.birthTime, currentToken.lifespanType), fontSize: '9px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      🕐 {gameState === 'rugged' ? 'ENDED' : formatAge(currentToken.birthTime)}
                    </div>
                    <div style={{ color: '#888', fontSize: '9px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      👁️ {viewerCount} watching
                    </div>
                  </div>
                )}
              </div>

              {/* Admin Delete - only for admins */}
              {currentToken?.isUserLaunched && isAdmin && (
                <button
                  onClick={() => setDeleteConfirmToken({ id: currentToken.id, ticker: currentToken.ticker })}
                  style={{
                    backgroundColor: '#1a0808',
                    border: '1px solid #ff3b69',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    color: '#ff3b69',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  🗑️
                </button>
              )}
            </div>

            {/* CENTER: Balance (clickable for wallet) */}
            <div
              onClick={() => setShowWallet(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                backgroundColor: hoverPreviewAmount ? 'rgba(255,59,105,0.1)' : '#0a0a0f',
                border: hoverPreviewAmount ? '1px solid rgba(255,59,105,0.3)' : '1px solid #1a1a2e',
                borderRadius: '10px',
                padding: isMobile ? '6px 10px' : '8px 14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative',
              }}
              onMouseEnter={e => { if (!hoverPreviewAmount) { e.currentTarget.style.borderColor = '#00d26a44'; }}}
              onMouseLeave={e => { if (!hoverPreviewAmount) { e.currentTarget.style.borderColor = '#1a1a2e'; }}}
            >
              <div style={{ fontSize: '16px' }}>💵</div>
              <div>
                <div style={{ color: hoverPreviewAmount ? '#ff3b69' : '#00d26a', fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', lineHeight: 1 }}>
                  ${formatSats(hoverPreviewAmount ? gameWallet - hoverPreviewAmount : gameWallet)}
                </div>
                <div style={{ color: '#666', fontSize: '8px' }}>
                  ≈ ${((hoverPreviewAmount ? gameWallet - hoverPreviewAmount : gameWallet) * satToUsd).toFixed(2)} USD
                </div>
              </div>
              {hoverPreviewAmount && (
                <div style={{ color: '#ff3b69', fontSize: '10px', fontWeight: 'bold' }}>
                  -{formatPB(hoverPreviewAmount)}
                </div>
              )}
              <span style={{ color: '#444', fontSize: '12px' }}>▼</span>
            </div>

            {/* RIGHT: Quick Stats + Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '8px' }}>
              {/* On-Chain badge - compact */}
              {!isMobile && (
                <div style={{ textAlign: 'center', padding: '4px 8px', backgroundColor: '#0a0f0a', borderRadius: '6px', border: '1px solid #00d26a33' }}>
                  <div style={{ color: '#00d26a', fontSize: '7px', fontWeight: 'bold' }}>⛓️ ON-CHAIN</div>
                  <div style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>{globalStats.onChainTransactions || (globalStats.totalDeposits || 0) + (globalStats.totalWithdrawals || 0)}</div>
                </div>
              )}

              {/* BSV Price */}
              <div style={{ textAlign: 'center', padding: '4px 8px' }}>
                <div style={{ color: '#555', fontSize: '7px' }}>BSV</div>
                <div style={{ color: '#888', fontSize: isMobile ? '10px' : '11px', fontWeight: 'bold' }}>${bsvPrice ? bsvPrice.toFixed(2) : '--'}</div>
              </div>

              {/* Sound */}
              <button onClick={() => { const newState = !soundEnabled; setSoundEnabled(newState); if (newState) Sounds.success(); }} style={{
                backgroundColor: soundEnabled ? '#0a1a0f' : '#1a0a0a',
                border: `1px solid ${soundEnabled ? '#00d26a33' : '#333'}`,
                borderRadius: '6px', padding: '6px 8px', cursor: 'pointer',
              }}>
                <span style={{ fontSize: '12px' }}>{soundEnabled ? '🔊' : '🔇'}</span>
              </button>
            </div>
          </div>

          {/* ROW 2: Mobile-only time/viewers + Extended stats (desktop) */}
          {isMobile && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '8px', fontSize: '10px', color: '#888' }}>
              <span style={{ color: getAgeColor(currentToken.birthTime, currentToken.lifespanType) }}>🕐 {gameState === 'rugged' ? 'ENDED' : formatAge(currentToken.birthTime)}</span>
              <span>👁️ {viewerCount} watching</span>
              <span style={{ color: '#00d26a' }}>⛓️ {globalStats.onChainTransactions || 0}</span>
            </div>
          )}

          {/* ROW 2: Token Stats Bar with Safety Score */}
          
          {/* 🤖 BOT STATUS BAR - Shows in trading view when bot is active */}
          {tradingBotData?.active && botControlEnabled && (
            <div style={{
              backgroundColor: tradingBotData.isTrading ? '#1a0a2e' : '#1a1a1a',
              border: `2px solid ${tradingBotData.isTrading ? '#a855f7' : '#666'}`,
              borderRadius: '10px',
              padding: '10px 16px',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
              animation: tradingBotData.isTrading ? 'botPulse 2s ease-in-out infinite' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>🤖</span>
                <div>
                  <div style={{ color: tradingBotData.isTrading ? '#a855f7' : '#888', fontSize: '10px', fontWeight: 'bold' }}>
                    {tradingBotData.currentCommand?.action === 'buying' && '💰 BUYING'}
                    {tradingBotData.currentCommand?.action === 'selling' && '📤 SELLING'}
                    {tradingBotData.currentCommand?.action === 'watching' && '👁️ WATCHING'}
                    {tradingBotData.currentCommand?.action === 'analyzing' && '🔍 ANALYZING'}
                    {(tradingBotData.currentCommand?.action === 'idle' || tradingBotData.currentCommand?.action === 'paused') && '⏸️ PAUSED'}
                    {(!tradingBotData.currentCommand?.action || tradingBotData.currentCommand?.action === 'scanning') && '📡 SCANNING'}
                  </div>
                  <div style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>
                    {tradingBotData.currentCommand?.narration || 'Bot active...'}
                  </div>
                </div>
              </div>
              {/* STOP button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  stopTradingBot();
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ff3b69',
                  backgroundColor: 'rgba(255,59,105,0.15)',
                  color: '#ff3b69',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                ⏹️ STOP
              </button>
            </div>
          )}
          
          {/* 🎯 VIEW MODE TOGGLE */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
            <div style={{ display: 'flex', backgroundColor: '#0a0a0f', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' }}>
              <button
                onClick={() => { setTradingViewMode('classic'); setChartFullscreen(false); }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: tradingViewMode === 'classic' ? '#1a1a2e' : 'transparent',
                  border: 'none',
                  borderRight: '1px solid #333',
                  color: tradingViewMode === 'classic' ? '#f0b90b' : '#666',
                  fontSize: '11px',
                  fontWeight: tradingViewMode === 'classic' ? 'bold' : 'normal',
                  cursor: 'pointer',
                }}
              >📺 Classic</button>
              <button
                onClick={() => { setTradingViewMode('focused'); setChartFullscreen(false); }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: tradingViewMode === 'focused' && !chartFullscreen ? '#0a1a0a' : 'transparent',
                  border: 'none',
                  borderRight: '1px solid #333',
                  color: tradingViewMode === 'focused' && !chartFullscreen ? '#00d26a' : '#666',
                  fontSize: '11px',
                  fontWeight: tradingViewMode === 'focused' && !chartFullscreen ? 'bold' : 'normal',
                  cursor: 'pointer',
                }}
              >🎯 Focused</button>
              <button
                onClick={() => { setTradingViewMode('focused'); setChartFullscreen(true); }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: tradingViewMode === 'focused' && chartFullscreen ? '#1a0a0a' : 'transparent',
                  border: 'none',
                  color: tradingViewMode === 'focused' && chartFullscreen ? '#ff3b69' : '#666',
                  fontSize: '11px',
                  fontWeight: tradingViewMode === 'focused' && chartFullscreen ? 'bold' : 'normal',
                  cursor: 'pointer',
                }}
              >🔫 Locked In</button>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* 🎯 FOCUSED VIEW - Mobile-first trading interface */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {tradingViewMode === 'focused' && (
            <div style={{ 
              width: '100%',
              maxWidth: isMobile ? '100%' : '900px',
              margin: '0 auto',
              background: '#0a0a0f',
              minHeight: 'calc(100vh - 140px)',
            }}>
              {/* MOBILE FOCUSED VIEW - Single column */}
              {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {/* INFO ZONE - Scrollable top section */}
                  <div style={{ 
                    flex: '0 0 auto',
                    maxHeight: '35vh',
                    overflowY: 'auto',
                    padding: '10px',
                    borderBottom: '2px solid #1a1a2e',
                  }}>
                    {/* 🔥 UNIFIED STATS GRID - Mobile version */}
                    {(() => {
                      const sinceLaunch = currentToken?.initialPrice ? ((currentPrice / currentToken.initialPrice) - 1) * 100 : 0;
                      const isPumping = sinceLaunch >= 0;
                      const healthScore = currentToken.health?.score || 50;
                      const healthColor = healthScore > 60 ? '#00d26a' : healthScore > 30 ? '#f0b90b' : '#ff3b69';
                      
                      return (
                        <div style={{ background: '#0d1117', borderRadius: '8px', border: '1px solid #1a1a2e', overflow: 'hidden', marginBottom: '8px' }}>
                          {/* TOP ROW - Token + Badge + Launch + Health */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.8fr 0.8fr', borderBottom: '1px solid #1a1a2e' }}>
                            {/* Token Name */}
                            <div style={{ padding: '8px', borderRight: '1px solid #1a1a2e' }}>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#f0b90b' }}>${currentToken.ticker}</div>
                              <div style={{ color: '#555', fontSize: '8px' }}>{currentToken.creatorType === 'ai' ? 'AI' : 'PvP'} • {viewerCount}</div>
                            </div>
                            {/* Pumping/Dumping Badge */}
                            <div style={{ padding: '8px 4px', borderRight: '1px solid #1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <div style={{
                                padding: '3px 6px',
                                background: isPumping ? '#00d26a22' : '#ff3b6922',
                                border: `1px solid ${isPumping ? '#00d26a' : '#ff3b69'}`,
                                borderRadius: '3px',
                                color: isPumping ? '#00d26a' : '#ff3b69',
                                fontSize: '8px',
                                fontWeight: 'bold',
                              }}>{isPumping ? '🟢 PUMP' : '🔴 DUMP'}</div>
                            </div>
                            {/* Since Launch */}
                            <div style={{ padding: '8px 4px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '7px' }}>LAUNCH</div>
                              <div style={{ color: isPumping ? '#00d26a' : '#ff3b69', fontSize: '11px', fontWeight: 'bold' }}>
                                {isPumping ? '+' : ''}{sinceLaunch.toFixed(1)}%
                              </div>
                            </div>
                            {/* Health */}
                            <div style={{ padding: '8px 4px', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '7px' }}>🫀</div>
                              <div style={{ color: healthColor, fontSize: '11px', fontWeight: 'bold' }}>{healthScore}</div>
                            </div>
                          </div>
                          {/* BOTTOM ROW - Stats Grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
                            <div style={{ padding: '6px 4px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '7px' }}>PRICE</div>
                              <div style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>{(currentToken.currentPrice || 0).toFixed(2)}</div>
                            </div>
                            <div style={{ padding: '6px 4px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '7px' }}>MCAP</div>
                              <div style={{ color: '#00d26a', fontSize: '10px', fontWeight: 'bold' }}>{formatPB(currentToken.marketCap || 0)}</div>
                            </div>
                            <div style={{ padding: '6px 4px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '7px' }}>LIQ</div>
                              <div style={{ color: '#00bcd4', fontSize: '10px', fontWeight: 'bold' }}>{formatPB(currentToken.liquidityPool || 0)}</div>
                            </div>
                            <div style={{ padding: '6px 4px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '7px' }}>VOL</div>
                              <div style={{ color: '#f0b90b', fontSize: '10px', fontWeight: 'bold' }}>{formatPB((currentToken.totalBuyVolume || 0) + (currentToken.totalSellVolume || 0))}</div>
                            </div>
                            <div style={{ padding: '6px 4px', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '7px' }}>ATH</div>
                              <div style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>{currentToken.athMultiplier ? `${currentToken.athMultiplier.toFixed(1)}x` : '1.0x'}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Token Description - Focused View */}
                    {currentToken?.isUserLaunched && currentToken?.description && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '8px',
                        padding: '8px 10px',
                        backgroundColor: '#0d0d1a',
                        borderRadius: '6px',
                        border: '1px solid #f0b90b33',
                      }}>
                        <span style={{ color: '#f0b90b', fontSize: '10px' }}>📝</span>
                        <span style={{ color: '#ccc', fontSize: '10px', fontStyle: 'italic', flex: 1 }}>
                          "{currentToken.description}"
                        </span>
                      </div>
                    )}

                    {/* 👥 IN THIS TRADE - Classic Style */}
                    {(() => {
                      const userIsCreator = currentToken?.isUserLaunched && currentToken?.creator &&
                        handcashUser?.handle?.replace('$', '').toLowerCase() === currentToken?.creator?.replace('$', '').toLowerCase();

                      return (
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '4px',
                          padding: '10px',
                          backgroundColor: userIsCreator ? '#0d0d1a' : '#0d0d12',
                          borderRadius: '6px',
                          border: userIsCreator ? '1px solid #f0b90b33' : '1px solid #1a1a2e',
                          marginBottom: '8px',
                        }}>
                          <span style={{ color: '#555', fontSize: '8px', width: '100%', marginBottom: '4px' }}>
                            👥 IN THIS TRADE {userIsCreator && <span style={{ color: '#f0b90b' }}>• 👑</span>}
                          </span>
                          {(() => {
                            const seenHandles = new Set();
                            return tokenPlayers.filter(p => {
                              if (!p.isActive) return false;
                              const handle = p.handle?.replace(/^\$/, '') || 'unknown';
                              if (seenHandles.has(handle)) return false;
                              seenHandles.add(handle);
                              return true;
                            }).slice(0, 8).map(p => {
                              const isCreator = currentToken?.isUserLaunched && currentToken?.creator &&
                                (p.handle === currentToken.creator || p.handle === '$' + currentToken.creator);
                              return (
                              <div key={p.handle} style={{
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '3px', 
                                padding: '3px 6px', 
                                backgroundColor: isCreator ? '#1a1508' : '#111', 
                                borderRadius: '4px', 
                                fontSize: '9px',
                                border: isCreator ? '1px solid #f0b90b44' : '1px solid #222',
                              }}>
                                <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: p.color }} />
                                <span style={{ color: isCreator ? '#f0b90b' : '#888' }}>{p.handle}</span>
                                {isCreator && <span style={{ fontSize: '7px', color: '#f0b90b' }}>👑</span>}
                                {userIsCreator && !isCreator && p.invested && (
                                  <span style={{ fontSize: '8px', color: '#00d26a' }}>{(p.invested / 1000).toFixed(0)}K</span>
                                )}
                              </div>
                              );
                            });
                          })()}
                          {gameState === 'playing' && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '3px', 
                              padding: '3px 6px', 
                              backgroundColor: '#0d1a0d', 
                              borderRadius: '4px', 
                              fontSize: '9px', 
                              border: '1px solid #0f2f0f',
                            }}>
                              <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#f0b90b' }} />
                              <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>YOU</span>
                            </div>
                          )}
                          {tokenPlayers.filter(p => p.isActive).length === 0 && <span style={{ color: '#666', fontSize: '8px' }}>No active positions</span>}
                        </div>
                      );
                    })()}

                    {/* 📊 LIVE TRADES - Classic Style */}
                    <div style={{
                      backgroundColor: '#0a0a0f',
                      borderRadius: '6px',
                      border: '1px solid #1a1a2e',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 10px',
                        backgroundColor: '#0d0d12',
                        borderBottom: '1px solid #1a1a2e',
                      }}>
                        <span style={{ color: '#5e6673', fontSize: '9px', fontWeight: 'bold' }}>📊 LIVE TRADES</span>
                        <span style={{ color: '#444', fontSize: '8px' }}>{transactions.length}</span>
                      </div>
                      
                      <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                        {(() => {
                          const seenIds = new Set();
                          const uniqueTxs = transactions.filter(tx => {
                            if ((tx.sats || 0) <= 0) return false;
                            if (!tx.id) return true;
                            if (seenIds.has(tx.id)) return false;
                            seenIds.add(tx.id);
                            return true;
                          });
                          
                          return uniqueTxs.slice(0, 6).map((tx, idx) => {
                            const isBuy = tx.type === 'buy';
                            const isHuge = (tx.sats || 0) >= 100000;
                            const timeAgo = tx.timestamp ? Math.floor((Date.now() - tx.timestamp) / 1000) : 0;
                            const timeStr = timeAgo < 60 ? `${timeAgo}s` : timeAgo < 3600 ? `${Math.floor(timeAgo/60)}m` : `${Math.floor(timeAgo/3600)}h`;
                            
                            return (
                              <div 
                                key={tx.id || idx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '6px 10px',
                                  borderBottom: '1px solid #111',
                                  backgroundColor: tx.isYou ? 'rgba(240,185,11,0.08)' : 'transparent',
                                }}
                              >
                                <div style={{
                                  width: '22px',
                                  height: '22px',
                                  borderRadius: '4px',
                                  backgroundColor: isBuy ? 'rgba(22,199,132,0.2)' : 'rgba(234,57,67,0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '10px',
                                }}>
                                  {tx.isYou ? '👤' : isHuge ? (isBuy ? '🐋' : '💀') : (isBuy ? '↗' : '↘')}
                                </div>
                                
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ color: isBuy ? '#16c784' : '#ea3943', fontWeight: 'bold', fontSize: '9px' }}>
                                      {isBuy ? 'BUY' : 'SELL'}
                                    </span>
                                    {tx.isYou && <span style={{ fontSize: '7px', padding: '1px 3px', backgroundColor: '#f0b90b', color: '#000', borderRadius: '2px', fontWeight: 'bold' }}>YOU</span>}
                                  </div>
                                  <div style={{ color: '#666', fontSize: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {tx.wallet || 'Unknown'} • {timeStr}
                                  </div>
                                </div>
                                
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ color: tx.isYou ? '#f0b90b' : (isBuy ? '#16c784' : '#ea3943'), fontWeight: 'bold', fontSize: '10px' }}>
                                    {isBuy ? '+' : '-'}{formatPB(tx.sats || 0)}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                        
                        {transactions.filter(tx => (tx.sats || 0) > 0).length === 0 && (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#444', fontSize: '9px' }}>
                            ⏳ Waiting for trades...
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* TRADING ZONE - Sticky bottom section */}
                  <div style={{ flex: 1, padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Chart - Same as Classic */}
                    {renderChart()}

                    {/* Position Card */}
                    {position && position.invested > 0 && (
                      <div style={{ background: '#111', border: '1px solid #00d26a', borderRadius: '8px', padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#555', fontSize: '7px', textTransform: 'uppercase' }}>Qty</div>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#fff' }}>{(position.size / 1000).toFixed(1)}K</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#555', fontSize: '7px', textTransform: 'uppercase' }}>Entry</div>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#fff' }}>${position.entry?.toFixed(2) || '0.00'}</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#555', fontSize: '7px', textTransform: 'uppercase' }}>Invested</div>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#f0b90b' }}>{formatPB(position.invested)}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#555', fontSize: '7px', textTransform: 'uppercase' }}>P/L</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: pnl >= 0 ? '#00d26a' : '#ff3b69' }}>
                            {pnl >= 0 ? '+' : ''}{formatPB(pnl)}
                          </div>
                          <div style={{ fontSize: '10px', color: pnl >= 0 ? '#00d26a' : '#ff3b69' }}>
                            {position.invested > 0 ? `${pnl >= 0 ? '+' : ''}${((pnl / position.invested) * 100).toFixed(1)}%` : '0%'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Price Impact Preview - Mobile */}
                    {(() => {
                      const poolLiquidity = currentToken?.liquidityPool || currentToken?.liquidity || 0;
                      const tokenReserve = currentToken?.tokenReserve || 1000000000;
                      const previewStake = 50000;
                      const k = poolLiquidity * tokenReserve;
                      const rakeAmt = Math.floor(previewStake * 0.01);
                      const netAmt = previewStake - rakeAmt;
                      const newLiq = poolLiquidity + netAmt;
                      const newTokenRes = k / newLiq;
                      const tokensRcvd = tokenReserve - newTokenRes;
                      const newPrc = newLiq / newTokenRes;
                      const currPrc = poolLiquidity / tokenReserve;
                      const prcImpact = poolLiquidity > 0 ? ((newPrc - currPrc) / currPrc) * 100 : 0;
                      const avgPrc = tokensRcvd > 0 ? previewStake / tokensRcvd : 0;
                      
                      return poolLiquidity > 0 ? (
                        <div style={{ background: '#0a0a12', border: '1px solid #333', borderRadius: '6px', padding: '8px' }}>
                          <div style={{ color: '#888', fontWeight: 'bold', marginBottom: '6px', fontSize: '9px' }}>
                            📊 PRICE IMPACT (for $50K buy)
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '10px' }}>
                            <div>
                              <div style={{ color: '#666', fontSize: '8px' }}>You'll receive</div>
                              <div style={{ color: '#00d26a', fontWeight: 'bold' }}>~{(tokensRcvd / 1000000).toFixed(2)}M tokens</div>
                            </div>
                            <div>
                              <div style={{ color: '#666', fontSize: '8px' }}>Avg buy price</div>
                              <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>{avgPrc.toFixed(6)}</div>
                            </div>
                            <div>
                              <div style={{ color: '#666', fontSize: '8px' }}>Price impact</div>
                              <div style={{ color: prcImpact > 10 ? '#ff3b69' : prcImpact > 5 ? '#f0b90b' : '#00d26a', fontWeight: 'bold' }}>+{prcImpact.toFixed(2)}%</div>
                            </div>
                            <div>
                              <div style={{ color: '#666', fontSize: '8px' }}>1% rake</div>
                              <div style={{ color: '#888' }}>{formatPB(rakeAmt)}</div>
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Trade Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ color: '#2d8f7b', fontSize: '8px', width: '35px', textAlign: 'right' }}>🟢 BUY</div>
                        <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                          {[10000, 50000, 100000, 250000, 500000, 1000000].map(amount => (
                            <button
                              key={amount}
                              onClick={() => executeApeIn(amount)}
                              disabled={gameWallet < amount || gameState === 'rugged'}
                              style={{
                                flex: 1,
                                padding: '10px 4px',
                                background: '#0a1612',
                                border: '1px solid #2d8f7b',
                                borderRadius: '6px',
                                color: '#2d8f7b',
                                fontWeight: 'bold',
                                fontSize: '10px',
                                cursor: gameWallet >= amount && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                                opacity: gameWallet >= amount && gameState !== 'rugged' ? 1 : 0.5,
                              }}
                            >{formatPB(amount)}</button>
                          ))}
                        </div>
                        {(() => {
                          const poolLiq = currentToken?.liquidityPool || 0;
                          const maxBuy = Math.floor(poolLiq * 0.05);
                          const nextLocked = [50000, 100000, 250000, 500000, 1000000].find(amt => amt > maxBuy);
                          if (nextLocked && poolLiq > 0) {
                            const unlockAt = nextLocked * 20;
                            return <div style={{ color: '#555', fontSize: '8px', marginTop: '4px', marginLeft: '41px' }}>🔒 {formatPB(nextLocked)} unlocks at {formatPB(unlockAt)} liq</div>;
                          }
                          return null;
                        })()}
                      </div>
                      {/* Custom buy input - mobile */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '35px' }}></div>
                        <input
                          type="number"
                          value={stake}
                          onChange={(e) => setStake(Math.max(1000, parseInt(e.target.value) || 0))}
                          style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #2d8f7b',
                            backgroundColor: '#0a0d12',
                            color: '#2d8f7b',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            outline: 'none',
                          }}
                          placeholder="Custom amount..."
                        />
                        <button
                          onClick={() => executeApeIn(stake)}
                          disabled={gameWallet < stake || gameState === 'rugged'}
                          style={{
                            padding: '8px 12px',
                            background: '#0a1612',
                            border: '1px solid #2d8f7b',
                            borderRadius: '6px',
                            color: '#2d8f7b',
                            fontWeight: 'bold',
                            fontSize: '10px',
                            cursor: gameWallet >= stake && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                            opacity: gameWallet >= stake && gameState !== 'rugged' ? 1 : 0.5,
                          }}
                        >BUY</button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ color: '#c75454', fontSize: '8px', width: '35px', textAlign: 'right' }}>🔴 SELL</div>
                        <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                          {[25, 50, 75, 100].map(pct => (
                            <button
                              key={pct}
                              onClick={() => sell(pct)}
                              disabled={!position?.size || position.size <= 0 || gameState === 'rugged'}
                              style={{
                                flex: 1,
                                padding: '10px 4px',
                                background: '#160a0a',
                                border: '1px solid #c75454',
                                borderRadius: '6px',
                                color: '#c75454',
                                fontWeight: 'bold',
                                fontSize: '10px',
                                cursor: position?.size > 0 && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                                opacity: position?.size > 0 && gameState !== 'rugged' ? 1 : 0.5,
                              }}
                            >{pct}%</button>
                          ))}
                        </div>
                      </div>
                      {/* Bag Value Display - mobile */}
                      {(() => {
                        const multiplier = position.entry > 0 ? currentPrice / position.entry : 1;
                        const bagValue = Math.floor(position.invested * multiplier) || 0;
                        return (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 10px',
                            background: '#0d1117',
                            borderRadius: '4px',
                            border: '1px solid #1a1a2e',
                            marginTop: '6px'
                          }}>
                            <span style={{ color: '#888', fontSize: '9px' }}>💰 BAG:</span>
                            <span style={{
                              color: multiplier >= 1 ? '#00d26a' : '#ff3b69',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              fontFamily: 'monospace'
                            }}>
                              {formatPB(bagValue)}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Custom USD sell input - mobile */}
                      {(() => {
                        const multiplier = position.entry > 0 ? currentPrice / position.entry : 1;
                        const bagValue = Math.floor(position.invested * multiplier) || 0;
                        const maxUSD = bagValue;
                        const usdAmount = parseFloat(customSellUSD) || 0;
                        const usdPercent = bagValue > 0 ? Math.min(100, Math.max(0, (usdAmount / bagValue) * 100)) : 0;

                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: '#c75454', fontSize: '12px', fontWeight: 'bold' }}>$</span>
                            <input
                              type="number"
                              step="any"
                              value={customSellUSD}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || val === '0' || val === '0.') {
                                  setCustomSellUSD(val);
                                } else {
                                  const num = parseFloat(val);
                                  if (!isNaN(num) && num >= 0) {
                                    setCustomSellUSD(num <= maxUSD ? val : maxUSD.toString());
                                  }
                                }
                              }}
                              style={{
                                width: '60px',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #c75454',
                                backgroundColor: '#160a0a',
                                color: '#c75454',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                outline: 'none',
                                textAlign: 'right',
                                fontFamily: 'monospace',
                              }}
                              placeholder="0.01"
                            />
                            <button
                              onClick={() => {
                                if (usdPercent > 0 && usdPercent <= 100) {
                                  sell(usdPercent);
                                  setCustomSellUSD('');
                                }
                              }}
                              disabled={usdPercent <= 0 || usdPercent > 100 || gameState === 'rugged'}
                              style={{
                                flex: 1,
                                padding: '8px 10px',
                                background: '#160a0a',
                                border: '1px solid #c75454',
                                borderRadius: '6px',
                                color: '#c75454',
                                fontWeight: 'bold',
                                fontSize: '10px',
                                cursor: usdPercent > 0 && usdPercent <= 100 && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                                opacity: usdPercent > 0 && usdPercent <= 100 && gameState !== 'rugged' ? 1 : 0.5,
                              }}
                            >SELL ${customSellUSD || '0'}</button>
                            <button
                              onClick={() => setCustomSellUSD(maxUSD.toString())}
                              style={{
                                padding: '8px 6px',
                                borderRadius: '6px',
                                border: '1px solid #444',
                                cursor: 'pointer',
                                backgroundColor: '#111',
                                color: '#666',
                                fontWeight: 'bold',
                                fontSize: '8px',
                              }}
                            >MAX</button>
                          </div>
                        );
                      })()}

                      {/* Custom % sell input - mobile */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <input
                          type="number"
                          value={customSellPct}
                          onChange={(e) => setCustomSellPct(Math.min(100, Math.max(1, parseInt(e.target.value) || 0)))}
                          style={{
                            width: '45px',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #444',
                            backgroundColor: '#0a0d12',
                            color: '#666',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            outline: 'none',
                            textAlign: 'center',
                          }}
                          placeholder="%"
                        />
                        <span style={{ color: '#555', fontSize: '9px' }}>%</span>
                        <button
                          onClick={() => sell(customSellPct)}
                          disabled={!position?.size || position.size <= 0 || gameState === 'rugged'}
                          style={{
                            flex: 1,
                            padding: '8px 10px',
                            background: '#111',
                            border: '1px solid #444',
                            borderRadius: '6px',
                            color: '#666',
                            fontWeight: 'bold',
                            fontSize: '10px',
                            cursor: position?.size > 0 && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                            opacity: position?.size > 0 && gameState !== 'rugged' ? 1 : 0.5,
                          }}
                        >SELL {customSellPct}%</button>
                      </div>
                    </div>

                    {/* Back to Lobby */}
                    <button
                      onClick={() => setView('lobby')}
                      style={{
                        padding: '12px',
                        background: '#111',
                        border: '2px solid #f0b90b',
                        borderRadius: '8px',
                        color: '#f0b90b',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                    >🏠 Back to Token List</button>
                  </div>
                </div>
              ) : (
                /* DESKTOP FOCUSED VIEW - Two column layout */
                <div style={{ display: 'flex', gap: '12px', padding: '10px', background: '#0a0a0f' }}>
                  {/* LEFT COLUMN - Chart & Trading */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* 🔥 UNIFIED STATS GRID - Sexy compact design */}
                    {(() => {
                      const sinceLaunch = currentToken?.initialPrice ? ((currentPrice / currentToken.initialPrice) - 1) * 100 : 0;
                      const isPumping = sinceLaunch >= 0;
                      const healthScore = currentToken.health?.score || 50;
                      const healthColor = healthScore > 60 ? '#00d26a' : healthScore > 30 ? '#f0b90b' : '#ff3b69';
                      
                      return (
                        <div style={{ background: '#0d1117', borderRadius: '8px', border: '1px solid #1a1a2e', overflow: 'hidden' }}>
                          {/* TOP ROW - Token + Badge + Launch + Health */}
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', borderBottom: '1px solid #1a1a2e' }}>
                            {/* Token Name */}
                            <div style={{ padding: '6px 10px', borderRight: '1px solid #1a1a2e' }}>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#f0b90b' }}>${currentToken.ticker}</div>
                              <div style={{ color: '#555', fontSize: '8px' }}>{currentToken.creatorType === 'ai' ? 'AI' : 'PvP'} • {viewerCount} watching</div>
                            </div>
                            {/* Pumping/Dumping Badge */}
                            <div style={{ padding: '6px 8px', borderRight: '1px solid #1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <div style={{
                                padding: '3px 8px',
                                background: isPumping ? '#00d26a22' : '#ff3b6922',
                                border: `1px solid ${isPumping ? '#00d26a' : '#ff3b69'}`,
                                borderRadius: '4px',
                                color: isPumping ? '#00d26a' : '#ff3b69',
                                fontSize: '9px',
                                fontWeight: 'bold',
                              }}>{isPumping ? '🟢 PUMPING' : '🔴 DUMPING'}</div>
                            </div>
                            {/* Since Launch */}
                            <div style={{ padding: '6px 8px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '8px', textTransform: 'uppercase' }}>Launch</div>
                              <div style={{ color: isPumping ? '#00d26a' : '#ff3b69', fontSize: '13px', fontWeight: 'bold' }}>
                                {isPumping ? '+' : ''}{sinceLaunch.toFixed(1)}%
                              </div>
                            </div>
                            {/* Health */}
                            <div style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '8px', textTransform: 'uppercase' }}>🫀 Health</div>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <div style={{ width: '35px', height: '5px', background: '#222', borderRadius: '3px', overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${healthScore}%`, background: `linear-gradient(90deg, ${healthColor}, ${healthColor})`, borderRadius: '3px' }}></div>
                                </div>
                                <span style={{ color: healthColor, fontSize: '11px', fontWeight: 'bold' }}>{healthScore}</span>
                              </div>
                            </div>
                          </div>
                          {/* BOTTOM ROW - Stats Grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
                            <div style={{ padding: '6px 8px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '8px', textTransform: 'uppercase' }}>Price</div>
                              <div style={{ color: '#fff', fontSize: '13px', fontWeight: 'bold' }}>{(currentToken.currentPrice || 0).toFixed(2)}</div>
                            </div>
                            <div style={{ padding: '6px 8px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '8px', textTransform: 'uppercase' }}>MCap</div>
                              <div style={{ color: '#00d26a', fontSize: '13px', fontWeight: 'bold' }}>{formatPB(currentToken.marketCap || 0)}</div>
                            </div>
                            <div style={{ padding: '6px 8px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '8px', textTransform: 'uppercase' }}>Liquidity</div>
                              <div style={{ color: '#00bcd4', fontSize: '13px', fontWeight: 'bold' }}>{formatPB(currentToken.liquidityPool || 0)}</div>
                            </div>
                            <div style={{ padding: '6px 8px', borderRight: '1px solid #1a1a2e', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '8px', textTransform: 'uppercase' }}>Volume</div>
                              <div style={{ color: '#f0b90b', fontSize: '13px', fontWeight: 'bold' }}>{formatPB((currentToken.totalBuyVolume || 0) + (currentToken.totalSellVolume || 0))}</div>
                            </div>
                            <div style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <div style={{ color: '#555', fontSize: '8px', textTransform: 'uppercase' }}>ATH</div>
                              <div style={{ color: '#fff', fontSize: '13px', fontWeight: 'bold' }}>{currentToken.athMultiplier ? `${currentToken.athMultiplier.toFixed(1)}x` : '1.0x'}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Chart - Same as Classic */}
                    {renderChart()}

                    {/* Position Card */}
                    {position && position.invested > 0 && (
                      <div style={{ background: '#111', border: '2px solid #00d26a', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '20px' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#555', fontSize: '9px', textTransform: 'uppercase' }}>Qty</div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{(position.size / 1000).toFixed(1)}K</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#555', fontSize: '9px', textTransform: 'uppercase' }}>Entry</div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>${position.entry?.toFixed(2) || '0.00'}</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#555', fontSize: '9px', textTransform: 'uppercase' }}>Invested</div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#f0b90b' }}>{formatPB(position.invested)}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#555', fontSize: '9px', textTransform: 'uppercase' }}>P/L</div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: pnl >= 0 ? '#00d26a' : '#ff3b69' }}>
                            {pnl >= 0 ? '+' : ''}{formatPB(pnl)}
                          </div>
                          <div style={{ fontSize: '11px', color: pnl >= 0 ? '#00d26a' : '#ff3b69' }}>
                            {position.invested > 0 ? `${pnl >= 0 ? '+' : ''}${((pnl / position.invested) * 100).toFixed(1)}%` : '0%'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Trade Buttons - Constrained width for Locked In mode */}
                    <div style={{ maxWidth: chartFullscreen ? '700px' : '100%', margin: chartFullscreen ? '0 auto' : '0' }}>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#2d8f7b', fontSize: '10px', fontWeight: 'bold', marginBottom: '6px', textAlign: 'center' }}>🟢 BUY</div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {[10000, 50000, 100000, 250000, 500000, 1000000].map(amount => (
                              <button
                                key={amount}
                                onClick={() => executeApeIn(amount)}
                                disabled={gameWallet < amount || gameState === 'rugged'}
                                style={{
                                  flex: 1,
                                  padding: '12px 6px',
                                  background: '#0a1612',
                                  border: '2px solid #2d8f7b',
                                  borderRadius: '6px',
                                  color: '#2d8f7b',
                                  fontWeight: 'bold',
                                  fontSize: '11px',
                                  cursor: gameWallet >= amount && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                                  opacity: gameWallet >= amount && gameState !== 'rugged' ? 1 : 0.5,
                                }}
                              >{formatPB(amount)}</button>
                            ))}
                          </div>
                          {(() => {
                            const poolLiq = currentToken?.liquidityPool || 0;
                            const maxBuy = Math.floor(poolLiq * 0.05);
                            const nextLocked = [50000, 100000, 250000, 500000, 1000000].find(amt => amt > maxBuy);
                            if (nextLocked && poolLiq > 0) {
                              const unlockAt = nextLocked * 20;
                              return <div style={{ color: '#555', fontSize: '9px', marginTop: '6px', textAlign: 'center' }}>🔒 {formatPB(nextLocked)} unlocks at {formatPB(unlockAt)} liq</div>;
                            }
                            return null;
                          })()}
                          {/* Custom buy input */}
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginTop: '6px' }}>
                            <input
                              type="number"
                              value={stake}
                              onChange={(e) => setStake(Math.max(1000, parseInt(e.target.value) || 0))}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #2d8f7b',
                                backgroundColor: '#0a0d12',
                                color: '#2d8f7b',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                outline: 'none',
                              }}
                              placeholder="Custom..."
                            />
                            <button
                              onClick={() => executeApeIn(stake)}
                              disabled={gameWallet < stake || gameState === 'rugged'}
                              style={{
                                padding: '8px 12px',
                                background: '#0a1612',
                                border: '2px solid #2d8f7b',
                                borderRadius: '6px',
                                color: '#2d8f7b',
                                fontWeight: 'bold',
                                fontSize: '10px',
                                cursor: gameWallet >= stake && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                                opacity: gameWallet >= stake && gameState !== 'rugged' ? 1 : 0.5,
                              }}
                            >BUY</button>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#c75454', fontSize: '10px', fontWeight: 'bold', marginBottom: '6px', textAlign: 'center' }}>🔴 SELL</div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {[25, 50, 75, 100].map(pct => (
                              <button
                                key={pct}
                                onClick={() => sell(pct)}
                                disabled={!position?.size || position.size <= 0 || gameState === 'rugged'}
                                style={{
                                  flex: 1,
                                  padding: '12px 6px',
                                  background: '#160a0a',
                                  border: '2px solid #c75454',
                                  borderRadius: '6px',
                                  color: '#c75454',
                                  fontWeight: 'bold',
                                  fontSize: '11px',
                                  cursor: position?.size > 0 && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                                  opacity: position?.size > 0 && gameState !== 'rugged' ? 1 : 0.5,
                                }}
                              >{pct}%</button>
                            ))}
                          </div>
                          {/* Bag Value Display - classic view */}
                          {(() => {
                            const multiplier = position.entry > 0 ? currentPrice / position.entry : 1;
                            const bagValue = Math.floor(position.invested * multiplier) || 0;
                            return (
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '6px 10px',
                                background: '#0d1117',
                                borderRadius: '4px',
                                border: '1px solid #1a1a2e',
                                marginTop: '6px'
                              }}>
                                <span style={{ color: '#888', fontSize: '10px' }}>💰 BAG VALUE:</span>
                                <span style={{
                                  color: multiplier >= 1 ? '#00d26a' : '#ff3b69',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace'
                                }}>
                                  {formatPB(bagValue)} ({position.size?.toLocaleString() || 0} tokens)
                                </span>
                              </div>
                            );
                          })()}

                          {/* Custom USD sell input - classic view */}
                          {(() => {
                            const multiplier = position.entry > 0 ? currentPrice / position.entry : 1;
                            const bagValue = Math.floor(position.invested * multiplier) || 0;
                            const maxUSD = bagValue;
                            const usdAmount = parseFloat(customSellUSD) || 0;
                            const usdPercent = bagValue > 0 ? Math.min(100, Math.max(0, (usdAmount / bagValue) * 100)) : 0;

                            return (
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginTop: '6px' }}>
                                <span style={{ color: '#c75454', fontSize: '13px', fontWeight: 'bold' }}>$</span>
                                <input
                                  type="number"
                                  step="any"
                                  value={customSellUSD}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || val === '0' || val === '0.') {
                                      setCustomSellUSD(val);
                                    } else {
                                      const num = parseFloat(val);
                                      if (!isNaN(num) && num >= 0) {
                                        setCustomSellUSD(num <= maxUSD ? val : maxUSD.toString());
                                      }
                                    }
                                  }}
                                  style={{
                                    width: '70px',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    border: '1px solid #c75454',
                                    backgroundColor: '#160a0a',
                                    color: '#c75454',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    outline: 'none',
                                    textAlign: 'right',
                                    fontFamily: 'monospace',
                                  }}
                                  placeholder="0.01"
                                />
                                <span style={{ color: '#555', fontSize: '9px', minWidth: '40px' }}>
                                  = {usdPercent.toFixed(4)}%
                                </span>
                                <button
                                  onClick={() => {
                                    if (usdPercent > 0 && usdPercent <= 100) {
                                      sell(usdPercent);
                                      setCustomSellUSD('');
                                    }
                                  }}
                                  disabled={usdPercent <= 0 || usdPercent > 100 || gameState === 'rugged'}
                                  style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    background: '#160a0a',
                                    border: '2px solid #c75454',
                                    borderRadius: '6px',
                                    color: '#c75454',
                                    fontWeight: 'bold',
                                    fontSize: '10px',
                                    cursor: usdPercent > 0 && usdPercent <= 100 && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                                    opacity: usdPercent > 0 && usdPercent <= 100 && gameState !== 'rugged' ? 1 : 0.5,
                                  }}
                                >SELL ${customSellUSD || '0'}</button>
                                <button
                                  onClick={() => setCustomSellUSD(maxUSD.toString())}
                                  style={{
                                    padding: '8px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #444',
                                    cursor: 'pointer',
                                    backgroundColor: '#111',
                                    color: '#666',
                                    fontWeight: 'bold',
                                    fontSize: '8px',
                                  }}
                                >MAX</button>
                              </div>
                            );
                          })()}

                          {/* Custom % sell input - classic view (secondary) */}
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginTop: '6px' }}>
                            <input
                              type="number"
                              value={customSellPct}
                              onChange={(e) => setCustomSellPct(Math.min(100, Math.max(1, parseInt(e.target.value) || 0)))}
                              style={{
                                width: '50px',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #444',
                                backgroundColor: '#0a0d12',
                                color: '#666',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                outline: 'none',
                                textAlign: 'center',
                              }}
                              placeholder="%"
                            />
                            <span style={{ color: '#555', fontSize: '10px' }}>%</span>
                            <button
                              onClick={() => sell(customSellPct)}
                              disabled={!position?.size || position.size <= 0 || gameState === 'rugged'}
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                background: '#111',
                                border: '1px solid #444',
                                borderRadius: '6px',
                                color: '#666',
                                fontWeight: 'bold',
                                fontSize: '10px',
                                cursor: position?.size > 0 && gameState !== 'rugged' ? 'pointer' : 'not-allowed',
                                opacity: position?.size > 0 && gameState !== 'rugged' ? 1 : 0.5,
                              }}
                            >SELL {customSellPct}%</button>
                          </div>
                        </div>
                      </div>

                      {/* Back to Lobby */}
                      <button
                        onClick={() => setView('lobby')}
                        style={{
                          width: '100%',
                          marginTop: '10px',
                          padding: '14px',
                          background: '#111',
                          border: '2px solid #f0b90b',
                          borderRadius: '8px',
                          color: '#f0b90b',
                          fontWeight: 'bold',
                          fontSize: '13px',
                          cursor: 'pointer',
                          textAlign: 'center',
                        }}
                      >🏠 Back to Token List</button>
                    </div>
                  </div>

                  {/* RIGHT COLUMN - Info Panels (hidden in fullscreen) */}
                  {!chartFullscreen && (
                  <div style={{ flex: '0 0 300px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* 👥 IN THIS TRADE - Classic Style */}
                    {(() => {
                      const userIsCreator = currentToken?.isUserLaunched && currentToken?.creator && 
                        handcashUser?.handle?.replace('$', '').toLowerCase() === currentToken?.creator?.replace('$', '').toLowerCase();
                      
                      return (
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '6px',
                          padding: '12px',
                          backgroundColor: userIsCreator ? '#0d0d1a' : '#0d0d12', 
                          borderRadius: '8px',
                          border: userIsCreator ? '1px solid #f0b90b33' : '1px solid #1a1a2e',
                        }}>
                          <span style={{ color: '#555', fontSize: '9px', marginRight: '4px', width: '100%', marginBottom: '6px' }}>
                            👥 IN THIS TRADE {userIsCreator && <span style={{ color: '#f0b90b' }}>• 👑 YOU SEE HOLDINGS</span>}
                          </span>
                          {(() => {
                            const seenHandles = new Set();
                            return tokenPlayers.filter(p => {
                              if (!p.isActive) return false;
                              const handle = p.handle?.replace(/^\$/, '') || 'unknown';
                              if (seenHandles.has(handle)) return false;
                              seenHandles.add(handle);
                              return true;
                            }).map(p => {
                              const isCreator = currentToken?.isUserLaunched && currentToken?.creator && 
                                (p.handle === currentToken.creator || p.handle === '$' + currentToken.creator || '$' + p.handle === currentToken.creator);
                              return (
                              <div key={p.handle} style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '4px', 
                                padding: '4px 8px', 
                                backgroundColor: isCreator ? '#1a1508' : '#111', 
                                borderRadius: '6px', 
                                fontSize: '10px',
                                border: isCreator ? '1px solid #f0b90b44' : '1px solid #222',
                              }}>
                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: p.color }} />
                                <span style={{ color: isCreator ? '#f0b90b' : '#888' }}>{p.handle}</span>
                                {isCreator && <span style={{ fontSize: '8px', color: '#f0b90b', backgroundColor: '#1a1508', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>CREATOR</span>}
                                {userIsCreator && !isCreator && p.invested && (
                                  <span style={{ fontSize: '9px', color: '#00d26a', marginLeft: '4px' }}>
                                    {(p.invested / 1000).toFixed(0)}K
                                  </span>
                                )}
                              </div>
                              );
                            });
                          })()}
                          {gameState === 'playing' && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '4px', 
                              padding: '4px 8px', 
                              backgroundColor: '#0d1a0d', 
                              borderRadius: '6px', 
                              fontSize: '10px', 
                              border: '1px solid #0f2f0f',
                            }}>
                              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#f0b90b' }} />
                              <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>YOU</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* 📊 LIVE TRADES - Classic Style */}
                    <div style={{
                      backgroundColor: '#0a0a0f',
                      borderRadius: '8px',
                      border: '1px solid #1a1a2e',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        backgroundColor: '#0d0d12',
                        borderBottom: '1px solid #1a1a2e',
                      }}>
                        <span style={{ color: '#5e6673', fontSize: '10px', fontWeight: 'bold' }}>📊 LIVE TRADES</span>
                        <span style={{ color: '#444', fontSize: '9px' }}>{transactions.length} total</span>
                      </div>
                      
                      <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        {(() => {
                          const seenIds = new Set();
                          const uniqueTxs = transactions.filter(tx => {
                            if ((tx.sats || 0) <= 0) return false;
                            if (!tx.id) return true;
                            if (seenIds.has(tx.id)) return false;
                            seenIds.add(tx.id);
                            return true;
                          });
                          
                          return uniqueTxs.slice(0, 15).map((tx, idx) => {
                            const isBuy = tx.type === 'buy';
                            const isLarge = (tx.sats || 0) >= 25000;
                            const isHuge = (tx.sats || 0) >= 100000;
                            const timeAgo = tx.timestamp ? Math.floor((Date.now() - tx.timestamp) / 1000) : 0;
                            const timeStr = timeAgo < 60 ? `${timeAgo}s` : timeAgo < 3600 ? `${Math.floor(timeAgo/60)}m` : `${Math.floor(timeAgo/3600)}h`;
                            
                            return (
                              <div 
                                key={tx.id || idx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px 12px',
                                  borderBottom: '1px solid #111',
                                  backgroundColor: tx.isYou 
                                    ? 'rgba(240,185,11,0.08)' 
                                    : isHuge 
                                      ? (isBuy ? 'rgba(22,199,132,0.1)' : 'rgba(234,57,67,0.1)')
                                      : 'transparent',
                                }}
                              >
                                <div style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '6px',
                                  backgroundColor: isBuy ? 'rgba(22,199,132,0.2)' : 'rgba(234,57,67,0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  fontSize: '12px',
                                }}>
                                  {tx.isYou ? '👤' : isHuge ? (isBuy ? '🐋' : '💀') : isLarge ? (isBuy ? '🦈' : '📉') : (isBuy ? '↗' : '↘')}
                                </div>
                                
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ 
                                      color: isBuy ? '#16c784' : '#ea3943', 
                                      fontWeight: 'bold', 
                                      fontSize: '10px',
                                    }}>
                                      {isBuy ? 'BUY' : 'SELL'}
                                    </span>
                                    {tx.isYou && (
                                      <span style={{ 
                                        fontSize: '8px', 
                                        padding: '1px 4px', 
                                        backgroundColor: '#f0b90b', 
                                        color: '#000', 
                                        borderRadius: '3px',
                                        fontWeight: 'bold',
                                      }}>YOU</span>
                                    )}
                                    {isHuge && !tx.isYou && (
                                      <span style={{ 
                                        fontSize: '8px', 
                                        padding: '1px 4px', 
                                        backgroundColor: isBuy ? '#16c784' : '#ea3943', 
                                        color: '#000', 
                                        borderRadius: '3px',
                                        fontWeight: 'bold',
                                      }}>{isBuy ? 'WHALE' : 'DUMP'}</span>
                                    )}
                                  </div>
                                  <div style={{ 
                                    color: '#666', 
                                    fontSize: '9px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {tx.isYou ? 'Your trade' : (tx.wallet || tx.handle || 'Unknown')} • {timeStr} ago
                                  </div>
                                </div>
                                
                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                  <div style={{ 
                                    color: tx.isYou ? '#f0b90b' : (isBuy ? '#16c784' : '#ea3943'),
                                    fontWeight: 'bold',
                                    fontSize: isHuge ? '12px' : '11px',
                                  }}>
                                    {isBuy ? '+' : '-'}{formatPB(tx.sats || 0)}
                                  </div>
                                  <div style={{ color: '#555', fontSize: '9px' }}>
                                    @${((tx.sats || 0) * satToUsd).toFixed(2)}
                                    {tx.txId && (
                                      <a 
                                        href={`https://whatsonchain.com/tx/${tx.txId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ 
                                          marginLeft: '4px', 
                                          color: '#00d26a',
                                          textDecoration: 'none',
                                        }}
                                        title="View on-chain transaction"
                                      >
                                        ⛓️
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                        
                        {transactions.filter(tx => (tx.sats || 0) > 0).length === 0 && (
                          <div style={{ padding: '24px', textAlign: 'center', color: '#444', fontSize: '11px' }}>
                            ⏳ Waiting for trades...
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Price Impact Preview */}
                    {(() => {
                      const poolLiquidity = currentToken?.liquidityPool || currentToken?.liquidity || 0;
                      const tokenReserve = currentToken?.tokenReserve || 1000000000;
                      const previewStake = 50000;
                      const k = poolLiquidity * tokenReserve;
                      const rakeAmt = Math.floor(previewStake * 0.01);
                      const netAmt = previewStake - rakeAmt;
                      const newLiq = poolLiquidity + netAmt;
                      const newTokenRes = k / newLiq;
                      const tokensRcvd = tokenReserve - newTokenRes;
                      const newPrc = newLiq / newTokenRes;
                      const currPrc = poolLiquidity / tokenReserve;
                      const prcImpact = poolLiquidity > 0 ? ((newPrc - currPrc) / currPrc) * 100 : 0;
                      const avgPrc = tokensRcvd > 0 ? previewStake / tokensRcvd : 0;
                      
                      return poolLiquidity > 0 ? (
                        <div style={{ background: '#0a0a12', border: '1px solid #333', borderRadius: '8px', padding: '12px' }}>
                          <div style={{ color: '#888', fontWeight: 'bold', marginBottom: '8px', fontSize: '10px' }}>
                            📊 PRICE IMPACT (for $50K buy)
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '10px' }}>
                            <div>
                              <div style={{ color: '#666', fontSize: '9px' }}>You'll receive</div>
                              <div style={{ color: '#00d26a', fontWeight: 'bold' }}>~{(tokensRcvd / 1000000).toFixed(2)}M tokens</div>
                            </div>
                            <div>
                              <div style={{ color: '#666', fontSize: '9px' }}>Avg buy price</div>
                              <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>{avgPrc.toFixed(6)}</div>
                            </div>
                            <div>
                              <div style={{ color: '#666', fontSize: '9px' }}>Price impact</div>
                              <div style={{ color: prcImpact > 10 ? '#ff3b69' : prcImpact > 5 ? '#f0b90b' : '#00d26a', fontWeight: 'bold' }}>+{prcImpact.toFixed(2)}%</div>
                            </div>
                            <div>
                              <div style={{ color: '#666', fontSize: '9px' }}>1% rake</div>
                              <div style={{ color: '#888' }}>{formatPB(rakeAmt)}</div>
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Token Chat - Full with input */}
                    <div style={{ background: '#0a0a0f', border: '1px solid #1a1a2e', borderRadius: '8px', padding: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ color: '#555', fontSize: '9px' }}>💬 TOKEN CHAT</span>
                        {mutedPlayers.length > 0 && (
                          <span style={{ color: '#666', fontSize: '8px' }}>
                            {mutedPlayers.length} muted • <button onClick={() => { setMutedPlayers([]); localStorage.removeItem('paperHands_mutedPlayers'); }} style={{ background: 'none', border: 'none', color: '#f0b90b', fontSize: '8px', cursor: 'pointer', padding: 0 }}>unmute</button>
                          </span>
                        )}
                      </div>
                      <div ref={tokenChatRef} style={{ height: '80px', overflowY: 'auto', marginBottom: '6px' }}>
                        {tokenChat.filter(m => m.handle && m.handle.startsWith('$') && !m.isAction && !m.isAlpha && !m.isRug && !m.isSlippage && !isPlayerMuted(m.handle)).length === 0 ? (
                          <div style={{ color: '#333', fontSize: '9px', fontStyle: 'italic', textAlign: 'center', padding: '15px 0' }}>
                            No player messages yet...
                          </div>
                        ) : (
                          tokenChat
                            .filter(m => m.handle && m.handle.startsWith('$') && !m.isAction && !m.isAlpha && !m.isRug && !m.isSlippage && !isPlayerMuted(m.handle))
                            .map(m => (
                            <div key={m.id} style={{ fontSize: '10px', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: m.isPremiumUser ? '#f0b90b' : '#666', fontWeight: 'bold' }}>{m.handle}:</span>
                              <span style={{ color: '#aaa', flex: 1 }}>{m.text}</span>
                            </div>
                          ))
                        )}
                      </div>
                      
                      {isPremium ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <input 
                            type="text" 
                            value={chatInput} 
                            onChange={e => setChatInput(e.target.value)} 
                            onKeyPress={e => {
                              if (e.key === 'Enter' && chatInput.trim()) {
                                sendTokenChat(chatInput.trim());
                                setChatInput('');
                              }
                            }}
                            placeholder="Chat..." 
                            style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #1a1a2e', backgroundColor: '#111', color: '#fff', fontSize: '10px', outline: 'none' }} 
                          />
                          <button 
                            onClick={() => {
                              if (chatInput.trim()) {
                                sendTokenChat(chatInput.trim());
                                setChatInput('');
                              }
                            }}
                            style={{ padding: '8px 12px', borderRadius: '6px', border: 'none', backgroundColor: '#f0b90b', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '10px' }}
                          >
                            Send
                          </button>
                        </div>
                      ) : (
                        <div 
                          onClick={() => setShowDeposit(true)}
                          style={{ padding: '8px', borderRadius: '6px', border: '1px solid #f0b90b', backgroundColor: 'rgba(240,185,11,0.05)', color: '#f0b90b', fontSize: '9px', textAlign: 'center', cursor: 'pointer' }}
                        >
                          👑 Premium required to chat
                        </div>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* 📺 CLASSIC VIEW - Original trading interface */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {tradingViewMode === 'classic' && (
          <>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '8px',
            padding: '12px 16px',
            backgroundColor: gameState === 'rugged' ? '#1a0a0a' : currentToken?.hasMooned ? '#0a1a0a' : '#0a0a0f',
            borderRadius: '10px',
            border: gameState === 'rugged' ? '1px solid #ff3b6933' : currentToken?.hasMooned ? '2px solid #00d26a55' : '1px solid #1a1a2e',
            overflowX: 'auto',
          }}>
            <div style={{ display: 'flex', gap: isMobile ? '12px' : '24px', flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', minWidth: isMobile ? '60px' : 'auto' }}>
                <div style={{ fontSize: '10px', color: '#888', marginBottom: '2px' }}>PRICE</div>
                <div style={{ fontSize: isMobile ? '14px' : '16px', color: gameState === 'rugged' ? '#ff3b69' : '#fff', fontWeight: 'bold' }}>
                  {gameState === 'rugged' ? '0' : (currentToken.currentPrice || 0).toFixed(6)}
                </div>
              </div>
              <div style={{ textAlign: 'center', minWidth: isMobile ? '50px' : 'auto' }}>
                <div style={{ fontSize: '10px', color: '#888', marginBottom: '2px' }} title="Market Cap = Total Supply × Price">MCAP</div>
                <div style={{ fontSize: isMobile ? '14px' : '16px', color: '#f0b90b', fontWeight: 'bold' }}>
                  {gameState === 'rugged' ? '$0' : formatPB(currentToken.marketCap || 50000)}
                </div>
              </div>
              <div style={{ textAlign: 'center', minWidth: isMobile ? '50px' : 'auto' }}>
                <div style={{ fontSize: '10px', color: '#888', marginBottom: '2px' }} title="Pool liquidity. If this drains fast, RUG incoming!">LIQUIDITY</div>
                <div style={{ fontSize: isMobile ? '14px' : '16px', color: gameState === 'rugged' ? '#ff3b69' : '#00d26a', fontWeight: 'bold' }}>
                  {gameState === 'rugged' ? '$0' : formatPB(currentToken.liquidityPool || 10000)}
                </div>
              </div>
              <div style={{ textAlign: 'center', minWidth: isMobile ? '70px' : 'auto' }}>
                {(() => {
                  const buyVol = currentToken.totalBuyVolume || 0;
                  const sellVol = currentToken.totalSellVolume || 0;
                  const totalVol = buyVol + sellVol;
                  const buyPercent = totalVol > 0 ? (buyVol / totalVol) * 100 : 50;
                  const isHot = totalVol > 5000;
                  const isBullish = buyVol > sellVol * 1.2;
                  const isBearish = sellVol > buyVol * 1.2;
                  return (
                    <>
                      <div style={{ fontSize: '10px', color: '#888', marginBottom: '2px' }} title="Green = more buying (bullish). Red = more selling (bearish). Watch the ratio bar!">
                        VOLUME {isHot && <span style={{ color: '#ff6b00' }}>🔥</span>}
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', color: isBullish ? '#00d26a' : isBearish ? '#ff3b69' : '#9b59b6' }}>
                        {formatPB(totalVol)}
                      </div>
                      {/* Buy/Sell ratio bar */}
                      <div style={{ 
                        display: 'flex', 
                        height: '4px', 
                        width: '60px', 
                        margin: '4px auto 0',
                        borderRadius: '2px',
                        overflow: 'hidden',
                        backgroundColor: '#1a1a2e',
                      }}>
                        <div style={{ width: `${buyPercent}%`, backgroundColor: '#00d26a', transition: 'width 0.3s' }} />
                        <div style={{ width: `${100 - buyPercent}%`, backgroundColor: '#ff3b69', transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ fontSize: '9px', color: '#777', marginTop: '3px' }}>
                        <span style={{ color: '#00d26a' }}>B:{formatPB(buyVol)}</span>
                        {' / '}
                        <span style={{ color: '#ff3b69' }}>S:{formatPB(sellVol)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div style={{ textAlign: 'center', minWidth: isMobile ? '40px' : 'auto' }}>
                <div style={{ fontSize: '10px', color: '#888', marginBottom: '2px' }} title="All-Time High. How high did it pump? Far from ATH = might be dumping">ATH</div>
                <div style={{ fontSize: isMobile ? '14px' : '16px', color: '#9b59b6', fontWeight: 'bold' }}>
                  {currentToken.athPrice && currentToken.initialPrice ? ((currentToken.athPrice / currentToken.initialPrice).toFixed(1) + 'x') : '1.0x'}
                </div>
              </div>
              {/* Market Sentiment Badge - inline on mobile */}
              {(() => {
                const sentiment = calculateMarketSentiment(currentToken);
                return (
                  <div style={{
                    backgroundColor: '#0a0a12',
                    border: `1px solid ${sentiment.color}44`,
                    borderRadius: '8px',
                    padding: isMobile ? '4px 8px' : '6px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    minWidth: isMobile ? '80px' : 'auto',
                  }}>
                    <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{sentiment.emoji}</span>
                    <div>
                      <div style={{ color: sentiment.color, fontSize: isMobile ? '10px' : '12px', fontWeight: 'bold' }}>{sentiment.label}</div>
                      {!isMobile && <div style={{ color: '#555', fontSize: '8px' }}>Market Sentiment</div>}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Launch Context */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '16px',
            marginBottom: '12px',
            padding: '6px',
            backgroundColor: '#08080c',
            borderRadius: '4px',
            fontSize: '10px',
            color: '#555',
          }}>
            <span>Launch: {formatPB(Math.floor((currentToken.totalSupply || 1000000) * (currentToken.initialPrice || 0)))} MCap</span>
            <span>•</span>
            <span>{formatPB(currentToken.initialLiquidity || 0)} Liq</span>
            <span>•</span>
            <span>Supply: {((currentToken.totalSupply || 1000000) >= 1000000000 ? (currentToken.totalSupply / 1000000000).toFixed(0) + 'B' : (currentToken.totalSupply / 1000000).toFixed(0) + 'M')}</span>
            <span>•</span>
            <span>ATH: {currentToken.athPrice && currentToken.initialPrice ? ((currentToken.athPrice / currentToken.initialPrice).toFixed(1) + 'x') : '1.0x'}</span>
          </div>

          {/* Token Description - Only shown for user-launched tokens with descriptions */}
          {currentToken?.isUserLaunched && currentToken?.description && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '12px',
              padding: '10px 16px',
              backgroundColor: '#0d0d1a',
              borderRadius: '8px',
              border: '1px solid #f0b90b33',
            }}>
              <span style={{ color: '#f0b90b', fontSize: '12px' }}>📝</span>
              <span style={{ color: '#ccc', fontSize: '12px', fontStyle: 'italic' }}>
                "{currentToken.description}"
              </span>
              <span style={{ color: '#666', fontSize: '10px' }}>— {currentToken.creator || currentToken.createdBy || 'Creator'}</span>
            </div>
          )}

          {/* Keyframes */}
          <style>{`
            @keyframes floatUp { 0% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-20px); } }
            @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
            @keyframes soundBar1 { 0%, 100% { height: 3px; } 50% { height: 10px; } }
            @keyframes soundBar2 { 0%, 100% { height: 7px; } 50% { height: 3px; } }
            @keyframes soundBar3 { 0%, 100% { height: 5px; } 50% { height: 8px; } }
            @keyframes fadeIn { 0% { opacity: 0; transform: translateY(-4px); } 100% { opacity: 1; transform: translateY(0); } }
          `}</style>
          
          {/* ============== TWO-COLUMN LAYOUT (tabbed on mobile) ============== */}
          {/* Mobile: Show tabs to switch between Trade and Info */}
          {isMobile && !chartFullscreen && (
            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
              <button
                onClick={() => setMobileTokenTab && setMobileTokenTab('trade')}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: (!mobileTokenTab || mobileTokenTab === 'trade') ? '2px solid #f0b90b' : '1px solid #333',
                  backgroundColor: (!mobileTokenTab || mobileTokenTab === 'trade') ? 'rgba(240,185,11,0.15)' : '#0a0a0f',
                  color: (!mobileTokenTab || mobileTokenTab === 'trade') ? '#f0b90b' : '#666',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                📈 Trade
              </button>
              <button
                onClick={() => setMobileTokenTab && setMobileTokenTab('info')}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: mobileTokenTab === 'info' ? '2px solid #9b59b6' : '1px solid #333',
                  backgroundColor: mobileTokenTab === 'info' ? 'rgba(155,89,182,0.15)' : '#0a0a0f',
                  color: mobileTokenTab === 'info' ? '#9b59b6' : '#666',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                🔮 Info & Bets
              </button>
            </div>
          )}
          
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row', 
            gap: isMobile ? '8px' : '16px', 
            alignItems: 'flex-start',
            transition: 'all 0.3s ease',
          }}>
            
            {/* LEFT COLUMN: Chart, Position, Trading - Full width in focus mode */}
            {(!isMobile || !mobileTokenTab || mobileTokenTab === 'trade' || chartFullscreen) && (
            <div style={{ 
              flex: chartFullscreen ? '1 1 100%' : '1 1 58%', 
              minWidth: 0, 
              width: (isMobile || chartFullscreen) ? '100%' : 'auto',
              transition: 'all 0.3s ease',
            }}>
          
          {/* Price/Position Display - Context aware */}
          <div style={{ textAlign: 'center', marginBottom: '4px' }}>
            {(gameState === 'spectating' || gameState === 'won' || (gameState === 'playing' && !(position?.size > 0))) && (
              <>
                {/* Since Launch with Focus Mode buttons on sides */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '2px' }}>
                  {/* Left Focus Mode button */}
                  {!isMobile && (
                    <button
                      onClick={() => setChartFullscreen(!chartFullscreen)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: chartFullscreen ? '1px solid #00d26a' : '1px solid #333',
                        backgroundColor: chartFullscreen ? 'rgba(0,210,106,0.15)' : 'transparent',
                        color: chartFullscreen ? '#00d26a' : '#666',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                      title={chartFullscreen ? 'Show side panel' : 'Focus on chart'}
                    >
                      {chartFullscreen ? '◀' : '⊕'}
                    </button>
                  )}
                  
                  <div>
                    <div style={{ fontSize: '12px', color: '#888' }}>Since Launch</div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: currentPrice >= (currentToken.initialPrice || 1) ? '#00d26a' : '#ff3b69' }}>
                      {(() => {
                        const changePercent = ((currentPrice / (currentToken.initialPrice || 1)) - 1) * 100;
                        return (changePercent >= 0 ? '+' : '') + changePercent.toFixed(1) + '%';
                      })()}
                    </div>
                  </div>
                  
                  {/* Right Focus Mode button */}
                  {!isMobile && (
                    <button
                      onClick={() => setChartFullscreen(!chartFullscreen)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: chartFullscreen ? '1px solid #00d26a' : '1px solid #333',
                        backgroundColor: chartFullscreen ? 'rgba(0,210,106,0.15)' : 'transparent',
                        color: chartFullscreen ? '#00d26a' : '#666',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                      title={chartFullscreen ? 'Show side panel' : 'Focus on chart'}
                    >
                      {chartFullscreen ? '▶' : '⊕'}
                    </button>
                  )}
                </div>
                
                {/* 🧠 Health for Spectators - Premium sees full, non-premium sees teaser */}
                {isPremium && currentToken?.health && currentToken.state === 'active' && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px', 
                    marginTop: '8px',
                    backgroundColor: currentToken.health.score > 70 ? 'rgba(0,210,106,0.08)' : 
                                     currentToken.health.score > 40 ? 'rgba(240,185,11,0.08)' : 
                                     currentToken.health.score > 20 ? 'rgba(255,107,0,0.1)' : 'rgba(255,59,105,0.15)',
                    borderRadius: '6px',
                    border: `1px solid ${currentToken.health.score > 70 ? '#00d26a44' : 
                                         currentToken.health.score > 40 ? '#f0b90b44' : 
                                         currentToken.health.score > 20 ? '#ff6b0066' : '#ff3b6966'}`,
                  }}>
                    <span style={{ fontSize: '16px' }}>🧠</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#888', fontSize: '10px' }}>HEALTH</span>
                        <span style={{ 
                          fontSize: '12px', 
                          fontWeight: 'bold',
                          color: currentToken.health.score > 70 ? '#00d26a' : 
                                 currentToken.health.score > 40 ? '#f0b90b' : 
                                 currentToken.health.score > 20 ? '#ff6b00' : '#ff3b69'
                        }}>
                          {currentToken.health.score}/100
                        </span>
                      </div>
                      <div style={{ 
                        height: '4px', 
                        backgroundColor: '#1a1a2e', 
                        borderRadius: '2px',
                        marginTop: '4px',
                        overflow: 'hidden',
                      }}>
                        <div style={{ 
                          width: `${currentToken.health.score}%`, 
                          height: '100%',
                          backgroundColor: currentToken.health.score > 70 ? '#00d26a' : 
                                           currentToken.health.score > 40 ? '#f0b90b' : 
                                           currentToken.health.score > 20 ? '#ff6b00' : '#ff3b69',
                          borderRadius: '2px',
                        }} />
                      </div>
                    </div>
                  </div>
                )}
                
                {!isPremium && currentToken?.health && currentToken.state === 'active' && (
                  <div 
                    onClick={() => setShowDeposit(true)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px', 
                      marginTop: '8px',
                      backgroundColor: 'rgba(240,185,11,0.05)',
                      borderRadius: '6px',
                      border: '1px dashed #f0b90b44',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>🧠</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#888', fontSize: '10px' }}>HEALTH SCORE</div>
                      <div style={{ color: '#555', fontSize: '9px' }}>See rug signals before others</div>
                    </div>
                    <span style={{ color: '#f0b90b', fontSize: '10px', fontWeight: 'bold' }}>👑 PRO</span>
                  </div>
                )}
              </>
            )}
            {gameState === 'rugged' && (
              <div>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#ff0040' }}>0.00x</div>
                <div style={{ color: '#ff3b69', fontSize: '12px' }}>💀 RUGGED - Lost {formatPB(position.invested)}</div>
                {ruggedPlayersCount > 0 && (
                  <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '4px', backgroundColor: '#1a0a0a', padding: '4px 8px', borderRadius: '4px', display: 'inline-block' }}>
                    👥 {ruggedPlayersCount} player{ruggedPlayersCount !== 1 ? 's' : ''} got rugged
                  </div>
                )}
              </div>
            )}
            {gameState === 'won' && <div style={{ color: '#00d26a', fontSize: '12px' }}>💰 Nice trade!</div>}
          </div>
          
          {/* Mobile Focus Mode Button */}
          {isMobile && (
            <button
              onClick={() => setChartFullscreen(!chartFullscreen)}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '8px',
                borderRadius: '8px',
                border: chartFullscreen ? '2px solid #00d26a' : '1px solid #333',
                backgroundColor: chartFullscreen ? 'rgba(0,210,106,0.15)' : '#0a0a0f',
                color: chartFullscreen ? '#00d26a' : '#888',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {chartFullscreen ? '✕ Exit Focus Mode' : '🎯 Focus Mode - Full Screen Chart'}
            </button>
          )}
          
          {/* Chart - Always visible */}
          <div 
            key={`chart-container-${chartFullscreen ? 'full' : 'normal'}`}
            style={{ 
              marginBottom: '8px', 
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              justifyContent: 'center',
              maxWidth: chartFullscreen ? '100%' : '100%',
              margin: '0 auto 8px auto',
            }}>
            {renderChart()}
          </div>
          
          {/* 📊 AMM INFO PANEL - Always visible under chart */}
          {(() => {
            const poolLiquidity = currentToken?.liquidityPool || currentToken?.liquidity || 0;
            const tokenReserve = currentToken?.tokenReserve || 1000000000;
            const currentPrice = poolLiquidity / tokenReserve;
            const isUserToken = currentToken?.isUserLaunched;
            
            // Calculate price impact for current stake
            const k = poolLiquidity * tokenReserve;
            const rakeAmount = Math.floor(stake * 0.01);
            const netAmount = stake - rakeAmount;
            const newLiquidity = poolLiquidity + netAmount;
            const newTokenReserve = k / newLiquidity;
            const tokensReceived = tokenReserve - newTokenReserve;
            const newPrice = newLiquidity / newTokenReserve;
            const priceImpact = poolLiquidity > 0 ? ((newPrice - currentPrice) / currentPrice) * 100 : 0;
            const avgPrice = tokensReceived > 0 ? stake / tokensReceived : 0;
            
            // Depth levels for mini chart
            const depthLevels = [1, 2, 5, 10, 20].map(pct => {
              const targetPrice = currentPrice * (1 + pct/100);
              const newReserve = Math.sqrt(k / targetPrice);
              const newLiq = k / newReserve;
              return { pct, satsNeeded: newLiq - poolLiquidity };
            });
            const sellLevels = [1, 2, 5, 10, 20].map(pct => {
              const targetPrice = currentPrice * (1 - pct/100);
              if (targetPrice <= 0) return { pct, satsOut: 0 };
              const newReserve = Math.sqrt(k / targetPrice);
              const newLiq = k / newReserve;
              return { pct, satsOut: poolLiquidity - newLiq };
            });
            const maxSats = Math.max(
              ...depthLevels.map(d => d.satsNeeded),
              ...sellLevels.map(d => d.satsOut || 0)
            );
            
            return poolLiquidity > 0 ? (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '8px',
                marginBottom: '8px',
              }}>
                {/* Price Impact Preview */}
                <div style={{ 
                  backgroundColor: '#0a0a12', 
                  border: '1px solid #333', 
                  borderRadius: '6px', 
                  padding: '10px',
                }}>
                  <div style={{ color: '#888', fontWeight: 'bold', marginBottom: '6px', fontSize: '10px' }}>
                    📊 PRICE IMPACT (for {formatPB(stake)} buy)
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '11px' }}>
                    <div>
                      <div style={{ color: '#666', fontSize: '9px' }}>You'll receive</div>
                      <div style={{ color: '#00d26a', fontWeight: 'bold' }}>
                        ~{(tokensReceived / 1000000).toFixed(2)}M tokens
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#666', fontSize: '9px' }}>Avg buy price</div>
                      <div style={{ color: '#f0b90b', fontWeight: 'bold' }}>
                        {avgPrice.toFixed(6)}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#666', fontSize: '9px' }}>Price impact</div>
                      <div style={{ 
                        color: priceImpact > 10 ? '#ff3b69' : priceImpact > 5 ? '#f0b90b' : '#00d26a', 
                        fontWeight: 'bold' 
                      }}>
                        +{priceImpact.toFixed(2)}%
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#666', fontSize: '9px' }}>1% rake</div>
                      <div style={{ color: '#888' }}>{formatPB(rakeAmount)}</div>
                    </div>
                  </div>
                  {priceImpact > 10 && (
                    <div style={{ 
                      color: '#ff3b69', fontSize: '9px', marginTop: '6px', padding: '4px',
                      backgroundColor: '#1a0a0a', borderRadius: '4px', textAlign: 'center'
                    }}>
                      ⚠️ High slippage! Consider smaller trade.
                    </div>
                  )}
                </div>
                
                {/* Mini Depth Chart */}
                <div style={{ 
                  backgroundColor: '#0a0a12', 
                  border: '1px solid #333', 
                  borderRadius: '6px', 
                  padding: '10px',
                }}>
                  <div style={{ color: '#888', fontWeight: 'bold', marginBottom: '6px', fontSize: '10px' }}>
                    📈 DEPTH CHART
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', height: '40px', gap: '2px' }}>
                    {/* Sell side (red, left) */}
                    {sellLevels.slice().reverse().map((level, i) => (
                      <div key={`sell-${i}`} style={{
                        flex: 1,
                        height: `${Math.max(10, (level.satsOut / maxSats) * 100)}%`,
                        backgroundColor: '#ff3b69',
                        opacity: 0.3 + (i * 0.14),
                        borderRadius: '2px',
                      }} title={`-${level.pct}%: ${formatPB(Math.floor(level.satsOut))} out`} />
                    ))}
                    {/* Center line */}
                    <div style={{ width: '2px', height: '100%', backgroundColor: '#f0b90b' }} />
                    {/* Buy side (green, right) */}
                    {depthLevels.map((level, i) => (
                      <div key={`buy-${i}`} style={{
                        flex: 1,
                        height: `${Math.max(10, (level.satsNeeded / maxSats) * 100)}%`,
                        backgroundColor: '#00d26a',
                        opacity: 0.3 + (i * 0.14),
                        borderRadius: '2px',
                      }} title={`+${level.pct}%: ${formatPB(Math.floor(level.satsNeeded))} needed`} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '8px', color: '#666' }}>
                    <span style={{ color: '#ff3b69' }}>SELL</span>
                    <span style={{ color: '#f0b90b' }}>{currentPrice.toFixed(6)}</span>
                    <span style={{ color: '#00d26a' }}>BUY</span>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
          
          {/* Position Info - Left Column */}
          {gameState === 'playing' && position?.size > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: isMobile ? '8px 10px' : '10px 14px', 
              marginBottom: '6px', 
              backgroundColor: pnl >= 0 ? '#0d1a0d' : '#1a0d0d', 
              border: `1px solid ${pnl >= 0 ? '#0f2f0f' : '#2f0f0f'}`, 
              borderRadius: '8px',
              gap: isMobile ? '6px' : '12px',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#888', fontSize: isMobile ? '8px' : '10px' }}>POSITION</div>
                <div style={{ color: '#f0b90b', fontSize: isMobile ? '11px' : '13px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatPB(position.invested)} → {formatSats(Math.floor(position.size))} tokens
                </div>
                <div style={{ color: '#777', fontSize: isMobile ? '8px' : '10px' }}>Entry: {position.entry.toFixed(3)}x</div>
              </div>
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <div style={{ color: '#888', fontSize: isMobile ? '8px' : '10px' }} title="Return on Investment. Above 1.0x = profit. Below 1.0x = loss.">ROI</div>
                <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 'bold', color: currentPrice >= position.entry ? '#00d26a' : '#ff3b69' }}>
                  {(currentPrice / position.entry).toFixed(2)}x
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ color: '#888', fontSize: isMobile ? '8px' : '10px' }}>P/L {isPremium && <span style={{ color: '#f0b90b' }}>+10%</span>}</div>
                <div style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold', color: pnl >= 0 ? '#00d26a' : '#ff3b69' }}>{pnl >= 0 ? '+' : ''}{formatPB(pnl)}</div>
              </div>
            </div>
          )}
          
          {/* Trading Buttons - Always visible */}
          <div style={{ marginTop: '0', position: 'relative' }}>
            {/* 🤖 Bot trade animation moved to mini toast notification (near DM popup) */}

            {(gameState === 'spectating' || (gameState === 'playing' && !(position?.size > 0))) && (
              <>
                {/* Show wallet status */}
                {gameWallet === 0 && (
                  <div style={{ 
                    backgroundColor: '#1a1510', 
                    border: '1px solid #f0b90b33', 
                    borderRadius: '6px', 
                    padding: '10px', 
                    marginBottom: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#f0b90b', fontSize: '11px', marginBottom: '6px' }}>⚠️ Wallet empty - Deposit to play</div>
                    <button 
                      onClick={() => setShowDeposit(true)} 
                      style={{ 
                        padding: '8px 16px', 
                        borderRadius: '4px', 
                        border: 'none', 
                        backgroundColor: '#f0b90b', 
                        color: '#000', 
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '11px'
                      }}
                    >
                      💰 Deposit Sats
                    </button>
                  </div>
                )}
                
                {/* Buy info - slippage warning for large trades */}
                {(() => {
                  const poolLiquidity = currentToken?.liquidityPool || currentToken?.liquidity || 0;
                  const isUserToken = currentToken?.isUserLaunched;
                  // No caps! AMM slippage is the natural limit
                  const canBuy = () => poolLiquidity > 0;
                  
                  return (
                    <>
                      {/* No liquidity warning */}
                      {poolLiquidity === 0 && (
                        <div style={{ 
                          backgroundColor: '#1a1010', 
                          border: '1px solid #ff3b6933', 
                          borderRadius: '6px', 
                          padding: '10px', 
                          marginBottom: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ color: '#ff3b69', fontSize: '11px' }}>⏳ Waiting for liquidity...</div>
                          <div style={{ color: '#666', fontSize: '9px', marginTop: '4px' }}>Token is loading. Please wait.</div>
                        </div>
                      )}
                
                      {/* Buy amount options - no limits, go big! */}
                      <div style={{ marginBottom: '6px' }}>
                        <div style={{ display: 'flex', gap: isMobile ? '3px' : '4px', justifyContent: 'center', marginBottom: '6px' }}>
                          {[10000, 50000, 100000, 250000, 500000, 1000000].map(s => {
                            const disabled = !canBuy();
                            return (
                              <button key={s} onClick={() => !disabled && setStake(s)} style={{
                                padding: isMobile ? '10px 6px' : '10px 8px',
                                borderRadius: '6px',
                                border: stake === s ? '2px solid #f0b90b' : '1px solid #333',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                backgroundColor: stake === s ? '#f0b90b' : disabled ? '#0a0a0a' : '#111',
                                color: stake === s ? '#000' : disabled ? '#444' : '#888',
                                fontWeight: stake === s ? 'bold' : 'normal',
                                fontSize: isMobile ? '12px' : '13px',
                                flex: 1,
                                opacity: disabled ? 0.5 : 1,
                                minHeight: '44px',
                              }}>
                                <div>{formatPB(s)}</div>
                                <div style={{ fontSize: isMobile ? '9px' : '8px', color: stake === s ? '#000' : '#555', marginTop: '2px' }}>
                                  {formatUsd(s)}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {(() => {
                          const poolLiq = currentToken?.liquidityPool || 0;
                          const maxBuy = Math.floor(poolLiq * 0.05);
                          const nextLocked = [50000, 100000, 250000, 500000, 1000000].find(amt => amt > maxBuy);
                          if (nextLocked && poolLiq > 0) {
                            const unlockAt = nextLocked * 20;
                            return <div style={{ color: '#555', fontSize: isMobile ? '9px' : '10px', textAlign: 'center', marginBottom: '4px' }}>🔒 {formatPB(nextLocked)} unlocks at {formatPB(unlockAt)} liq</div>;
                          }
                          return null;
                        })()}
                        {/* Custom buy amount input */}
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <input
                            type="number"
                            value={stake}
                            onChange={(e) => setStake(Math.max(1000, parseInt(e.target.value) || 0))}
                            style={{
                              flex: 1,
                              padding: '8px 10px',
                              borderRadius: '6px',
                              border: '1px solid #333',
                              backgroundColor: '#0a0a0f',
                              color: '#f0b90b',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              outline: 'none',
                            }}
                            placeholder="Custom amount..."
                          />
                          <span style={{ color: '#555', fontSize: '10px' }}>PB</span>
                        </div>
                      </div>
                      
                      {/* Broke message when wallet too low */}
                      {gameWallet < stake && poolLiquidity > 0 && (
                        <div 
                          onClick={() => setShowDeposit(true)}
                          style={{ 
                            backgroundColor: '#1a1510', 
                            border: '1px solid #f0b90b55', 
                            borderRadius: '6px', 
                            padding: '12px', 
                            marginBottom: '8px',
                            textAlign: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ color: '#f0b90b', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>
                            💸 You're broke!
                          </div>
                          <div style={{ color: '#888', fontSize: '11px', marginBottom: '8px' }}>
                            Need {formatPB(stake - gameWallet)} more to ape in
                          </div>
                          <div style={{ 
                            display: 'inline-block',
                            padding: '8px 16px', 
                            backgroundColor: '#f0b90b', 
                            color: '#000', 
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}>
                            💰 Deposit Now
                          </div>
                        </div>
                      )}
                      
                      <button 
                        onClick={apeIn} 
                        disabled={!canBuy()}
                        style={{ 
                          width: '100%', 
                          padding: '14px', 
                          borderRadius: '6px', 
                          border: 'none', 
                          cursor: canBuy() ? 'pointer' : 'not-allowed', 
                          backgroundColor: canBuy() ? '#00d26a' : '#333', 
                          color: canBuy() ? '#fff' : '#666', 
                          fontSize: '16px', 
                          fontWeight: 'bold' 
                        }}
                      >
                        {poolLiquidity === 0 ? '⏳ Waiting...' : 
                         `⛓️ BUY ${formatPB(stake)} (~${formatUsd(stake)} USD)`}
                      </button>
                    </>
                  );
                })()}
                
                {/* Show balance after trade */}
                {gameWallet >= stake && (currentToken?.liquidityPool || currentToken?.liquidity || 0) > 0 && (
                  <div style={{ textAlign: 'center', marginTop: '4px', fontSize: '10px', color: '#555' }}>
                    After: ${formatSats(gameWallet - stake)} remaining
                  </div>
                )}
                
                {/* Always show deposit button in token room */}
                {gameWallet > 0 && gameWallet < 500 && (
                  <button 
                    onClick={() => setShowDeposit(true)} 
                    style={{ 
                      width: '100%',
                      marginTop: '8px',
                      padding: '10px', 
                      borderRadius: '6px', 
                      border: '1px solid #333', 
                      backgroundColor: 'transparent', 
                      color: '#888',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    + Add Paper Bucks
                  </button>
                )}
              </>
            )}
            
            {gameState === 'playing' && (position?.size > 0 || position?.tokens > 0) && (
              <>
                {/* 🧠 Smart Health Score - Premium Feature */}
                {isPremium && currentToken?.health && currentToken.state === 'active' && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '6px',
                    padding: '10px', 
                    marginBottom: '8px',
                    backgroundColor: currentToken.health.score > 70 ? 'rgba(0,210,106,0.05)' : 
                                     currentToken.health.score > 40 ? 'rgba(240,185,11,0.05)' : 
                                     currentToken.health.score > 20 ? 'rgba(255,107,0,0.08)' : 'rgba(255,59,105,0.1)',
                    borderRadius: '6px',
                    border: `1px solid ${currentToken.health.score > 70 ? '#00d26a33' : 
                                         currentToken.health.score > 40 ? '#f0b90b33' : 
                                         currentToken.health.score > 20 ? '#ff6b0055' : '#ff3b6955'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#888', fontSize: '10px', fontWeight: 'bold' }} title="AI health analysis. 70+ = strong, 40-70 = caution, <40 = danger, <20 = EXIT NOW!">🧠 SMART HEALTH</span>
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold',
                        color: currentToken.health.score > 70 ? '#00d26a' : 
                               currentToken.health.score > 40 ? '#f0b90b' : 
                               currentToken.health.score > 20 ? '#ff6b00' : '#ff3b69'
                      }}>
                        {currentToken.health.score}/100
                      </span>
                    </div>
                    {/* Health bar */}
                    <div style={{ 
                      height: '6px', 
                      backgroundColor: '#1a1a2e', 
                      borderRadius: '3px',
                      overflow: 'hidden',
                    }}>
                      <div style={{ 
                        width: `${currentToken.health.score}%`, 
                        height: '100%',
                        backgroundColor: currentToken.health.score > 70 ? '#00d26a' : 
                                         currentToken.health.score > 40 ? '#f0b90b' : 
                                         currentToken.health.score > 20 ? '#ff6b00' : '#ff3b69',
                        borderRadius: '3px',
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                    {/* Death signals warning */}
                    {currentToken.health.signals && currentToken.health.signals.length > 0 && (
                      <div style={{ 
                        fontSize: '9px', 
                        color: currentToken.health.score < 30 ? '#ff3b69' : 
                               currentToken.health.score < 50 ? '#ff6b00' : '#888',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                      }}>
                        {currentToken.health.signals.map((signal, idx) => (
                          <span key={idx} style={{
                            padding: '2px 6px',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            borderRadius: '3px',
                          }}>
                            {signal === 'price_collapsed' ? '📉 price' :
                             signal === 'price_critical' ? '💸 price' :
                             signal === 'no_volume' ? '🔇 volume' :
                             signal === 'volume_dying' ? '📊 volume' :
                             signal === 'liquidity_drained' ? '💧 liquidity' :
                             signal === 'abandoned' ? '👻 abandoned' :
                             signal === 'death_spiral' ? '💀 spiral' :
                             signal === 'mass_exodus' ? '🏃 exodus' :
                             signal === 'old_and_dying' ? '⏰ aging' :
                             signal.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Health status message */}
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#666',
                      fontStyle: 'italic',
                    }}>
                      {currentToken.health.score > 80 ? '💪 Strong vitals - looking healthy' :
                       currentToken.health.score > 60 ? '👍 Stable - monitor for changes' :
                       currentToken.health.score > 40 ? '⚠️ Weakening - consider exit strategy' :
                       currentToken.health.score > 20 ? '🚨 Critical - high risk of rug' :
                       '💀 Flatline - exit immediately!'}
                    </div>
                  </div>
                )}
                
                {/* 🧠 Health Teaser for Non-Premium */}
                {!isPremium && currentToken?.health && currentToken.state === 'active' && (
                  <div 
                    onClick={() => setShowDeposit(true)}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px', 
                      marginBottom: '8px',
                      backgroundColor: 'rgba(240,185,11,0.05)',
                      borderRadius: '6px',
                      border: '1px dashed #f0b90b44',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>🧠</span>
                      <div>
                        <div style={{ color: '#888', fontSize: '11px', fontWeight: 'bold' }}>SMART HEALTH</div>
                        <div style={{ color: '#555', fontSize: '9px' }}>Know when to exit before the rug</div>
                      </div>
                    </div>
                    <div style={{ 
                      backgroundColor: '#f0b90b', 
                      color: '#000', 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      fontSize: '9px', 
                      fontWeight: 'bold' 
                    }}>
                      👑 UNLOCK
                    </div>
                  </div>
                )}
                
                {/* Slippage indicator - now based on liquidity impact */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '4px',
                  padding: '8px 10px', 
                  marginBottom: '8px',
                  backgroundColor: '#1a1a1a',
                  borderRadius: '6px',
                  fontSize: '11px'
                }}>
                  {/* Liquidity health bar */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                    <span style={{ color: '#999' }} title="Current liquidity vs initial. Below 50% = pool draining, rug risk high!">Pool Health:</span>
                    <span style={{ 
                      color: (() => {
                        const health = (currentToken?.liquidityPool || 10000) / (currentToken?.initialLiquidity || 10000);
                        if (health > 0.7) return '#00d26a';
                        if (health > 0.4) return '#f0b90b';
                        return '#ff3b69';
                      })(),
                      fontWeight: 'bold'
                    }}>
                      {Math.round(((currentToken?.liquidityPool || 10000) / (currentToken?.initialLiquidity || 10000)) * 100)}%
                    </span>
                  </div>
                  {/* Estimated slippage on full sell */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                    <span style={{ color: '#999' }} title="Price impact if you sell everything. PvP tokens limit each sell to max 50% price impact - you may need multiple sells.">Est. slippage (100%):</span>
                    <span style={{ 
                      color: (() => {
                        if (!position.size || position.size <= 0) return '#00d26a';
                        const liquidityPool = currentToken?.liquidityPool || 10000;
                        const tokenReserve = currentToken?.tokenReserve || 1000000;
                        const k = liquidityPool * tokenReserve;
                        const newTokenReserve = tokenReserve + position.size;
                        const newLiquidityPool = k / newTokenReserve;
                        const satsFromPool = liquidityPool - newLiquidityPool;
                        const sellImpact = satsFromPool / liquidityPool;
                        const liquidityHealth = liquidityPool / (currentToken?.initialLiquidity || liquidityPool);
                        let slip = Math.pow(sellImpact, 1.5) * 0.7 * (1 + (1 - liquidityHealth) * 1.5);
                        slip = Math.min(0.50, slip);
                        if (isPremium) slip *= 0.5;
                        if (slip > 0.15) return '#ff3b69';
                        if (slip > 0.05) return '#f0b90b';
                        return '#00d26a';
                      })(),
                      fontWeight: 'bold'
                    }}>
                      {(() => {
                        if (!position.size || position.size <= 0) return '0.1%';
                        const liquidityPool = currentToken?.liquidityPool || 10000;
                        const tokenReserve = currentToken?.tokenReserve || 1000000;
                        const k = liquidityPool * tokenReserve;
                        const newTokenReserve = tokenReserve + position.size;
                        const newLiquidityPool = k / newTokenReserve;
                        const satsFromPool = liquidityPool - newLiquidityPool;
                        const sellImpact = satsFromPool / liquidityPool;
                        const liquidityHealth = liquidityPool / (currentToken?.initialLiquidity || liquidityPool);
                        let slip = Math.pow(sellImpact, 1.5) * 0.7 * (1 + (1 - liquidityHealth) * 1.5);
                        slip = Math.min(0.50, slip);
                        if (isPremium) slip *= 0.5;
                        return (slip * 100).toFixed(1) + '%';
                      })()}
                      {isPremium && <span style={{ color: '#f0b90b', marginLeft: '4px' }}>(50% off)</span>}
                    </span>
                  </div>
                  {/* 🛡️ Slippage protection notice for big positions */}
                  {currentToken?.isUserLaunched && position.size > 0 && (() => {
                    const liquidityPool = currentToken?.liquidityPool || 10000;
                    const tokenReserve = currentToken?.tokenReserve || 1000000;
                    const k = liquidityPool * tokenReserve;
                    const newTokenReserve = tokenReserve + position.size;
                    const newLiquidityPool = k / newTokenReserve;
                    const testNewPrice = newLiquidityPool / newTokenReserve;
                    const priceImpact = (currentPrice - testNewPrice) / currentPrice;
                    if (priceImpact > 0.50) {
                      return (
                        <div style={{ 
                          fontSize: '10px', 
                          color: '#f0b90b', 
                          backgroundColor: '#1a1508',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          marginTop: '4px',
                          border: '1px solid #f0b90b44'
                        }}>
                          🛡️ Large position - multiple sells needed (max 50% impact per trade)
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', overflow: 'hidden', position: 'relative' }}>
                  {[25, 50, 75].map(pct => {
                    // Calculate current value using multiplier (same as position display at line 15203)
                    const multiplier = position.entry > 0 ? currentPrice / position.entry : 1;
                    const currentVal = Math.floor(position.invested * multiplier) || position.invested || 0;
                    const estValue = Math.floor(currentVal * (pct / 100));
                    return (
                      <button 
                        key={pct}
                        onClick={() => sell(pct)} 
                        style={{ 
                          flex: 1, 
                          padding: '8px 4px', 
                          borderRadius: '6px', 
                          border: '1px solid #ff3b69', 
                          cursor: 'pointer', 
                          backgroundColor: '#1a0d12',
                          color: '#ff3b69', 
                          fontWeight: 'bold', 
                          fontSize: '11px',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={e => { e.target.style.backgroundColor = '#2a1520'; e.target.style.transform = 'scale(1.02)'; }}
                        onMouseLeave={e => { e.target.style.backgroundColor = '#1a0d12'; e.target.style.transform = 'scale(1)'; }}
                        onMouseDown={e => { e.target.style.transform = 'scale(0.95)'; }}
                        onMouseUp={e => { e.target.style.transform = 'scale(1.02)'; }}
                      >
                        <div>-{pct}%</div>
                        <div style={{ fontSize: '9px', opacity: 0.8 }}>~{formatPB(estValue)}</div>
                      </button>
                    );
                  })}
                </div>
                {/* Current Bag Value Display */}
                {(() => {
                  const multiplier = position.entry > 0 ? currentPrice / position.entry : 1;
                  const bagValue = Math.floor(position.invested * multiplier) || 0;
                  return (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: '#0d1117',
                      borderRadius: '6px',
                      border: '1px solid #1a1a2e',
                      marginBottom: '8px'
                    }}>
                      <span style={{ color: '#888', fontSize: '11px' }}>💰 BAG VALUE:</span>
                      <span style={{
                        color: multiplier >= 1 ? '#00d26a' : '#ff3b69',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        fontFamily: 'monospace'
                      }}>
                        {formatPB(bagValue)} ({position.size?.toLocaleString() || 0} tokens)
                      </span>
                    </div>
                  );
                })()}

                {/* Custom USD sell input - PRIMARY */}
                {(() => {
                  const multiplier = position.entry > 0 ? currentPrice / position.entry : 1;
                  const bagValue = Math.floor(position.invested * multiplier) || 0;
                  const maxUSD = bagValue; // Max is full bag value in PB (which displays as $)

                  // Calculate percentage from USD input
                  const usdAmount = parseFloat(customSellUSD) || 0;
                  const usdPercent = bagValue > 0 ? Math.min(100, Math.max(0, (usdAmount / bagValue) * 100)) : 0;

                  return (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ color: '#ff3b69', fontSize: '14px', fontWeight: 'bold' }}>$</span>
                      <input
                        type="number"
                        step="any"
                        value={customSellUSD}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow empty, or any valid decimal including leading "0."
                          if (val === '' || val === '0' || val === '0.') {
                            setCustomSellUSD(val);
                          } else {
                            const num = parseFloat(val);
                            if (!isNaN(num) && num >= 0) {
                              // Keep the raw string to preserve decimals like "0.01"
                              setCustomSellUSD(num <= maxUSD ? val : maxUSD.toString());
                            }
                          }
                        }}
                        style={{
                          width: '80px',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #ff3b69',
                          backgroundColor: '#1a0d12',
                          color: '#ff3b69',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          outline: 'none',
                          textAlign: 'right',
                          fontFamily: 'monospace',
                        }}
                        placeholder="0.01"
                      />
                      <span style={{ color: '#555', fontSize: '10px', minWidth: '50px' }}>
                        = {usdPercent.toFixed(4)}%
                      </span>
                      <button
                        onClick={() => {
                          if (usdPercent > 0 && usdPercent <= 100) {
                            // Pass exact percentage for micro-sells (don't round!)
                            sell(usdPercent);
                            setCustomSellUSD(''); // Clear after sell
                          }
                        }}
                        disabled={usdPercent <= 0 || usdPercent > 100}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #ff3b69',
                          cursor: usdPercent > 0 && usdPercent <= 100 ? 'pointer' : 'not-allowed',
                          backgroundColor: '#1a0d12',
                          color: '#ff3b69',
                          fontWeight: 'bold',
                          fontSize: '11px',
                          opacity: usdPercent > 0 && usdPercent <= 100 ? 1 : 0.5,
                        }}
                      >
                        SELL ${customSellUSD || '0'}
                      </button>
                      <button
                        onClick={() => setCustomSellUSD(maxUSD.toString())}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #555',
                          cursor: 'pointer',
                          backgroundColor: '#111',
                          color: '#888',
                          fontWeight: 'bold',
                          fontSize: '9px',
                        }}
                      >
                        MAX
                      </button>
                    </div>
                  );
                })()}

                {/* Custom sell percentage input - SECONDARY */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '6px' }}>
                  <input
                    type="number"
                    value={customSellPct}
                    onChange={(e) => setCustomSellPct(Math.min(100, Math.max(1, parseInt(e.target.value) || 0)))}
                    style={{
                      width: '50px',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid #333',
                      backgroundColor: '#0a0a0f',
                      color: '#888',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      outline: 'none',
                      textAlign: 'center',
                    }}
                    placeholder="%"
                  />
                  <span style={{ color: '#555', fontSize: '10px' }}>%</span>
                  <button
                    onClick={() => sell(customSellPct)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #444',
                      cursor: 'pointer',
                      backgroundColor: '#111',
                      color: '#888',
                      fontWeight: 'bold',
                      fontSize: '11px',
                    }}
                  >
                    SELL {customSellPct}%
                  </button>
                </div>
                
                {/* Big CLOSE POSITION button - REQUIRES LONG PRESS (hold 1 sec) */}
                <button
                  onMouseDown={() => {
                    // Start hold timer - fills progress bar over 1 second
                    let progress = 0;
                    const interval = setInterval(() => {
                      progress += 5;
                      setSellAllHoldProgress(progress);
                      if (progress >= 100) {
                        clearInterval(interval);
                        sellAllHoldTimer.current = null;
                        sell(100);
                        setSellAllHoldProgress(0);
                      }
                    }, 50); // 50ms * 20 = 1000ms total
                    sellAllHoldTimer.current = interval;
                  }}
                  onMouseUp={() => {
                    // Cancel if released early
                    if (sellAllHoldTimer.current) {
                      clearInterval(sellAllHoldTimer.current);
                      sellAllHoldTimer.current = null;
                    }
                    setSellAllHoldProgress(0);
                  }}
                  onMouseLeave={() => {
                    // Cancel if mouse leaves button
                    if (sellAllHoldTimer.current) {
                      clearInterval(sellAllHoldTimer.current);
                      sellAllHoldTimer.current = null;
                    }
                    setSellAllHoldProgress(0);
                  }}
                  onTouchStart={() => {
                    // Mobile: Start hold timer
                    let progress = 0;
                    const interval = setInterval(() => {
                      progress += 5;
                      setSellAllHoldProgress(progress);
                      if (progress >= 100) {
                        clearInterval(interval);
                        sellAllHoldTimer.current = null;
                        sell(100);
                        setSellAllHoldProgress(0);
                      }
                    }, 50);
                    sellAllHoldTimer.current = interval;
                  }}
                  onTouchEnd={() => {
                    // Mobile: Cancel if released early
                    if (sellAllHoldTimer.current) {
                      clearInterval(sellAllHoldTimer.current);
                      sellAllHoldTimer.current = null;
                    }
                    setSellAllHoldProgress(0);
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    cursor: 'pointer',
                    background: sellAllHoldProgress > 0
                      ? `linear-gradient(90deg, #ff1a50 ${sellAllHoldProgress}%, #1a0808 ${sellAllHoldProgress}%)`
                      : 'linear-gradient(135deg, #ff3b69 0%, #ff1a50 100%)',
                    color: '#fff',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    marginBottom: '6px',
                    boxShadow: sellAllHoldProgress > 0 ? '0 0 20px rgba(255,59,105,0.6)' : '0 4px 15px rgba(255,59,105,0.3)',
                    transition: sellAllHoldProgress > 0 ? 'none' : 'all 0.15s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '4px',
                    position: 'relative',
                    overflow: 'hidden',
                    userSelect: 'none',
                  }}
                >
                  {(() => {
                    const multiplier = position.entry > 0 ? currentPrice / position.entry : 1;
                    const currentVal = Math.floor(position.invested * multiplier) || position.invested || 0;
                    const usdValue = bsvPrice ? (currentVal / 100000000 * bsvPrice).toFixed(2) : null;
                    const showUsd = currentToken?.isUserLaunched && usdValue;
                    return (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>💰 {sellAllHoldProgress > 0 ? 'HOLD TO SELL...' : 'SELL ALL'}</span>
                          <span style={{
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}>
                            {formatPB(currentVal)}
                            {showUsd && <span style={{ color: '#00d26a', fontSize: '11px' }}>(${usdValue})</span>}
                          </span>
                        </div>
                        {sellAllHoldProgress === 0 && (
                          <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)', marginTop: '-2px' }}>
                            Hold 1 sec to confirm
                          </div>
                        )}
                      </>
                    );
                  })()}
                </button>
                
                <div style={{ marginTop: '6px' }}>
                  <div style={{ fontSize: '10px', color: '#555', marginBottom: '4px', textAlign: 'center' }}>Add to Position:</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {[10000, 25000, 50000, 100000, 250000].map(a => (
                      <button 
                        key={a} 
                        onClick={() => addPosition(a)} 
                        disabled={gameWallet < a} 
                        style={{ 
                          flex: 1, 
                          padding: isMobile ? '6px 2px' : '8px 4px', 
                          borderRadius: '4px', 
                          border: gameWallet < a ? 'none' : '1px solid #00d26a', 
                          cursor: gameWallet < a ? 'not-allowed' : 'pointer', 
                          backgroundColor: gameWallet < a ? '#1a1a1a' : 'rgba(0,210,106,0.1)', 
                          color: gameWallet < a ? '#444' : '#00d26a', 
                          fontWeight: 'bold', 
                          fontSize: isMobile ? '10px' : '11px',
                          transition: 'all 0.15s ease',
                          opacity: gameWallet >= a ? 1 : 0.5,
                        }}
                        onMouseEnter={e => { if (gameWallet >= a) { e.target.style.backgroundColor = 'rgba(0,210,106,0.3)'; setHoverPreviewAmount(a); }}}
                        onMouseLeave={e => { if (gameWallet >= a) { e.target.style.backgroundColor = 'rgba(0,210,106,0.1)'; } setHoverPreviewAmount(null); }}
                      >
                        <div>+{formatPB(a)}</div>
                        <div style={{ fontSize: '8px', opacity: 0.7 }}>{formatUsd(a)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            
            {/* WON STATE - Token still active? Can ape back in! */}
            {gameState === 'won' && currentToken?.state === 'active' && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={leaveToken} style={{ flex: 1, padding: '12px', borderRadius: '6px', border: '1px solid #333', cursor: 'pointer', backgroundColor: 'transparent', color: '#888', fontSize: '12px' }}>← Back to Lobby</button>
                <button 
                  onClick={() => setGameState('spectating')} 
                  style={{ flex: 1, padding: '14px', borderRadius: '8px', border: 'none', cursor: 'pointer', backgroundColor: '#00d26a', color: '#000', fontWeight: 'bold' }}
                >
                  🦍 Ape Back In
                </button>
                <button onClick={leaveToken} style={{ flex: 1, padding: '14px', borderRadius: '8px', border: 'none', cursor: 'pointer', backgroundColor: '#f0b90b', color: '#000', fontWeight: 'bold' }}>
                  🎯 New Token
                </button>
              </div>
            )}
            
            {/* SPECTATING - Just show lobby button, buy panel handles APE IN */}
            {gameState === 'spectating' && currentToken?.state === 'active' && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={leaveToken} style={{ flex: 1, padding: '12px', borderRadius: '6px', border: '1px solid #333', cursor: 'pointer', backgroundColor: 'transparent', color: '#888', fontSize: '12px' }}>← Back to Lobby</button>
              </div>
            )}
            
            {/* WON/RUGGED - Token ended, go find new one */}
            {(gameState === 'won' || gameState === 'rugged') && currentToken?.state !== 'active' && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={leaveToken} style={{ flex: 1, padding: '12px', borderRadius: '6px', border: '1px solid #333', cursor: 'pointer', backgroundColor: 'transparent', color: '#888', fontSize: '12px' }}>← Back to Lobby</button>
                <button onClick={leaveToken} style={{ flex: 1, padding: '14px', borderRadius: '8px', border: 'none', cursor: 'pointer', backgroundColor: '#f0b90b', color: '#000', fontWeight: 'bold' }}>
                  🎯 Find New Token
                </button>
              </div>
            )}
          </div>
          
              {/* 📊 LIVE TRADES - Mobile only (desktop has it in right column) */}
              {isMobile && (
                <div style={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #1a1a2e',
                  borderRadius: '8px',
                  marginTop: '8px',
                  marginBottom: '6px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 10px',
                    backgroundColor: '#0d0d12',
                    borderBottom: '1px solid #1a1a2e',
                  }}>
                    <span style={{ color: '#5e6673', fontSize: '9px', fontWeight: 'bold' }}>📊 LIVE TRADES</span>
                    <span style={{ color: '#444', fontSize: '8px' }}>{transactions.length} total</span>
                  </div>

                  <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                    {(() => {
                      const seenIds = new Set();
                      const uniqueTxs = transactions.filter(tx => {
                        if ((tx.sats || 0) <= 0) return false;
                        if (!tx.id) return true;
                        if (seenIds.has(tx.id)) return false;
                        seenIds.add(tx.id);
                        return true;
                      });

                      return uniqueTxs.slice(0, 10).map((tx, idx) => {
                        const isBuy = tx.type === 'buy';
                        const isLarge = (tx.sats || 0) >= 25000;
                        const timeAgo = tx.timestamp ? Math.floor((Date.now() - tx.timestamp) / 1000) : 0;
                        const timeStr = timeAgo < 60 ? `${timeAgo}s` : `${Math.floor(timeAgo / 60)}m`;

                        return (
                          <div key={tx.id || idx} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 10px',
                            borderBottom: '1px solid #1a1a2e11',
                            backgroundColor: tx.isYou ? 'rgba(240,185,11,0.08)' : 'transparent',
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                <span style={{ color: isBuy ? '#16c784' : '#ea3943', fontWeight: 'bold' }}>
                                  {isBuy ? 'BUY' : 'SELL'}
                                </span>
                                {tx.isYou && (
                                  <span style={{ fontSize: '7px', padding: '1px 3px', backgroundColor: '#f0b90b', color: '#000', borderRadius: '2px', fontWeight: 'bold' }}>YOU</span>
                                )}
                                {isLarge && !tx.isYou && (
                                  <span style={{ fontSize: '7px', padding: '1px 3px', backgroundColor: isBuy ? '#16c784' : '#ea3943', color: '#000', borderRadius: '2px', fontWeight: 'bold' }}>{isBuy ? 'WHALE' : 'DUMP'}</span>
                                )}
                              </div>
                              <div style={{ color: '#555', fontSize: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {tx.isYou ? 'You' : (tx.wallet || tx.handle || 'Anon')} • {timeStr}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ color: tx.isYou ? '#f0b90b' : (isBuy ? '#16c784' : '#ea3943'), fontWeight: 'bold', fontSize: '10px' }}>
                                {isBuy ? '+' : '-'}{formatPB(tx.sats || 0)}
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}

                    {transactions.filter(tx => (tx.sats || 0) > 0).length === 0 && (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#444', fontSize: '10px' }}>
                        ⏳ Waiting for trades...
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Activity Feed - Bot trades, alpha, rugs */}
              <div style={{
                backgroundColor: '#0a0a0f',
                border: '1px solid #1a1a2e',
                borderRadius: '8px',
                padding: '8px',
                marginTop: '8px',
                marginBottom: '6px'
              }}>
                <div style={{ color: '#555', fontSize: '9px', marginBottom: '4px' }}>📊 ACTIVITY</div>
                <div style={{ height: '50px', overflowY: 'auto' }}>
                  {tokenChat.filter(m => m.isAction || m.isAlpha || m.isRug || m.isSlippage || (m.handle && !m.handle.startsWith('$'))).slice(-15).reverse().map((m, i) => (
                    <div 
                      key={m.id || i} 
                      style={{ 
                        fontSize: '9px', 
                        marginBottom: '2px',
                        color: m.isAlpha ? '#f0b90b' : m.isRug ? '#ff3b69' : m.isAction ? '#00d26a' : m.isSlippage ? '#ff6b00' : '#555'
                      }}
                    >
                      <span style={{ fontWeight: 'bold' }}>{m.handle}:</span> {m.text}
                    </div>
                  ))}
                  {tokenChat.filter(m => m.isAction || m.isAlpha || m.isRug || m.isSlippage || (m.handle && !m.handle.startsWith('$'))).length === 0 && (
                    <div style={{ color: '#333', fontSize: '9px', fontStyle: 'italic', textAlign: 'center', padding: '12px 0' }}>
                      No activity yet...
                    </div>
                  )}
                </div>
              </div>
              
              {/* Player Chat */}
              <div style={{ 
                backgroundColor: '#0a0a0f', 
                border: '1px solid #1a1a2e', 
                borderRadius: '8px', 
                padding: '8px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ color: '#555', fontSize: '9px' }}>💬 TOKEN CHAT</span>
                  {mutedPlayers.length > 0 && (
                    <span style={{ color: '#666', fontSize: '8px' }}>
                      {mutedPlayers.length} muted • <button onClick={() => { setMutedPlayers([]); localStorage.removeItem('paperHands_mutedPlayers'); }} style={{ background: 'none', border: 'none', color: '#f0b90b', fontSize: '8px', cursor: 'pointer', padding: 0 }}>unmute</button>
                    </span>
                  )}
                </div>
                <div ref={tokenChatRef} style={{ height: '50px', overflowY: 'auto', marginBottom: '6px' }}>
                  {tokenChat.filter(m => m.handle && m.handle.startsWith('$') && !m.isAction && !m.isAlpha && !m.isRug && !m.isSlippage && !isPlayerMuted(m.handle)).length === 0 ? (
                    <div style={{ color: '#333', fontSize: '9px', fontStyle: 'italic', textAlign: 'center', padding: '15px 0' }}>
                      No player messages yet...
                    </div>
                  ) : (
                    tokenChat
                      .filter(m => m.handle && m.handle.startsWith('$') && !m.isAction && !m.isAlpha && !m.isRug && !m.isSlippage && !isPlayerMuted(m.handle))
                      .map(m => (
                      <div key={m.id} style={{ 
                        fontSize: '10px', 
                        marginBottom: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span style={{ color: m.isPremiumUser ? '#f0b90b' : '#666', fontWeight: 'bold' }}>
                          {m.handle}:
                        </span>
                        <span style={{ color: '#aaa', flex: 1 }}>
                          {renderClickableTickers(m.text, '#aaa')}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                
                {isPremium ? (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input 
                      type="text" 
                      value={chatInput} 
                      onChange={e => setChatInput(e.target.value)} 
                      onKeyPress={e => {
                        if (e.key === 'Enter' && chatInput.trim()) {
                          sendTokenChat(chatInput.trim());
                          setChatInput('');
                        }
                      }}
                      placeholder="Chat..." 
                      style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #1a1a2e', backgroundColor: '#111', color: '#fff', fontSize: isMobile ? '16px' : '10px', outline: 'none' }} 
                    />
                    <button 
                      onClick={() => {
                        if (chatInput.trim()) {
                          sendTokenChat(chatInput.trim());
                          setChatInput('');
                        }
                      }}
                      style={{ padding: '8px 12px', borderRadius: '6px', border: 'none', backgroundColor: '#f0b90b', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '10px', minHeight: '44px' }}
                    >
                      Send
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => setShowDeposit(true)}
                    style={{ 
                      padding: '8px', 
                      borderRadius: '6px', 
                      border: '1px solid #f0b90b', 
                      backgroundColor: 'rgba(240,185,11,0.05)', 
                      color: '#f0b90b', 
                      fontSize: '9px',
                      textAlign: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    🔒 Premium to chat
                  </div>
                )}
              </div>
          
            </div>
            )}
            {/* END LEFT COLUMN */}
            
            {/* RIGHT COLUMN: Players - Hidden in focus mode */}
            {!chartFullscreen && (!isMobile || mobileTokenTab === 'info') && (
            <div style={{ flex: '1 1 42%', minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
              
              {/* Players in this trade - Creator can see amounts */}
              {tokenPlayers.length > 0 && (() => {
                const userIsCreator = currentToken?.isUserLaunched && currentToken?.creator && 
                  handcashUser?.handle?.replace('$', '').toLowerCase() === currentToken?.creator?.replace('$', '').toLowerCase();
                
                return (
                <div style={{ 
                  display: 'flex', 
                  gap: '4px', 
                  flexWrap: 'wrap', 
                  alignItems: 'center', 
                  padding: '10px 12px', 
                  marginBottom: '12px', 
                  backgroundColor: userIsCreator ? '#0d0d1a' : '#0d0d12', 
                  borderRadius: '8px',
                  border: userIsCreator ? '1px solid #f0b90b33' : '1px solid #1a1a2e',
                }}>
                  <span style={{ color: '#555', fontSize: '9px', marginRight: '4px', width: '100%', marginBottom: '6px' }}>
                    👥 IN THIS TRADE {userIsCreator && <span style={{ color: '#f0b90b' }}>• 👑 YOU SEE HOLDINGS</span>}
                  </span>
                  {(() => {
                    // Extra deduplication at render time
                    const seenHandles = new Set();
                    return tokenPlayers.filter(p => {
                      if (!p.isActive) return false;
                      const handle = p.handle?.replace(/^\$/, '') || 'unknown';
                      if (seenHandles.has(handle)) return false;
                      seenHandles.add(handle);
                      return true;
                    }).map(p => {
                      const isCreator = currentToken?.isUserLaunched && currentToken?.creator && 
                        (p.handle === currentToken.creator || p.handle === '$' + currentToken.creator || '$' + p.handle === currentToken.creator);
                      return (
                      <div key={p.handle} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px', 
                        padding: '4px 8px', 
                        backgroundColor: isCreator ? '#1a1508' : '#111', 
                        borderRadius: '6px', 
                        fontSize: '10px',
                        border: isCreator ? '1px solid #f0b90b44' : '1px solid #222',
                      }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: p.color }} />
                        <span style={{ color: isCreator ? '#f0b90b' : '#888' }}>{p.handle}</span>
                        {isCreator && <span style={{ fontSize: '8px', color: '#f0b90b', backgroundColor: '#1a1508', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>CREATOR</span>}
                        {userIsCreator && !isCreator && p.invested && (
                          <span style={{ fontSize: '9px', color: '#00d26a', marginLeft: '4px' }}>
                            {(p.invested / 1000).toFixed(0)}K
                          </span>
                        )}
                      </div>
                      );
                    });
                  })()}
                  {gameState === 'playing' && (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px', 
                      padding: '4px 8px', 
                      backgroundColor: '#0d1a0d', 
                      borderRadius: '6px', 
                      fontSize: '10px', 
                      border: '1px solid #0f2f0f',
                    }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#f0b90b' }} />
                      <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>YOU</span>
                    </div>
                  )}
                </div>
              );
              })()}
              
              {/* 👑 CREATOR STATS PANEL - Only visible to token creator */}
              {currentToken?.isUserLaunched && currentToken?.creator && 
                handcashUser?.handle?.replace('$', '').toLowerCase() === currentToken?.creator?.replace('$', '').toLowerCase() && (
                <div style={{ 
                  backgroundColor: '#0d0a08', 
                  border: '1px solid #f0b90b44', 
                  borderRadius: '10px', 
                  padding: '14px',
                  marginBottom: '12px',
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: '12px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #f0b90b22'
                  }}>
                    <span style={{ fontSize: '18px' }}>👑</span>
                    <span style={{ color: '#f0b90b', fontSize: '13px', fontWeight: 'bold' }}>CREATOR DASHBOARD</span>
                  </div>
                  
                  {(() => {
                    // Calculate creator stats
                    const otherHolders = tokenPlayers.filter(p => {
                      const handle = p.handle?.replace('$', '').toLowerCase();
                      const creatorHandle = currentToken.creator?.replace('$', '').toLowerCase();
                      return p.isActive && handle !== creatorHandle;
                    });
                    
                    const totalBagValue = otherHolders.reduce((sum, p) => sum + (p.invested || 0), 0);
                    const holderCount = otherHolders.length;
                    const biggestHolder = otherHolders.sort((a, b) => (b.invested || 0) - (a.invested || 0))[0];
                    const poolShare = currentToken.liquidityPool || 0;
                    const totalVolume = (currentToken.totalBuyVolume || 0) + (currentToken.totalSellVolume || 0);
                    
                    // Your position value
                    const myPosition = tokenPlayers.find(p => {
                      const handle = p.handle?.replace('$', '').toLowerCase();
                      const creatorHandle = currentToken.creator?.replace('$', '').toLowerCase();
                      return handle === creatorHandle;
                    });
                    const myTokens = myPosition?.tokens || 0;
                    const myValue = myTokens * (currentToken.currentPrice || 0);
                    
                    // Profit if rug = pool liquidity (what you'd drain)
                    const profitIfRug = poolShare;
                    
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {/* Top Stats Row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #222' }}>
                            <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>👥 HOLDERS</div>
                            <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>{holderCount}</div>
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #00d26a33' }}>
                            <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>💰 TOTAL BAGS</div>
                            <div style={{ color: '#00d26a', fontSize: '18px', fontWeight: 'bold' }}>{(totalBagValue / 1000000).toFixed(2)}M</div>
                          </div>
                        </div>
                        
                        {/* Biggest Holder */}
                        {biggestHolder && (
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #9b59b633' }}>
                            <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>🐋 BIGGEST HOLDER</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: '#9b59b6', fontSize: '13px', fontWeight: 'bold' }}>${biggestHolder.handle}</span>
                              <span style={{ color: '#fff', fontSize: '13px' }}>{((biggestHolder.invested || 0) / 1000).toFixed(0)}K PB</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Volume & Pool Stats */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #222' }}>
                            <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>📊 TOTAL VOLUME</div>
                            <div style={{ color: '#00a8ff', fontSize: '14px', fontWeight: 'bold' }}>{(totalVolume / 1000000).toFixed(2)}M</div>
                          </div>
                          <div style={{ backgroundColor: '#111', borderRadius: '8px', padding: '10px', border: '1px solid #222' }}>
                            <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>💧 POOL SIZE</div>
                            <div style={{ color: '#3498db', fontSize: '14px', fontWeight: 'bold' }}>{(poolShare / 1000000).toFixed(2)}M</div>
                          </div>
                        </div>
                        
                        {/* Your Holdings */}
                        <div style={{ backgroundColor: '#0a0d0a', borderRadius: '8px', padding: '10px', border: '1px solid #00d26a44' }}>
                          <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>🎯 YOUR HOLDINGS</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#888', fontSize: '11px' }}>{(myTokens / 1000).toFixed(0)}K tokens</span>
                            <span style={{ color: '#00d26a', fontSize: '14px', fontWeight: 'bold' }}>{(myValue / 1000000).toFixed(2)}M PB</span>
                          </div>
                        </div>
                        
                        {/* Rug Stats */}
                        <div style={{ backgroundColor: '#140a0a', borderRadius: '8px', padding: '10px', border: '1px solid #ff3b6944' }}>
                          <div style={{ color: '#666', fontSize: '9px', marginBottom: '4px' }}>💀 IF YOU RUG NOW</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#ff3b69', fontSize: '10px' }}>Pool drain →</span>
                            <span style={{ color: '#ff3b69', fontSize: '16px', fontWeight: 'bold' }}>{(profitIfRug / 1000000).toFixed(2)}M PB</span>
                          </div>
                          <div style={{ color: '#ff3b6966', fontSize: '9px', marginTop: '4px' }}>
                            ≈ ${((profitIfRug / 100000000) * (bsvPrice || 20)).toFixed(2)} USD
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              {/* 🔮 RUG PROPHET QUICK BET - AI tokens only! */}
              {currentToken && currentToken.state === 'active' && !currentToken.isUserLaunched && (() => {
                const existingBet = rugProphetBets.find(b => b.tokenId === currentToken.id && b.status === 'pending');
                // Use server's flag - don't calculate locally (hides lifecycle from players)
                const canBet = currentToken.canBetRugProphet && !existingBet;
                const hasPosition = gameState === 'playing';
                
                return (
                  <div style={{ 
                    backgroundColor: '#0d0a14', 
                    border: '1px solid #9b59b633', 
                    borderRadius: '8px', 
                    padding: '12px',
                    marginBottom: '12px',
                  }}>
                    {/* 3 WAYS TO PLAY TIP */}
                    <div style={{
                      backgroundColor: '#0a0a12',
                      border: '1px solid #f0b90b33',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      marginBottom: '10px',
                    }}>
                      <div style={{ color: '#f0b90b', fontSize: '9px', fontWeight: 'bold', marginBottom: '6px' }}>
                        💡 3 WAYS TO PLAY THIS TOKEN
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ 
                            width: '16px', height: '16px', borderRadius: '4px', 
                            backgroundColor: hasPosition ? '#00d26a33' : '#222',
                            border: hasPosition ? '1px solid #00d26a' : '1px solid #333',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '8px', color: hasPosition ? '#00d26a' : '#555'
                          }}>{hasPosition ? '✓' : '1'}</span>
                          <span style={{ color: hasPosition ? '#00d26a' : '#888', fontSize: '10px' }}>
                            <strong>BUY</strong> the token, sell before rug
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ 
                            width: '16px', height: '16px', borderRadius: '4px', 
                            backgroundColor: existingBet?.prediction === 'rug' ? '#ff3b6933' : '#222',
                            border: existingBet?.prediction === 'rug' ? '1px solid #ff3b69' : '1px solid #333',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '8px', color: existingBet?.prediction === 'rug' ? '#ff3b69' : '#555'
                          }}>{existingBet?.prediction === 'rug' ? '✓' : '2'}</span>
                          <span style={{ color: existingBet?.prediction === 'rug' ? '#ff3b69' : '#888', fontSize: '10px' }}>
                            <strong>BET RUG</strong> if you think it'll fail (1.8x)
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ 
                            width: '16px', height: '16px', borderRadius: '4px', 
                            backgroundColor: existingBet?.prediction === 'moon' ? '#00d26a33' : '#222',
                            border: existingBet?.prediction === 'moon' ? '1px solid #00d26a' : '1px solid #333',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '8px', color: existingBet?.prediction === 'moon' ? '#00d26a' : '#555'
                          }}>{existingBet?.prediction === 'moon' ? '✓' : '3'}</span>
                          <span style={{ color: existingBet?.prediction === 'moon' ? '#00d26a' : '#888', fontSize: '10px' }}>
                            <strong>BET MOON</strong> if you think it'll pump (2.5x)
                          </span>
                        </div>
                      </div>
                      {(hasPosition || existingBet) && (
                        <div style={{ 
                          marginTop: '6px', 
                          paddingTop: '6px', 
                          borderTop: '1px solid #333',
                          color: '#f0b90b',
                          fontSize: '9px',
                          textAlign: 'center'
                        }}>
                          🎯 {hasPosition && existingBet ? 'TRIPLE PLAY! Trading + Betting' : hasPosition ? 'Trading active!' : `Betting ${existingBet.prediction.toUpperCase()}!`}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ color: '#9b59b6', fontSize: '10px', fontWeight: 'bold' }}>🔮 RUG PROPHET</span>
                      {existingBet && (
                        <span style={{ 
                          fontSize: '9px', 
                          padding: '2px 6px', 
                          borderRadius: '4px',
                          backgroundColor: existingBet.prediction === 'rug' ? '#ff3b6933' : '#00d26a33',
                          color: existingBet.prediction === 'rug' ? '#ff3b69' : '#00d26a',
                        }}>
                          {existingBet.prediction === 'rug' ? '💀 RUG' : '🌙 MOON'} • {formatPB(existingBet.amount)}
                        </span>
                      )}
                    </div>
                    
                    {existingBet ? (
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <div style={{ color: '#9b59b6', fontSize: '12px', marginBottom: '4px' }}>
                          Bet Active: {existingBet.prediction === 'rug' ? '💀 RUG' : '🌙 MOON'}
                        </div>
                        <div style={{ color: '#666', fontSize: '10px' }}>
                          {formatPB(existingBet.amount)} → potential {formatPB(existingBet.prediction === 'rug' ? Math.floor(existingBet.amount * 1.8) : Math.floor(existingBet.amount * 2.5))}
                        </div>
                      </div>
                    ) : canBet ? (
                      <div>
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                          {[10000, 50000, 100000].map(amt => (
                            <button
                              key={amt}
                              onClick={() => setBetAmount(amt)}
                              style={{
                                flex: 1,
                                padding: '6px',
                                borderRadius: '4px',
                                border: betAmount === amt ? '1px solid #f0b90b' : '1px solid #333',
                                backgroundColor: betAmount === amt ? '#f0b90b22' : '#111',
                                color: betAmount === amt ? '#f0b90b' : '#666',
                                fontSize: '10px',
                                cursor: 'pointer',
                              }}
                            >
                              {formatPB(amt)}
                            </button>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => {
                              if (gameWallet < betAmount) {
                                alert('Insufficient balance!');
                                return;
                              }
                              placeRugProphetBet(currentToken.id, 'rug', betAmount);
                            }}
                            disabled={gameWallet < betAmount || placingBet}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: gameWallet < betAmount ? '#333' : '#ff3b69',
                              color: '#fff',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: gameWallet < betAmount || placingBet ? 'not-allowed' : 'pointer',
                              opacity: placingBet ? 0.5 : 1,
                            }}
                          >
                            {placingBet ? '⏳...' : '💀 RUG (1.8x)'}
                          </button>
                          <button
                            onClick={() => {
                              if (gameWallet < betAmount) {
                                alert('Insufficient balance!');
                                return;
                              }
                              placeRugProphetBet(currentToken.id, 'moon', betAmount);
                            }}
                            disabled={gameWallet < betAmount || placingBet}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: gameWallet < betAmount ? '#333' : '#00d26a',
                              color: '#fff',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: gameWallet < betAmount || placingBet ? 'not-allowed' : 'pointer',
                              opacity: placingBet ? 0.5 : 1,
                            }}
                          >
                            {placingBet ? '⏳...' : '🌙 MOON (2.5x)'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', color: '#666', fontSize: '10px', padding: '8px 0' }}>
                        {!currentToken.canBetRugProphet ? 'Betting window closed' : 'Loading...'}
                      </div>
                    )}
                  </div>
                );
              })()}
              
              {/* 📊 LIVE TRADES - Desktop only (mobile shows below chart) */}
              {!isMobile && (
              <div style={{
                marginTop: '12px',
                backgroundColor: '#0a0a0f',
                borderRadius: '8px',
                border: '1px solid #1a1a2e',
                overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  backgroundColor: '#0d0d12',
                  borderBottom: '1px solid #1a1a2e',
                }}>
                  <span style={{ color: '#5e6673', fontSize: '10px', fontWeight: 'bold' }}>📊 LIVE TRADES</span>
                  <span style={{ color: '#444', fontSize: '9px' }}>{transactions.length} total</span>
                </div>
                
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {(() => {
                    const seenIds = new Set();
                    const uniqueTxs = transactions.filter(tx => {
                      if ((tx.sats || 0) <= 0) return false;
                      if (!tx.id) return true;
                      if (seenIds.has(tx.id)) return false;
                      seenIds.add(tx.id);
                      return true;
                    });
                    
                    return uniqueTxs.slice(0, 15).map((tx, idx) => {
                      const isBuy = tx.type === 'buy';
                      const isLarge = (tx.sats || 0) >= 25000;
                      const isHuge = (tx.sats || 0) >= 100000;
                      const timeAgo = tx.timestamp ? Math.floor((Date.now() - tx.timestamp) / 1000) : 0;
                      const timeStr = timeAgo < 60 ? `${timeAgo}s` : timeAgo < 3600 ? `${Math.floor(timeAgo/60)}m` : `${Math.floor(timeAgo/3600)}h`;
                      
                      return (
                        <div 
                          key={tx.id || idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            borderBottom: '1px solid #111',
                            backgroundColor: tx.isYou 
                              ? 'rgba(240,185,11,0.08)' 
                              : isHuge 
                                ? (isBuy ? 'rgba(22,199,132,0.1)' : 'rgba(234,57,67,0.1)')
                                : 'transparent',
                          }}
                        >
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            backgroundColor: isBuy ? 'rgba(22,199,132,0.2)' : 'rgba(234,57,67,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontSize: '12px',
                          }}>
                            {tx.isYou ? '👤' : isHuge ? (isBuy ? '🐋' : '💀') : isLarge ? (isBuy ? '🦈' : '📉') : (isBuy ? '↗' : '↘')}
                          </div>
                          
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ 
                                color: isBuy ? '#16c784' : '#ea3943', 
                                fontWeight: 'bold', 
                                fontSize: '10px',
                              }}>
                                {isBuy ? 'BUY' : 'SELL'}
                              </span>
                              {tx.isYou && (
                                <span style={{ 
                                  fontSize: '8px', 
                                  padding: '1px 4px', 
                                  backgroundColor: '#f0b90b', 
                                  color: '#000', 
                                  borderRadius: '3px',
                                  fontWeight: 'bold',
                                }}>YOU</span>
                              )}
                              {isHuge && !tx.isYou && (
                                <span style={{ 
                                  fontSize: '8px', 
                                  padding: '1px 4px', 
                                  backgroundColor: isBuy ? '#16c784' : '#ea3943', 
                                  color: '#000', 
                                  borderRadius: '3px',
                                  fontWeight: 'bold',
                                }}>{isBuy ? 'WHALE' : 'DUMP'}</span>
                              )}
                            </div>
                            <div style={{ 
                              color: '#666', 
                              fontSize: '9px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {tx.isYou ? 'Your trade' : (tx.wallet || tx.handle || 'Unknown')} • {timeStr} ago
                            </div>
                          </div>
                          
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ 
                              color: tx.isYou ? '#f0b90b' : (isBuy ? '#16c784' : '#ea3943'),
                              fontWeight: 'bold',
                              fontSize: isHuge ? '12px' : '11px',
                            }}>
                              {isBuy ? '+' : '-'}{formatPB(tx.sats || 0)}
                            </div>
                            <div style={{ color: '#555', fontSize: '9px' }}>
                              @${((tx.sats || 0) * satToUsd).toFixed(2)}
                              {tx.txId && (
                                <a 
                                  href={`https://whatsonchain.com/tx/${tx.txId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ 
                                    marginLeft: '4px', 
                                    color: '#00d26a',
                                    textDecoration: 'none',
                                  }}
                                  title="View on-chain transaction"
                                >
                                  ⛓️
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                  
                  {transactions.filter(tx => (tx.sats || 0) > 0).length === 0 && (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#444', fontSize: '11px' }}>
                      ⏳ Waiting for trades...
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
            )}
            {/* END RIGHT COLUMN */}
            
          </div>
          {/* END TWO-COLUMN LAYOUT */}
          
          {/* 🛡️ STREAK PROTECTED NOTIFICATION */}
          {showStreakProtected && (
            <div style={{ 
              position: 'fixed', 
              top: '100px', 
              left: '50%', 
              transform: 'translateX(-50%)', 
              zIndex: 2500,
              animation: 'slideDown 0.3s ease-out',
            }}>
              <div style={{
                backgroundColor: '#1a2f1a',
                border: '2px solid #00d26a',
                borderRadius: '12px',
                padding: '16px 32px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 0 30px rgba(0,210,106,0.5), inset 0 0 20px rgba(0,210,106,0.1)',
              }}>
                <div style={{ fontSize: '32px' }}>🛡️</div>
                <div>
                  <div style={{ color: '#00d26a', fontWeight: 'bold', fontSize: '16px' }}>STREAK PROTECTED!</div>
                  <div style={{ color: '#888', fontSize: '12px' }}>Your win streak was saved (1/day)</div>
                </div>
                <div style={{ 
                  marginLeft: '12px',
                  padding: '4px 8px', 
                  backgroundColor: '#f0b90b', 
                  color: '#000', 
                  borderRadius: '4px', 
                  fontSize: '10px', 
                  fontWeight: 'bold' 
                }}>
                  👑 PREMIUM
                </div>
              </div>
            </div>
          )}
          
          {/* 🏆 WIN CELEBRATION */}
          {showWinCelebration && (
            <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2000, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,50,0,0.7)' }} />
              
              {/* Small/Paper hands win */}
              {showWinCelebration.type === 'paperHands' && (
                <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
                  <div style={{ fontSize: '80px', marginBottom: '16px' }}>📄🤲</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#888' }}>Paper Hands...</div>
                  <div style={{ fontSize: '16px', color: '#666', marginTop: '8px' }}>+{formatPB(showWinCelebration.profit)} (weak exit)</div>
                </div>
              )}
              
              {/* Small win */}
              {showWinCelebration.type === 'small' && (
                <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
                  <div style={{ fontSize: '80px', marginBottom: '16px' }}>👍</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#00d26a' }}>Nice!</div>
                  <div style={{ fontSize: '20px', color: '#00d26a', marginTop: '8px' }}>+{formatPB(showWinCelebration.profit)}</div>
                </div>
              )}
              
              {/* Medium win */}
              {showWinCelebration.type === 'medium' && (
                <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
                  <div style={{ fontSize: '100px', marginBottom: '16px' }}>🐸👍</div>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#00d26a' }}>SOLID GAINS</div>
                  <div style={{ fontSize: '24px', color: '#00ff88', marginTop: '8px' }}>+{formatPB(showWinCelebration.profit)} ({showWinCelebration.multiplier.toFixed(2)}x)</div>
                </div>
              )}
              
              {/* Big win */}
              {showWinCelebration.type === 'big' && (
                <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
                  <div style={{ fontSize: '120px', marginBottom: '16px', animation: 'bounce 0.5s ease infinite' }}>🐸😎</div>
                  <div style={{ fontSize: '42px', fontWeight: '900', color: '#00ff88', textShadow: '0 0 30px rgba(0,255,136,0.5)' }}>CHAD MOVE</div>
                  <div style={{ fontSize: '28px', color: '#00d26a', marginTop: '12px' }}>+{formatPB(showWinCelebration.profit)} ({showWinCelebration.multiplier.toFixed(2)}x)</div>
                </div>
              )}
              
              {/* Huge win */}
              {showWinCelebration.type === 'huge' && (
                <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
                  <div style={{ fontSize: '100px', marginBottom: '16px' }}>🐸🏎️💨</div>
                  <div style={{ fontSize: '48px', fontWeight: '900', color: '#ffd700', textShadow: '0 0 40px rgba(255,215,0,0.6)', animation: 'pulse 0.5s ease infinite' }}>LAMBO SOON</div>
                  <div style={{ fontSize: '32px', color: '#00ff88', marginTop: '16px' }}>+{formatPB(showWinCelebration.profit)} ({showWinCelebration.multiplier.toFixed(2)}x)</div>
                  <div style={{ fontSize: '16px', color: '#888', marginTop: '8px' }}>🔥 You're on fire 🔥</div>
                </div>
              )}
              
              {/* LEGENDARY win */}
              {showWinCelebration.type === 'legendary' && (
                <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
                  <div style={{ fontSize: '120px', marginBottom: '16px', animation: 'bounce 0.3s ease infinite' }}>👑🐸👑</div>
                  <div style={{ fontSize: '56px', fontWeight: '900', background: 'linear-gradient(135deg, #ffd700, #ff6b00, #ffd700)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'pulse 0.3s ease infinite' }}>LEGENDARY</div>
                  <div style={{ fontSize: '36px', color: '#ffd700', marginTop: '16px', textShadow: '0 0 30px rgba(255,215,0,0.8)' }}>+{formatPB(showWinCelebration.profit)} ({showWinCelebration.multiplier.toFixed(2)}x)</div>
                  <div style={{ fontSize: '18px', color: '#00ff88', marginTop: '12px' }}>🏆 Hall of Fame Material 🏆</div>
                </div>
              )}
              
              {/* Coins/confetti for wins */}
              {showWinCelebration.type !== 'paperHands' && [...Array(30)].map((_, i) => (
                <div key={i} style={{
                  position: 'absolute',
                  left: `${Math.random() * 100}%`,
                  top: '-30px',
                  fontSize: `${16 + Math.random() * 24}px`,
                  animation: `confettiFall ${1.5 + Math.random() * 2}s linear ${Math.random() * 0.3}s infinite`,
                }}>
                  {['💰', '🪙', '💵', '✨', '💎'][Math.floor(Math.random() * 5)]}
                </div>
              ))}
              
              <style>{`
                @keyframes bounce { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.8; } }
                @keyframes popIn {
                  from { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
                  to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
                }
                @keyframes confettiFall {
                  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
                }
              `}</style>
            </div>
          )}
          
          {/* 📉 PAPER HANDS LOSS - Sold at a loss */}
          {showPaperHandsLoss && (
            <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2000, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(50,20,0,0.9)' }} />
              
              <div style={{ position: 'absolute', left: '50%', top: '45%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
                {/* Random humiliation messages */}
                {(() => {
                  const messages = [
                    { emoji: '📄🙌', title: 'PAPER HANDS', subtitle: 'Sold the dip... classic' },
                    { emoji: '📉🤡', title: 'WEAK HANDS', subtitle: 'Buy high, sell low speedrun' },
                    { emoji: '🏃💨', title: 'PANIC SOLD', subtitle: 'Fear won this round' },
                    { emoji: '📄😭', title: 'PAPER HANDED', subtitle: 'Couldn\'t handle the pressure' },
                    { emoji: '🐔💸', title: 'CHICKENED OUT', subtitle: 'Bawk bawk!' },
                  ];
                  const msg = messages[Math.floor(Math.random() * messages.length)];
                  
                  return (
                    <>
                      <div style={{ fontSize: '80px', marginBottom: '12px' }}>{msg.emoji}</div>
                      <div style={{ fontSize: '42px', fontWeight: '900', color: '#ff6b00' }}>{msg.title}</div>
                      <div style={{ fontSize: '18px', color: '#ff9500', marginTop: '8px' }}>{msg.subtitle}</div>
                    </>
                  );
                })()}
                
                <div style={{ fontSize: '28px', color: '#ff3b69', marginTop: '16px', fontWeight: 'bold' }}>
                  -{showPaperHandsLoss.loss} sats
                </div>
                
                <div style={{ fontSize: '14px', color: '#888', marginTop: '8px' }}>
                  Sold {showPaperHandsLoss.tokenTicker} at {showPaperHandsLoss.multiplier.toFixed(3)}x (below your entry)
                </div>
                
                {/* The wisdom - what MIGHT have happened */}
                <div style={{ 
                  marginTop: '20px', 
                  padding: '12px 20px', 
                  backgroundColor: 'rgba(255,107,0,0.15)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(255,107,0,0.3)',
                  maxWidth: '350px'
                }}>
                  {showPaperHandsLoss.tokenFate === 'RUG' ? (
                    <div style={{ fontSize: '13px', color: '#00d26a' }}>
                      💡 <strong>Plot twist:</strong> This token is destined to RUG anyway... 
                      <br/><span style={{ color: '#888' }}>Maybe you dodged a bullet? 🎯</span>
                    </div>
                  ) : showPaperHandsLoss.tokenFate === 'MOON' ? (
                    <div style={{ fontSize: '13px', color: '#ff3b69' }}>
                      💀 <strong>Bad news:</strong> This one's going to MOON...
                      <br/><span style={{ color: '#888' }}>You'll regret this later 🚀</span>
                    </div>
                  ) : (
                    <div style={{ fontSize: '13px', color: '#f0b90b' }}>
                      🎲 <strong>Who knows:</strong> It might rug... or moon without you
                      <br/><span style={{ color: '#888' }}>That's crypto! 🎰</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Falling sad emojis */}
              {[...Array(15)].map((_, i) => (
                <div key={i} style={{
                  position: 'absolute',
                  left: `${Math.random() * 100}%`,
                  top: '-30px',
                  fontSize: `${16 + Math.random() * 20}px`,
                  animation: `confettiFall ${2 + Math.random() * 2}s linear ${Math.random() * 0.5}s infinite`,
                }}>
                  {['📄', '😭', '📉', '💸', '🐔'][Math.floor(Math.random() * 5)]}
                </div>
              ))}
              
              <style>{`
                @keyframes popIn {
                  from { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
                  to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
                }
                @keyframes confettiFall {
                  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
                }
              `}</style>
            </div>
          )}
          
          {/* 💀 LOSS HUMILIATION */}
          {showLossHumiliation && (
            <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2000, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(50,0,0,0.85)' }} />
              
              {/* HARD RUG - Instant death */}
              {showLossHumiliation.rugType === 'HARD' && (
                <div style={{ position: 'absolute', left: '50%', top: '45%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'shakeScreen 0.5s ease-out' }}>
                  <div style={{ fontSize: '100px', marginBottom: '16px', animation: 'coffin 0.5s ease infinite' }}>⚡💀⚡</div>
                  <div style={{ fontSize: '48px', fontWeight: '900', color: '#ff0040' }}>HARD RUGGED</div>
                  <div style={{ fontSize: '24px', color: '#ff3b69', marginTop: '12px' }}>-{showLossHumiliation.loss} sats</div>
                  <div style={{ fontSize: '14px', color: '#ff6b6b', marginTop: '8px', maxWidth: '400px' }}>
                    Dev drained liquidity instantly. Zero warning. This is the classic rug.
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '16px', padding: '10px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                    💡 <strong>IRL:</strong> Always check if LP is locked/burned before buying
                  </div>
                </div>
              )}
              
              {/* TRAP RUG - Pump and dump */}
              {showLossHumiliation.rugType === 'TRAP' && (
                <div style={{ position: 'absolute', left: '50%', top: '45%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
                  <div style={{ fontSize: '100px', marginBottom: '16px' }}>🪤🐸😭</div>
                  <div style={{ fontSize: '48px', fontWeight: '900', color: '#ff6b00' }}>TRAP RUGGED</div>
                  <div style={{ fontSize: '24px', color: '#ff6b6b', marginTop: '12px' }}>-{showLossHumiliation.loss} sats</div>
                  <div style={{ fontSize: '14px', color: '#ff9966', marginTop: '8px', maxWidth: '400px' }}>
                    That pump was fake. Coordinated buys created FOMO, then insiders dumped on you.
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '16px', padding: '10px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                    💡 <strong>IRL:</strong> Be suspicious of sudden pumps with shill messages. Check wallet distribution.
                  </div>
                </div>
              )}
              
              {/* SLOW RUG - Death by 1000 cuts */}
              {showLossHumiliation.rugType === 'SLOW' && (
                <div style={{ position: 'absolute', left: '50%', top: '45%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'slowFade 1s ease-out' }}>
                  <div style={{ fontSize: '100px', marginBottom: '16px', animation: 'melt 2s ease-out forwards' }}>📉🐸📉</div>
                  <div style={{ fontSize: '48px', fontWeight: '900', color: '#ff9500' }}>SLOW RUGGED</div>
                  <div style={{ fontSize: '24px', color: '#ff6b6b', marginTop: '12px' }}>-{showLossHumiliation.loss} sats</div>
                  <div style={{ fontSize: '14px', color: '#ffaa66', marginTop: '8px', maxWidth: '400px' }}>
                    Dev slowly sold while you coped. "Just profit taking" they said. Now there's no liquidity.
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '16px', padding: '10px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                    💡 <strong>IRL:</strong> Track dev wallets. Set stop losses. Don't marry your bags.
                  </div>
                </div>
              )}
              
              {/* HONEYPOT - Can't sell */}
              {showLossHumiliation.rugType === 'HONEYPOT' && (
                <div style={{ position: 'absolute', left: '50%', top: '45%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'popIn 0.3s ease-out' }}>
                  <div style={{ fontSize: '100px', marginBottom: '16px' }}>🍯🐸⛓️</div>
                  <div style={{ fontSize: '48px', fontWeight: '900', color: '#ff0040' }}>HONEYPOTTED</div>
                  <div style={{ fontSize: '24px', color: '#ff6b6b', marginTop: '12px' }}>-{showLossHumiliation.loss} sats</div>
                  <div style={{ fontSize: '14px', color: '#ff6b6b', marginTop: '8px', maxWidth: '400px' }}>
                    Contract blocked your sells. Only dev could sell. You never had a chance.
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '16px', padding: '10px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                    💡 <strong>IRL:</strong> ALWAYS test sell with tiny amount first. Use honeypot.is before buying.
                  </div>
                </div>
              )}
              
              {/* Learn More Button */}
              <div style={{ 
                position: 'absolute', 
                bottom: '60px', 
                left: '50%', 
                transform: 'translateX(-50%)',
                animation: 'fadeIn 1s ease-out 1.5s both',
                pointerEvents: 'auto',
                textAlign: 'center'
              }}>
                <button 
                  onClick={() => { 
                    setShowLossHumiliation(null); 
                    setShowRugEncyclopedia(true); 
                    setSelectedRug(showLossHumiliation.rugType); 
                  }}
                  style={{ 
                    padding: '12px 24px', 
                    borderRadius: '8px', 
                    border: '2px solid #ff3b69', 
                    backgroundColor: 'rgba(255,59,105,0.2)', 
                    color: '#ff3b69', 
                    fontSize: '14px', 
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  📚 Learn to spot {showLossHumiliation.rugType} rugs in real life →
                </button>
                <div style={{ color: '#666', fontSize: '11px', marginTop: '8px' }}>
                  Don't let this happen to you IRL
                </div>
              </div>
              
              {/* Clown appearing */}
              <div style={{ 
                position: 'absolute', 
                right: '10%', 
                top: '50%', 
                transform: 'translateY(-50%)',
                fontSize: '100px',
                animation: 'fadeInClown 1s ease-out 0.5s both',
                opacity: 0
              }}>
                🤡
              </div>
              
              {/* Skulls falling */}
              {[...Array(12)].map((_, i) => (
                <div key={i} style={{
                  position: 'absolute',
                  left: `${Math.random() * 100}%`,
                  top: '-30px',
                  fontSize: `${20 + Math.random() * 20}px`,
                  animation: `confettiFall ${2 + Math.random() * 2}s linear ${Math.random() * 0.5}s infinite`,
                }}>
                  {['💀', '☠️', '🦴', '📉', '😵'][Math.floor(Math.random() * 5)]}
                </div>
              ))}
              
              <style>{`
                @keyframes shakeScreen {
                  0%, 100% { transform: translate(-50%, -50%); }
                  10% { transform: translate(-52%, -48%); }
                  20% { transform: translate(-48%, -52%); }
                  30% { transform: translate(-52%, -50%); }
                  40% { transform: translate(-48%, -50%); }
                  50% { transform: translate(-50%, -48%); }
                }
                @keyframes coffin {
                  0%, 100% { transform: rotate(-5deg); }
                  50% { transform: rotate(5deg); }
                }
                @keyframes melt {
                  0% { transform: scaleY(1); opacity: 1; }
                  100% { transform: scaleY(0.5) translateY(30px); opacity: 0.6; }
                }
                @keyframes slowFade {
                  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
                  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                }
                @keyframes fadeInClown {
                  0% { opacity: 0; transform: translateY(-50%) scale(0.5) rotate(-20deg); }
                  100% { opacity: 1; transform: translateY(-50%) scale(1) rotate(0deg); }
                }
                @keyframes fadeIn {
                  0% { opacity: 0; }
                  100% { opacity: 1; }
                }
                @keyframes popIn {
                  from { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
                  to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
                }
                @keyframes confettiFall {
                  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
                }
              `}</style>
            </div>
          )}
          
          {/* 🧹 RUG ACKNOWLEDGE MODAL - Must clear rugs before trading again */}
          {showRugAcknowledgeModal && unclearedRugs > 0 && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
              <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #ff3b69', borderRadius: '16px', padding: '24px', maxWidth: '380px', width: '90%', textAlign: 'center' }}>
                <div style={{ fontSize: '60px', marginBottom: '12px' }}>💀</div>
                <div style={{ color: '#ff3b69', fontSize: '22px', fontWeight: 'bold', marginBottom: '8px' }}>
                  YOU GOT RUGGED
                </div>
                <div style={{ color: '#888', fontSize: '14px', marginBottom: '20px' }}>
                  You have <span style={{ color: '#f0b90b', fontWeight: 'bold' }}>{unclearedRugs}</span> rugged position{unclearedRugs > 1 ? 's' : ''} that need your acknowledgment before you can trade again.
                </div>

                <div style={{ backgroundColor: '#1a0a0a', border: '1px solid #ff3b69', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                  <div style={{ color: '#ff6b6b', fontSize: '13px', fontStyle: 'italic' }}>
                    "Every degen learns the hard way. The rug is the teacher."
                  </div>
                </div>

                <button
                  onClick={clearRugs}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: '#ff3b69',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  I Accept My Fate 🪦
                </button>

                <div style={{ color: '#666', fontSize: '11px', marginTop: '12px' }}>
                  Your rugged positions have been added to your bags.
                </div>
              </div>
            </div>
          )}

          {/* 🗑️ TRASH MODAL - After getting rugged, choose to keep or trash tokens */}
          {showTrashModal && pendingRuggedPosition && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, overflowY: 'auto' }} onTouchMove={(e) => e.stopPropagation()}>
              <div style={{ backgroundColor: '#0a0a0f', border: '1px solid #ff3b69', borderRadius: '16px', padding: '24px', maxWidth: '380px', width: '90%', textAlign: 'center' }}>
                <div style={{ fontSize: '60px', marginBottom: '12px' }}>🗑️</div>
                <div style={{ color: '#ff3b69', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                  Not Even Worth Selling
                </div>
                <div style={{ color: '#888', fontSize: '13px', marginBottom: '16px' }}>
                  You still own <span style={{ color: '#f0b90b' }}>{formatSats(Math.floor(pendingRuggedPosition.tokensOwned))}</span> worthless {pendingRuggedPosition.ticker} tokens.
                </div>
                
                {/* Token "receipt" */}
                <div style={{ backgroundColor: '#111', border: '1px solid #1a1a2e', borderRadius: '8px', padding: '12px', marginBottom: '16px', textAlign: 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#666', fontSize: '11px' }}>Token</span>
                    <span style={{ color: '#f0b90b', fontSize: '11px' }}>{pendingRuggedPosition.ticker}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#666', fontSize: '11px' }}>You paid</span>
                    <span style={{ color: '#ff3b69', fontSize: '11px' }}>{pendingRuggedPosition.invested} sats</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#666', fontSize: '11px' }}>Current value</span>
                    <span style={{ color: '#ff3b69', fontSize: '11px' }}>≈ 0 sats</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666', fontSize: '11px' }}>Rug type</span>
                    <span style={{ color: '#ff6b6b', fontSize: '11px' }}>{pendingRuggedPosition.rugType}</span>
                  </div>
                </div>
                
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '16px' }}>
                  Want me to trash these tokens or keep them in your bags as a reminder?
                </div>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => {
                      // Trash - just close
                      setShowTrashModal(false);
                      setPendingRuggedPosition(null);
                      positionRef.current = { size: 0, invested: 0, entry: 1.0 };
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#ff3b69',
                      color: '#fff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    🗑️ Trash Them
                  </button>
                  <button
                    onClick={() => {
                      // Keep in bags
                      addToBags(
                        { ticker: pendingRuggedPosition.ticker, category: pendingRuggedPosition.category, rugType: pendingRuggedPosition.rugType },
                        pendingRuggedPosition.tokensOwned,
                        pendingRuggedPosition.invested
                      );
                      setShowTrashModal(false);
                      setPendingRuggedPosition(null);
                      positionRef.current = { size: 0, invested: 0, entry: 1.0 };
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #333',
                      backgroundColor: 'transparent',
                      color: '#888',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    💼 Keep in Bags
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* 🚀 SUPERMOON CELEBRATION */}
          {showSupermoonCelebration && (
            <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2000, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,20,50,0.95), rgba(20,0,40,0.95))' }} />
              
              <div style={{ position: 'absolute', left: '50%', top: '45%', transform: 'translate(-50%, -50%)', textAlign: 'center', animation: 'superMoonEntry 0.5s ease-out' }}>
                <div style={{ fontSize: '120px', marginBottom: '16px', animation: 'rocketBlast 0.5s ease infinite' }}>🚀🌙🚀</div>
                <div style={{ 
                  fontSize: '72px', 
                  fontWeight: '900', 
                  background: 'linear-gradient(135deg, #f0b90b, #ff6b00, #ff0080)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'rainbowShift 2s linear infinite'
                }}>SUPERMOON!</div>
                <div style={{ fontSize: '36px', color: '#00ff88', marginTop: '12px', fontWeight: 'bold' }}>
                  +{formatPB(showSupermoonCelebration.profit)}
                </div>
                <div style={{ fontSize: '24px', color: '#f0b90b', marginTop: '8px' }}>
                  {showSupermoonCelebration.multiplier.toFixed(2)}x LEGENDARY EXIT! 🎉
                </div>
                <div style={{ fontSize: '16px', color: '#888', marginTop: '16px' }}>
                  You caught a SUPERMOON! This is rare. You are a legend.
                </div>
              </div>
              
              {/* Rockets flying up */}
              {[...Array(20)].map((_, i) => (
                <div key={i} style={{
                  position: 'absolute',
                  left: `${Math.random() * 100}%`,
                  bottom: '-50px',
                  fontSize: `${25 + Math.random() * 25}px`,
                  animation: `rocketUp ${1.5 + Math.random() * 2}s linear ${Math.random() * 0.5}s infinite`,
                }}>
                  {['🚀', '🌙', '⭐', '✨', '💫', '🌟'][Math.floor(Math.random() * 6)]}
                </div>
              ))}
              
              {/* Gold particles */}
              {[...Array(30)].map((_, i) => (
                <div key={`gold-${i}`} style={{
                  position: 'absolute',
                  left: `${Math.random() * 100}%`,
                  top: '-20px',
                  fontSize: `${15 + Math.random() * 15}px`,
                  animation: `goldFall ${2 + Math.random() * 3}s linear ${Math.random()}s infinite`,
                }}>
                  {['💰', '💵', '💎', '👑', '🏆'][Math.floor(Math.random() * 5)]}
                </div>
              ))}
              
              <style>{`
                @keyframes superMoonEntry {
                  from { transform: translate(-50%, -50%) scale(0.3); opacity: 0; }
                  to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
                }
                @keyframes rocketBlast {
                  0%, 100% { transform: translateY(0) scale(1); }
                  50% { transform: translateY(-15px) scale(1.1); }
                }
                @keyframes rocketUp {
                  0% { transform: translateY(0) rotate(-5deg); opacity: 1; }
                  100% { transform: translateY(-120vh) rotate(5deg); opacity: 0; }
                }
                @keyframes goldFall {
                  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                  100% { transform: translateY(120vh) rotate(720deg); opacity: 0; }
                }
                @keyframes rainbowShift {
                  0% { filter: hue-rotate(0deg); }
                  100% { filter: hue-rotate(360deg); }
                }
              `}</style>
            </div>
          )}
          
          {/* 🐸 UNIVERSAL PEPE TOAST */}
          {pepeToast && (
            <div style={{ 
              position: 'fixed', 
              inset: 0, 
              pointerEvents: 'none', 
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: pepeToast.direction === 'left' ? 'flex-start' : 'flex-end',
            }}>
              {/* Pepe sliding in */}
              <div style={{
                fontSize: '80px',
                animation: pepeToast.direction === 'left' 
                  ? 'pepeSlideFromLeft 0.5s ease-out forwards, pepeFloatGentle 2s ease-in-out infinite 0.5s'
                  : 'pepeSlideFromRight 0.5s ease-out forwards, pepeFloatGentle 2s ease-in-out infinite 0.5s',
              }}>
                🐸
              </div>
              
              {/* Speech bubble */}
              <div style={{
                backgroundColor: '#1a1a2e',
                border: '2px solid #00d26a',
                borderRadius: '16px',
                padding: '12px 20px',
                marginLeft: pepeToast.direction === 'left' ? '0' : '-20px',
                marginRight: pepeToast.direction === 'left' ? '-20px' : '0',
                maxWidth: '200px',
                animation: 'bubblePop 0.3s ease-out 0.4s both',
                position: 'relative',
              }}>
                <div style={{ 
                  color: '#00d26a', 
                  fontSize: '14px', 
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}>
                  {pepeToast.message}
                </div>
                {/* Speech bubble arrow */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  [pepeToast.direction === 'left' ? 'left' : 'right']: '-10px',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '10px solid transparent',
                  borderBottom: '10px solid transparent',
                  [pepeToast.direction === 'left' ? 'borderRight' : 'borderLeft']: '10px solid #00d26a',
                }} />
              </div>
              
              <style>{`
                @keyframes pepeSlideFromRight {
                  from { transform: translateX(100px); opacity: 0; }
                  to { transform: translateX(-20px); opacity: 1; }
                }
                @keyframes pepeSlideFromLeft {
                  from { transform: translateX(-100px); opacity: 0; }
                  to { transform: translateX(20px); opacity: 1; }
                }
                @keyframes pepeFloatGentle {
                  0%, 100% { transform: rotate(-3deg); }
                  50% { transform: translateY(-10px) rotate(3deg); }
                }
                @keyframes bubblePop {
                  from { transform: scale(0.5); opacity: 0; }
                  to { transform: scale(1); opacity: 1; }
                }
              `}</style>
            </div>
          )}
          
          {/* Unified Deposit Modal */}
          <RealMoneyWarningModal />
          <RakeConsentModal />
          <RakeDisclosureModal />
          <DepositModal />
          <WithdrawModal />
          
          {/* 💸 TIP RECEIVED TOAST - Shows when you get tipped while in token view */}
          {tipReceived && (
            <div style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(8, 35, 8, 0.98)',
              border: '3px solid #00d26a',
              borderRadius: '20px',
              padding: '30px 50px',
              zIndex: 3000,
              animation: 'tipPop 0.5s ease-out',
              boxShadow: '0 0 60px rgba(0,210,106,0.5)',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '50px', marginBottom: '10px' }}>💸</div>
              <div style={{ color: '#00d26a', fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                TIP RECEIVED!
              </div>
              <div style={{ color: '#fff', fontSize: '36px', fontWeight: 'bold', marginBottom: '4px' }}>
                +{formatPB(tipReceived.amount)}
              </div>
              <div style={{ color: '#666', fontSize: '12px', marginBottom: '8px' }}>
                {formatUsd(tipReceived.amount)} real value
              </div>
              <div style={{ color: '#888', fontSize: '14px' }}>
                from <span style={{ color: '#f0b90b' }}>${tipReceived.from}</span>
              </div>
              <style>{`
                @keyframes tipPop {
                  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
                  50% { transform: translate(-50%, -50%) scale(1.1); }
                  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                }
              `}</style>
            </div>
          )}
          
          {/* 🐛 REMOVED: Purple DM notification popup - Paper Chat handles DMs with unread badges */}

          {/* 💰 Trade Mini Toast Notification - Trading View */}
          {tradeNotification && (
            <div
              style={{
                position: 'fixed',
                top: '80px',
                right: '20px',
                backgroundColor: tradeNotification.type === 'buy'
                  ? 'rgba(0, 40, 20, 0.98)'
                  : 'rgba(40, 10, 20, 0.98)',
                border: `2px solid ${tradeNotification.type === 'buy' ? '#00d26a' : '#ff3b69'}`,
                borderRadius: '12px',
                padding: '14px 18px',
                zIndex: 2999,
                animation: 'tradeSlideIn 0.3s ease-out',
                boxShadow: tradeNotification.type === 'buy'
                  ? '0 0 30px rgba(0, 210, 106, 0.4)'
                  : '0 0 30px rgba(255, 59, 105, 0.4)',
                maxWidth: '280px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  fontSize: '24px',
                  animation: 'tradeIconPulse 0.5s ease-out',
                }}>
                  {tradeNotification.isBot
                    ? (tradeNotification.type === 'buy' ? '🤖💚' : '🤖💔')
                    : (tradeNotification.type === 'buy' ? '💚' : '💔')
                  }
                </div>
                <div>
                  <div style={{
                    color: tradeNotification.type === 'buy' ? '#00d26a' : '#ff3b69',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    marginBottom: '2px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    {tradeNotification.isBot ? 'BOT ' : ''}{tradeNotification.type === 'buy' ? 'BOUGHT' : 'SOLD'}
                    {tradeNotification.isPvP && <span style={{ color: '#ff69b4', marginLeft: '6px' }}>⚔️ PvP</span>}
                  </div>
                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>
                    ${tradeNotification.ticker || '???'}
                  </div>
                  {tradeNotification.amount && (
                    <div style={{ color: '#f0b90b', fontSize: '12px', marginTop: '2px' }}>
                      {tradeNotification.type === 'buy' ? '−' : '+'}{formatPB(tradeNotification.amount)}
                    </div>
                  )}
                  {tradeNotification.profit !== undefined && tradeNotification.type === 'sell' && (
                    <div style={{
                      color: tradeNotification.profit >= 0 ? '#00d26a' : '#ff3b69',
                      fontSize: '11px',
                      marginTop: '2px',
                      fontWeight: 'bold',
                    }}>
                      {tradeNotification.profit >= 0 ? '+' : ''}{formatPB(tradeNotification.profit)} profit
                    </div>
                  )}
                  {tradeNotification.narration && tradeNotification.isBot && (
                    <div style={{
                      color: '#aaa',
                      fontSize: '11px',
                      marginTop: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '200px',
                    }}>
                      {tradeNotification.narration}
                    </div>
                  )}
                  {tradeNotification.isPvP && tradeNotification.rake > 0 && (
                    <div style={{
                      color: '#f0b90b',
                      fontSize: '10px',
                      marginTop: '4px',
                      opacity: 0.8,
                    }}>
                      💰 {formatPB(tradeNotification.rake)} rake paid
                    </div>
                  )}
                </div>
              </div>
              <style>{`
                @keyframes tradeSlideIn {
                  0% { opacity: 0; transform: translateX(100px); }
                  100% { opacity: 1; transform: translateX(0); }
                }
                @keyframes tradeIconPulse {
                  0% { transform: scale(1.3); }
                  100% { transform: scale(1); }
                }
              `}</style>
            </div>
          )}
          </>
          )}

          {/* 🤖 DEGEN AI - Chat Window (button moved to nav bar) */}
          {handcashConnected && (
            <>
              {/* Chat Window */}
              {showDegenAI && (
                <div
                  style={{
                    position: 'fixed',
                    bottom: isMobile ? '0' : '24px',
                    right: isMobile ? '0' : '24px',
                    width: isMobile ? '100%' : '380px',
                    height: isMobile ? '70vh' : '500px',
                    backgroundColor: 'rgba(15, 15, 25, 0.98)',
                    border: '2px solid #f0b90b',
                    borderRadius: isMobile ? '16px 16px 0 0' : '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 9999,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    overflow: 'hidden',
                    fontFamily: "'SF Mono', monospace",
                  }}
                >
                  {/* Header */}
                  <div style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #f0b90b 0%, #d4a30a 100%)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '24px' }}>🤖</span>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#000', fontSize: '14px' }}>
                          DEGEN AI {degenAIStatus?.isSubscriber && <span style={{ fontSize: '10px', background: '#000', color: '#f0b90b', padding: '2px 6px', borderRadius: '4px', marginLeft: '4px' }}>UNLIMITED</span>}
                        </div>
                        <div style={{ fontSize: '10px', color: '#333' }}>
                          {degenAIStatus?.isSubscriber
                            ? 'Unlimited questions'
                            : degenAIStatus?.freeRemaining > 0
                              ? `${degenAIStatus.freeRemaining} free questions left`
                              : 'Subscribe for unlimited access'
                          }
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowDegenAI(false)}
                      style={{
                        background: 'rgba(0,0,0,0.2)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        color: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Messages */}
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}>
                    {/* Welcome message if no messages */}
                    {degenAIMessages.length === 0 && (
                      <div style={{
                        textAlign: 'center',
                        color: '#888',
                        padding: '20px',
                        fontSize: '13px',
                      }}>
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>🤖💬</div>
                        <div style={{ color: '#f0b90b', fontWeight: 'bold', marginBottom: '8px' }}>
                          Yo degen! I'm your AI assistant.
                        </div>
                        <div>
                          Ask me about game mechanics, strategy tips, or how things work around here.
                        </div>
                        <div style={{ marginTop: '12px', fontSize: '11px', color: '#666' }}>
                          I can't tell you when tokens will rug though... nobody can predict that!
                        </div>
                      </div>
                    )}

                    {/* Chat messages */}
                    {degenAIMessages.map((msg, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <div style={{
                          maxWidth: '85%',
                          padding: '10px 14px',
                          borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          backgroundColor: msg.role === 'user'
                            ? '#f0b90b'
                            : msg.isError
                              ? 'rgba(255, 59, 105, 0.2)'
                              : 'rgba(255, 255, 255, 0.1)',
                          color: msg.role === 'user' ? '#000' : msg.isError ? '#ff3b69' : '#fff',
                          fontSize: '13px',
                          lineHeight: '1.4',
                        }}>
                          {msg.content}
                          {msg.role === 'assistant' && msg.cost > 0 && (
                            <div style={{
                              marginTop: '6px',
                              fontSize: '10px',
                              color: '#f0b90b',
                              opacity: 0.8,
                            }}>
                              -{formatPB(msg.cost)} Paper Bucks
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Loading indicator */}
                    {degenAILoading && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }}>
                        <div style={{
                          padding: '10px 14px',
                          borderRadius: '16px 16px 16px 4px',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: '#888',
                          fontSize: '13px',
                        }}>
                          <span style={{ animation: 'degenTyping 1s infinite' }}>🤖 Thinking...</span>
                        </div>
                      </div>
                    )}

                    <div ref={degenAIMessagesEndRef} />
                  </div>

                  {/* UPSELL MODAL - Shows when free questions run out */}
                  {showDegenAIUpsell && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '20px',
                      zIndex: 10,
                    }}>
                      <div style={{
                        backgroundColor: 'rgba(20, 20, 35, 0.98)',
                        border: '2px solid #f0b90b',
                        borderRadius: '16px',
                        padding: '24px',
                        maxWidth: '320px',
                        textAlign: 'center',
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🤖</div>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f0b90b', marginBottom: '8px' }}>
                          Out of free questions!
                        </div>
                        <div style={{ fontSize: '13px', color: '#ccc', marginBottom: '16px', lineHeight: '1.5' }}>
                          That token you're watching? It could rug any second, ser. Do you <span style={{ color: '#ff3b69' }}>REALLY</span> want to trade without your AI wingman?
                        </div>

                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fff', marginBottom: '4px' }}>
                          DEGEN AI Unlimited
                        </div>
                        <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f0b90b', marginBottom: '16px' }}>
                          $5/month
                        </div>

                        <div style={{ textAlign: 'left', marginBottom: '20px', fontSize: '12px', color: '#aaa' }}>
                          <div style={{ marginBottom: '6px' }}>→ One saved rug pays for a YEAR</div>
                          <div style={{ marginBottom: '6px' }}>→ Ask anything, anytime, no limits</div>
                          <div>→ Your edge while others guess</div>
                        </div>

                        <button
                          onClick={subscribeToDegenAI}
                          disabled={degenAISubscribing}
                          style={{
                            width: '100%',
                            padding: '14px 20px',
                            background: degenAISubscribing
                              ? 'rgba(240, 185, 11, 0.5)'
                              : 'linear-gradient(135deg, #f0b90b 0%, #d4a30a 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            color: '#000',
                            fontWeight: 'bold',
                            fontSize: '15px',
                            cursor: degenAISubscribing ? 'wait' : 'pointer',
                            marginBottom: '12px',
                          }}
                        >
                          {degenAISubscribing ? 'Processing...' : '🚀 Unlock Now - $5/mo'}
                        </button>

                        <button
                          onClick={() => setShowDegenAIUpsell(false)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            background: 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: '#888',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          I'll risk it alone
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Input */}
                  <div style={{
                    padding: '12px',
                    borderTop: '1px solid rgba(240, 185, 11, 0.3)',
                    display: 'flex',
                    gap: '8px',
                  }}>
                    <input
                      type="text"
                      value={degenAIInput}
                      onChange={(e) => setDegenAIInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          askDegenAI(degenAIInput);
                        }
                      }}
                      placeholder="Ask me anything..."
                      disabled={degenAILoading}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(240, 185, 11, 0.3)',
                        borderRadius: '20px',
                        color: '#fff',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                    />
                    <button
                      onClick={() => askDegenAI(degenAIInput)}
                      disabled={degenAILoading || !degenAIInput.trim()}
                      style={{
                        padding: '10px 16px',
                        background: degenAILoading || !degenAIInput.trim()
                          ? 'rgba(240, 185, 11, 0.3)'
                          : 'linear-gradient(135deg, #f0b90b 0%, #d4a30a 100%)',
                        border: 'none',
                        borderRadius: '20px',
                        color: degenAILoading || !degenAIInput.trim() ? '#666' : '#000',
                        fontWeight: 'bold',
                        cursor: degenAILoading || !degenAIInput.trim() ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                      }}
                    >
                      {degenAILoading ? '...' : 'Ask'}
                    </button>
                  </div>
                </div>
              )}

              {/* Animations */}
              <style>{`
                @keyframes degenPulse {
                  0%, 100% { transform: scale(1); box-shadow: 0 4px 20px rgba(240, 185, 11, 0.5); }
                  50% { transform: scale(1.05); box-shadow: 0 4px 30px rgba(240, 185, 11, 0.7); }
                }
                @keyframes degenTyping {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.5; }
                }
              `}</style>
            </>
          )}
        </div>
      </div>
      </>
    );
  }

  return null;
};

export default PaperHands;
